import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
// [address-picker-sweep-2026-07-28] react-dom is already a direct dependency (see
// package.json / src/main.jsx). createPortal is used by AddressAutocomplete so the
// suggestion dropdown escapes the Sheet/card `overflow:auto` containers that would
// otherwise clip it on a phone-sized WebView.
import { createPortal } from "react-dom";
// [work-log-wizard-2026-09-20] New capture flow; gated by useWorkLogWizard, AttemptSheet stays the default.
import { WorkLogWizard } from "./work-log-wizard.jsx";
/* [recon-server-web-2026-10-21] One integration of PR #15 (biometric-login-v2), #16 (esign-evidence-v2)
   and #17 (attempt-badge-served) on top of main c02ede2; www/ rebuilt once. */
/* [biometric-login-2026-10-11] Face ID / Touch ID sign-in (native iOS only; no-ops on web) */
import { bioAvailable, bioEnrolled, bioEnrolledEmail, bioWasAsked, bioMarkAsked, bioEnroll, bioRefreshAfterPasswordLogin, bioDisable, bioSignIn, bioIsExpired } from "./biometric.js";
// [server-parity-web-2026-10-05] Platform shim — every window.Capacitor probe in
// this file goes through it so the web and native builds share one source.
import { isNative as ssIsNative, platform as ssPlatform, plugin as ssPlugin, noPurchase as ssNoPurchaseRaw, brandMode as ssBrandMode, tenantSlug as ssTenantSlug } from "./platform.js";
import {
  Home, Briefcase, DollarSign, User, Menu, Bell, X, ChevronRight, ChevronLeft, ChevronDown, Plus, MoreHorizontal,
  Map as MapIcon, List, MapPin, Navigation, Search, Filter, Clock, Calendar, Flame, Star, Route, ArrowRight,
  Camera, PenLine, Mic, FileText, Download, Printer, Upload, CheckCircle2, XCircle, AlertTriangle, AlertCircle, Info, Check, Circle, CircleDot, PauseCircle,
  TrendingUp, TrendingDown, Receipt, Wallet, CreditCard, Banknote, ArrowUpRight, ArrowDownRight, ExternalLink,
  Settings, GraduationCap, FileSignature, HelpCircle, LogOut, Shield, ShieldAlert, Lock, BookOpen, Award, FolderOpen,
  Phone, MessageSquare, Mail, Headphones, Siren, LifeBuoy, PlayCircle,
  Wifi, WifiOff, Battery, Signal, Zap, Sparkles, Sun, Moon,
  Eye, EyeOff, Edit3, Trash2, Send, Paperclip, Image as ImageIcon, ChevronUp, ArrowUp, ArrowDown, RotateCcw, Sliders, Crosshair, Compass,
  QrCode, ScanLine,
  Gift, Share2, Copy, // [server-referrals-screen-2026-10-08] ReferralsScreen
  ScanFace // [field-view-2026-09-07] Field View row-action icon (Jobs list card + map claim bar)
} from "lucide-react";
// __PHASE4_REALIZED__ — do not remove; idempotency marker for realize.py
// [mobile-web-cle-2026-07-20] synced from ~/Developer/strikeserve-mobile/src/server-app.jsx for mobile.californialegalexpress.com Android web deploy (re-synced 2026-07-21 with [tenant-brand-2026-07-21])
// ═══════════════════════════════════════════════════════════════════════
// API CLIENT — borrowed from cle-frontend, simplified for mobile
// ═══════════════════════════════════════════════════════════════════════
// Runtime API URL resolver — no build step. Priority:
//   1) window.__SS_API_URL (set by capacitor-bootstrap.js or host shell)
//   2) localStorage "ss_api_url_override" (QA testing)
//   3) production default
// Resolved once at module load; restart the app to pick up a new override.
/* [server-parity-web-2026-10-05] Pinned to the backend's real domain (netlify.toml
   connect-src already allows it); the raw Railway host is being migrated off. */
const SS_API_URL_DEFAULT = "https://api.strikeserve.com";
// [audit-fixes-2026-10-15] M-api-override: the localStorage override used to redirect
// EVERY API call — Bearer JWT included — to any host a script or XSS wrote into
// localStorage. It is now honored only on a non-production bundle (package.json
// `dev` defines NODE_ENV=development; build.sh defines "production") or when the
// override's host is one of ours. Anything else is ignored with a console.warn.
const SS_API_HOST_ALLOWLIST = ["api.strikeserve.com", "api.californialegalexpress.com", "localhost"];
function ssIsProductionBuild() {
  try { return process.env.NODE_ENV === "production"; } catch (_) { return true; } // no define → treat as production
}
function ssApiOverrideAllowed(o) {
  if (!ssIsProductionBuild()) return true;
  try {
    const u = new URL(String(o));
    const host = String(u.hostname || "").toLowerCase();
    if (!SS_API_HOST_ALLOWLIST.includes(host)) return false;
    return u.protocol === "https:" || (u.protocol === "http:" && host === "localhost");
  } catch (_) { return false; }
}
function resolveApiUrl() {
  try {
    if (typeof window !== "undefined" && typeof window.__SS_API_URL === "string" && window.__SS_API_URL) {
      return window.__SS_API_URL;
    }
  } catch (_) { /* ignore */ }
  try {
    if (typeof localStorage !== "undefined") {
      const o = localStorage.getItem("ss_api_url_override");
      if (o && ssApiOverrideAllowed(o)) return o;
      if (o) { try { console.warn("[api] ignoring ss_api_url_override (host not allowed on a production build):", o); } catch (_) {} } // [audit-fixes-2026-10-15] M-api-override
    }
  } catch (_) { /* ignore */ }
  return SS_API_URL_DEFAULT;
}
const API_URL = resolveApiUrl();
const SS_JWT_KEY = "ss_jwt";
const SS_USER_KEY = "ss_user";

// [tenant-brand-2026-07-21] Tenant branding by domain. Web builds served on an
// agency domain (e.g. mobile.californialegalexpress.com) fetch the public
// /api/tenant/config (same source as the web admin app) and wear that tenant's
// name/logo/tagline on the brand chrome (login, splash, title, About). Native
// shells and unknown domains keep StrikeServe defaults. Contractual copy
// (contractor agreement, payouts, consents, 1099s) intentionally stays
// StrikeServe — the platform is the legal counterparty.
const SS_BRAND_DEFAULT = { name: "StrikeServe", tagline: "National Legal Technology Platform", logoUrl: "ss-shield.png", isDefault: true };
let __ssBrand = { ...SS_BRAND_DEFAULT };
const __ssBrandListeners = new Set();
function ssGetBrand() { return __ssBrand; }
/* [work-log-wizard-2026-09-20] Tenant feature flags. `tenants.features` (JSONB)
   has existed since migrations/20260906_001_eservice.sql and already ships on the
   SAME /api/tenant/config payload this file fetches for branding — SAFE_COLUMNS
   in routes/tenantConfig.js:63 includes it. The field app was reading the branding
   half and discarding the rest. Nothing new on the wire.

   Module scope beside __ssBrand, not AppCtx, so a pre-login screen can read it and
   the lifetime matches the brand's. Coercion matches the convention already used
   in cle-frontend/app.jsx:24174 (`String(...) === 'true'`) — the JSONB round-trips
   as a string on some paths, so a bare truthiness test is not safe. */
let __ssFeatures = {};
function ssFeature(name) {
  return String(__ssFeatures && __ssFeatures[name]) === "true";
}
(function ssLoadTenantBrand() {
  try {
    if (typeof window === "undefined" || typeof fetch === "undefined") return;
    /* [server-parity-web-2026-10-05] Native shells used to skip this entirely. A
       tenant-branded shell now declares its slug via window.__SS_TENANT (set in
       capacitor-bootstrap.js) and we fetch that tenant's config. GET /api/tenant/config
       accepts the slug as ?slug= and as the CORS-allowed X-Tenant-Slug header (a bare
       Origin of `capacitor://localhost` would otherwise resolve to the platform
       tenant); both are sent, and the reply is only applied when its slug matches
       what was asked for. A platform-branded native shell (no slug) keeps the
       StrikeServe defaults with no network call, as before. */
    const mode = ssBrandMode();
    if (mode === "platform") return;
    const wantSlug = mode === "tenant" ? ssTenantSlug() : "";
    const url = API_URL + "/api/tenant/config" + (wantSlug ? ("?slug=" + encodeURIComponent(wantSlug)) : "");
    fetch(url, wantSlug ? { headers: { "X-Tenant-Slug": wantSlug } } : undefined)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!j || j._fallback) return;
        if (wantSlug && String(j.slug || "").toLowerCase() !== wantSlug.toLowerCase()) {
          console.warn("[tenant-brand] native shell asked for tenant '" + wantSlug + "' but the API answered '" + (j.slug || "?") + "' — keeping platform defaults");
          return;
        }
        const b = j.branding || {};
        __ssBrand = {
          name: j.brand_name || j.name || SS_BRAND_DEFAULT.name,
          tagline: b.tagline || SS_BRAND_DEFAULT.tagline,
          logoUrl: b.logo_url || SS_BRAND_DEFAULT.logoUrl,
          isDefault: (j.brand_name || j.name || "StrikeServe") === "StrikeServe",
        };
        try { document.title = __ssBrand.name; } catch (_) {}
        try {
          if (b.favicon_url) {
            let l = document.querySelector('link[rel="icon"]');
            if (!l) { l = document.createElement("link"); l.rel = "icon"; document.head.appendChild(l); }
            l.href = b.favicon_url;
          }
        } catch (_) {}
        // [work-log-wizard-2026-09-20] Same payload, previously discarded.
        __ssFeatures = (j.features && typeof j.features === "object") ? j.features : {};
        __ssBrandListeners.forEach((fn) => { try { fn(__ssBrand); } catch (_) {} });
      })
      .catch(() => {});
  } catch (_) {}
})();
function useBrand() {
  const [b, setB] = useState(__ssBrand);
  useEffect(() => {
    __ssBrandListeners.add(setB);
    return () => __ssBrandListeners.delete(setB);
  }, []);
  return b;
}
/* [work-log-wizard-2026-09-20] Is the new capture wizard on for this session?
   Subscribes through useBrand so the component re-renders when /api/tenant/config
   resolves — same listener set, no second mechanism.

   The localStorage override is the stage-1 rollout lever from the spec ("enabled
   for Montana's own account first"): it needs no backend write and no deploy.
   Stage 2 sets tenants.features.work_log_wizard = 'true' per tenant. Native builds
   short-circuit the tenant-config fetch entirely (see ssLoadTenantBrand), so on
   iOS the override is currently the ONLY path — that is why it is not dev-only. */
function useWorkLogWizard() {
  useBrand();
  try {
    if (localStorage.getItem("ss_work_log_wizard") === "1") return true;
    if (localStorage.getItem("ss_work_log_wizard") === "0") return false;
  } catch (_) { /* private mode / blocked storage — fall through to the flag */ }
  return ssFeature("work_log_wizard");
}
// Login-card logo + wordmark, tenant-aware. StrikeServe keeps the classic
// split wordmark; agency tenants get their logo + plain name + tagline.
function BrandLogo() {
  const brand = useBrand();
  return (
    <img src={brand.logoUrl} alt={brand.name} width="76"
         style={{ width: 76, height: "auto", display: "block", marginTop: 8, marginBottom: 12 }} />
  );
}
function BrandWordmark({ deep, gold, muted }) {
  const brand = useBrand();
  return (
    <>
      <div style={{
        fontFamily: "Fraunces, Georgia, serif", fontSize: 26, fontWeight: 700, color: deep,
        textAlign: "center", letterSpacing: "-0.01em",
      }}>
        {brand.isDefault
          ? (<>Strike<span style={{ fontStyle: "italic", color: gold, fontWeight: 500 }}>Serve</span></>)
          : brand.name}
      </div>
      <div style={{
        fontSize: 11, color: muted, textAlign: "center", marginTop: 4, marginBottom: 22, lineHeight: 1.4,
      }}>
        {brand.tagline}
      </div>
    </>
  );
}

// Support phone — sourced from env / runtime override. NO fake fallback ships.
// Reads (in priority order):
//   1) import.meta.env.VITE_SUPPORT_PHONE  (Vite/build-time)
//   2) window.__SS_SUPPORT_PHONE           (native shell or QA override)
//   3) "" (empty) — callers MUST hide the "Call support" affordance when this is
//      empty rather than dialing a placeholder number. Apple rejects apps that
//      dial non-routable numbers, and a fake support phone is worse than no phone.
// Used to centralise tel: deep links so we don't sprinkle the placeholder number around.
function resolveSupportPhone() {
  try {
    // eslint-disable-next-line no-undef
    const viaImportMeta = typeof import.meta !== "undefined" && import.meta && import.meta.env && import.meta.env.VITE_SUPPORT_PHONE;
    if (viaImportMeta) return String(viaImportMeta);
  } catch (_) { /* ignore — esbuild may strip import.meta in some targets */ }
  try {
    if (typeof window !== "undefined" && window.__SS_SUPPORT_PHONE) return String(window.__SS_SUPPORT_PHONE);
  } catch (_) { /* ignore */ }
  return "";
}
const SUPPORT_PHONE = resolveSupportPhone();
// Pretty-print SUPPORT_PHONE for display labels: "9165550100" -> "(916) 555-0100".
// Falls back to the raw string when input isn't exactly 10 digits.
function formatSupportPhone(raw) {
  const d = String(raw || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith("1")) {
    const r = d.slice(1);
    return `(${r.slice(0, 3)}) ${r.slice(3, 6)}-${r.slice(6)}`;
  }
  return String(raw || "");
}

// Marketing fallbacks. The in-app Billing screen now drives subscribe/manage
// through authenticated backend endpoints (Stripe Checkout + Customer Portal)
// and opens the returned hosted URL in-app via the Browser plugin. These bare
// URLs are only used as a last resort if the backend can't mint a hosted URL.
const SS_SUBSCRIBE_URL = "https://www.strikeserve.com/pricing";
const SS_BILLING_URL = "https://www.strikeserve.com/billing";   // for managing existing subs

// [ios-no-purchase-2026-10-05] App Store Guideline 3.1.1: the iOS app shows no way to buy, upgrade,
// subscribe or manage billing. Existing plans still display (read-only) and all
// paid features keep working for subscribers. Web and Android are unchanged.
// [server-parity-web-2026-10-05] ported from strikeserve-mobile; the platform
// test lives in src/platform.js so it cannot drift from the other probes.
function ssNoPurchase() {
  try { return ssNoPurchaseRaw(); } catch (_) { return false; }
}

/* [sms-compliance-2026-10-05] [server-parity-web-2026-10-05] One SMS consent CTA for every
   registration path (cta-v2-2026-09-22 — the same optional CTA the web admin app shows),
   plus the audit metadata the backend stores beside the consent flag. The source
   names the surface so a consent record can be traced to the build that collected it. */
const SS_SMS_CONSENT_TEXT_VERSION = "cta-v2-2026-09-22";
function ssSmsConsentSource(ctx) {
  const surface = ssIsNative() ? ("strikeserve-" + ssPlatform() + "-app") : "strikeserve-web-app";
  return surface + ":" + ctx;
}
function SmsConsentCta({ style }) {
  const link = (href, label) => (
    <a href="#" onClick={(e) => { e.preventDefault(); openExternal(href); }} style={{ color: "inherit", textDecoration: "underline" }}>{label}</a>
  );
  return (
    <span style={style}>
      I agree to receive SMS text messages from StrikeServe about service request updates, job assignments,
      delivery confirmations, account alerts, and customer support. Message frequency varies. Message and data
      rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase or
      registration.{" "}{link("https://strikeserve.com/sms-terms", "SMS Terms")}{" "}&middot;{" "}
      {link("https://strikeserve.com/privacy", "Privacy Policy")}{" "}&middot;{" "}
      {link("https://strikeserve.com/terms", "Terms of Service")}
    </span>
  );
}

// Open external URLs via the Capacitor Browser plugin when available — gives
// us a real SFSafariViewController on iOS (in-app browser, no jarring app
// switch, shared cookies for the marketing/billing site). Falls back to
// window.open with "_system" for older Capacitor versions and "_blank" in
// plain web. Always swallow errors so a billing handoff never throws.
async function openExternal(url) {
  try {
    const Browser = ssPlugin("Browser"); // [server-parity-web-2026-10-05]
    if (Browser) {
      await Browser.open({ url });
      return;
    }
  } catch (_) { /* fall through */ }
  try { window.open(url, "_system"); } catch (_) { window.open(url, "_blank", "noopener"); }
}

/* [workflow-audit-2026-10-28 V-36] SMS JOB OFFER LINKS. The backend texts outside servers a
   link {FRONTEND}/offer/<token> (contract C1). The public accept page lives on the ADMIN
   frontend, not in this app. If this app is handed such a link (web: someone opens
   mobile.strikeserve.com/offer/<token> -- handled first by the inline script in
   www/index.html, since relative assets do not load under /offer/; native: a universal
   link / appUrlOpen), send the
   person to the admin frontend's offer page in the browser. Host mapping: a CLE domain or the
   CLE tenant shell -> app.californialegalexpress.com, everything else -> app.strikeserve.com;
   window.__SS_ADMIN_URL overrides. Never redirects to its own origin (no loop). */
function ssOfferTokenFromUrl(u) {
  try {
    const path = new URL(String(u || ""), "https://x.invalid").pathname || "";
    const m = path.match(/^\/offer\/([A-Za-z0-9_-]{8,200})\/?$/);
    return m ? m[1] : "";
  } catch (_) { return ""; }
}
function ssAdminFrontendUrl() {
  try {
    if (typeof window !== "undefined" && typeof window.__SS_ADMIN_URL === "string" && /^https:\/\//.test(window.__SS_ADMIN_URL)) return window.__SS_ADMIN_URL.replace(/\/+$/, "");
  } catch (_) {}
  let cle = false;
  try { cle = /californialegalexpress|calegalexpress/i.test((window.location && window.location.hostname) || "") || /^(cle|california-legal-express)$/i.test(ssTenantSlug() || ""); } catch (_) {}
  return cle ? "https://app.californialegalexpress.com" : "https://app.strikeserve.com";
}
function ssOfferPageUrl(token) { return ssAdminFrontendUrl() + "/offer/" + encodeURIComponent(token); }
(function ssHandleOfferLinks() {
  try {
    if (typeof window === "undefined") return;
    if (!ssIsNative()) {
      const tok = ssOfferTokenFromUrl(window.location.href);
      if (!tok) return;
      const target = ssOfferPageUrl(tok);
      if (new URL(target).origin !== window.location.origin) window.location.replace(target);
      return;
    }
    const App = ssPlugin("App");
    if (!App) return;
    const open = (u) => { const tok = ssOfferTokenFromUrl(u); if (tok) openExternal(ssOfferPageUrl(tok)); };
    if (typeof App.addListener === "function") App.addListener("appUrlOpen", (ev) => open(ev && ev.url));
    if (typeof App.getLaunchUrl === "function") {
      Promise.resolve(App.getLaunchUrl()).then((r) => open(r && r.url)).catch(() => {});
    }
  } catch (_) { /* never block boot */ }
})();

/* [blob-xss-field-2026-09-19] ACTIVE-CONTENT GUARD — upload side and view side.
   Context: the stored-XSS defect fixed in the admin app under
   [upload-types-2026-09-18] was a SAME-ORIGIN blob: URL built from fetched document
   bytes and handed to <iframe src>/window.open — a blob: inherits the creating
   document's origin, so an uploaded SVG (XML, can carry <script>) executed as the app
   with the session JWT in reach. THIS APP HAS NO SUCH PATH: it never calls
   URL.createObjectURL, and View/Download hand the presigned S3 URL straight to the
   Capacitor Browser (SFSafariViewController) or window.open, which is a foreign
   origin. So the admin bug is not reproduced here.
   What this app DOES do is FEED that pipeline. Every document/evidence picker below
   uses accept="image/*", and image/svg+xml matches image/*, so a field user can put
   an SVG into a job that an admin, agency or client viewer opens later. Two cheap,
   independent mitigations:
   1. UPLOAD — declare an active type as application/octet-stream on BOTH the presign
      request and the S3 PUT header (they must match or the signature fails). S3 then
      stores and serves the object as octet-stream permanently, so no downstream
      viewer in any app can render it as script. This is the one that matters.
   2. VIEW — never hand an active-typed document to the in-app browser. Writing the
      bytes to disk via the Filesystem plugin stays allowed; that cannot execute.
   PDFs and genuine raster images are untouched — opening a PDF in the field is the
   entire point of this screen. */
const SS_ACTIVE_UPLOAD_EXT = /\.(svg|svgz|html?|xhtml|xml|xsl|xslt|mht|mhtml)$/i;
const SS_ACTIVE_UPLOAD_MIME = /^(image\/svg(\+xml)?|text\/html|text\/xml|application\/xhtml\+xml|application\/xml|application\/xslt?\+xml|message\/rfc822|multipart\/related)$/i;
function ssIsActiveUpload(fileName, mime) {
  const n = String(fileName || "");
  const m = String(mime || "").toLowerCase().split(";")[0].trim();
  return SS_ACTIVE_UPLOAD_EXT.test(n) || SS_ACTIVE_UPLOAD_MIME.test(m);
}
/* Coerce an active type to octet-stream; pass everything else through untouched. */
function ssSafeUploadMime(fileName, mime) {
  return ssIsActiveUpload(fileName, mime) ? "application/octet-stream" : mime;
}
/* A document row from GET /api/server/jobs/:id/documents. `kind` is the uppercased
   mime SUBTYPE ("PDF", "SVG+XML"), not a full mime, so it needs its own test. */
function ssIsActiveDoc(d) {
  const kind = String((d && d.kind) || "").toLowerCase();
  return ssIsActiveUpload((d && d.filename) || "", "")
    || /^(svg|svg\+xml|html|xhtml\+xml|xml|xslt?\+xml|xsl)$/.test(kind);
}
const SS_ACTIVE_DOC_MSG = "This file type can't be previewed in the app — open it from the web dashboard.";

// ── In-app subscription handoffs ────────────────────────────────────────
// Auth-required Stripe URLs must be minted by the backend FIRST (the api
// helper attaches the Bearer token), THEN handed to the in-app browser.
// Opening the bare endpoint in SFSafariViewController would send no auth and
// fail — same pattern as the Stripe connect-link fix in AccountScreen.
//
// ssStartCheckout: POST /api/subscriptions/create -> { url } (Stripe Checkout
//   hosted page, 30-day trial auto-applied first time). Falls back to the
//   marketing pricing page if the backend can't mint a session.
async function ssStartCheckout(plan) {
  if (ssNoPurchase()) return true; // [ios-no-purchase-2026-10-05]
  try {
    const r = await api.post("/api/subscriptions/create", { plan: plan || "pro" });
    if (r && r.url) { await openExternal(r.url); return true; }
  } catch (_) { /* fall through to marketing pricing */ }
  await openExternal(SS_SUBSCRIBE_URL);
  return false;
}

// ssOpenBillingPortal: POST /api/subscriptions/portal -> { url } (Stripe
//   Customer Portal on the StrikeServe account — change card, view invoices,
//   cancel). Falls back to the marketing billing page if no portal session can
//   be created (e.g. the Stripe customer id isn't on the users row yet).
//   NOTE: the old /api/billing/billing-portal mount is disabled — it ran on the
//   requester's tenant Stripe account, not StrikeServe's. Subscriptions are a
//   StrikeServe product, so the portal must open on the StrikeServe account.
async function ssOpenBillingPortal() {
  if (ssNoPurchase()) return true; // [ios-no-purchase-2026-10-05]
  try {
    const r = await api.post("/api/subscriptions/portal", {});
    if (r && r.url) { await openExternal(r.url); return true; }
  } catch (_) { /* fall through to marketing billing */ }
  await openExternal(SS_BILLING_URL);
  return false;
}

// ═══════════════════════════════════════════════════════════════════════
// QR SCANNER LIBRARY LOADER (html5-qrcode)
// ═══════════════════════════════════════════════════════════════════════
// The native @capacitor-mlkit/barcode-scanning plugin isn't reachable in this
// build (its pod/method throws UNIMPLEMENTED), so — exactly like the web app —
// we use the pure-JS html5-qrcode library over getUserMedia. It works in the
// iOS WKWebView because camera permission is already granted (Camera plugin +
// NSCameraUsageDescription). Injects the unpkg script once and memoizes the
// promise; resolves immediately if window.Html5Qrcode is already present.
const loadQrLib = (() => {
  let p = null;
  return () => {
    if (typeof window !== "undefined" && window.Html5Qrcode) return Promise.resolve();
    if (p) return p;
    p = new Promise((res, rej) => {
      const s = document.createElement("script");
      s.src = "https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js";
      s.onload = () => res();
      s.onerror = () => { p = null; rej(new Error("Failed to load scanner library")); };
      document.head.appendChild(s);
    });
    return p;
  };
})();

export const api = {
  _jwt: typeof localStorage !== "undefined" ? localStorage.getItem(SS_JWT_KEY) || "" : "",
  _csrf: null,
  _csrfFetched: false,
  setToken(t) {
    this._jwt = t || "";
    if (t) localStorage.setItem(SS_JWT_KEY, t);
    else localStorage.removeItem(SS_JWT_KEY);
    try {
      // Mirror to native iOS UserDefaults so Apple Watch app can read the JWT via KVO
      const Preferences = ssPlugin("Preferences"); // [server-parity-web-2026-10-05]
      if (Preferences) {
        if (t) {
          Preferences.set({ key: SS_JWT_KEY, value: t });
        } else {
          Preferences.remove({ key: SS_JWT_KEY });
        }
      }
    } catch (_) { /* Capacitor or plugin not available — phone-only path */ }
  },
  headers() {
    const h = { "Content-Type": "application/json" };
    if (this._jwt) h["Authorization"] = "Bearer " + this._jwt;
    if (this._csrf) h["X-CSRF-Token"] = this._csrf;
    return h;
  },
  async ensureCsrf() {
    // Backend sets an httpOnly cle_token cookie on login. Once a cookie exists, all mutating
    // requests require a matching CSRF header (double-submit cookie pattern). We fetch the
    // CSRF token once and reuse it; CapacitorHttp persists the matching cle_csrf cookie.
    if (this._csrf) return this._csrf;
    try {
      const r = await fetch(API_URL + "/api/auth/csrf", {
        method: "GET",
        headers: this._jwt ? { "Authorization": "Bearer " + this._jwt } : {},
      });
      if (r.ok) {
        const d = await r.json().catch(() => ({}));
        this._csrf = d.csrfToken || d.token || null;
        return this._csrf;
      }
    } catch (e) { console.warn("[api] csrf fetch failed:", e.message); }
    return null;
  },
  async req(method, path, body) {
    // Mutating verbs need a CSRF token if a cookie is present (backend enforces).
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
      await this.ensureCsrf();
    }
    /* [srvapp-2026-10-16] A 401 only means "session expired" when we actually sent a
       session. Without a JWT it is an ordinary refusal and falls through to the
       !r.ok branch, which throws the backend's own error text. */
    const _ssSentJwt = !!this._jwt;
    const r = await fetch(API_URL + path, {
      method,
      headers: this.headers(),
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (r.status === 401 && _ssSentJwt) { // [srvapp-2026-10-16]
      this.setToken("");
      this._csrf = null;
      localStorage.removeItem(SS_USER_KEY);
      window.dispatchEvent(new Event("ss-session-expired"));
      throw new Error("Session expired — please sign in again");
    }
    if (r.status === 403) {
      // Possible stale CSRF token — refresh and retry once
      const errBody = await r.clone().json().catch(() => ({}));
      if (errBody && (errBody.code === "PENDING_APPROVAL" || /pending approval/i.test(String(errBody.error || "")))) ssNotifyAccountPending(); // [srvapp-2026-10-16]
      if ((errBody.error || "").toLowerCase().includes("csrf")) {
        this._csrf = null;
        await this.ensureCsrf();
        const retry = await fetch(API_URL + path, {
          method,
          headers: this.headers(),
          ...(body ? { body: JSON.stringify(body) } : {}),
        });
        if (retry.ok) {
          const out2 = await retry.json().catch(() => ({}));
          ssTicketNoteAttempt(method, path, body, out2); // [support-tickets-2026-10-08]
          ssDoorTagNoteAttempt(method, path, body, out2); // [door-tags-server-2026-10-22]
          return out2;
        }
        const e2 = await retry.json().catch(() => ({}));
        // Attach the parsed error body + status so callers can branch on
        // structured codes (e.g. claim blocks return { code, error }).
        const err2 = new Error(e2.error || retry.statusText || "Request failed");
        err2.body = e2; err2.status = retry.status; err2.code = e2 && e2.code;
        throw err2;
      }
    }
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      // Attach the parsed error body + status so callers can branch on
      // structured codes (the web client reads e.body.code / e.body.error).
      const ex = new Error(err.error || r.statusText || "Request failed");
      ex.body = err; ex.status = r.status; ex.code = err && err.code;
      throw ex;
    }
    const out = await r.json().catch(() => ({}));
    /* [support-tickets-2026-10-08] After a failed attempt (any capture path: attempt
       sheet, work-log wizard, Field Command one-tap) offer "Need help with this job?".
       Pure event dispatch, wrapped; it can never affect the save. */
    ssTicketNoteAttempt(method, path, body, out);
    ssDoorTagNoteAttempt(method, path, body, out); // [door-tags-server-2026-10-22] "Did you post the door tag?" after a non-served attempt
    return out;
  },
  get(p) { return this.req("GET", p); },
  post(p, b) { return this.req("POST", p, b); },
  patch(p, b) { return this.req("PATCH", p, b); },
  del(p) { return this.req("DELETE", p); },
  async login(email, password, rememberMe) {
    /* [srvapp-2026-10-16] own fetch (mirrors cle-frontend api.login, login-errors-2026-10-16):
       this.req turns every 401 into "Session expired" and fires ss-session-expired, but
       NO_ACCOUNT / WRONG_PASSWORD are 401 sign-in refusals, not an expired session.
       The backend refusal body is {error:"<message> (Error code: X)", code, message}. */
    try { await this.ensureCsrf(); } catch (_) {}
    let _lr;
    try {
      _lr = await fetch(API_URL + "/api/auth/login", {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({ email, password, rememberMe: !!rememberMe }),
      });
    } catch (_) {
      const _ne = new Error("Could not reach StrikeServe. Check your connection and try again. (Error code: NETWORK)");
      _ne.code = "NETWORK";
      throw _ne;
    }
    const r = await _lr.json().catch(() => ({}));
    if (!_lr.ok) {
      const _msg = (r && typeof r.error === "string" && r.error.trim()) ? r.error : ("Sign-in failed (Error code: HTTP_" + _lr.status + ")");
      const _le = new Error(_msg);
      _le.status = _lr.status; _le.body = r; _le.code = (r && r.code) || ("HTTP_" + _lr.status);
      throw _le;
    }
    if (r?.token) {
      /* [user-normalizer-2026-09-20] Normalize BEFORE caching and before this block's
         `return r.user`: the app boots optimistically straight out of this cached
         object, so a raw snake_case payload here means the first paint after sign-in
         reads the wrong keys. */
      r.user = this._normalizeUser(r.user);
      this.setToken(r.token);
      this._lastRefresh = r.refreshToken || ""; /* [biometric-login-2026-10-11] memory only; Face ID stores it in the Keychain */
      localStorage.setItem(SS_USER_KEY, JSON.stringify(r.user));
      // Backend bundles the user's first batch of jobs in the login response
      // (2026-06-05 server.js mod). When present, seed both the localStorage
      // cache and the prefetch promise so AppInner mounts with real data on
      // its first paint instead of waiting for a separate /api/jobs RTT.
      if (Array.isArray(r.initialJobs)) {
        /* [fc-crash-fix-2026-09-11] ROOT CAUSE OF THE iPad WHITE SCREEN.
           `r.initialJobs` are RAW backend rows (snake_case, `attempts` = the ARRAY of
           attempt records). They were written to ss_jobs_cache un-normalized, and
           AppInner hydrates `liveJobs` straight from that cache with no normalizer —
           so the first paint after sign-in rendered raw rows. ListJobCard renders
           `{job.attempts}/4 attempts`; with an array there React walks it and throws
           "Objects are not valid as a React child (object with keys {id, gps, date,
           notes, photos, result, locType})" — React error #31 — which unmounts the
           whole tree. It only showed on the tablet shell because JobsScreen's tablet
           branch mounts ListView ALWAYS (beside the map); the phone shell defaults to
           the map view, so ListJobCard never mounted inside the ~1s window before
           reload() replaced the cache with normalized jobs.
           Cache what reload() caches: normalized jobs. The prefetch promise stays RAW
           because AppInner maps _normalizeJob over it itself — normalizing here too
           would double-normalize and zero every attempt count. */
        const _fcNormalizedInitial = r.initialJobs.map(_normalizeJob);
        try { localStorage.setItem("ss_jobs_cache", JSON.stringify(_fcNormalizedInitial)); } catch (_) {}
        try { writeJobsCacheNative(_fcNormalizedInitial); } catch (_) {}
        try { window.__ssJobsPrefetch = Promise.resolve(r.initialJobs); } catch (_) {}
      }
      return r.user;
    }
    return null;
  },
  /* [user-normalizer-2026-09-20] Structural fix for the class of bug that produced
     both the coverage breakage and the notification-prefs one: this app reads the user
     object with camelCase keys in some places and snake_case in others, while
     GET /api/auth/me returns BOTH shapes (explicit camelCase aliases plus the raw row
     via a spread) and other payloads return only one. Ported from cle-frontend's
     api._normalizeUser, with one deliberate difference: that version DELETES the
     snake_case key after aliasing. Here the mapping is ADDITIVE, because call sites in
     this file still read snake_case directly (ssAvatarUrl reads user.avatar_url,
     ssAppStatus reads user.application_status) and deleting the originals would break
     them. Never overwrites a camelCase value the API already supplied. */
  _normalizeUser(u) {
    if (!u || typeof u !== "object") return u;
    const m = { ...u };
    const alias = (snake, camel) => {
      if (u[snake] !== undefined && m[camel] === undefined) m[camel] = u[snake];
    };
    alias("application_status",    "applicationStatus");
    alias("reg_number",            "regNumber");
    alias("mfa_enabled",           "mfaEnabled");
    alias("created_at",            "createdAt");
    alias("last_login",            "lastLogin");
    alias("service_counties",      "serviceCounties");
    alias("notif_prefs",           "notifPrefs");
    alias("avatar_url",            "avatarUrl");
    alias("account_number",        "accountNumber");
    alias("stripe_connect_id",     "stripeConnectId");
    alias("stripe_connect_status", "stripeConnectStatus");
    alias("subscription_status",   "subscriptionStatus");
    alias("coverage_lat",          "coverageLat");
    alias("coverage_lng",          "coverageLng");
    alias("coverage_radius",       "coverageRadius");
    alias("home_address",          "homeAddress"); // [server-zone-30-2026-10-18]
    alias("home_lat",              "homeLat");
    alias("home_lng",              "homeLng");
    return m;
  },
  async me() { return this._normalizeUser(await this.get("/api/auth/me")); },
  logout() {
    this.setToken("");
    this._csrf = null;
    this._lastRefresh = ""; /* [recon-server-web-2026-10-21] drop the in-memory refresh token too */
    localStorage.removeItem(SS_USER_KEY);
  },
};

// ═══════════════════════════════════════════════════════════════════════
// [server-parity-web-2026-10-05] PDF FETCH + IN-APP DOCUMENT VIEWER + PRINT
// ─────────────────────────────────────────────────────────────────────
// Three backend routes hand back raw PDF bytes behind the Bearer token
// (print-packet, briefing-sheet.pdf, pos/print-merged), so they cannot be
// opened by URL. ssFetchPdfBlob pulls the bytes with the api headers and
// wraps them in an application/pdf Blob. The viewer below renders a PDF or
// image full-screen in an <iframe>/<img> with Print / Open-in-browser.
//
// [blob-xss-field-2026-09-19] NOTE ON blob: URLs. The ACTIVE-CONTENT GUARD above
// forbids handing attacker-supplied bytes to a same-origin blob: URL. Every blob
// created here is either (a) a PDF the backend rendered itself (print packet,
// briefing sheet, merged POS, generated form) — the bytes are checked for the
// %PDF- magic and typed application/pdf, so an SVG/HTML payload cannot ride
// through — or (b) nothing: user-uploaded job documents are shown by their
// presigned S3 URL (foreign origin) inside the iframe, never re-wrapped in a
// blob, and active types are refused by ssIsActiveDoc before reaching here.
// ═══════════════════════════════════════════════════════════════════════
async function ssFetchPdfBlob(path, { method = "GET", body } = {}) {
  if (method !== "GET") await api.ensureCsrf();
  const r = await fetch(API_URL + path, {
    method,
    headers: api.headers(),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (r.status === 401) {
    api.setToken(""); api._csrf = null;
    try { localStorage.removeItem(SS_USER_KEY); } catch (_) {}
    try { window.dispatchEvent(new Event("ss-session-expired")); } catch (_) {}
    throw new Error("Session expired — please sign in again");
  }
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    const e = new Error(j.error || r.statusText || "Request failed");
    e.body = j; e.status = r.status; e.code = j && j.code;
    /* Preflight failures carry the issue list — surface the first message so the
       toast says WHAT is wrong instead of "POS preflight failed". */
    if (j && j.preflight && Array.isArray(j.preflight.issues) && j.preflight.issues.length) {
      const first = j.preflight.issues.find((i) => i.severity === "error") || j.preflight.issues[0];
      if (first && first.message) e.message = (j.error || "Preflight failed") + ": " + first.message;
    }
    throw e;
  }
  const buf = await r.arrayBuffer();
  const head = new Uint8Array(buf.slice(0, 5));
  const magic = String.fromCharCode.apply(null, Array.from(head));
  if (magic !== "%PDF-") throw new Error("The server did not return a PDF");
  return new Blob([buf], { type: "application/pdf" });
}

/* Open the in-app viewer from anywhere (deep components don't need threaded
   state). detail: { title, blob?, url?, kind: "pdf"|"image", print?: bool,
   externalUrl?: string, filename?: string }. */
function ssOpenDocViewer(detail) {
  try { window.dispatchEvent(new CustomEvent("ss-doc-view", { detail: detail || {} })); } catch (_) {}
}
function ssToast(kind, text) {
  try { window.dispatchEvent(new CustomEvent("ss-toast", { detail: { kind, text } })); } catch (_) {}
}

/* Mounted once per shell beside GlobalToast. Owns the blob URL lifecycle. */
function DocViewerHost() {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    const on = (e) => {
      const d = (e && e.detail) || {};
      let url = d.url || "";
      let revoke = null;
      if (d.blob) {
        try { url = URL.createObjectURL(d.blob); revoke = url; } catch (_) { url = ""; }
      }
      if (!url) { ssToast("err", "Nothing to show"); return; }
      setDoc((prev) => {
        if (prev && prev.revoke) { try { URL.revokeObjectURL(prev.revoke); } catch (_) {} }
        return { ...d, url, revoke, kind: d.kind || "pdf", openedAt: Date.now() };
      });
    };
    window.addEventListener("ss-doc-view", on);
    return () => window.removeEventListener("ss-doc-view", on);
  }, []);
  const close = React.useCallback(() => {
    setDoc((prev) => {
      if (prev && prev.revoke) { setTimeout(() => { try { URL.revokeObjectURL(prev.revoke); } catch (_) {} }, 1500); }
      return null;
    });
  }, []);
  if (!doc) return null;
  return <DocViewerSheet doc={doc} onClose={close} />;
}

function DocViewerSheet({ doc, onClose }) {
  const frameRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [printErr, setPrintErr] = useState("");
  const isBlob = /^blob:/i.test(String(doc.url || ""));
  const native = ssIsNative();
  const isPdf = doc.kind !== "image";

  function doPrint() {
    setPrintErr("");
    try {
      const w = frameRef.current && frameRef.current.contentWindow;
      if (w && typeof w.print === "function") { w.focus(); w.print(); return; }
    } catch (_) { /* cross-origin frame (presigned S3) — fall through */ }
    try {
      /* Cross-origin or sandboxed frame: open in a tab; the browser's own viewer prints. */
      const w2 = window.open(doc.url, "_blank", "noopener");
      if (!w2) setPrintErr("Pop-up blocked — allow pop-ups for this site, then press Print again.");
    } catch (_) { setPrintErr("Printing is not available here. Use Open in browser."); }
  }
  function doOpenExternal() {
    const ext = doc.externalUrl || (!isBlob ? doc.url : "");
    if (ext) { openExternal(ext); return; }
    /* blob: cannot leave this document. On web a new tab works; on native there is
       no browser that can resolve it, so the button is hidden below. */
    try { window.open(doc.url, "_blank"); } catch (_) {}
  }
  function doDownload() {
    try {
      const a = document.createElement("a");
      a.href = doc.url; a.download = doc.filename || (isPdf ? "document.pdf" : "image");
      a.rel = "noopener"; a.style.display = "none";
      document.body.appendChild(a); a.click();
      setTimeout(() => { try { document.body.removeChild(a); } catch (_) {} }, 1000);
    } catch (_) { /* WKWebView ignores download — Open in browser is the native path */ }
  }
  /* Auto-print once the frame has painted when the opener asked for it (web
     "Print all" / "Briefing sheet"). Same-origin blob frames only — a presigned
     S3 frame is cross-origin and print() on it throws. */
  useEffect(() => {
    if (!doc.print || !loaded || !isBlob || native) return undefined;
    const t = setTimeout(doPrint, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.print, loaded, isBlob, native]);

  const btn = (label, Icon, onClick, disabled) => (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 10,
      background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.22)", color: C.white,
      fontSize: 12, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
      fontFamily: "inherit", whiteSpace: "nowrap",
    }}>
      <Icon size={14} color={C.white} /> {label}
    </button>
  );
  const showExternal = !!doc.externalUrl || !isBlob || !native;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9100, background: "#0a1628",
      display: "flex", flexDirection: "column",
      paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", flexShrink: 0 }}>
        <button type="button" onClick={onClose} aria-label="Close document" style={{
          width: 40, height: 40, borderRadius: 12, background: "rgba(255,255,255,0.12)", border: "none",
          display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0, flexShrink: 0,
        }}>
          <X size={22} color={C.white} />
        </button>
        <div style={{ flex: 1, minWidth: 0, color: C.white, fontSize: 14, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {doc.title || (isPdf ? "Document" : "Photo")}
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0, overflowX: "auto" }}>
          {isPdf && btn("Print", Printer, doPrint, !loaded)}
          {!native && btn("Save", Download, doDownload, false)}
          {showExternal && btn(native ? "Open in Safari" : "Open in browser", ExternalLink, doOpenExternal, false)}
        </div>
      </div>
      {printErr ? (
        <div style={{ margin: "0 12px 8px", padding: "8px 10px", borderRadius: 8, background: C.warnL, color: C.warn, fontSize: 12, fontWeight: 600 }}>{printErr}</div>
      ) : null}
      <div style={{ flex: 1, minHeight: 0, background: "#1e2430", position: "relative" }}>
        {!loaded && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 700 }}>
            Loading…
          </div>
        )}
        {isPdf ? (
          <iframe
            ref={frameRef}
            title={doc.title || "Document"}
            src={doc.url}
            onLoad={() => setLoaded(true)}
            style={{ width: "100%", height: "100%", border: "none", background: C.white, display: "block" }}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>
            <img src={doc.url} alt={doc.title || "Photo"} onLoad={() => setLoaded(true)}
              onError={() => { setLoaded(true); setPrintErr("The image could not be loaded."); }}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Print actions shared by JobDetailSheet, FieldView and ProofsScreen ──
   Native: the packet endpoint can presign the PDF (?url=1) so SFSafariViewController
   opens it and iOS offers the share sheet / AirPrint. The briefing sheet has no
   presign form, so on native it goes through the SAME packet endpoint with
   documents:false — identical bytes to GET /briefing-sheet.pdf, just hosted.
   Web: bytes -> Blob -> in-app viewer, which auto-prints via its iframe. */
async function ssPrintPacketForJobs(jobIds, include, { title, autoPrint = true } = {}) {
  const ids = (Array.isArray(jobIds) ? jobIds : [jobIds]).map((x) => Number(x)).filter((n) => Number.isFinite(n));
  if (!ids.length) throw new Error("No job selected");
  if (ssIsNative()) {
    const r = await api.post("/api/server/print-packet?url=1", { jobIds: ids, include });
    if (!r || !r.url) throw new Error("No print link returned");
    await openExternal(String(r.url));
    return { url: r.url };
  }
  const blob = await ssFetchPdfBlob("/api/server/print-packet", { method: "POST", body: { jobIds: ids, include } });
  ssOpenDocViewer({ title: title || "Print packet", blob, kind: "pdf", print: autoPrint, filename: (title || "packet").replace(/[^A-Za-z0-9_-]+/g, "_") + ".pdf" });
  return { blob };
}
async function ssOpenBriefingSheet(job) {
  const label = "Briefing sheet · #" + (job.jobNumber || job.id);
  if (ssIsNative()) {
    return ssPrintPacketForJobs([job.id], { briefing: true, documents: false, pos: false }, { title: label });
  }
  const blob = await ssFetchPdfBlob("/api/jobs/" + encodeURIComponent(job.id) + "/briefing-sheet.pdf");
  ssOpenDocViewer({ title: label, blob, kind: "pdf", print: true, filename: "briefing_" + (job.jobNumber || job.id) + ".pdf" });
  return { blob };
}
async function ssPrintSignedProofs(jobIds) {
  const ids = (Array.isArray(jobIds) ? jobIds : [jobIds]).map((x) => Number(x)).filter((n) => Number.isFinite(n));
  if (!ids.length) throw new Error("Select at least one signed proof");
  const blob = await ssFetchPdfBlob("/api/server/pos/print-merged", { method: "POST", body: { jobIds: ids } });
  ssOpenDocViewer({ title: "Signed proofs of service (" + ids.length + ")", blob, kind: "pdf", print: !ssIsNative(), filename: "POS_merged_" + ids.length + ".pdf" });
  return { blob };
}

/* Button-state wrapper: one in-flight action at a time, spinner label, toast on error. */
function useJobPrint(job) {
  const [busy, setBusy] = useState("");
  const run = React.useCallback(async (which, fn) => {
    if (busy) return;
    setBusy(which);
    try { await fn(); }
    catch (e) {
      const msg = (e && e.message) ? String(e.message) : "Could not build the PDF";
      ssToast("err", msg);
    } finally { setBusy(""); }
  }, [busy]);
  const printAll = React.useCallback(() => run("all", () =>
    ssPrintPacketForJobs([job.id], { briefing: true, documents: true, pos: false }, { title: "Print packet · #" + (job.jobNumber || job.id) })
  ), [run, job && job.id, job && job.jobNumber]);
  const briefing = React.useCallback(() => run("brief", () => ssOpenBriefingSheet(job)), [run, job]);
  return { busy, printAll, briefing };
}

function SpinnerDot({ color }) {
  return (
    <span style={{
      width: 12, height: 12, borderRadius: "50%", display: "inline-block", flexShrink: 0,
      border: `2px solid ${color || C.white}`, borderTopColor: "transparent",
      animation: "ssSpin 0.8s linear infinite",
    }}>
      <style>{`@keyframes ssSpin { to { transform: rotate(360deg); } }`}</style>
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// [onboarding-wizard-2026-06-23] APPLICATION-STATUS READER
// Reads the user's application status across naming conventions. This native
// build normalizes to camelCase (`applicationStatus`) — there is no
// `application_status` — but we read both defensively so the post-approval
// onboarding gate works regardless of which shape /api/auth/me returns.
// ═══════════════════════════════════════════════════════════════════════
function _appStatus(u) {
  return (u && (u.applicationStatus ?? u.application_status)) || null;
}

// ═══════════════════════════════════════════════════════════════════════
// WATCH BRIDGE — mirror jobs cache to native UserDefaults so the paired
// Apple Watch can fetch the latest jobs via WCSession REQUEST_JOBS without
// hitting the backend. PhoneWatchBridge.swift on the iOS side reads from
// UserDefaults (and the App Group container) under "ss_jobs_cache".
// Also drains the deferred attempt queue the Watch wrote while offline.
// ═══════════════════════════════════════════════════════════════════════
const SS_JOBS_CACHE_KEY = "ss_jobs_cache";
const SS_DEFERRED_ATTEMPTS_KEY = "ss_deferred_attempts";

function writeJobsCacheNative(jobs) {
  try {
    if (typeof window === "undefined") return;
    const Preferences = ssPlugin("Preferences"); // [server-parity-web-2026-10-05]
    if (!Preferences) return;
    const payload = JSON.stringify(Array.isArray(jobs) ? jobs : []);
    // PhoneWatchBridge.swift reads either a JSON string or a Data blob from
    // this key. Capacitor Preferences writes a string, which is fine.
    Preferences.set({ key: SS_JOBS_CACHE_KEY, value: payload });
  } catch (_) { /* native plugin not available — phone-only path */ }
}

/// Drain any serve attempts the Apple Watch queued while the JS bundle wasn't
/// running. PhoneWatchBridge.swift writes these to App Group UserDefaults
/// under "ss_deferred_attempts" — Capacitor Preferences mirrors the standard
/// suite, so we read it from there.
async function drainDeferredWatchAttempts(apiClient) {
  try {
    if (typeof window === "undefined") return;
    const Preferences = ssPlugin("Preferences"); // [server-parity-web-2026-10-05]
    if (!Preferences) return;
    const r = await Preferences.get({ key: SS_DEFERRED_ATTEMPTS_KEY });
    const raw = r && r.value;
    if (!raw) return;
    let queue = [];
    try { queue = JSON.parse(raw); } catch { return; }
    if (!Array.isArray(queue) || queue.length === 0) return;
    /* [watch-drain-fix-2026-07-27] Three defects in one loop:
       1. `note` — the backend reads `notes` (validateString(att.notes, 5000)). Every
          observation a server dictated to the Watch was silently discarded on arrival.
       2. A queued attempt with no lat/lng sent `gps: undefined`, which the backend rejects
          with 400 "GPS coordinates are required". The entry went back on the queue and was
          retried on EVERY subsequent drain, forever, never surfacing to anyone. Such an
          entry can never succeed, so quarantine it instead of looping: it is dropped from
          the retry queue and reported, so the operator can re-log it by hand rather than
          believing it is still pending.
       3. `result: att.status || "served"` defaulted a Watch entry with NO recorded outcome
          to a SUCCESSFUL SERVE. Defaulting toward "served" on missing data is the one
          direction that manufactures a proof of service. Entries without an explicit
          outcome are now quarantined too. */
    const failed = [];
    const unsendable = [];
    for (const att of queue) {
      if (!att || att.jobId == null) continue;
      const hasGps = att.lat != null && att.lng != null && isFinite(Number(att.lat)) && isFinite(Number(att.lng));
      if (!hasGps || !att.status) {
        unsendable.push({ ...att, _reason: !hasGps ? "no GPS recorded" : "no outcome recorded" });
        continue;
      }
      try {
        const path = `/api/jobs/${encodeURIComponent(att.jobId)}/attempts`;
        await apiClient.post(path, {
          result: att.status,
          notes: att.note || att.notes || "",
          gps: { lat: Number(att.lat), lng: Number(att.lng) },
          source: "watch",
          date: att.ts || undefined,
          /* [late-entry-2026-10-18] The Watch entry WAS made at att.ts; the phone only relays it
             later. Without this the backend would measure lateness to the drain time and flag
             a correctly-timed Watch attempt as logged late. */
          enteredAt: (() => { try { const d = new Date(typeof att.ts === "string" && /^\d+$/.test(att.ts) ? Number(att.ts) : att.ts); return isNaN(d.getTime()) ? undefined : d.toISOString(); } catch (_) { return undefined; } })(),
        });
      } catch (_) { failed.push(att); }
    }
    if (unsendable.length) {
      console.warn("[watch-drain] %d queued attempt(s) can never be sent and were removed from the retry queue:",
        unsendable.length, unsendable.map(u => `job ${u.jobId} (${u._reason})`).join("; "));
      try {
        await Preferences.set({
          key: SS_DEFERRED_ATTEMPTS_KEY + "_unsendable",
          value: JSON.stringify(unsendable),
        });
      } catch (_) { /* best effort — the console warning is the primary record */ }
    }
    await Preferences.set({
      key: SS_DEFERRED_ATTEMPTS_KEY,
      value: JSON.stringify(failed),
    });
  } catch (_) { /* swallow — drain is best-effort */ }
}

/* [browser-evidence-upload-2026-07-27] ────────────────────────────────────────
   The presign -> PUT -> complete pipeline, extracted verbatim from takePhoto so the
   Capacitor camera path and the new browser file-input path cannot drift apart. The
   sequence is load-bearing: /api/uploads/complete is what records the S3 key against the
   job, and skipping it produces an object in the bucket that no attempt references —
   the `s3Key:null` condition already documented in patches/inbox for 18/18 prod photos.

   Takes raw bytes so callers can supply either a base64 camera result or a File.
   Throws on failure; callers surface the message. Never swallows. */
/* [ios-binary-upload-2026-10-07] ROOT CAUSE of "attempt photo unavailable" on iOS.
   capacitor.config.json enables CapacitorHttp, which patches window.fetch. Its body
   converter (Capacitor 6 native-bridge.js convertBody) runs a Uint8Array / ReadableStream
   body through `new TextDecoder().decode()` -- every byte >= 0x80 becomes U+FFFD, so the
   JPEG lands in S3 as `ef bf bd ef bf bd 00 10 4a 46 49 46 ...` and no viewer can open it.
   A File body takes the `type:'file'` branch instead: base64 on the JS side, decoded back
   to the exact bytes natively. Every S3 PUT therefore sends a File, never raw bytes or a
   plain Blob (a non-File Blob falls through to `type:'json'` and is mangled too). */
export function ssS3Body(data, mime, fileName) {
  const type = mime || "application/octet-stream";
  const name = fileName || "upload";
  try {
    if (typeof File === "function") {
      if (data instanceof File) return data;
      return new File([data], name, { type });
    }
  } catch (_) { /* very old WebKit without the File constructor */ }
  return (data instanceof Blob) ? data : new Blob([data], { type });
}

export async function ssUploadEvidence(api, { bytes, mime, fileName, context, jobId }) {
  const size = bytes && bytes.byteLength != null ? bytes.byteLength : (bytes ? bytes.length : 0);
  if (!size) throw new Error("Empty file — nothing to upload");
  /* [blob-xss-field-2026-09-19] Single choke point for every evidence upload in this
     file (attempt photos, stamped copies, retrieved docs). Callers gate on
     /^image\//, which image/svg+xml passes — so the coercion has to live here, and it
     has to apply to the presign fileType and the PUT Content-Type identically or the
     presigned signature will not validate. See the ACTIVE-CONTENT GUARD note above. */
  const _mime = ssSafeUploadMime(fileName, mime);
  const presign = await api.post("/api/uploads/presign", {
    fileName, fileType: _mime, fileSize: size, context, jobId,
  });
  if (!presign || !presign.presignedUrl) throw new Error("No upload URL from backend");
  const put = await fetch(presign.presignedUrl, {
    method: "PUT", headers: { "Content-Type": _mime }, body: ssS3Body(bytes, _mime, fileName), /* [ios-binary-upload-2026-10-07] */
  });
  if (!put.ok) throw new Error("S3 upload failed: " + put.status);
  await api.post("/api/uploads/complete", {
    key: presign.key, fileName, fileSize: size, fileType: _mime, context, jobId,
  });
  return { key: presign.key, fileUrl: presign.fileUrl };
}

/* Base64 (Capacitor camera) -> Uint8Array. */
function ssB64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/* File/Blob (browser input) -> Uint8Array. */
export async function ssFileToBytes(file) {
  const buf = await file.arrayBuffer();
  return new Uint8Array(buf);
}

/* ═══════════════════════════════════════════════════════════════════════
   APPLICANT REGISTRATION DOCUMENTS — presigned upload
   [applicant-upload-presign-2026-09-20]

   THE BUG THIS FIXES. Every registration document (photo ID front/back,
   selfie, PS registration cert, bond, insurance, W-9, signature) used to
   travel as a base64 data URL inside ONE atomic POST /api/auth/register
   body. onDocFile capped each file at 5MB individually but nothing capped
   the TOTAL, and express.json() on the backend is limited to 15mb
   (server.js:1471). Base64 inflates ~33%, so four 5MB PDFs alone are
   ~27MB of body — a 413 that kills the ENTIRE registration, not one file.

   Worse, onDocFile downscaled images but passed PDFs through untouched,
   and the documents applicants actually attach as PDFs (bond, insurance,
   W-9, registration certificate) are exactly the bulky ones. So the
   applicants most likely to fail were the ones with complete paperwork.

   THE FIX. Upload the bytes straight to S3 through the PUBLIC presign
   route the backend already exposes for not-logged-in guests
   (POST /api/public/uploads/presign, server.js:26395 — no auth, same MIME
   allowlist and blocked-extension regex as the authed route, 500MB cap,
   server-owned non-guessable key). Then send only {s3Key,fileUrl} in the
   register body, which server.js:5657 ALREADY prefers over doc.data:

       if (s3Key) details[canonical] = { ...s3Key, fileUrl }
       else if (doc.data?.startsWith('data:')) ...base64 fallback

   So no backend change is needed. A registration body goes from tens of
   megabytes to a few hundred bytes of JSON.

   WHY NOT ssUploadEvidence. That helper posts to the AUTHED presign route
   and then to /api/uploads/complete, which also requires auth. An
   applicant has no account yet — by definition there is no token during
   registration. It also records the key against a jobId, which does not
   exist here. The register route records the key itself.

   WHY BARE fetch AND NOT api.post. api.req wipes the stored token and
   fires ss-session-expired on any 401. Running that mid-registration
   would be harmful and pointless on a route that takes no auth at all.

   FALLS BACK, NEVER BLOCKS. If S3 is unconfigured or the PUT fails, the
   caller keeps the old base64 path so registration still completes in a
   dev/self-hosted environment with no bucket. That is why this returns a
   shape rather than throwing. */

const SS_APPLICANT_DOC_MAX_BYTES = 25 * 1024 * 1024;
/* Only what survives the base64 fallback inside the 15mb body once the other
   documents are accounted for. Deliberately far below the presign cap. */
const SS_APPLICANT_B64_FALLBACK_MAX_BYTES = 4 * 1024 * 1024;

/* Aggregate budget for the degraded inline path. Each doc independently passing
   the per-file check is NOT enough: four 3.9MB fallbacks are ~21MB of base64
   against the backend's 15mb express.json limit — the exact 413 this change
   exists to remove. Reset when the wizard mounts. */
const SS_APPLICANT_B64_BUDGET_BYTES = 8 * 1024 * 1024;
let _ssApplicantB64Used = 0;
function ssResetApplicantB64Budget() { _ssApplicantB64Used = 0; }

/* Timeouts. Without these a stalled cellular PUT never settles, the in-flight
   counter never decrements, and the wizard's Continue button stays disabled for
   the rest of the session with no way back. */
const SS_PRESIGN_TIMEOUT_MS = 45 * 1000;
const SS_PUT_TIMEOUT_MS = 180 * 1000;

async function ssFetchWithTimeout(url, opts, ms) {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), ms);
  try {
    return await fetch(url, { ...opts, signal: ctl.signal });
  } catch (e) {
    if (e && e.name === "AbortError") {
      const err = new Error("Upload timed out. Check your connection and try again.");
      err.code = "TIMEOUT";
      throw err;
    }
    throw e;
  } finally { clearTimeout(t); }
}

/* "data:application/pdf;base64,JVBER..." -> { bytes, mime }. Returns null for
   anything that is not a base64 data URL. The mime group must be able to span
   parameters (";charset=utf-8"), or such a URL silently falls through to the
   inline path and re-inflates the body. */
function ssDataUrlToParts(dataUrl) {
  if (typeof dataUrl !== "string") return null;
  const m = /^data:([^,]*?)(;base64)?,(.*)$/s.exec(dataUrl);
  if (!m || !m[2]) return null;
  try {
    const mime = String(m[1] || "").split(";")[0] || "application/octet-stream";
    return { bytes: ssB64ToBytes(m[3]), mime };
  } catch (_) { return null; }
}

/* Uploads one applicant document to S3 and returns the reference the register
   route stores. Throws (with err.code) on failure so the caller can decide. */
async function ssUploadApplicantDoc({ bytes, mime, fileName }) {
  const size = bytes && bytes.byteLength != null ? bytes.byteLength : 0;
  if (!size) { const e = new Error("Empty file — nothing to upload"); e.code = "EMPTY"; throw e; }
  if (size > SS_APPLICANT_DOC_MAX_BYTES) {
    const e = new Error(
      `File too large (${(size / 1024 / 1024).toFixed(1)}MB). Please upload a file under 25MB.`
    );
    e.code = "TOO_LARGE";
    throw e;
  }
  /* [blob-xss-field-2026-09-19] Same coercion ssUploadEvidence applies: an SVG or
     HTML file served from the bucket is active content. Must match on the presign
     fileType AND the PUT Content-Type or the signature will not validate. */
  const _mime = ssSafeUploadMime(fileName, mime || "application/octet-stream");
  const pr = await ssFetchWithTimeout(API_URL + "/api/public/uploads/presign", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fileName, fileType: _mime, fileSize: size }),
  }, SS_PRESIGN_TIMEOUT_MS);
  if (!pr.ok) {
    const b = await pr.json().catch(() => ({}));
    const e = new Error(b.error || "Could not get an upload URL (" + pr.status + ")");
    /* The public presign route is rate limited to 60 requests / 15 min per IP
       and the wizard issues one per document, so an office behind one NAT can
       trip it. Say so rather than silently degrading to inline base64. */
    e.code = pr.status === 429 ? "RATE_LIMITED" : "PRESIGN_FAILED";
    throw e;
  }
  const presign = await pr.json();
  if (!presign || !presign.presignedUrl) {
    const e = new Error("No upload URL from backend"); e.code = "PRESIGN_FAILED"; throw e;
  }
  const put = await ssFetchWithTimeout(presign.presignedUrl, {
    method: "PUT", headers: { "Content-Type": _mime }, body: ssS3Body(bytes, _mime, fileName), /* [ios-binary-upload-2026-10-07] */
  }, SS_PUT_TIMEOUT_MS);
  if (!put.ok) {
    const e = new Error("Upload failed (" + put.status + ")"); e.code = "PUT_FAILED"; throw e;
  }
  return { s3Key: presign.key, fileUrl: presign.fileUrl };
}

/* The shape every registration-document call site stores.
   [applicant-upload-presign-2026-09-20]

   Takes BYTES, not a data URL, so a 25MB PDF never round-trips through
   readAsDataURL -> atob -> a per-byte JS loop (that path held the File, a 33MB
   base64 string, a 25MB binary string and a 25MB array at once — ~85MB peak and
   a 25-million-iteration main-thread loop, which is a jetsam kill on an older
   iPhone). `previewDataUrl` is supplied ONLY by the image call sites, which
   already have a small downscaled JPEG in hand.

   Tries S3 first and degrades to inline base64 only when S3 is unavailable AND
   the aggregate budget allows it. Throws otherwise — a document that cannot be
   stored must fail loudly, because a silently missing registration document is
   a compliance problem, not a UI annoyance. */
async function ssApplicantDocRef({ bytes, mime, fileName, size, previewDataUrl, onNote }) {
  const base = { name: fileName, ts: new Date().toISOString() };
  const nBytes = bytes && bytes.byteLength != null ? bytes.byteLength : 0;
  try {
    const ref = await ssUploadApplicantDoc({ bytes, mime, fileName });
    /* previewUrl keeps the wizard's <img> thumbnail working now that `data` no
       longer travels. React state ONLY — ssStripDocPreviews removes it before
       the register body is built and the draft-slimming effect removes it before
       localStorage, or we would have put the base64 straight back. Raster images
       only: the [blob-xss-field-2026-09-19] guard coerces SVG to octet-stream on
       upload, so it must not be handed to an <img> here either. */
    const isRaster = /^image\/(jpeg|png|gif|webp|bmp|heic|heif)$/i.test(mime || "");
    return {
      ...base, ...ref, size: nBytes, uploaded: true,
      ...(isRaster && previewDataUrl ? { previewUrl: previewDataUrl } : {}),
    };
  } catch (e) {
    const code = (e && e.code) || "";
    if (code === "TOO_LARGE" || code === "EMPTY" || code === "RATE_LIMITED") throw e;
    if (!previewDataUrl) {
      /* No inline representation to fall back to (non-image path). */
      throw e;
    }
    if (nBytes > SS_APPLICANT_B64_FALLBACK_MAX_BYTES
        || _ssApplicantB64Used + nBytes > SS_APPLICANT_B64_BUDGET_BYTES) {
      const err = new Error(
        "Upload failed and this file can't be attached directly. "
        + "Please check your connection and try again."
      );
      err.code = "FALLBACK_REFUSED";
      throw err;
    }
    _ssApplicantB64Used += nBytes;
    if (onNote) onNote("Stored inline — direct upload unavailable.");
    return { ...base, data: previewDataUrl, size: size != null ? size : nBytes };
  }
}

/* Removes previewUrl from every document on an application object before it is
   POSTed. previewUrl exists purely so the wizard can show a thumbnail after the
   bytes have gone to S3; sending it would re-inflate the body with the exact
   base64 this change removed. Returns a shallow copy — never mutates state.
   [applicant-upload-presign-2026-09-20] */
function ssStripDocPreviews(appObj) {
  if (!appObj || typeof appObj !== "object") return appObj;
  const out = {};
  for (const k of Object.keys(appObj)) {
    const v = appObj[k];
    if (v && typeof v === "object" && !Array.isArray(v) && "previewUrl" in v) {
      const { previewUrl, ...rest } = v;
      out[k] = rest;
    } else {
      out[k] = v;
    }
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════════
// LIVE ACTIVITIES — iOS 16.1+ ActivityKit bridge
// Surfaces the active job on the lock screen + Dynamic Island while the
// server is en route / attempting. Backed by StrikeServeActivityPlugin
// (ios/App/App/ActivityManager.swift) and the SwiftUI widget extension
// `StrikeServeActivities`. Every call fails soft when the plugin isn't
// present (Android, iOS < 16.1, simulator without Dynamic Island).
//
// Activity IDs are kept in sessionStorage keyed by job id so updates and
// the final end() can find the right handle across re-renders. We use
// sessionStorage (not localStorage) so a stale ID can't leak across a
// re-install or a user switch.
// ═══════════════════════════════════════════════════════════════════════
function _ssLivePlugin() {
  try {
    if (typeof window === "undefined") return null;
    return ssPlugin("StrikeServeActivity"); // [server-parity-web-2026-10-05]
  } catch (_) { return null; }
}

async function startJobLiveActivity(job) {
  const plugin = _ssLivePlugin();
  if (!plugin || !job) return null;
  try {
    const { activityId } = await plugin.startActivity({
      jobNumber: String(job.job_number || job.jobNumber || job.id || ""),
      recipientName: job.recipient_name || job.servee || job.defendant || "Recipient",
      address: job.address || "Unknown",
      city: job.city || "",
      stage: "En route to recipient",
    });
    if (activityId) {
      try { sessionStorage.setItem(`ss_live_activity_${job.id}`, activityId); } catch (_) {}
    }
    return activityId || null;
  } catch (e) {
    console.warn("[live-activity] start failed", (e && e.message) || e);
    return null;
  }
}

export async function updateJobLiveActivity(jobId, patch) {
  const plugin = _ssLivePlugin();
  if (!plugin || jobId == null) return;
  let activityId = null;
  try { activityId = sessionStorage.getItem(`ss_live_activity_${jobId}`); } catch (_) {}
  if (!activityId) return;
  const args = { activityId };
  if (patch && typeof patch === "object") {
    if (patch.stage != null) args.stage = String(patch.stage);
    if (patch.etaMinutes != null) args.etaMinutes = Number(patch.etaMinutes);
    if (patch.attemptNumber != null) args.attemptNumber = Number(patch.attemptNumber);
    if (patch.lat != null) args.lat = Number(patch.lat);
    if (patch.lng != null) args.lng = Number(patch.lng);
    if (patch.status != null) args.status = String(patch.status);
  }
  try {
    await plugin.updateActivity(args);
  } catch (e) {
    console.warn("[live-activity] update failed", (e && e.message) || e);
  }
}

export async function endJobLiveActivity(jobId, finalStage = "Completed") {
  const plugin = _ssLivePlugin();
  if (!plugin || jobId == null) return;
  let activityId = null;
  try { activityId = sessionStorage.getItem(`ss_live_activity_${jobId}`); } catch (_) {}
  if (!activityId) return;
  try {
    await plugin.endActivity({ activityId, finalStage });
  } catch (e) {
    console.warn("[live-activity] end failed", (e && e.message) || e);
  } finally {
    try { sessionStorage.removeItem(`ss_live_activity_${jobId}`); } catch (_) {}
  }
}

// ═══════════════════════════════════════════════════════════════════════
// MILEAGE PERSISTENCE — localStorage keys + buffer cap
// `ss_mileage_session_id` lets the user resume a session if the app was
// backgrounded mid-route. `ss_mileage_buffer` is the per-session offline
// queue of un-uploaded points (clamped to ~2 hours of capture at ~1 Hz =
// 7200 points; we hard-cap at 7200 for safety).
// ═══════════════════════════════════════════════════════════════════════
const SS_MILEAGE_SESSION_KEY = "ss_mileage_session_id";
const SS_MILEAGE_BUFFER_KEY = "ss_mileage_buffer";
const SS_MILEAGE_PING_INTERVAL_MS = 30000;
const SS_MILEAGE_MAX_BUFFER = 7200;

function ssLoadMileageBuffer() {
  try {
    const raw = localStorage.getItem(SS_MILEAGE_BUFFER_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function ssSaveMileageBuffer(arr) {
  try { localStorage.setItem(SS_MILEAGE_BUFFER_KEY, JSON.stringify(arr || [])); } catch (_) {}
}
function ssClearMileageBuffer() {
  try { localStorage.removeItem(SS_MILEAGE_BUFFER_KEY); } catch (_) {}
}

// ═══════════════════════════════════════════════════════════════════════
// PUSH NOTIFICATION REGISTRATION
// Wires @capacitor/push-notifications -> POST /api/devices/register so the
// backend can target this device. Accesses the plugin via the global Capacitor
// bridge (window.Capacitor.Plugins.PushNotifications) — the hand-bundled webview
// has no module resolver, so a dynamic import would reject. Listeners
// re-dispatch events via window CustomEvents so deep components (job
// details, notifications cache) can react without prop drilling.
// ═══════════════════════════════════════════════════════════════════════
const SS_PUSH_TOKEN_KEY = "ss_push_token";

async function registerPushNotifications() {
  try {
    // Access the plugin via the GLOBAL Capacitor bridge (window.Capacitor.Plugins.X),
    // exactly like Camera / Geolocation / Browser do elsewhere in this file. A dynamic
    // bare-module import (`import("@capacitor/push-notifications")`) does NOT resolve in
    // the hand-bundled esbuild webview (there's no module resolver), so it rejected and
    // the catch swallowed it — meaning the OS permission prompt never appeared.
    const PushNotifications = ssPlugin("PushNotifications"); // [server-parity-web-2026-10-05]
    if (!PushNotifications) { console.warn("[push] PushNotifications plugin unavailable"); return { granted: false }; }
    // Drop any listeners attached on a prior login — without this, every
    // login adds a fresh set and a single push fires the handler N times.
    try { await PushNotifications.removeAllListeners(); } catch (_) {}
    // Check current status first; only prompt when iOS hasn't decided yet ('prompt').
    let perm = null;
    try { perm = await PushNotifications.checkPermissions(); } catch (_) {}
    if (!perm || perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm && perm.receive !== "granted") {
      console.warn("[push] permission not granted:", perm && perm.receive);
      return { granted: false };
    }
    // Attach listeners BEFORE register() — iOS can fire 'registration' with a
    // cached token immediately on relaunch, before a listener added afterward
    // exists, which silently drops the token (user_devices stayed empty).
    PushNotifications.addListener("registration", async (token) => {
      try {
        const value = token && token.value;
        if (!value) return;
        try { localStorage.setItem(SS_PUSH_TOKEN_KEY, value); } catch (_) {}
        await api.post("/api/devices/register", {
          token: value,
          platform: "ios",
          app_version: "1.0.0-beta",
        });
      } catch (e) { console.warn("[push] register failed", e && e.message); }
    });
    PushNotifications.addListener("registrationError", (err) => {
      console.warn("[push] registration error", err);
    });
    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      // Foreground notif — surface as an in-app event the bell/notifs cache can pick up.
      try { window.dispatchEvent(new CustomEvent("ss-push-received", { detail: notification })); } catch (_) {}
    });
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      // Background tap — route to whatever the notification points at.
      // [notif-link-2026-08-08] This used to key on data.job_id alone, so any
      // notification carrying only an action_url (e.g. "drawer:settings", or a
      // "job:<id>" set without job_id) opened the app to the default tab and
      // left the user to hunt for what pinged them. The DB trigger
      // (20260703_notification_push_trigger.sql) already ships BOTH fields in
      // every payload, so honour action_url too, using the same conventions the
      // in-app bell uses in NotificationsSheet.handleTap.
      try {
        const data = (action && action.notification && action.notification.data) || {};
        /* [pos-overhaul-2026-10-07:server-app] POS signature requests open review-before-sign
           for that job directly — checked BEFORE job_id, which would only open the job. */
        {
          const link = String(data.link || data.action_url || "");
          const sm = link.match(/^sign:(.+)$/i);
          const sid = sm ? sm[1] : (String(data.type || "") === "pos_signature_required" ? (data.jobId || data.job_id) : null);
          if (sid) {
            window.dispatchEvent(new CustomEvent("ss-open-sign", { detail: { jobId: String(sid) } }));
            return;
          }
        }
        /* [ticket-link-2026-10-08] "ticket:<id>" — checked BEFORE job_id: ticket notifications
           carry the ticket's job_id too, which would only open the job's Brief tab. */
        {
          const tl = String(data.link || data.action_url || "");
          const tkm = tl.match(/^ticket:(.+)$/i);
          if (tkm) {
            const tj = data.job_id != null ? data.job_id : data.jobId;
            window.dispatchEvent(new CustomEvent("ss-open-ticket", { detail: { ticketId: String(tkm[1]), jobId: tj != null && String(tj) !== "" && String(tj) !== "null" ? String(tj) : "" } }));
            return;
          }
        }
        /* [messaging-v2-2026-10-08] "msg:<convId>" → Messages drawer. Checked BEFORE job_id
           so a message notification that also carries a job id still opens the thread. */
        if (ssOpenMsgLink(data.action_url || data.link)) return;
        /* [job-bundles-2026-10-13] Bundle pushes (type new_job_in_area + bundle_id/bundle_count)
           carry job_id, so they open that job here; Accept there shows the bundle prompt. */
        /* [job-bundles-v2-2026-10-16] A whole-bundle offer to THIS server: open Home's
           "Bundle offered to you" card and its review sheet. */
        if (ssOpenBundleLink(data.action_url || data.link, data)) return;
        if (data.job_id) {
          window.dispatchEvent(new CustomEvent("ss-push-open-job", { detail: data.job_id }));
          return;
        }
        const url = data.action_url ? String(data.action_url) : "";
        if (!url || url === "null") return;
        if (/^https?:\/\//i.test(url)) {
          window.open(url, "_blank", "noopener");
          return;
        }
        const dm = url.match(/^drawer:([a-z_-]+)$/i);
        if (dm) {
          /* [server-referral-links-2026-10-08] only drawers DrawerScreen renders */
          if (DRAWER_IDS.includes(dm[1])) window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: dm[1] }));
          return;
        }
        /* [server-referral-links-2026-10-08] "page:<name>" (billing, referrals, …) — same map as the bell. */
        const pgm = url.match(/^page:([a-z-]+)$/i);
        if (pgm) { ssOpenPageLink(pgm[1]); return; }
        /* [server-referrals-screen-2026-10-08] "work-item:<id>" — staff-only (queueStaff) on the
           backend, unreadable for a server; the OS banner already showed the text. Open nothing. */
        if (/^work-item:/i.test(url)) return;
        const jm = url.match(/^job:(.+)$/i) || url.match(/^\/jobs\/(.+)$/i);
        if (jm) {
          window.dispatchEvent(new CustomEvent("ss-push-open-job", { detail: String(jm[1]) }));
          return;
        }
        const tm = url.match(/^tab:([a-z]+)$/i);
        if (tm && TAB_IDS.includes(tm[1].toLowerCase())) {
          window.dispatchEvent(new CustomEvent("ss-open-tab", { detail: tm[1].toLowerCase() }));
        }
      } catch (_) {}
    });
    // Now that all listeners are attached, ask iOS to register with APNs.
    await PushNotifications.register();
    return { granted: true };
  } catch (e) {
    // Unexpected failure (permission API threw, etc.) — degrade gracefully.
    console.warn("[push] registration failed:", e && e.message);
    return { granted: false };
  }
}

async function unregisterPushNotifications() {
  // Best-effort: tell the backend to forget this device on logout so future
  // notifs aren't delivered to a signed-out app. The Capacitor plugin keeps
  // its own APNs token until the OS rotates it.
  let token = "";
  try { token = localStorage.getItem(SS_PUSH_TOKEN_KEY) || ""; } catch (_) {}
  if (!token) return;
  try {
    await api.del("/api/devices/" + encodeURIComponent(token));
  } catch (e) {
    console.warn("[push] unregister failed:", e && e.message);
  }
  try { localStorage.removeItem(SS_PUSH_TOKEN_KEY); } catch (_) {}
}

// React context so deep components can read user / jobs / refresh without prop drilling.
// Includes isTablet so deep layout components can switch to the iPad split-pane shell
// without prop drilling through every screen.
const AppCtx = React.createContext({ user: null, setUser: () => {}, jobs: [], jobsLoading: false, reload: () => {}, logout: () => {}, isTablet: false, routePolyline: null, setRoutePolyline: () => {}, unreadNotifs: 0, setUnreadNotifs: () => {}, dark: false, setDarkMode: () => {} });

// Runtime kill switch for the iPad split-pane (tablet) shell.
// v1.1 — iPad blank-screen triage. The brand-new tablet shell was never validated
// on a real device, so default is OFF: iPad falls back to the proven phone layout.
// To enable for QA on a single device, set EITHER:
//   - window.__SS_TABLET_SHELL = true            (set by native shell / QA override)
//   - localStorage.setItem("ss_tablet_shell","1") (per-device, persists)
// Any other value -> tablet shell stays disabled and useIsTablet() returns false.
// [field-command-flag-url-2026-09-11] A URL switch, because typing a localStorage
// line into an iPad console is miserable. Opening the app with ?tablet=1 turns the
// shell on and PERSISTS it (so the next launch from the home screen keeps it);
// ?tablet=0 turns it back off and clears the flag. The query param wins over the
// stored value on the load that carries it.
function resolveTabletShell() {
  try {
    if (typeof window !== "undefined" && window.location && window.location.search) {
      const q = new URLSearchParams(window.location.search).get("tablet");
      if (q === "1" || q === "0") {
        try { localStorage.setItem("ss_tablet_shell", q === "1" ? "1" : "0"); } catch (_) { /* ignore */ }
        return q === "1";
      }
    }
  } catch (_) { /* ignore */ }
  try {
    if (typeof window !== "undefined" && window.__SS_TABLET_SHELL === true) return true;
  } catch (_) { /* ignore */ }
  try {
    if (typeof localStorage !== "undefined") {
      const stored = localStorage.getItem("ss_tablet_shell");
      if (stored === "1") return true;
      if (stored === "0") return false;   // explicit opt-out sticks
    }
  } catch (_) { /* ignore */ }
  // [field-command-default-2026-09-11] Default-ON at tablet width was REVERTED on
  // 2026-09-11: Safari on a real iPad rendered a blank page with the shell enabled
  // (the same symptom that made this default OFF originally). It stays opt-in via
  // ?tablet=1 until that crash is identified — a white screen in the field is worse
  // than a letterboxed one. Chromium at 1024x768 does not reproduce it.
  return false;
}
const SS_TABLET_SHELL_ENABLED = resolveTabletShell();

/* [field-command-2026-09-11] Field Command — the single-screen iPad dash.
   It lives INSIDE the tablet shell, so it inherits that kill switch: with
   ss_tablet_shell unset nothing below can render and the phone shell is
   untouched. A second switch is provided so Field Command can be taken out
   on a device that is already running the tablet shell:
     - window.__SS_FIELD_COMMAND === false
     - localStorage.setItem("ss_field_command","0")
   Anything else leaves it on wherever the tablet shell is on. */
function resolveFieldCommand() {
  if (!SS_TABLET_SHELL_ENABLED) return false;
  try {
    if (typeof window !== "undefined" && window.__SS_FIELD_COMMAND === false) return false;
  } catch (_) { /* ignore */ }
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem("ss_field_command") === "0") return false;
  } catch (_) { /* ignore */ }
  return true;
}
const SS_FIELD_COMMAND_ENABLED = resolveFieldCommand();

/* [fc-standalone-2026-09-11] /?command=1 renders Field Command full-screen with no
   shell around it. Independent of the tablet kill switch on purpose — this is the
   route that works on any device, including one where the tablet sidebar fails. */
const SS_COMMAND_STANDALONE = (() => {
  try {
    return typeof window !== "undefined"
      && new URLSearchParams(window.location.search).get("command") === "1";
  } catch (_) { return false; }
})();

// Tablet breakpoint hook — 768px matches the @media (min-width: 768px) in server.html
// where the iPad split-pane layout activates. Updates on resize + orientation change.
// AND-gated by SS_TABLET_SHELL_ENABLED — when the kill switch is off, this hook
// always returns false and AppInner falls back to the phone shell on iPad.
function useIsTablet() {
  const [is, setIs] = React.useState(() =>
    SS_TABLET_SHELL_ENABLED
    && typeof window !== "undefined"
    && window.matchMedia("(min-width: 768px)").matches
  );
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (!SS_TABLET_SHELL_ENABLED) return; // kill switch off — never flip to tablet shell
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e) => setIs(SS_TABLET_SHELL_ENABLED && e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    const resize = () => setIs(SS_TABLET_SHELL_ENABLED && window.matchMedia("(min-width: 768px)").matches);
    window.addEventListener("orientationchange", resize);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
      window.removeEventListener("orientationchange", resize);
    };
  }, []);
  return is;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVER APPLICATION WIZARD — 11-step process-server onboarding
// ─────────────────────────────────────────────────────────────────────
// Step 1: Account credentials (name, email, password)
// Step 2: Personal information + emergency contact
// Step 3: Photo ID (front, back, optional selfie)
// Step 4: California process-server registration
// Step 5: Bond information ($2k minimum per CA law)
// Step 6: Insurance (optional)
// Step 7: W-9 / Tax info
// Step 8: Background check authorization
// Step 9: Vehicle & equipment
// Step 10: Service areas & availability
// Step 11: Agreements & digital signature
//
// Ported 2026-06-05 from cle-frontend ServerOnboarding (app.jsx:2211). Changes:
//   - Combined "create account" (creds) as Step 1 so the applicant submits
//     everything in a single round-trip; backend /api/auth/register accepts
//     `{name,email,password, application:{...}}` atomically (see server.js
//     2026-06-05 mod).
//   - All emojis stripped per founder preference (no icons in tab strip,
//     bullets, or step headers).
//   - CSS class system swapped for inline SS palette (matches LoginScreen).
//   - Replaced canvas SigPad with typed-signature for v1 (Type your full
//     legal name to sign). Canvas signature can be reintroduced when we
//     wire Capacitor's touch event handling cleanly.
//   - "California Legal Express" copy → "StrikeServe".
// ═══════════════════════════════════════════════════════════════════════
/* [join-li-2026-10-18] Leads Hub / SMS campaign invite token. strikeserve.com/join?li=<token>
   redirects to mobile.strikeserve.com/?register=1&li=<token>. Read ?li (or the ?i alias) the
   same way cle-frontend app.jsx does ([waitlist-onboarding-2026-10-15]), keep it in
   sessionStorage so a reload inside the session still carries it, and send it with the
   final submit as application.inviteTokens.lead so the backend (linkApplicationInvites ->
   markInviteApplied -> campaignHooks.onApplication) links the application to the lead and
   counts it against the campaign. Every step is best-effort: a missing or bad token never
   blocks registration. */
const SS_JOIN_INVITE_KEY = "ss_signup_invite";
function ssJoinInviteFromUrl() {
  try {
    const qp = new URLSearchParams(window.location.search);
    return (qp.get("li") || qp.get("i") || "").trim();
  } catch (_) { return ""; }
}
function ssJoinInviteToken() {
  const fromUrl = ssJoinInviteFromUrl();
  if (fromUrl && fromUrl.length <= 200) {
    try { sessionStorage.setItem(SS_JOIN_INVITE_KEY, JSON.stringify({ lead: fromUrl, ts: Date.now() })); } catch (_) {}
    return fromUrl;
  }
  try {
    const kept = JSON.parse(sessionStorage.getItem(SS_JOIN_INVITE_KEY) || "null");
    if (kept && typeof kept.lead === "string" && kept.lead) return kept.lead;
  } catch (_) {}
  return "";
}
function ssClearJoinInvite() {
  try { sessionStorage.removeItem(SS_JOIN_INVITE_KEY); } catch (_) {}
}

function ServerApplicationWizard({ onSubmitted, onCancel }) {
  // ─── SS brand palette (mirrors LoginScreen) ──────────────────────────
  const deep = "#0f2860";
  const royal = "#1a3a7a";
  const gold = "#c9a84c";
  const goldL = "#e8d48b";
  const ink = "#0a1628";
  const muted = "#6b7280";
  const ivory = "#faf8f4";
  const fieldBg = "#f6f4ee";
  const cardBg = "#fff";
  const borderC = "#e5e7eb";
  const successBg = "#e8f5ee";
  const successFg = "#2d8a4e";
  const warnBg = "#fff7ed";
  const warnFg = "#9a3412";
  const warnBorder = "#fed7aa";

  // ─── State ───────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  /* COUNT of documents currently uploading to S3, not a name. Two slots can be
     picked in quick succession (Step 3 has three boxes sharing one input); with
     a single string the first upload to finish would clear the flag while the
     second was still in flight and un-gate Continue. Gates the Next button and
     the submit so a step cannot advance with a document reference still
     outstanding. [applicant-upload-presign-2026-09-20] */
  const [uploadingN, setUploadingN] = useState(0);
  const uploadStart = () => setUploadingN(n => n + 1);
  const uploadEnd = () => setUploadingN(n => Math.max(0, n - 1));

  // Account credentials (Step 1)
  const [creds, setCreds] = useState({
    name: "", email: "", password: "", confirmPassword: "", phone: "",
    smsConsent: false,
  });
  const c = (k, v) => setCreds(p => ({ ...p, [k]: v }));

  // Application data (Steps 2-11)
  const [app, setApp] = useState({
    legalName: "", dob: "", phone: "",
    address: "", city: "", state: "CA", zip: "",
    emergencyName: "", emergencyPhone: "", emergencyRelation: "",
    photoIdType: "drivers-license", photoIdFront: null, photoIdBack: null, selfiePhoto: null,
    regNumber: "", regCounty: "", regExpDate: "", yearsExp: "", prevEmployers: "",
    bondCompany: "", bondNumber: "", bondAmount: "2000", bondExpDate: "",
    insCompany: "", insPolicy: "", insCoverage: "", insExpDate: "", insDoc: null,
    w9Uploaded: false, w9Doc: null, tinType: "ssn", ssn4: "", ein: "",
    bgCheckAuth: false, felonyHistory: "no", felonyExplain: "",
    hasVehicle: true, vehicleYear: "", vehicleMake: "", vehicleModel: "", vehiclePlate: "",
    hasSmartphone: true, hasCamera: true, hasPrinter: false,
    primaryCounty: "Sacramento", secondaryCounties: [], maxRadius: 30 /* [server-zone-30-2026-10-18] fixed */, languages: ["English"],
    availDays: ["mon","tue","wed","thu","fri"], availHours: "9am-6pm",
    agreeIC: false, agreeCode: false, agreeNDA: false, agreeBG: false, agreeTOS: false,
    typedSignature: "", signedDate: "",
  });
  const u = (k, v) => setApp(p => ({ ...p, [k]: v }));

  // [app-draft-autosave-2026-07-03] LOCAL draft persistence (parity with web).
  // The native wizard is ATOMIC — the account is created only at final submit — so there
  // is no server row to autosave into mid-flow (unlike the web post-login path). Instead
  // we persist a lightweight draft (text fields + current step; base64 blobs reduced to
  // refs and the PASSWORD never stored) to device storage on every change and restore it
  // on mount, so a reload, app backgrounding, or crash never wipes a partially-completed
  // application. Cleared on successful submit. This is the root remedy for applicant data
  // loss on native, mirroring the web localStorage draft.
  const DRAFT_KEY = "ss_native_app_draft_v1";
  const draftHydrated = useRef(false);
  useEffect(() => {
    if (draftHydrated.current) return; draftHydrated.current = true;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d && d.app) setApp(p => ({ ...p, ...d.app }));
      if (d && d.creds) setCreds(p => ({ ...p, ...d.creds }));
      if (typeof d?.step === "number" && d.step >= 1 && d.step <= 11) setStep(d.step);
    } catch (_) { /* corrupt draft — ignore */ }
  }, []);
  /* [applicant-upload-presign-2026-09-20] The inline-fallback byte budget is
     module scope so ssApplicantDocRef can see it without prop drilling. Reset it
     per wizard so a second applicant on the same device (or a retry after a
     failed submit) does not inherit a spent budget and get refused. */
  useEffect(() => { ssResetApplicantB64Budget(); }, []);
  useEffect(() => {
    if (!draftHydrated.current) return;
    try {
      const slimApp = {};
      for (const k of Object.keys(app)) {
        const v = app[k];
        /* [applicant-upload-presign-2026-09-20] previewUrl is a full base64 data
           URL held in state so the wizard can show a thumbnail after the bytes
           go to S3. It must be stripped here for exactly the reason `data` was:
           localStorage is a ~5MB quota and several of these would blow it, after
           which the whole draft silently stops saving. */
        const _heavy = v && typeof v === "object"
          && ((typeof v.data === "string" && v.data.startsWith("data:"))
              || (typeof v.previewUrl === "string" && v.previewUrl.startsWith("data:")));
        if (_heavy) {
          if (v.s3Key) {
            slimApp[k] = { name: v.name, ts: v.ts, uploaded: true, s3Key: v.s3Key, ...(v.fileUrl ? { fileUrl: v.fileUrl } : {}) };
          } else {
            /* Inline-fallback document: the bytes live ONLY in `data`, and `data`
               is too big to persist. Writing {uploaded:true} with no s3Key and no
               data would restore a slot that looks complete, pass canNext(), and
               then be silently DROPPED by the register handler — which is
               `if (s3Key) … else if (doc.data) …` with no else (server.js:5657).
               The applicant would be told they were done with no W-9 on file.
               Persist nothing so the slot is empty and they are re-prompted. */
            slimApp[k] = null;
          }
        } else { slimApp[k] = v; }
      }
      // NEVER persist the password — only non-secret account fields.
      const slimCreds = { name: creds.name, email: creds.email, phone: creds.phone, smsConsent: creds.smsConsent };
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, app: slimApp, creds: slimCreds, ts: Date.now() }));
    } catch (_) { /* quota/serialize — best effort */ }
  }, [app, creds, step]);
  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch (_) {} };

  // [join-li-2026-10-18] Invite token from ?li / ?i (or this session). Looked up once to
  // prefill name / email / phone; values the applicant (or a restored draft) already has win.
  const inviteTokenRef = useRef("");
  useEffect(() => {
    let tok = "";
    try { tok = ssJoinInviteToken(); } catch (_) { tok = ""; }
    inviteTokenRef.current = tok;
    if (!tok) return;
    try {
      fetch(API_URL + "/api/public/lead-invites/" + encodeURIComponent(tok), { method: "GET" })
        .then(r => (r && r.ok ? r.json() : null))
        .then(d => {
          const pf = d && d.prefill;
          if (!pf) return;
          const nm = String(pf.name || [pf.first_name, pf.last_name].filter(Boolean).join(" ") || "").trim();
          const em = String(pf.email || "").trim();
          const ph = String(pf.phone || "").trim();
          setCreds(p => ({
            ...p,
            name: p.name || nm,
            email: p.email || em,
            phone: p.phone || ph,
          }));
          setApp(p => ({
            ...p,
            legalName: p.legalName || nm,
            phone: p.phone || ph,
          }));
        })
        .catch(() => {});
    } catch (_) { /* lookup is best-effort */ }
  }, []);
  const inviteTokensPayload = () => {
    const t = inviteTokenRef.current;
    return t ? { inviteTokens: { lead: t } } : {};
  };

  // ─── File upload helpers ─────────────────────────────────────────────
  // Photo capture uses the native Capacitor Camera plugin when available — the
  // raw <input type="file" capture="environment"> intermittently no-ops inside
  // WKWebView on iOS (silent permission failure, wrong picker on iPadOS). The
  // plugin lets the user choose Camera vs Photos via the system action sheet
  // (CameraSource.Prompt) and returns a data URL we can downscale below.
  //
  // On non-native (web) builds we fall back to the hidden <input type="file">
  // that's still rendered at the bottom of Step 3.
  const fileRef = useRef(null);
  const [fileTarget, setFileTarget] = useState("");
  const pickFile = async (target) => {
    setFileTarget(target);
    const isNative = ssIsNative(); // [server-parity-web-2026-10-05]
    const Camera = ssPlugin("Camera");
    if (isNative && Camera) {
      try {
        const p = await Camera.getPhoto({
          quality: 85,
          // String literals are accepted by the Capacitor bridge and avoid
          // pulling in @capacitor/camera ESM types in this hand-bundled build.
          // Equivalent to CameraResultType.DataUrl + CameraSource.Prompt.
          resultType: "dataUrl",
          source: "PROMPT",
          saveToGallery: false,
          allowEditing: false,
          correctOrientation: true,
          promptLabelHeader: "Add photo",
          promptLabelPhoto: "Choose from library",
          promptLabelPicture: "Take photo",
        });
        const dataUrl = p && (p.dataUrl || p.webPath || p.path);
        if (!dataUrl) return;
        // Downscale to keep the wizard payload manageable (<2MB).
        /* [applicant-upload-presign-2026-09-20] Downscale, then upload to S3 and
           store only the key. See ssApplicantDocRef. */
        const _scaled = await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const max = 1600;
            let w = img.width, h = img.height;
            if (w > max) { h = h * (max / w); w = max; }
            if (h > max) { w = w * (max / h); h = max; }
            const canvas = document.createElement("canvas");
            canvas.width = w; canvas.height = h;
            canvas.getContext("2d").drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL("image/jpeg", 0.85));
          };
          img.onerror = () => resolve(null);
          img.src = dataUrl;
        });
        if (_scaled) {
          const _name = `${target}-${Date.now()}.jpg`;
          const _p = ssDataUrlToParts(_scaled);
          uploadStart();
          try {
            u(target, await ssApplicantDocRef({
              bytes: _p && _p.bytes, mime: (_p && _p.mime) || "image/jpeg",
              fileName: _name, previewDataUrl: _scaled,
            }));
            setError("");
          } catch (upErr) {
            /* Clear the slot. Leaving a stale or half-set document behind lets
               the applicant submit believing it is present. */
            u(target, null);
            setError((upErr && upErr.message) || "Upload failed. Please try again.");
          } finally { uploadEnd(); }
        }
        return;
      } catch (ex) {
        // User cancelled, denied permission, or plugin errored — fall through
        // to the file-input fallback so the form is still completable.
        const msg = (ex && ex.message) || "";
        if (/cancel/i.test(msg) || /denied/i.test(msg)) return;
        // Otherwise let the file picker handle it.
      }
    }
    // Web (or native plugin missing/erroring): hidden file input click.
    fileRef.current?.click();
  };
  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const _target = fileTarget;
    setError("");
    const reader = new FileReader();
    reader.onload = (ev) => {
      // Downscale large images, then upload to S3 and keep only the key.
      // [applicant-upload-presign-2026-09-20]
      const img = new Image();
      img.onload = async () => {
        const max = 1600;
        let w = img.width, h = img.height;
        if (w > max) { h = h * (max / w); w = max; }
        if (h > max) { w = w * (max / h); h = max; }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        const _p = ssDataUrlToParts(dataUrl);
        uploadStart();
        try {
          u(_target, await ssApplicantDocRef({
            bytes: _p && _p.bytes, mime: (_p && _p.mime) || "image/jpeg",
            fileName: f.name, previewDataUrl: dataUrl,
          }));
          setError("");
        } catch (upErr) {
          u(_target, null);
          setError((upErr && upErr.message) || "Upload failed. Please try again.");
        } finally { uploadEnd(); }
      };
      /* A HEIC the <img> decoder can't read used to be a silent no-op — the
         applicant saw nothing happen at all. */
      img.onerror = () => setError(
        "That image couldn't be read. Please try a JPEG or PNG."
      );
      img.src = ev.target.result;
    };
    reader.readAsDataURL(f);
    e.target.value = "";
  };
  const onDocFile = (target) => (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    /* [applicant-upload-presign-2026-09-20] Was a 5MB per-file cap with NO total
       cap, base64'd into the single atomic register body against a 15mb
       express.json limit — four 5MB PDFs 413'd the whole registration. Documents
       now go straight to S3 and only the key travels in the body. The inline
       fallback still has an aggregate budget (SS_APPLICANT_B64_BUDGET_BYTES) for
       the case where S3 is unreachable. */
    if (f.size > SS_APPLICANT_DOC_MAX_BYTES) {
      setError(
        `File too large (${(f.size / 1024 / 1024).toFixed(1)}MB). Please upload a file under 25MB.`
      );
      e.target.value = "";
      return;
    }
    setError("");
    const store = async (args) => {
      uploadStart();
      try {
        u(target, await ssApplicantDocRef({ fileName: f.name, size: f.size, ...args }));
        setError("");
      } catch (upErr) {
        /* Clear the slot so a failed replacement can never leave the superseded
           document in place looking successful. */
        u(target, null);
        setError((upErr && upErr.message) || "Upload failed. Please try again.");
      } finally { uploadEnd(); }
    };
    // Images get downscaled (max dim 1600px, JPEG q=0.85) — cheap, and it gives
    // us a thumbnail. Everything else (PDFs above all) is read straight to bytes:
    // no data URL, no atob, no per-byte loop, no 3x memory spike on a big scan.
    const isImage = f.type === "image/jpeg" || f.type === "image/png" || /\.(jpe?g|png)$/i.test(f.name);
    if (!isImage) {
      ssFileToBytes(f)
        .then(bytes => store({ bytes, mime: f.type || "application/octet-stream" }))
        .catch(() => setError("That file couldn't be read. Please try another."));
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const max = 1600;
        let w = img.width, h = img.height;
        if (w > max) { h = h * (max / w); w = max; }
        if (h > max) { w = w * (max / h); h = max; }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        const _p = ssDataUrlToParts(dataUrl);
        store({
          bytes: _p && _p.bytes, mime: (_p && _p.mime) || "image/jpeg",
          previewDataUrl: dataUrl,
        });
      };
      // Undecodable image (HEIC on a browser without support): still upload the
      // original bytes rather than dropping it — just without a thumbnail.
      img.onerror = () => {
        ssFileToBytes(f)
          .then(bytes => store({ bytes, mime: f.type || "application/octet-stream" }))
          .catch(() => setError("That file couldn't be read. Please try another."));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  // ─── Validation ──────────────────────────────────────────────────────
  function pwOK(pw) {
    return pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /\d/.test(pw)
        && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(pw);
  }
  function emailOK(em) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);
  }
  const canNext = () => {
    /* [applicant-upload-presign-2026-09-20] Documents now upload to S3 in the
       background, so a fast tapper could advance (or submit) before the key
       comes back and ship a step with no document reference at all. */
    if (uploadingN > 0) return false;
    if (step === 1) {
      return creds.name.trim() && emailOK(creds.email.trim()) && pwOK(creds.password)
          && creds.password === creds.confirmPassword;
    }
    if (step === 2) {
      return app.legalName && app.dob && app.phone && app.address && app.city
          && app.zip && app.emergencyName && app.emergencyPhone;
    }
    if (step === 3) return !!app.photoIdFront && !!app.selfiePhoto;
    if (step === 4) return !!(app.regNumber && app.regCounty);
    if (step === 5) return !!(app.bondCompany && app.bondNumber);
    if (step === 6) return true; // Insurance optional
    // [applicant-upload-presign-2026-09-20] app.w9Doc only — see the onChange note.
    if (step === 7) return !!app.w9Doc;
    if (step === 8) {
      if (!app.bgCheckAuth) return false;
      // If the applicant has felony history, require a meaningful explanation
      // (>=20 chars after trim) so we have something useful for compliance review.
      if (app.felonyHistory === "yes" && app.felonyExplain.trim().length < 20) return false;
      return true;
    }
    if (step === 9) return true; // Vehicle/equipment optional checkboxes
    if (step === 10) return !!app.primaryCounty;
    if (step === 11) {
      return app.agreeIC && app.agreeCode && app.agreeNDA && app.agreeBG && app.agreeTOS
          && app.typedSignature.trim().length >= 3;
    }
    return true;
  };
  const missingHint = () => {
    /* [applicant-upload-presign-2026-09-20] Without this the applicant sees a
       greyed-out Continue and no explanation for as long as the upload takes —
       which on a 20MB PDF over LTE reads as a frozen app. */
    if (uploadingN > 0) {
      return uploadingN > 1
        ? `Uploading ${uploadingN} documents — one moment…`
        : "Uploading document — one moment…";
    }
    if (step === 1) {
      const m = [];
      if (!creds.name.trim()) m.push("full name");
      if (!emailOK(creds.email.trim())) m.push("valid email");
      if (!pwOK(creds.password)) m.push("strong password (8+ chars, upper, lower, digit, special)");
      if (creds.password && creds.password !== creds.confirmPassword) m.push("matching password confirmation");
      return m.length ? "Fill in: " + m.join(", ") : "";
    }
    if (step === 2) {
      const m = [];
      if (!app.legalName) m.push("full legal name");
      if (!app.dob) m.push("date of birth");
      if (!app.phone) m.push("phone");
      if (!app.address) m.push("street address");
      if (!app.city) m.push("city");
      if (!app.zip) m.push("ZIP");
      if (!app.emergencyName) m.push("emergency contact name");
      if (!app.emergencyPhone) m.push("emergency contact phone");
      return m.length ? "Fill in: " + m.join(", ") : "";
    }
    if (step === 3) {
      const m = [];
      if (!app.photoIdFront) m.push("the front of your ID");
      if (!app.selfiePhoto) m.push("a selfie photo");
      return m.length ? "Upload " + m.join(" and ") + " to continue" : "";
    }
    if (step === 4) {
      const m = [];
      if (!app.regNumber) m.push("CA registration number");
      if (!app.regCounty) m.push("county");
      return m.length ? "Fill in: " + m.join(", ") : "";
    }
    if (step === 5) {
      const m = [];
      if (!app.bondCompany) m.push("bond company");
      if (!app.bondNumber) m.push("bond number");
      return m.length ? "Fill in: " + m.join(", ") : "";
    }
    if (step === 7) return app.w9Doc ? "" : "Upload your W-9 form to continue";
    if (step === 8) {
      const m = [];
      if (!app.bgCheckAuth) m.push("background check authorization");
      if (app.felonyHistory === "yes" && app.felonyExplain.trim().length < 20) {
        m.push("a felony explanation of at least 20 characters");
      }
      return m.length ? "Still needed: " + m.join(", ") : "";
    }
    if (step === 10) return app.primaryCounty ? "" : "Enter your primary county";
    if (step === 11) {
      const m = [];
      if (!app.agreeIC) m.push("Independent Contractor Agreement");
      if (!app.agreeCode) m.push("Code of Conduct");
      if (!app.agreeNDA) m.push("Confidentiality Agreement");
      if (!app.agreeBG) m.push("Background Check Consent");
      if (!app.agreeTOS) m.push("Terms of Service");
      if (app.typedSignature.trim().length < 3) m.push("typed signature");
      return m.length ? "Still needed: " + m.join(", ") : "";
    }
    return "";
  };

  // ─── Submit ──────────────────────────────────────────────────────────
  async function submit() {
    if (!canNext()) { setError(missingHint()); return; }
    setBusy(true); setError(""); setInfo("");
    try {
      // UETA / California Civil Code § 1633 — capture an audit trail at the
      // moment of typed-signature submit. We package the same four fields
      // into both the application JSON (`signatureAudit`) and the synthetic
      // signature doc so they survive any backend that only persists the doc.
      const signedAt = new Date().toISOString();
      const signerName = app.typedSignature.trim();
      const userAgent = (typeof navigator !== "undefined" && navigator.userAgent) || "";
      let connectionHint = "";
      try {
        const conn = typeof navigator !== "undefined" && (navigator.connection || navigator.mozConnection || navigator.webkitConnection);
        if (conn && conn.effectiveType) connectionHint = String(conn.effectiveType);
      } catch (_) { /* ignore */ }
      const ipHeaderHint = (connectionHint + " " + userAgent.slice(0, 80)).trim();
      const signatureAudit = signerName ? {
        signedAt,
        signerName,
        ipHeaderHint,
        userAgent,
      } : null;
      const auditText = signerName
        ? `Signed by ${signerName} at ${signedAt} from ${userAgent} / ${connectionHint || "unknown-connection"}`
        : "";

      const r = await api.post("/api/auth/register", {
        name: creds.name.trim(),
        email: creds.email.trim().toLowerCase(),
        password: creds.password,
        phone: creds.phone.trim() || null,
        role: "server",
        smsConsent: creds.smsConsent,
        smsConsentSource: ssSmsConsentSource("server-application"), // [sms-compliance-2026-10-05]
        smsConsentTextVersion: SS_SMS_CONSENT_TEXT_VERSION,
        application: {
          // [applicant-upload-presign-2026-09-20] Strip the wizard-only preview
          // data URLs; the documents themselves are already in S3 and travel as
          // {s3Key,fileUrl}, which server.js:5657 prefers over doc.data.
          ...ssStripDocPreviews(app),
          maxRadius: SS_ZONE_MILES, // [server-zone-30-2026-10-18] fixed; never above 30 (an old draft may hold 50)
          ...inviteTokensPayload(), // [join-li-2026-10-18]
          signedDate: signedAt,
          signatureAudit,
          // Send typed-signature both as the signature string (visible in
          // admin review) and as a synthetic "data" doc so the existing
          // doc-handling pipeline persists it.
          signature: signerName ? {
            name: "typed-signature.txt",
            ts: signedAt,
            data: "data:text/plain;base64," + btoa(auditText),
          } : null,
        },
      });
      // [atomic-gate-2026-06-17] TRUE DB-WRITE CONFIRMATION (mirrors the web
      // atomic flow at cle-frontend app.jsx:3105). The backend ALWAYS returns
      // pending:true / applicationStatus:'pending' for role=server — even when
      // the server_applications insert failed and only a draft `incomplete`
      // row (no docs) was seeded. Gating on those flags would falsely report
      // "submitted" with NO persisted application + documents. Success counts
      // ONLY when the response confirms a committed application row:
      // applicationCreated===true.
      if (r && r.applicationCreated === true) {
        clearDraft(); ssClearJoinInvite(); onSubmitted?.(r); /* [join-li-2026-10-18] */
        return;
      }
      // Account WAS created (token issued) but the application row did NOT
      // persist. Authenticate the session with the issued token and retry the
      // application once via the logged-in POST /api/applications path (flat
      // shape — same doc keys, normalized by the backend DOC_KEY_MAP). Only
      // report success if THAT write confirms.
      if (r && r.token) {
        api.setToken(r.token);
        try {
          const retry = await api.post("/api/applications", {
            // [applicant-upload-presign-2026-09-20] Same strip as the atomic path.
            ...ssStripDocPreviews(app),
            maxRadius: SS_ZONE_MILES, // [server-zone-30-2026-10-18]
            ...inviteTokensPayload(), // [join-li-2026-10-18] POST /api/applications reads req.body.inviteTokens
            signedDate: signedAt,
            signatureAudit,
            signature: signerName ? {
              name: "typed-signature.txt",
              ts: signedAt,
              data: "data:text/plain;base64," + btoa(auditText),
            } : null,
          });
          if (retry && (retry.id || retry.application || retry.ok || retry.status === "pending")) {
            clearDraft(); ssClearJoinInvite(); onSubmitted?.(r); /* [join-li-2026-10-18] */
            return;
          }
          setError((retry && retry.error) || r.applicationError
            || "Your account was created but the application could not be saved. Please try again.");
        } catch (re) {
          setError(re.message || r.applicationError
            || "Your account was created but the application could not be saved. Please try again.");
        }
      } else if (r?.error) {
        setError(r.error);
      } else if (r?.applicationError) {
        setError(r.applicationError);
      } else {
        setError("Could not submit application. Please try again.");
      }
    } catch (ex) {
      setError(ex.message || "Network error. Please check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  // ─── Style tokens ────────────────────────────────────────────────────
  const labelStyle = { fontSize: 11, fontWeight: 700, color: muted, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" };
  const inputStyle = {
    width: "100%", padding: "12px 14px", borderRadius: 10,
    border: "1px solid " + borderC, background: fieldBg, color: ink,
    fontSize: 16, boxSizing: "border-box", outline: "none",
    fontFamily: "inherit",
  };
  const selectStyle = { ...inputStyle, appearance: "menulist" };
  const checkRow = { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: ink, lineHeight: 1.5, marginBottom: 10, cursor: "pointer" };
  const sectionTitle = { fontSize: 13, fontWeight: 700, color: deep, marginTop: 18, marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid " + borderC };
  const helpBlock = (kind = "info") => ({
    padding: "10px 14px", borderRadius: 8, fontSize: 12,
    background: kind === "warn" ? warnBg : "#eef3f9",
    color: kind === "warn" ? warnFg : muted,
    border: kind === "warn" ? "1px solid " + warnBorder : "1px solid #d8e2f0",
    marginBottom: 14, lineHeight: 1.5,
  });

  const STEP_TITLES = [
    "Create Your Account",                    // 1
    "Personal Information",                   // 2
    "Photo Identification",                   // 3
    "Process Server Registration",            // 4
    "Bond Information",                       // 5
    "Insurance",                              // 6
    "W-9 / Tax Information",                  // 7
    "Background Check",                       // 8
    "Vehicle & Equipment",                    // 9
    "Service Areas & Availability",           // 10
    "Agreements & Signature",                 // 11
  ];

  // ─── Render ──────────────────────────────────────────────────────────
  // [applicant-form-visible-2026-10-08] className="ss-login" opts this wizard out of the
  // index.html phone-shell !important overrides (same escape hatch LoginScreen uses).
  // Without it the card is clamped to one screen with overflow hidden and every field
  // renders below the fold — applicants saw a blank form on mobile.strikeserve.com.
  return (
    <div className="ss-login" style={{
      position: "fixed", inset: 0,
      display: "flex", flexDirection: "column",
      background: `linear-gradient(180deg, ${ivory} 0%, #efece4 100%)`,
      padding: "calc(env(safe-area-inset-top) + 16px) 14px calc(env(safe-area-inset-bottom) + 12px)",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
      overflow: "auto", color: ink,
    }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 10 }}>
        <div style={{
          fontFamily: "Fraunces, Georgia, serif", fontSize: 22, fontWeight: 700, color: deep,
          letterSpacing: "-0.01em",
        }}>
          Strike<span style={{ fontStyle: "italic", color: gold, fontWeight: 500 }}>Serve</span>
        </div>
        <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>
          Process Server Application
        </div>
      </div>

      {/* Card container */}
      <div style={{
        width: "100%", maxWidth: 520, margin: "0 auto",
        background: cardBg, borderRadius: 16, padding: 20,
        boxShadow: `0 10px 40px ${deep}1A, 0 2px 6px ${deep}0D`,
        border: "1px solid #ece8db",
      }}>
        {/* Progress bar */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: deep }}>
              Step {step} of 11: {STEP_TITLES[step - 1]}
            </span>
            <span style={{ fontSize: 11, color: muted }}>{Math.round(step / 11 * 100)}%</span>
          </div>
          <div style={{ height: 6, background: "#eef0f4", borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${(step / 11) * 100}%`,
              background: `linear-gradient(90deg, ${royal}, ${gold})`,
              borderRadius: 3, transition: "width 0.3s",
            }} />
          </div>
          {/* Step dots */}
          <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap", justifyContent: "center" }}>
            {STEP_TITLES.map((_t, i) => {
              const n = i + 1;
              const done = n < step, current = n === step;
              return (
                <div
                  key={n}
                  onClick={() => n < step && setStep(n)}
                  style={{
                    width: 24, height: 24, borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 700, cursor: n < step ? "pointer" : "default",
                    background: done ? gold : current ? deep : "#eef0f4",
                    color: done || current ? "#fff" : muted,
                    border: current ? "2px solid " + deep : "none",
                    transition: "all 0.2s",
                  }}
                >
                  {n}
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── STEP 1: Account credentials ───────────────────────────── */}
        {step === 1 && (
          <div>
            <div style={helpBlock()}>
              First, let's create your StrikeServe account. You'll use this email and password to sign in once your application is approved.
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Full Name</div>
              <input type="text" value={creds.name} onChange={e => c("name", e.target.value)}
                     autoCapitalize="words" autoComplete="name" style={inputStyle}
                     placeholder="As it appears on legal documents" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Email</div>
              <input type="email" inputMode="email" value={creds.email}
                     onChange={e => c("email", e.target.value)}
                     autoCapitalize="none" autoCorrect="off" autoComplete="username"
                     style={inputStyle} placeholder="you@example.com" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Password</div>
              <input type="password" value={creds.password}
                     onChange={e => c("password", e.target.value)}
                     autoComplete="new-password" style={inputStyle}
                     placeholder="8+ chars, upper, lower, digit, special" />
              {creds.password && !pwOK(creds.password) && (
                <div style={{ fontSize: 11, color: warnFg, marginTop: 4 }}>
                  Must contain uppercase, lowercase, a digit, and a special character.
                </div>
              )}
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Confirm Password</div>
              <input type="password" value={creds.confirmPassword}
                     onChange={e => c("confirmPassword", e.target.value)}
                     autoComplete="new-password" style={inputStyle} />
              {creds.confirmPassword && creds.password !== creds.confirmPassword && (
                <div style={{ fontSize: 11, color: warnFg, marginTop: 4 }}>
                  Passwords don't match.
                </div>
              )}
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Phone (optional)</div>
              <input type="tel" inputMode="tel" value={creds.phone}
                     onChange={e => c("phone", e.target.value)}
                     autoComplete="tel" style={inputStyle} placeholder="(555) 555-0100" />
            </div>
            {creds.phone && (
              <label style={checkRow}>
                <input type="checkbox" checked={creds.smsConsent}
                       onChange={e => c("smsConsent", e.target.checked)} style={{ marginTop: 3 }} />
                {/* [sms-compliance-2026-10-05] same optional CTA as the web (cta-v2-2026-09-22) */}
                <SmsConsentCta style={{ fontSize: 11.5, color: muted }} />
              </label>
            )}
          </div>
        )}

        {/* ─── STEP 2: Personal information ──────────────────────────── */}
        {step === 2 && (
          <div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Full Legal Name</div>
              <input type="text" value={app.legalName} onChange={e => u("legalName", e.target.value)}
                     style={inputStyle} placeholder="As it appears on your ID" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={labelStyle}>Date of Birth</div>
                <input type="date" value={app.dob} onChange={e => u("dob", e.target.value)} style={inputStyle} />
              </div>
              <div>
                <div style={labelStyle}>Phone</div>
                <input type="tel" inputMode="tel" value={app.phone}
                       onChange={e => u("phone", e.target.value)} style={inputStyle} placeholder="(555) 555-0100" />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Street Address</div>
              {/* [address-picker-sweep-2026-07-28] CONVERT — applicant street address.
                  Same value binding and same inputStyle as the plain input it replaced;
                  the picker only adds suggestions and auto-fills the City / State / ZIP
                  fields below. Runs pre-auth (no account yet), so a 401 from the lookup
                  is swallowed and the field stays a normal typeable input. */}
              <AddressAutocomplete
                value={app.address}
                onChange={v => u("address", v)}
                onAddressParsed={p => {
                  if (p.street) u("address", p.street);
                  if (p.city) u("city", p.city);
                  if (p.state) u("state", String(p.state).toUpperCase().slice(0, 2));
                  if (p.zip) u("zip", String(p.zip).replace(/[^\d-]/g, "").slice(0, 10));
                }}
                style={inputStyle}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={labelStyle}>City</div>
                <input type="text" value={app.city} onChange={e => u("city", e.target.value)}
                       autoComplete="address-level2" style={inputStyle} />
              </div>
              <div>
                <div style={labelStyle}>State</div>
                <input type="text" value={app.state} onChange={e => u("state", e.target.value)}
                       maxLength={2} style={{ ...inputStyle, textTransform: "uppercase" }} />
              </div>
              <div>
                <div style={labelStyle}>ZIP</div>
                <input type="text" inputMode="numeric" value={app.zip}
                       onChange={e => u("zip", e.target.value)} maxLength={10} style={inputStyle} />
              </div>
            </div>
            <div style={sectionTitle}>Emergency Contact</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={labelStyle}>Contact Name</div>
                <input type="text" value={app.emergencyName}
                       onChange={e => u("emergencyName", e.target.value)} style={inputStyle} />
              </div>
              <div>
                <div style={labelStyle}>Phone</div>
                <input type="tel" inputMode="tel" value={app.emergencyPhone}
                       onChange={e => u("emergencyPhone", e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Relationship</div>
              <input type="text" value={app.emergencyRelation}
                     onChange={e => u("emergencyRelation", e.target.value)}
                     style={inputStyle} placeholder="Spouse, parent, sibling, etc." />
            </div>
          </div>
        )}

        {/* ─── STEP 3: Photo ID ──────────────────────────────────────── */}
        {step === 3 && (
          <div>
            <div style={helpBlock()}>
              Upload a clear photo of your government-issued ID. Both front and back if available.
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>ID Type</div>
              <select value={app.photoIdType} onChange={e => u("photoIdType", e.target.value)} style={selectStyle}>
                <option value="drivers-license">Driver's License</option>
                <option value="state-id">State ID</option>
                <option value="passport">Passport</option>
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <PhotoUploadBox label="Front of ID" value={app.photoIdFront} required
                              onPick={() => pickFile("photoIdFront")} onClear={() => u("photoIdFront", null)}
                              styles={{ borderC, gold, muted, fieldBg }} />
              <PhotoUploadBox label="Back of ID" value={app.photoIdBack}
                              onPick={() => pickFile("photoIdBack")} onClear={() => u("photoIdBack", null)}
                              styles={{ borderC, gold, muted, fieldBg }} />
            </div>
            <div style={{ marginBottom: 14, textAlign: "center" }}>
              <div style={labelStyle}>Selfie Photo (required)</div>
              <PhotoUploadBox label="" value={app.selfiePhoto} round required
                              onPick={() => pickFile("selfiePhoto")} onClear={() => u("selfiePhoto", null)}
                              styles={{ borderC, gold, muted, fieldBg }} />
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
                   onChange={onFile} style={{ display: "none" }} />
          </div>
        )}

        {/* ─── STEP 4: PS Registration ───────────────────────────────── */}
        {step === 4 && (
          <div>
            <div style={helpBlock()}>
              California process servers must be registered with the County Clerk in their county of business per CCP § 22350.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={labelStyle}>CA Registration Number</div>
                <input type="text" value={app.regNumber} onChange={e => u("regNumber", e.target.value)}
                       style={inputStyle} placeholder="e.g. 2024-12345" />
              </div>
              <div>
                <div style={labelStyle}>County of Registration</div>
                <input type="text" value={app.regCounty} onChange={e => u("regCounty", e.target.value)}
                       style={inputStyle} placeholder="e.g. Sacramento" />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={labelStyle}>Expiration Date</div>
                <input type="date" value={app.regExpDate}
                       onChange={e => u("regExpDate", e.target.value)} style={inputStyle} />
              </div>
              <div>
                <div style={labelStyle}>Years of Experience</div>
                <select value={app.yearsExp} onChange={e => u("yearsExp", e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  <option value="0-1">0-1 year</option>
                  <option value="1-3">1-3 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Previous Employers (optional)</div>
              <textarea value={app.prevEmployers}
                        onChange={e => u("prevEmployers", e.target.value)}
                        style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
                        placeholder="Process serving companies or legal services employers" />
            </div>
          </div>
        )}

        {/* ─── STEP 5: Bond Information ──────────────────────────────── */}
        {step === 5 && (
          <div>
            <div style={helpBlock()}>
              California requires process servers to carry a minimum $2,000 bond per Business and Professions Code § 22350(a).
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={labelStyle}>Bond Company</div>
                <input type="text" value={app.bondCompany}
                       onChange={e => u("bondCompany", e.target.value)} style={inputStyle} />
              </div>
              <div>
                <div style={labelStyle}>Bond Number</div>
                <input type="text" value={app.bondNumber}
                       onChange={e => u("bondNumber", e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={labelStyle}>Bond Amount ($)</div>
                <input type="number" inputMode="numeric" value={app.bondAmount}
                       onChange={e => u("bondAmount", e.target.value)} style={inputStyle} placeholder="2000" />
              </div>
              <div>
                <div style={labelStyle}>Expiration Date</div>
                <input type="date" value={app.bondExpDate}
                       onChange={e => u("bondExpDate", e.target.value)} style={inputStyle} />
              </div>
            </div>
          </div>
        )}

        {/* ─── STEP 6: Insurance ─────────────────────────────────────── */}
        {step === 6 && (
          <div>
            <div style={helpBlock("warn")}>
              Optional. Insurance can be added later from your profile. Skip this step if you don't have it yet.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={labelStyle}>Insurance Company</div>
                <input type="text" value={app.insCompany}
                       onChange={e => u("insCompany", e.target.value)} style={inputStyle} />
              </div>
              <div>
                <div style={labelStyle}>Policy Number</div>
                <input type="text" value={app.insPolicy}
                       onChange={e => u("insPolicy", e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={labelStyle}>Coverage Amount</div>
                <input type="text" value={app.insCoverage}
                       onChange={e => u("insCoverage", e.target.value)}
                       style={inputStyle} placeholder="e.g. $1,000,000" />
              </div>
              <div>
                <div style={labelStyle}>Expiration Date</div>
                <input type="date" value={app.insExpDate}
                       onChange={e => u("insExpDate", e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Upload Insurance Document</div>
              <input type="file" accept="application/pdf,.pdf"
                     onChange={onDocFile("insDoc")}
                     style={{ fontSize: 13, color: muted }} />
              {app.insDoc && (
                <div style={{ fontSize: 12, color: successFg, marginTop: 6 }}>
                  Uploaded: {app.insDoc.name}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── STEP 7: W-9 / Tax ─────────────────────────────────────── */}
        {step === 7 && (
          <div>
            <div style={helpBlock()}>
              Required for 1099 contractor payments. Your SSN or EIN is encrypted at rest and only the last 4 digits are visible to administrators.
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>TIN Type</div>
              <select value={app.tinType} onChange={e => u("tinType", e.target.value)} style={selectStyle}>
                <option value="ssn">Social Security Number</option>
                <option value="ein">Employer Identification Number</option>
              </select>
            </div>
            {app.tinType === "ssn" && (
              <div style={{ marginBottom: 14 }}>
                <div style={labelStyle}>Last 4 of SSN</div>
                <input type="text" inputMode="numeric"
                       value={app.ssn4}
                       onChange={e => u("ssn4", e.target.value.replace(/\D/g, "").slice(0, 4))}
                       maxLength={4}
                       style={{ ...inputStyle, width: 140, letterSpacing: "0.3em", textAlign: "center" }}
                       placeholder="XXXX" />
              </div>
            )}
            {app.tinType === "ein" && (
              <div style={{ marginBottom: 14 }}>
                <div style={labelStyle}>EIN</div>
                <input type="text" value={app.ein}
                       onChange={e => u("ein", e.target.value)}
                       style={inputStyle} placeholder="XX-XXXXXXX" />
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Upload W-9 Form</div>
              <input type="file" accept="application/pdf,.pdf"
                     /* [applicant-upload-presign-2026-09-20] Do NOT set
                        w9Uploaded here. onDocFile is now async — setting a
                        "uploaded" boolean on the file-PICK meant a failed or
                        oversized upload still satisfied the step-7 gate, and
                        the backend persists w9Uploaded in detailFields, so the
                        admin record read "W-9 uploaded: true" with an empty w9
                        slot. That is a 1099 exposure, not a UI bug. The gate
                        now reads app.w9Doc, which only exists on success. */
                     onChange={onDocFile("w9Doc")}
                     style={{ fontSize: 13, color: muted }} />
              {app.w9Doc && (
                <div style={{ fontSize: 12, color: successFg, marginTop: 6 }}>
                  Uploaded: {app.w9Doc.name}
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, color: muted, marginTop: 8 }}>
              Need a blank W-9?{" "}
              <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" rel="noopener"
                 style={{ color: deep, fontWeight: 600 }}>Download from IRS.gov</a>
            </div>
          </div>
        )}

        {/* ─── STEP 8: Background Check ──────────────────────────────── */}
        {step === 8 && (
          <div>
            <div style={helpBlock()}>
              StrikeServe requires all process servers to undergo a background check. By authorizing below, you consent to a background screening including criminal history, driving record, and identity verification.
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Criminal History</div>
              <select value={app.felonyHistory} onChange={e => u("felonyHistory", e.target.value)} style={selectStyle}>
                <option value="no">No criminal history</option>
                <option value="yes">I have criminal history to disclose</option>
              </select>
            </div>
            {app.felonyHistory === "yes" && (
              <div style={{ marginBottom: 14 }}>
                <div style={labelStyle}>Please explain</div>
                <textarea value={app.felonyExplain}
                          onChange={e => u("felonyExplain", e.target.value)}
                          style={{ ...inputStyle, minHeight: 100, resize: "vertical" }}
                          placeholder="Provide details about your criminal history" />
              </div>
            )}
            <label style={{ ...checkRow, marginTop: 14, fontSize: 13 }}>
              <input type="checkbox" checked={app.bgCheckAuth}
                     onChange={e => u("bgCheckAuth", e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                I authorize StrikeServe to conduct a background check including criminal records,
                driving history, and identity verification. I understand this is required for all
                process server contractors.
              </span>
            </label>
          </div>
        )}

        {/* ─── STEP 9: Vehicle & Equipment ───────────────────────────── */}
        {step === 9 && (
          <div>
            <label style={{ ...checkRow, fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
              <input type="checkbox" checked={app.hasVehicle}
                     onChange={e => u("hasVehicle", e.target.checked)} style={{ marginTop: 3 }} />
              <span>I have a reliable vehicle for serving</span>
            </label>
            {app.hasVehicle && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
                <div>
                  <div style={labelStyle}>Year</div>
                  <input type="text" inputMode="numeric" value={app.vehicleYear}
                         onChange={e => u("vehicleYear", e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <div style={labelStyle}>Make</div>
                  <input type="text" value={app.vehicleMake}
                         onChange={e => u("vehicleMake", e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <div style={labelStyle}>Model</div>
                  <input type="text" value={app.vehicleModel}
                         onChange={e => u("vehicleModel", e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <div style={labelStyle}>Plate</div>
                  <input type="text" value={app.vehiclePlate}
                         onChange={e => u("vehiclePlate", e.target.value)}
                         style={{ ...inputStyle, textTransform: "uppercase" }} />
                </div>
              </div>
            )}
            <div style={sectionTitle}>Equipment</div>
            <label style={checkRow}>
              <input type="checkbox" checked={app.hasSmartphone}
                     onChange={e => u("hasSmartphone", e.target.checked)} style={{ marginTop: 3 }} />
              <span>Smartphone with camera and GPS (required for proof-of-service)</span>
            </label>
            <label style={checkRow}>
              <input type="checkbox" checked={app.hasCamera}
                     onChange={e => u("hasCamera", e.target.checked)} style={{ marginTop: 3 }} />
              <span>Separate camera for evidence photos</span>
            </label>
            <label style={checkRow}>
              <input type="checkbox" checked={app.hasPrinter}
                     onChange={e => u("hasPrinter", e.target.checked)} style={{ marginTop: 3 }} />
              <span>Portable or home printer</span>
            </label>
          </div>
        )}

        {/* ─── STEP 10: Service Areas & Availability ─────────────────── */}
        {step === 10 && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={labelStyle}>Primary County</div>
                <input type="text" value={app.primaryCounty}
                       onChange={e => u("primaryCounty", e.target.value)} style={inputStyle} />
              </div>
              <div>
                {/* [server-zone-30-2026-10-18] Read-only: every server's service area is 30 miles
                    around the home base the office sets from this application's address. */}
                <div style={labelStyle}>Service Radius</div>
                <div style={{ ...inputStyle, display: "flex", alignItems: "center", color: muted }} aria-readonly="true">30 miles</div>
                <div style={{ fontSize: 11, color: muted, marginTop: 4, lineHeight: 1.4 }}>Around your home address</div>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Languages Spoken</div>
              <input type="text"
                     value={(Array.isArray(app.languages) ? app.languages : []).join(", ")}
                     onChange={e => u("languages", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                     style={inputStyle} placeholder="English, Spanish, etc." />
            </div>
            <div style={sectionTitle}>Availability</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
              {[
                { k: "mon", l: "Mon" }, { k: "tue", l: "Tue" }, { k: "wed", l: "Wed" },
                { k: "thu", l: "Thu" }, { k: "fri", l: "Fri" },
                { k: "sat", l: "Sat" }, { k: "sun", l: "Sun" },
              ].map(d => {
                const days = Array.isArray(app.availDays) ? app.availDays : [];
                const on = days.includes(d.k);
                return (
                  <div key={d.k}
                       onClick={() => u("availDays", on ? days.filter(x => x !== d.k) : [...days, d.k])}
                       style={{
                         padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                         cursor: "pointer",
                         background: on ? deep : "#f3f5f8",
                         color: on ? "#fff" : muted,
                         border: "1px solid " + (on ? deep : borderC),
                         userSelect: "none",
                       }}>
                    {d.l}
                  </div>
                );
              })}
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Available Hours</div>
              <input type="text" value={app.availHours}
                     onChange={e => u("availHours", e.target.value)}
                     style={inputStyle} placeholder="e.g. 9am - 6pm" />
            </div>
          </div>
        )}

        {/* ─── STEP 11: Agreements & Signature ───────────────────────── */}
        {step === 11 && (
          <div>
            <div style={{ fontSize: 12, color: muted, marginBottom: 14, lineHeight: 1.5 }}>
              Please read and agree to each of the following. These are legally binding once you submit.
            </div>
            <label style={{ ...checkRow, fontSize: 12.5 }}>
              <input type="checkbox" checked={app.agreeIC}
                     onChange={e => u("agreeIC", e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                <strong style={{ color: ink }}>Independent Contractor Agreement</strong> &mdash; I understand I am
                an independent contractor, not an employee, and am responsible for my own taxes, insurance, and expenses.
              </span>
            </label>
            <label style={{ ...checkRow, fontSize: 12.5 }}>
              <input type="checkbox" checked={app.agreeCode}
                     onChange={e => u("agreeCode", e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                <strong style={{ color: ink }}>Code of Conduct</strong> &mdash; I agree to maintain professional
                conduct, follow all legal requirements for process serving in California, and represent
                StrikeServe with integrity.
              </span>
            </label>
            <label style={{ ...checkRow, fontSize: 12.5 }}>
              <input type="checkbox" checked={app.agreeNDA}
                     onChange={e => u("agreeNDA", e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                <strong style={{ color: ink }}>Confidentiality Agreement</strong> &mdash; I understand all case
                information is confidential and agree to never disclose case details to unauthorized parties.
              </span>
            </label>
            <label style={{ ...checkRow, fontSize: 12.5 }}>
              <input type="checkbox" checked={app.agreeBG}
                     onChange={e => u("agreeBG", e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                <strong style={{ color: ink }}>Background Check Consent</strong> &mdash; I consent to an ongoing
                background check as a condition of my contractor status.
              </span>
            </label>
            <label style={{ ...checkRow, fontSize: 12.5, marginBottom: 16 }}>
              <input type="checkbox" checked={app.agreeTOS}
                     onChange={e => u("agreeTOS", e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                <strong style={{ color: ink }}>Terms of Service</strong> &mdash; I agree to the StrikeServe
                Terms of Service and Platform Usage Policy.
              </span>
            </label>
            <div style={sectionTitle}>Digital Signature</div>
            <div style={{ fontSize: 12, color: muted, marginBottom: 8 }}>
              Type your full legal name below to sign this application. This typed signature has the same legal
              effect as a handwritten signature under the California Uniform Electronic Transactions Act.
            </div>
            <input type="text" value={app.typedSignature}
                   onChange={e => u("typedSignature", e.target.value)}
                   style={{
                     ...inputStyle,
                     fontFamily: "Fraunces, Georgia, serif",
                     fontStyle: "italic", fontSize: 22, color: deep,
                     padding: "16px 18px",
                   }}
                   placeholder="Type your full legal name" />
            <div style={{ fontSize: 11, color: muted, marginTop: 6 }}>
              Signed on {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </div>
          </div>
        )}

        {/* Error / hint area */}
        {error && (
          <div style={{
            marginTop: 16, padding: "10px 14px", background: "#fdecea",
            border: "1px solid #f5c2bd", borderRadius: 8, color: "#c0392b", fontSize: 13,
          }}>{error}</div>
        )}
        {!error && !canNext() && missingHint() && (
          <div style={{
            marginTop: 16, padding: "10px 14px", background: warnBg,
            border: "1px solid " + warnBorder, borderRadius: 8, color: warnFg, fontSize: 12,
          }}>{missingHint()}</div>
        )}

        {/* Navigation */}
        <div style={{
          display: "flex", justifyContent: "space-between", marginTop: 18,
          paddingTop: 16, borderTop: "1px solid " + borderC,
        }}>
          <button type="button"
                  onClick={() => step === 1 ? onCancel?.() : setStep(Math.max(1, step - 1))}
                  disabled={busy}
                  style={{
                    padding: "12px 18px", borderRadius: 10,
                    border: "1px solid " + borderC, background: "#fff", color: deep,
                    fontWeight: 600, fontSize: 14, cursor: busy ? "wait" : "pointer",
                  }}>
            {step === 1 ? "Cancel" : "Back"}
          </button>
          {step < 11 && (
            <button type="button"
                    onClick={() => canNext() && setStep(Math.min(11, step + 1))}
                    disabled={!canNext() || busy}
                    style={{
                      padding: "12px 22px", borderRadius: 10, border: "none",
                      background: canNext() ? deep : "#a8b3c7",
                      color: "#fff", fontWeight: 700, fontSize: 14,
                      cursor: canNext() ? "pointer" : "not-allowed",
                      boxShadow: canNext() ? `0 4px 12px ${deep}40` : "none",
                    }}>
              {uploadingN > 0
                ? "Uploading…"
                : (step === 10 ? "Review & Sign" : "Continue")}
            </button>
          )}
          {step === 11 && (
            <button type="button" onClick={submit}
                    disabled={!canNext() || busy}
                    style={{
                      padding: "12px 22px", borderRadius: 10, border: "none",
                      background: canNext() ? `linear-gradient(135deg, ${gold}, #b8932f)` : "#a8b3c7",
                      color: "#fff", fontWeight: 700, fontSize: 14,
                      cursor: canNext() && !busy ? "pointer" : "not-allowed",
                      boxShadow: canNext() ? `0 4px 12px ${gold}60` : "none",
                    }}>
              {busy ? "Submitting..." : "Submit Application"}
            </button>
          )}
        </div>
      </div>

      <div style={{ textAlign: "center", marginTop: 16, fontSize: 11, color: muted }}>
        Your application is encrypted in transit. Once it has been processed, we'll reach out to you. {/* [no-timeframes-2026-10-16] */}
      </div>
    </div>
  );
}

// Tiny photo-upload box used by Step 3. Kept inline so the wizard stays
// self-contained and we don't add a new export surface.
function PhotoUploadBox({ label, value, onPick, onClear, required, round, styles }) {
  const { borderC, gold, muted, fieldBg } = styles || {};
  const boxStyle = {
    width: round ? 120 : "100%", height: round ? 120 : 130,
    borderRadius: round ? "50%" : 10,
    border: "2px dashed " + borderC,
    background: fieldBg,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", flexDirection: "column", gap: 4,
    color: muted, fontSize: 11, textAlign: "center",
    margin: round ? "0 auto" : 0,
  };
  return (
    <div style={{ textAlign: "center" }}>
      {label && (
        <div style={{ fontSize: 11, fontWeight: 700, color: muted, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>
          {label}{required ? " *" : ""}
        </div>
      )}
      {value ? (
        <div style={{ position: "relative", display: "inline-block" }}>
          <img src={value.previewUrl || value.data || value.fileUrl} alt={label}
               style={{
                 width: round ? 120 : "100%", maxWidth: round ? 120 : "100%",
                 height: round ? 120 : "auto",
                 borderRadius: round ? "50%" : 10,
                 objectFit: round ? "cover" : "contain",
                 border: "2px solid " + (gold || "#c9a84c"),
               }} />
          <button type="button" onClick={onClear}
                  style={{
                    position: "absolute", top: 4, right: 4,
                    width: 24, height: 24, borderRadius: "50%", border: "none",
                    background: "rgba(192, 57, 43, 0.95)", color: "#fff",
                    fontSize: 14, fontWeight: 700, cursor: "pointer", lineHeight: 1,
                  }}>×</button>
        </div>
      ) : (
        <div onClick={onPick} style={boxStyle}>
          <div style={{ fontSize: 11, fontWeight: 600 }}>{round ? "Add Selfie" : "Upload"}</div>
          <div style={{ fontSize: 9, color: muted }}>Tap to choose photo</div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// LOGIN SCREEN — branded StrikeServe, with Sign In / Register / Forgot modes
// ═══════════════════════════════════════════════════════════════════════
/* [workflow-audit-2026-10-28 V-14] SET-PASSWORD / RESET LINK on the server app.
   The welcome and reset emails can point at mobile.strikeserve.com/?reset=<token> (the
   backend's existing convention, server.js forgot-password + tenant provisioning).
   /reset-password?token=<token> (lib/emailContent.js template) is rewritten to ?reset= by the
   inline script in www/index.html before the bundle loads. The token is sent
   to the EXISTING POST /api/auth/reset-password {token, newPassword}; nothing new on the
   backend. The token is removed from the address bar once used or dismissed. */
function ssResetTokenFromUrl() {
  try {
    const qp = new URLSearchParams(window.location.search || "");
    const t = (qp.get("reset") || "").trim();
    return t && t.length <= 512 && /^[A-Za-z0-9._~-]+$/.test(t) ? t : "";
  } catch (_) { return ""; }
}
function ssClearResetTokenFromUrl() {
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete("reset");
    window.history.replaceState(window.history.state, "", u.pathname + (u.search || "") + (u.hash || ""));
  } catch (_) {}
}
function SetPasswordScreen({ token, onDone, onCancel }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const deep = "#0f2860", gold = "#c9a84c", ink = "#0a1628", muted = "#6b7280", ivory = "#faf8f4", fieldBg = "#f6f4ee";
  const labelStyle = { fontSize: 11, fontWeight: 700, color: muted, letterSpacing: "0.08em", marginBottom: 6 };
  const inputStyle = { width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #e5e7eb", background: fieldBg, color: ink, fontSize: 16, boxSizing: "border-box", outline: "none" };
  const strong = pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /\d/.test(pw) && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(pw);
  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!strong) { setError("Your password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a special character (for example ! @ # $)."); return; }
    if (pw !== pw2) { setError("The two passwords do not match."); return; }
    setBusy(true);
    try {
      const r = await api.post("/api/auth/reset-password", { token, newPassword: pw });
      onDone((r && r.message) || "Password set. Sign in with your email and new password.");
    } catch (ex) {
      setError((ex && ex.message) || "Could not set your password. Try again.");
    } finally { setBusy(false); }
  }
  return (
    <div className="ss-login" style={{
      position: "fixed", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
      background: `linear-gradient(180deg, ${ivory} 0%, #efece4 100%)`,
      padding: "calc(env(safe-area-inset-top) + 20px) 16px calc(env(safe-area-inset-bottom) + 16px)",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif", overflow: "auto",
    }}>
      <BrandLogo />
      <div style={{ width: "100%", maxWidth: 380, background: "#fff", borderRadius: 18, padding: 24, boxShadow: `0 10px 40px ${deep}1A, 0 2px 6px ${deep}0D`, border: "1px solid #ece8db" }}>
        <BrandWordmark deep={deep} gold={gold} muted={muted} />
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: ink }}>Set your password</div>
          <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>Choose a password for your account. You will sign in with it next.</div>
        </div>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={labelStyle}>NEW PASSWORD</div>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" style={inputStyle} required
              placeholder="8+ chars, upper, lower, digit, special" />
          </div>
          <div>
            <div style={labelStyle}>CONFIRM PASSWORD</div>
            <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" style={inputStyle} required />
          </div>
          {error && <div style={{ color: "#c0392b", fontSize: 13, padding: "10px 12px", background: "#fdecea", borderRadius: 8 }}>{error}</div>}
          <button type="submit" disabled={busy || !pw || !pw2} style={{
            padding: "13px 16px", borderRadius: 10, border: "none", background: busy ? "#445573" : deep,
            color: "#fff", fontWeight: 700, fontSize: 15, cursor: busy ? "wait" : "pointer",
          }}>{busy ? "Saving..." : "Set password"}</button>
          <button type="button" onClick={onCancel} disabled={busy} style={{ background: "none", border: "none", color: muted, fontSize: 13, cursor: "pointer", marginTop: -4 }}>← Back to Sign In</button>
        </form>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin, initialInfo }) {
  const [mode, setMode] = useState(() => { try { return (new URLSearchParams(window.location.search).get("register") === "1" || !!ssJoinInviteFromUrl()) ? "register" : "login"; /* [join-li-2026-10-18] an invite link also opens the application */ } catch (_) { return "login"; } }); // login | register | forgot  /* [join-link-2026-10-16] strikeserve.com/join -> ?register=1 opens the application wizard */
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("server"); // client | server
  const [smsConsent, setSmsConsent] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(initialInfo || ""); /* [workflow-audit-2026-10-28 V-14] message after a set-password link */
  const [busy, setBusy] = useState(false);

  /* [biometric-login-2026-10-11] FACE ID SIGN-IN.
     bio: what this iPhone supports + whether this role has a stored credential.
     bioOffer: set right after a password sign-in to ask "Use Face ID next time?"
     before entering the app. On open, an enrolled device prompts Face ID once
     automatically; cancelling ("Use Password") leaves the normal form. */
  const [bio, setBio] = useState({ available: false, enrolled: false, label: "Face ID" });
  const [bioOffer, setBioOffer] = useState(null);
  const bioAutoTried = useRef(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      const a = await bioAvailable();
      if (!a.available) return;
      const en = await bioEnrolled("server");
      if (!alive) return;
      setBio({ available: true, enrolled: en, label: a.label });
      if (en) {
        const em = bioEnrolledEmail("server");
        if (em) setEmail(prev => prev || em);
        /* [recon-server-web-2026-10-21] after an "expired" result, wait for a password sign-in */
        if (!bioAutoTried.current && !bioIsExpired("server")) { bioAutoTried.current = true; bioLogin(a.label); }
      }
    })();
    return () => { alive = false; };
  }, []);

  async function bioLogin(label) {
    setBusy(true); setError(""); setInfo("");
    let signedIn = false;
    try {
      const t = await bioSignIn("server", API_URL, label || bio.label, (ssGetBrand() || {}).name); /* [recon-server-web-2026-10-21] tenant name in the iOS prompt */
      api.setToken(t.token);
      const user = await api.me();
      if (!user || !user.id) throw new Error("Sign-in failed. Try again.");
      localStorage.setItem(SS_USER_KEY, JSON.stringify(user));
      if (!window.__ssJobsPrefetch) window.__ssJobsPrefetch = api.get("/api/jobs").catch(() => null);
      signedIn = true;
      onLogin(user);
    } catch (ex) {
      if (!signedIn && api._jwt) api.setToken("");
      if (ex && (ex.code === "not_enrolled" || ex.code === "blocked")) setBio(b => ({ ...b, enrolled: false })); /* [biometric-login-v2-2026-10-18] a gate refusal also clears enrollment */
      if (!(ex && ex.code === "cancelled")) setError((ex && ex.message) || "Sign-in failed");
    } finally {
      if (!signedIn) setBusy(false);
    }
  }

  async function finishBioOffer(accept) {
    const o = bioOffer;
    if (!o) return;
    if (accept) await bioEnroll("server", o.email, o.refreshToken);
    else bioMarkAsked("server");
    setBioOffer(null);
    onLogin(o.user);
  }

  function pwOK(pw) {
    return pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /\d/.test(pw)
        && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(pw);
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const emailLc = email.trim().toLowerCase();
      if (mode === "login") {
        const user = await api.login(emailLc, password, remember);
        if (user) {
          // api.login already seeds window.__ssJobsPrefetch from the bundled
          // initialJobs in the login response (2026-06-05). Only fall back to
          // a fresh GET /api/jobs if the backend didn't include them — e.g.
          // older backend deploy or older client cached this code path.
          if (!window.__ssJobsPrefetch) {
            window.__ssJobsPrefetch = api.get("/api/jobs").catch(() => null);
          }
          /* [biometric-login-2026-10-11] keep an enrolled Face ID current; offer it once otherwise
             [recon-server-web-2026-10-21] only a "Remember me" sign-in yields the long-lived refresh
             token Face ID should hold; without it nothing is enrolled or offered, but an enrolled
             device still drops Face ID when a different (or unknown) account signs in. */
          const rt = remember ? (api._lastRefresh || "") : "";
          if (bio.available) {
            if (await bioEnrolled("server")) {
              await bioRefreshAfterPasswordLogin("server", emailLc, rt);
            } else if (rt && !bioWasAsked("server")) {
              setBioOffer({ user, email: emailLc, refreshToken: rt });
              return;
            }
          }
          onLogin(user);
        }
        else setError("Sign-in did not complete. Please try again. (Error code: NO_SESSION)"); // [srvapp-2026-10-16]
      } else if (mode === "register") {
        if (!name.trim()) { setError("Name is required"); return; }
        if (!pwOK(password)) {
          setError("Password must be 8+ chars with uppercase, lowercase, digit, and special character.");
          return;
        }
        if (phone && !smsConsent) {
          setError("If providing a phone, please agree to SMS notifications.");
          return;
        }
        const r = await api.post("/api/auth/register", {
          name: name.trim(),
          email: emailLc,
          password,
          phone: phone.trim() || null,
          role,
          smsConsent,
          smsConsentSource: ssSmsConsentSource("register"), // [sms-compliance-2026-10-05]
          smsConsentTextVersion: SS_SMS_CONSENT_TEXT_VERSION,
        });
        if (r?.token) {
          // Legacy auto-login path. Under the D-2026-05-23-01 approval gate, the
          // backend no longer issues a token at register, so this branch is dead
          // for new registrations — kept as a defensive fallback if that policy
          // ever flips.
          api.setToken(r.token);
          localStorage.setItem(SS_USER_KEY, JSON.stringify(r.user));
          onLogin(r.user);
        } else if (r && (r.pending || r.applicationStatus === "pending_review")) {
          // Expected new-user flow: account created, pending admin approval. The
          // backend returns { ok:true, pending:true, message, user } with NO token
          // (D-2026-05-23-01 — closes the auto-login hole). Surface the success
          // message and flip back to Sign In so the applicant can come back once
          // they get the approval email. Previously this branch fell through to
          // setError("Registration failed") which lied to real users whose
          // accounts had actually been created.
          setInfo(r.message || "Application received. We'll email you when your account is approved.");
          setMode("login");
          setPassword("");
          setName("");
          setPhone("");
        } else {
          setError("Registration failed");
        }
      } else if (mode === "forgot") {
        await api.post("/api/auth/forgot-password", { email: emailLc });
        setInfo("If an account exists for this email, a reset link has been sent.");
      }
    } catch (ex) {
      setError(ex.message || "Request failed");
    } finally {
      setBusy(false);
    }
  }

  // When the user picks Register, hand the entire screen over to the 11-step
  // ServerApplicationWizard. Coming back from it (cancel or successful submit)
  // pops the user back to the Sign In view with an appropriate message.
  if (mode === "register") {
    return (
      <ServerApplicationWizard
        onSubmitted={(r) => {
          setMode("login");
          setInfo(r?.message || "Application received. We'll email you when your account is approved.");
          setError("");
          // Keep email field empty so the user explicitly signs in with their
          // approved creds later — avoids the impression they're "already in".
          setEmail("");
          setPassword("");
          setName("");
          setPhone("");
        }}
        onCancel={() => {
          setMode("login");
          setError("");
          setInfo("");
        }}
      />
    );
  }

  // StrikeServe brand palette
  const deep = "#0f2860";    // deep royal
  const royal = "#1a3a7a";   // royal blue
  const gold = "#c9a84c";    // burnished gold
  const goldL = "#e8d48b";   // gold highlight
  const ink = "#0a1628";
  const muted = "#6b7280";
  const ivory = "#faf8f4";
  const fieldBg = "#f6f4ee";
  const labelStyle = { fontSize: 11, fontWeight: 700, color: muted, letterSpacing: "0.08em", marginBottom: 6 };
  const inputStyle = {
    width: "100%", padding: "12px 14px", borderRadius: 10,
    border: "1px solid #e5e7eb", background: fieldBg, color: ink,
    fontSize: 16, boxSizing: "border-box", outline: "none",
  };
  const tabStyle = (active) => ({
    flex: 1, padding: "10px 0", textAlign: "center", borderRadius: 8,
    fontSize: 14, fontWeight: 700, cursor: "pointer",
    background: active ? deep : "transparent",
    color: active ? "#fff" : deep,
    border: "none",
    transition: "all 0.15s",
  });

  return (
    <div className="ss-login" style={{
      position: "fixed", inset: 0,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
      background: `linear-gradient(180deg, ${ivory} 0%, #efece4 100%)`,
      padding: "calc(env(safe-area-inset-top) + 20px) 16px calc(env(safe-area-inset-bottom) + 16px)",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
      overflow: "auto",
    }}>
      {/* Logo: tenant-aware [tenant-brand-2026-07-21] (StrikeServe shield by default) */}
      <BrandLogo />

      <div style={{
        width: "100%", maxWidth: 380,
        background: "#fff", borderRadius: 18, padding: 24,
        boxShadow: `0 10px 40px ${deep}1A, 0 2px 6px ${deep}0D`,
        border: `1px solid #ece8db`,
      }}>
        {/* Brand header — tenant-aware [tenant-brand-2026-07-21] */}
        <BrandWordmark deep={deep} gold={gold} muted={muted} />

        {/* Tabs */}
        {mode !== "forgot" && (
          <div style={{
            display: "flex", gap: 6, padding: 4, borderRadius: 10,
            background: "#f3f5f8", marginBottom: 18,
          }}>
            <button type="button" onClick={() => { setMode("login"); setError(""); setInfo(""); }} style={tabStyle(mode === "login")}>Sign In</button>
            <button type="button" onClick={() => { setMode("register"); setError(""); setInfo(""); }} style={tabStyle(mode === "register")}>Register</button>
          </div>
        )}

        {mode === "forgot" && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: ink }}>Forgot Password</div>
            <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>Enter your email and we'll send a reset link.</div>
          </div>
        )}

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Role picker for register */}
          {mode === "register" && (
            <div>
              <div style={labelStyle}>I AM A</div>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { id: "server", label: "Process Server" },
                  { id: "client", label: "Client" },
                ].map(r => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRole(r.id)}
                    style={{
                      flex: 1, padding: "12px 8px", borderRadius: 10,
                      border: role === r.id ? `2px solid ${gold}` : "1px solid #e5e7eb",
                      background: role === r.id ? `${gold}15` : "#fff",
                      cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 700, color: role === r.id ? gold : deep, letterSpacing: 0.5 }}>{r.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === "register" && (
            <div>
              <div style={labelStyle}>FULL NAME</div>
              <input type="text" value={name} onChange={e => setName(e.target.value)} autoCapitalize="words" autoComplete="name" style={inputStyle} required />
            </div>
          )}

          <div>
            <div style={labelStyle}>EMAIL</div>
            <input type="email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)}
                   autoCapitalize="none" autoCorrect="off" autoComplete="username" style={inputStyle} required />
          </div>

          {mode !== "forgot" && (
            <div>
              <div style={{ ...labelStyle, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span>PASSWORD</span>
                {mode === "login" && (
                  <button type="button" onClick={() => { setMode("forgot"); setError(""); setInfo(""); }} style={{
                    background: "none", border: "none", color: deep, fontSize: 11, fontWeight: 600,
                    cursor: "pointer", padding: 0, letterSpacing: "0",
                  }}>Forgot password?</button>
                )}
              </div>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                     autoComplete={mode === "register" ? "new-password" : "current-password"} style={inputStyle} required
                     placeholder={mode === "register" ? "8+ chars, upper, lower, digit, special" : ""} />
            </div>
          )}

          {mode === "register" && (
            <div>
              <div style={labelStyle}>PHONE (OPTIONAL)</div>
              <input type="tel" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)}
                     autoComplete="tel" style={inputStyle} placeholder="(555) 555-0100" />
            </div>
          )}

          {mode === "register" && phone && (
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, color: ink, fontSize: 11, lineHeight: 1.5 }}>
              <input type="checkbox" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} style={{ marginTop: 3, flexShrink: 0 }} />
              {/* [sms-compliance-2026-10-05] same optional CTA as the web (cta-v2-2026-09-22) */}
              <SmsConsentCta />
            </label>
          )}

          {mode === "login" && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: ink, fontSize: 12 }}>
              <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
              Remember me
            </label>
          )}

          {error && (
            <div style={{ color: "#c0392b", fontSize: 13, padding: "10px 12px", background: "#fdecea", borderRadius: 8 }}>{error}</div>
          )}
          {info && (
            <div style={{ color: "#2d8a4e", fontSize: 13, padding: "10px 12px", background: "#e8f5ee", borderRadius: 8 }}>{info}</div>
          )}

          <button
            type="submit"
            disabled={busy || !email || (mode !== "forgot" && !password) || (mode === "register" && !name)}
            style={{
              padding: "13px 16px", borderRadius: 10, border: "none",
              background: busy ? "#445573" : deep,
              color: "#fff", fontWeight: 700, fontSize: 15, cursor: busy ? "wait" : "pointer",
              boxShadow: busy ? "none" : `0 4px 12px ${deep}40`,
            }}
          >
            {busy
              ? (mode === "login" ? "Signing in..." : mode === "register" ? "Creating account..." : "Sending...")
              : (mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send Reset Link")}
          </button>

          {mode === "forgot" && (
            <button type="button" onClick={() => { setMode("login"); setError(""); setInfo(""); }} style={{
              background: "none", border: "none", color: muted, fontSize: 13, cursor: "pointer", marginTop: -4,
            }}>← Back to Sign In</button>
          )}

          {/* [biometric-login-2026-10-11] */}
          {mode === "login" && bio.available && bio.enrolled && (
            <button type="button" disabled={busy} onClick={() => bioLogin()} style={{
              padding: "12px 16px", borderRadius: 10, border: `1.5px solid ${deep}`,
              background: "#fff", color: deep, fontWeight: 700, fontSize: 15,
              cursor: busy ? "wait" : "pointer", marginTop: -4,
            }}>Sign in with {bio.label}</button>
          )}
        </form>

        {bioOffer && (
          <div role="dialog" aria-modal="true" style={{
            position: "fixed", inset: 0, zIndex: 1000, background: "rgba(10,22,40,0.55)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}>
            <div style={{
              width: "100%", maxWidth: 440, background: "#fff", borderRadius: "18px 18px 0 0",
              padding: "22px 20px calc(env(safe-area-inset-bottom) + 18px)",
            }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: ink }}>Use {bio.label} to sign in?</div>
              <div style={{ fontSize: 13, color: muted, marginTop: 6, lineHeight: 1.5 }}>
                Next time, sign in with {bio.label} instead of typing your password. You can turn this off in Settings.
              </div>
              <button type="button" onClick={() => finishBioOffer(true)} style={{
                width: "100%", marginTop: 16, padding: "13px 16px", borderRadius: 10, border: "none",
                background: deep, color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer",
              }}>Use {bio.label}</button>
              <button type="button" onClick={() => finishBioOffer(false)} style={{
                width: "100%", marginTop: 8, padding: "12px 16px", borderRadius: 10, border: "none",
                background: "none", color: muted, fontWeight: 600, fontSize: 14, cursor: "pointer",
              }}>Not now</button>
            </div>
          </div>
        )}

        {/* Footer link */}
        {mode === "login" && (
          <div style={{
            marginTop: 16, padding: "12px 14px", background: "#f3f5f8",
            borderRadius: 10, textAlign: "center", fontSize: 12, color: muted,
          }}>
            Don't have an account?{" "}
            <button type="button" onClick={() => { setMode("register"); setError(""); setInfo(""); }} style={{
              background: "none", border: "none", color: deep, fontSize: 12, fontWeight: 600,
              cursor: "pointer", textDecoration: "underline", padding: 0,
            }}>Register here</button>
          </div>
        )}

        <div style={{
          marginTop: 14, padding: "8px 12px", background: "#e8f5ee",
          borderRadius: 8, textAlign: "center", fontSize: 10, color: "#2d8a4e", fontWeight: 600,
        }}>
          TLS 1.3 · AES-256 · OWASP Compliant
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// [claim-map-unmask-2026-07-27] JOB VISIBILITY — NO pre-claim masking.
// Owner decision (Montana Hull, 2026-07-27): a process server must be able to
// evaluate the COMPLETE job record BEFORE accepting it — full servee name, full
// street address, client/firm name, documents and navigation. Every server the
// backend deems eligible to see a job sees all of it, including servers from
// other agencies on network-posted jobs. The former claim-to-reveal /
// hidden-address masking is gone; jobDisplay() below always returns the real
// values and no call site may re-mask them.
//
// isJobClaimed() is RETAINED — it is no longer a privacy gate, but it is still
// referenced for status-dependent UI (ListJobCard's claimed-only "Attempt
// overdue" badge). Do not repurpose it for masking.
// ═══════════════════════════════════════════════════════════════════════
function isJobClaimed(job) {
  return !!job && (job.status === "active" || job.status === "completed");
}

// ── Courthouse-job detection ──
// In-person filing ('court-filing') and document retrieval ('document-retrieval')
// are courthouse jobs: the server physically goes to the clerk's office to file
// or retrieve documents. These branch the job-detail UI (courthouse context
// instead of a recipient/residence map) and use the /filing-complete flow
// instead of the serve attempt/affidavit flow. eFiles are office-only and never
// reach the server feed, so they're not handled here.
function courthouseServiceType(job) {
  const st = String((job && job.serviceType) || "").toLowerCase();
  if (st === "court-filing") return "court-filing";
  if (st === "document-retrieval") return "document-retrieval";
  return null;
}
function isCourthouseJob(job) {
  return courthouseServiceType(job) != null;
}

/* [court-closed-warning-2026-10-08] COURT-CLOSED WARNING.
   Owner decision: a "court closed" attempt still counts as one of the server's 3
   attempts, because the app tells the server the court is closed BEFORE they go.
   GET /api/jobs/:id/court-status answers from the courts directory + court holiday
   calendar: { applies, court, status:{open, reason, holidayName, opensAtLabel,
   closesDailyLabel, minutesUntilClose, ...}, upcomingClosures:[{date,name,label}] }.
   Degrades silently: any error (older backend 404, not assigned 403, offline) just
   means no banner. */
const _ssCourtStatusCache = new Map(); // jobId -> { at, data }
let _ssCourtStatusUnsupported = false;
const SS_COURT_STATUS_TTL_MS = 5 * 60 * 1000;
function useCourtStatus(job) {
  const id = job && job.id != null ? String(job.id) : "";
  const [data, setData] = useState(() => {
    const c = id && _ssCourtStatusCache.get(id);
    return c && Date.now() - c.at < SS_COURT_STATUS_TTL_MS ? c.data : null;
  });
  useEffect(() => {
    if (!id || _ssCourtStatusUnsupported) { setData(null); return undefined; }
    let alive = true;
    const load = () => {
      api.get(`/api/jobs/${encodeURIComponent(id)}/court-status`).then((d) => {
        const v = d && typeof d === "object" && "applies" in d ? d : null;
        _ssCourtStatusCache.set(id, { at: Date.now(), data: v });
        if (alive) setData(v);
      }).catch((e) => {
        // A missing ROUTE (older backend) answers Express's HTML 404 -> no JSON error body.
        if (e && e.status === 404 && !(e.body && e.body.error)) _ssCourtStatusUnsupported = true;
        if (alive) setData(null);
      });
    };
    const c = _ssCourtStatusCache.get(id);
    if (c && Date.now() - c.at < SS_COURT_STATUS_TTL_MS) setData(c.data); else load();
    const t = setInterval(load, SS_COURT_STATUS_TTL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [id]);
  return data;
}
function ssCourtShownClosed(cs) {
  return !!(cs && cs.applies && cs.status && cs.status.open === false);
}
function ssCourtClosedReason(st) {
  if (!st) return "";
  if (st.reason === "holiday") return "Holiday: " + (st.holidayName || "court holiday");
  if (st.reason === "weekend") return "Weekend";
  if (st.reason === "before_hours") return st.opensDailyLabel ? "Not open yet — opens at " + st.opensDailyLabel : "Not open yet";
  if (st.reason === "after_hours") return st.closesDailyLabel ? "Closed at " + st.closesDailyLabel : "Closed for the day";
  return "Closed";
}
/* Red when closed, amber when closing within 60 minutes, nothing otherwise.
   `big` = Field Command sizing. */
function CourtStatusBanner({ cs, big, style }) {
  if (!cs || !cs.applies || !cs.status) return null;
  const st = cs.status;
  const closed = st.open === false;
  const closingSoon = !closed && typeof st.minutesUntilClose === "number" && st.minutesUntilClose <= 60;
  const upcoming = Array.isArray(cs.upcomingClosures) ? cs.upcomingClosures : [];
  if (!closed && !closingSoon && !upcoming.length) return null;
  const fs = big ? 19 : 12;
  const courtName = cs.court && cs.court.name;
  const upLine = upcoming.length ? (
    <div style={{ fontSize: big ? 15 : 11, fontWeight: 600, marginTop: (closed || closingSoon) ? 4 : 0, opacity: 0.9 }}>
      Court closed next 7 days: {upcoming.map((u) => (u.label || u.date) + " (" + u.name + ")").join(" · ")}
    </div>
  ) : null;
  if (!closed && !closingSoon) {
    return (
      <div role="note" style={{ padding: big ? "10px 14px" : "7px 12px", borderRadius: 10, background: "#fef3e0", color: "#7a4a06", border: "1px solid #e0a23a", ...style }}>
        {upLine}
      </div>
    );
  }
  const red = closed;
  const bg = red ? "#c0392b" : "#d4870e";
  return (
    <div role="alert" style={{
      padding: big ? "12px 16px" : "10px 12px", borderRadius: 12, background: bg, color: "#fff",
      boxShadow: `0 2px 8px ${bg}55`, ...style,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <AlertTriangle size={big ? 24 : 18} style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: big ? 22 : 14, fontWeight: 800, lineHeight: 1.25 }}>
            {red
              ? <>Court closed — {ssCourtClosedReason(st)}.{st.opensAtLabel ? <> Next open: {st.opensAtLabel}.</> : null}</>
              : <>Court closes at {st.closesAtLabel || st.closesDailyLabel} ({st.minutesUntilClose} min)</>}
          </div>
          {courtName ? <div style={{ fontSize: fs, fontWeight: 600, opacity: 0.9, marginTop: 2 }}>{courtName}</div> : null}
          {upLine}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SKELETON FAMILY — shimmer placeholders for any data >150ms to load.
// Lightweight inline CSS animation; no extra deps. The base SkeletonRect
// was introduced for the documents list; sister components were added
// during the v1 skeleton sweep so every async screen has a layout-stable
// placeholder. All variants compose SkeletonRect under the hood so the
// shimmer keyframes stay singular.
// ═══════════════════════════════════════════════════════════════════════
function SkeletonRect({ width = "100%", height = 12, radius = 6, style = {} }) {
  return (
    <div style={{
      width, height, borderRadius: radius,
      background: `linear-gradient(90deg, ${C.steelXXL} 0%, ${C.steelXL} 50%, ${C.steelXXL} 100%)`,
      backgroundSize: "200% 100%",
      animation: "ss-skeleton-shimmer 1.4s ease-in-out infinite",
      ...style,
    }} />
  );
}
// Single-line placeholder for body text. Height matches a 13px line.
function SkeletonLine({ width = "100%", height = 12, style = {} }) {
  return <SkeletonRect width={width} height={height} radius={6} style={{ marginBottom: 8, ...style }} />;
}
// Circle (avatar, status dot, icon button). `size` sets both axes.
function SkeletonCircle({ size = 40, style = {} }) {
  return <SkeletonRect width={size} height={size} radius={size / 2} style={style} />;
}
// Card placeholder — approximates the dimensions of ListJobCard / MiniJobCard
// / notification rows so the layout doesn't jump when real data arrives.
function SkeletonCard({ height = 116, style = {} }) {
  return (
    <div style={{
      background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 16,
      padding: 14, marginBottom: 10, ...style,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <SkeletonRect width="55%" height={14} />
        <SkeletonRect width={50} height={14} />
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <SkeletonRect width={64} height={16} radius={10} />
        <SkeletonRect width={48} height={16} radius={10} />
      </div>
      <SkeletonRect width="80%" height={11} style={{ marginBottom: 6 }} />
      <SkeletonRect width="40%" height={10} />
      {height > 116 && <SkeletonRect width="100%" height={height - 116} style={{ marginTop: 10 }} />}
    </div>
  );
}
// Wrapper that exposes the right a11y semantics for any cluster of skeletons.
// Use this any time you render >1 placeholder so screen readers announce the
// region as "busy/loading" rather than reading every shimmering box.
function SkeletonGroup({ label = "Loading", children, style = {} }) {
  return (
    <div role="status" aria-busy="true" aria-label={label} style={style}>
      {children}
      <span style={{
        position: "absolute", width: 1, height: 1, padding: 0, margin: -1,
        overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0,
      }}>{label}…</span>
    </div>
  );
}
// Keyframes injected once at module scope. Guarded so HMR/hot reload doesn't dup.
if (typeof document !== "undefined" && !document.getElementById("ss-skeleton-keyframes")) {
  const _ssKf = document.createElement("style");
  _ssKf.id = "ss-skeleton-keyframes";
  _ssKf.textContent = "@keyframes ss-skeleton-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }";
  document.head.appendChild(_ssKf);
}

// [claim-map-unmask-2026-07-27] Always returns the real record — claimed or not.
// Built defensively: jobs have no city column (the address lives in
// recipient_address), so job.city is empty on virtually every row. Never emit a
// bare comma or a dangling "CA" — each segment is only joined when non-empty.
export function jobDisplay(job) {
  if (!job) return { name: "—", street: "", streetCity: "", cityZip: "", fullAddress: "" };
  const street = String(job.address || "").trim();
  const city = String(job.city || "").trim();
  const zip = String(job.zip == null ? "" : job.zip).trim();
  // "Sacramento, CA 95814" / "Sacramento, CA" / "CA 95814" / "" — never ", CA" alone.
  const cityZip = city
    ? (zip ? `${city}, CA ${zip}` : `${city}, CA`)
    : (zip ? `CA ${zip}` : "");
  return {
    name: job.servee || "—",
    street,
    streetCity: [street, city].filter(Boolean).join(", "),
    cityZip,
    fullAddress: [street, cityZip].filter(Boolean).join(", "),
  };
}

// ═══════════════════════════════════════════════════════════════════════
// AUTH GATE — shows login screen if not signed in, otherwise renders app
// ═══════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════
// [srvapp-2026-10-16] APPLICATION STATUS GATE
// A process server whose application is not approved can sign in (backend gated
// onboarding: none / pending / pending_review / needs_revision) but every
// operational route answers 403 "Account pending approval". Rendering the full
// shell for them produced nothing but error toasts. AuthGate now shows this screen
// instead: plain-words status, the admin's reason, and — when the application was
// sent back or a re-upload was requested — the same targeted re-upload the web app's
// DocReuploadForm offers (presign -> PUT -> /api/uploads/complete, then
// PATCH /api/applications/:id/reupload-docs). All calls used here are on the
// backend's pending-server allowlist.
// ═══════════════════════════════════════════════════════════════════════
let __ssPendingNotifiedAt = 0;
function ssNotifyAccountPending() {
  const now = Date.now();
  if (now - __ssPendingNotifiedAt < 30000) return; // one re-check per 30 s, however many calls fail
  __ssPendingNotifiedAt = now;
  try { window.dispatchEvent(new Event("ss-account-pending")); } catch (_) {}
}
// Returns the lower-cased status when the gate applies, else null. Mirrors the
// backend: only role server / process_server is gated; 'approved' and legacy NULL
// pass. Admins, clients and approved servers are never affected.
function ssApprovalGateStatus(u) {
  if (!u || typeof u !== "object") return null;
  const role = String(u.role || "").toLowerCase();
  if (role !== "server" && role !== "process_server") return null;
  const st = _appStatus(u);
  if (st == null) return null;
  const s = String(st).toLowerCase();
  return s === "approved" ? null : s;
}
const SS_REUPLOAD_LABELS = {
  photoIdFront: "Photo ID (Front)", photoIdBack: "Photo ID (Back)", selfiePhoto: "Selfie",
  regCert: "Registration Certificate", bondDoc: "Bond Document", insDoc: "Insurance", w9Doc: "W-9",
  bgCheckSig: "Background Check Authorization", signature: "Signature",
  photo_id_front: "Photo ID (Front)", photo_id_back: "Photo ID (Back)", selfie: "Selfie",
  ps_registration: "Registration Certificate", bond: "Bond Document", insurance: "Insurance", w9: "W-9",
};
// Same rule as cle-frontend DocReuploadForm [onboarding-docs-2026-10-16]: a pending
// docReRequest names the docs; otherwise an application sent back for revision asks
// for the docs its reason mentions, or every document when it names none.
function ssReuploadRequest(app) {
  if (!app) return null;
  const rq = app.docReRequest || (app.details && app.details.docReRequest);
  if (rq && rq.status === "pending" && Array.isArray(rq.keys) && rq.keys.length) return rq;
  if (String(app.status || "") !== "needs_revision") return null;
  const why = String(app.rejectionReason || app.rejection_reason || "");
  const map = [[/bond/i, ["bondDoc"]], [/insur/i, ["insDoc"]], [/regist|certificate|ps-?\d/i, ["regCert"]], [/w-?9/i, ["w9Doc"]],
    [/selfie/i, ["selfiePhoto"]], [/photo\s*id|driver|licen[cs]e|\bid\b/i, ["photoIdFront", "photoIdBack"]], [/signature/i, ["signature"]]];
  let keys = [];
  map.forEach(([re, ks]) => { if (re.test(why)) keys = keys.concat(ks); });
  if (!keys.length) keys = ["photoIdFront", "photoIdBack", "selfiePhoto", "regCert", "bondDoc", "insDoc", "w9Doc"];
  return { status: "pending", keys: [...new Set(keys)], message: why };
}
function ssStatusWords(st) {
  switch (st) {
    case "needs_revision": return { title: "Needs revision", body: "Our team reviewed your application and needs changes before it can be approved.", color: "#b7791f", bg: "#fdf6e3" };
    case "suspended": return { title: "Suspended", body: "This account is suspended. Contact support to find out why and how to restore it.", color: "#c0392b", bg: "#fdecea" };
    case "rejected": return { title: "Not approved", body: "Your application was not approved. Contact support if you think this is a mistake.", color: "#c0392b", bg: "#fdecea" };
    case "revoked": return { title: "Access revoked", body: "Access for this account has been revoked. Contact support.", color: "#c0392b", bg: "#fdecea" };
    case "none": case "incomplete": case "draft":
      return { title: "Application not finished", body: "We do not have a completed application for this account yet. Sign in on the website to finish it, or contact support.", color: "#1a3a7a", bg: "#eef2fa" };
    default: return { title: "Pending review", body: "Your application has been submitted and is being reviewed by our team. You will get an email as soon as it is approved. Reviews typically take 1 to 3 business days.", color: "#1a3a7a", bg: "#eef2fa" };
  }
}
function ServerApplicationStatusScreen({ user, setUser }) {
  const deep = "#0f2860", gold = "#c9a84c", ink = "#0a1628", muted = "#6b7280", ivory = "#faf8f4";
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [files, setFiles] = useState({});
  const [progress, setProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const loadApp = useCallback(async () => {
    try {
      const r = await api.get("/api/applications/mine");
      if (aliveRef.current) { setApp((r && r.application) || null); setErr(""); }
    } catch (e) {
      if (aliveRef.current) setErr((e && e.message) || "Could not load your application.");
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);
  useEffect(() => { loadApp(); }, [loadApp]);

  const refresh = async () => {
    setRefreshing(true); setInfo("");
    try {
      const u = await api.me();
      if (u && typeof u === "object" && (u.id != null || u.email)) {
        try { localStorage.setItem(SS_USER_KEY, JSON.stringify(u)); } catch (_) {}
        if (aliveRef.current) setUser(u); // approved -> AuthGate drops this screen and renders the app
        if (!ssApprovalGateStatus(u)) return;
      }
      await loadApp();
      if (aliveRef.current) setInfo("Status checked. Nothing has changed yet.");
    } catch (e) {
      if (aliveRef.current) setErr((e && e.message) || "Could not check your status. Try again.");
    } finally {
      if (aliveRef.current) setRefreshing(false);
    }
  };

  const signOut = async () => {
    try { await unregisterPushNotifications(); } catch (_) {}
    api.logout();
    try {
      const P = ssPlugin("Preferences");
      if (P) await P.remove({ key: "ss_jwt" });
    } catch (_) {}
    setUser(null);
    try { window.location.replace("index.html"); } catch (_) { window.location.href = "index.html"; }
  };

  const req = ssReuploadRequest(app);
  const submitReupload = async () => {
    if (!app || !req) return;
    if (!Object.keys(files).some((k) => files[k])) { setErr("Choose a file for at least one document."); return; }
    setUploading(true); setErr(""); setInfo("");
    try {
      const docPatch = {};
      const failed = [];
      for (const k of req.keys) {
        const f = files[k];
        if (!f) continue;
        setProgress((p) => ({ ...p, [k]: "uploading" }));
        try {
          const up = await ssUploadEvidence(api, {
            bytes: await ssFileToBytes(f),
            mime: f.type || "application/octet-stream",
            fileName: f.name || (k + "-" + Date.now()),
            context: "application",
          });
          docPatch[k] = { name: f.name || k, ts: new Date().toISOString(), s3Key: up.key, fileUrl: up.fileUrl };
          setProgress((p) => ({ ...p, [k]: "done" }));
        } catch (e) {
          failed.push((SS_REUPLOAD_LABELS[k] || k) + ": " + ((e && e.message) || "upload failed"));
          setProgress((p) => ({ ...p, [k]: "failed" }));
        }
      }
      if (!Object.keys(docPatch).length) { setErr("Upload failed. " + failed.join("; ")); return; }
      const res = await api.patch("/api/applications/" + encodeURIComponent(app.id) + "/reupload-docs", docPatch);
      if (res && res.success) {
        setFiles({});
        setInfo("Documents sent. An admin will review them shortly." + (failed.length ? " Not sent: " + failed.join("; ") : ""));
        try {
          const u = await api.me();
          if (u && typeof u === "object" && (u.id != null || u.email)) {
            try { localStorage.setItem(SS_USER_KEY, JSON.stringify(u)); } catch (_) {}
            if (aliveRef.current) setUser(u);
          }
        } catch (_) {}
        await loadApp();
      } else {
        setErr("Submit failed: " + ((res && res.error) || "unknown error"));
      }
    } catch (e) {
      setErr((e && e.message) || "Upload failed. Try again.");
    } finally {
      if (aliveRef.current) setUploading(false);
    }
  };

  const st = String((app && app.status) || ssApprovalGateStatus(user) || "pending").toLowerCase();
  const words = ssStatusWords(st);
  const reason = app && (app.rejectionReason || app.rejection_reason);
  const btn = (primary) => ({
    flex: 1, padding: "13px 16px", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer",
    border: primary ? "none" : "1px solid #e5e7eb",
    background: primary ? deep : "#fff", color: primary ? "#fff" : deep,
  });

  return (
    <div className="ss-login" style={{
      position: "fixed", inset: 0,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
      background: `linear-gradient(180deg, ${ivory} 0%, #efece4 100%)`,
      padding: "calc(env(safe-area-inset-top) + 20px) 16px calc(env(safe-area-inset-bottom) + 16px)",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
      overflow: "auto",
    }}>
      <BrandLogo />
      <div style={{
        width: "100%", maxWidth: 380, boxSizing: "border-box",
        background: "#fff", borderRadius: 18, padding: 24,
        boxShadow: `0 10px 40px ${deep}1A, 0 2px 6px ${deep}0D`,
        border: "1px solid #ece8db",
      }}>
        <BrandWordmark deep={deep} gold={gold} muted={muted} />
        <div style={{ fontSize: 11, fontWeight: 700, color: muted, letterSpacing: "0.08em", marginBottom: 6 }}>APPLICATION STATUS</div>
        <div style={{ padding: "12px 14px", borderRadius: 10, background: words.bg, marginBottom: 14 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: words.color }}>{loading ? "Checking…" : words.title}</div>
          {!loading && <div style={{ fontSize: 13, color: ink, marginTop: 4, lineHeight: 1.5 }}>{words.body}</div>}
          {!loading && reason && (st === "needs_revision" || st === "rejected" || st === "suspended") && (
            <div style={{ fontSize: 13, color: ink, marginTop: 8, lineHeight: 1.5 }}>
              <strong>Reason from our team:</strong> {String(reason)}
            </div>
          )}
        </div>
        {user && user.email && (
          <div style={{ fontSize: 12, color: muted, marginBottom: 14 }}>Signed in as {user.email}</div>
        )}

        {!loading && app && req && (
          <div style={{ borderTop: "1px solid #ece8db", paddingTop: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: ink, marginBottom: 4 }}>Re-upload documents</div>
            <div style={{ fontSize: 12.5, color: muted, marginBottom: 10 }}>
              Upload {req.keys.length === 1 ? "this document" : "these documents"} so we can finish reviewing your application.
            </div>
            {req.message && req.message !== reason && (
              <div style={{ fontSize: 12.5, color: ink, padding: "8px 12px", background: "#fdf6e3", borderRadius: 8, marginBottom: 10, fontStyle: "italic" }}>
                Note from our team: {String(req.message)}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
              {req.keys.map((k) => (
                <label key={k} style={{ display: "flex", flexDirection: "column", gap: 6, padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb", background: "#f6f4ee" }}>
                  <span style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, color: ink }}>
                    <span>{SS_REUPLOAD_LABELS[k] || k}</span>
                    {progress[k] === "done" && <span style={{ fontSize: 11, color: "#2d8a4e" }}>Uploaded</span>}
                    {progress[k] === "uploading" && <span style={{ fontSize: 11, color: "#b7791f" }}>Uploading…</span>}
                    {progress[k] === "failed" && <span style={{ fontSize: 11, color: "#c0392b" }}>Failed, choose again</span>}
                  </span>
                  <input type="file" accept="image/*,application/pdf" disabled={uploading}
                         onChange={(e) => { const f = e.target.files && e.target.files[0]; setFiles((prev) => ({ ...prev, [k]: f || null })); }}
                         style={{ fontSize: 12, maxWidth: "100%" }} />
                </label>
              ))}
            </div>
            <button type="button" onClick={submitReupload}
                    disabled={uploading || !Object.keys(files).some((k) => files[k])}
                    style={{ ...btn(true), width: "100%", flex: "none", opacity: (uploading || !Object.keys(files).some((k) => files[k])) ? 0.6 : 1 }}>
              {uploading ? "Uploading…" : "Submit documents"}
            </button>
          </div>
        )}

        {err && <div style={{ color: "#c0392b", fontSize: 13, padding: "10px 12px", background: "#fdecea", borderRadius: 8, marginBottom: 12 }}>{err}</div>}
        {info && <div style={{ color: "#2d8a4e", fontSize: 13, padding: "10px 12px", background: "#e8f5ee", borderRadius: 8, marginBottom: 12 }}>{info}</div>}

        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={refresh} disabled={refreshing || uploading} style={{ ...btn(true), opacity: refreshing ? 0.6 : 1 }}>
            {refreshing ? "Checking…" : "Refresh"}
          </button>
          <button type="button" onClick={signOut} disabled={uploading} style={btn(false)}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

function AuthGate({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SS_USER_KEY) || "null"); } catch { return null; }
  });
  // OPTIMISTIC BOOT: if we have BOTH a cached user object AND a JWT, render
  // AppInner immediately and validate the session in the background. The boot
  // splash only shows when there's no cached user (first launch, or after
  // logout). This eliminates the cold-start network wait that was making
  // the Jobs tab look empty for 1-2s after sign-in.
  const cachedUser = user;
  const [booting, setBooting] = useState(!!api._jwt && !cachedUser);
  /* [workflow-audit-2026-10-28 V-14] ?reset=<token> set-password link; shown even over an open session */
  const [resetToken, setResetToken] = useState(() => ssResetTokenFromUrl());
  const [loginInfo, setLoginInfo] = useState("");

  useEffect(() => {
    if (!api._jwt) { setBooting(false); return; }
    let alive = true;
    // Fire jobs fetch immediately so AppInner can pick it up the moment it mounts.
    // AuthGate writes prefetch on warm-boot (with cached jwt); LoginScreen also writes after a fresh login. Second write wins. Both paths complete safely — see AppInner useEffect.
    window.__ssJobsPrefetch = api.get("/api/jobs").catch(() => null);
    api.me()
      .then(u => { if (alive) { setUser(u); localStorage.setItem(SS_USER_KEY, JSON.stringify(u)); } })
      .catch(() => { if (alive) setUser(null); })
      .finally(() => { if (alive) setBooting(false); });
    return () => { alive = false; };
  }, []);

  // Listen for session-expired events (401 from any request)
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener("ss-session-expired", handler);
    return () => window.removeEventListener("ss-session-expired", handler);
  }, []);

  // [srvapp-2026-10-16] A 403 "Account pending approval" means the cached user is
  // stale (approval withdrawn / sent back). Re-read the user so the status gate below
  // takes over instead of every screen toasting the same error.
  useEffect(() => {
    let busy = false;
    const onPending = () => {
      if (busy || !api._jwt) return;
      busy = true;
      api.me()
        .then((u) => {
          if (u && typeof u === "object" && (u.id != null || u.email)) {
            setUser(u);
            try { localStorage.setItem(SS_USER_KEY, JSON.stringify(u)); } catch (_) {}
          }
        })
        .catch(() => {})
        .finally(() => { busy = false; });
    };
    window.addEventListener("ss-account-pending", onPending);
    return () => window.removeEventListener("ss-account-pending", onPending);
  }, []);

  if (resetToken) {
    return (
      <SetPasswordScreen token={resetToken}
        onDone={(msg) => {
          /* the backend revoked every session of that account: sign out here too */
          ssClearResetTokenFromUrl();
          try { api.logout(); } catch (_) {}
          setUser(null); setBooting(false); setLoginInfo(msg); setResetToken("");
        }}
        onCancel={() => { ssClearResetTokenFromUrl(); setResetToken(""); }} />
    );
  }

  if (booting) {
    return (
      <div style={{
        position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
        background: "#0f2860", color: "#c9a84c", fontFamily: "Fraunces, Georgia, serif", fontSize: 32, fontWeight: 700,
      }}>{ssGetBrand().name}{/* [tenant-brand-2026-07-21] */}</div>
    );
  }

  if (!user) return <LoginScreen onLogin={setUser} initialInfo={loginInfo} />;
  // [srvapp-2026-10-16] Not-yet-approved process server: status screen, never the app shell.
  if (ssApprovalGateStatus(user)) return <ServerApplicationStatusScreen user={user} setUser={setUser} />;
  return React.cloneElement(children, { __ssUser: user, __ssSetUser: setUser });
}



// ═══════════════════════════════════════════════════════════════════════
// STRIKESERVE SERVER APP — MOBILE PROTOTYPE
// 4 tabs: Home / Jobs (center, map default) / Earnings / Account
// ═══════════════════════════════════════════════════════════════════════

// ── Theme palettes ────────────────────────────────────────────────────
// StrikeServe brand: navy #1a3a7a / gold #c9a84c. Two palettes share the
// same KEYS so every existing `C.foo` reference resolves in both themes.
// `C` itself is a single mutable object whose properties are reassigned by
// applyTheme(); a top-level theme-version state in AppInner forces a full
// re-render on toggle so all the direct `C.*` reads pick up new values.
const C_LIGHT = {
  royal: "#1a3a7a", deep: "#0f2860", light: "#2a4f9a",
  gold: "#c9a84c", goldL: "#e8d48b", goldM: "#d4b76a", goldXL: "#faf2dc",
  steel: "#8a8d93", steelL: "#c8cad0", steelXL: "#e8eaed", steelXXL: "#f4f5f7",
  bg: "#f5f3ef", bgW: "#faf8f4", white: "#fff", ink: "#0a1628",
  ok: "#2d8a4e", okL: "#e8f5ee",
  warn: "#d4870e", warnL: "#fef3e0",
  err: "#c0392b", errL: "#fdecea",
  info: "#2980b9", infoL: "#e8f1f8",
  purple: "#7c3aed", purpleL: "#f3ebff",
};
// Dark variant — keep the navy/gold accents, invert the neutral scale.
// `deep`/`royal` stay readable as accent fills; `white`/`bg` become dark
// surfaces; `ink`/`deep` text tones flip to light so text stays legible
// where the code uses C.deep / C.ink as a foreground color.
const C_DARK = {
  royal: "#4a72c8", deep: "#dfe6f4", light: "#6f93da",
  gold: "#d9bc63", goldL: "#7a6526", goldM: "#b9963f", goldXL: "#2a2410",
  steel: "#9aa0aa", steelL: "#3a3f48", steelXL: "#2a2e36", steelXXL: "#22262e",
  bg: "#121620", bgW: "#1a1f2b", white: "#1e2430", ink: "#eef2f8",
  ok: "#4ec27a", okL: "#16331f",
  warn: "#e0a23a", warnL: "#3a2c0e",
  err: "#e06458", errL: "#3a1714",
  info: "#5aa6dd", infoL: "#12283a",
  purple: "#a273ed", purpleL: "#23163a",
};
// Mutable live palette. Initialized from the persisted ss_dark_mode flag so
// a dark-mode user's first paint is already dark (no light flash).
export const C = {};
function ssIsDarkPref() {
  try { return localStorage.getItem("ss_dark_mode") === "1"; } catch { return false; }
}
function applyTheme(dark) {
  Object.assign(C, dark ? C_DARK : C_LIGHT);
  try {
    if (typeof document !== "undefined" && document.documentElement) {
      document.documentElement.style.background = C.bg;
      document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
    }
  } catch {}
}
applyTheme(ssIsDarkPref());

// ═══════════════════════════════════════════════════════════════════════
// ADDRESS AUTOCOMPLETE  [address-picker-sweep-2026-07-28]
// ─────────────────────────────────────────────────────────────────────
// Every address in this app was free text. That is the root cause of platform
// rows like "1015 R Street, S, CA9" and "720 9th St, Sacramento, CA95814" —
// the latter fails to geocode while "720 9th Street, Sacramento, CA 95814"
// succeeds, which is why 16 of 45 job addresses could not be pinned at all.
//
// This is the LOCAL consumer of GET /api/address/autocomplete (backend provider
// chain: Smarty US Autocomplete Pro -> Google Places -> Nominatim). It mirrors
// the web client's AddressAutocomplete (cle-frontend/app.jsx) — same endpoint,
// same 300 ms debounce, same 3-character floor, same source==='smarty' parsed-
// component branch — but is written against THIS file's palette, `api` helper
// and inline-style conventions rather than imported (different repo, different
// bundle, no shared module).
//
// NON-NEGOTIABLE — the picker is an ASSIST, never a gate:
//   * A failed, slow, unauthorized or empty lookup NEVER clears, rewrites or
//     blocks what the user typed. Every wired field still accepts a fully
//     hand-typed address and submits it.
//   * Enter with no highlighted suggestion is not swallowed, so form submit
//     keeps working exactly as it did with the plain <input>.
//   * After two consecutive transport failures the component stops calling the
//     endpoint for the rest of its life and silently degrades to a plain field.
//
// iOS / Capacitor specifics:
//   * Selection is committed on touchend (with a drag threshold so scrolling the
//     list does not select) AND on click, de-duplicated by a timestamp guard.
//   * mousedown is preventDefault()ed so the input does not blur-and-close
//     before the tap lands.
//   * The dropdown renders through a portal at position:fixed and re-measures on
//     scroll / resize / visualViewport changes, so no ancestor `overflow:auto`
//     (Sheet bodies, DCard rows, the wizard scroller) can clip it, and it flips
//     above the field when the on-screen keyboard leaves no room below.
//
// Declared here (after the C palette so the dropdown chrome can use it) but a
// hoisted function declaration, so the pre-login applicant wizard defined
// earlier in this file can render it.
// ═══════════════════════════════════════════════════════════════════════
const SS_ADDR_MIN_CHARS = 3;          // [address-picker-sweep-2026-07-28]
const SS_ADDR_DEBOUNCE_MS = 300;      // [address-picker-sweep-2026-07-28] matches the web client

// [address-picker-sweep-2026-07-28] The endpoint returns a bare array today; accept
// the common envelope shapes too so a backend response tweak degrades to "no
// suggestions" instead of throwing inside render.
function ssAddrList(d) {
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.suggestions)) return d.suggestions;
  if (d && Array.isArray(d.results)) return d.results;
  return [];
}

// [address-picker-sweep-2026-07-28] Resolves to an array of suggestions. THROWS on a
// transport/auth failure so the caller can tell "the lookup broke" (stay silent) from
// "the lookup worked and matched nothing" (show the manual-entry hint).
async function ssAddressSuggest(q) {
  const path = "/api/address/autocomplete?q=" + encodeURIComponent(q);
  // Signed-in callers go through the shared helper (auth header, error shaping).
  if (api._jwt) return ssAddrList(await api.get(path));
  // Pre-auth path: the applicant wizard runs before an account exists. A raw fetch
  // keeps a 401 away from api.req()'s session-expired branch, which would otherwise
  // clear the token cache and fire ss-session-expired for a lookup nobody asked for.
  const r = await fetch(API_URL + path, { method: "GET" });
  if (!r.ok) throw new Error("address lookup " + r.status);
  return ssAddrList(await r.json().catch(() => null));
}

// [address-picker-sweep-2026-07-28]
// Props:
//   value / onChange        controlled mode (the usual case)
//   defaultValue / inputRef uncontrolled mode — required by MobileW9Form, whose
//                           fields must stay uncontrolled for the iOS autofill fix
//                           [w9-autofill-fix-2026-06-23]. onChange still fires on
//                           every edit and on pick, and a pick writes the DOM value
//                           directly so a ref read at submit sees it.
//   onAddressParsed(p)      p = { street, city, state, zip, county, lat, lng,
//                                 formatted, source, verified }. Call sites narrow
//                           the street field and fill their own city/state/zip.
//   placeholder             passed through unchanged
//   style                   applied to the <input> (call sites pass their existing
//                           inputStyle here so styling is byte-identical)
//   wrapStyle               applied to the positioning wrapper (e.g. { flex: 1 })
function AddressAutocomplete({
  value, defaultValue, onChange, onAddressParsed,
  placeholder, style, wrapStyle, inputRef: extInputRef,
  type = "text", autoComplete = "street-address", inputMode, maxLength,
  disabled, readOnly, id, name, onFocus, onBlur, onKeyDown,
}) {
  const controlled = value !== undefined;
  const innerRef = useRef(null);
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  const timerRef = useRef(null);
  const seqRef = useRef(0);           // stale-response guard
  const failRef = useRef(0);          // consecutive transport failures
  const touchRef = useRef({ x: 0, y: 0, moved: false });
  const pickedAtRef = useRef(0);      // de-dupes touchend -> synthesized click
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const [box, setBox] = useState(null);

  function attachRef(el) {
    innerRef.current = el;
    if (typeof extInputRef === "function") extInputRef(el);
    else if (extInputRef && typeof extInputRef === "object") extInputRef.current = el;
  }

  const close = useCallback(() => { setOpen(false); setActive(-1); }, []);

  // Measure the field in viewport coordinates for the fixed-position portal.
  const measure = useCallback(() => {
    const el = innerRef.current;
    if (!el || typeof window === "undefined") return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth || r.width;
    const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight || 640;
    const below = vh - r.bottom;
    const above = r.top;
    const flip = below < 150 && above > below;   // keyboard is up — open upward
    setBox({
      left: Math.max(6, Math.min(r.left, vw - Math.min(r.width, vw - 12) - 6)),
      width: Math.max(160, Math.min(r.width, vw - 12)),
      top: flip ? null : Math.round(r.bottom + 4),
      bottom: flip ? Math.round(vh - r.top + 4) : null,
      maxHeight: Math.max(110, Math.min(260, (flip ? above : below) - 14)),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    measure();
    const outside = (e) => {
      const t = e.target;
      if (wrapRef.current && wrapRef.current.contains(t)) return;
      if (popRef.current && popRef.current.contains(t)) return;
      close();
    };
    const reflow = () => measure();
    document.addEventListener("mousedown", outside, true);
    document.addEventListener("touchstart", outside, true);
    window.addEventListener("scroll", reflow, true);
    window.addEventListener("resize", reflow);
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (vv) { vv.addEventListener("resize", reflow); vv.addEventListener("scroll", reflow); }
    return () => {
      document.removeEventListener("mousedown", outside, true);
      document.removeEventListener("touchstart", outside, true);
      window.removeEventListener("scroll", reflow, true);
      window.removeEventListener("resize", reflow);
      if (vv) { vv.removeEventListener("resize", reflow); vv.removeEventListener("scroll", reflow); }
    };
  }, [open, measure, close]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  function search(raw) {
    if (timerRef.current) clearTimeout(timerRef.current);
    const q = String(raw == null ? "" : raw).trim();
    if (failRef.current >= 2) return;                 // endpoint is down — plain field from here on
    if (q.length < SS_ADDR_MIN_CHARS) {
      seqRef.current++;
      setItems([]); setEmpty(false); setLoading(false); setOpen(false);
      return;
    }
    setLoading(true); setEmpty(false); setOpen(true);
    timerRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      let out = null;
      try { out = await ssAddressSuggest(q); failRef.current = 0; }
      catch (e) {
        failRef.current++;
        console.warn("[address] autocomplete unavailable:", (e && e.message) || e);
      }
      if (seq !== seqRef.current) return;             // superseded by a newer keystroke
      setLoading(false);
      if (out === null) { setItems([]); setEmpty(false); setOpen(false); return; }
      setItems(out); setActive(-1); setEmpty(out.length === 0); setOpen(true);
      measure();
    }, SS_ADDR_DEBOUNCE_MS);
  }

  function handleInput(e) {
    const v = e.target.value;
    if (onChange) onChange(v);
    search(v);
  }

  function pick(s) {
    if (!s) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    seqRef.current++;                                  // discard any in-flight lookup
    setOpen(false); setItems([]); setEmpty(false); setLoading(false); setActive(-1);
    const p = (s.parsed && typeof s.parsed === "object") ? s.parsed : {};
    const text = String(s.description || s.formatted || s.mainText || "").trim();
    // Same order as the web client: push the full formatted line, then let the call
    // site's onAddressParsed narrow the street field and fill city / state / ZIP.
    if (text) {
      if (!controlled && innerRef.current) innerRef.current.value = text;
      if (onChange) onChange(text);
    }
    if (onAddressParsed) onAddressParsed({
      street: p.street || p.street_line || s.mainText || "",
      city: p.city || "",
      state: p.state || "",
      zip: p.zip || p.zipcode || "",
      county: p.county || "",
      lat: s.lat != null ? s.lat : (p.lat != null ? p.lat : null),
      lng: s.lng != null ? s.lng : (p.lng != null ? p.lng : null),
      formatted: text,
      source: s.source || "",
      verified: true,
    });
    try { innerRef.current && innerRef.current.focus({ preventScroll: true }); } catch (_) {}
  }

  function keyDown(e) {
    if (onKeyDown) onKeyDown(e);
    if (e.key === "Escape") { if (open) { e.preventDefault(); close(); } return; }
    if (!open || items.length === 0) return;           // never swallow keys with nothing to navigate
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(i => (i + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(i => (i <= 0 ? items.length - 1 : i - 1)); }
    else if (e.key === "Enter") {
      if (active >= 0 && active < items.length) { e.preventDefault(); pick(items[active]); }
      else close();                                    // no highlight -> Enter behaves normally
    }
  }

  const rowBase = {
    padding: "11px 12px", cursor: "pointer", textAlign: "left",
    borderBottom: `1px solid ${C.steelXXL}`, WebkitTapHighlightColor: "transparent",
  };

  const dropdown = (open && box && !disabled && !readOnly && typeof document !== "undefined")
    ? createPortal(
        <div
          ref={popRef}
          style={{
            position: "fixed", zIndex: 100000,
            left: box.left, width: box.width,
            ...(box.top != null ? { top: box.top } : { bottom: box.bottom }),
            maxHeight: box.maxHeight, overflowY: "auto",
            WebkitOverflowScrolling: "touch", overscrollBehavior: "contain",
            background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 10,
            boxShadow: "0 10px 28px rgba(10,22,40,.18)",
          }}>
          {loading && items.length === 0 && (
            <div style={{ padding: "11px 12px", fontSize: 12, color: C.steel }}>Looking up addresses…</div>
          )}
          {!loading && empty && items.length === 0 && (
            <div style={{ padding: "11px 12px", fontSize: 12, color: C.steel, lineHeight: 1.4 }}>
              No matches — keep typing to enter the address by hand.
            </div>
          )}
          {items.map((it, i) => {
            const main = it.mainText || it.description || "";
            const sec = it.secondaryText || "";
            return (
              <div
                key={(it.placeId || main) + "|" + i}
                onMouseDown={e => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onTouchStart={e => {
                  const t = e.touches && e.touches[0];
                  touchRef.current = { x: t ? t.clientX : 0, y: t ? t.clientY : 0, moved: false };
                }}
                onTouchMove={e => {
                  const t = e.touches && e.touches[0];
                  if (!t) return;
                  if (Math.abs(t.clientY - touchRef.current.y) > 8 || Math.abs(t.clientX - touchRef.current.x) > 8) {
                    touchRef.current.moved = true;     // this is a scroll, not a tap
                  }
                }}
                onTouchEnd={e => {
                  if (touchRef.current.moved) return;
                  e.preventDefault();
                  pickedAtRef.current = Date.now();
                  pick(it);
                }}
                onClick={() => {
                  if (Date.now() - pickedAtRef.current < 700) return;  // already handled by touchend
                  pick(it);
                }}
                style={{
                  ...rowBase,
                  borderBottom: i < items.length - 1 ? rowBase.borderBottom : "none",
                  background: active === i ? C.bgW : "transparent",
                }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.deep }}>{main}</div>
                {sec ? <div style={{ fontSize: 11.5, color: C.steel, marginTop: 1 }}>{sec}</div> : null}
              </div>
            );
          })}
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={wrapRef} style={{ position: "relative", ...(wrapStyle || {}) }}>
      <input
        ref={attachRef}
        type={type}
        {...(controlled
          ? { value: value || "", onChange: handleInput }
          : { defaultValue: defaultValue, onInput: handleInput })}
        onFocus={e => {
          if (onFocus) onFocus(e);
          const cur = controlled ? value : (innerRef.current && innerRef.current.value);
          if (items.length > 0 && String(cur || "").trim().length >= SS_ADDR_MIN_CHARS) { setOpen(true); measure(); }
        }}
        onBlur={onBlur}
        onKeyDown={keyDown}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoCorrect="off"
        spellCheck={false}
        inputMode={inputMode}
        maxLength={maxLength}
        disabled={disabled}
        readOnly={readOnly}
        id={id}
        name={name}
        style={style}
      />
      {dropdown}
    </div>
  );
}

// Mock JOBS/NOTIFS/TRAINING arrays removed: AppInner hydrates jobs from
// /api/jobs, NotificationsSheet reads /api/notifications, and TrainingScreen
// uses TRAINING_VIDEOS + /api/training/videos as the seed/source of truth.

// ═══════════════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════════════
function AppInner({ __ssUser, __ssSetUser }) {
  // ── Phase 4: real-data hooks ─────────────────────────────────────────
  const user = __ssUser;
  // Hydrate instantly from localStorage cache so the UI never starts empty.
  // Stale-while-revalidate: show cached jobs immediately, refresh in background.
  const [liveJobs, setLiveJobs] = useState(() => {
    try {
      const cached = localStorage.getItem("ss_jobs_cache");
      if (cached) {
        const arr = JSON.parse(cached);
        /* [fc-crash-fix-2026-09-11] Never trust the cache's shape. A device that
           signed in on an earlier build still holds RAW rows here, and a raw row's
           `attempts` is an array of attempt objects — rendering one is React #31 and
           a blank app. _ssRehydrateCachedJob normalizes anything that is not already
           a normalized job, so a stale cache can no longer take the shell down. */
        if (Array.isArray(arr)) return arr.map(_ssRehydrateCachedJob);
      }
    } catch {}
    return null; // null = loading, [] = none, [...] = real
  });
  const [jobsErr, setJobsErr] = useState("");
  const reload = useCallback(async () => {
    try {
      setJobsErr("");
      const data = await api.get("/api/jobs");
      const normalized = Array.isArray(data) ? data.map(_normalizeJob) : [];
      setLiveJobs(normalized);
      try { localStorage.setItem("ss_jobs_cache", JSON.stringify(normalized)); } catch {}
      try { writeJobsCacheNative(normalized); } catch {}
    } catch (e) { setJobsErr(e.message || "Could not load jobs"); }
  }, []);
  // On mount: if AuthGate or LoginScreen already kicked off /api/jobs, await
  // that promise instead of starting a duplicate fetch. Falls back to fresh
  // reload() if no prefetch exists.
  useEffect(() => {
    const prefetch = window.__ssJobsPrefetch;
    if (prefetch && typeof prefetch.then === "function") {
      let alive = true;
      prefetch.then(data => {
        window.__ssJobsPrefetch = null;
        if (!alive) return;
        if (Array.isArray(data)) {
          const normalized = data.map(_normalizeJob);
          setLiveJobs(normalized);
          try { localStorage.setItem("ss_jobs_cache", JSON.stringify(normalized)); } catch {}
          try { writeJobsCacheNative(normalized); } catch {}
        } else {
          reload();
        }
      });
      return () => { alive = false; };
    }
    reload();
    // Drain any serve attempts queued by the paired Apple Watch while the JS
    // bundle wasn't running. Safe to call even if no Watch is paired.
    try { drainDeferredWatchAttempts(api); } catch {}
  }, [reload]);

  // ── Freshness: refresh-on-focus, app-resume, and a recurring jobs poll ──
  // Keeps the jobs feed near-real-time so claims/reassignments by others (and
  // any changes made on web) surface without a manual pull. A shared 3s debounce
  // prevents a resume + visibilitychange firing together from double-fetching.
  useEffect(() => {
    let alive = true;
    let lastReload = 0;
    const refresh = () => {
      if (!alive) return;
      const now = Date.now();
      if (now - lastReload < 3000) return; // debounce coalesced triggers
      lastReload = now;
      reload();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    // Capacitor app-state resume via the global bridge (no ESM import). The App
    // plugin is installed; guard every hop in case it isn't on a given build.
    let capRemove = null;
    try {
      const App = ssPlugin("App"); // [server-parity-web-2026-10-05]
      if (App && typeof App.addListener === "function") {
        const handle = App.addListener("appStateChange", (s) => {
          if (s && s.isActive) refresh();
        });
        // addListener may return a handle directly or a promise of one.
        if (handle && typeof handle.then === "function") {
          handle.then(h => { capRemove = h; if (!alive && h && h.remove) h.remove(); });
        } else {
          capRemove = handle;
        }
      }
    } catch (_) { /* plugin missing — focus + poll still cover freshness */ }
    // Recurring poll while the jobs view is mounted (every 15s). Goes through
    // the same debounce so it won't pile on top of a focus-triggered reload.
    const pollId = setInterval(refresh, 15000);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(pollId);
      try { if (capRemove && typeof capRemove.remove === "function") capRemove.remove(); } catch (_) {}
    };
  }, [reload]);
  // liveJobs is null while the first fetch is in flight (and no cache hit);
  // render an empty list rather than mock data — the loading state is handled
  // downstream by screens that check for empty arrays.
  const jobs = Array.isArray(liveJobs) ? liveJobs : [];
  const logout = async () => {
    // Unregister the push token before clearing auth so future pushes don't
    // deliver to a signed-out app. Awaited so the JWT is still valid when the
    // unregister request fires.
    try { await unregisterPushNotifications(); } catch (_) {}
    try { await bioDisable("server"); } catch (_) {} /* [biometric-login-2026-10-11] sign-out turns Face ID off */
    api.logout();
    // Ensure the native-stored JWT is fully cleared before we leave, so the
    // role launcher (and any role we pick next) doesn't auto-resume the session.
    try {
      const P = ssPlugin("Preferences"); // [server-parity-web-2026-10-05]
      if (P) await P.remove({ key: "ss_jwt" });
    } catch (_) {}
    __ssSetUser(null);
    // Return to the role chooser (Process Server / Client / Agency / Admin).
    try { window.location.replace("index.html"); } catch (_) { window.location.href = "index.html"; }
  };
  const isTablet = useIsTablet();
  // Route polyline: array of {lat,lng} the user planned via RoutePlanner. When
  // non-null, MapView draws a gold connecting line + numbered markers. Cleared
  // via the "Clear route" pill in the Map header.
  const [routePolyline, setRoutePolyline] = useState(null);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  // jobsLoading: true while the first /api/jobs fetch is in flight AND we
  // have no cached list to show. Consumed by ListView / MapView to render
  // skeletons instead of an empty state on cold launch.
  const jobsLoading = liveJobs === null;
  // setUser is exposed so screens (e.g. ProfileScreen) can reflect profile/signature
  // saves app-wide without a full reload — mirrors web app's AppCtx setUser/refreshUser.
  // ── Dark mode ─────────────────────────────────────────────────────────
  // `dark` mirrors the persisted ss_dark_mode flag. Toggling it (from the
  // TopBar icon or from SettingsScreen via the ss-toggle-dark event)
  // re-applies palette C and re-renders the whole tree so every C.* read
  // refreshes. We bump _themeV purely to guarantee a re-render.
  const [dark, setDark] = useState(() => ssIsDarkPref());
  const [, _setThemeV] = useState(0);
  const setDarkMode = useCallback((v) => {
    applyTheme(v);
    try { localStorage.setItem("ss_dark_mode", v ? "1" : "0"); } catch {}
    setDark(v);
    _setThemeV(n => n + 1);
    // Notify any open SettingsScreen toggle so it stays in sync.
    try { window.dispatchEvent(new CustomEvent("ss-dark-changed", { detail: v })); } catch {}
  }, []);

  // [live-route-2026-09-20] Tablet sidebar visibility. Defaults CLOSED — it was a
  // permanently mounted 260px column that ate a fifth of every iPad screen whether
  // or not anyone wanted it. The choice is remembered, so someone who prefers it
  // pinned open is not made to reopen it on every launch.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try { return localStorage.getItem("ss_tablet_sidebar") === "1"; } catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("ss_tablet_sidebar", sidebarOpen ? "1" : "0"); } catch (_) {}
  }, [sidebarOpen]);

  /* [distance-fix-2026-09-20] Real distances, computed once here rather than in
     each of the six places that display one. Attaching to the job object means
     every existing consumer keeps reading `job.dist` and simply starts getting
     a true number — or null, where the job has no coordinates. As of the
     2026-09-20 backfill that is still 13 of 29 historical jobs, so the null
     path is the common case, not an edge case. */
  const distOrigin = useDistanceOrigin(user);
  const jobsWithDist = React.useMemo(
    () => (jobs || []).map((j) => ({ ...j, dist: ssJobMiles(distOrigin, j) })),
    [jobs, distOrigin]
  );

  const ctxValue = { user, setUser: __ssSetUser, jobs: jobsWithDist, jobsLoading, jobsErr /* [jobs-empty-reason-2026-10-18] */, reload, logout, isTablet, routePolyline, setRoutePolyline, unreadNotifs, setUnreadNotifs, dark, setDarkMode };

  // Register for push notifications once we have a confirmed user + token.
  // Re-runs only when user.id changes (login/logout) so we don't hammer
  // requestPermissions() on every render. Runtime-safe before the
  // @capacitor/push-notifications plugin is installed — see helper above.
  useEffect(() => {
    if (!user || !user.id) {
      // Transition to signed-out (ss-session-expired or explicit logout) —
      // forget the push token server-side so future notifs don't deliver to
      // an unauthenticated app. Best-effort; swallow errors so unmount doesn't
      // throw.
      try { unregisterPushNotifications(); } catch (_) {}
      return;
    }
    if (typeof api._jwt !== "string" || !api._jwt) return;
    registerPushNotifications();
    // Optional: react to the foreground "push received" event by refreshing the bell.
    function onReceived() {
      try { api.get("/api/notifications").then(arr => {
        if (Array.isArray(arr)) setUnreadNotifs(arr.filter(n => !n.read).length);
      }); } catch (_) {}
    }
    window.addEventListener("ss-push-received", onReceived);
    return () => window.removeEventListener("ss-push-received", onReceived);
  }, [user && user.id]);

  // Poll /api/notifications every 25s (and once on mount) so the bell badge
  // reflects the real unread count. Tightened from 60s to roughly match the
  // web app's cadence; also refreshes on focus/resume via the handler below.
  useEffect(() => {
    let alive = true;
    async function tick() {
      try {
        const arr = await api.get("/api/notifications");
        if (!alive) return;
        if (Array.isArray(arr)) {
          setUnreadNotifs(arr.filter(n => !n.read).length);
        }
      } catch (e) {
        // swallow — polling is best-effort
      }
    }
    tick();
    const id = setInterval(tick, 25000);
    // Piggyback a refresh on app-foreground so the badge isn't stale right
    // after a resume; the recurring poll still runs underneath.
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  const [tab, setTab] = useState("jobs");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [drawerScreen, setDrawerScreen] = useState(null);
  const [routePlannerOpen, setRoutePlannerOpen] = useState(false);

  /* [field-command-2026-09-11] The Command tab only exists in the tablet shell. If a
     device ever falls back to the phone shell while it is selected (kill switch
     flipped at runtime, viewport resized below 768px), the phone branch has no case
     for it and would paint an empty pane. Land on Jobs instead. Declared here, below
     the `tab` state, because the dependency array reads `tab` during render. */
  useEffect(() => {
    if ((!isTablet || !SS_FIELD_COMMAND_ENABLED) && tab === "command") setTab("jobs");
  }, [isTablet, tab]);

  // ── [onboarding-wizard-2026-06-23] POST-APPROVAL ONBOARDING GATE ──────────
  // An approved server must finish the 6-step onboarding flow (agreement →
  // training → payout → signature → profile → w9) before the backend lets them
  // claim jobs. We load status once and, ONLY when it is explicitly false,
  // take over the screen with <OnboardingWizard locked/>.
  //   null  = unknown / not-loaded-yet / failed  → NEVER locks (backend is source of truth)
  //   true  = fully onboarded → no lock
  //   false = onboarding outstanding → lock (EXPLICIT false only)
  const [onboardingComplete, setOnboardingComplete] = useState(null);
  // [onboarding-activation-2026-07-01] One-time "you're active" modal shown the moment
  // onboarding transitions from locked (false) to complete (true) — previously the gate
  // unmounted the wizard instantly and the server was never told they can accept jobs.
  const [justActivated, setJustActivated] = useState(false);
  // [cert-of-completion-2026-07-03] certificate overlay — auto-opens on activation; opens via 'ss-open-certificate'
  const [showCert, setShowCert] = useState(false);
  useEffect(() => { if (justActivated) setShowCert(true); }, [justActivated]);
  useEffect(() => { const onOpen = () => setShowCert(true); window.addEventListener('ss-open-certificate', onOpen); return () => window.removeEventListener('ss-open-certificate', onOpen); }, []);
  const _setOnbComplete = (next) => {
    setOnboardingComplete(prev => { if (prev === false && next === true) setJustActivated(true); return next; });
  };
  useEffect(() => {
    // Only approved servers are ever gated. The status may arrive as camelCase
    // or raw snake_case — accept both. null/error leaves the app open.
    if (!user || user.role !== "server" || _appStatus(user) !== "approved") {
      setOnboardingComplete(null);
      return;
    }
    let cancelled = false;
    const refetch = async () => {
      try {
        const s = await api.get("/api/onboarding/status");
        if (!cancelled && s) { if (s.onboardingCompletedAt) _ssOnbCompletedAt = s.onboardingCompletedAt; _setOnbComplete(!!s.onboardingComplete); }
      } catch (_) {
        // Don't hard-lock on a fetch failure — leave it untouched; the backend
        // still enforces the claim block (403 ONBOARDING_INCOMPLETE).
      }
    };
    refetch();
    const onUpd = (e) => {
      if (e && e.detail && typeof e.detail.onboardingComplete === "boolean") {
        if (!cancelled) _setOnbComplete(e.detail.onboardingComplete);
      } else {
        refetch();
      }
    };
    window.addEventListener("cle-onboarding-updated", onUpd);
    return () => { cancelled = true; window.removeEventListener("cle-onboarding-updated", onUpd); };
  }, [user?.id, user?.role, user?.applicationStatus, user?.application_status]);

  // True only when an approved server's onboarding is EXPLICITLY incomplete.
  // null/true never locks; admins, clients, and non-approved servers are
  // never affected. The Account tab stays reachable while locked so the user
  // can complete payout/signature/profile/W-9 steps and is never trapped.
  const ONBOARDING_LOCK = !!(user && user.role === "server" && _appStatus(user) === "approved"
    && onboardingComplete === false);

  // [onboarding-activation-2026-07-01] Shared "you're active" modal — rendered by both
  // the phone and tablet shells the moment onboarding flips complete (the lock unmounts
  // the wizard before its success card can ever be seen).
  const activationModal = (justActivated && !showCert) ? (
    <div style={{ position: "absolute", inset: 0, zIndex: 10000, background: "rgba(10,22,40,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: C.white, borderRadius: 18, padding: "30px 22px", textAlign: "center", maxWidth: 330, width: "100%", boxShadow: "0 18px 50px rgba(10,22,40,0.35)" }}>
        <div style={{ width: 60, height: 60, borderRadius: "50%", background: C.okL, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <CheckCircle2 size={32} color={C.ok} />
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.deep, marginBottom: 8, fontFamily: "Fraunces, Georgia, serif" }}>You're now active</div>
        <div style={{ fontSize: 13, color: C.steel, lineHeight: 1.55, marginBottom: 18 }}>
          Your onboarding is complete — you can start accepting jobs right away. Payouts go straight to the bank account you connected.
        </div>
        <button style={{ width: "100%", padding: "12px 16px", background: C.gold, color: C.deep, border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer", marginBottom: 8 }}
          onClick={() => setShowCert(true)}>View your certificate</button>
        <button style={{ width: "100%", padding: "12px 16px", background: C.deep, color: C.white, border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer", marginBottom: 8 }}
          onClick={() => { setJustActivated(false); setTab("jobs"); }}>Start accepting jobs</button>
        <button style={{ width: "100%", padding: "10px 16px", background: C.white, color: C.steel, border: `1px solid ${C.steelXL}`, borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
          onClick={() => setJustActivated(false)}>Close</button>
      </div>
    </div>
  ) : null;

  const certOverlay = showCert ? (<StrikeServeCertificate user={user} onClose={() => setShowCert(false)} />) : null;

  // App-level toast (e.g. QR-scan / document-scan feedback). Fired via a
  // window "ss-toast" CustomEvent so deep components don't need a threaded
  // setter. { kind: "ok" | "err", text }.
  const [appToast, setAppToast] = useState(null);
  useEffect(() => {
    function onToast(ev) {
      const d = ev && ev.detail;
      if (!d || !d.text) return;
      setAppToast({ kind: d.kind === "err" ? "err" : "ok", text: String(d.text) });
    }
    window.addEventListener("ss-toast", onToast);
    return () => window.removeEventListener("ss-toast", onToast);
  }, []);
  useEffect(() => {
    if (!appToast) return;
    const t = setTimeout(() => setAppToast(null), 3200);
    return () => clearTimeout(t);
  }, [appToast]);

  useEffect(() => {
    // Ensure palette matches the flag on mount (covers a stale module value
    // after a JS reload without a full process restart).
    applyTheme(dark);
    function onToggle(ev) { setDarkMode(!!(ev && ev.detail)); }
    window.addEventListener("ss-toggle-dark", onToggle);
    return () => window.removeEventListener("ss-toggle-dark", onToggle);
  }, [dark, setDarkMode]);

  // Cross-component bridge: deep components (e.g. NotificationsSheet) can ask
  // AppInner to open a specific drawer screen by firing a window CustomEvent.
  // This avoids having to thread a setter through every sheet/screen.
  useEffect(() => {
    function onOpen(ev) {
      const screen = ev && ev.detail;
      if (typeof screen === "string" && screen.length) {
        setBellOpen(false);
        setDrawerScreen(screen);
      }
    }
    window.addEventListener("ss-open-drawer-screen", onOpen);
    return () => window.removeEventListener("ss-open-drawer-screen", onOpen);
  }, []);

  /* [pos-overhaul-2026-10-07:server-app] "sign:<jobId>" deep link (push tap, bell,
     web ?sign=<jobId>) opens review-before-sign for that job directly. Lives at the
     shell so it works from any tab and on a cold start before the job list loads —
     the sheet fetches its own draft; the queue row (if present) supplies the header. */
  const [signTarget, setSignTarget] = useState(null); // { jobId, meta }
  useEffect(() => {
    function onOpenSign(ev) {
      const d = (ev && ev.detail) || {};
      const id = d.jobId != null ? String(d.jobId) : "";
      if (!id) return;
      setBellOpen(false);
      setDrawerScreen(null);
      setSignTarget({ jobId: id, meta: d.meta || null });
      if (!d.meta) {
        api.get("/api/server/pos-queue").then((r) => {
          const it = ssNormalizePosQueue(r).find((x) => x.jobId === id);
          if (it) setSignTarget((cur) => (cur && cur.jobId === id && !cur.meta ? { jobId: id, meta: it } : cur));
        }).catch(() => {});
      }
    }
    window.addEventListener("ss-open-sign", onOpenSign);
    try {
      const qs = new URLSearchParams(window.location.search || "");
      const sid = qs.get("sign");
      if (sid && /^[\w-]{1,64}$/.test(sid)) setTimeout(() => ssOpenSign(sid), 0);
    } catch (_) { /* no URL params on this shell */ }
    return () => window.removeEventListener("ss-open-sign", onOpenSign);
  }, []);
  /* Keep the Proofs badge fresh: every 60s, on foreground push, and on resume. */
  useEffect(() => {
    if (!user || !user.id) return undefined;
    ssRefreshPosSignCount();
    const id = setInterval(ssRefreshPosSignCount, 60000);
    const onPush = () => ssRefreshPosSignCount();
    const onVis = () => { if (document.visibilityState === "visible") ssRefreshPosSignCount(); };
    window.addEventListener("ss-push-received", onPush);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); window.removeEventListener("ss-push-received", onPush); document.removeEventListener("visibilitychange", onVis); };
  }, [user && user.id]);
  const signSheet = signTarget ? (
    <PosSignReviewSheet key={signTarget.jobId} jobId={signTarget.jobId} meta={signTarget.meta} onClose={() => setSignTarget(null)} />
  ) : null;

  // [notif-link-2026-08-08] "tab:<name>" deep-link target — see TAB_IDS.
  useEffect(() => {
    function onOpenTab(ev) {
      const id = ev && typeof ev.detail === "string" ? ev.detail : "";
      if (!TAB_IDS.includes(id)) return;
      setBellOpen(false);
      setDrawerScreen(null);
      setTab(id);
    }
    window.addEventListener("ss-open-tab", onOpenTab);
    return () => window.removeEventListener("ss-open-tab", onOpenTab);
  }, []);

  // Notification deep-link: a "job:<id>" / "/jobs/<id>" action_url fires
  // ss-open-job; route to that job's detail (matching id or jobNumber).
  useEffect(() => {
    function onOpenJob(ev) {
      const id = ev && ev.detail != null ? String(ev.detail) : "";
      if (!id) return;
      const match = (jobs || []).find(j =>
        String(j.id) === id || String(j.jobNumber) === id || String(j._raw?.id) === id
      );
      if (match) {
        setBellOpen(false);
        setTab("jobs");
        setSelectedJob(match);
      } else {
        /* [open-job-fetch-2026-10-08] Not in the loaded list (cold start, list still
           loading, or the job is past the first page) — this used to do nothing. Hand
           off to the ss-push-open-job resolver below, which fetches the job from the
           API, opens it, and toasts when it is not found. (There is no server-visible
           GET /api/jobs/:id; that resolver's GET /api/jobs lookup is the single-job
           path the app already uses.) */
        setBellOpen(false);
        window.dispatchEvent(new CustomEvent("ss-push-open-job", { detail: id }));
      }
    }
    window.addEventListener("ss-open-job", onOpenJob);
    return () => window.removeEventListener("ss-open-job", onOpenJob);
  }, [jobs]);

  // [field-view-2026-09-07] Field View deep-link. The Field View is no longer a
  // bottom tab — it is entered from a Jobs row action (ListJobCard footer, map
  // claim bar). Those components sit too deep to receive setTab as a prop and
  // setTab is NOT in AppCtx, so they fire this event instead. Payload + lookup
  // mirror ss-open-job above exactly: detail is the job id as a string.
  useEffect(() => {
    function onOpenField(ev) {
      const id = ev && ev.detail != null ? String(ev.detail) : "";
      if (!id) return;
      const match = (jobs || []).find(j =>
        String(j.id) === id || String(j.jobNumber) === id || String(j._raw?.id) === id
      );
      if (!match) return;
      setBellOpen(false);
      setSelectedJob(match);
      setTab("field");
    }
    window.addEventListener("ss-open-field", onOpenField);
    return () => window.removeEventListener("ss-open-field", onOpenField);
  }, [jobs]);

  // QR-scan deep-link: TopBar fires ss-scan-open-job with a raw QR token. Mirror
  // the web app's ?scan=<jobId> contract — resolve to a job (loaded list first,
  // then GET /api/jobs by id / job_number / tracking_token) and open its detail.
  // A toast (window "ss-toast" event) reports not-found so the user gets feedback.
  useEffect(() => {
    async function onScanOpenJob(ev) {
      const raw = ev && ev.detail != null ? String(ev.detail).trim() : "";
      if (!raw) return;
      // The QR encodes `<origin>?scan=<jobId>`. If raw parses as a URL, pull the
      // `scan` query param; otherwise treat the raw value as the token itself.
      let token = raw;
      try {
        if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
          const u = new URL(raw);
          token = u.searchParams.get("scan") || u.searchParams.get("track") || token;
        }
      } catch (_) { /* not a URL — use raw as the token */ }
      token = String(token || "").trim();
      if (!token) return;
      const matches = (j) =>
        String(j.id) === token ||
        String(j.jobNumber) === token ||
        String(j._raw?.id) === token ||
        String(j._raw?.job_number) === token ||
        String(j._raw?.tracking_token) === token;
      // 1) Already-loaded jobs list.
      let match = (jobs || []).find(matches);
      // 2) Fall back to a fresh GET /api/jobs lookup.
      if (!match) {
        try {
          // [open-job-fetch-2026-10-08] max page (server default is 100) so an older job is still found
          const r = await api.get("/api/jobs?limit=500");
          const arr = Array.isArray(r) ? r : (Array.isArray(r && r.jobs) ? r.jobs : []);
          const rawFound = arr.find(x =>
            String(x.id) === token ||
            String(x.job_number) === token ||
            String(x.jobNumber) === token ||
            String(x.tracking_token) === token
          );
          if (rawFound) match = _normalizeJob(rawFound);
        } catch (_) { /* network/auth error — fall through to not-found toast */ }
      }
      if (match) {
        setBellOpen(false);
        setTab("jobs");
        setSelectedJob(match);
      } else {
        try { window.dispatchEvent(new CustomEvent("ss-toast", { detail: { kind: "err", text: "Job not found or not assigned to you." } })); } catch (_) {}
      }
    }
    window.addEventListener("ss-scan-open-job", onScanOpenJob);
    // [notif-link-2026-08-08] Background push taps land here too. Until now
    // pushNotificationActionPerformed dispatched "ss-push-open-job" and NOTHING
    // in the app listened for it — grep returned two dispatches and zero
    // addEventListener. So every tap on a background push has been a silent
    // no-op since the handler was written: the app opened to the default tab and
    // the server was left to work out what had pinged them. This handler is the
    // right target rather than "ss-open-job" because it is the only one that
    // survives a cold start — it falls back to GET /api/jobs when the job list
    // has not loaded yet, which is the normal state when a push launches the app
    // from terminated, and it toasts on not-found instead of failing silently.
    window.addEventListener("ss-push-open-job", onScanOpenJob);
    return () => {
      window.removeEventListener("ss-scan-open-job", onScanOpenJob);
      window.removeEventListener("ss-push-open-job", onScanOpenJob);
    };
  }, [jobs]);

  // Wrap centered screens with a readable max-width on tablet. Phone keeps the
  // edge-to-edge feel; tablet centers Home/Proofs/Earnings/Account.
  const tabletCenter = (node) => isTablet
    ? <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px 32px" }}>{node}</div>
    : node;

  // ── Phone shell (unchanged) ─────────────────────────────────────────
  if (!isTablet) {
    return (
      <AppCtx.Provider value={ctxValue}>
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        background: C.bg,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: 0, overflow: "hidden",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
      }}>
        <PhoneFrame>
          <TopBar tab={tab} onMenu={() => setDrawerOpen(true)} onBell={() => setBellOpen(true)} unread={unreadNotifs} />

          <div style={{ flex: 1, overflow: "auto", background: C.bg, position: "relative" }}>
            {/* [onboarding-wizard-2026-06-23] LOGIN-LOCK GATE — an approved server
                whose onboarding is explicitly incomplete is locked into the wizard
                on every tab EXCEPT Account (where payout/signature/profile steps
                deep-link). null/true never locks; admins, clients, and
                non-approved servers are never affected. */}
            {ONBOARDING_LOCK && tab !== "account" ? (
              <OnboardingWizard
                user={user}
                locked
                goToAccount={(screen) => { setTab("account"); if (screen) setDrawerScreen(screen); }}
                onDone={() => setTab("jobs")}
              />
            ) : (
              <>
                {/* [offer-banner-fix-2026-10-20] Offer banner on EVERY tab until accept / decline / expiry (Home renders its own). */}
                {tab !== "home" && <div style={{ padding: "12px 16px 0" }}><OfferBanner /></div>}
                {tab === "home" && <HomeScreen setTab={setTab} setSelectedJob={setSelectedJob} />}
                {tab === "proofs" && <ProofsScreen />}
                {tab === "jobs" && <JobsScreen setSelectedJob={setSelectedJob} setRoutePlannerOpen={setRoutePlannerOpen} />}
                {tab === "earnings" && <EarningsScreen />}
                {tab === "field" && <FieldView selectedJob={selectedJob} setSelectedJob={setSelectedJob} setTab={setTab} />}{/* [field-view-2026-09-07] */}
                {tab === "account" && <AccountScreen setDrawerScreen={setDrawerScreen} />}
              </>
            )}
          </div>

          <SafetyFAB onPress={() => setSafetyOpen(true)} />
          <BottomTabs tab={tab} setTab={setTab} />

          {drawerOpen && <Drawer onClose={() => setDrawerOpen(false)} setDrawerScreen={(s) => { setDrawerScreen(s); setDrawerOpen(false); }} />}
          {bellOpen && <NotificationsSheet onClose={() => setBellOpen(false)} />}
          {safetyOpen && <SafetySheet onClose={() => setSafetyOpen(false)} />}
          {/* [field-view-2026-09-07] the Field tab OWNS selectedJob while it is
              active — without this guard picking a job in Field View would also
              pop JobDetailSheet over the top of it. */}
          {selectedJob && tab !== "field" && <JobDetailSheet job={selectedJob} onClose={() => setSelectedJob(null)} />}
          {drawerScreen && <DrawerScreen screen={drawerScreen} onClose={() => setDrawerScreen(null)} />}
          {routePlannerOpen && <RoutePlanner onClose={() => setRoutePlannerOpen(false)} />}
          {signSheet}{/* [pos-overhaul-2026-10-07:server-app] */}
          {activationModal}{certOverlay}
          <GlobalToast toast={appToast} />
          <DocViewerHost />{/* [server-parity-web-2026-10-05] in-app PDF/image viewer */}
          <TicketPromptHost />{/* [support-tickets-2026-10-08] "Need help with this job?" after a failed attempt */}
          <DoorTagPromptHost />{/* [door-tags-server-2026-10-22] required door-tag follow-up after a non-served attempt */}
          <TicketLinkHost />{/* [ticket-link-2026-10-08] ticket:<id> notification deep links */}
        </PhoneFrame>
      </div>
      </AppCtx.Provider>
    );
  }

  /* [fc-standalone-2026-09-11] Field Command on its own URL: /?command=1
     Renders the dash full-screen with no shell around it — no sidebar, no tab
     bar, no split panes. The tablet shell has never been validated on a real
     iPad (its sidebar failed to paint on one), and the dash does not need it:
     a windscreen-mounted screen wants the whole glass anyway. This also means
     Field Command can be reached from a phone or a home-screen shortcut.
     Placed here, after every hook has run, so the early return is safe. */
  if (SS_COMMAND_STANDALONE) {
    return (
      <AppCtx.Provider value={ctxValue}>
        <div style={{
          position: "fixed", inset: 0, background: C.bg,
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
        }}>
          <SsPanelBoundary name="Field Command">
            <CommandView setTab={setTab} setSelectedJob={setSelectedJob} />
          </SsPanelBoundary>
          {selectedJob && <JobDetailSheet job={selectedJob} onClose={() => setSelectedJob(null)} />}
          {signSheet}{/* [pos-overhaul-2026-10-07:server-app] */}
          <GlobalToast toast={appToast} />
          <DocViewerHost />{/* [server-parity-web-2026-10-05] in-app PDF/image viewer */}
          <TicketPromptHost />{/* [support-tickets-2026-10-08] "Need help with this job?" after a failed attempt */}
          <DoorTagPromptHost />{/* [door-tags-server-2026-10-22] required door-tag follow-up after a non-served attempt */}
          <TicketLinkHost />{/* [ticket-link-2026-10-08] ticket:<id> notification deep links */}
        </div>
      </AppCtx.Provider>
    );
  }

  // ── Tablet shell (iPad split-pane) ──────────────────────────────────
  // Permanent left sidebar + right pane that hosts TopBar, content, BottomTabs.
  // Drawer is NOT rendered on tablet — sidebar replaces it. JobDetail renders
  // inline as a third pane inside JobsScreen rather than as a bottom sheet.
  return (
    <AppCtx.Provider value={ctxValue}>
      {/* v1.1 tablet shell parent — was `position: fixed; inset: 0;` which
          collapsed to 0×0 on iPad's first paint when the offset parent hadn't
          resolved yet. Use explicit viewport units + flex row so the parent
          gets a deterministic size and the right pane flexes into space
          without depending on absolute offsets. */}
      <div style={{
        width: "100vw", height: "100vh",
        display: "flex", flexDirection: "row",
        background: C.bg,
        // [live-route-2026-09-20] position:relative so the overlay sidebar's
        // Backdrop (position:absolute; inset:0) resolves against this shell
        // instead of whatever positioned ancestor happens to be above it.
        position: "relative",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
      }}>
        {/* [live-route-2026-09-20] The sidebar was a permanent 260px flex child,
            so it consumed a fifth of the iPad's width on every screen whether or
            not it was wanted. It is now an overlay drawer: absent from the layout
            entirely until summoned from the TopBar hamburger, and dismissed by
            the backdrop or by picking a destination. */}
        {sidebarOpen && (
          <>
            <Backdrop onClose={() => setSidebarOpen(false)} />
            <div style={{
              position: "absolute", left: 0, top: 0, bottom: 0, zIndex: 200,
              display: "flex", boxShadow: "0 0 40px rgba(10,22,40,0.28)",
            }}>
              <TabletSidebar
                user={user}
                activeScreen={drawerScreen}
                onPickScreen={(s) => { setDrawerScreen(s); setSidebarOpen(false); }}
                onLogout={logout}
              />
            </div>
          </>
        )}
        {/* Right pane — now spans the full shell width, since the sidebar no
            longer occupies a column. */}
        <div style={{
          flex: 1, minWidth: 0, minHeight: 0,
          display: "flex", flexDirection: "column", background: C.bg, overflow: "hidden",
        }}>
          <TopBar tab={tab} onMenu={() => setSidebarOpen((v) => !v)} onBell={() => setBellOpen(true)} unread={unreadNotifs} />

          <div style={{ flex: 1, overflow: "auto", background: C.bg, position: "relative" }}>
            {/* [onboarding-wizard-2026-06-23] LOGIN-LOCK GATE (tablet) — same
                semantics as the phone shell: lock every tab except Account so
                the user can finish onboarding and is never trapped. */}
            {ONBOARDING_LOCK && tab !== "account" ? (
              tabletCenter(
                <OnboardingWizard
                  user={user}
                  locked
                  goToAccount={(screen) => { setTab("account"); if (screen) setDrawerScreen(screen); }}
                  onDone={() => setTab("jobs")}
                />
              )
            ) : (
              <>
                {/* [offer-banner-fix-2026-10-20] tablet: same every-tab offer banner */}
                {tab !== "home" && tabletCenter(<div style={{ padding: "12px 16px 0" }}><OfferBanner /></div>)}
                {tab === "home" && tabletCenter(<HomeScreen setTab={setTab} setSelectedJob={setSelectedJob} />)}
                {tab === "proofs" && tabletCenter(<ProofsScreen />)}
                {/* [field-command-2026-09-11] Field Command — full-bleed, deliberately
                    NOT wrapped in tabletCenter(): the dash is the whole windscreen-
                    mounted screen, not a 720px reading column. */}
                {tab === "command" && SS_FIELD_COMMAND_ENABLED && (
                  /* [fc-crash-fix-2026-09-11] A dash that blanks the whole app at
                     60mph is the worst possible failure mode — degrade to a readable
                     panel and leave the rest of the shell alive. */
                  <SsPanelBoundary name="Field Command">
                    <CommandView setTab={setTab} setSelectedJob={setSelectedJob} />
                  </SsPanelBoundary>
                )}
                {tab === "jobs" && (
                  <JobsScreen
                    setSelectedJob={setSelectedJob}
                    setRoutePlannerOpen={setRoutePlannerOpen}
                    selectedJob={selectedJob}
                  />
                )}
                {tab === "earnings" && tabletCenter(<EarningsScreen />)}
                {/* [field-view-2026-09-07] deliberately NOT tabletCenter() — the Field
                    View is a full-bleed two-column layout, not a 720px reading column. */}
                {tab === "field" && <FieldView selectedJob={selectedJob} setSelectedJob={setSelectedJob} setTab={setTab} />}
                {tab === "account" && tabletCenter(<AccountScreen setDrawerScreen={setDrawerScreen} />)}
              </>
            )}
          </div>

          <SafetyFAB onPress={() => setSafetyOpen(true)} />
          <BottomTabs tab={tab} setTab={setTab} showCommand={SS_FIELD_COMMAND_ENABLED} />
        </div>

        {bellOpen && <NotificationsSheet onClose={() => setBellOpen(false)} />}
        {safetyOpen && <SafetySheet onClose={() => setSafetyOpen(false)} />}
        {/* On tablet, JobDetail for non-Jobs tabs (e.g. tapped from Home) still
            opens as a sheet so it doesn't disrupt the Home reading flow. */}
        {/* [field-view-2026-09-07] "field" joins "jobs" as a tab that renders the
            selected job itself, so the sheet must stay closed there. */}
        {selectedJob && tab !== "jobs" && tab !== "field" && <JobDetailSheet job={selectedJob} onClose={() => setSelectedJob(null)} />}
        {drawerScreen && <DrawerScreen screen={drawerScreen} onClose={() => setDrawerScreen(null)} />}
        {routePlannerOpen && <RoutePlanner onClose={() => setRoutePlannerOpen(false)} />}
        {signSheet}{/* [pos-overhaul-2026-10-07:server-app] */}
        {activationModal}{certOverlay}
        <GlobalToast toast={appToast} />
          <DocViewerHost />{/* [server-parity-web-2026-10-05] in-app PDF/image viewer */}
          <TicketPromptHost />{/* [support-tickets-2026-10-08] "Need help with this job?" after a failed attempt */}
          <DoorTagPromptHost />{/* [door-tags-server-2026-10-22] required door-tag follow-up after a non-served attempt */}
          <TicketLinkHost />{/* [ticket-link-2026-10-08] ticket:<id> notification deep links */}
      </div>
    </AppCtx.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// GLOBAL TOAST — app-level transient banner, driven by AppInner's appToast
// state (fired via the window "ss-toast" CustomEvent). Mirrors the dark-theme
// toast styling used by EarningsScreen. Renders nothing when there is no toast.
// ═══════════════════════════════════════════════════════════════════════
function GlobalToast({ toast }) {
  if (!toast) return null;
  /* [job-bundles-v2-2026-10-16] Portal to <body> + position:fixed. The app root is
     position:fixed (its own stacking context), so an absolute toast inside it always
     sat UNDER body-portaled sheets (BundleConfirmSheet zIndex 3000) on iPhone. */
  const node = (
    <div role="status" style={{
      position: "fixed", left: 16, right: 16, bottom: 92, zIndex: 9000,
      padding: "12px 16px", borderRadius: 12,
      background: toast.kind === "err" ? C.err : C.ok, color: C.white,
      fontSize: 13, fontWeight: 700, textAlign: "center",
      boxShadow: "0 8px 24px rgba(15,40,96,0.28)",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    }}>
      {toast.kind === "err"
        ? <AlertTriangle size={16} color={C.white} />
        : <CheckCircle2 size={16} color={C.white} />}
      <span>{toast.text}</span>
    </div>
  );
  try { if (typeof document !== "undefined" && document.body) return createPortal(node, document.body); } catch (_) {} // [job-bundles-v2-2026-10-16]
  return node;
}

// ═══════════════════════════════════════════════════════════════════════
// TABLET SIDEBAR — permanent left nav, replaces the hamburger Drawer
// ═══════════════════════════════════════════════════════════════════════
function TabletSidebar({ user, activeScreen, onPickScreen, onLogout }) {
  const trainingBadge = useTrainingBadge(); // [server-parity-web-2026-10-05] real count, was the literal 3
  const unreadMessages = useUnreadMessages();
  const items = [
    { id: "profile", icon: User, label: "Profile" },
    { id: "messages", icon: MessageSquare, label: "Messages", badge: unreadMessages || undefined }, // [server-parity-web-2026-10-05]
    { id: "availability", icon: Calendar, label: "Availability" },                                 // [server-parity-web-2026-10-05]
    { id: "settings", icon: Settings, label: "Settings" },
    { id: "training", icon: GraduationCap, label: "Training videos", badge: trainingBadge || undefined },
    { id: "coverage", icon: MapPin, label: "Coverage areas" },
    { id: "documents", icon: FolderOpen, label: "My documents" },
    { id: "mileage", icon: Route, label: "Mileage Log" },
    { id: "referrals", icon: Gift, label: "Referrals" }, // [server-referrals-screen-2026-10-08]
    { id: "support", icon: HelpCircle, label: "Help & support" },
  ];
  const initials = ((user?.name || "S").split(" ").map(s => s[0]).join("").slice(0,2)).toUpperCase();
  const roleLabel = user?.role === "client" ? "Client" : "Process Server";

  return (
    // v1.1 — sidebar is a flex child of the row-flex tablet shell parent (was
    // position: absolute). Fixed 260px width via flex: "0 0 260px" so the
    // right pane gets the remaining space deterministically.
    <div style={{
      flex: "0 0 260px", width: 260, height: "100%",
      background: C.white, borderRight: `1px solid ${C.steelXL}`,
      display: "flex", flexDirection: "column", zIndex: 10,
      paddingTop: "env(safe-area-inset-top)",
    }}>
      {/* Brand wordmark */}
      <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${C.steelXXL}` }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <span style={{ fontSize: 20, fontWeight: 800, color: C.deep, letterSpacing: "-0.01em" }}>Strike</span>
          <span style={{ fontSize: 20, fontWeight: 400, fontStyle: "italic", color: C.gold, letterSpacing: "-0.01em" }}>Serve</span>
        </div>
        <div style={{ fontSize: 9, fontWeight: 600, color: C.steel, letterSpacing: "0.18em", marginTop: 2 }}>
          PROCESS SERVER
        </div>
      </div>

      {/* User card */}
      <div style={{ padding: "16px 18px 14px", borderBottom: `1px solid ${C.steelXXL}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar user={user} size={44} fontSize={15} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.deep, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name || "Server"}</div>
              <ProBadge user={user} />
            </div>
            <div style={{
              display: "inline-block", marginTop: 4,
              fontSize: 9, fontWeight: 700, color: C.deep, letterSpacing: "0.06em",
              background: C.goldXL, padding: "2px 7px", borderRadius: 8,
            }}>{roleLabel.toUpperCase()}</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <div style={{ flex: 1, overflow: "auto", padding: "10px 10px" }}>
        {items.map(it => {
          const Icon = it.icon;
          const active = activeScreen === it.id;
          return (
            <button key={it.id} onClick={() => onPickScreen(it.id)} style={{
              width: "100%", padding: "10px 12px", marginBottom: 2,
              background: active ? C.goldXL : "transparent",
              border: "none", borderRadius: 10,
              display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left",
            }}>
              <Icon size={18} color={active ? C.deep : C.deep} strokeWidth={active ? 2.2 : 1.8} />
              <span style={{ flex: 1, fontSize: 13, fontWeight: active ? 800 : 600, color: C.deep }}>{it.label}</span>
              {it.badge && (
                <div style={{
                  minWidth: 18, height: 18, padding: "0 5px", background: C.gold, color: C.deep,
                  borderRadius: 9, fontSize: 10, fontWeight: 800,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{it.badge}</div>
              )}
            </button>
          );
        })}
      </div>

      {/* [mdt-2026-09-20] Pinned footer. These were two separate blocks with the
          default flex-shrink:1, so the nav above (flex:1) squeezed them and Sign
          out was clipped off the bottom entirely — the button existed, it was
          just never on screen. flexShrink:0 pins them, and the safe-area padding
          keeps Sign out clear of the home indicator. Both rows now share one
          treatment instead of billing floating on its own. */}
      <div style={{
        flexShrink: 0, borderTop: `1px solid ${C.steelXL}`,
        padding: "6px 10px calc(env(safe-area-inset-bottom) + 8px)",
      }}>
        {SS_PRO_ENABLED && ( /* [pro-disabled-2026-10-06] */
        <button
          onClick={() => onPickScreen("billing")}
          style={{
            width: "100%", padding: "11px 12px", background: "none", border: "none",
            borderRadius: 10,
            display: "flex", alignItems: "center", gap: 12, cursor: "pointer", color: C.deep,
          }}>
          <CreditCard size={18} color={C.deep} />
          <span style={{ flex: 1, textAlign: "left", fontSize: 13, fontWeight: 600 }}>Plan &amp; billing</span>
          <ChevronRight size={14} color={C.steel} />
        </button>
        )}
        <button onClick={onLogout} style={{
          width: "100%", padding: "11px 12px", background: "none", border: "none",
          borderRadius: 10,
          display: "flex", alignItems: "center", gap: 12, cursor: "pointer", color: C.err,
        }}>
          <LogOut size={18} color={C.err} />
          <span style={{ flex: 1, textAlign: "left", fontSize: 13, fontWeight: 700 }}>Sign out</span>
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PHONE FRAME
// ═══════════════════════════════════════════════════════════════════════
function PhoneFrame({ children }) {
  return (
    <div style={{
      width: "100%",
      height: "100%",
      background: "transparent",
      borderRadius: 0,
      padding: 0,
      boxShadow: "none",
      position: "relative",
    }}>
      <div style={{
        display: "none", position: "absolute", top: 22, left: "50%", transform: "translateX(-50%)",
        width: 120, height: 36, background: "transparent", borderRadius: 22, zIndex: 100,
      }} />
      <div style={{
        width: "100%", height: "100%", background: C.bg, borderRadius: 0, overflow: "hidden",
        display: "flex", flexDirection: "column", position: "relative",
      }}>
        {children}
      </div>
    </div>
  );
}

function StatusBar() {
  return (
    <div style={{
      height: 50, background: C.deep, display: "flex", alignItems: "flex-end", justifyContent: "space-between",
      padding: "0 28px 6px", color: C.white, fontSize: 14, fontWeight: 600, flexShrink: 0,
    }}>
      <div>9:41</div>
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <Signal size={14} /> <Wifi size={14} /> <Battery size={16} />
      </div>
    </div>
  );
}

function TopBar({ tab, onMenu, onBell, unread }) {
  const { isTablet, dark, setDarkMode } = React.useContext(AppCtx);
  const titles = { home: "Home", proofs: "Proofs", jobs: "Jobs", field: "Field", earnings: "Earnings", account: "Account" }; // [field-view-2026-09-07]
  const [scanBusy, setScanBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  // QR scan-to-open-job — mirrors the web app's ?scan=<jobId> deep link. Uses
  // the pure-JS html5-qrcode library over getUserMedia (rendered in the
  // <QrScannerOverlay> below), NOT the native MLKit BarcodeScanner plugin
  // (whose scan() throws UNIMPLEMENTED in this build). Opening the overlay
  // pre-warms the library so we can surface a clear toast if it can't load.
  async function scanQRCode() {
    if (scanBusy || scanOpen) return;
    setScanBusy(true);
    try {
      await loadQrLib();
      setScanOpen(true);
    } catch (e) {
      try { window.dispatchEvent(new CustomEvent("ss-toast", { detail: { kind: "err", text: "Scanner unavailable — check your connection." } })); } catch (_) {}
    } finally {
      setScanBusy(false);
    }
  }

  // [mdt-2026-09-20] Same inversion as the FieldView masthead: C.deep is a
  // near-white in the dark palette, so the app bar rendered white-on-dark.
  // C.bgW is a genuine dark surface. And C.white is #1e2430 in dark — also a
  // SURFACE, not a foreground — so every icon and label sitting on this bar
  // needs barFg, or it renders dark-on-dark and effectively disappears.
  const barFg = dark ? C.ink : C.white;
  return (
    <div style={{ background: dark ? C.bgW : C.deep, padding: "calc(env(safe-area-inset-top) + 8px) 16px 14px", flexShrink: 0, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {isTablet ? (
          // [live-route-2026-09-20] The sidebar is an overlay now rather than a
          // permanent column, so the tablet header needs a way to summon it. This
          // was the title alone, which is exactly why there was no way to dismiss
          // the sidebar once it was sitting there taking up space.
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <button onClick={onMenu} style={iconBtn} aria-label="Menu">
              <Menu size={22} color={barFg} />
            </button>
            <div style={{
              fontSize: 16, fontWeight: 800, color: barFg, letterSpacing: "-0.01em",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {titles[tab] || ""}
            </div>
          </div>
        ) : (
          <button onClick={onMenu} style={iconBtn} aria-label="Menu">
            <Menu size={22} color={barFg} />
          </button>
        )}
        {!isTablet && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <span style={{ fontSize: 18, fontWeight: 800, color: barFg, letterSpacing: "-0.01em" }}>Strike</span>
              <span style={{ fontSize: 18, fontWeight: 400, fontStyle: "italic", color: C.gold, letterSpacing: "-0.01em" }}>Serve</span>
            </div>
            <div style={{ fontSize: 9, fontWeight: 600, color: "rgba(255,255,255,0.5)", letterSpacing: "0.18em", marginTop: 1 }}>
              {titles[tab]?.toUpperCase()}
            </div>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={scanQRCode}
            disabled={scanBusy}
            aria-label="Scan a job QR code"
            style={{ ...iconBtn, cursor: scanBusy ? "wait" : "pointer", opacity: scanBusy ? 0.6 : 1 }}
          >
            <QrCode size={20} color={barFg} />
          </button>
          <button
            onClick={() => setDarkMode(!dark)}
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
            style={iconBtn}
          >
            {dark
              ? <Sun size={20} color={C.gold} />
              : <Moon size={20} color={barFg} />}
          </button>
          <button onClick={onBell} style={{ ...iconBtn, position: "relative" }}>
            <Bell size={20} color={barFg} />
            {unread > 0 && (
              <div style={{
                position: "absolute", top: 4, right: 4, minWidth: 16, height: 16, padding: "0 4px",
                background: C.gold, color: C.deep, borderRadius: 8, fontSize: 10, fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center", border: `1.5px solid ${C.deep}`,
              }}>{unread}</div>
            )}
          </button>
        </div>
      </div>
      {scanOpen && <QrScannerOverlay onClose={() => setScanOpen(false)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// QR SCANNER OVERLAY — full-screen html5-qrcode camera (pure JS, no native plugin)
// ═══════════════════════════════════════════════════════════════════════
// Mirrors the web app's ScannerModal: spins up window.Html5Qrcode against the
// rear camera, and on a successful decode stops the camera, closes the overlay,
// and dispatches the EXISTING "ss-scan-open-job" CustomEvent so AppInner's
// resolver opens the matching job (parses ?scan=<id>/token, checks the loaded
// list, then GET /api/jobs). loadQrLib() has already resolved before we mount.
const SS_QR_READER_ID = "ss-qr-reader";
function QrScannerOverlay({ onClose }) {
  const qrRef = useRef(null);
  const [err, setErr] = useState("");

  const toast = (kind, text) => {
    try { window.dispatchEvent(new CustomEvent("ss-toast", { detail: { kind, text } })); } catch (_) {}
  };

  useEffect(() => {
    let cancelled = false;
    // Give the reader <div> a tick to mount before html5-qrcode grabs it.
    const t = setTimeout(() => {
      if (cancelled) return;
      const el = document.getElementById(SS_QR_READER_ID);
      if (!el || !window.Html5Qrcode) {
        setErr("Scanner unavailable");
        return;
      }
      try {
        const qr = new window.Html5Qrcode(SS_QR_READER_ID);
        qrRef.current = qr;
        qr.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (decodedText) => {
            // One-shot: stop the camera, close the overlay, hand off to the resolver.
            try { qr.stop().catch(() => {}); } catch (_) {}
            qrRef.current = null;
            try { window.dispatchEvent(new CustomEvent("ss-scan-open-job", { detail: String(decodedText) })); } catch (_) {}
            onClose();
          },
          () => {} // per-frame decode failures are normal; ignore.
        ).catch((e) => {
          console.warn("[qr] camera start failed:", e && e.message);
          setErr("Camera unavailable");
          toast("err", "Camera unavailable — check permissions");
        });
      } catch (e) {
        console.warn("[qr] init failed:", e && e.message);
        setErr("Camera unavailable");
        toast("err", "Camera unavailable — check permissions");
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
      const qr = qrRef.current;
      qrRef.current = null;
      if (qr) { try { qr.stop().catch(() => {}); } catch (_) {} }
    };
  }, []);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9000,
      background: "rgba(8,12,20,0.94)", display: "flex", flexDirection: "column",
      paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 16px 8px" }}>
        <div style={{ color: C.white, fontSize: 16, fontWeight: 800, letterSpacing: "-0.01em" }}>Scan job QR</div>
        <button
          onClick={onClose}
          aria-label="Cancel scan"
          style={{
            width: 38, height: 38, borderRadius: 12, background: "rgba(255,255,255,0.12)",
            border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0,
          }}
        >
          <X size={22} color={C.white} />
        </button>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 16px" }}>
        <div
          id={SS_QR_READER_ID}
          style={{ width: "100%", maxWidth: 360, borderRadius: 18, overflow: "hidden", background: "#000" }}
        />
        {err
          ? <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, marginTop: 18, textAlign: "center" }}>{err}. Check camera permissions and try again.</div>
          : <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 13, marginTop: 18, textAlign: "center" }}>Point your camera at the job's QR code.</div>}
      </div>
      <div style={{ padding: "12px 16px 18px", display: "flex", justifyContent: "center" }}>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.12)", color: C.white, border: "none",
            borderRadius: 12, padding: "12px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

const iconBtn = {
  width: 38, height: 38, borderRadius: 12, background: "rgba(255,255,255,0.08)",
  border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0,
};

// ═══════════════════════════════════════════════════════════════════════
// BOTTOM TABS
// ═══════════════════════════════════════════════════════════════════════
// [notif-link-2026-08-08] Single source of truth for the shell's tab ids, so a
// "tab:<name>" action_url can be validated against what actually exists rather
// than trusted from the database. An unknown id is logged and ignored.
const TAB_IDS = ["home", "proofs", "jobs", "earnings", "account"];

/* [server-referral-links-2026-10-08] Drawer ids DrawerScreen actually renders. A
   "drawer:<x>" link outside this list used to open an empty, untitled sheet; it is
   now ignored like an unknown tab. Keep in sync with DrawerScreen's titles map. */
const DRAWER_IDS = ["profile", "settings", "training", "coverage", "documents", "support", "mileage", "billing", "deleteAccount", "messages", "availability", "referrals"]; /* [server-referrals-screen-2026-10-08] + referrals */

/* [server-referral-links-2026-10-08] One resolver for "page:<name>" links (written
   for the web portal) shared by the bell sheet and the push-tap handler — the push
   handler previously ignored "page:" entirely, so a background tap on a billing or
   referral push just opened the app on Home. Returns { drawer } | { tab } | null.
   [server-referrals-screen-2026-10-08] "page:referrals" (and referral/refer/invite)
   now opens the Referrals drawer (ReferralsScreen, GET /api/referrals/my) — no toast. */
function ssPageLinkTarget(name) {
  const pg = String(name || "").toLowerCase();
  const DRAWER = { referrals: "referrals", referral: "referrals", refer: "referrals", invite: "referrals", /* [server-referrals-screen-2026-10-08] */ billing: "billing", subscription: "billing", messages: "messages", settings: "settings", "notif-prefs": "settings", documents: "documents", support: "support", profile: "profile", account: "profile", mileage: "mileage", coverage: "coverage", training: "training", availability: "availability" };
  const TAB = { payouts: "earnings", earnings: "earnings", "my-jobs": "jobs", "all-jobs": "jobs", jobs: "jobs", dashboard: "home", home: "home", proofs: "proofs" };
  if (DRAWER[pg] && DRAWER_IDS.includes(DRAWER[pg])) return { drawer: DRAWER[pg] };
  if (TAB[pg] && TAB_IDS.includes(TAB[pg])) return { tab: TAB[pg] };
  return null;
}
/* Dispatch a resolved page target. Returns true when something was opened. */
function ssOpenPageLink(name) {
  const t = ssPageLinkTarget(name);
  if (!t) return false;
  if (t.drawer) window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: t.drawer }));
  else if (t.tab) window.dispatchEvent(new CustomEvent("ss-open-tab", { detail: t.tab }));
  if (t.toast) ssToast("ok", t.toast);
  return true;
}
/* [messaging-v2-2026-10-08] "msg:<convId>" (backend message notifications, contract
   action_url) → the Messages drawer. A server has exactly one thread (their Dispatch
   thread), so every msg: link opens it; the convId is not trusted for anything else.
   Shared by the bell (NotificationsSheet) and the push-tap handler. */
/* [job-bundles-v2-2026-10-16] Bundle offer deep link: action_url "bundle:<id>" (backend
   notifications row type 'job_offer'; push data carries the same action_url), or
   type 'bundle_offer' + bundle_id. Opens Home's bundle offer card + review sheet.
   Shared by the bell (NotificationsSheet) and the push-tap handler. */
function ssOpenBundleLink(url, data) {
  const m = String(url || "").match(/^bundle:(.+)$/i);
  let id = m ? m[1].trim() : "";
  const d = data || {};
  if (!id && String(d.type || "") === "bundle_offer" && d.bundle_id != null) id = String(d.bundle_id);
  if (!id || id === "null" || id === "undefined") return false;
  ssOpenBundleOffer(id);
  return true;
}
function ssOpenMsgLink(url) {
  const m = String(url || "").match(/^msg:(.+)$/i);
  if (!m || !DRAWER_IDS.includes("messages")) return false;
  try { ssInvalidateBadge("messages"); } catch (_) {}
  window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: "messages" }));
  return true;
}

function BottomTabs({ tab, setTab, showCommand }) {
  const tabs = [
    { id: "home", icon: Home, label: "Home" },
    { id: "proofs", icon: FileSignature, label: "Proofs" },
    { id: "jobs", icon: Briefcase, label: "Jobs" },
    // [field-view-2026-09-07] NO "field" tab here — six tabs squeezed the phone
    // bar to ~62px per label at 375pt. Field View is entered from a Jobs row
    // action (ListJobCard footer / map claim bar) via the ss-open-field event.
    { id: "earnings", icon: DollarSign, label: "Earnings" },
    { id: "account", icon: User, label: "Account" },
  ];
  /* [field-command-2026-09-11] The Command tab is inserted ONLY when the caller asks
     for it — the tablet shell does, the phone shell does not. "command" is also
     deliberately absent from TAB_IDS so a notification deep link can never drop a
     phone into a tab its shell has no branch for. */
  if (showCommand) tabs.splice(2, 0, { id: "command", icon: Crosshair, label: "Command" });
  const signCount = usePosSignCount(); // [pos-overhaul-2026-10-07:server-app] proofs waiting for this server's signature
  return (
    <div style={{
      background: C.white, borderTop: `1px solid ${C.steelXL}`, padding: "6px 4px 12px",
      display: "flex", justifyContent: "space-around", alignItems: "center", flexShrink: 0,
      boxShadow: "0 -4px 20px rgba(15,40,96,0.06)",
    }}>
      {tabs.map(t => {
        const active = tab === t.id;
        const Icon = t.icon;
        return (
          <button key={t.id} onClick={() => setTab(t.id)} aria-label={t.id === "proofs" && signCount > 0 ? `Proofs, ${signCount} to sign` : t.label} style={{
            background: "none", border: "none", display: "flex", flexDirection: "column",
            alignItems: "center", gap: 3, cursor: "pointer", padding: "6px 4px", flex: 1,
          }}>
            <span style={{ position: "relative", display: "inline-flex" }}>
              <Icon size={22} color={active ? C.deep : C.steel} strokeWidth={active ? 2.4 : 1.8} />
              {t.id === "proofs" && signCount > 0 ? (
                <span style={{
                  position: "absolute", top: -6, right: -10, minWidth: 17, height: 17, padding: "0 4px", borderRadius: 9,
                  background: C.err, color: C.white, fontSize: 10, fontWeight: 800, lineHeight: "17px", textAlign: "center",
                  border: `1.5px solid ${C.white}`, boxSizing: "content-box",
                }}>{signCount > 99 ? "99+" : signCount}</span>
              ) : null}
            </span>
            <div style={{ fontSize: 10, fontWeight: active ? 700 : 500, color: active ? C.deep : C.steel }}>
              {t.label}
            </div>
            {active ? (
              <div style={{ width: 4, height: 4, borderRadius: "50%", background: C.gold }} />
            ) : (
              <div style={{ width: 4, height: 4 }} />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// FLOATING SAFETY BUTTON
// ═══════════════════════════════════════════════════════════════════════
function SafetyFAB({ onPress }) {
  return (
    <button onClick={onPress} style={{
      position: "absolute", bottom: 100, right: 16, width: 44, height: 44, borderRadius: "50%",
      background: C.white, border: `1.5px solid ${C.errL}`,
      display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0,
      boxShadow: "0 4px 12px rgba(15,40,96,0.15)", zIndex: 50,
    }}>
      <Shield size={20} color={C.err} strokeWidth={2.2} />
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// HOME SCREEN
// ═══════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════
// [direct-offer-2026-07-30] DIRECT JOB OFFER BANNER
// A job assigned directly to THIS server, with a countdown. Accept takes it;
// Decline releases it back to the general pool and notifies admin. Ignoring it
// until the timer runs out does the same thing as declining.
// Polls /api/jobs/my-offers every 30s and re-checks when the app is refocused,
// so a push notification tapped from the lock screen lands on a fresh list.
// Do NOT add a second fetch to the offer endpoints anywhere in this file.
// ═══════════════════════════════════════════════════════════════════════
function useOfferCountdown(expiresAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const msLeft = Math.max(0, new Date(expiresAt).getTime() - now);
  const totalSec = Math.floor(msLeft / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const label = h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}:${String(s).padStart(2, "0")}`;
  return { msLeft, label, urgent: msLeft > 0 && msLeft < 5 * 60 * 1000, expired: msLeft <= 0 };
}

function OfferCard({ offer, onDone }) {
  const { label, urgent, expired } = useOfferCountdown(offer.offer_expires_at);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState("");
  /* [job-bundles-v2-2026-10-16] A bundled job can never be accepted alone: Accept on a
     direct offer for a bundle member goes through the bundle sheet (preview -> claim
     with accept_bundle). The backend refuses /offer/accept with 409 BUNDLED_JOB anyway. */
  const { claim: claimBundle, bundlePrompt } = useJobClaim(() => onDone());
  const offerBundleId = offer && offer.bundle_id != null && offer.bundle_id !== "" ? offer.bundle_id : null;

  async function act(kind) {
    if (busy) return;
    if (kind === "accept" && offerBundleId != null) { // [job-bundles-v2-2026-10-16]
      setErr("");
      await claimBundle({ id: offer.id, bundle_id: offerBundleId, bundle_job_count: offer.bundle_job_count, bundle_status: offer.bundle_status });
      return;
    }
    setBusy(kind); setErr("");
    try {
      if (kind === "accept") await api.post(`/api/jobs/${offer.id}/offer/accept`);
      else await api.post(`/api/jobs/${offer.id}/offer/decline`, reason ? { reason } : {});
      await onDone();
    } catch (e) {
      const code = e && (e.code || (e.body && e.body.code));
      const be = e && e.body && e.body.error;
      if (code === "BUNDLED_JOB") { // [job-bundles-v2-2026-10-16]
        setErr(be || "This job is part of a bundle. Review the bundle to accept all of its jobs.");
        const bid = e.body && e.body.bundle_id;
        if (kind === "accept" && bid != null) { setBusy(""); await claimBundle({ id: offer.id, bundle_id: bid }); return; }
      }
      else if (code === "OFFER_EXPIRED") setErr("This offer ran out — the job went back to the pool.");
      else if (code === "NOT_AVAILABLE") setErr("This job is no longer available.");
      else if (code === "NOT_OFFERED_TO_YOU") setErr("This job is not assigned to you.");
      else setErr(be || e.message || "Something went wrong — try again.");
    } finally { setBusy(""); }
  }

  const addr = [offer.recipient_address, offer.recipient_city, offer.recipient_state].filter(Boolean).join(", ");

  return (
    <div style={{
      background: C.white, border: `2px solid ${urgent ? C.err : C.gold}`,
      borderRadius: 16, padding: 14, marginBottom: 10,
      boxShadow: `0 6px 18px ${urgent ? C.err : C.gold}22`,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: C.gold }}>
          ASSIGNED TO YOU
        </div>
        <div style={{
          fontSize: 12, fontWeight: 800, fontVariantNumeric: "tabular-nums",
          color: urgent ? C.err : C.deep,
          background: urgent ? C.errL : C.goldXL,
          padding: "3px 9px", borderRadius: 999,
        }}>
          {expired ? "Expired" : `${label} left`}
        </div>
      </div>

      <div style={{ fontSize: 17, fontWeight: 800, color: C.deep, marginBottom: 2 }}>
        Job #{offer.job_number || offer.id}
      </div>
      {offer.recipient_name && (
        <div style={{ fontSize: 13, color: C.ink, marginBottom: 2 }}>{offer.recipient_name}</div>
      )}
      {addr && <div style={{ fontSize: 12, color: C.steel, marginBottom: 6 }}>{addr}</div>}
      {/* [pos-overhaul-2026-10-07:server-app] offer.payment is the CLIENT price — show server pay only. */}
      <div style={{ fontSize: 15, fontWeight: 800, color: ssServerPayOf(offer) != null ? C.ok : C.steel, marginBottom: 8 }}>
        {ssPayLabel(ssServerPayOf(offer))}
      </div>

      {err && (
        <div style={{ fontSize: 12, color: C.err, background: C.errL, padding: "7px 10px", borderRadius: 9, marginBottom: 8 }}>
          {err}
        </div>
      )}

      {declineOpen && (
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional) — this goes to dispatch"
          rows={2}
          style={{
            width: "100%", boxSizing: "border-box", marginBottom: 8, padding: "8px 10px",
            border: `1px solid ${C.steelL}`, borderRadius: 9, fontSize: 13,
            fontFamily: "inherit", background: C.bgW, color: C.ink, resize: "vertical",
          }}
        />
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          disabled={!!busy || expired}
          onClick={() => act("accept")}
          style={{
            flex: 2, padding: "11px 0", borderRadius: 11, border: "none",
            background: expired ? C.steelL : `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
            color: C.white, fontSize: 14, fontWeight: 800,
            cursor: busy || expired ? "default" : "pointer", opacity: busy === "decline" ? 0.5 : 1,
          }}>
          {busy === "accept" ? "Accepting…" : (offerBundleId != null ? "Review bundle" : "Accept")/* [job-bundles-v2-2026-10-16] */}
        </button>
        <button
          type="button"
          disabled={!!busy}
          onClick={() => { if (!declineOpen) setDeclineOpen(true); else act("decline"); }}
          style={{
            flex: 1, padding: "11px 0", borderRadius: 11,
            border: `1.5px solid ${C.err}`, background: "transparent",
            color: C.err, fontSize: 14, fontWeight: 800,
            cursor: busy ? "default" : "pointer", opacity: busy === "accept" ? 0.5 : 1,
          }}>
          {busy === "decline" ? "Sending…" : declineOpen ? "Confirm decline" : "Decline"}
        </button>
      </div>
      {bundlePrompt}{/* [job-bundles-v2-2026-10-16] */}
    </div>
  );
}

function OfferBanner() {
  const { reload } = React.useContext(AppCtx);
  const [offers, setOffers] = useState([]);
  const [bundleOffers, setBundleOffers] = useState([]); // [job-bundles-v2-2026-10-16]
  // [job-bundles-v2-2026-10-16] Bundle offers share this card's review sheet + error mapping.
  const loadRef = useRef(null);
  const refreshOffers = React.useCallback(() => { try { if (loadRef.current) loadRef.current(); } catch (_) {} }, []);
  const { reviewBundleOffer, bundlePrompt } = useJobClaim(refreshOffers, refreshOffers);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/api/jobs/my-offers");
      setOffers((r && r.offers) || []);
    } catch (e) {
      // 404 = backend not yet deployed with the offer routes. Stay silent so an
      // older API can never break the home screen.
      /* [offer-banner-fix-2026-10-20] Only a 404 clears the list. Any other failure (500, timeout, offline) used to
         wipe it too, which hid live offers; keep what we last had and retry on the next poll. */
      const _st = e && (e.status || (e.response && e.response.status));
      if (_st === 404) setOffers([]);
      else { try { console.warn("[offer-banner] my-offers failed", _st || (e && e.message)); } catch (_) {} }
    }
    /* [job-bundles-v2-2026-10-16] Whole-bundle offers, polled alongside. Fail-soft (404 on
       an older backend). Returns the list so the push deep link can find its bundle. */
    let bl = [];
    try {
      const rb = await api.get("/api/job-bundles/my-offers");
      bl = Array.isArray(rb) ? rb : (rb && Array.isArray(rb.offers) ? rb.offers : (rb && Array.isArray(rb.bundles) ? rb.bundles : []));
    } catch (_) { bl = []; }
    bl = bl.filter((b) => b && b.id != null);
    setBundleOffers(bl);
    return bl;
  }, []);
  loadRef.current = load;

  /* [job-bundles-v2-2026-10-16] Push data.type 'bundle_offer' -> ssOpenBundleOffer() leaves the
     bundle id pending (survives a cold start before Home mounts) and fires ss-open-bundle-offer. */
  const openPendingBundleOffer = React.useCallback(async () => {
    const want = ssPendingBundleOfferId;
    if (want == null) return;
    ssPendingBundleOfferId = null;
    const bl = await load();
    const hit = bl.find((b) => String(b.id) === String(want));
    if (hit) reviewBundleOffer(hit);
    else ssToast("err", "This bundle offer is no longer available.");
  }, [load, reviewBundleOffer]);
  useEffect(() => {
    openPendingBundleOffer();
    const on = () => { openPendingBundleOffer(); };
    window.addEventListener("ss-open-bundle-offer", on);
    return () => window.removeEventListener("ss-open-bundle-offer", on);
  }, [openPendingBundleOffer]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30 * 1000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    /* [offer-banner-fix-2026-10-20] a push (new offer) or a tapped push refreshes the banner immediately */
    window.addEventListener("ss-push-received", onFocus);
    window.addEventListener("ss-push-open-job", onFocus);
    window.addEventListener("ss-offers-refresh", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("ss-push-received", onFocus);
      window.removeEventListener("ss-push-open-job", onFocus);
      window.removeEventListener("ss-offers-refresh", onFocus);
    };
  }, [load]);

  async function handleDone() {
    await load();
    try { await reload(); } catch (_) {}
  }

  if (!offers.length && !bundleOffers.length) return bundlePrompt; // [job-bundles-v2-2026-10-16]

  return (
    <div style={{ marginBottom: 16 }}>
      {/* [job-bundles-v2-2026-10-16] Bundle offers first — one card per bundle. */}
      {bundleOffers.map((b) => (
        <BundleOfferCard key={"b" + b.id} offer={b} onReview={() => reviewBundleOffer(b)} onDone={handleDone} />
      ))}
      {offers.length ? (
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", color: C.steel, marginBottom: 8 }}>
          {offers.length === 1 ? "1 JOB WAITING ON YOU" : `${offers.length} JOBS WAITING ON YOU`}
        </div>
      ) : null}
      {offers.map((o) => (
        <OfferCard key={o.id} offer={o} onDone={handleDone} />
      ))}
      {bundlePrompt}
    </div>
  );
}


// [unregistered-cap-2026-09-07] §22350 annual-cap banner for staff who are NOT registered process servers.
// user.unregisteredCap comes from GET /api/auth/me and is null for everyone else
// (registered, exempt, or not flagged), so this renders nothing for normal servers.
// Warns at the 5th and 9th serve, then shows a hard stop at 10.
function UnregisteredCapBanner() {
  const { user } = React.useContext(AppCtx);
  const cap = user && user.unregisteredCap;
  if (!cap) return null;
  const { used, limit, remaining, blocked, year } = cap;
  if (!blocked && used !== 5 && used !== 9) return null;
  const stop = blocked;
  return (
    <div role="alert" style={{
      background: stop ? C.errL : C.warnL,
      border: "1px solid " + (stop ? C.err : C.warn),
      borderRadius: 12, padding: "12px 14px", marginBottom: 14,
    }}>
      <div style={{ fontWeight: 800, color: stop ? C.err : C.deep, fontSize: 14, marginBottom: 4 }}>
        {stop ? "Serve limit reached for " + year : "Serve limit warning"}
      </div>
      <div style={{ fontSize: 13, color: C.steel, lineHeight: 1.45 }}>
        {stop
          ? "You've completed all " + limit + " serves allowed for " + year + " without a process server registration. California law (B&P Code 22350) requires registration beyond 10 serves in a calendar year. Contact the office to get registered."
          : "You've completed " + used + " of " + limit + " serves allowed in " + year + " without a process server registration. " + remaining + " left. California law (B&P Code 22350) requires registration beyond 10 in a calendar year."}
      </div>
    </div>
  );
}

function HomeScreen({ setTab, setSelectedJob }) {
  const { user, jobs, setUser } = React.useContext(AppCtx);
  const [shiftOn, setShiftOn] = useState(!!(user?.availability?.discoverable));
  const [shiftBusy, setShiftBusy] = useState(false);
  async function toggleShift() {
    if (shiftBusy) return;
    const next = !shiftOn;
    setShiftBusy(true);
    setShiftOn(next);
    try {
      await api.patch("/api/auth/profile", { availability: { ...(user?.availability || {}), discoverable: next } });
      // Re-read authoritative server state instead of trusting the optimistic
      // local value — the backend may normalize/reject the toggle. Reflect the
      // canonical availability back into app context + the persisted user cache
      // so the rest of the tree (and a cold relaunch) sees the real value.
      try {
        const fresh = await api.me();
        if (fresh) {
          setShiftOn(!!(fresh?.availability?.discoverable));
          if (typeof setUser === "function") setUser(fresh);
          try { localStorage.setItem(SS_USER_KEY, JSON.stringify(fresh)); } catch {}
        }
      } catch (_) { /* keep optimistic value if the refetch fails */ }
    } catch (e) { console.warn("[shift]", e.message); setShiftOn(!next); }
    finally { setShiftBusy(false); }
  }
  const completedCount = jobs.filter(j => j.status === "completed" || (j._raw && j._raw.pos_status === "signed")).length;
  // [field-list-priority-2026-10-08] high-priority jobs first on the home cards too
  const active = ssSortByPriority(jobs.filter(j => j.status === "active"));
  const available = ssSortByPriority(jobs.filter(j => j.status === "available"));
  // Today's count: jobs completed today (so the "Today" card isn't a flat dash)
  const completedTodayJobs = jobs.filter(j => {
    const ts = j._raw && (j._raw.completed_at || j._raw.signed_at || j._raw.pos_signed_at);
    if (!ts) return false;
    const d = new Date(ts);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  });
  const completedToday = completedTodayJobs.length;
  /* [pos-overhaul-2026-10-07:server-app] `todayPay` was read below but never declared
     (ReferenceError the first time a job completed today). Server pay only. */
  const todayPay = completedTodayJobs.reduce((s, j) => s + (Number(j.pay) || 0), 0);

  // Drive the Training-waiting card from the actual cached catalog instead of
  // the old hardcoded "3 videos · ~30 min". Cache miss falls back to the seed.
  const trainingSummary = React.useMemo(() => {
    const cat = (typeof loadTrainingCatalogCache === "function" && loadTrainingCatalogCache()) || TRAINING_VIDEOS;
    const done = (typeof loadTrainingCompletion === "function" && loadTrainingCompletion()) || {};
    const pending = cat.filter(v => (v.required || v.category === "Required") && !done[v.id]);
    const totalSec = pending.reduce((s, v) => {
      if (Number.isFinite(v.duration_seconds)) return s + v.duration_seconds;
      // Fallback: parse "12m" / "12 min" → minutes.
      const m = String(v.duration || "").match(/(\d+)/);
      return s + (m ? Number(m[1]) * 60 : 0);
    }, 0);
    return { count: pending.length, minutes: Math.round(totalSec / 60) };
  }, []);

  return (
    <div style={{ padding: "16px 16px 100px" }}>
      {/* [unregistered-cap-2026-09-07] annual serve cap for unregistered staff (renders nothing otherwise) */}
      <UnregisteredCapBanner />
      <PosProfileBanner />{/* [pos-overhaul-2026-10-07:server-app] */}
      {/* [direct-offer-2026-07-30] Jobs assigned directly to this server, with countdown */}
      <OfferBanner />
      {/* Greeting */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 13, color: C.steel, fontWeight: 500 }}>Good morning</div>
        <div style={{ fontSize: 26, fontWeight: 800, color: C.deep, letterSpacing: "-0.02em" }}>{(user?.name || "Server").split(" ")[0]}</div>
      </div>

      {/* On-shift toggle */}
      <div style={{
        background: `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
        borderRadius: 18, padding: 16, marginBottom: 14, color: C.white,
        boxShadow: `0 8px 24px ${C.royal}33`,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.gold, boxShadow: `0 0 8px ${C.gold}` }} />
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: shiftOn ? C.goldL : "rgba(255,255,255,0.5)" }}>{shiftOn ? "ON SHIFT" : "OFF SHIFT"}</div>
            </div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{shiftOn ? "You're discoverable" : "You're offline"}</div>
            {(() => {
              // Drive the coverage line from user state. Coverage center / radius
              // are stored on the user object (set via CoverageScreen). When the
              // user has no county selected, render a tap target that opens the
              // Coverage drawer screen.
              /* [server-zone-30-2026-10-18] The line now shows the fixed 30-mile service area
                 around the admin-set home base; with no home base it opens Coverage areas,
                 which explains that support sets it. */
              const hbHome = ssHomeBase(user);
              if (hbHome) {
                return (
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                    {`${SS_ZONE_MILES} mi around ${hbHome.address || "your home base"}`}
                  </div>
                );
              }
              return (
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: "coverage" }))}
                  style={{
                    marginTop: 2, padding: 0, background: "none", border: "none",
                    color: C.goldL, fontSize: 12, fontWeight: 700,
                    cursor: "pointer", textDecoration: "underline",
                  }}>
                  Service area not set
                </button>
              );
            })()}
          </div>
          <button onClick={toggleShift} disabled={shiftBusy} style={{
            width: 52, height: 30, background: shiftOn ? C.gold : "rgba(255,255,255,0.18)", borderRadius: 15,
            display: "flex", alignItems: "center", padding: 3, justifyContent: shiftOn ? "flex-end" : "flex-start",
            border: "none", cursor: shiftBusy ? "wait" : "pointer", transition: "all 0.2s",
          }}>
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: C.white, boxShadow: "0 2px 4px rgba(0,0,0,0.2)" }} />
          </button>
        </div>
      </div>

      {/* Today stats — surface today's serve count + take-home so the card isn't a flat "—" */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 18 }}>
        <StatCard icon={DollarSign} value={completedToday > 0 && todayPay > 0 ? ssMoney(todayPay) : "—"} label="Today" color={C.ok} />
        <StatCard icon={CheckCircle2} value={completedCount} label="Served" color={C.royal} />
        <StatCard icon={Briefcase} value={active.length} label="Active" color={C.warn} />
      </div>

      {/* Active jobs */}
      <SectionHeader title="Active jobs" action="View all" onAction={() => setTab("jobs")} />
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
        {active.map(j => (
          <MiniJobCard key={j.id} job={j} onPress={() => setSelectedJob(j)} />
        ))}
      </div>

      {/* Available near you */}
      <SectionHeader title="Available near you" action="See map" onAction={() => setTab("jobs")} />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {/* [job-bundles-v2-2026-10-16] a bundle is one item here too */}
        {ssGroupJobsForList(available).slice(0, 2).map(it => (
          it.kind === "bundle"
            ? <BundleCard key={it.key} group={it} onPress={(j) => setSelectedJob(j)} />
            : <MiniJobCard key={it.key} job={it.job} onPress={() => setSelectedJob(it.job)} />
        ))}
      </div>

      {/* Quick training — count + minutes driven from catalog; tap opens Training drawer screen */}
      {trainingSummary.count > 0 && (
        <button
          onClick={() => window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: "training" }))}
          style={{
            marginTop: 18, padding: 14, borderRadius: 14, background: C.white,
            border: `1px solid ${C.steelXL}`, display: "flex", alignItems: "center", gap: 12,
            width: "100%", textAlign: "left", cursor: "pointer",
          }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, background: C.purpleL,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <GraduationCap size={20} color={C.purple} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>Training waiting</div>
            <div style={{ fontSize: 11, color: C.steel }}>
              {trainingSummary.count} {trainingSummary.count === 1 ? "video" : "videos"}
              {trainingSummary.minutes > 0 ? ` · ~${trainingSummary.minutes} min` : ""}
            </div>
          </div>
          <ChevronRight size={18} color={C.steel} />
        </button>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, value, label, color }) {
  return (
    <div style={{
      background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 12, padding: 10,
      display: "flex", flexDirection: "row", alignItems: "center", gap: 8,
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 8, background: C.goldXL,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <Icon size={16} color={color} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: C.steel, letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.deep, letterSpacing: "-0.02em" }}>{value}</div>
      </div>
    </div>
  );
}

function SectionHeader({ title, action, onAction }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: C.deep, letterSpacing: "-0.01em" }}>{title}</div>
      {action && (
        <button onClick={onAction} style={{ background: "none", border: "none", display: "flex", alignItems: "center", gap: 2, cursor: "pointer", padding: 0, color: C.royal }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>{action}</span>
          <ChevronRight size={14} color={C.royal} />
        </button>
      )}
    </div>
  );
}

function MiniJobCard({ job, onPress }) {
  const dot = ssIsPriorityJob(job) ? C.err : C.royal; // [field-list-priority-2026-10-08]
  const attemptDue = ssAttemptDueInfo(job); // [field-list-attempt-due-2026-10-08]
  return (
    <button onClick={onPress} style={{
      background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 14, padding: 0,
      display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left", width: "100%",
    }}>
      <div style={{ width: 8, height: 40, borderRadius: 4, background: dot }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
          {/* [claim-map-unmask-2026-07-27] real servee name, same weight/color pre- and post-claim */}
          <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{jobDisplay(job).name}</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.ok, marginLeft: 8 }}>{ssPayLabel(ssJobShownPay(job))/* [job-bundles-v2-2026-10-16] */}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: C.steel }}>
          <MapPin size={10} /> {ssFmtMiles(job.dist) || "—"}
          <span style={{ margin: "0 2px" }}>·</span>
          <Clock size={10} /> {job.deadline}
          {ssNormPriority(job.priority) !== "standard" && (
            <span style={{ marginLeft: 4, fontWeight: 800, color: C.err }}>· {ssPriorityLabel(job.priority)}</span>
          )}
        </div>
        {attemptDue && (
          <div style={{ fontSize: 11, fontWeight: 700, color: attemptDue.color, marginTop: 2 }}>{attemptDue.label}</div>
        )}
      </div>
      <ChevronRight size={16} color={C.steel} />
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// JOBS SCREEN
// ═══════════════════════════════════════════════════════════════════════
function JobsScreen({ setSelectedJob, setRoutePlannerOpen, selectedJob }) {
  const { jobs, isTablet, routePolyline, setRoutePolyline, user } = React.useContext(AppCtx);
  // On tablet both Map and List are always visible side-by-side, so the toggle
  // is irrelevant. Default to "map" still (used only on phone).
  const [view, setView] = useState("map");
  const [filter, setFilter] = useState("all");
  /* [server-archived-jobs-2026-10-18] Archived filter. GET /api/jobs?archived=1 already
     returns ONLY archived rows (archived=true or archived_at set) through the same server
     visibility filter as the live feed, so it can include archived *available* rows in the
     zone. We keep only jobs assigned to this server (server_id === my email, the same
     comparison the backend uses). Loaded lazily the first time the chip is tapped. */
  const [archived, setArchived] = useState(null); // null = not loaded, [] = none
  const [archivedErr, setArchivedErr] = useState("");
  const [archivedLoading, setArchivedLoading] = useState(false);
  const loadArchived = useCallback(async () => {
    setArchivedLoading(true); setArchivedErr("");
    try {
      const data = await api.get("/api/jobs?archived=1&limit=200");
      const me = String((user && user.email) || "").toLowerCase();
      const mine = Array.isArray(data)
        ? data.filter(r => r && me && String(r.server_id || "").toLowerCase() === me).map(_normalizeJob)
        : [];
      setArchived(mine);
    } catch (e) {
      setArchivedErr((e && e.message) || "Could not load archived jobs");
    } finally { setArchivedLoading(false); }
  }, [user]);
  function pickFilter(f) {
    setFilter(f);
    if (f === "archived" && archived == null && !archivedLoading) loadArchived();
  }
  const archivedCount = archived ? archived.length : "\u2026";

  // [job-bundles-v2-2026-10-16] a bundle counts once; non-open bundles are hidden from Available
  const availableCount = ssGroupJobsForList(jobs.filter(j => j.status === "available")).length;
  const filtered = filter === "archived" ? (archived || []) : jobs.filter(j => { // [server-archived-jobs-2026-10-18]
    if (filter === "available") return j.status === "available";
    if (filter === "active") return j.status === "active";
    return true;
  });

  // ── Tablet: side-by-side Map + List, with InlineJobDetail as third pane ──
  if (isTablet) {
    const detailOpen = !!selectedJob;
    // When detail is open, list collapses to 320px so detail gets room. When
    // closed, map and list each take 50%.
    const listWidth = detailOpen ? 320 : "50%";
    const mapWidth = detailOpen ? "calc(50% - 160px)" : "50%";
    // (50% - 160px leaves room for the 320px list while map stays roughly half)
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Filter chips bar — tab pills (Map/List) hidden on tablet */}
        <div style={{ padding: "12px 24px 10px", background: C.bg, borderBottom: `1px solid ${C.steelXL}` }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <FilterChip label="All" count={jobs.length} active={filter === "all"} onPress={() => setFilter("all")} />
            <FilterChip label="Available" count={availableCount} active={filter === "available"} onPress={() => setFilter("available")} />
            <FilterChip label="Active" count={jobs.filter(j => j.status === "active").length} active={filter === "active"} onPress={() => setFilter("active")} />
            <FilterChip label="Archived" count={archivedCount} active={filter === "archived"} onPress={() => pickFilter("archived")} />{/* [server-archived-jobs-2026-10-18] */}
            {routePolyline && routePolyline.length >= 2 && (
              <button onClick={() => setRoutePolyline(null)} style={clearRoutePill}>
                <X size={12} color={C.deep} strokeWidth={2.6} />
                <span>Clear route</span>
              </button>
            )}
          </div>
        </div>

        {/* Three-pane split: Map | List | (optional) InlineJobDetail */}
        <div style={{ flex: 1, position: "relative", overflow: "hidden", display: "flex" }}>
          <div style={{ width: mapWidth, height: "100%", borderRight: `1px solid ${C.steelXL}`, position: "relative", overflow: "hidden" }}>
            <MapView jobs={filtered} onPin={setSelectedJob} />
          </div>
          {/* [fc-crash-fix-2026-09-11] The list pane is the one the iPad crash came
              from, and unlike the phone shell it is mounted on every visit to Jobs.
              Boundaries here keep a bad row from taking the map and the tabs down. */}
          <div style={{ width: listWidth, height: "100%", overflow: "auto", borderRight: detailOpen ? `1px solid ${C.steelXL}` : "none" }}>
            <SsPanelBoundary name="The job list">
              <ListView jobs={filtered} onPress={setSelectedJob} filter={filter}
                archivedState={{ loading: archivedLoading, err: archivedErr, retry: loadArchived }} />{/* [server-archived-jobs-2026-10-18] */}
            </SsPanelBoundary>
          </div>
          {detailOpen && (
            <div style={{ flex: 1, height: "100%", overflow: "hidden", background: C.bg }}>
              <SsPanelBoundary name="Job detail">
                <InlineJobDetail job={selectedJob} onClose={() => setSelectedJob(null)} />
              </SsPanelBoundary>
            </div>
          )}
        </div>

        {/* Plan My Day FAB — kept fixed at bottom-right; it's small enough not
            to overlap the right edge of the iPad viewport. */}
        {filtered.length >= 2 && filter !== "archived" && (
          <button onClick={() => setRoutePlannerOpen(true)} title="Plan my day" aria-label="Plan my day" style={{
            position: "fixed", bottom: 152, right: 16, width: 44, height: 44, borderRadius: "50%",
            background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
            border: "none", display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", padding: 0, boxShadow: `0 4px 12px ${C.gold}55`, zIndex: 50,
          }}>
            <Route size={20} color={C.deep} strokeWidth={2.4} />
          </button>
        )}
      </div>
    );
  }

  // ── Phone: original toggle layout ───────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Map/List toggle pill */}
      <div style={{ padding: "12px 16px 10px", background: C.bg, borderBottom: `1px solid ${C.steelXL}` }}>
        <PosProfileBanner />{/* [pos-overhaul-2026-10-07:server-app] */}
        <div style={{
          background: C.steelXXL, borderRadius: 24, padding: 4, display: "flex", marginBottom: 10,
        }}>
          <button onClick={() => setView("map")} style={pillBtn(view === "map")}>
            <MapIcon size={14} color={view === "map" ? C.deep : C.steel} />
            <span>Map</span>
          </button>
          <button onClick={() => setView("list")} style={pillBtn(view === "list")}>
            <List size={14} color={view === "list" ? C.deep : C.steel} />
            <span>List</span>
          </button>
        </div>

        {/* Filter chips */}
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <FilterChip label="All" count={jobs.length} active={filter === "all"} onPress={() => setFilter("all")} />
          <FilterChip label="Available" count={availableCount} active={filter === "available"} onPress={() => setFilter("available")} />
          <FilterChip label="Active" count={jobs.filter(j => j.status === "active").length} active={filter === "active"} onPress={() => setFilter("active")} />
            <FilterChip label="Archived" count={archivedCount} active={filter === "archived"} onPress={() => pickFilter("archived")} />{/* [server-archived-jobs-2026-10-18] */}
          {routePolyline && routePolyline.length >= 2 && (
            <button onClick={() => setRoutePolyline(null)} style={clearRoutePill}>
              <X size={12} color={C.deep} strokeWidth={2.6} />
              <span>Clear route</span>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        {/* [server-archived-jobs-2026-10-18] Archived always shows as a list (nothing to route to). */}
        {view === "map" && filter !== "archived" ? (
          <MapView jobs={filtered} onPin={setSelectedJob} />
        ) : (
          <ListView jobs={filtered} onPress={setSelectedJob} filter={filter}
            archivedState={{ loading: archivedLoading, err: archivedErr, retry: loadArchived }} />
        )}
      </div>

      {/* Plan My Day FAB — small circular icon button stacked above the
          SafetyFAB. Only shows if there are 2+ jobs to route between. */}
      {filtered.length >= 2 && filter !== "archived" && (
        <button onClick={() => setRoutePlannerOpen(true)} title="Plan my day" aria-label="Plan my day" style={{
          position: "fixed", bottom: 152, right: 16, width: 44, height: 44, borderRadius: "50%",
          background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
          border: "none", display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", padding: 0, boxShadow: `0 4px 12px ${C.gold}55`, zIndex: 50,
        }}>
          <Route size={20} color={C.deep} strokeWidth={2.4} />
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// [claim-map-unmask-2026-07-27] SHARED CLAIM PATH — the ONE implementation of
// POST /api/jobs/:id/claim plus the structured-403 error mapping. JobDetailSheet,
// InlineJobDetail and the map's claim bar all route through this hook, so the
// backend guards (PREMIUM_RESERVED, ONBOARDING_INCOMPLETE, TRAINING_INCOMPLETE,
// CLAIM_CAP_REACHED) surface identically no matter where the server taps Accept.
// Do NOT add a second fetch to the claim endpoint anywhere in this file.
// ═══════════════════════════════════════════════════════════════════════
/* [job-bundles-2026-10-13] Bundled jobs: claim() first shows BundleConfirmSheet (job list
   from GET /api/job-bundles/:id/preview, or the 409 BUNDLE_CONFIRM_REQUIRED payload when the
   row had no bundle_id). Confirm re-runs the SAME single fetch below with
   { accept_bundle: true }. Callers render the returned `bundlePrompt` (a portal, so where
   they put it does not matter). */
function useJobClaim(onClaimed, onRefresh) {
  const { reload } = React.useContext(AppCtx);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  /* [job-bundles-2026-10-13] { job, bundle }
     [job-bundles-v2-2026-10-16] + offer (true = whole-bundle direct offer -> POST
     /api/job-bundles/:id/offer/accept), notice (pay changed), err (shown inside the sheet). */
  const [bundleAsk, setBundleAsk] = useState(null);
  // [job-bundles-v2-2026-10-16] Refresh the job list (and the caller's offers) after a bundle went away.
  async function refreshAfterBundleGone() {
    try { await reload(); } catch (_) {}
    try { if (typeof onRefresh === "function") onRefresh(); } catch (_) {}
  }
  /* [server-zone-30-2026-10-18] OUT_OF_ZONE / NO_HOME_BASE / JOB_NOT_LOCATED from a claim
     or a bundle preview: close the bundle sheet, show the backend text (inline err + toast)
     and, for OUT_OF_ZONE, reload the job list so the job drops off. True when handled. */
  async function zoneFail(e) {
    const z = ssZoneErr(e);
    if (!z) return false;
    setBundleAsk(null); setErr(z.msg); ssToast("err", z.msg);
    if (z.code === "OUT_OF_ZONE") await refreshAfterBundleGone();
    return true;
  }
  /* [job-bundles-v2-2026-10-16] Shared 409 mapping for both accept paths. Returns true when
     the error was a bundle error and has been fully handled (sheet state / message). */
  async function handleBundleError(e, ask) {
    const bx = ssBundleErr(e);
    if (!bx) return false;
    if (bx.kind === "confirm" && bx.bundle && ask.job) {
      setBundleAsk({ job: ask.job, bundle: bx.bundle });
      return true;
    }
    if (bx.kind === "changed") {
      let fresh = bx.bundle;
      if (!fresh) {
        const bid = (ask.bundle && ask.bundle.id) != null ? ask.bundle.id : (ssJobBundle(ask.job) || {}).id;
        if (bid != null) { try { fresh = await api.get(`/api/job-bundles/${encodeURIComponent(bid)}/preview`); } catch (pe) { if (await zoneFail(pe)) return true; fresh = null; } } /* [server-zone-30-2026-10-18] */
      }
      if (fresh) { setBundleAsk({ ...ask, bundle: fresh, notice: bx.msg, err: "" }); return true; }
      setBundleAsk(null); setErr(bx.msg); ssToast("err", bx.msg);
      await refreshAfterBundleGone();
      return true;
    }
    if (bx.kind === "busy") {
      setBundleAsk((cur) => (cur ? { ...cur, err: bx.msg } : cur));
      setErr(bx.msg);
      return true;
    }
    if (bx.kind === "gone") {
      setBundleAsk(null); setErr(bx.msg); ssToast("err", bx.msg);
      await refreshAfterBundleGone();
      return true;
    }
    if (bx.kind === "bundled") {
      // A single-job path hit a bundled job: show its message and open the bundle sheet.
      const bid = bx.bundleId != null ? bx.bundleId : (ssJobBundle(ask.job) || {}).id;
      let pv = null;
      if (bid != null) { try { pv = await api.get(`/api/job-bundles/${encodeURIComponent(bid)}/preview`); } catch (pe) { if (await zoneFail(pe)) return true; pv = null; } } /* [server-zone-30-2026-10-18] */
      if (pv && ask.job) setBundleAsk({ job: ask.job, bundle: pv, notice: bx.msg });
      else { setErr(bx.msg); ssToast("err", bx.msg); }
      return true;
    }
    return false;
  }
  async function claim(job, opts) {
    if (!job || job.id == null || busy) return false;
    const acceptBundle = !!(opts && opts.acceptBundle);
    setBusy(true); setErr("");
    // [job-bundles-2026-10-13] Ask before claiming a bundled job.
    if (!acceptBundle) {
      const b = ssJobBundle(job);
      if (b && b.id != null && (b.count == null || b.count > 1)) {
        let preview = null;
        try { preview = await api.get(`/api/job-bundles/${encodeURIComponent(b.id)}/preview`); }
        catch (pe) {
          // [server-zone-30-2026-10-18] Bundle outside the service area: say so, do not claim.
          if (await zoneFail(pe)) { setBusy(false); return false; }
          preview = null;
        }
        const n = preview ? Number(preview.job_count || (Array.isArray(preview.jobs) ? preview.jobs.length : 0)) : 0;
        if (preview && n > 1) {
          setBundleAsk({ job, bundle: preview });
          setBusy(false);
          return false;
        }
        // No preview: fall through — the backend answers 409 BUNDLE_CONFIRM_REQUIRED if needed.
      }
    }
    try {
      // [job-bundles-v2-2026-10-16] expected_total = the total the server just agreed to;
      // the backend answers 409 BUNDLE_CHANGED if the pay moved underneath them.
      const body = acceptBundle
        ? { accept_bundle: true, expected_total: opts && opts.expectedTotal != null ? Number(opts.expectedTotal) : undefined }
        : undefined;
      const res = await api.post(`/api/jobs/${job.id}/claim`, body);
      // Fire-and-forget — surfaces the job on the lock screen + Dynamic Island.
      // Safe to call; helper is fail-soft on non-iOS / iOS < 16.1.
      startJobLiveActivity(job);
      setBundleAsk(null); // [job-bundles-2026-10-13]
      await reload();
      // [job-bundles-2026-10-13] All bundle members are now the server's jobs.
      if (res && (res.bundle || Array.isArray(res.bundle_jobs))) {
        const n = Number((res.bundle && res.bundle.job_count) || (Array.isArray(res.bundle_jobs) ? res.bundle_jobs.length : 0));
        ssToast("ok", n > 1 ? `Accepted all ${n} jobs in this bundle` : "Accepted bundle");
      }
      if (typeof onClaimed === "function") onClaimed(job);
      return true;
    } catch (e) {
      // [job-bundles-2026-10-13] Backend wants the bundle confirmed — show the prompt.
      // [job-bundles-v2-2026-10-16] + BUNDLE_CHANGED / BUSY / NOT_AVAILABLE / OFFERED / BUNDLED_JOB.
      const ask = bundleAsk && bundleAsk.job && String(bundleAsk.job.id) === String(job.id) ? bundleAsk : { job };
      if (await zoneFail(e)) return false; // [server-zone-30-2026-10-18]
      if (await handleBundleError(e, acceptBundle ? ask : { job })) return false;
      if (acceptBundle) setBundleAsk(null);
      // [claim-error-surface-2026-06-23] Surface the REAL backend reason (parity
      // with web), not a generic message. Backend returns 403 { code, error }
      // for these blocks; api.req attaches it as e.body / e.code.
      const c = e && (e.code || (e.body && e.body.code));
      const be = e && e.body && e.body.error;
      let msg;
      if (c === "PREMIUM_RESERVED") msg = "This job is reserved for Pro subscribers for the first 24 hours after posting. It opens to all servers if it isn't claimed.";
      else if (c === "ONBOARDING_INCOMPLETE") msg = be || "Finish onboarding before you can claim jobs.";
      else if (c === "TRAINING_INCOMPLETE") msg = "Finish your required training before you can claim jobs.";
      else if (c === "CLAIM_CAP_REACHED") msg = be || "You've reached your active-job limit — finish or release a job first.";
      else if (c === "UNREGISTERED_ANNUAL_LIMIT") msg = be || "You've reached the 10-serve annual limit for unregistered servers (B&P Code 22350)."; /* [unregistered-cap-2026-09-07] */
      else msg = be || e.message || "Could not accept job";
      setErr(msg);
      if (acceptBundle) ssToast("err", msg); // [job-bundles-v2-2026-10-16] the sheet closed; callers like BundleCard may not render err
      // If onboarding is the blocker, re-check status so the login-lock takes over.
      if (c === "ONBOARDING_INCOMPLETE") { try { window.dispatchEvent(new CustomEvent("cle-onboarding-updated")); } catch (_) {} }
      return false;
    }
    finally { setBusy(false); }
  }
  /* [job-bundles-v2-2026-10-16] Whole-bundle direct offer: open the same sheet; confirm posts
     /api/job-bundles/:id/offer/accept { expected_total }. */
  const reviewBundleOffer = React.useCallback((bundle) => {
    if (!bundle || bundle.id == null) return;
    setErr("");
    setBundleAsk({ offer: true, bundle });
  }, []);
  async function acceptBundleOffer(ask) {
    const b = ask && ask.bundle;
    if (!b || b.id == null || busy) return false;
    setBusy(true); setErr("");
    setBundleAsk((cur) => (cur ? { ...cur, err: "" } : cur));
    try {
      const res = await api.post(`/api/job-bundles/${encodeURIComponent(b.id)}/offer/accept`, { expected_total: Number(b.total_pay) });
      setBundleAsk(null);
      try { await reload(); } catch (_) {}
      const n = Number((res && res.bundle && res.bundle.job_count) || (res && Array.isArray(res.jobs) ? res.jobs.length : 0) || b.job_count || 0);
      ssToast("ok", n > 1 ? `Accepted all ${n} jobs in this bundle` : "Accepted bundle");
      try { const first = res && Array.isArray(res.jobs) && res.jobs[0]; if (first) startJobLiveActivity(_normalizeJob(first)); } catch (_) {}
      if (typeof onClaimed === "function") onClaimed(null);
      return true;
    } catch (e) {
      if (await zoneFail(e)) return false; // [server-zone-30-2026-10-18]
      if (await handleBundleError(e, ask)) return false;
      const c = e && (e.code || (e.body && e.body.code));
      const be = e && e.body && e.body.error;
      let msg;
      if (c === "OFFER_EXPIRED") msg = "This offer ran out — the bundle went back to the office.";
      else if (c === "NOT_OFFERED_TO_YOU") msg = "This bundle is not offered to you.";
      else if (c === "CLAIM_CAP_REACHED") msg = be || "You've reached your active-job limit — finish or release a job first.";
      else msg = be || e.message || "Could not accept this bundle";
      setBundleAsk(null); setErr(msg); ssToast("err", msg);
      await refreshAfterBundleGone();
      return false;
    } finally { setBusy(false); }
  }
  // [job-bundles-2026-10-13] Rendered by every caller; portal-mounted, fixed overlay.
  // [job-bundles-v2-2026-10-16] keyed on the numbers so a BUNDLE_CHANGED re-show clears the tick.
  const bundlePrompt = bundleAsk ? (
    <BundleConfirmSheet
      key={[bundleAsk.bundle && bundleAsk.bundle.id, bundleAsk.bundle && bundleAsk.bundle.total_pay, bundleAsk.bundle && bundleAsk.bundle.job_count, bundleAsk.notice || ""].join("|")}
      bundle={bundleAsk.bundle}
      busy={busy}
      notice={bundleAsk.notice || ""}
      error={bundleAsk.err || ""}
      onCancel={() => { if (!busy) setBundleAsk(null); }}
      onConfirm={() => (bundleAsk.offer
        ? acceptBundleOffer(bundleAsk)
        : claim(bundleAsk.job, { acceptBundle: true, expectedTotal: bundleAsk.bundle && bundleAsk.bundle.total_pay }))}
    />
  ) : null;
  return { claim, busy, err, setErr, bundlePrompt, reviewBundleOffer };
}

/* [server-zone-30-2026-10-18] Service-area refusals from POST /api/jobs/:id/claim and
   GET /api/job-bundles/:id/preview -> { code, msg } or null. The backend only shows a server
   jobs within 30 miles of their admin-set home base and refuses claims outside it. The
   backend `error` text is shown as-is; the fallbacks only cover an empty body. */
function ssZoneErr(e) {
  const code = (e && (e.code || (e.body && e.body.code))) || "";
  const be = e && e.body && e.body.error;
  if (code === "OUT_OF_ZONE") return { code, msg: be || "This job is outside your 30-mile service area." };
  if (code === "NO_HOME_BASE") return { code, msg: be || "Your service area is not set yet. Please contact support." };
  if (code === "JOB_NOT_LOCATED") return { code, msg: be || "This job's address could not be located yet. Try again later or contact support." };
  return null;
}

/* [job-bundles-v2-2026-10-16] Bundle 409 codes -> { kind, msg, bundle?, bundleId? } or null. */
function ssBundleErr(e) {
  const code = (e && (e.code || (e.body && e.body.code))) || "";
  const be = e && e.body && e.body.error;
  const bundle = e && e.body && e.body.bundle && typeof e.body.bundle === "object" ? e.body.bundle : null;
  if (code === "BUNDLE_CONFIRM_REQUIRED") return { kind: "confirm", bundle, msg: be || "" };
  if (code === "BUNDLE_CHANGED") return { kind: "changed", bundle, msg: "The pay for this bundle changed. Review and accept again." };
  if (code === "BUNDLE_BUSY" || code === "RETRY") return { kind: "busy", msg: "Someone else is accepting this bundle right now. Try again in a moment." };
  if (code === "BUNDLE_NOT_AVAILABLE") return { kind: "gone", msg: be || "This bundle is no longer available." };
  if (code === "BUNDLE_OFFERED") return { kind: "gone", msg: be || "This bundle is being offered to another server right now." };
  if (code === "BUNDLED_JOB") return { kind: "bundled", bundleId: e.body && e.body.bundle_id != null ? e.body.bundle_id : null,
    msg: be || "This job is part of a bundle. Accepting assigns every job in the bundle to you." };
  return null;
}

/* [job-bundles-2026-10-13] "Accept all N jobs" prompt. Shows SERVER pay only (total_pay);
   the client price is never part of the preview payload and is never read here. */
function ssBundleDate(d) {
  if (!d) return "—";
  try {
    // A bare YYYY-MM-DD is a calendar date; new Date() would read it as UTC and show the day before.
    const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const dt = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(d);
    if (isNaN(dt.getTime())) return String(d);
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch (_) { return String(d); }
}
/* [job-bundles-v2-2026-10-16] Sortable time for a deadline (bare dates read as local). */
function ssBundleDateMs(d) {
  if (!d) return null;
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const t = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime() : new Date(d).getTime();
  return isFinite(t) ? t : null;
}
function ssEarliestDeadline(list) {
  let best = null, bestMs = null;
  (list || []).forEach((d) => { const ms = ssBundleDateMs(d); if (ms != null && (bestMs == null || ms < bestMs)) { best = d; bestMs = ms; } });
  return best;
}
/* [job-bundles-v2-2026-10-16] "First job $35.00 · each additional job $9.80 · Total $64.40".
   Server pay only — first_job_pay / additional_job_pay / total_pay (or pay_text). */
function ssBundlePayText(b) {
  if (!b) return "";
  const first = Number(b.first_job_pay != null ? b.first_job_pay : b.firstPay);
  const add = Number(b.additional_job_pay != null ? b.additional_job_pay : b.additionalPay);
  const total = b.total_pay != null ? b.total_pay : b.totalPay;
  if (b.pay_model !== "split" && isFinite(first) && first > 0 && isFinite(add) && add >= 0) {
    return `First job ${ssMoney(first)} · each additional job ${ssMoney(add)} · Total ${ssPayLabel(total)}`;
  }
  if (b.pay_text) return String(b.pay_text);
  return `Total ${ssPayLabel(total)}`;
}
function BundleConfirmSheet({ bundle, busy, onConfirm, onCancel, notice, error }) {
  const b = bundle || {};
  const jobs = Array.isArray(b.jobs) ? b.jobs : [];
  const n = Number(b.job_count || jobs.length || 0);
  const addr = b.service_address || "this address";
  const total = ssPayLabel(b.total_pay);
  const earliest = b.earliest_deadline || ssEarliestDeadline(jobs.map((j) => j.deadline));
  // [job-bundles-v2-2026-10-16] Required acknowledgement — confirm stays disabled until ticked.
  const [agreed, setAgreed] = useState(false);
  const canConfirm = agreed && !busy;
  const node = (
    <div role="dialog" aria-modal="true" aria-label={`Bundled order: ${n} jobs`} style={{
      position: "fixed", inset: 0, zIndex: 3000, display: "flex", alignItems: "flex-end", justifyContent: "center",
      background: "rgba(10,22,40,0.55)",
    }} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{
        /* [job-bundles-v2-2026-10-16] iPhone: never taller than the screen below the notch. */
        width: "100%", maxWidth: 560, maxHeight: "calc(100% - env(safe-area-inset-top, 0px) - 24px)", background: C.bg, borderRadius: "24px 24px 0 0",
        display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 -10px 40px rgba(0,0,0,0.3)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)", boxSizing: "border-box",
      }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          <div style={{ padding: "18px 16px 10px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>BUNDLED ORDER</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, marginTop: 2, lineHeight: 1.3 }}>Bundled order: {n} jobs at {addr}</div>
            {notice ? (
              <div role="alert" style={{ marginTop: 10, padding: "9px 11px", background: C.warnL, border: `1px solid ${C.warn}`, borderRadius: 10, fontSize: 13, fontWeight: 700, color: C.deep, lineHeight: 1.4 }}>
                {notice}
              </div>
            ) : null}
            <div style={{ fontSize: 13, color: C.deep, lineHeight: 1.45, marginTop: 8 }}>
              Accepting assigns all {n} jobs to you. You must serve every one yourself — you can't accept or hand off part of this bundle.
            </div>
            <div style={{ marginTop: 10, padding: "10px 12px", background: C.white, border: `1.5px solid ${C.gold}`, borderRadius: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: C.steel, letterSpacing: "0.1em" }}>YOUR PAY</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.ok, marginTop: 1 }}>{total} total</div>
              <div style={{ fontSize: 12, color: C.deep, marginTop: 2, lineHeight: 1.4 }}>{ssBundlePayText(b)}</div>
            </div>
            <div style={{ fontSize: 13, color: C.deep, fontWeight: 700, marginTop: 10 }}>Earliest deadline: {ssBundleDate(earliest)}</div>
          </div>
          <div style={{ padding: "0 16px 6px" }}>
            {jobs.map((j) => (
              <div key={String(j.id)} style={{
                padding: 12, marginBottom: 8, background: C.white, borderRadius: 12, border: `1px solid ${C.steelXL}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, minWidth: 0 }}>
                    Job #{j.job_number || j.id}{j.case_number ? ` · Case ${j.case_number}` : ""}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.ok, flexShrink: 0 }}>
                    {ssPayLabel(j.server_payout)}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: C.steel, marginTop: 2, lineHeight: 1.4 }}>
                  {[j.recipient_name || "Unnamed", j.service_type || "", "Due " + ssBundleDate(j.deadline)].filter(Boolean).join(" · ")}
                </div>
                <div style={{ fontSize: 11, color: C.steel, marginTop: 2 }}>
                  {[j.pay_role === "first" ? "First job" : j.pay_role === "additional" ? "Additional job" : "",
                    j.doc_count != null ? `${Number(j.doc_count) || 0} document${Number(j.doc_count) === 1 ? "" : "s"}` : ""].filter(Boolean).join(" · ")}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: "10px 16px 16px", flexShrink: 0, borderTop: `1px solid ${C.steelXL}`, background: C.white }}>
          {error ? (
            <div role="alert" style={{ fontSize: 12, color: C.err, background: C.errL, padding: "7px 10px", borderRadius: 9, marginBottom: 8, lineHeight: 1.4 }}>{error}</div>
          ) : null}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, fontWeight: 700, color: C.deep, lineHeight: 1.35, marginBottom: 10, cursor: "pointer" }}>
            <input type="checkbox" checked={agreed} disabled={busy} onChange={(e) => setAgreed(!!e.target.checked)}
              aria-required="true" style={{ width: 20, height: 20, margin: 0, flexShrink: 0, accentColor: C.deep }} />
            <span>I will serve all {n} jobs in this bundle myself.</span>
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onCancel} disabled={busy} style={{
              flex: 1, padding: 14, background: C.white, color: C.deep, border: `1.5px solid ${C.deep}`,
              borderRadius: 14, fontSize: 14, fontWeight: 700, cursor: busy ? "default" : "pointer",
            }}>Cancel</button>
            <button onClick={() => { if (canConfirm) onConfirm(); }} disabled={!canConfirm} style={{
              flex: 1.8, padding: 14, background: canConfirm ? C.deep : C.steelL, color: C.white, border: "none",
              borderRadius: 14, fontSize: 14, fontWeight: 800, cursor: canConfirm ? "pointer" : "default", opacity: busy ? 0.65 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <Check size={17} /> {busy ? "Accepting…" : `Accept all ${n} jobs · ${total}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  try { if (typeof document !== "undefined" && document.body) return createPortal(node, document.body); } catch (_) {}
  return node;
}

/* [job-bundles-2026-10-13] "Bundle · N jobs · $X total" (server pay) for bundled jobs. */
function BundlePill({ job }) {
  if (!ssIsMultiBundle(job)) return null;
  const b = ssJobBundle(job);
  return (
    <Pill bg={C.goldXL} color="#7a5e1a" icon={Briefcase}>
      {`Bundle · ${b.count} jobs` + (b.totalPay != null && b.totalPay > 0 ? ` · ${ssMoney(b.totalPay)} total` : "")}
    </Pill>
  );
}
/* [job-bundles-2026-10-13] Job-detail note for a bundled job. */
function BundleNote({ job, style }) {
  if (!ssIsMultiBundle(job)) return null;
  const b = ssJobBundle(job);
  return (
    <div style={{
      margin: "10px 14px 0", padding: "10px 12px", background: C.goldXL, border: `1.5px solid ${C.gold}`,
      borderRadius: 12, color: C.deep, fontSize: 13, lineHeight: 1.4, display: "flex", gap: 8, alignItems: "flex-start",
      ...(style || {}),
    }}>
      <Briefcase size={16} color={C.deep} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>
        Part of a bundled order of {b.count} jobs at this address.{job.status === "available" ? ` Accepting assigns all ${b.count} to you, and you must serve every one yourself.` : " You serve every job in this bundle."}
        {b.totalPay != null && b.totalPay > 0 ? <strong style={{ display: "block", marginTop: 2 }}>Bundle pay: {ssMoney(b.totalPay)} total</strong> : null}
        {b.firstPay != null && b.firstPay > 0 ? <span style={{ display: "block", fontSize: 12, marginTop: 1 }}>{ssBundlePayText({ first_job_pay: b.firstPay, additional_job_pay: b.additionalPay, total_pay: b.totalPay })}</span> : null}
      </span>
    </div>
  );
}
/* [network-claim-label-2026-10-28] A network job owned by another company that this account cannot be paid
   for (backend sets network_claim_blocked with the same rule the claim guard enforces). Show who owns it
   instead of an Accept button that would only fail. */
function SsNetworkBlockedNotice({ job, style }) {
  const co = job && job.network_owner_company ? job.network_owner_company : "another company";
  return (
    <div style={{
      width: "100%", padding: "12px 14px", background: C.goldXL, border: `1.5px solid ${C.gold}`, color: C.deep, borderRadius: 14,
      fontSize: 13, lineHeight: 1.4, textAlign: "center", boxSizing: "border-box", ...(style || {}),
    }}>
      <strong style={{ display: "block", fontSize: 14, marginBottom: 2 }}>Belongs to {co}</strong>
      Accept it from your {co} account.
    </div>
  );
}
function ssAcceptLabel(job) {
  if (ssIsMultiBundle(job)) return `Review bundle · ${ssJobBundle(job).count} jobs`; // [job-bundles-v2-2026-10-16]
  return `Accept this job · ${ssPayLabel(ssJobShownPay(job))/* [job-bundles-v2-2026-10-16] */}`;
}
/* [job-bundles-v2-2026-10-16] The pay to show for a job card/detail. An AVAILABLE bundle
   member shows the bundle total (the server can only take them all), never its own share. */
function ssJobShownPay(job) {
  if (job && job.status === "available" && ssIsMultiBundle(job)) {
    const b = ssJobBundle(job);
    if (b.totalPay != null && b.totalPay > 0) return b.totalPay;
  }
  return job ? job.pay : null;
}

/* ═══════════════════════════════════════════════════════════════════════
   [job-bundles-v2-2026-10-16] BUNDLED JOBS v2 — list grouping, BundleCard, My Jobs
   bundle group (release), bundle offers. ONE server accepts and serves every job in a
   bundle: Available shows ONE card per bundle (never a member card or a member's own
   pay), and every accept goes through BundleConfirmSheet. Server pay only.
   ═══════════════════════════════════════════════════════════════════════ */
/* Push 'bundle_offer' deep link. Module-level so it survives a cold start: Home's
   OfferBanner picks it up when it mounts (or right away via ss-open-bundle-offer). */
let ssPendingBundleOfferId = null;
function ssOpenBundleOffer(id) {
  ssPendingBundleOfferId = String(id);
  try { window.dispatchEvent(new CustomEvent("ss-open-tab", { detail: "home" })); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent("ss-open-bundle-offer", { detail: String(id) })); } catch (_) {}
}
function ssBundleListable(job) {
  const b = ssJobBundle(job);
  return !!(b && b.id != null && (b.count == null || b.count > 1));
}
/* Ordered list items: { kind: "job", job } | { kind: "bundle" (available) | "mybundle"
   (the server's own), id, jobs }. Available members of a bundle that is not 'open'
   (assigned / dissolved / offered elsewhere) are dropped. Order follows the input. */
function ssGroupJobsForList(jobs) {
  const out = [];
  const groups = {};
  (jobs || []).forEach((j) => {
    if (!j) return;
    const b = ssBundleListable(j) ? ssJobBundle(j) : null;
    if (!b || (j.status !== "available" && j.status !== "active")) { out.push({ kind: "job", key: "j" + j.id, job: j }); return; }
    if (j.status === "available" && b.status && b.status !== "open") return;
    const mine = j.status === "active";
    const key = (mine ? "m" : "a") + b.id;
    let g = groups[key];
    if (!g) { g = groups[key] = { kind: mine ? "mybundle" : "bundle", key, id: b.id, jobs: [] }; out.push(g); }
    g.jobs.push(j);
  });
  return out;
}
function ssBundleSummary(group) {
  const list = (group && group.jobs) || [];
  const first = list[0] || null;
  const b = (first && ssJobBundle(first)) || {};
  const primary = list.find((j) => (ssJobBundle(j) || {}).payRole === "first") || first;
  const earliest = ssEarliestDeadline(list.map((j) => {
    const r = (j && j._raw) || {};
    return r.deadline || r.deadline_at || r.attempt_due_date || null;
  }));
  return {
    id: b.id != null ? b.id : group && group.id,
    count: b.count || list.length,
    total: b.totalPay,
    firstPay: b.firstPay,
    additionalPay: b.additionalPay,
    label: b.label || "",
    address: b.address || (first ? (jobDisplay(first).streetCity || first.address || "") : ""),
    earliest,
    offeredToMe: !!b.offeredToMe,
    primary,
  };
}
function ssBundleBreakdown(s) {
  if (!s || s.firstPay == null || !(s.firstPay > 0)) return "";
  return ssBundlePayText({ first_job_pay: s.firstPay, additional_job_pay: s.additionalPay, total_pay: s.total });
}

/* Available list: ONE card per bundle. "Review bundle" opens BundleConfirmSheet. */
function BundleCard({ group, onPress }) {
  const s = ssBundleSummary(group);
  const [open, setOpen] = useState(false);
  const { claim, busy, err, bundlePrompt, reviewBundleOffer } = useJobClaim();
  const { reload } = React.useContext(AppCtx); // [server-zone-30-2026-10-18]
  const breakdown = ssBundleBreakdown(s);
  const hidden = Math.max(0, s.count - group.jobs.length);
  async function review() {
    if (busy || !s.primary) return;
    if (s.offeredToMe && s.id != null) {
      // A live offer to THIS server: accept through the offer endpoint.
      let pv = null;
      try { pv = await api.get(`/api/job-bundles/${encodeURIComponent(s.id)}/preview`); }
      catch (pe) {
        // [server-zone-30-2026-10-18] Outside the service area: show the backend text and refresh.
        const z = ssZoneErr(pe);
        if (z) { ssToast("err", z.msg); if (z.code === "OUT_OF_ZONE") { try { await reload(); } catch (_) {} } return; }
        pv = null;
      }
      if (pv && pv.id != null) { reviewBundleOffer(pv); return; }
    }
    claim(s.primary);
  }
  return (
    <div style={{
      background: C.white, border: `1.5px solid ${C.gold}`, borderRadius: 16, padding: 14,
      boxShadow: "0 1px 3px rgba(15,40,96,0.04)", textAlign: "left",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: "#7a5e1a" }}>BUNDLED ORDER</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.deep, letterSpacing: "-0.01em" }}>{s.count} jobs at one address</div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.ok }}>{ssPayLabel(s.total)}</div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel }}>total pay</div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 6 }}>
        <MapPin size={14} color={C.steel} style={{ marginTop: 1, flexShrink: 0 }} />
        <div style={{ fontSize: 13, color: C.deep, fontWeight: 500 }}>{s.address || "—"}</div>
      </div>
      {breakdown ? <div style={{ fontSize: 12, color: C.deep, marginBottom: 4, lineHeight: 1.4 }}>{breakdown}</div> : null}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center", marginBottom: 8 }}>
        <Pill bg={C.goldXL} color="#7a5e1a" icon={Briefcase}>{`Bundle · ${s.count} jobs`}</Pill>
        {s.offeredToMe ? <Pill bg={C.infoL} color={C.info}>Offered to you</Pill> : null}
        <span style={{ fontSize: 12, fontWeight: 700, color: C.deep }}>Earliest deadline: {ssBundleDate(s.earliest)}</span>
      </div>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} style={{
        display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: "2px 0",
        color: C.royal, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
      }}>
        {open ? <ChevronUp size={14} color={C.royal} /> : <ChevronDown size={14} color={C.royal} />}
        {open ? "Hide the jobs" : `Show the ${s.count} jobs`}
      </button>
      {open ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
          {group.jobs.map((j) => (
            <button key={String(j.id)} type="button" onClick={() => { if (onPress) onPress(j); }} style={{
              display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", cursor: onPress ? "pointer" : "default",
              background: C.bg, border: `1px solid ${C.steelXL}`, borderRadius: 10, padding: "8px 10px", fontFamily: "inherit",
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{jobDisplay(j).name}</div>
                <div style={{ fontSize: 11, color: C.steel }}>
                  {[`Job #${j.jobNumber}`, j.type, j.deadline ? `Due ${j.deadline}` : ""].filter(Boolean).join(" · ")}
                </div>
              </div>
              {onPress ? <ChevronRight size={14} color={C.steel} style={{ flexShrink: 0 }} /> : null}
            </button>
          ))}
          {hidden > 0 ? <div style={{ fontSize: 11, color: C.steel }}>+ {hidden} more in this bundle (shown when you review it)</div> : null}
        </div>
      ) : null}
      {err ? <div style={{ color: C.err, fontSize: 12, marginTop: 8, lineHeight: 1.4 }}>{err}</div> : null}
      <button type="button" onClick={review} disabled={busy} style={{
        width: "100%", marginTop: 10, padding: 12, background: C.deep, color: C.white, border: "none",
        borderRadius: 13, fontSize: 14, fontWeight: 800, cursor: busy ? "default" : "pointer", opacity: busy ? 0.65 : 1,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit",
      }}>
        <Briefcase size={16} /> {busy ? "Loading…" : "Review bundle"}
      </button>
      {bundlePrompt}
    </div>
  );
}

/* My Jobs: the server's own bundle jobs under one "Bundle · N jobs" header. */
function MyBundleGroup({ group, children }) {
  const { reload } = React.useContext(AppCtx);
  const s = ssBundleSummary(group);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function release() {
    if (busy || s.id == null) return;
    let ok = true;
    try { ok = window.confirm(`Release this bundle? All ${s.count} jobs go back to the office. You can only release it before any attempt is made.`); } catch (_) { ok = true; }
    if (!ok) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/api/job-bundles/${encodeURIComponent(s.id)}/release`);
      ssToast("ok", `Released all ${s.count} jobs back to the office`);
      try { await reload(); } catch (_) {}
    } catch (e) {
      const c = e && (e.code || (e.body && e.body.code));
      const be = e && e.body && e.body.error;
      const msg = c === "BUNDLE_IN_PROGRESS"
        ? (be || "Work has started on this bundle, so it can't be released. Contact the office.")
        : (be || e.message || "Could not release this bundle");
      setErr(msg); ssToast("err", msg);
    } finally { setBusy(false); }
  }
  return (
    <div style={{ background: C.goldXL, border: `1.5px solid ${C.gold}`, borderRadius: 18, padding: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 4px 8px" }}>
        <Briefcase size={16} color={C.deep} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.deep }}>
            Bundle · {s.count} jobs{s.total != null && s.total > 0 ? ` · ${ssMoney(s.total)} total` : ""}
          </div>
          {s.address ? <div style={{ fontSize: 11, color: C.steel, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.address}</div> : null}
        </div>
        <button type="button" onClick={release} disabled={busy} style={{
          flexShrink: 0, padding: "7px 10px", borderRadius: 10, border: `1.5px solid ${C.err}`, background: C.white,
          color: C.err, fontSize: 12, fontWeight: 800, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, fontFamily: "inherit",
        }}>{busy ? "Releasing…" : "Release bundle"}</button>
      </div>
      {err ? <div role="alert" style={{ fontSize: 12, color: C.err, background: C.errL, padding: "7px 10px", borderRadius: 9, margin: "0 2px 8px", lineHeight: 1.4 }}>{err}</div> : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}

/* Home: a whole bundle offered directly to THIS server. Review & accept opens the shared
   sheet (POST /api/job-bundles/:id/offer/accept); Decline posts /offer/decline. */
function BundleOfferCard({ offer, onReview, onDone }) {
  const exp = offer && offer.offer && offer.offer.expires_at ? offer.offer.expires_at : null;
  const { label, urgent, expired } = useOfferCountdown(exp || "2999-01-01T00:00:00Z");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState("");
  const jobs = Array.isArray(offer && offer.jobs) ? offer.jobs : [];
  const n = Number((offer && offer.job_count) || jobs.length || 0);
  const isExpired = !!exp && expired;
  const earliest = (offer && offer.earliest_deadline) || ssEarliestDeadline(jobs.map((j) => j.deadline));
  async function decline() {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/api/job-bundles/${encodeURIComponent(offer.id)}/offer/decline`, reason ? { reason } : {});
      ssToast("ok", "Bundle declined");
      await onDone();
    } catch (e) {
      const be = e && e.body && e.body.error;
      setErr(be || e.message || "Something went wrong — try again.");
    } finally { setBusy(false); }
  }
  const tone = urgent && !!exp ? C.err : C.gold;
  return (
    <div style={{
      background: C.white, border: `2px solid ${tone}`, borderRadius: 16, padding: 14, marginBottom: 10,
      boxShadow: `0 6px 18px ${tone}22`,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: C.gold }}>BUNDLE OFFERED TO YOU</div>
        {exp ? (
          <div style={{
            fontSize: 12, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: urgent ? C.err : C.deep,
            background: urgent ? C.errL : C.goldXL, padding: "3px 9px", borderRadius: 999,
          }}>{isExpired ? "Expired" : `Expires in ${label}`}</div>
        ) : null}
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, color: C.deep, marginBottom: 2 }}>{n} jobs at one address</div>
      {offer.service_address ? <div style={{ fontSize: 12, color: C.steel, marginBottom: 6 }}>{offer.service_address}</div> : null}
      <div style={{ fontSize: 15, fontWeight: 800, color: C.ok }}>{ssPayLabel(offer.total_pay)} total</div>
      <div style={{ fontSize: 12, color: C.deep, marginTop: 2, lineHeight: 1.4 }}>{ssBundlePayText(offer)}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.deep, marginTop: 4, marginBottom: 8 }}>Earliest deadline: {ssBundleDate(earliest)}</div>
      {err ? <div style={{ fontSize: 12, color: C.err, background: C.errL, padding: "7px 10px", borderRadius: 9, marginBottom: 8 }}>{err}</div> : null}
      {declineOpen ? (
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
          placeholder="Reason (optional) — this goes to dispatch"
          style={{
            width: "100%", boxSizing: "border-box", marginBottom: 8, padding: "8px 10px",
            border: `1px solid ${C.steelL}`, borderRadius: 9, fontSize: 13,
            fontFamily: "inherit", background: C.bgW, color: C.ink, resize: "vertical",
          }} />
      ) : null}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" disabled={busy || isExpired} onClick={onReview} style={{
          flex: 2, padding: "11px 0", borderRadius: 11, border: "none",
          background: isExpired ? C.steelL : `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
          color: C.white, fontSize: 14, fontWeight: 800, cursor: busy || isExpired ? "default" : "pointer", fontFamily: "inherit",
        }}>Review &amp; accept</button>
        <button type="button" disabled={busy} onClick={() => { if (!declineOpen) setDeclineOpen(true); else decline(); }} style={{
          flex: 1, padding: "11px 0", borderRadius: 11, border: `1.5px solid ${C.err}`, background: "transparent",
          color: C.err, fontSize: 14, fontWeight: 800, cursor: busy ? "default" : "pointer", fontFamily: "inherit",
        }}>{busy ? "Sending…" : declineOpen ? "Confirm decline" : "Decline"}</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// INLINE JOB DETAIL — Tablet third-pane variant of JobDetailSheet
// Renders the same Brief/Attempts/Chat content inline (no Sheet/Backdrop).
// ═══════════════════════════════════════════════════════════════════════
function InlineJobDetail({ job, onClose }) {
  // [claim-map-unmask-2026-07-27] claim routed through the shared useJobClaim hook
  const { claim, busy: acceptBusy, err: acceptErr, bundlePrompt } = useJobClaim(() => onClose()); // [job-bundles-2026-10-13]
  function handleAccept() { return claim(job); }

  const [tab, setTab] = useState("brief");
  const [attemptOpen, setAttemptOpen] = useState(false);
  const [filingOpen, setFilingOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [posOpen, setPosOpen] = useState(false);
  const courthouseKind = courthouseServiceType(job);
  const courtSt = useCourtStatus(job); // [court-closed-warning-2026-10-08]
  const print = useJobPrint(job); // [server-parity-web-2026-10-05]
  const canPrint = job.status === "active" || job.status === "completed";
  const sup = useJobSupport(job); // [support-tickets-2026-10-08]
  const focusTicketId = useSupportDeepLink(job, setTab); // [ticket-link-2026-10-08]

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", position: "relative", background: C.bg }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
        padding: "18px 18px 14px", color: C.white, flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: C.goldL }}>
            JOB #{job.jobNumber}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {sup.canCreate ? <EscalateButton onPress={() => sup.openEscalate()} badge={sup.openCount} /> : null}{/* [support-tickets-2026-10-08] */}
            {canPrint ? <JobPrintButtons print={print} /> : null}{/* [server-parity-web-2026-10-05] */}
            <button onClick={onClose} style={iconBtn} aria-label="Close job detail">
              <X size={20} color={C.white} />
            </button>
          </div>
        </div>

        {/* [claim-map-unmask-2026-07-27] real servee name + client/firm shown pre-claim */}
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 4 }}>{jobDisplay(job).name}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.85, marginBottom: 12 }}>
          <span>{job.type}</span>
          {job.client && (<><span>·</span><span>{job.client}</span></>)}
        </div>

        <div style={{
          display: "flex", justifyContent: "space-between", padding: "12px 14px",
          background: "rgba(255,255,255,0.08)", borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.1)",
        }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", opacity: 0.6 }}>PAY</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.gold }}>{ssPayLabel(ssJobShownPay(job))/* [job-bundles-v2-2026-10-16] */}</div>
          </div>
          <div style={{ width: 1, background: "rgba(255,255,255,0.1)" }} />
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", opacity: 0.6 }}>DUE</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>{job.deadline}</div>
            {/* [field-list-attempt-due-2026-10-08] */}
            {(() => { const ad = ssAttemptDueInfo(job); return ad ? (
              <div style={{ fontSize: 10, fontWeight: 700, marginTop: 3,
                color: ad.tone === "overdue" ? "#ff8a80" : ad.tone === "today" ? C.goldL : "rgba(255,255,255,0.7)" }}>{ad.label}</div>
            ) : null; })()}
          </div>
          <div style={{ width: 1, background: "rgba(255,255,255,0.1)" }} />
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", opacity: 0.6 }}>DISTANCE</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>{ssFmtMiles(job.dist) || "—"}</div>
          </div>
        </div>
      </div>

      {/* [court-closed-warning-2026-10-08] courthouse closed / closing-soon warning */}
      {courtSt && courtSt.applies ? <div style={{ flexShrink: 0 }}><CourtStatusBanner cs={courtSt} style={{ margin: "10px 14px 0" }} /></div> : null}
      <BulkServeBanner job={job} style={{ flexShrink: 0 }} />{/* [field-bulk-serve-2026-10-08] */}
      <BundleNote job={job} style={{ flexShrink: 0 }} />{/* [job-bundles-2026-10-13] */}
      <DoorTagNotice job={job} style={{ flexShrink: 0 }} />{/* [door-tags-server-2026-10-22] */}
      {/* Tab pills */}
      <div style={{ background: C.white, padding: "12px 16px 0", borderBottom: `1px solid ${C.steelXL}`, flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 4 }}>
          {[
            { id: "brief", label: "Brief", icon: FileText },
            { id: "attempts", label: "Attempts", icon: Clock },
            { id: "notes", label: "Notes", icon: Edit3 }, /* [server-parity-web-2026-10-05] */
            { id: "chat", label: "Chat", icon: MessageSquare },
            ...(sup.present ? [{ id: "support", label: "Support", icon: LifeBuoy, badge: sup.openCount, dot: sup.needsYou }] : []), /* [support-tickets-2026-10-08] */
          ].map(t => {
            const active = tab === t.id;
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                flex: 1, padding: "10px 8px", background: "none", border: "none",
                borderBottom: active ? `2.5px solid ${C.gold}` : "2.5px solid transparent",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer",
                color: active ? C.deep : C.steel,
              }}>
                <span style={{ position: "relative", display: "inline-flex" }}>
                  <Icon size={16} strokeWidth={active ? 2.4 : 1.8} />
                  {t.badge > 0 ? (
                    <span style={{
                      position: "absolute", top: -6, right: -10, minWidth: 15, height: 15, borderRadius: 8, padding: "0 4px",
                      background: t.dot ? C.ok : C.gold, color: "#fff", fontSize: 9, fontWeight: 800, lineHeight: "15px", textAlign: "center",
                    }}>{t.badge}</span>
                  ) : null}
                </span>
                <span style={{ fontSize: 11, fontWeight: active ? 800 : 600 }}>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: "auto", background: C.bg, paddingBottom: 80 }}>
        {tab === "brief" && <BriefTab job={job} onNav={() => setNavOpen(true)} onPos={() => setPosOpen(true)} />}
        {tab === "attempts" && <AttemptsTab job={job} onRecord={() => setAttemptOpen(true)} />}
        {tab === "notes" && <JobNotesTab job={job} />}{/* [server-parity-web-2026-10-05] */}
        {tab === "chat" && <ChatTab job={job} />}
        {tab === "support" && sup.present && (
          <SupportTab job={job} tickets={sup.tickets} err={sup.err} reload={sup.reload}
            canCreate={sup.canCreate} onEscalate={() => sup.openEscalate()} focusTicketId={focusTicketId} />
        )}{/* [support-tickets-2026-10-08] */}
      </div>

      {/* Sticky CTA */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, padding: "10px 14px 14px",
        background: C.white, borderTop: `1px solid ${C.steelXL}`,
      }}>
        {acceptErr && <div style={{ color: "#c0392b", fontSize: 12, marginBottom: 8, textAlign: "center" }}>{acceptErr}</div>}
        {job.status === "available" && job.network_claim_blocked === true && <SsNetworkBlockedNotice job={job} />}
        {job.status === "available" && job.network_claim_blocked !== true && (
          <button onClick={handleAccept} disabled={acceptBusy} style={{
            width: "100%", padding: 14, background: C.deep, color: C.white,
            border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            letterSpacing: "0.02em", boxShadow: `0 4px 12px ${C.deep}40`,
          }}>
            <Check size={18} /> {ssAcceptLabel(job)}{/* [job-bundles-2026-10-13] */}
          </button>
        )}
        {job.status === "active" && (
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setNavOpen(true)} style={{
              flex: 1, minHeight: 52, padding: "14px 16px", background: C.white, color: C.deep,
              border: `1.5px solid ${C.deep}`, borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <Navigation size={20} /> Navigate
            </button>
            {courthouseKind ? (
              <button onClick={() => setFilingOpen(true)} style={{
                flex: 1.2, minHeight: 52, padding: "14px 16px", background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
                color: C.deep, border: "none", borderRadius: 14, fontSize: 15, fontWeight: 800, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                boxShadow: `0 4px 10px ${C.gold}55`,
              }}>
                {courthouseKind === "document-retrieval"
                  ? (<><FolderOpen size={20} /> Retrieve documents</>)
                  : (<><FileSignature size={20} /> File documents</>)}
              </button>
            ) : (
              <button onClick={() => setAttemptOpen(true)} style={{
                flex: 1.2, minHeight: 52, padding: "14px 16px", background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
                color: C.deep, border: "none", borderRadius: 14, fontSize: 15, fontWeight: 800, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                boxShadow: `0 4px 10px ${C.gold}55`,
              }}>
                <Plus size={20} /> Log Attempt
              </button>
            )}
          </div>
        )}
      </div>

      {bundlePrompt}{/* [job-bundles-2026-10-13] */}
      {attemptOpen && <AttemptCapture job={job} onClose={() => setAttemptOpen(false)} />}
      {filingOpen && <FilingCompleteSheet job={job} kind={courthouseKind} onClose={() => setFilingOpen(false)} />}
      {navOpen && <NavigationSheet job={job} onClose={() => setNavOpen(false)} />}
      {/* [server-parity-web-2026-10-05] opens the admin-prepared proof (preview -> sign).
          [audit-fixes-2026-10-15] H25: the form picker / POSSheet fallback no longer exist. */}
      {posOpen && <ProofFlowSheet job={job} onClose={() => setPosOpen(false)} />}
      {sup.escalate && (
        <EscalateSheet job={job} seedCategory={sup.escalate.seedCategory} seedAttemptRef={sup.escalate.seedAttemptRef}
          onClose={sup.closeEscalate} onCreated={() => setTab("support")} />
      )}{/* [support-tickets-2026-10-08] */}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   PANEL ERROR BOUNDARY   [fc-crash-fix-2026-09-11]

   A render error anywhere under the tablet shell used to take the ENTIRE app with
   it — React unmounts the whole tree when nothing catches, which is the white
   screen an iPad in a windshield mount showed in the field. A boundary around each
   large pane turns that into one degraded panel: the sidebar, the tabs and every
   other pane keep working, and the operator is told what failed instead of staring
   at nothing. The message is deliberately readable (no stack, no minified code)
   because the reader is a process server, not a developer.

   Deliberately NOT wrapped around the whole app: a boundary that swallows
   everything hides regressions. One per pane, each named.
   ═══════════════════════════════════════════════════════════════════════ */
class SsPanelBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
    this.retry = this.retry.bind(this);
  }
  static getDerivedStateFromError(err) { return { err: err || new Error("Unknown error") }; }
  componentDidCatch(err, info) {
    // Console only — this must never itself throw, and it must never post anything.
    try {
      console.error("[ss-panel] " + (this.props.name || "panel") + " failed to render:",
        (err && err.message) || err, (info && info.componentStack) || "");
    } catch (_) { /* ignore */ }
  }
  retry() { this.setState({ err: null }); }
  render() {
    if (!this.state.err) return this.props.children;
    const label = this.props.name || "This panel";
    return (
      <div style={{
        height: "100%", minHeight: 180, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 12, padding: 28, textAlign: "center",
        background: C.bg, color: C.deep,
      }}>
        <AlertTriangle size={34} color={C.err} />
        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em" }}>
          {label} could not be displayed
        </div>
        <div style={{ fontSize: 13, color: C.steel, lineHeight: 1.5, maxWidth: 420 }}>
          The rest of the app is still working — switch tabs to keep going. If this keeps
          happening, sign out and back in; that rebuilds the local job cache.
        </div>
        <button type="button" onClick={this.retry} style={{
          marginTop: 4, minHeight: 44, padding: "12px 20px", borderRadius: 12,
          background: C.deep, color: C.white, border: "none",
          fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
        }}>Try again</button>
      </div>
    );
  }
}

/* ═══════════════════════════════════════════════════════════════════════
   FIELD COMMAND — single-screen iPad dash   [field-command-2026-09-11]
   Spec: ~/My Drive/App/FIELD_COMMAND_SPEC_2026-09-11.md

   One screen a process server works from a dash-mounted iPad: who to serve,
   what to serve, where it is, how to get there, who to call, how to log the
   outcome — without switching screens.

   Everything in this block is additive. It renders only inside the tablet
   shell (SS_FIELD_COMMAND_ENABLED is AND-gated by SS_TABLET_SHELL_ENABLED),
   so with the tablet kill switch off none of it can paint.

   Legal constraints that are load-bearing, not stylistic:
     - One tap may record ONLY not_home / no_answer / unsafe. served,
       sub_served and refused each swear delivery under penalty of perjury and
       must open the full attempt sheet. Nothing here posts them.
     - Subject photo, DOB and physical description are shown only on a CLAIMED
       job — a face on an unclaimed job broadcasts an identifiable person to
       every browsing server.
     - Four windows is StrikeServe POLICY. California requires reasonable
       diligence, not a fixed number of attempts, and the label says so.
     - Never prompt to photograph the person served (Civil Code 1708.8) and
       never record audio at the door (Penal Code 632). The camera target here
       is labelled for documents / door / vehicle and captures no audio.
     - Nothing sensitive is ever spoken aloud (cars carry passengers).
   ═══════════════════════════════════════════════════════════════════════ */

/* ── Motion bus ────────────────────────────────────────────────────────
   A single module-level channel for device motion. RoutePlanner's mileage
   watch publishes into it (it is the only continuous position source the app
   already runs) and so does Field Command's own watch, so the lockout keeps
   working whether or not the planner sheet happens to be open. Consumers
   subscribe; nothing here touches the DOM or React. */
const SS_FC_MOTION = { mph: null, headingDeg: null, lat: null, lng: null, accuracy: null, ts: 0 };
const _ssFcMotionSubs = new Set();
const SS_FC_MPS_TO_MPH = 2.2369362920544;

function ssPublishMotion(sample) {
  if (!sample) return;
  const ts = Number(sample.ts || Date.now());
  const lat = (sample.lat != null && isFinite(sample.lat)) ? Number(sample.lat) : null;
  const lng = (sample.lng != null && isFinite(sample.lng)) ? Number(sample.lng) : null;
  // Prefer the device's own speed. Fall back to distance/time between fixes, but
  // only over a gap wide enough that GPS jitter cannot masquerade as motion —
  // two watches publishing into this bus can land samples milliseconds apart.
  let mph = null;
  if (sample.speed != null && isFinite(sample.speed) && Number(sample.speed) >= 0) {
    mph = Number(sample.speed) * SS_FC_MPS_TO_MPH;
  } else if (lat != null && lng != null && SS_FC_MOTION.lat != null && SS_FC_MOTION.ts) {
    const dtMs = ts - SS_FC_MOTION.ts;
    if (dtMs >= 2000 && dtMs <= 120000) {
      const miles = ssHaversineMiles(
        { lat: SS_FC_MOTION.lat, lng: SS_FC_MOTION.lng },
        { lat: lat, lng: lng }
      );
      mph = miles / (dtMs / 3600000);
    }
  }
  SS_FC_MOTION.mph = (mph != null && isFinite(mph)) ? mph : null;
  SS_FC_MOTION.headingDeg = (sample.heading != null && isFinite(sample.heading)) ? Number(sample.heading) : null;
  if (lat != null) SS_FC_MOTION.lat = lat;
  if (lng != null) SS_FC_MOTION.lng = lng;
  if (sample.accuracy != null) SS_FC_MOTION.accuracy = Number(sample.accuracy) || 0;
  SS_FC_MOTION.ts = ts;
  const snapshot = {
    mph: SS_FC_MOTION.mph, headingDeg: SS_FC_MOTION.headingDeg,
    lat: SS_FC_MOTION.lat, lng: SS_FC_MOTION.lng,
    accuracy: SS_FC_MOTION.accuracy, ts: SS_FC_MOTION.ts,
  };
  _ssFcMotionSubs.forEach((fn) => { try { fn(snapshot); } catch (_) { /* one bad subscriber must not stop the rest */ } });
}

function ssSubscribeMotion(fn) {
  if (typeof fn !== "function") return () => {};
  _ssFcMotionSubs.add(fn);
  return () => { _ssFcMotionSubs.delete(fn); };
}

/* ── Motion lockout ───────────────────────────────────────────────────
   Enter at >10 mph sustained 5s. Exit at <3 mph sustained 12s — the twelve
   seconds is what stops a red light from unlocking and re-locking repeatedly.
   Evaluation runs on a 1s tick as well as on each fix, so a lock can time out
   on its own rather than waiting for a sample that may never arrive.
   If speed goes unknown or stale the lockout RELEASES: a stuck lock the
   operator cannot escape is worse than no lock at all. */
const SS_FC_ENTER_MPH = 10;
const SS_FC_ENTER_MS = 5000;
const SS_FC_EXIT_MPH = 3;
const SS_FC_EXIT_MS = 12000;
const SS_FC_STALE_MS = 25000;
const SS_FC_PARK_GRACE_MS = 6000;

function useMotionLockout(enabled) {
  const [moving, setMoving] = useState(false);
  const [mph, setMph] = useState(null);
  const movingRef = useRef(false);
  const overSinceRef = useRef(null);
  const underSinceRef = useRef(null);
  const lastRef = useRef({ mph: null, ts: 0 });
  const parkUntilRef = useRef(0);

  const commit = useCallback((v) => {
    if (movingRef.current === v) return;
    movingRef.current = v;
    setMoving(v);
  }, []);

  const evaluate = useCallback(() => {
    const now = Date.now();
    // The operator tapped "Arrived" — honour it for a short grace window. If they
    // are in fact still driving, the 5s entry test simply re-locks afterwards.
    if (now < parkUntilRef.current) {
      overSinceRef.current = null;
      underSinceRef.current = null;
      commit(false);
      return;
    }
    const last = lastRef.current;
    const fresh = !!last.ts && (now - last.ts) < SS_FC_STALE_MS;
    const cur = (fresh && typeof last.mph === "number" && isFinite(last.mph)) ? last.mph : null;
    if (cur == null) {
      overSinceRef.current = null;
      underSinceRef.current = null;
      commit(false);
      return;
    }
    if (!movingRef.current) {
      if (cur > SS_FC_ENTER_MPH) {
        if (overSinceRef.current == null) overSinceRef.current = now;
        else if (now - overSinceRef.current >= SS_FC_ENTER_MS) {
          overSinceRef.current = null;
          underSinceRef.current = null;
          commit(true);
        }
      } else {
        overSinceRef.current = null;
      }
      return;
    }
    if (cur < SS_FC_EXIT_MPH) {
      if (underSinceRef.current == null) underSinceRef.current = now;
      else if (now - underSinceRef.current >= SS_FC_EXIT_MS) {
        overSinceRef.current = null;
        underSinceRef.current = null;
        commit(false);
      }
    } else {
      underSinceRef.current = null;
    }
  }, [commit]);

  useEffect(() => {
    if (!enabled) { commit(false); return undefined; }
    const off = ssSubscribeMotion((s) => {
      lastRef.current = { mph: s.mph, ts: s.ts };
      setMph(s.mph);
      evaluate();
    });
    const id = setInterval(evaluate, 1000);
    return () => { off(); clearInterval(id); };
  }, [enabled, evaluate, commit]);

  // Single tap, no dialog. CVC 23123.5 permits a mounted device operated with
  // one swipe or tap; a modal to dismiss on unlock would itself be a task.
  const declareParked = useCallback(() => {
    parkUntilRef.current = Date.now() + SS_FC_PARK_GRACE_MS;
    overSinceRef.current = null;
    underSinceRef.current = null;
    commit(false);
  }, [commit]);

  return { moving, mph, declareParked };
}

/* ── Due-diligence windows, derived client-side ────────────────────────
   There is no `windows` column on a job, which is why every job in the app
   permanently reads "0/4 covered". Derive it from the attempt timestamps the
   backend already writes (`date`; the legacy names stay as a fallback).
   Weekend is a separate axis from the time-of-day bands, exactly as the
   AttemptsTab grid presents it. */
function ssDeriveWindows(job) {
  const rows = Array.isArray(job && job._raw && job._raw.attempts)
    ? job._raw.attempts
    : (Array.isArray(job && job.rawAttempts) ? job.rawAttempts : []);
  /* [three-windows-2026-10-08] Three windows to match the 3 included attempts:
     weekday daytime (7am-6pm), weekday evening (6pm-10pm), weekend. A court-closed
     attempt is the server's responsibility and is not one of the client's attempts. */
  const windows = { day: false, eve: false, weekend: false };
  let dated = 0;
  const countable = rows.filter(ssCountsTowardAttempts);
  for (const a of countable) {
    const ts = a && (a.date || a.attempted_at || a.timestamp || a.created_at);
    if (!ts) continue;
    const d = new Date(ts);
    if (isNaN(d.getTime())) continue;
    dated++;
    const day = d.getDay();
    const h = d.getHours();
    if (day === 0 || day === 6) windows.weekend = true;
    else if (h >= 7 && h < 18) windows.day = true;
    else if (h >= 18 && h < 22) windows.eve = true;
  }
  const covered = [windows.day, windows.eve, windows.weekend].filter(Boolean).length;
  return { windows, covered, attempts: countable.length, dated };
}

/* ── [sub-serve-mailing-2026-09-18] CCP §415.20(b) FOLLOW-UP MAILING ──────────
   Substituted service is NOT complete when the copies are left. The statute
   requires a copy to ALSO be mailed, first class, to the person to be served at
   the place where the copies were left, and service is deemed complete on the
   10th day AFTER that mailing. POS-010 item 5b(4) swears to the DATE and the
   CITY the mailing went out from.

   The backend has persisted `mailDate` / `mailCity` on the attempt record since
   [pos-dataflow-fix-2026-07-26] (server.js ~9906), binds them to POS-010 5b(4)
   and POS-030 item 3, and hard-errors SUB_SERVE_MAILING_MISSING in preflight on
   a blank date — but NO CLIENT HAS EVER SENT THEM. Every substituted-service
   proof this platform has produced therefore has 5b(4) blank and the "I
   thereafter mailed" box unticked, which is facially incomplete. This block is
   the client-side half. */

/* Mirrors _ss_deriveServiceManner (server.js ~7552) so the app and the POS
   mapper agree on which attempts owe a mailing: the explicit substitute result
   codes, PLUS a nominally-personal success code that carries a substitute's
   name — which the backend downgrades to 'substituted' and prints item 5b for.
   [attempt-badge-served-2026-10-20] 'served-substitute-mailing' is NOT in this list (it is
   caught by the 'served-substitute' prefix in ssAttemptIsSubstituted). Since
   [attempt-result-correction-2026-10-15] the backend derives it to 'substituted'
   (CCP 415.20(c)), not 'mail'. ssResultNeedsPhoto still exempts it from the photo gate. */
const SS_SUBSERVE_RESULTS = ["sub-served", "served-substitute-home", "served-substitute-business"];
/* [served-photo-required-2026-07-20] [server-parity-web-2026-10-05] A completed serve
   must carry photographic proof. Ported from strikeserve-mobile, where the gate
   tested RESULT_MAP's coarse "served" / "sub-served"; this file's RESULT_MAP emits
   the granular codes (served-personal, served-substitute-home, …) so the test is
   written against those. 'served-substitute-mailing' is service BY mail — there is
   no one to photograph — and is deliberately not gated. */
/* [attempt-gate-2026-10-08] EVERY attempt — any outcome, served or not — needs a
   verified GPS fix AND at least SS_ATTEMPT_MIN_PHOTOS uploaded photos before it can be
   saved. Only photos that finished uploading (carry an S3 key) count; an in-flight or
   failed upload is not evidence. Mirrors the backend ATTEMPT_PHOTO_GATE rule. */
const SS_ATTEMPT_MIN_PHOTOS = 3;
const SS_ATTEMPT_PHOTO_TYPES = [
  "Front door / point of contact",
  "House or unit number",
  "Wide shot of the property / street view",
];
/* [unsafe-photo-exempt-2026-10-08] Owner decision: an Unsafe attempt needs GPS but
   NO photos, so the server can leave immediately. Every other outcome keeps the minimum. */
function ssPhotosRequiredFor(outcome) {
  return String(outcome || "").toLowerCase() === "unsafe" ? 0 : SS_ATTEMPT_MIN_PHOTOS;
}
function ssUploadedPhotoCount(photos) {
  return (Array.isArray(photos) ? photos : []).filter((p) => p && p.key).length;
}
function ssResultNeedsPhoto(result) {
  const r = String(result || "").trim().toLowerCase();
  if (!r) return false;
  if (r === "served-substitute-mailing") return false;
  if (r === "served" || r === "sub-served" || r === "served-personal") return true;
  if (r === "served-registered-agent") return true; // [registered-agent-2026-10-22] a registered-agent serve needs proof like any serve
  if (SS_SUBSERVE_RESULTS.indexOf(r) !== -1) return true;
  return r.indexOf("served-substitute") === 0;
}
/* [attempt-outcomes-2026-10-07] Field outcomes that record WHY a visit produced no
   service. Each id maps to a value the backend enum accepts (applyServiceAttempt,
   "Invalid or missing attempt result"); 'moved', 'vacant' and 'court-closed' were added
   to that enum alongside this change. None of these is a service outcome: the backend's
   success test is 'served' | 'sub-served' | 'served-*', so every one keeps the job
   in-progress and is listed as a failed attempt in the Declaration re Diligence.
   GPS / time capture is identical to every other non-service attempt — same
   submitAttempt path, same gps payload, same backdate rules server-side. */
const SS_NONSERVICE_RESULT_MAP = {
  bad_addr: "wrong-address",   // was "not-served" — the backend has had the specific code all along
  moved: "moved",
  vacant: "vacant",
  gated: "gate-locked",
  court_closed: "court-closed",
};
/* Note rule per outcome. "note" = a written note is required. "note_or_chip" = a note
   OR at least one indicator chip (the chip IS the explanation). Anything absent here
   keeps the existing optional-notes behaviour (gated is self-explanatory; served /
   sub-served / refused have their own sworn detail gates). */
const SS_OUTCOME_NOTE_RULE = {
  moved: "note",          // who said so, and what they said
  court_closed: "note",   // which court, and why it was closed (holiday, furlough, early close)
  other: "note",          // "other" means nothing without the explanation
  bad_addr: "note_or_chip",
  vacant: "note_or_chip",
};
const SS_NOTE_MIN_CHARS = 3;
function ssOutcomeNoteMissing(outcome, notes, chips) {
  const rule = SS_OUTCOME_NOTE_RULE[outcome];
  if (!rule) return false;
  const hasNote = String(notes || "").trim().length >= SS_NOTE_MIN_CHARS;
  if (rule === "note") return !hasNote;
  const hasChip = Array.isArray(chips) && chips.some((c) => String(c || "").trim());
  return !hasNote && !hasChip;
}
function ssOutcomeNotePrompt(outcome) {
  switch (outcome) {
    case "moved":        return "Required — who told you they moved, and what they said (e.g. \"Current tenant says she moved out in August\")";
    case "court_closed": return "Required — which court, and why it was closed (holiday, furlough, closed early)";
    case "other":        return "Required — describe what happened";
    case "bad_addr":     return "Required unless an indicator is selected — what is wrong with the address";
    case "vacant":       return "Required unless a sign is selected — what shows the property is vacant";
    default:             return "Notes (optional)...";
  }
}
function ssAttemptIsSubstituted(a) {
  if (!a || typeof a !== "object") return false;
  const r = String(a.result || "").trim().toLowerCase();
  if (!r) return false;
  /* [attempt-badge-served-2026-10-20] served-substitute-mailing is no longer excluded. The backend
     ([attempt-result-correction-2026-10-15]) now derives it to 'substituted' (CCP 415.20(c): left
     with the person in charge at the mailing address, then mailed), so the follow-up mailing
     reminder must fire for it like any other substituted service. */
  if (String(a.serviceMannerAchieved || "").trim().toLowerCase() === "substituted") return true;
  if (SS_SUBSERVE_RESULTS.indexOf(r) !== -1) return true;
  if (r.indexOf("served-substitute") === 0) return true;
  const isSuccess = r === "served" || r === "sub-served" || r.indexOf("served-") === 0;
  return isSuccess && !!String(a.subName || "").trim();
}
/* A substituted attempt whose mailing has not been recorded. This is the whole
   reminder condition — it reads the same `mailDate` the backend preflight
   reads, so the app and the proof generator cannot disagree about it. */
function ssAttemptMailingPending(a) {
  return ssAttemptIsSubstituted(a) && !String((a && a.mailDate) || "").trim();
}
/* Job-level: the first substituted attempt still owing a mailing, or null. */
function ssJobMailingDue(job) {
  const rows = Array.isArray(job && job._raw && job._raw.attempts)
    ? job._raw.attempts
    : (Array.isArray(job && job.rawAttempts) ? job.rawAttempts : []);
  for (const a of rows) if (ssAttemptMailingPending(a)) return a;
  return null;
}
/* Default mailing origin. Only ever a DEFAULT — the statute asks where the copy
   was mailed FROM, and a server may drop it at a post office in another city,
   so both fields stay editable. Order: the city the copies were left in
   (job.city is empty on virtually every row — see jobDisplay — so the address
   string is parsed too), then the server's own city from their profile. */
export function ssMailOriginDefault(job, user) {
  let city = String((job && job.city) || "").trim();
  let state = "";
  const parts = String((job && job.address) || "").split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const m = /^([A-Za-z]{2})\b/.exec(parts[parts.length - 1]);
    if (m) {
      state = m[1];
      if (!city) city = parts[parts.length - 2];
    } else if (!city) {
      city = parts[parts.length - 1];
    }
  }
  if (!city) city = String((user && (user.home_city || user.city)) || "").trim();
  if (!state) state = String((user && (user.home_state || user.state)) || "").trim();
  if (!/^[A-Za-z]{2}$/.test(state)) state = "CA";
  return { city: city, state: state.toUpperCase() };
}
// Local calendar date as YYYY-MM-DD, for <input type="date"> and comparisons.
export function ssTodayLocalISO() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
/* POS-010 5b(4) prints served.mailDate VERBATIM (server.js ~14648) — it is
   never re-formatted on the way to the PDF — so the wire value must already be
   in the form a court reads. */
export function ssIsoToUsDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || "").trim());
  return m ? `${m[2]}/${m[3]}/${m[1]}` : "";
}
// Service is deemed complete on the 10th day after the mailing (CCP §415.20(b)).
function ssMailingCompleteOn(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || "").trim());
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setDate(d.getDate() + 10);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/* One flag line for the in-motion view. Safety first, then access, then rush —
   only ever one line, because the in-motion budget is <=30 characters and
   <=2 lines total. */
function ssFcFlagLine(job) {
  if (!job) return "";
  const rows = Array.isArray(job._raw && job._raw.attempts)
    ? job._raw.attempts
    : (Array.isArray(job.rawAttempts) ? job.rawAttempts : []);
  const priorUnsafe = rows.some((a) => /unsafe|weapon|aggress|threat/i.test(
    String((a && a.notes) || "") + " " + String((a && a.result) || "")
  ));
  if (priorUnsafe) return "Unsafe prior attempt";
  const instr = String(job.instructions || "");
  if (/weapon|aggressive|threat|hostile/i.test(instr)) return "Unsafe — see instructions";
  if (/\bdogs?\b|k-?9|canine|pit ?bull/i.test(instr)) return "Dog on property";
  if (/gate ?code|access code|call ?box|keypad|gate #/i.test(instr)) return "Gate code in instructions";
  if (ssIsPriorityJob(job)) return ssPriorityLabel(job.priority).toUpperCase(); // [field-list-priority-2026-10-08]
  return "";
}

function ssFcTruncate(s, n) {
  const t = String(s == null ? "" : s).trim();
  if (t.length <= n) return t;
  return t.slice(0, Math.max(0, n - 1)).trimEnd() + "…";
}

/* ── Route cache + Directions ──────────────────────────────────────────
   MapKit tiles are network-only, but the step list and the ETA do not have to
   be. Cache each leg so a dead zone shows a "last known route" instead of an
   empty map. Points are downsampled before caching — a city leg can carry
   thousands of coordinates and localStorage is a few megabytes. */
const SS_FC_ROUTE_TTL_MS = 30 * 60 * 1000;
const SS_FC_ROUTE_MAX_POINTS = 400;

function _ssFcRouteKey(from, to) {
  return "ss_fc_route_"
    + Number(from.lat).toFixed(4) + "," + Number(from.lng).toFixed(4)
    + "_" + Number(to.lat).toFixed(4) + "," + Number(to.lng).toFixed(4);
}

function ssReadCachedRoute(from, to) {
  if (!from || !to) return null;
  try {
    const raw = localStorage.getItem(_ssFcRouteKey(from, to));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.points) || parsed.points.length < 2) return null;
    parsed.stale = !(parsed.ts && (Date.now() - parsed.ts) < SS_FC_ROUTE_TTL_MS);
    return parsed;
  } catch (_) { return null; }
}

function ssWriteCachedRoute(from, to, data) {
  if (!from || !to || !data || !Array.isArray(data.points)) return;
  try {
    let pts = data.points;
    if (pts.length > SS_FC_ROUTE_MAX_POINTS) {
      const step = Math.ceil(pts.length / SS_FC_ROUTE_MAX_POINTS);
      const thinned = [];
      for (let i = 0; i < pts.length; i += step) thinned.push(pts[i]);
      if (thinned[thinned.length - 1] !== pts[pts.length - 1]) thinned.push(pts[pts.length - 1]);
      pts = thinned;
    }
    localStorage.setItem(_ssFcRouteKey(from, to), JSON.stringify({
      points: pts, seconds: data.seconds || null, meters: data.meters || null,
      steps: Array.isArray(data.steps) ? data.steps.slice(0, 40) : [], ts: Date.now(),
    }));
  } catch (_) { /* quota or private mode — the route still works, it just won't survive a reload */ }
}

/* Resolves to a route object, or null. NEVER throws and never rejects: every
   caller treats null as "no route available" and keeps whatever it had. */
function ssRequestDirections(from, to) {
  return new Promise((resolve) => {
    try {
      if (typeof mapkit === "undefined" || !mapkit.Directions) { resolve(null); return; }
      if (!from || !to || from.lat == null || to.lat == null) { resolve(null); return; }
      let settled = false;
      const done = (v) => { if (!settled) { settled = true; resolve(v); } };
      // MapKit gives no timeout of its own; a request that never calls back would
      // otherwise hang the banner on "routing…" forever.
      setTimeout(() => done(null), 12000);
      const opts = {
        origin: new mapkit.Coordinate(Number(from.lat), Number(from.lng)),
        destination: new mapkit.Coordinate(Number(to.lat), Number(to.lng)),
      };
      try {
        if (mapkit.Directions.Transport && mapkit.Directions.Transport.Automobile) {
          opts.transportType = mapkit.Directions.Transport.Automobile;
        }
      } catch (_) { /* older SDK — server default is driving */ }
      const directions = new mapkit.Directions();
      directions.route(opts, (err, data) => {
        if (err || !data || !Array.isArray(data.routes) || !data.routes.length) { done(null); return; }
        const r = data.routes[0];
        const points = [];
        try {
          const raw = (r.polyline && r.polyline.points) || [];
          for (const c of raw) {
            if (c && c.latitude != null && c.longitude != null) points.push({ lat: c.latitude, lng: c.longitude });
          }
        } catch (_) { /* no geometry — ETA and steps are still worth returning */ }
        const steps = [];
        try {
          const rawSteps = Array.isArray(r.steps) ? r.steps : [];
          for (const s of rawSteps.slice(0, 40)) {
            steps.push({
              text: String((s && s.instructions) || "").replace(/<[^>]*>/g, "").trim(),
              meters: Number(s && s.distance) || 0,
            });
          }
        } catch (_) { /* steps are optional */ }
        done({
          points,
          seconds: isFinite(Number(r.expectedTravelTime)) ? Number(r.expectedTravelTime) : null,
          meters: isFinite(Number(r.distance)) ? Number(r.distance) : null,
          steps,
          ts: Date.now(),
          stale: false,
        });
      });
    } catch (e) {
      console.warn("[field-command] directions threw:", (e && e.message) || e);
      resolve(null);
    }
  });
}

/* Cache-then-network. Returns the cached leg immediately if it is still warm,
   otherwise asks MapKit and falls back to a stale cache entry. */
async function ssGetRoute(from, to) {
  const cached = ssReadCachedRoute(from, to);
  if (cached && !cached.stale) return cached;
  const fresh = await ssRequestDirections(from, to);
  if (fresh && fresh.points.length >= 2) {
    ssWriteCachedRoute(from, to, fresh);
    return fresh;
  }
  return cached || null;
}

async function ssFcResolveDest(job) {
  if (!job) return null;
  if (job.lat != null && job.lng != null && isFinite(Number(job.lat)) && isFinite(Number(job.lng))) {
    return { lat: Number(job.lat), lng: Number(job.lng) };
  }
  // No stored coordinates on the row — fall back to the same geocode path and the
  // same cache key MapView uses, so the two panes never disagree about a pin.
  const queryParts = [job.address, job.city, job.zip];
  if (!queryParts.filter(Boolean).length) return null;
  const cacheKey = ("ss_geo_f_" + queryParts.filter(Boolean).join("|")).toLowerCase();
  try {
    const c = localStorage.getItem(cacheKey);
    if (c) { const p = JSON.parse(c); if (p && p.lat != null) return p; }
  } catch (_) { /* fall through to the network */ }
  try {
    const r = await api.get("/api/geocode?q=" + encodeURIComponent(queryParts.filter(Boolean).join(", ")));
    if (Array.isArray(r) && r.length && r[0].lat) {
      const c = { lat: parseFloat(r[0].lat), lng: parseFloat(r[0].lon) };
      try { localStorage.setItem(cacheKey, JSON.stringify(c)); } catch (_) {}
      return c;
    }
  } catch (_) { /* offline — the dash still renders, just without a map pin */ }
  return null;
}

function ssFcEta(seconds) {
  if (seconds == null || !isFinite(seconds)) return "—";
  const m = Math.max(1, Math.round(Number(seconds) / 60));
  if (m < 60) return m + " min";
  return Math.floor(m / 60) + "h " + (m % 60) + "m";
}

function ssFcMiles(meters) {
  if (meters == null || !isFinite(meters)) return "—";
  const mi = Number(meters) / 1609.344;
  return (mi < 10 ? mi.toFixed(1) : Math.round(mi)) + " mi";
}

/* Spoken, never shown. The complement is the rule that matters: the servee's
   name, the case number, the address and the physical description are NEVER
   passed to this function — cars carry passengers. */
function ssFcSpeak(text) {
  try {
    if (typeof window === "undefined" || !window.speechSynthesis || !window.SpeechSynthesisUtterance) return;
    if (localStorage.getItem("ss_fc_voice") === "0") return;
    const u = new window.SpeechSynthesisUtterance(String(text || ""));
    u.rate = 1;
    window.speechSynthesis.speak(u);
  } catch (_) { /* no speech engine — the on-screen banner already said it */ }
}

/* ── Day / night ──────────────────────────────────────────────────────
   Built from the app's own C_LIGHT / C_DARK with the spec's adjustments:
   the pale tint fills (#e8f5ee, #fef3e0) wash out in direct sun, so status is
   carried by a 3px keyline in the full-strength hue instead. Night shifts gold
   to #d9bb63 and drops map brightness to 70%; no full-area white anywhere. */
function ssFcPalette(night) {
  const base = night ? C_DARK : C_LIGHT;
  return {
    night: !!night,
    // [live-route-2026-09-20] bg/card/ink were literals that re-typed the exact
    // C_LIGHT / C_DARK values. Derived now so they cannot drift out of sync with
    // the canonical palette. Pure refactor — the resolved colors are unchanged.
    bg: base.bg,
    card: base.white,
    ink: base.ink,
    dim: base.steel,
    // DELIBERATE, do not "fix" to base.steelL: a warm bone keyline reads correctly
    // against the bone bg in direct sun; the cool grey #c8cad0 does not.
    line: night ? base.steelL : "#ded9cf",
    // NOTE [live-route-2026-09-20]: night gold is #d9bb63 here but C_DARK.gold is
    // #d9bc63 — one character apart. Left as-is pending owner confirmation of
    // which is intended; do not silently reconcile.
    gold: night ? "#d9bb63" : "#c9a84c",
    ok: base.ok,
    warn: base.warn,
    err: base.err,
    info: base.info,
    royal: base.royal,
    onDark: "#ffffff",
    mapFilter: night ? "brightness(0.7)" : "none",
  };
}

function _ssFcClockIsNight() {
  const h = new Date().getHours();
  return h >= 19 || h < 6;
}

/* Auto-switch on sunset and ambient light. The clock band is the sunset proxy
   (no location-to-sunset table ships in this bundle); an AmbientLightSensor
   reading wins over it where the platform exposes one, which iOS Safari does
   not. The user's explicit app-wide dark toggle always forces night. */
function useFcNight() {
  const ctx = React.useContext(AppCtx);
  const dark = !!(ctx && ctx.dark);
  const [auto] = useState(() => {
    try { return localStorage.getItem("ss_fc_auto_night") !== "0"; } catch (_) { return true; }
  });
  const [clockNight, setClockNight] = useState(() => _ssFcClockIsNight());
  const [lightNight, setLightNight] = useState(null);

  useEffect(() => {
    const id = setInterval(() => setClockNight(_ssFcClockIsNight()), 60000);
    let mq = null;
    let onChange = null;
    try {
      mq = window.matchMedia("(prefers-color-scheme: dark)");
      if (mq.matches) setClockNight(true);
      onChange = (e) => { if (e && e.matches) setClockNight(true); else setClockNight(_ssFcClockIsNight()); };
      if (mq.addEventListener) mq.addEventListener("change", onChange);
      else if (mq.addListener) mq.addListener(onChange);
    } catch (_) { /* no matchMedia — the clock band stands alone */ }
    let sensor = null;
    try {
      if (typeof window.AmbientLightSensor === "function") {
        sensor = new window.AmbientLightSensor({ frequency: 0.2 });
        sensor.addEventListener("reading", () => {
          try { if (sensor.illuminance != null) setLightNight(Number(sensor.illuminance) < 12); } catch (_) {}
        });
        sensor.addEventListener("error", () => { setLightNight(null); });
        sensor.start();
      }
    } catch (_) { /* not exposed / permission refused — clock band only */ }
    return () => {
      clearInterval(id);
      try {
        if (mq && onChange) {
          if (mq.removeEventListener) mq.removeEventListener("change", onChange);
          else if (mq.removeListener) mq.removeListener(onChange);
        }
      } catch (_) {}
      try { if (sensor) sensor.stop(); } catch (_) {}
    };
  }, []);

  const env = (lightNight != null) ? lightNight : clockNight;
  return { night: dark || (auto ? env : false) };
}

function useFcPortrait() {
  const [portrait, setPortrait] = useState(() => {
    try { return window.innerHeight > window.innerWidth; } catch (_) { return false; }
  });
  useEffect(() => {
    const on = () => { try { setPortrait(window.innerHeight > window.innerWidth); } catch (_) {} };
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => {
      window.removeEventListener("resize", on);
      window.removeEventListener("orientationchange", on);
    };
  }, []);
  return portrait;
}

/* An iPad in a windshield mount sleeps mid-route. Best-effort only: no browser
   that lacks the API is any worse off than it is today. */
function useFcWakeLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    let lock = null;
    let released = false;
    const acquire = async () => {
      try {
        if (!navigator.wakeLock || typeof navigator.wakeLock.request !== "function") return;
        lock = await navigator.wakeLock.request("screen");
      } catch (_) { /* denied, or not in a secure context */ }
    };
    acquire();
    const onVisible = () => { if (document.visibilityState === "visible" && !released) acquire(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisible);
      try { if (lock) lock.release(); } catch (_) {}
    };
  }, [active]);
}

/* ── Action target ────────────────────────────────────────────────────
   150x88, fixed geometry, never mirrored — blind operation depends on the
   buttons never moving. Status is a 3px keyline in full-strength hue, never a
   pale tint fill. */
function FcTarget({ label, sub, tone, Icon, onPress, disabled, busy, P, solid, width, height }) {
  const w = width || 150;
  const h = height || 88;
  return (
    <button
      type="button"
      onClick={disabled || busy ? undefined : onPress}
      disabled={!!disabled || !!busy}
      aria-label={label}
      style={{
        width: w, height: h, flex: "0 0 auto",
        borderRadius: 16,
        // Status is carried by a 3px keyline in the full-strength hue, never a
        // pale tint fill — the tint fills wash out in direct sun.
        border: "3px solid " + tone,
        background: solid ? tone : P.card,
        color: solid ? P.onDark : tone,
        opacity: disabled ? 0.42 : 1,
        cursor: disabled || busy ? "not-allowed" : "pointer",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 4,
        fontFamily: "inherit",
        padding: "6px 8px",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {Icon ? <Icon size={24} strokeWidth={2.4} /> : null}
      <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.05, textAlign: "center" }}>
        {busy ? "Saving…" : label}
      </span>
      {sub ? (
        <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.04em", opacity: 0.72 }}>{sub}</span>
      ) : null}
    </button>
  );
}

/* ── Map pane ─────────────────────────────────────────────────────────
   Its own mapkit.Map instance — MapView is not mounted on this tab. Degrades
   to a plain address card when MapKit never loads (no token, no network, old
   SDK): the dash must stay usable without a map. */
function CommandMap({ dest, routePoints, night, P }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const annRef = useRef(null);
  const lineRef = useRef(null);
  const [failed, setFailed] = useState(false);
  // MapKit constructs asynchronously. The pin / route effects below run once on
  // mount with no map to draw on, so they have to be re-triggered once it
  // exists — otherwise a destination resolved before construction never pins.
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!elRef.current || mapRef.current) return undefined;
    let cancelled = false;
    const ready = (typeof window !== "undefined" && window.__mapkitReady && typeof window.__mapkitReady.then === "function")
      ? window.__mapkitReady : null;
    if (!ready) {
      // MapKit JS never initialised on this page. Re-check once in case the CDN
      // is still loading, then give up quietly and show the fallback card.
      const id = setTimeout(() => { if (!cancelled && !mapRef.current) setFailed(true); }, 2500);
      return () => { cancelled = true; clearTimeout(id); };
    }
    ready.then(() => {
      if (cancelled || !elRef.current || mapRef.current) return;
      if (typeof mapkit === "undefined") { setFailed(true); return; }
      try {
        mapRef.current = new mapkit.Map(elRef.current, {
          showsUserLocation: true,
          tracksUserLocation: false,
          showsCompass: mapkit.FeatureVisibility.Hidden,
          showsZoomControl: false,
          showsScale: mapkit.FeatureVisibility.Hidden,
          colorScheme: night ? mapkit.Map.ColorSchemes.Dark : mapkit.Map.ColorSchemes.Light,
        });
        setMapReady(true);
      } catch (e) {
        // Never throw out of here — a synchronous mapkit.Map throw is the known
        // iPad blank-screen cause this whole shell was defaulted off for.
        console.warn("[field-command] map construction threw:", (e && e.message) || e);
        setFailed(true);
      }
    }).catch(() => setFailed(true));
    return () => {
      cancelled = true;
      if (mapRef.current) { try { mapRef.current.destroy(); } catch (_) {} mapRef.current = null; }
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof mapkit === "undefined") return;
    try { map.colorScheme = night ? mapkit.Map.ColorSchemes.Dark : mapkit.Map.ColorSchemes.Light; } catch (_) {}
  }, [night]);

  // Destination pin + region.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof mapkit === "undefined") return;
    if (annRef.current) { try { map.removeAnnotation(annRef.current); } catch (_) {} annRef.current = null; }
    if (!dest || dest.lat == null) return;
    try {
      const coord = new mapkit.Coordinate(Number(dest.lat), Number(dest.lng));
      const ann = new mapkit.Annotation(coord, () => {
        const el = document.createElement("div");
        el.style.cssText = "width:26px;height:26px;border-radius:50%;background:#c0392b;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.45)";
        return el;
      }, { anchorOffset: new DOMPoint(0, -13), displayPriority: 1000 });
      map.addAnnotation(ann);
      annRef.current = ann;
      map.region = new mapkit.CoordinateRegion(coord, new mapkit.CoordinateSpan(0.05, 0.05));
    } catch (e) { console.warn("[field-command] dest pin failed:", (e && e.message) || e); }
  }, [dest && dest.lat, dest && dest.lng, mapReady]);

  // Route geometry.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof mapkit === "undefined") return undefined;
    if (lineRef.current) { try { map.removeOverlay(lineRef.current); } catch (_) {} lineRef.current = null; }
    if (!Array.isArray(routePoints) || routePoints.length < 2) return undefined;
    try {
      const coords = routePoints
        .filter((p) => p && p.lat != null && p.lng != null)
        .map((p) => new mapkit.Coordinate(Number(p.lat), Number(p.lng)));
      if (coords.length < 2) return undefined;
      const line = new mapkit.PolylineOverlay(coords, {
        style: new mapkit.Style({ strokeColor: "#c9a84c", lineWidth: 6, lineJoin: "round", lineCap: "round" }),
      });
      map.addOverlay(line);
      lineRef.current = line;
      try { map.showItems([line], { animate: true, padding: { top: 110, right: 40, bottom: 40, left: 40 } }); } catch (_) {}
    } catch (e) { console.warn("[field-command] route overlay failed:", (e && e.message) || e); }
    return () => {
      if (lineRef.current && mapRef.current) {
        try { mapRef.current.removeOverlay(lineRef.current); } catch (_) {}
        lineRef.current = null;
      }
    };
  }, [routePoints, mapReady]);

  return (
    <div style={{ position: "absolute", inset: 0, background: P.card, overflow: "hidden" }}>
      <div ref={elRef} style={{ position: "absolute", inset: 0, filter: P.mapFilter }} />
      {failed ? (
        <div style={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 10, padding: 28, textAlign: "center",
        }}>
          <MapPin size={40} color={P.dim} />
          <div style={{ fontSize: 19, fontWeight: 600, color: P.ink }}>Map unavailable</div>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.1em", color: P.dim }}>
            NO NETWORK OR NO MAPKIT TOKEN
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ── Phone panel ──────────────────────────────────────────────────────
   Every call goes through POST /api/phone/call. That endpoint already
   authenticates field-server roles, plays the Penal Code 632 two-party
   disclosure and writes audit_log + call_compliance_log; the backend change
   landing alongside this rings the server's own stored phone as leg A and
   selects caller ID per tenant, so the servee never sees the server's cell.
   A raw tel: link is REJECTED for these calls — it exposes the personal
   number, skips the 632 disclosure and writes no call log. tel: stays only
   for dispatch and 911, which are elsewhere in the app. */
/* [server-parity-web-2026-10-05] ── MASKED CLICK-TO-CALL ────────────────────
   POST /api/server/jobs/:id/call { target: 'subject' | 'attorney' }. Twilio rings
   the server's own phone from the tenant's business line, then bridges to the
   target with that same line as caller ID — the subject/attorney never sees the
   server's number, and the server never sees theirs. The number comes from the
   job row on the backend, never from this client, so no tel: link is needed and
   none is rendered. The client is not a permitted target: servers reach the
   client through the job Chat, never by phone. */
async function ssMaskedCall(job, target) {
  if (!job || !job.id) throw new Error("No job selected");
  const r = await api.post(`/api/server/jobs/${encodeURIComponent(job.id)}/call`, { target });
  if (!r || (r.success === false && !r.callSid)) throw new Error((r && r.error) || "The call could not be placed.");
  return r;
}
function ssMaskedCallErr(e) {
  const code = e && (e.code || (e.body && e.body.code));
  if (code === "NO_SERVER_PHONE") return "Add a mobile number to your profile before placing calls.";
  if (code === "NO_TARGET_PHONE") return "There is no phone number on this job for that person.";
  if (code === "SELF_CALL") return "That number is your own.";
  if (e && e.status === 429) return "Too many call requests — wait a minute and try again.";
  if (e && e.status === 503) return "Calling is not set up for this account yet.";
  return (e && e.message) || "The call could not be placed.";
}
function MaskedCallButton({ job, target, hasNumber, style, compact }) {
  const [st, setSt] = useState("idle"); // idle | connecting | ringing | failed
  const [msg, setMsg] = useState("");
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);
  async function go() {
    if (st === "connecting") return;
    setSt("connecting"); setMsg("");
    try {
      await ssMaskedCall(job, target);
      if (!aliveRef.current) return;
      setSt("ringing"); setMsg("Your phone will ring — answer it to be connected.");
      setTimeout(() => { if (aliveRef.current) { setSt("idle"); setMsg(""); } }, 12000);
    } catch (e) {
      if (!aliveRef.current) return;
      const m = ssMaskedCallErr(e);
      setSt("failed"); setMsg(m); ssToast("err", m);
      setTimeout(() => { if (aliveRef.current) { setSt("idle"); setMsg(""); } }, 6000);
    }
  }
  const disabled = hasNumber === false || st === "connecting";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
      <button type="button" onClick={go} disabled={disabled}
        aria-label={`Call ${target} (masked)`}
        style={{
          display: "flex", alignItems: "center", gap: 8, minHeight: 44, padding: compact ? "0 12px" : "0 16px",
          borderRadius: 10, border: `1.5px solid ${disabled ? C.steelL : C.royal}`,
          background: st === "ringing" ? C.okL : C.white, color: disabled ? C.steel : C.royal,
          fontSize: compact ? 13 : 15, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit",
          ...(style || {}),
        }}>
        {st === "connecting" ? <SpinnerDot color={C.royal} /> : <Phone size={18} color={disabled ? C.steel : C.royal} />}
        {st === "connecting" ? "Connecting…" : st === "ringing" ? "Ringing you…" : "Call (masked)"}
      </button>
      {msg ? <div style={{ fontSize: 11, color: st === "failed" ? C.err : C.ok, fontWeight: 600, textAlign: "right", maxWidth: 220 }}>{msg}</div> : null}
    </div>
  );
}

function CommandPhonePanel({ job, P, onClose }) {
  const [num, setNum] = useState("");
  const [state, setState] = useState({ status: "idle", who: "", err: "" });
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  /* [server-parity-web-2026-10-05] Subject and attorney go through the masked
     bridge (POST /api/server/jobs/:id/call) — numbers never leave the backend, so
     the buttons show WHO, not a phone number. The client target is gone: servers
     are not permitted to call clients; the job Chat is the channel. */
  const targets = [
    { id: "subject", label: "Call Subject", has: !!(job && job.recipientPhone), who: "the subject" },
    { id: "attorney", label: "Call Attorney", has: !!(job && job.attorneyPhone), who: "the attorney" },
  ];

  async function placeMaskedCall(target, who) {
    setState({ status: "connecting", who: who, err: "" });
    try {
      await ssMaskedCall(job, target);
      if (!aliveRef.current) return;
      setState({ status: "ringing", who: who, err: "" });
    } catch (e) {
      if (!aliveRef.current) return;
      setState({ status: "failed", who: who, err: ssMaskedCallErr(e) });
    }
  }

  async function placeCall(raw, who) {
    const to = String(raw || "").replace(/[^0-9+*#]/g, "");
    if (!to) return;
    setState({ status: "connecting", who: who, err: "" });
    try {
      await api.post("/api/phone/call", { to: to, jobId: job ? job.id : null });
      if (!aliveRef.current) return;
      setState({ status: "ringing", who: who, err: "" });
    } catch (e) {
      if (!aliveRef.current) return;
      setState({ status: "failed", who: who, err: (e && e.message) || "The call could not be placed." });
    }
  }

  const banner =
    state.status === "connecting" ? { text: "Connecting — your phone will ring.", tone: P.info }
    : state.status === "ringing" ? { text: "Your phone is ringing. Answer it and you will be bridged to " + state.who + ".", tone: P.ok }
    : state.status === "failed" ? { text: state.err || "The call could not be placed.", tone: P.err }
    : null;

  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 400,
      background: P.night ? "rgba(8,12,20,0.82)" : "rgba(10,22,40,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div style={{
        width: 420, maxWidth: "100%", maxHeight: "100%", overflowY: "auto",
        background: P.card, borderRadius: 20, border: "3px solid " + P.line, padding: 22,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.1em", color: P.dim }}>PHONE</div>
          <button type="button" onClick={onClose} aria-label="Close phone panel" style={{
            width: 44, height: 44, borderRadius: 12, background: "transparent",
            border: "3px solid " + P.line, color: P.ink, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <X size={22} />
          </button>
        </div>

        {banner ? (
          <div style={{
            border: "3px solid " + banner.tone, color: banner.tone, borderRadius: 14,
            padding: "12px 14px", marginBottom: 14, fontSize: 19, fontWeight: 600, lineHeight: 1.35,
          }}>{banner.text}</div>
        ) : null}

        {targets.map((t) => (
          <button
            key={t.id}
            type="button"
            disabled={!t.has || state.status === "connecting"}
            onClick={() => placeMaskedCall(t.id, t.who)}
            style={{
              width: "100%", minHeight: 64, marginBottom: 10, borderRadius: 14,
              border: "3px solid " + (t.has ? P.ok : P.line),
              background: P.card, color: t.has ? P.ink : P.dim,
              opacity: t.has ? 1 : 0.55,
              cursor: t.has ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
              fontFamily: "inherit", textAlign: "left",
            }}
          >
            <Phone size={22} color={t.has ? P.ok : P.dim} />
            <span style={{ flex: 1 }}>
              <span style={{ display: "block", fontSize: 22, fontWeight: 700 }}>{t.label}</span>
              <span style={{ display: "block", fontSize: 15, fontWeight: 700, letterSpacing: "0.04em", color: P.dim }}>
                {t.has ? "Masked — your number stays private" : "No number on file"}
              </span>
            </span>
          </button>
        ))}

        {/* Manual keypad — same markup pattern as cle-frontend/softphone.jsx:360-367 */}
        <div style={{
          background: P.card, border: "3px solid " + P.line, borderRadius: 14,
          padding: "10px 14px", margin: "14px 0 10px", minHeight: 52,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <input
            value={num}
            onChange={(e) => setNum(e.target.value.replace(/[^0-9+*#]/g, ""))}
            placeholder="Enter number"
            inputMode="tel"
            style={{
              flex: 1, border: "none", outline: "none", background: "transparent",
              fontFamily: "Menlo, monospace", fontSize: 22, color: P.ink, minWidth: 0,
            }}
          />
          {num ? (
            <button type="button" onClick={() => setNum(num.slice(0, -1))} aria-label="Backspace" style={{
              background: "none", border: "none", cursor: "pointer", fontSize: 22, color: P.dim, padding: "4px 8px",
            }}>{"⌫"}</button>
          ) : null}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}>
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((d) => (
            <button key={d} type="button" onClick={() => setNum((n) => n + d)} style={{
              aspectRatio: "1.6", background: P.card, border: "3px solid " + P.line,
              borderRadius: 12, fontFamily: "Menlo, monospace", fontSize: 22,
              color: P.ink, cursor: "pointer", fontWeight: 600,
            }}>{d}</button>
          ))}
        </div>
        <button
          type="button"
          disabled={!num}
          onClick={() => placeCall(num, "that number")}
          style={{
            width: "100%", padding: 16, borderRadius: 14, border: "3px solid " + (num ? P.ok : P.line),
            background: num ? P.ok : "transparent", color: num ? P.onDark : P.dim,
            fontSize: 22, fontWeight: 700, cursor: num ? "pointer" : "not-allowed", fontFamily: "inherit",
          }}
        >
          Call
        </button>
        <div style={{ fontSize: 15, fontWeight: 600, color: P.dim, marginTop: 12, lineHeight: 1.45 }}>
          Calls are placed by StrikeServe, not from your handset. Your phone rings first, the
          two-party recording disclosure plays, and the call is logged.
        </div>
      </div>
    </div>
  );
}

/* ── Subject thumbnail ────────────────────────────────────────────────
   [fc-crash-fix-2026-09-11] The dossier used to do `<img src={job.subjectPhoto}>`,
   which only works if that field is a URL string. It never is: jobs.subject_photo is
   JSONB ({name,size,type,key,s3Key,fileUrl,url,s3}) and the bucket is private, so the
   key has to be presigned exactly the way FieldSubjectPhoto does it on the phone
   shell. Same key precedence (subjectPhotoKey), same presign endpoint, same silent
   fall back to the placeholder — no broken-image glyph, and nothing thrown. */
function CommandSubjectThumb({ job, P }) {
  const key = subjectPhotoKey(job);
  const [url, setUrl] = useState("");
  useEffect(() => {
    let cancelled = false;
    setUrl("");
    if (!key) return undefined;
    presignAttemptPhoto(key)
      .then((u) => { if (!cancelled) setUrl(String(u || "")); })
      .catch(() => { /* offline or expired key — the placeholder is the honest state */ });
    return () => { cancelled = true; };
  }, [key]);
  if (!url) return <User size={44} color={P.dim} />;
  return (
    <img
      src={url}
      alt=""
      onError={() => setUrl("")}
      style={{ width: "100%", height: "100%", objectFit: "cover" }}
    />
  );
}

/* ── The screen ───────────────────────────────────────────────────────── */
function CommandView({ setTab, setSelectedJob }) {
  const { jobs, reload } = React.useContext(AppCtx);
  const { night } = useFcNight();
  const P = ssFcPalette(night);
  const portrait = useFcPortrait();
  useFcWakeLock(true);

  // Only claimed work belongs on the dash — an unclaimed job has no served
  // address to drive to and no right to show a face.
  /* [route-order-2026-09-21] Follow the planned route. Jobs not in the saved
     order sort to the end rather than being hidden — a stop claimed after the
     route was planned must still be reachable from the dash. */
  const [routeOrder, setRouteOrder] = useState(() => ssReadRouteOrder());
  useEffect(() => {
    const onOrder = () => setRouteOrder(ssReadRouteOrder());
    window.addEventListener("ss-route-order", onOrder);
    return () => window.removeEventListener("ss-route-order", onOrder);
  }, []);
  const stops = React.useMemo(() => {
    const active = (jobs || []).filter((j) => j && j.status === "active");
    if (!routeOrder.length) return active;
    const rank = new Map(routeOrder.map((id, i) => [String(id), i]));
    const at = (j) => (rank.has(String(j.id)) ? rank.get(String(j.id)) : 1e9);
    return active.slice().sort((a, b) => at(a) - at(b));
  }, [jobs, routeOrder]);
  const [currentId, setCurrentId] = useState(() => {
    try { return localStorage.getItem("ss_fc_stop") || ""; } catch (_) { return ""; }
  });
  const job = React.useMemo(
    () => stops.find((j) => String(j.id) === String(currentId)) || stops[0] || null,
    [stops, currentId]
  );
  const jobId = job ? String(job.id) : "";
  useEffect(() => {
    if (!jobId) return;
    try { localStorage.setItem("ss_fc_stop", jobId); } catch (_) {}
  }, [jobId]);
  const courtSt = useCourtStatus(job); // [court-closed-warning-2026-10-08]

  const { moving, mph, declareParked } = useMotionLockout(true);

  // ── Held GPS fix ────────────────────────────────────────────────────
  // A continuous watch, so a one-tap outcome always has coordinates ALREADY in
  // hand. Nothing is ever invented: no fix, no one-tap save.
  const [fix, setFix] = useState(null);
  const [gpsErr, setGpsErr] = useState("");
  useEffect(() => {
    const geo = (typeof navigator !== "undefined" && navigator.geolocation) || null;
    if (!geo || typeof geo.watchPosition !== "function") {
      setGpsErr("This device cannot report a position. One-tap outcomes are unavailable.");
      return undefined;
    }
    let id = null;
    const onPos = (p) => {
      const lat = Number(p && p.coords && p.coords.latitude);
      const lng = Number(p && p.coords && p.coords.longitude);
      if (!isFinite(lat) || !isFinite(lng)) return;
      const sample = {
        lat: lat, lng: lng,
        accuracy: Number((p.coords && p.coords.accuracy) || 0),
        speed: (p.coords && p.coords.speed != null) ? Number(p.coords.speed) : null,
        heading: (p.coords && p.coords.heading != null) ? Number(p.coords.heading) : null,
        ts: Number(p.timestamp || Date.now()),
      };
      setFix(sample);
      setGpsErr("");
      ssPublishMotion(sample);
    };
    const onErr = (e) => {
      const code = e && e.code;
      setGpsErr(code === 1
        ? "Location permission denied — one-tap outcomes are disabled."
        : "No GPS fix yet — one-tap outcomes are disabled until there is one.");
    };
    try {
      id = geo.watchPosition(onPos, onErr, { enableHighAccuracy: true, maximumAge: 2000, timeout: 20000 });
    } catch (_) {
      setGpsErr("Location is unavailable on this device.");
    }
    return () => { try { if (id != null) geo.clearWatch(id); } catch (_) {} };
  }, []);
  const gpsOk = !!(fix && typeof fix.lat === "number" && typeof fix.lng === "number");

  /* [arrival-geofence-2026-09-21] Switch to the stop you pulled up at, so the
     dash is already on the right job before you get out of the vehicle.

     Three guards, each load-bearing:
       * `moving` — never re-page under someone who is still driving. Passing
         within a block of stop 5 on the way to stop 2 must not hijack the dash.
       * `arrivedRef` — fires ONCE per arrival. After it switches, manual paging
         sticks; otherwise an operator reading ahead to the next stop while
         parked would be yanked back every GPS tick.
       * the ref clears on leaving the radius, so returning to a stop later in
         the day re-arms it.
     A stop with no coordinates simply never triggers — as of the 2026-09-20
     backfill that is still a large share of jobs. */
  const SS_FC_ARRIVE_MI = 0.12;   // ~630 ft: pulled up, not merely nearby
  const arrivedRef = useRef("");
  useEffect(() => {
    if (!gpsOk || moving) return;
    let bestId = "";
    let bestMi = Infinity;
    (stops || []).forEach((s) => {
      if (!s || s.lat == null || s.lng == null) return;
      const d = ssHaversineMiles(
        { lat: fix.lat, lng: fix.lng },
        { lat: Number(s.lat), lng: Number(s.lng) }
      );
      if (d < bestMi) { bestMi = d; bestId = String(s.id); }
    });
    if (!bestId || bestMi > SS_FC_ARRIVE_MI) { arrivedRef.current = ""; return; }
    if (bestId === String(currentId)) { arrivedRef.current = bestId; return; }
    if (arrivedRef.current === bestId) return;
    arrivedRef.current = bestId;
    setCurrentId(bestId);
    ssFcSpeak("Arrived");
  }, [gpsOk, moving, fix && fix.lat, fix && fix.lng, stops, currentId]);

  // ── Destination + route ─────────────────────────────────────────────
  const [dest, setDest] = useState(null);
  const [route, setRoute] = useState(null);
  const [routing, setRouting] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setDest(null);
    setRoute(null);
    if (!job) return undefined;
    ssFcResolveDest(job).then((d) => { if (!cancelled) setDest(d); });
    return () => { cancelled = true; };
  }, [jobId]);

  useEffect(() => {
    if (!dest || !gpsOk) return undefined;
    let cancelled = false;
    // Show any cached leg immediately so a dead zone keeps an ETA and a step
    // list on screen instead of an empty banner.
    const cached = ssReadCachedRoute({ lat: fix.lat, lng: fix.lng }, dest);
    if (cached && !cancelled) setRoute(cached);
    setRouting(true);
    ssGetRoute({ lat: fix.lat, lng: fix.lng }, dest).then((r) => {
      if (cancelled) return;
      setRouting(false);
      if (r) setRoute(r);
    });
    return () => { cancelled = true; };
    // Re-route on a new stop, not on every GPS tick — MapKit's quota is 25k
    // service calls a day and a 1Hz watch would burn it before lunch.
  }, [dest && dest.lat, dest && dest.lng, jobId, gpsOk]);

  // ── Evidence ────────────────────────────────────────────────────────
  // Photograph the documents, the door, the vehicle. NEVER the person served
  // (Civil Code 1708.8) and never any audio (Penal Code 632).
  const [photos, setPhotos] = useState([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInputRef = useRef(null);
  // [attempt-photo-library-2026-10-09] Camera-only made servers re-open the camera for every one of the
  // three required photos. The library input has no capture attribute, so iOS opens
  // the Photos picker with multi-select; it feeds the same onPhotoPicked pipeline.
  const libraryInputRef = useRef(null);
  useEffect(() => { setPhotos([]); }, [jobId]);

  const [banner, setBanner] = useState(null);
  const [saving, setSaving] = useState("");
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [sheetSeed, setSheetSeed] = useState(null);
  const sup = useJobSupport(job); // [support-tickets-2026-10-08] hooks run before the empty-state return

  async function onPhotoPicked(ev) {
    const files = Array.from((ev && ev.target && ev.target.files) || []);
    if (ev && ev.target) ev.target.value = "";
    if (!files.length || !job) return;
    setPhotoBusy(true);
    const failures = [];
    for (const f of files) {
      try {
        const _ssBytes = await ssFileToBytes(f); // [pos-overhaul-2026-10-07:server-app] read once; sniff HEIC/empty types
        const mime = (/pdf/i.test(String(f.type || "")) || /\.pdf$/i.test(String(f.name || ""))) ? "application/pdf" : ssImageMimeOf(f, _ssBytes);
        if (!/^image\//.test(mime)) { failures.push(f.name + ": not an image"); continue; }
        const ext = ssExtForMime(mime);
        const rec = await ssUploadEvidence(api, {
          bytes: _ssBytes,
          mime: mime,
          fileName: "attempt-" + job.id + "-" + Date.now() + "-" + Math.random().toString(36).slice(2, 7) + "." + ext,
          context: "attempt-photo",
          jobId: job.id,
        });
        setPhotos((prev) => prev.concat([rec]));
      } catch (e) { failures.push(f.name + ": " + ((e && e.message) || "upload failed")); }
    }
    setPhotoBusy(false);
    // A failed upload is reported and nothing more. It NEVER gates an outcome:
    // the attempt is the record that matters and it must not be lost because a
    // photo could not reach S3 from a bad cell.
    if (failures.length) {
      setBanner({ kind: "err", text: failures.length + " photo(s) failed to upload and were NOT attached. The outcome buttons still work." });
      ssFcSpeak("A photo did not upload");
    }
  }

  /* One tap records ONLY these three. served / sub_served / refused each swear
     delivery under penalty of perjury — they open the attempt sheet instead,
     and nothing on this dash can post them. */
  const ONE_TAP_RESULT = { not_home: "not-home", no_answer: "no-answer", unsafe: "other" };
  const ONE_TAP_LABEL = { not_home: "Not home", no_answer: "No answer", unsafe: "Unsafe" };

  async function saveOneTap(kind) {
    if (!job || !job.id || moving) return;
    if (!ONE_TAP_RESULT[kind]) return;
    if (!gpsOk) {
      setBanner({ kind: "err", text: gpsErr || "No GPS fix held. Every attempt must carry real coordinates." });
      return;
    }
    /* [attempt-gate-2026-10-08] One-tap outcomes are attempts too — same photo minimum. */
    if (ssUploadedPhotoCount(photos) < ssPhotosRequiredFor(kind)) {
      setBanner({ kind: "err", text: "Take at least " + SS_ATTEMPT_MIN_PHOTOS + " photos first (" + ssUploadedPhotoCount(photos) + " of " + SS_ATTEMPT_MIN_PHOTOS + "): front door, house number, wide shot of the property." });
      return;
    }
    setSaving(kind);
    setBanner(null);
    const evidence = (photos || []).filter((p) => p && p.key).map((p) => ({
      name: p.name || ("attempt-" + job.id + "-" + Date.now() + ".jpg"),
      ts: p.ts || new Date().toISOString(),
      size: p.size || 0,
      s3Key: p.key,
      fileUrl: p.fileUrl || null,
      s3: true,
    }));
    const payload = {
      result: ONE_TAP_RESULT[kind],
      notes: kind === "unsafe" ? "Unsafe conditions at the address; no contact attempted." : "",
      photoCount: evidence.length,
      photos: evidence,
      subName: null,
      subRelation: null,
      gps: { lat: fix.lat, lng: fix.lng, accuracy: fix.accuracy },
      // Same canonical serviceDetails shape the attempt sheet posts (§4 of
      // ATTEMPT_MODEL_CANONICAL_2026-07-21). A one-tap outcome is a no-contact
      // outcome, so every contact-made field is null/false by construction —
      // this dash never asserts anything it did not observe.
      serviceDetails: {
        identityVerifiedBy: null, recipientName: null, recipientIsAdult: false,
        informedOfNature: false, relationship: null, leftInPresence: false,
        mailingRequired: false, subjectSex: null, subjectRace: null,
        subjectAgeApprox: null, subjectHeight: null, subjectWeight: null,
        subjectHair: null, subjectGlasses: false, subjectFeatures: [],
        observations: [], vehiclePlate: null,
        unsafeReasons: kind === "unsafe" ? ["Unsafe conditions reported from the field"] : [],
        talkedTo: null, talkedToName: null,
      },
    };
    try {
      await api.post("/api/jobs/" + job.id + "/attempts", payload);
      setPhotos([]);
      ssFcSpeak("Attempt saved");
      setBanner({ kind: "ok", text: ONE_TAP_LABEL[kind] + " logged." });
      try { await reload(); } catch (_) {}
    } catch (e) {
      ssFcSpeak("Attempt did not save");
      setBanner({ kind: "err", text: (e && e.message) || "Could not save the attempt." });
    } finally {
      setSaving("");
    }
  }

  const dx = job ? jobDisplay(job) : null;
  const dd = ssDeriveWindows(job);
  const flag = ssFcFlagLine(job);
  const claimed = !!(job && job.status === "active");
  const appleMapsHref = dest
    ? "https://maps.apple.com/?daddr=" + dest.lat + "," + dest.lng + "&dirflg=d"
    : (job ? "https://maps.apple.com/?daddr=" + encodeURIComponent((dx && dx.fullAddress) || "") + "&dirflg=d" : "#");

  const lbl = { fontSize: 15, fontWeight: 700, letterSpacing: "0.1em", color: P.dim, textTransform: "uppercase" };
  const val = { fontSize: 19, fontWeight: 600, color: P.ink, lineHeight: 1.35 };

  // ── Empty state ─────────────────────────────────────────────────────
  if (!job) {
    return (
      <div style={{
        height: "100%", background: P.bg, color: P.ink, display: "flex",
        flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 40,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
      }}>
        <Crosshair size={48} color={P.dim} />
        <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: "-0.02em" }}>No claimed stop</div>
        <div style={{ fontSize: 19, fontWeight: 600, color: P.dim, textAlign: "center", maxWidth: 460 }}>
          Field Command drives your claimed work. Claim a job on the Jobs tab and it appears here.
        </div>
        <button type="button" onClick={() => setTab && setTab("jobs")} style={{
          marginTop: 8, padding: "16px 28px", borderRadius: 14, border: "3px solid " + P.gold,
          background: "transparent", color: P.ink, fontSize: 22, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
        }}>Open Jobs</button>
      </div>
    );
  }

  // ── Dossier rail ────────────────────────────────────────────────────
  // Read while parked; leaning left costs nothing. Rendered as a flat list of
  // blocks so landscape can stack them in a rail and portrait can lay the same
  // blocks out as a two-column band without re-authoring any of them.
  const stopIndex = stops.findIndex((j) => String(j.id) === jobId);
  const dossierItems = (
    <>
      {stops.length > 1 ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <button type="button" aria-label="Previous stop"
            onClick={() => setCurrentId(String(stops[(stopIndex - 1 + stops.length) % stops.length].id))}
            style={{
              width: 56, height: 56, borderRadius: 12, border: "3px solid " + P.line,
              background: "transparent", color: P.ink, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}><ChevronLeft size={24} /></button>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.1em", color: P.dim }}>
            STOP {stopIndex + 1} OF {stops.length}
          </div>
          <button type="button" aria-label="Next stop"
            onClick={() => setCurrentId(String(stops[(stopIndex + 1) % stops.length].id))}
            style={{
              width: 56, height: 56, borderRadius: 12, border: "3px solid " + P.line,
              background: "transparent", color: P.ink, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}><ChevronRight size={24} /></button>
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", minWidth: 0 }}>
        <div style={{
          width: 128, height: 128, flex: "0 0 128px", borderRadius: 16,
          border: "3px solid " + P.line, background: P.card, overflow: "hidden",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {/* Gated behind claim: a face on an unclaimed job broadcasts an
              identifiable person to every browsing server. Nothing in any UI
              uploads a subject photo yet, so the placeholder is the normal case. */}
          {/* [fc-crash-fix-2026-09-11] presigned via CommandSubjectThumb — the raw
              field is a JSONB object, never an <img src>. */}
          {claimed
            ? <CommandSubjectThumb job={job} P={P} />
            : <User size={44} color={P.dim} />}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={lbl}>Serve</div>
          <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: "-0.02em", color: P.ink, lineHeight: 1.1, wordBreak: "break-word" }}>
            {dx.name}
          </div>
          {flag ? (
            <div style={{
              display: "inline-block", marginTop: 8, padding: "4px 10px", borderRadius: 10,
              border: "3px solid " + (flag === "RUSH" ? P.warn : P.err),
              color: flag === "RUSH" ? P.warn : P.err, fontSize: 15, fontWeight: 700, letterSpacing: "0.06em",
            }}>{flag}</div>
          ) : null}
        </div>
      </div>

      <div>
        <div style={lbl}>Description</div>
        {claimed ? (
          <div style={val}>
            {[job.recipientDesc, job.defendantPhysicalNotes].filter(Boolean).join(" · ") || "None on file"}
            {job.defendantDob ? <div style={{ marginTop: 4 }}>DOB {job.defendantDob}</div> : null}
          </div>
        ) : (
          <div style={{ ...val, color: P.dim }}>Hidden until the job is claimed</div>
        )}
      </div>

      <div>
        <div style={lbl}>Serving</div>
        <div style={val}>{job.type || "—"}</div>
        <div style={val}>{job.docs || "—"}</div>
        {job.caseNo ? <div style={val}>Case {job.caseNo}</div> : null}
        {job.courtName || job.county ? (
          <div style={{ ...val, color: P.dim }}>{job.courtName || (job.county + " County")}</div>
        ) : null}
      </div>

      <div>
        <div style={lbl}>Client</div>
        <div style={val}>{job.client || "—"}</div>
      </div>

      <div>
        <div style={lbl}>Due diligence</div>
        <div style={{ ...val, fontWeight: 700 }}>
          windows {dd.covered}/3 {"·"} attempts {dd.attempts}/{SS_ATTEMPTS_INCLUDED}
        </div>
        {/* California requires REASONABLE DILIGENCE, not a fixed number of
            attempts. Four windows is StrikeServe policy and is labelled as such. */}
        <div style={{ fontSize: 15, fontWeight: 600, color: P.dim, lineHeight: 1.4, marginTop: 4 }}>
          Three windows (weekday day, weekday evening, weekend) is StrikeServe policy, not California
          law. The standard is reasonable diligence, not a fixed count.
        </div>
        {dd.attempts > dd.dated ? (
          <div style={{ fontSize: 15, fontWeight: 600, color: P.warn, marginTop: 4 }}>
            {dd.attempts - dd.dated} attempt(s) carry no timestamp and cannot be placed in a window.
          </div>
        ) : null}
      </div>

      <div>
        <button type="button" onClick={() => { if (setSelectedJob) setSelectedJob(job); if (setTab) setTab("jobs"); }} style={{
          padding: "12px 18px", borderRadius: 12, border: "3px solid " + P.line,
          background: "transparent", color: P.ink, fontSize: 19, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
        }}>Full job</button>
        {/* [support-tickets-2026-10-08] Escalate / get help. In the parked-reading
            dossier, not the action rail: the rail's fixed targets stay untouched. */}
        {sup.canCreate ? (
          <button type="button" onClick={() => sup.openEscalate()} style={{
            marginLeft: 12, padding: "12px 18px", borderRadius: 12, border: "3px solid " + P.gold,
            background: "transparent", color: P.ink, fontSize: 19, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            display: "inline-flex", alignItems: "center", gap: 8,
          }}><LifeBuoy size={20} /> Get help{sup.openCount > 0 ? " (" + sup.openCount + ")" : ""}</button>
        ) : null}
      </div>
    </>
  );

  // Landscape rail: one column, blocks stacked.
  const dossier = (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>{dossierItems}</div>
  );
  // Portrait band: the same blocks as direct grid children, two across. A CSS
  // multi-column wrapper would not work here — the blocks are flex/grid boxes
  // and columns cannot break inside them.
  const dossierBand = (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px 28px", alignItems: "start", minWidth: 0 }}>
      {dossierItems}
    </div>
  );

  // ── Map pane ────────────────────────────────────────────────────────
  const mapPane = (
    <div style={{ position: "relative", height: "100%", minWidth: 0, overflow: "hidden", background: P.card }}>
      <CommandMap dest={dest} routePoints={route && route.points} night={night} P={P} />
      {/* Banner locked to the top — closest to the road sightline, so it is the
          only thing glanced at in motion. */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, zIndex: 20,
        background: P.night ? "rgba(18,22,32,0.92)" : "rgba(255,255,255,0.94)",
        borderBottom: "3px solid " + P.gold, padding: "14px 18px",
      }}>
        <div style={{
          fontSize: 40, fontWeight: 800, letterSpacing: "-0.02em", color: P.ink,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.1,
        }}>{dx.street || "No address on file"}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 19, fontWeight: 600, color: P.dim }}>{dx.cityZip || ""}</span>
          <span style={{ fontSize: 19, fontWeight: 700, color: P.ink }}>
            ETA {route ? ssFcEta(route.seconds) : (routing ? "…" : "—")}
          </span>
          <span style={{ fontSize: 19, fontWeight: 700, color: P.ink }}>
            {route ? ssFcMiles(route.meters) : (ssFmtMiles(job.dist == null ? null : Number(job.dist)) || "—") /* [distance-round-2026-10-08] was the raw float, e.g. 3.158350341032145 mi */}
          </span>
          {route && route.stale ? (
            <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.06em", color: P.warn }}>LAST KNOWN ROUTE</span>
          ) : null}
          {/* Handoff opens in a NEW context. Navigating this web app to
              maps.apple.com would let iOS evict the view and return the server
              to a cold boot with the dash gone. */}
          <a
            href={appleMapsHref}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginLeft: "auto", padding: "8px 14px", borderRadius: 10,
              border: "3px solid " + P.gold, color: P.ink, textDecoration: "none",
              fontSize: 19, fontWeight: 700, whiteSpace: "nowrap",
            }}
          >Open in Apple Maps</a>
        </div>
        {/* [court-closed-warning-2026-10-08] court closed / closing soon, in the sightline banner */}
        {courtSt && courtSt.applies ? <CourtStatusBanner cs={courtSt} big style={{ marginTop: 10 }} /> : null}
      </div>
      {route && Array.isArray(route.steps) && route.steps.length ? (
        <div style={{
          position: "absolute", left: 14, bottom: 14, maxWidth: "72%", zIndex: 20,
          background: P.night ? "rgba(18,22,32,0.92)" : "rgba(255,255,255,0.94)",
          border: "3px solid " + P.line, borderRadius: 14, padding: "10px 14px",
        }}>
          <div style={lbl}>Next</div>
          <div style={{ fontSize: 19, fontWeight: 600, color: P.ink, lineHeight: 1.35 }}>
            {ssFcTruncate(route.steps[0].text, 90) || "Proceed to the destination"}
          </div>
        </div>
      ) : null}
    </div>
  );

  // ── Action rail ─────────────────────────────────────────────────────
  // Six fixed targets, 150x88, 16px gaps, bottom anchored. Unsafe is isolated
  // by an extra 28px so it is never a slip.
  /* [attempt-gate-2026-10-08] One-tap needs GPS AND the photo minimum, like the sheet. */
  const oneTapPhotoCount = ssUploadedPhotoCount(photos);
  const oneTapPhotoShort = oneTapPhotoCount < SS_ATTEMPT_MIN_PHOTOS;
  const oneTapBlocked = !gpsOk || oneTapPhotoShort;
  const actions = (
    <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 16, minWidth: 0, minHeight: "100%" }}>
      {/* Sworn outcomes. These NEVER save from the dash — each opens the attempt
          sheet pre-seeded, because served / sub-served / refused swear delivery
          under penalty of perjury. */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <FcTarget label="Sub-served" sub="OPENS FORM" tone={P.royal} Icon={User} P={P}
          onPress={() => setSheetSeed("sub_served")} />
        <FcTarget label="Refused" sub="OPENS FORM" tone={P.err} Icon={XCircle} P={P}
          onPress={() => setSheetSeed("refused")} />
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <FcTarget label="Served" sub="OPENS FORM" tone={P.ok} Icon={CheckCircle2} P={P} solid
          onPress={() => setSheetSeed("served")} />
        <FcTarget label="Not Home" tone={P.warn} Icon={AlertCircle} P={P}
          disabled={oneTapBlocked} busy={saving === "not_home"}
          onPress={() => saveOneTap("not_home")} />
      </div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <FcTarget label="No Answer" tone={P.warn} Icon={AlertCircle} P={P}
          disabled={oneTapBlocked} busy={saving === "no_answer"}
          onPress={() => saveOneTap("no_answer")} />
        <FcTarget label="Camera" sub="DOOR / DOCS" tone={P.info} Icon={Camera} P={P}
          busy={photoBusy}
          onPress={() => { if (photoInputRef.current) photoInputRef.current.click(); }} />
        {/* [attempt-photo-library-2026-10-09] pick several photos from the library in one go */}
        <FcTarget label="Library" sub="PICK PHOTOS" tone={P.info} Icon={ImageIcon} P={P}
          busy={photoBusy}
          onPress={() => { if (libraryInputRef.current) libraryInputRef.current.click(); }} />
      </div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <FcTarget label="Call" tone={P.info} Icon={Phone} P={P} onPress={() => setPhoneOpen(true)} />
        {/* [attempt-outcomes-2026-10-07] Bad address / moved / vacant / gated / court
            closed / other each need a note or an indicator, so none is one-tap — this
            opens the attempt sheet with no outcome pre-selected. */}
        <FcTarget label="Other outcome" sub="OPENS FORM" tone={P.info} Icon={MoreHorizontal} P={P}
          onPress={() => setSheetSeed("more")} />
      </div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 28 }}>
        <FcTarget label="Unsafe" tone={P.err} Icon={ShieldAlert} P={P}
          disabled={!gpsOk} busy={saving === "unsafe"}
          onPress={() => saveOneTap("unsafe")} />
      </div>

      <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.06em", color: oneTapBlocked ? P.err : P.dim, lineHeight: 1.4 }}>
        {!gpsOk
          ? (gpsErr || "NO GPS FIX — ONE-TAP OUTCOMES DISABLED")
          : oneTapPhotoShort
          ? ("PHOTOS " + oneTapPhotoCount + " OF " + SS_ATTEMPT_MIN_PHOTOS + " — TAKE FRONT DOOR, HOUSE NUMBER, WIDE SHOT. UNSAFE NEEDS NO PHOTOS.")
          : ("GPS HELD ±" + Math.round((fix && fix.accuracy) || 0) + "m"
             + (photos.length ? "  ·  " + photos.length + " PHOTO(S) ATTACHED" : ""))}
      </div>
      {/* Never prompt to photograph the person served (Civil Code 1708.8). */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={onPhotoPicked}
        style={{ display: "none" }}
      />
      {/* [attempt-photo-library-2026-10-09] no capture attribute = Photos library picker, multi-select */}
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        onChange={onPhotoPicked}
        style={{ display: "none" }}
      />
    </div>
  );

  const bannerEl = banner ? (
    <div style={{
      position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)", zIndex: 300,
      border: "3px solid " + (banner.kind === "err" ? P.err : P.ok),
      background: P.card, color: banner.kind === "err" ? P.err : P.ok,
      borderRadius: 14, padding: "12px 20px", fontSize: 19, fontWeight: 700, maxWidth: "80%",
    }} onClick={() => setBanner(null)}>{banner.text}</div>
  ) : null;

  /* ── In-motion display ───────────────────────────────────────────────
     The NHTSA budget: <=30 characters, <=2 lines, ONE tap target, a 2-second
     glance, a 12-second total task. CVC 23123.5 permits a mounted device
     operated with a single swipe or tap; two taps, typing or scrolling while
     driving is a violation, and a citation attached to a serve attempt is
     impeachment material in the case the server is testifying in.
     This overlays rather than replaces, so an open attempt sheet underneath
     keeps its draft untouched — a frozen draft, never a discarded one. */
  const motionEl = moving ? (
    <div style={{
      position: "absolute", inset: 0, zIndex: 9500, background: P.bg, color: P.ink,
      display: "flex", flexDirection: "column", justifyContent: "center", gap: 28, padding: 48,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
    }}>
      <div style={{ fontSize: 40, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
        {ssFcTruncate(dx.street || "Next stop", 30)}
      </div>
      <div style={{ fontSize: 40, fontWeight: 800, color: P.gold }}>
        ETA {route ? ssFcEta(route.seconds) : "—"}
      </div>
      {flag ? (
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "0.04em", color: flag === "RUSH" ? P.warn : P.err }}>
          {ssFcTruncate(flag, 30)}
        </div>
      ) : null}
      <button type="button" onClick={declareParked} style={{
        alignSelf: "flex-start", width: 320, height: 120, borderRadius: 20,
        border: "3px solid " + P.gold, background: P.gold, color: "#0a1628",
        fontSize: 40, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
      }}>Arrived</button>
      <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.1em", color: P.dim }}>
        IN MOTION {mph != null ? "· " + Math.round(mph) + " MPH" : ""}
      </div>
    </div>
  ) : null;

  const shell = {
    height: "100%", background: P.bg, color: P.ink, position: "relative", overflow: "hidden",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
  };

  // ── Portrait reflow ─────────────────────────────────────────────────
  // Map full width on top at ~46% height, dossier as a band beneath, actions as
  // a grid pinned to the bottom. Same components, same fixed target sizes.
  if (portrait) {
    return (
      <div style={shell}>
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div style={{ height: "46%", flex: "0 0 46%", position: "relative" }}>{mapPane}</div>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "18px 22px" }}>
            {dossierBand}
          </div>
          <div style={{ flex: "0 0 auto", minHeight: 220, borderTop: "3px solid " + P.line, padding: "16px 22px" }}>
            {actions}
          </div>
        </div>
        {bannerEl}
        {phoneOpen ? <CommandPhonePanel job={job} P={P} onClose={() => setPhoneOpen(false)} /> : null}
        {sheetSeed ? (
          <AttemptCapture job={job} seedOutcome={sheetSeed === "more" ? null : sheetSeed} onClose={() => { setSheetSeed(null); }} />
        ) : null}
        {sup.escalate ? (
          <EscalateSheet job={job} seedCategory={sup.escalate.seedCategory} seedAttemptRef={sup.escalate.seedAttemptRef}
            onClose={sup.closeEscalate} />
        ) : null}{/* [support-tickets-2026-10-08] */}
        {motionEl}
      </div>
    );
  }

  // ── Landscape: three columns, no app chrome ─────────────────────────
  return (
    <div style={shell}>
      <div style={{ display: "flex", flexDirection: "row", height: "100%" }}>
        <div style={{ width: "30%", flex: "0 0 30%", minWidth: 0, overflowY: "auto", padding: "22px 24px", borderRight: "3px solid " + P.line }}>
          {dossier}
        </div>
        <div style={{ width: "44%", flex: "0 0 44%", minWidth: 0, position: "relative" }}>
          {mapPane}
        </div>
        <div style={{ width: "26%", flex: "0 0 26%", minWidth: 0, overflowY: "auto", padding: "22px 20px", borderLeft: "3px solid " + P.line }}>
          {actions}
        </div>
      </div>
      {bannerEl}
      {phoneOpen ? <CommandPhonePanel job={job} P={P} onClose={() => setPhoneOpen(false)} /> : null}
      {sheetSeed ? (
        <AttemptCapture job={job} seedOutcome={sheetSeed === "more" ? null : sheetSeed} onClose={() => { setSheetSeed(null); }} />
      ) : null}
      {sup.escalate ? (
        <EscalateSheet job={job} seedCategory={sup.escalate.seedCategory} seedAttemptRef={sup.escalate.seedAttemptRef}
          onClose={sup.closeEscalate} />
      ) : null}{/* [support-tickets-2026-10-08] */}
      {motionEl}
    </div>
  );
}

const pillBtn = (active) => ({
  flex: 1, padding: "8px 12px", borderRadius: 20, border: "none",
  background: active ? C.white : "transparent",
  color: active ? C.deep : C.steel, fontSize: 13, fontWeight: 700,
  display: "flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer",
  boxShadow: active ? "0 2px 6px rgba(15,40,96,0.1)" : "none", transition: "all 0.15s",
});

// Clear-route pill in the JobsScreen header — shown only when AppCtx.routePolyline
// is non-null. Tappable to call setRoutePolyline(null) and tear the gold overlay off the map.
const clearRoutePill = {
  display: "inline-flex", alignItems: "center", gap: 5,
  padding: "5px 10px", borderRadius: 14,
  background: C.goldXL,
  border: `1px solid ${C.gold}`,
  color: C.deep, fontSize: 11, fontWeight: 800, cursor: "pointer",
  letterSpacing: "0.02em",
};

function FilterChip({ label, count, active, onPress }) {
  return (
    <button onClick={onPress} style={{
      padding: "6px 10px", borderRadius: 14, border: `1px solid ${active ? C.deep : C.steelXL}`,
      background: active ? C.deep : C.white, color: active ? C.white : C.deep,
      fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 5, cursor: "pointer",
    }}>
      {label}
      <span style={{
        background: active ? "rgba(255,255,255,0.2)" : C.steelXXL,
        color: active ? C.white : C.steel,
        padding: "1px 6px", borderRadius: 8, fontSize: 10, fontWeight: 800,
      }}>{count}</span>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════

/* [pos-overhaul-2026-10-07:server-app] SignatureSheet (draw -> POST /api/forms/sign with no
   review, no attestation, no city and no draft hash) was removed. Every POS signature now
   goes through PosSignReviewSheet. */

// MAP VIEW (faux map with brand-themed terrain)
// ═══════════════════════════════════════════════════════════════════════
function MapView({ jobs, onPin }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);          // mapkit.PolylineOverlay (single overlay)
  const numberedMarkersRef = useRef([]);     // numbered route stop annotations
  // [live-route-2026-09-20] `dark` added so the Jobs-tab map follows the sun/moon
  // toggle. Deliberately NOT useFcNight() — this is ordinary app chrome, so it
  // tracks the explicit toggle only and never auto-dims on the clock/ambient band.
  const { routePolyline, isTablet, dark } = React.useContext(AppCtx);
  const [coords, setCoords] = useState({}); // jobId -> {lat, lng}
  // [claim-map-unmask-2026-07-27] Pin currently selected on the map. Drives the
  // in-map claim bar below so a job can be accepted without leaving the map.
  const [pinJob, setPinJob] = useState(null);
  // True while the annotation layer is being torn down + rebuilt (coords stream
  // in one job at a time). MapKit fires "deselect" during removeAnnotations, and
  // without this guard the claim bar would flicker away on every rebuild.
  const rebuildingRef = useRef(false);
  // [claim-map-unmask-2026-07-27] SAME claim path as JobDetailSheet / InlineJobDetail.
  const { claim: claimJob, busy: claimBusy, err: claimErr, setErr: setClaimErr, bundlePrompt: claimBundlePrompt } =
    useJobClaim(() => setPinJob(null), () => setPinJob(null)); // [job-bundles-2026-10-13] [server-zone-30-2026-10-18] onRefresh clears the pin after OUT_OF_ZONE
  // v1.1 (iPad blank-screen triage): we render a neutral placeholder until
  // window.__mapkitReady resolves AND mapkit construction has succeeded. Never
  // throw out of this component — a crash here was the suspected root cause of
  // an unmounted tablet parent tree on iPad.
  const [mapReady, setMapReady] = useState(false);

  // [live-route-2026-09-20] Re-apply the color scheme when the sun/moon toggle
  // flips. Mirrors CommandMap's handling — the init effect above can't do this
  // because it short-circuits once mapRef.current exists.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof mapkit === "undefined") return;
    try { map.colorScheme = dark ? mapkit.Map.ColorSchemes.Dark : mapkit.Map.ColorSchemes.Light; } catch (_) {}
  }, [dark, mapReady]);

  // Initialize Apple MapKit JS once. Center on user's GPS (35-mile radius) so the
  // server can see all jobs across the whole city. Falls back to Sacramento center
  // if geolocation hasn't resolved yet — re-centers as soon as it does.
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    let cancelled = false;
    // 30-mile radius. 1° lat ≈ 69 mi, so 30-mi radius ≈ 0.435° lat half-span,
    // ≈ 0.87° lat full span. Longitude at 38°N is ~54.5 mi/deg, so 30-mi
    // radius ≈ 0.55° lon half-span ≈ 1.10° lon full span. Round up slightly
    // for breathing room.
    const SPAN_LAT = 0.90;
    const SPAN_LON = 1.12;
    const FALLBACK = { lat: 38.5816, lng: -121.4944 }; // Sacramento

    // Hardened guard — never throw if MapKit JS hasn't initialized. If
    // __mapkitReady isn't a Promise yet (CDN still loading), poll briefly and
    // bail without crashing the parent tree. The placeholder div below stays
    // mounted in the meantime.
    if (!window.__mapkitReady || typeof window.__mapkitReady.then !== "function") {
      const id = setTimeout(() => {
        if (cancelled || mapRef.current || !mapEl.current) return;
        // Force a re-render once MapKit (likely) shows up so this effect re-runs.
        if (window.__mapkitReady && typeof window.__mapkitReady.then === "function") {
          setCoords(c => ({ ...c }));
        }
      }, 600);
      return () => { cancelled = true; clearTimeout(id); };
    }
    window.__mapkitReady.then(() => {
      if (cancelled || !mapEl.current || mapRef.current) return;
      if (typeof mapkit === "undefined") {
        console.warn("[mapkit] __mapkitReady resolved but mapkit global is undefined; skipping map init");
        return;
      }
      let map;
      try {
        const span = new mapkit.CoordinateSpan(SPAN_LAT, SPAN_LON);
        const center = new mapkit.Coordinate(FALLBACK.lat, FALLBACK.lng);
        map = new mapkit.Map(mapEl.current, {
          region: new mapkit.CoordinateRegion(center, span),
          showsUserLocation: true,
          tracksUserLocation: false,
          showsCompass: mapkit.FeatureVisibility.Adaptive,
          showsZoomControl: false,
          showsScale: mapkit.FeatureVisibility.Hidden,
          // [live-route-2026-09-20] was hardcoded Light — stayed a white rectangle
          // in dark mode. `dark` is intentionally NOT in this effect's deps: the
          // effect bails on `mapRef.current`, so re-running it would do nothing.
          // The dedicated effect below re-applies the scheme on toggle instead.
          colorScheme: dark ? mapkit.Map.ColorSchemes.Dark : mapkit.Map.ColorSchemes.Light,
        });
      } catch (e) {
        // Swallow — a synchronous throw from mapkit.Map construction was the
        // suspected unmounted-tree cause on iPad. Leave the placeholder up.
        console.warn("[mapkit] map construction threw:", (e && e.message) || e);
        return;
      }
      mapRef.current = map;
      setMapReady(true);
      // Once we have real GPS, re-center on the user (still 30-mi radius).
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (cancelled || !mapRef.current) return;
            /* [mapkit-span-scope-fix-2026-07-27] `span` is a const inside the try block
               above, so it is not in scope here — this line threw
               "ReferenceError: span is not defined" on every map load and re-centre-on-user
               never worked. Build an equivalent span from SPAN_LAT/SPAN_LON, which ARE in
               the effect closure, and never let a map call take down the callback. */
            try {
              const userSpan = new mapkit.CoordinateSpan(SPAN_LAT, SPAN_LON);
              const userCenter = new mapkit.Coordinate(pos.coords.latitude, pos.coords.longitude);
              mapRef.current.region = new mapkit.CoordinateRegion(userCenter, userSpan);
            } catch (e) {
              console.warn("[mapkit] re-center on user failed:", (e && e.message) || e);
            }
          },
          () => { /* permission denied — stay on fallback */ },
          { maximumAge: 60000, timeout: 5000, enableHighAccuracy: false }
        );
      }
    }).catch(err => console.warn("[mapkit] init failed:", err && err.message || err));
    return () => {
      cancelled = true;
      if (mapRef.current) { try { mapRef.current.destroy(); } catch(_){} mapRef.current = null; }
    };
  }, []);

  // Geocode each job address (cached in localStorage)
  useEffect(() => {
    let cancelled = false;
    async function geocodeJob(j) {
      // [2026-06-19 owner decision] Pin every job at its precise recipient
      // address. The previous city-only mask for unclaimed jobs produced an
      // empty query — jobs carry no separate city column (the address lives in
      // recipient_address), so unclaimed jobs never pinned. Use the full address.
      if (!j.address && !j.city) return null;
      const queryParts = [j.address, j.city, j.zip];
      const cacheKey = `ss_geo_f_${queryParts.filter(Boolean).join("|")}`.toLowerCase();
      const cached = localStorage.getItem(cacheKey);
      if (cached) { try { return JSON.parse(cached); } catch {} }
      const q = queryParts.filter(Boolean).join(", ");
      try {
        const r = await api.get(`/api/geocode?q=${encodeURIComponent(q)}`);
        if (Array.isArray(r) && r.length && r[0].lat) {
          const c = { lat: parseFloat(r[0].lat), lng: parseFloat(r[0].lon) };
          localStorage.setItem(cacheKey, JSON.stringify(c));
          return c;
        }
      } catch (e) { console.warn("[geocode]", j.id, e.message); }
      return null;
    }
    (async () => {
      for (const j of jobs) {
        if (coords[j.id]) continue;
        const c = await geocodeJob(j);
        if (cancelled) return;
        if (c) setCoords(prev => ({ ...prev, [j.id]: c }));
        await new Promise(r => setTimeout(r, 200)); // rate-limit Nominatim
      }
    })();
    return () => { cancelled = true; };
  }, [jobs]);

  // Render markers via mapkit.Annotation whenever coords or jobs change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof mapkit === "undefined") return;
    // [claim-map-unmask-2026-07-27] suppress deselect side-effects during rebuild
    rebuildingRef.current = true;
    map.removeAnnotations(markersRef.current);
    markersRef.current = [];
    const items = [];
    /* [field-map-ungroup-2026-10-08] Every job is its own pin — no clustering. Jobs at
       the EXACT same coordinates (same building / same geocode) would sit on top of
       each other with only the top one tappable, so each duplicate after the first is
       stacked upward by one pin height via anchorOffset (screen pixels, so the
       separation holds at every zoom). Index is deterministic: duplicates are ordered
       by job id. */
    /* [job-bundles-v2-2026-10-16] ONE pin per available bundle ("N jobs · $X"): the first
       geocoded member stands in for the bundle; the other members get no pin. Members of a
       bundle that is not 'open' are hidden. Everything else is unchanged. */
    const pinJobs = [];
    const bundlePinLabel = {};
    {
      const seenBundle = {};
      for (const j of jobs) {
        if (j && j.status === "available" && ssIsMultiBundle(j)) {
          const b = ssJobBundle(j);
          if (b.status && b.status !== "open") continue;
          if (seenBundle[b.id] || !coords[j.id]) continue;
          seenBundle[b.id] = true;
          bundlePinLabel[j.id] = `${Number(b.count) || 0} jobs · ${ssPayLabel(b.totalPay)}`;
        }
        pinJobs.push(j);
      }
    }
    const dupIndex = {};
    {
      const groups = {};
      for (const j of pinJobs) {
        const c = coords[j.id];
        if (!c) continue;
        const key = Number(c.lat).toFixed(6) + "," + Number(c.lng).toFixed(6);
        (groups[key] = groups[key] || []).push(j.id);
      }
      for (const key of Object.keys(groups)) {
        groups[key].slice().sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }))
          .forEach((id, i) => { dupIndex[id] = i; });
      }
    }
    for (const j of pinJobs) {
      const c = coords[j.id];
      if (!c) continue;
      const stackK = dupIndex[j.id] || 0;
      const bundleLbl = bundlePinLabel[j.id] || ""; // [job-bundles-v2-2026-10-16]
      const color = j.status === "completed" ? "#8a8d93"
                  : ssIsPriorityJob(j) ? "#c0392b"
                  : bundleLbl ? "#7a5e1a"
                  : "#1a3a7a";
      const ann = new mapkit.Annotation(
        new mapkit.Coordinate(c.lat, c.lng),
        () => {
          const el = document.createElement("div");
          el.style.cssText = "display:flex;flex-direction:column;align-items:center;cursor:pointer";
          el.innerHTML = `<div style="background:${color};color:#fff;padding:4px 8px;border-radius:14px;font-weight:800;font-size:12px;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid #fff">${bundleLbl || ssPayLabel(j.pay)}</div>`; /* [job-bundles-v2-2026-10-16] */
          return el;
        },
        {
          // [field-map-ungroup-2026-10-08] exact-duplicate pins stack upward 28px each
          anchorOffset: new DOMPoint(0, -7 - 28 * stackK),
          // [claim-map-unmask-2026-07-27] real servee name on every pin, claimed or not
          title: bundleLbl ? ("Bundled order: " + bundleLbl) : (j.servee || ("Job #" + j.jobNumber)), // [job-bundles-v2-2026-10-16]
          subtitle: jobDisplay(j).streetCity || ("Job #" + j.jobNumber),
          // [field-map-ungroup-2026-10-08] Clustering removed (was clusteringIdentifier
          // "ss-jobs" + map.annotationForCluster). Collision hiding is off too: with
          // CollisionMode.Circle and displayPriority < Required, MapKit silently HIDES
          // overlapping lower-priority pins, which is grouping by another name.
          collisionMode: mapkit.Annotation.CollisionMode.None,
          displayPriority: mapkit.Annotation.DisplayPriority ? mapkit.Annotation.DisplayPriority.Required : 1000,
        }
      );
      // [claim-map-unmask-2026-07-27] Selecting a pin raises the in-map claim bar
      // (see the MAP CLAIM BAR block in this component's return). On tablet the
      // map stays visible beside the detail pane, so we also open detail there —
      // preserving the old one-tap behavior on the layout that has room for it.
      // On phone the bar's card is tappable and calls onPin, so the detail sheet
      // is still one tap away without the sheet covering the claim affordance.
      ann.addEventListener("select", () => {
        setPinJob(j);
        setClaimErr("");
        if (isTablet && onPin) onPin(j);
      });
      ann.addEventListener("deselect", () => {
        if (rebuildingRef.current) return;
        setPinJob(prev => (prev && prev.id === j.id ? null : prev));
      });
      map.addAnnotation(ann);
      markersRef.current.push(ann);
      items.push(ann);
    }
    rebuildingRef.current = false;
    // [field-map-ungroup-2026-10-08] No cluster annotation: clear any handler a
    // previous build installed on this map instance.
    try { map.annotationForCluster = undefined; } catch (_) {}
    // Note: intentionally NOT calling map.showItems() — we want a stable 30-mile
    // radius around the user, not an auto-fit that zooms in on whatever's loaded.
    // [claim-map-unmask-2026-07-27] isTablet/setClaimErr added — the select handler closes over them.
  }, [coords, jobs, onPin, isTablet, setClaimErr]);

  // ── Route polyline overlay ─────────────────────────────────────────────
  // When routePolyline (an array of {lat,lng}) is set in AppCtx, draw a gold
  // connecting line across the stops in order plus a set of numbered (1, 2, 3...)
  // markers that overlay the existing $-pin annotations. When routePolyline is
  // cleared (null), tear the overlay + numbered markers back down.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof mapkit === "undefined") return;
    // Always clean up previous overlay/markers first.
    if (polylineRef.current) {
      try { map.removeOverlay(polylineRef.current); } catch (_) {}
      polylineRef.current = null;
    }
    if (numberedMarkersRef.current.length) {
      try { map.removeAnnotations(numberedMarkersRef.current); } catch (_) {}
      numberedMarkersRef.current = [];
    }
    if (!routePolyline || !Array.isArray(routePolyline) || routePolyline.length < 2) return;

    const valid = routePolyline.filter(p => p && p.lat != null && p.lng != null);
    if (valid.length < 2) return;
    const coordsArr = valid.map(p => new mapkit.Coordinate(p.lat, p.lng));

    // Gold polyline overlay across the stops.
    const polyline = new mapkit.PolylineOverlay(coordsArr, {
      style: new mapkit.Style({
        strokeColor: "#c9a84c", // C.gold
        lineWidth: 4,
        lineJoin: "round",
        lineCap: "round",
      }),
    });
    try { map.addOverlay(polyline); polylineRef.current = polyline; }
    catch (e) { console.warn("[mapkit] addOverlay failed:", e && e.message || e); }

    // Numbered markers (1, 2, 3...) drawn ON TOP of existing $-pins.
    const numbered = valid.map((p, idx) => {
      const ann = new mapkit.Annotation(
        new mapkit.Coordinate(p.lat, p.lng),
        () => {
          const el = document.createElement("div");
          el.style.cssText = "display:flex;flex-direction:column;align-items:center;cursor:default";
          el.innerHTML = `<div style="background:#0f2860;color:#fff;width:26px;height:26px;border-radius:50%;font-weight:800;font-size:13px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.4);border:2.5px solid #c9a84c">${idx + 1}</div>`;
          return el;
        },
        {
          anchorOffset: new DOMPoint(0, -22),
          // Stay visible above $ pins / clusters.
          displayPriority: 1000,
          collisionMode: mapkit.Annotation.CollisionMode.None,
          selected: false,
          title: `Stop ${idx + 1}`,
        }
      );
      try { map.addAnnotation(ann); } catch (_) {}
      return ann;
    });
    numberedMarkersRef.current = numbered;

    /* [field-command-2026-09-11] REAL ROAD ROUTE.
       The gold line above is a straight segment between stop coordinates — it crosses
       rivers, runs through blocks, and understates every distance it is read off. It
       stays, but only as the instant fallback: we now ask mapkit.Directions for the
       actual driving path leg by leg and, once EVERY leg resolves, swap the straight
       line for the road geometry in one atomic replace (a partial swap would show a
       route that is half real and half invented). Any failure — no Directions in this
       SDK, a quota error, one leg that will not route, the component unmounting — and
       the straight line simply stays up. Legs are cached, so the dead-zone case reuses
       the last good geometry instead of falling back. */
    let upgradeCancelled = false;
    (async () => {
      if (typeof mapkit === "undefined" || !mapkit.Directions) return;
      const legs = [];
      for (let i = 0; i < valid.length - 1; i++) {
        const leg = await ssGetRoute(valid[i], valid[i + 1]);
        if (upgradeCancelled) return;
        if (!leg || !Array.isArray(leg.points) || leg.points.length < 2) return; // keep the straight line
        legs.push(leg);
      }
      if (upgradeCancelled || !legs.length) return;
      const realCoords = [];
      for (const leg of legs) {
        for (const p of leg.points) {
          if (p && p.lat != null && p.lng != null) realCoords.push(new mapkit.Coordinate(p.lat, p.lng));
        }
      }
      if (upgradeCancelled || realCoords.length < 2) return;
      try {
        const real = new mapkit.PolylineOverlay(realCoords, {
          style: new mapkit.Style({
            strokeColor: "#c9a84c", // C.gold
            lineWidth: 5,
            lineJoin: "round",
            lineCap: "round",
          }),
        });
        map.addOverlay(real);
        const old = polylineRef.current;
        polylineRef.current = real;
        if (old) { try { map.removeOverlay(old); } catch (_) {} }
      } catch (e) {
        console.warn("[mapkit] road-route overlay failed:", (e && e.message) || e);
      }
    })();

    // Auto-fit to the route. showItems handles padding + the bounding region.
    try {
      map.showItems([polyline, ...numbered], {
        animate: true,
        padding: { top: 40, right: 40, bottom: 40, left: 40 },
      });
    } catch (e) { /* showItems may be unavailable in old SDKs — ignore */ }

    return () => {
      // Cleanup on unmount or when routePolyline changes
      // [field-command-2026-09-11] stop the in-flight Directions upgrade from
      // adding an overlay to a map that has already been torn down.
      upgradeCancelled = true;
      if (polylineRef.current) {
        try { map.removeOverlay(polylineRef.current); } catch (_) {}
        polylineRef.current = null;
      }
      if (numberedMarkersRef.current.length) {
        try { map.removeAnnotations(numberedMarkersRef.current); } catch (_) {}
        numberedMarkersRef.current = [];
      }
    };
  }, [routePolyline]);

  // [claim-map-unmask-2026-07-27] Resolve the selected pin against the CURRENT
  // jobs array so the bar reflects post-reload status instead of a stale row.
  const livePinJob = pinJob ? ((jobs || []).find(x => x && x.id === pinJob.id) || pinJob) : null;
  const pinDx = livePinJob ? jobDisplay(livePinJob) : null;
  // [claim-map-unmask-2026-07-27] Dismiss also clears MapKit's own selection so
  // re-tapping the same pin fires "select" again.
  function dismissPin() {
    setPinJob(null);
    setClaimErr("");
    try { if (mapRef.current) mapRef.current.selectedAnnotation = null; } catch (_) {}
  }

  // Always render the mapEl div (mapkit attaches to it imperatively). When
  // MapKit isn't ready we overlay a neutral placeholder rather than throwing —
  // an unmounted/thrown MapView was the suspected cause of the iPad blank tree.
  return (
    <div style={{ position: "relative", width: "100%", height: "100%", minHeight: 320 }}>
      <div ref={mapEl} style={{ width: "100%", height: "100%", minHeight: 320 }} />
      {!mapReady && (
        <SkeletonGroup label="Loading map" style={{
          position: "absolute", inset: 0, pointerEvents: "none",
        }}>
          <SkeletonRect width="100%" height="100%" radius={0} style={{ minHeight: 320 }} />
        </SkeletonGroup>
      )}

      {claimBundlePrompt}{/* [job-bundles-2026-10-13] bundle confirm for the map claim bar */}
      {/* ═══ [claim-map-unmask-2026-07-27] MAP CLAIM BAR ═══════════════════
          Persistent action bar over the map for the selected pin — this is the
          "claim straight from the map" affordance. Accept calls claimJob() from
          the SHARED useJobClaim hook (identical to the sheet's Accept button),
          so PREMIUM_RESERVED / ONBOARDING_INCOMPLETE / TRAINING_INCOMPLETE /
          CLAIM_CAP_REACHED all still render here verbatim. Tapping the card body
          opens the full detail sheet via onPin, preserving the old behavior.
          zIndex 60 keeps it above the Plan-My-Day FAB (zIndex 50). */}
      {livePinJob && (
        <div style={{
          position: "absolute", left: 12, right: 12, bottom: 14, zIndex: 60,
          background: C.white, borderRadius: 16, border: `1px solid ${C.steelXL}`,
          boxShadow: "0 8px 24px rgba(15,40,96,0.22)", padding: 12,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <button onClick={() => { if (onPin) onPin(livePinJob); }} style={{
              flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8,
              background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer",
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 14, fontWeight: 800, color: C.deep,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{livePinJob.status === "available" && ssIsMultiBundle(livePinJob) ? `Bundled order: ${ssJobBundle(livePinJob).count} jobs` : pinDx.name/* [job-bundles-v2-2026-10-16] */}</div>
                <div style={{
                  fontSize: 11, color: C.steel, marginTop: 1,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{pinDx.streetCity || `Job #${livePinJob.jobNumber}`}</div>
                <div style={{
                  fontSize: 11, color: C.steel, marginTop: 1,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {livePinJob.type}{livePinJob.client ? ` · ${livePinJob.client}` : ""}
                  {ssFmtMiles(livePinJob.dist == null ? null : Number(livePinJob.dist)) ? ` · ${ssFmtMiles(Number(livePinJob.dist))}` : ""/* [distance-round-2026-10-08] */}
                </div>
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.ok, flexShrink: 0 }}>{ssPayLabel(ssJobShownPay(livePinJob))/* [job-bundles-v2-2026-10-16] bundle total */}</div>
              <ChevronRight size={16} color={C.steel} style={{ flexShrink: 0 }} />
            </button>
            <button onClick={dismissPin} aria-label="Dismiss job" style={{
              background: "none", border: "none", padding: 2, cursor: "pointer", flexShrink: 0,
            }}>
              <X size={16} color={C.steel} />
            </button>
          </div>

          {ssIsMultiBundle(livePinJob) && (
            <div style={{ marginTop: 8 }}><BundlePill job={livePinJob} /></div>
          )}{/* [job-bundles-2026-10-13] */}
          {claimErr && (
            <div style={{ color: "#c0392b", fontSize: 12, marginTop: 8, lineHeight: 1.4 }}>{claimErr}</div>
          )}

          {livePinJob.status === "available" && livePinJob.network_claim_blocked === true ? (
            <SsNetworkBlockedNotice job={livePinJob} style={{ marginTop: 10 }} />
          ) : livePinJob.status === "available" ? (
            <button onClick={() => claimJob(livePinJob)} disabled={claimBusy} style={{
              width: "100%", marginTop: 10, padding: 13, background: C.deep, color: C.white,
              border: "none", borderRadius: 13, fontSize: 14, fontWeight: 800,
              cursor: claimBusy ? "default" : "pointer", opacity: claimBusy ? 0.65 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              letterSpacing: "0.02em", boxShadow: `0 4px 12px ${C.deep}40`,
            }}>
              <Check size={17} /> {claimBusy ? "Accepting…" : ssAcceptLabel(livePinJob)/* [job-bundles-2026-10-13] */}
            </button>
          ) : (
            <button onClick={() => { if (onPin) onPin(livePinJob); }} style={{
              width: "100%", marginTop: 10, padding: 13, background: C.white, color: C.deep,
              border: `1.5px solid ${C.deep}`, borderRadius: 13, fontSize: 14, fontWeight: 700,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <Briefcase size={16} /> Open job
            </button>
          )}

          {/* [field-view-2026-09-07] Field View entry point from the map pin.
              Tertiary styling (1px steel border, 12.5px label, no shadow) so it
              can never be confused with the navy Accept CTA above it. It makes
              NO claim call — it only fires ss-open-field. */}
          <button
            onClick={() => openFieldView(livePinJob)}
            aria-label={"Open Field View for " + pinDx.name}
            style={{
              width: "100%", marginTop: 8, padding: 10, background: C.white, color: C.deep,
              border: `1px solid ${C.steelXL}`, borderRadius: 12, fontSize: 12.5, fontWeight: 700,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <ScanFace size={15} color={C.royal} strokeWidth={2.2} /> Field View
          </button>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// LIST VIEW
// ═══════════════════════════════════════════════════════════════════════
/* [jobs-empty-reason-2026-10-18] Why the list is empty. The backend does not send an
   X-Jobs-Empty-Reason header (checked 2026-10-18), so this is derived on the client from
   what it knows: the filter, and whether the office has set a home base (no home base =
   the backend shows no available jobs at all, fail closed). */
function ssJobsEmptyMessage(filter, user) {
  const hasHome = !!ssHomeBase(user);
  const zone = `within ${SS_ZONE_MILES} miles of your home base`;
  if (filter === "archived") return { title: "No archived jobs", body: "Jobs the office archives after they are finished will appear here." };
  if (filter === "active") return { title: "No jobs in progress", body: "Jobs you claim or are assigned show here until they are served or closed." };
  if (!hasHome) return { title: filter === "available" ? "No available jobs" : "No jobs yet", body: "Your service area hasn't been set yet, so available jobs can't be shown. Contact support to have your home base set." };
  if (filter === "available") return { title: "No available jobs right now", body: `You see open jobs ${zone}. New jobs appear here as they are posted.` };
  return { title: "No jobs yet", body: `You have no assigned jobs, and there are no open jobs ${zone} right now. New jobs appear here as they are posted.` };
}

function ListView({ jobs, onPress, filter, archivedState }) {
  const { jobsLoading, jobsErr, user, reload } = React.useContext(AppCtx);
  const _arch = filter === "archived"; // [server-archived-jobs-2026-10-18]
  if (_arch && archivedState && (archivedState.loading || archivedState.err) && jobs.length === 0) {
    return (
      <div style={{ padding: "40px 24px", textAlign: "center", color: C.steel, fontSize: 13 }}>
        {archivedState.loading ? "Loading archived jobs\u2026" : (
          <>
            <div style={{ color: C.err, fontWeight: 700, marginBottom: 8 }}>{archivedState.err}</div>
            <button onClick={archivedState.retry} style={{
              background: "none", border: `1px solid ${C.steelL}`, borderRadius: 10, padding: "6px 12px",
              color: C.deep, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
            }}>Try again</button>
          </>
        )}
      </div>
    );
  }
  // Cold-launch skeleton: 6 cards approximating ListJobCard's footprint so the
  // scroll position is stable when real jobs hydrate. We only show this when
  // there are no jobs to render — if there's even a cached list, we skip
  // straight to the real cards (stale-while-revalidate UX).
  if (!_arch && jobsLoading && jobs.length === 0) {
    return (
      <div style={{ padding: "12px 16px 110px", overflow: "auto", height: "100%" }}>
        <SkeletonGroup label="Loading jobs">
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[0,1,2,3,4,5].map(i => <SkeletonCard key={i} height={142} />)}
          </div>
        </SkeletonGroup>
      </div>
    );
  }
  // [jobs-empty-reason-2026-10-18] Explain an empty list instead of a blank pane. A load
  // error (jobsErr) is reported as such rather than as "no jobs".
  if (jobs.length === 0) {
    const loadFailed = !_arch && !!jobsErr;
    const m = loadFailed
      ? { title: "Couldn't load jobs", body: String(jobsErr) }
      : ssJobsEmptyMessage(filter || "all", user);
    return (
      <div style={{ padding: "40px 28px 110px", textAlign: "center", overflow: "auto", height: "100%" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.deep, marginBottom: 6 }}>{m.title}</div>
        <div style={{ fontSize: 12, color: C.steel, lineHeight: 1.5 }}>{m.body}</div>
        {loadFailed && typeof reload === "function" && (
          <button onClick={() => reload()} style={{
            marginTop: 12, background: "none", border: `1px solid ${C.steelL}`, borderRadius: 10, padding: "6px 12px",
            color: C.deep, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
          }}>Try again</button>
        )}
      </div>
    );
  }
  return (
    <div style={{ padding: "12px 16px 110px", overflow: "auto", height: "100%" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* [field-list-priority-2026-10-08] same-day / urgent / rush first; no user sort exists here */}
        {/* [job-bundles-v2-2026-10-16] Available bundle members -> ONE BundleCard; the server's own
            bundle jobs sit under a "Bundle · N jobs" header with Release bundle. */}
        {ssGroupJobsForList(ssSortByPriority(jobs)).map((it) => (
          it.kind === "bundle" ? <BundleCard key={it.key} group={it} onPress={onPress} />
          : it.kind === "mybundle" ? (
            <MyBundleGroup key={it.key} group={it}>
              {it.jobs.map(job => <ListJobCard key={job.id} job={job} onPress={() => onPress(job)} />)}
            </MyBundleGroup>
          )
          : <ListJobCard key={it.key} job={it.job} onPress={() => onPress(it.job)} />
        ))}
      </div>
    </div>
  );
}

function ListJobCard({ job, onPress }) {
  /* [fc-crash-fix-2026-09-11] THE LINE THAT CRASHED THE iPad. This card rendered
     `{job.attempts}/4 attempts`, and on a raw (un-normalized) job row `attempts` is
     the array of attempt records — React then tried to render an attempt object as a
     child and threw #31, blanking the app. ssAttemptCount() always returns a number. */
  const attemptCount = ssAttemptCount(job);
  // [served-not-overdue-2026-10-11] Only while the job is still in progress: a served /
  // completed job (normalized status "completed") must never read "Attempt overdue".
  const overdueAttempts = attemptCount >= 2 && job.status === "active";
  const claimed = isJobClaimed(job);
  // [sub-serve-mailing-2026-09-18] Outstanding §415.20(b) mailing — same badge
  // mechanism this card already uses for an overdue attempt.
  const mailingDue = !!ssJobMailingDue(job);
  const attemptDue = ssAttemptDueInfo(job); // [field-list-attempt-due-2026-10-08]
  const dx = jobDisplay(job);
  const statusPill = job.status === "active" ? { bg: C.purpleL, color: C.purple, label: "In Progress" }
    : job.status === "completed" ? { bg: C.okL, color: C.ok, label: "Completed" }
    : { bg: C.infoL, color: C.info, label: "Available" };

  return (
    <button onClick={onPress} style={{
      background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 16, padding: 14,
      cursor: "pointer", textAlign: "left", width: "100%",
      boxShadow: "0 1px 3px rgba(15,40,96,0.04)",
    }}>
      {overdueAttempts && claimed && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          background: C.errL, color: C.err, padding: "3px 8px", borderRadius: 10,
          fontSize: 10, fontWeight: 700, marginBottom: 8,
        }}>
          <Clock size={10} /> Attempt overdue
        </div>
      )}
      {/* [sub-serve-mailing-2026-09-18] Substituted service is not complete and the
          10-day clock has not started until the follow-up copy is mailed. */}
      {mailingDue && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          background: C.errL, color: C.err, padding: "3px 8px", borderRadius: 10,
          fontSize: 10, fontWeight: 700, marginBottom: 8, marginLeft: overdueAttempts && claimed ? 6 : 0,
        }}>
          <AlertTriangle size={10} /> Mailing due · §415.20(b)
        </div>
      )}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8 }}>
        {/* [claim-map-unmask-2026-07-27] real servee name pre-claim */}
        <div style={{ fontSize: 16, fontWeight: 800, color: C.deep, letterSpacing: "-0.01em" }}>{dx.name}</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.deep }}>{ssPayLabel(ssJobShownPay(job))/* [job-bundles-v2-2026-10-16] */}</div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
        <Pill bg={statusPill.bg} color={statusPill.color}>{statusPill.label}</Pill>
        <Pill bg={C.goldXL} color="#7a5e1a">{job.type}</Pill>
        <PriorityPill priority={job.priority} />{/* [field-list-priority-2026-10-08] */}
        <BundlePill job={job} />{/* [job-bundles-2026-10-13] */}
        {/* [field-list-attempt-due-2026-10-08] first-attempt due date; red overdue, amber today */}
        {attemptDue && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            background: attemptDue.bg === "transparent" ? C.steelXXL : attemptDue.bg,
            color: attemptDue.color, padding: "3px 9px", borderRadius: 12,
            fontSize: 11, fontWeight: 700,
          }}>
            <Clock size={10} /> {attemptDue.label}
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 10 }}>
        <MapPin size={14} color={C.steel} style={{ marginTop: 1, flexShrink: 0 }} />
        <div>
          {/* [claim-map-unmask-2026-07-27] real street address pre-claim */}
          <div style={{ fontSize: 13, color: C.deep, fontWeight: 500 }}>{dx.streetCity || "—"}</div>
          {/* [distance-fix-2026-09-20] "0 mi away" was the reported bug. The
              word "away" asserts a measured separation, so a Turlock job read
              from Sacramento claimed to be next door. No coordinates now means
              the phrase is simply absent — the county still shows. */}
          <div style={{ fontSize: 11, color: C.steel, marginTop: 1 }}>
            {ssFmtMiles(job.dist) ? ssFmtMiles(job.dist) + " away" : ""}
            {ssFmtMiles(job.dist) && job.county ? " · " : ""}
            {job.county ? `${job.county} County` : ""}
            {!ssFmtMiles(job.dist) && !job.county ? "—" : ""}
          </div>
        </div>
      </div>

      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        paddingTop: 10, borderTop: `1px solid ${C.steelXXL}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: C.steel, fontWeight: 600 }}>
            {attemptCount}/{SS_ATTEMPTS_INCLUDED} attempts{/* [attempts-three-2026-10-06] */}
          </div>
          <div style={{ fontSize: 11, color: C.steel, fontWeight: 600 }}>
            #{job.jobNumber}
          </div>
        </div>
        {/* [field-view-2026-09-07] Field View row action — this REPLACES the 6th
            bottom tab. Rendered as role="button", NOT <button>, because this
            card's root element is already a <button> and nesting one is invalid
            HTML. stopPropagation stops the card's own tap (open job detail) from
            firing too. Ghost styling + ScanFace keep it clearly separate from the
            navy Accept/Claim CTA; no claim request is made from here. */}
        <div
          role="button"
          tabIndex={0}
          aria-label={"Open Field View for " + dx.name}
          onClick={(e) => { e.stopPropagation(); openFieldView(job); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); openFieldView(job); }
          }}
          style={{
            flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 5,
            minHeight: 32, padding: "6px 10px", borderRadius: 10,
            background: C.white, border: `1px solid ${C.steelXL}`,
            fontSize: 11.5, fontWeight: 700, color: C.deep, cursor: "pointer",
          }}
        >
          <ScanFace size={13} color={C.royal} strokeWidth={2.2} /> Field View
        </div>
      </div>
    </button>
  );
}

function Pill({ bg, color, icon: Icon, children }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: bg, color, padding: "3px 9px", borderRadius: 12,
      fontSize: 11, fontWeight: 700,
    }}>
      {Icon && <Icon size={10} />}
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// JOB DETAIL SHEET — Custom 3-tab structure (Brief / Attempts / Chat)
// ═══════════════════════════════════════════════════════════════════════
/* ═══════════════════════════════════════════════════════════════════════
   SUPPORT / ESCALATION TICKETS — field app   [support-tickets-2026-10-08]
   Spec: TICKETING_DESIGN.md §5.1 (field app), §1.1, §3.3 + the Owner decisions.

   What the server gets:
     * "Get help" on the job (JobDetailSheet header, InlineJobDetail header,
       FieldView masthead, Field Command dossier) -> EscalateSheet: category,
       a required short description, optional photo, optional attempt link.
     * A "Support" tab on the job: that job's tickets, the thread (only rows the
       backend lets a server see; internal rows are dropped here too as a second
       guard) and a reply box.
     * After a failed attempt, a small dismissible "Need help with this job?"
       card (TicketPromptHost) that opens EscalateSheet with the attempt linked.

   Owner decisions that shape the client:
     * Every ticket goes to the Work Queue at the highest priority. The app
       sends NO priority; it never pretends a category is "less urgent".
     * A server reassigned off the job loses the thread entirely. Any 404 on a
       ticket or a job's ticket list is therefore a normal state, not an error:
       the ticket disappears with a one-line explanation.
     * Opening a ticket does not change the job. Nothing here touches the job.

   Graceful absence: the whole feature is invisible until a probe of
   GET /api/tickets/mine succeeds. A 404 (older backend) or a disabled flag
   hides every entry point for the session. Safety: the existing Panic flow is
   untouched; a "safety" ticket only points at it.
   ═══════════════════════════════════════════════════════════════════════ */

/* ── Pure helpers (no React, no DOM) — node-checkable between these markers ── */
/* @@SS_TICKET_PURE_BEGIN@@ */
const SS_TICKET_CATEGORIES = [
  { key: "bad_address",      label: "Bad address",       sub: "Can't find / wrong address" },
  { key: "access_issue",     label: "Access issue",      sub: "Gate, guard, no access" },
  { key: "safety",           label: "Safety concern",    sub: "Not an emergency" },
  { key: "document_problem", label: "Document problem",  sub: "Wrong or missing papers" },
  { key: "subject_info",     label: "Subject info",      sub: "Need more on the subject" },
  { key: "client_question",  label: "Client question",   sub: "Dispatch asks the client" },
  { key: "deadline_risk",    label: "Deadline risk",     sub: "Can't make the deadline" },
  { key: "payment",          label: "Payment",           sub: "Pay or payout question" },
  { key: "app_bug",          label: "App problem",       sub: "Something isn't working" },
  { key: "other",            label: "Other",             sub: "Something else" },
];
const SS_TICKET_BODY_MAX = 2000;
const SS_TICKET_BODY_MIN = 3;

function ssTicketCatLabel(key) {
  const c = SS_TICKET_CATEGORIES.find((x) => x.key === key);
  return c ? c.label : (key ? String(key).replace(/_/g, " ") : "Support");
}

/* Status -> what a server needs to know, in words. `open` reads as "waiting"
   because the server is waiting on dispatch, not the other way round. */
function ssTicketStatusMeta(status) {
  const s = String(status || "open").toLowerCase();
  if (s === "open") return { label: "Sent to dispatch", tone: "warn", open: true, canReply: true };
  if (s === "in_progress") return { label: "Dispatch is on it", tone: "info", open: true, canReply: true };
  if (s === "waiting_on_requester") return { label: "Dispatch replied", tone: "ok", open: true, canReply: true, needsYou: true };
  if (s === "waiting_on_client") return { label: "Asked the client", tone: "info", open: true, canReply: true };
  if (s === "resolved") return { label: "Resolved", tone: "ok", open: false, canReply: true };
  if (s === "closed") return { label: "Closed", tone: "steel", open: false, canReply: false };
  if (s === "cancelled") return { label: "Cancelled", tone: "steel", open: false, canReply: false };
  return { label: s.replace(/_/g, " "), tone: "steel", open: true, canReply: true };
}

/* Accepts {tickets:[...]}, {items:[...]}, {rows:[...]} or a bare array. */
function ssTicketListFrom(resp) {
  if (Array.isArray(resp)) return resp;
  if (!resp || typeof resp !== "object") return [];
  for (const k of ["tickets", "items", "rows", "data"]) if (Array.isArray(resp[k])) return resp[k];
  return [];
}
/* Accepts {ticket:{...}} or the ticket itself. */
function ssTicketFrom(resp) {
  if (!resp || typeof resp !== "object") return null;
  if (resp.ticket && typeof resp.ticket === "object") return resp.ticket;
  return resp.id != null ? resp : null;
}
/* Comments from GET /api/tickets/:id, oldest first, never an internal row. */
function ssTicketCommentsFrom(resp) {
  if (!resp || typeof resp !== "object") return [];
  const t = resp.ticket && typeof resp.ticket === "object" ? resp.ticket : {};
  const raw = Array.isArray(resp.comments) ? resp.comments
    : Array.isArray(t.comments) ? t.comments
    : Array.isArray(resp.thread) ? resp.thread : [];
  return raw
    .filter((c) => c && !/^(staff_only|internal)$/i.test(String(c.visibility || "server")))
    .slice()
    .sort((a, b) => ssTicketTs(a) - ssTicketTs(b));
}
function ssTicketTs(x) {
  const v = x && (x.createdAt || x.created_at);
  const n = v ? new Date(v).getTime() : NaN;
  return isFinite(n) ? n : 0;
}
function ssTicketNumber(t) {
  if (!t) return "";
  return t.ticketNumber || t.ticket_number || (t.id != null ? "#" + t.id : "");
}
function ssTicketIsOpen(t) { return ssTicketStatusMeta(t && t.status).open; }
function ssTicketOpenCount(list) { return (list || []).filter(ssTicketIsOpen).length; }

/* 404 is "you can't see this": feature missing, or (owner decision) the job
   was reassigned and the thread went with it. 410 is treated the same. */
function ssTicketIsGone(e) { return !!(e && (e.status === 404 || e.status === 410)); }
/* The feature is switched off for this tenant / backend. */
function ssTicketIsDisabled(e) {
  if (!e) return false;
  const code = String((e.code || (e.body && e.body.code) || "")).toUpperCase();
  return e.status === 404 || e.status === 501 || code === "TICKETS_DISABLED" || code === "FEATURE_DISABLED";
}
/* A network failure (no HTTP status) is the only thing worth one automatic retry. */
function ssTicketIsNetwork(e) { return !!e && !e.status; }

/* Same addressing as /api/jobs/:id/attempts/:attemptId/evidence. */
function ssTicketAttemptRef(attempt, idx) {
  if (attempt && attempt.id != null && String(attempt.id).trim()) return String(attempt.id);
  return "idx-" + idx;
}
/* Last `n` attempts, newest first, as link chips. */
function ssTicketRecentAttempts(rawAttempts, n) {
  const list = Array.isArray(rawAttempts) ? rawAttempts : [];
  const out = [];
  for (let i = list.length - 1; i >= 0 && out.length < (n || 3); i--) {
    const a = list[i] || {};
    const when = a.date || a.ts || a.created_at || a.timestamp || "";
    let whenTxt = "";
    try {
      const d = when ? new Date(when) : null;
      if (d && !isNaN(d.getTime())) whenTxt = d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch (_) {}
    const result = String(a.result || a.status || "attempt").replace(/[-_]/g, " ");
    out.push({ ref: ssTicketAttemptRef(a, i), label: "#" + (i + 1) + " · " + result + (whenTxt ? " · " + whenTxt : "") });
  }
  return out;
}

/* Attempt results (backend codes) that mean "this did not work out". */
const SS_TICKET_FAILED_RESULTS = {
  "not-home": null, "no-answer": null,
  "bad-address": "bad_address", "wrong-address": "bad_address", "moved": "bad_address", "vacant": "bad_address",
  "gate-locked": "access_issue",
  "court-closed": "deadline_risk",
  "other": null,
};
function ssTicketFailedAttempt(result) {
  const r = String(result || "").toLowerCase();
  return Object.prototype.hasOwnProperty.call(SS_TICKET_FAILED_RESULTS, r);
}
/* Category to preselect for a failed result, or "" to let the server choose. */
function ssTicketSeedCategory(result) {
  const r = String(result || "").toLowerCase();
  return SS_TICKET_FAILED_RESULTS[r] || "";
}
/* The new attempt's ref from the POST /api/jobs/:id/attempts response (the
   updated job row, attempts array included). "" when it cannot be told. */
function ssTicketAttemptRefFromResponse(resp) {
  const atts = resp && (Array.isArray(resp.attempts) ? resp.attempts : (resp.job && Array.isArray(resp.job.attempts) ? resp.job.attempts : null));
  if (resp && resp.attemptId) return String(resp.attemptId);
  if (!atts || !atts.length) return "";
  return ssTicketAttemptRef(atts[atts.length - 1], atts.length - 1);
}
/* Path of a create-attempt call, or "" — used by the api hook below. */
function ssTicketAttemptPathJobId(method, path) {
  if (String(method).toUpperCase() !== "POST") return "";
  const m = /^\/api\/jobs\/([^/?#]+)\/attempts\/?(?:\?.*)?$/.exec(String(path || ""));
  return m ? decodeURIComponent(m[1]) : "";
}
function ssTicketValidate({ category, body }) {
  if (!category || !SS_TICKET_CATEGORIES.some((c) => c.key === category)) return "Pick what you need help with.";
  const b = String(body || "").trim();
  if (b.length < SS_TICKET_BODY_MIN) return "Add a short description so dispatch knows what's going on.";
  if (b.length > SS_TICKET_BODY_MAX) return "Keep it under " + SS_TICKET_BODY_MAX + " characters.";
  return "";
}
function ssTicketDraftId() {
  try { if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID(); } catch (_) {}
  return "d-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}
/* @@SS_TICKET_PURE_END@@ */

/* ── API paths (API_CONTRACT.md §2) ────────────────────────────────────── */
const SS_TICKET_API = {
  settings: (jobId) => "/api/tickets/settings" + (jobId != null && jobId !== "" ? "?jobId=" + encodeURIComponent(jobId) : ""),
  jobList: (jobId) => "/api/jobs/" + encodeURIComponent(jobId) + "/tickets",
  create: (jobId) => "/api/jobs/" + encodeURIComponent(jobId) + "/tickets",
  get: (id) => "/api/tickets/" + encodeURIComponent(id),
  comment: (id) => "/api/tickets/" + encodeURIComponent(id) + "/comments",
  status: (id) => "/api/tickets/" + encodeURIComponent(id) + "/status",
  attachment: (id, commentId, idx) => "/api/tickets/" + encodeURIComponent(id) + "/attachments/" + encodeURIComponent(commentId) + "/" + encodeURIComponent(idx),
};

/* ── Feature probe: once per session, shared by every entry point ───────────
   GET /api/tickets/settings (any signed-in user). A 404 means the backend has
   no ticket routes at all -> hide everything. `enabled:false` -> the routes
   exist but new tickets are off for the caller's tenant (reading old ones
   still works). Per-job tenant flags are read in useJobTickets. */
let _ssTicketsState = null;        // null unknown · { present, enabled }
let _ssTicketsProbe = null;        // in-flight promise
let _ssTicketsProbeAt = 0;
const SS_TICKETS_ABSENT = { present: false, enabled: false };
function ssTicketsProbe() {
  if (_ssTicketsState !== null) return Promise.resolve(_ssTicketsState);
  // A network blip must not hide the feature for the whole session; re-probe after 2 min.
  if (_ssTicketsProbe && Date.now() - _ssTicketsProbeAt < 120000) return _ssTicketsProbe;
  _ssTicketsProbeAt = Date.now();
  _ssTicketsProbe = api.get(SS_TICKET_API.settings())
    .then((r) => {
      _ssTicketsState = { present: true, enabled: !(r && r.enabled === false) };
      return _ssTicketsState;
    })
    .catch((e) => {
      if (e && (e.status === 404 || e.status === 501)) { _ssTicketsState = SS_TICKETS_ABSENT; }
      return SS_TICKETS_ABSENT; // network: unknown for now; re-probed after the window
    });
  return _ssTicketsProbe;
}
function useTicketsFeature() {
  const [st, setSt] = useState(_ssTicketsState || SS_TICKETS_ABSENT);
  useEffect(() => {
    let live = true;
    ssTicketsProbe().then((v) => { if (live) setSt(v || SS_TICKETS_ABSENT); });
    return () => { live = false; };
  }, []);
  return st;
}
/* A job the server can raise a ticket on: assigned (active) or recently completed. */
function ssTicketJobEligible(job) {
  return !!(job && job.id != null && (job.status === "active" || job.status === "completed"));
}

/* Tickets on one job. A 404 means "nothing you can see" (reassigned, or the
   backend lacks the route) and is shown as an empty list, never an error. */
function useJobTickets(job, enabled) {
  const jobId = job && job.id != null ? String(job.id) : "";
  const [tickets, setTickets] = useState(null);
  const [err, setErr] = useState("");
  const [canCreate, setCanCreate] = useState(true); // the JOB's tenant flag (network servers)
  const load = React.useCallback(async () => {
    if (!enabled || !jobId) { setTickets([]); return; }
    // Settings is advisory: if it fails, leave creation on and let the create
    // call's 403 TICKETS_DISABLED speak for itself.
    api.get(SS_TICKET_API.settings(jobId))
      .then((s) => setCanCreate(!(s && s.enabled === false)))
      .catch(() => {});
    try {
      const r = await api.get(SS_TICKET_API.jobList(jobId));
      setTickets(ssTicketListFrom(r).slice().sort((a, b) => ssTicketTs(b) - ssTicketTs(a)));
      setErr("");
    } catch (e) {
      if (ssTicketIsGone(e)) { setTickets([]); setErr(""); return; }
      setTickets((prev) => prev || []);
      setErr((e && e.message) || "Could not load support requests");
    }
  }, [jobId, enabled]);
  useEffect(() => { setTickets(null); load(); }, [load]);
  useEffect(() => {
    const on = (ev) => {
      const d = (ev && ev.detail) || {};
      if (!d.jobId || String(d.jobId) === jobId) load();
    };
    window.addEventListener("ss-tickets-changed", on);
    return () => window.removeEventListener("ss-tickets-changed", on);
  }, [load, jobId]);
  return { tickets, err, reload: load, canCreate };
}
function ssTicketsChanged(jobId) {
  try { window.dispatchEvent(new CustomEvent("ss-tickets-changed", { detail: { jobId: jobId != null ? String(jobId) : "" } })); } catch (_) {}
}

function ssTicketFmt(ts) {
  try {
    const d = new Date(ts); if (isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (_) { return ""; }
}
function ssTicketTone(tone) {
  if (tone === "ok") return { bg: C.okL, fg: C.ok };
  if (tone === "warn") return { bg: C.warnL, fg: C.warn };
  if (tone === "info") return { bg: C.infoL, fg: C.info };
  if (tone === "err") return { bg: C.errL, fg: C.err };
  return { bg: C.steelXXL, fg: C.steel };
}
const SS_TICKET_CAT_ICON = {
  bad_address: MapPin, access_issue: Lock, safety: ShieldAlert, document_problem: FileText,
  subject_info: User, client_question: MessageSquare, deadline_risk: Clock, payment: DollarSign,
  app_bug: AlertTriangle, other: HelpCircle,
};

/* Header button: "Help" on the navy job header (JobDetailSheet / InlineJobDetail).
   `badge` = open-ticket count, shown as a small gold dot-number. */
function EscalateButton({ onPress, badge, compact }) {
  return (
    <button type="button" onClick={onPress} aria-label="Escalate or get help on this job" title="Escalate / get help" style={{
      ...iconBtn, width: compact ? iconBtn.width : "auto", padding: compact ? 0 : "0 10px", gap: 6, position: "relative",
      fontSize: 11, fontWeight: 800, color: "#fff", letterSpacing: "0.04em", fontFamily: "inherit",
    }}>
      <LifeBuoy size={compact ? 18 : 16} color="#fff" />{compact ? null : <span>Help</span>}
      {badge > 0 ? (
        <span style={{
          position: "absolute", top: -4, right: -4, minWidth: 16, height: 16, borderRadius: 8, padding: "0 4px",
          background: C.gold, color: "#0a1628", fontSize: 10, fontWeight: 800, lineHeight: "16px", textAlign: "center",
        }}>{badge}</span>
      ) : null}
    </button>
  );
}

/* ── EscalateSheet ──────────────────────────────────────────────────────── */
function EscalateSheet({ job, onClose, seedCategory, seedAttemptRef, onCreated }) {
  const jobId = job && job.id != null ? String(job.id) : "";
  const draftKey = "ss_ticket_draft_" + jobId;
  const restored = React.useMemo(() => {
    if (seedCategory || seedAttemptRef) return null;
    try { const raw = localStorage.getItem(draftKey); return raw ? JSON.parse(raw) : null; } catch (_) { return null; }
  }, [draftKey]);
  const [category, setCategory] = useState(seedCategory || (restored && restored.category) || "");
  const [body, setBody] = useState((restored && restored.body) || "");
  const [attemptRef, setAttemptRef] = useState(seedAttemptRef || "");
  const [photo, setPhoto] = useState(null);       // { key, fileUrl, name, size, mime }
  const [photoBusy, setPhotoBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [safetyOpen, setSafetyOpen] = useState(false);
  const draftId = useRef((restored && restored.clientDraftId) || ssTicketDraftId());
  const fileRef = useRef(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // Keep the draft locally so a dead zone or an accidental close does not lose it.
  useEffect(() => {
    if (!jobId) return;
    try {
      if (!category && !body.trim()) localStorage.removeItem(draftKey);
      else localStorage.setItem(draftKey, JSON.stringify({ category, body, clientDraftId: draftId.current }));
    } catch (_) {}
  }, [category, body, draftKey, jobId]);

  const attempts = React.useMemo(() => {
    const raw = Array.isArray(job && job.rawAttempts) ? job.rawAttempts
      : (Array.isArray(job && job._raw && job._raw.attempts) ? job._raw.attempts : []);
    const list = ssTicketRecentAttempts(raw, 3);
    if (seedAttemptRef && !list.some((a) => a.ref === seedAttemptRef)) list.unshift({ ref: seedAttemptRef, label: "The attempt you just logged" });
    return list;
  }, [job, seedAttemptRef]);

  async function uploadBytes(bytes, mime, fileName) {
    const rec = await ssUploadEvidence(api, { bytes, mime, fileName, context: "support-ticket", jobId: job.id });
    return { key: rec.key, fileUrl: rec.fileUrl || null, name: fileName, size: bytes.byteLength, mime };
  }
  async function onPick(ev) {
    const f = ev && ev.target && ev.target.files && ev.target.files[0];
    if (ev && ev.target) ev.target.value = "";
    if (!f) return;
    setPhotoBusy(true); setErr("");
    try {
      const bytes = await ssFileToBytes(f);
      const mime = ssImageMimeOf(f, bytes);
      if (!/^image\//.test(mime)) throw new Error("That file is not a photo.");
      const rec = await uploadBytes(bytes, mime, "ticket-" + job.id + "-" + Date.now() + "." + ssExtForMime(mime));
      if (alive.current) setPhoto(rec);
    } catch (e) {
      if (alive.current) setErr("Photo not attached: " + ((e && e.message) || "upload failed") + ". You can still send without it.");
    } finally { if (alive.current) setPhotoBusy(false); }
  }
  async function addPhoto() {
    if (photoBusy) return;
    const Cam = ssPlugin("Camera");
    if (!Cam) { if (fileRef.current) fileRef.current.click(); return; }
    setPhotoBusy(true); setErr("");
    try {
      const p = await Cam.getPhoto({
        quality: 80, resultType: "base64", source: "PROMPT", saveToGallery: false, correctOrientation: true,
        promptLabelHeader: "Add a photo for dispatch", promptLabelPhoto: "Choose from library", promptLabelPicture: "Take photo",
      });
      const b64 = (p && (p.base64String || p.base64)) || "";
      if (!b64) return;
      const bytes = ssB64ToBytes(b64);
      const fmt = String((p && p.format) || "jpeg").toLowerCase();
      const mime = ssSniffImageMime(bytes, fmt === "heic" || fmt === "heif" ? "image/" + fmt : fmt === "png" ? "image/png" : "image/jpeg");
      const rec = await uploadBytes(bytes, mime, "ticket-" + job.id + "-" + Date.now() + "." + ssExtForMime(mime));
      if (alive.current) setPhoto(rec);
    } catch (e) {
      const m = (e && e.message) ? String(e.message) : "";
      if (!/cancel/i.test(m) && alive.current) setErr("Photo not attached: " + (m || "upload failed") + ". You can still send without it.");
    } finally { if (alive.current) setPhotoBusy(false); }
  }

  async function submit() {
    if (busy || photoBusy) return;
    const v = ssTicketValidate({ category, body });
    if (v) { setErr(v); return; }
    setBusy(true); setErr("");
    // No priority, no visibility: the backend sets priority 1 and `server`
    // visibility for a server-opened ticket (API_CONTRACT §0, §2.3).
    const payload = { category, body: body.trim(), clientDraftId: draftId.current };
    if (attemptRef) payload.attemptRef = attemptRef;
    if (photo && photo.key) {
      payload.attachments = [{ s3Key: photo.key, name: photo.name, size: photo.size, mime: photo.mime, capturedAt: new Date().toISOString() }];
    }
    const send = () => api.post(SS_TICKET_API.create(jobId), payload);
    try {
      let r;
      try { r = await send(); }
      catch (e) {
        if (!ssTicketIsNetwork(e)) throw e;
        await new Promise((res) => setTimeout(res, 1500));
        r = await send(); // idempotent on clientDraftId
      }
      try { localStorage.removeItem(draftKey); } catch (_) {}
      const t = ssTicketFrom(r);
      ssToast("ok", "Sent to dispatch" + (t && ssTicketNumber(t) ? " · " + ssTicketNumber(t) : ""));
      ssTicketsChanged(jobId);
      try { onCreated && onCreated(t); } catch (_) {}
      onClose();
    } catch (e) {
      if (!alive.current) return;
      const code = String((e && (e.code || (e.body && e.body.code) || (e.body && e.body.error))) || "");
      if (ssTicketIsGone(e)) {
        setErr("This job is no longer assigned to you, so a request can't be opened on it. Call support if you still need help.");
      } else if (/TICKETS_DISABLED/i.test(code)) {
        setErr("Support requests are turned off for this company. Call support instead.");
      } else if (/invalid_attempt_ref/i.test(code)) {
        setAttemptRef("");
        setErr("That attempt couldn't be linked. It's been unlinked — tap Send again.");
      } else if (/BAD_ATTACHMENT_KEY/i.test(code)) {
        setPhoto(null);
        setErr("The photo couldn't be attached. It's been removed — tap Send again, or add it again.");
      } else {
        setErr((e && e.body && e.body.error) || (e && e.message) || "Could not send. Your note is saved — try again.");
      }
    } finally { if (alive.current) setBusy(false); }
  }

  const label = { fontSize: 10, fontWeight: 800, color: C.steel, letterSpacing: "0.1em", margin: "14px 0 8px" };
  const chip = (on) => ({
    padding: "8px 11px", borderRadius: 18, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
    border: `1.5px solid ${on ? C.deep : C.steelXL}`, background: on ? C.deep : C.white, color: on ? C.white : C.deep,
    maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  });
  const v = ssTicketValidate({ category, body });

  return (
    <Sheet onClose={busy ? () => {} : onClose}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: `1px solid ${C.steelXL}`, background: C.white }}>
        <button type="button" onClick={onClose} disabled={busy} style={iconBtnLight} aria-label="Close"><X size={18} color={C.deep} /></button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.deep }}>Get help with this job</div>
          <div style={{ fontSize: 11, color: C.steel }}>Job #{job && job.jobNumber} · goes straight to dispatch</div>
        </div>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "4px 16px 110px" }}>
        <div style={label}>WHAT DO YOU NEED HELP WITH?</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {SS_TICKET_CATEGORIES.map((c) => {
            const on = category === c.key;
            const Icon = SS_TICKET_CAT_ICON[c.key] || HelpCircle;
            const danger = c.key === "safety";
            return (
              <button key={c.key} type="button" onClick={() => { setCategory(c.key); setErr(""); }} aria-pressed={on} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "11px 10px", borderRadius: 12, minHeight: 56,
                textAlign: "left", cursor: "pointer", fontFamily: "inherit",
                background: on ? (danger ? C.errL : C.goldXL) : C.white,
                border: `2px solid ${on ? (danger ? C.err : C.gold) : C.steelXL}`,
              }}>
                <Icon size={18} color={danger ? C.err : C.deep} style={{ flexShrink: 0 }} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13, fontWeight: 800, color: C.deep }}>{c.label}</span>
                  <span style={{ display: "block", fontSize: 10, color: C.steel, lineHeight: 1.3 }}>{c.sub}</span>
                </span>
              </button>
            );
          })}
        </div>

        {category === "safety" && (
          <div role="alert" style={{ marginTop: 12, padding: "12px 12px", borderRadius: 12, background: C.errL, border: `1.5px solid ${C.err}` }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <Siren size={20} color={C.err} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 13, color: C.err, fontWeight: 700, lineHeight: 1.45 }}>
                In danger right now? Don't wait for a reply — use Panic. It alerts the owner and dispatch immediately with your location.
              </div>
            </div>
            <button type="button" onClick={() => setSafetyOpen(true)} style={{
              marginTop: 10, width: "100%", minHeight: 46, borderRadius: 10, border: "none", background: C.err, color: "#fff",
              fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}><ShieldAlert size={18} color="#fff" /> Open Panic / Safety</button>
            <div style={{ fontSize: 11, color: C.err, marginTop: 8, lineHeight: 1.4 }}>
              Use this request for things dispatch should know before the next attempt (aggressive dog, threats on a prior visit).
            </div>
          </div>
        )}

        <div style={label}>DESCRIBE IT (REQUIRED)</div>
        <textarea value={body} onChange={(e) => { setBody(e.target.value); if (err) setErr(""); }} rows={4} maxLength={SS_TICKET_BODY_MAX}
          placeholder="What happened, and what do you need from dispatch?"
          style={{
            width: "100%", padding: "10px 11px", border: `1px solid ${C.steelXL}`, borderRadius: 10,
            fontSize: 14, color: C.ink, outline: "none", fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", background: C.bgW,
          }} />
        <div style={{ fontSize: 10, color: C.steel, marginTop: 4 }}>{body.length}/{SS_TICKET_BODY_MAX} · dispatch sees this; the client does not</div>

        {attempts.length > 0 && (
          <>
            <div style={label}>LINK AN ATTEMPT (OPTIONAL)</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              <button type="button" onClick={() => setAttemptRef("")} style={chip(!attemptRef)}>None</button>
              {attempts.map((a) => (
                <button key={a.ref} type="button" onClick={() => setAttemptRef(a.ref)} style={chip(attemptRef === a.ref)}>{a.label}</button>
              ))}
            </div>
          </>
        )}

        <div style={label}>PHOTO (OPTIONAL)</div>
        <input ref={fileRef} type="file" accept="image/*,.heic,.heif" capture="environment" onChange={onPick} style={{ display: "none" }} />
        {photo ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", borderRadius: 10, background: C.white, border: `1px solid ${C.steelXL}` }}>
            <ImageIcon size={16} color={C.ok} />
            <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: C.deep }}>Photo attached</span>
            <button type="button" onClick={() => setPhoto(null)} style={{ ...iconBtnLight, width: 28, height: 28 }} aria-label="Remove photo"><X size={14} color={C.steel} /></button>
          </div>
        ) : (
          <button type="button" onClick={addPhoto} disabled={photoBusy} style={{
            display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 13px", borderRadius: 10,
            background: C.white, border: `1.5px solid ${C.steelXL}`, color: C.deep, fontSize: 12, fontWeight: 800,
            cursor: photoBusy ? "wait" : "pointer", fontFamily: "inherit",
          }}>{photoBusy ? <SpinnerDot color={C.deep} /> : <Camera size={14} />} {photoBusy ? "Uploading…" : "Add photo"}</button>
        )}
        <div style={{ fontSize: 10, color: C.steel, marginTop: 6, lineHeight: 1.4 }}>Door, gate, documents or the street sign. Never photograph the person served.</div>

        {err && <div role="alert" style={{ fontSize: 12, color: C.err, background: C.errL, padding: "9px 11px", borderRadius: 10, marginTop: 12, lineHeight: 1.45 }}>{err}</div>}
      </div>

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "10px 14px 14px", background: C.white, borderTop: `1px solid ${C.steelXL}` }}>
        <button type="button" onClick={submit} disabled={busy || photoBusy} style={{
          width: "100%", minHeight: 52, borderRadius: 14, border: "none", fontSize: 15, fontWeight: 800, fontFamily: "inherit",
          background: busy || photoBusy || v ? C.steelL : C.deep, color: C.white, cursor: busy || photoBusy ? "wait" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>{busy ? <SpinnerDot /> : <Send size={18} />} {busy ? "Sending…" : "Send to dispatch"}</button>
      </div>
      {safetyOpen && <SafetySheet onClose={() => setSafetyOpen(false)} />}
    </Sheet>
  );
}

/* ── One ticket: header row + (expanded) thread and reply ──────────────── */
function TicketCard({ ticket, onGone, autoOpen }) {
  const { user } = React.useContext(AppCtx);
  const myEmail = String((user && user.email) || "").toLowerCase();
  const [open, setOpen] = useState(!!autoOpen);
  /* [ticket-link-2026-10-08] opened from a notification: expand and bring into view */
  const cardRef = useRef(null);
  useEffect(() => {
    if (!autoOpen) return;
    setOpen(true);
    const id = setTimeout(() => { try { cardRef.current && cardRef.current.scrollIntoView({ block: "start", behavior: "smooth" }); } catch (_) {} }, 120);
    return () => clearTimeout(id);
  }, [autoOpen]);
  const [data, setData] = useState(null);       // { ticket, comments }
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState("");
  const t = (data && data.ticket) || ticket;
  const meta = ssTicketStatusMeta(t.status);
  const tone = ssTicketTone(meta.tone);
  // The backend says whether this caller may comment; fall back to the status table.
  const canReply = t.canComment === false ? false : meta.canReply;
  const canWithdraw = Array.isArray(t.allowedStatusChanges) && t.allowedStatusChanges.includes("cancelled");
  const jobId = t.jobId != null ? t.jobId : t.job_id;

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(SS_TICKET_API.get(ticket.id));
      setData({ ticket: ssTicketFrom(r) || ticket, comments: ssTicketCommentsFrom(r) });
      setErr("");
    } catch (e) {
      if (ssTicketIsGone(e)) { onGone && onGone(ticket.id); return; }
      setErr((e && e.message) || "Could not load this request");
    } finally { setLoading(false); }
  }, [ticket.id]);
  useEffect(() => { if (open && !data) load(); }, [open]);

  function failed(e, fallback) {
    if (ssTicketIsGone(e)) { onGone && onGone(ticket.id); return; }
    if (e && e.status === 409) { setErr("This request is closed. Open a new one from this tab if you still need help."); load(); return; }
    setErr((e && e.body && e.body.error) || (e && e.message) || fallback);
  }
  async function reply() {
    const b = draft.trim();
    if (!b || busy) return;
    if (b.length > SS_TICKET_BODY_MAX) { setErr("Keep it under " + SS_TICKET_BODY_MAX + " characters."); return; }
    setBusy(true); setErr("");
    try {
      // A server may only post `server` visibility (API_CONTRACT §0).
      await api.post(SS_TICKET_API.comment(ticket.id), { body: b, visibility: "server" });
      setDraft("");
      await load();
      ssTicketsChanged(jobId);
    } catch (e) { failed(e, "Could not send your reply"); }
    finally { setBusy(false); }
  }
  async function withdraw() {
    if (busy) return;
    try { if (!window.confirm("Withdraw this request? Dispatch will see it as cancelled.")) return; } catch (_) {}
    setBusy(true); setErr("");
    try {
      await api.post(SS_TICKET_API.status(ticket.id), { status: "cancelled", note: "Withdrawn by the server" });
      await load();
      ssTicketsChanged(jobId);
    } catch (e) { failed(e, "Could not withdraw this request"); }
    finally { setBusy(false); }
  }
  async function viewAttachment(c, a, i) {
    const idx = a && a.idx != null ? a.idx : i;
    const k = c.id + ":" + idx;
    if (viewing) return;
    setViewing(k);
    try {
      const r = await api.get(SS_TICKET_API.attachment(ticket.id, c.id, idx));
      if (!r || !r.url) throw new Error("No link returned");
      const isImg = /^image\//i.test(String(r.mime || a.mime || "")) && !/svg/i.test(String(r.mime || a.mime || ""));
      ssOpenDocViewer({ title: r.name || a.name || "Attachment", url: r.url, kind: isImg ? "image" : "pdf", externalUrl: r.url });
    } catch (e) {
      if (ssTicketIsGone(e)) ssToast("err", "That attachment is no longer available.");
      else ssToast("err", (e && e.message) || "Could not open the attachment");
    } finally { setViewing(""); }
  }

  return (
    <div ref={cardRef} style={{ background: C.white, borderRadius: 14, border: `1px solid ${meta.needsYou ? C.ok : C.steelXL}`, marginBottom: 10, overflow: "hidden" }}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "12px 12px", background: "none", border: "none",
        textAlign: "left", cursor: "pointer", fontFamily: "inherit",
      }}>
        {React.createElement(SS_TICKET_CAT_ICON[t.category] || HelpCircle, { size: 18, color: t.category === "safety" ? C.err : C.deep, style: { flexShrink: 0 } })}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.deep, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {ssTicketCatLabel(t.category)} <span style={{ color: C.steel, fontWeight: 700 }}>{ssTicketNumber(t)}</span>
          </div>
          <div style={{ fontSize: 11, color: C.steel }}>
            {ssTicketFmt(t.lastActivityAt || t.createdAt || t.created_at)}
            {t.commentCount > 1 ? " · " + t.commentCount + " messages" : ""}
          </div>
        </div>
        <span style={{ fontSize: 10, fontWeight: 800, padding: "4px 8px", borderRadius: 10, background: tone.bg, color: tone.fg, whiteSpace: "nowrap" }}>{meta.label}</span>
        {open ? <ChevronUp size={16} color={C.steel} /> : <ChevronDown size={16} color={C.steel} />}
      </button>
      {open && (
        <div style={{ padding: "0 12px 12px", borderTop: `1px solid ${C.steelXXL}` }}>
          {loading && !data && <div style={{ padding: "12px 0", fontSize: 12, color: C.steel, display: "flex", gap: 8, alignItems: "center" }}><SpinnerDot color={C.steel} /> Loading…</div>}
          {data && data.comments.map((c) => {
            const system = c.kind === "status_change" || c.kind === "system";
            const author = String(c.authorEmail || c.author_email || "").toLowerCase();
            const mine = !!myEmail && author === myEmail;
            const when = ssTicketFmt(c.createdAt || c.created_at);
            if (system) {
              return <div key={c.id} style={{ fontSize: 11, color: C.steel, textAlign: "center", margin: "10px 0", lineHeight: 1.4 }}>{c.body} · {when}</div>;
            }
            const atts = Array.isArray(c.attachments) ? c.attachments : [];
            return (
              <div key={c.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginTop: 10 }}>
                <div style={{ maxWidth: "85%", padding: "8px 11px", borderRadius: 12, background: mine ? C.goldXL : C.infoL, border: `1px solid ${mine ? C.goldL : C.steelXL}` }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: C.steel, marginBottom: 2 }}>
                    {mine ? "You" : (c.authorName || c.author_name || "Dispatch")} · {when}
                  </div>
                  {c.body ? <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{c.body}</div> : null}
                  {atts.map((a, i) => {
                    const k = c.id + ":" + (a && a.idx != null ? a.idx : i);
                    return (
                      <button key={k} type="button" onClick={() => viewAttachment(c, a || {}, i)} style={{
                        marginTop: 6, display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 8,
                        background: C.white, border: `1px solid ${C.steelXL}`, color: C.deep, fontSize: 11, fontWeight: 700,
                        cursor: "pointer", fontFamily: "inherit", maxWidth: "100%",
                      }}>
                        {viewing === k ? <SpinnerDot color={C.deep} /> : <Paperclip size={12} />}
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(a && a.name) || "Attachment"}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {err && <div style={{ fontSize: 11, color: C.err, background: C.errL, padding: "8px 10px", borderRadius: 8, marginTop: 10 }}>{err}</div>}
          {canReply ? (
            <div style={{ marginTop: 12 }}>
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} maxLength={SS_TICKET_BODY_MAX}
                placeholder={t.status === "resolved" ? "Reply to reopen this request…" : "Reply to dispatch…"}
                style={{
                  width: "100%", padding: "9px 10px", border: `1px solid ${C.steelXL}`, borderRadius: 8,
                  fontSize: 13, color: C.ink, outline: "none", fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", background: C.bgW,
                }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, gap: 8 }}>
                {canWithdraw ? (
                  <button type="button" onClick={withdraw} disabled={busy} style={{
                    background: "none", border: "none", color: C.steel, fontSize: 11, fontWeight: 700, textDecoration: "underline",
                    cursor: "pointer", fontFamily: "inherit", padding: 0,
                  }}>Withdraw request</button>
                ) : <span />}
                <button type="button" onClick={reply} disabled={busy || !draft.trim()} style={{
                  padding: "9px 14px", background: busy || !draft.trim() ? C.steelL : C.deep, color: C.white,
                  border: "none", borderRadius: 10, fontSize: 12, fontWeight: 800, fontFamily: "inherit",
                  cursor: busy || !draft.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6,
                }}>{busy ? <SpinnerDot /> : <Send size={14} />} {busy ? "Sending…" : "Reply"}</button>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: C.steel, marginTop: 12 }}>This request is {meta.label.toLowerCase()}. Open a new one if you still need help.</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Support tab (job sheet) ───────────────────────────────────────────── */
function SupportTab({ job, tickets, err, reload, onEscalate, canCreate, focusTicketId }) {
  const [gone, setGone] = useState([]);
  const list = (tickets || []).filter((t) => t && !gone.includes(t.id));
  function onGone(id) {
    setGone((g) => g.concat([id]));
    ssToast("err", "That request is no longer available to you — the job may have been reassigned.");
  }
  return (
    <div style={{ padding: "16px 14px" }}>
      <div style={{ background: C.white, borderRadius: 14, padding: 14, marginBottom: 12, border: `1px solid ${C.steelXL}` }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.deep, marginBottom: 4 }}>Stuck on this job?</div>
        <div style={{ fontSize: 12, color: C.steel, lineHeight: 1.5, marginBottom: 10 }}>
          Send a request to dispatch — bad address, no access, document problems, deadline risk. Replies show up here.
        </div>
        {!canCreate ? (
          <div style={{ fontSize: 12, color: C.steel }}>New requests are turned off for this company. Call support if you need help.</div>
        ) : ssTicketJobEligible(job) ? (
          <button type="button" onClick={onEscalate} style={{
            width: "100%", minHeight: 46, borderRadius: 12, border: "none", background: C.deep, color: C.white,
            fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}><LifeBuoy size={18} /> Escalate / Get help</button>
        ) : (
          <div style={{ fontSize: 12, color: C.steel }}>Requests open once this job is assigned to you.</div>
        )}
      </div>
      {err && <div style={{ fontSize: 11, color: C.err, background: C.errL, padding: "8px 10px", borderRadius: 8, marginBottom: 10 }}>{err} <button type="button" onClick={reload} style={{ background: "none", border: "none", color: C.err, fontWeight: 800, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>Retry</button></div>}
      {tickets === null && (
        <SkeletonGroup label="Loading support requests">
          {[0, 1].map((i) => (
            <div key={i} style={{ background: C.white, borderRadius: 14, padding: 14, marginBottom: 10, border: `1px solid ${C.steelXL}` }}>
              <SkeletonLine width="45%" height={10} />
              <SkeletonLine width="80%" height={12} style={{ marginBottom: 0 }} />
            </div>
          ))}
        </SkeletonGroup>
      )}
      {tickets !== null && list.length === 0 && !err && (
        <div style={{ background: C.white, borderRadius: 14, padding: 22, textAlign: "center", border: `1.5px dashed ${C.steelL}` }}>
          <LifeBuoy size={24} color={C.steelL} />
          <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, marginTop: 8 }}>No support requests on this job</div>
        </div>
      )}
      {list.map((t) => <TicketCard key={t.id} ticket={t} onGone={onGone} autoOpen={!!focusTicketId && String(t.id) === String(focusTicketId)} />)}
      {/* [ticket-link-2026-10-08] linked ticket not in this job's list (yet) — still show its thread */}
      {focusTicketId && tickets !== null && !list.some((t) => String(t.id) === String(focusTicketId)) && !gone.includes(focusTicketId) ? (
        <TicketCard key={"focus-" + focusTicketId} ticket={{ id: focusTicketId }} onGone={onGone} autoOpen />
      ) : null}
    </div>
  );
}

/* [ticket-link-2026-10-08] Ticket deep links. A "ticket:<id>" notification (bell or push)
   fires ss-open-ticket { ticketId, jobId }. TicketLinkHost resolves the job (from the
   event, else GET /api/tickets/:id), parks { jobId, ticketId } in _ssPendingTicketFocus
   and opens the job through ss-open-job. The job surface (JobDetailSheet /
   InlineJobDetail) picks the pending focus up via useSupportDeepLink: Support tab,
   that ticket expanded. A ticket with no job (account-level request) opens in its own
   sheet here, since the field app has no job-less ticket list. */
let _ssPendingTicketFocus = null; // { jobId, ticketId, at }
function useSupportDeepLink(job, setTab) {
  const jobId = job && job.id != null ? String(job.id) : "";
  const [focusId, setFocusId] = useState("");
  useEffect(() => {
    const apply = () => {
      const p = _ssPendingTicketFocus;
      if (!p || !jobId) return;
      if (Date.now() - p.at > 60000) { _ssPendingTicketFocus = null; return; }
      const raw = job && job._raw;
      const ids = [jobId, job && job.jobNumber, raw && raw.id, raw && raw.job_number].filter((x) => x != null).map(String);
      if (!ids.includes(String(p.jobId))) return;
      _ssPendingTicketFocus = null;
      setTab("support");
      setFocusId(String(p.ticketId || ""));
    };
    apply();
    window.addEventListener("ss-ticket-focus", apply);
    return () => window.removeEventListener("ss-ticket-focus", apply);
  }, [jobId]);
  return focusId;
}
function TicketLinkHost() {
  const [solo, setSolo] = useState(null); // { id } — a ticket with no job
  useEffect(() => {
    async function on(ev) {
      const d = (ev && ev.detail) || {};
      const ticketId = String((typeof d === "object" ? d.ticketId : d) || "").trim();
      if (!ticketId) return;
      let jobId = typeof d === "object" && d.jobId ? String(d.jobId) : "";
      if (!jobId) {
        try {
          const t = ssTicketFrom(await api.get(SS_TICKET_API.get(ticketId)));
          const j = t && (t.jobId != null ? t.jobId : t.job_id);
          if (j != null && String(j) !== "") jobId = String(j);
        } catch (e) {
          if (ssTicketIsGone(e)) { ssToast("err", "That support request is no longer available to you."); return; }
          // Network trouble: still show the thread sheet; TicketCard reports its own load error.
        }
      }
      if (jobId) {
        _ssPendingTicketFocus = { jobId, ticketId, at: Date.now() };
        window.dispatchEvent(new CustomEvent("ss-open-job", { detail: jobId }));
        // Already-open job surface for this job: apply now (a fresh mount applies on its own).
        setTimeout(() => { try { window.dispatchEvent(new CustomEvent("ss-ticket-focus")); } catch (_) {} }, 0);
        return;
      }
      setSolo({ id: ticketId });
    }
    window.addEventListener("ss-open-ticket", on);
    return () => window.removeEventListener("ss-open-ticket", on);
  }, []);
  if (!solo) return null;
  return (
    <Sheet onClose={() => setSolo(null)}>
      <div style={{ padding: "16px 14px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <LifeBuoy size={18} color={C.deep} />
          <div style={{ flex: 1, fontSize: 15, fontWeight: 800, color: C.deep }}>Support request</div>
          <button type="button" onClick={() => setSolo(null)} aria-label="Close" style={{ ...iconBtnLight, width: 30, height: 30 }}><X size={15} color={C.steel} /></button>
        </div>
        <TicketCard ticket={{ id: solo.id }} autoOpen onGone={() => { setSolo(null); ssToast("err", "That support request is no longer available to you."); }} />
      </div>
    </Sheet>
  );
}

/* Shared state for a job surface: feature flag, the job's tickets, the sheet. */
/* `present`  -> show the Support tab (reading old tickets works even when off).
   `canCreate` -> show the Help / Escalate entry points. */
function useJobSupport(job) {
  const feat = useTicketsFeature();
  const present = !!feat.present && ssTicketJobEligible(job);
  const jt = useJobTickets(job, present);
  const canCreate = present && jt.canCreate;
  const [escalate, setEscalate] = useState(null); // null | { seedCategory, seedAttemptRef }
  return {
    present, canCreate,
    tickets: jt.tickets, err: jt.err, reload: jt.reload,
    openCount: ssTicketOpenCount(jt.tickets),
    needsYou: (jt.tickets || []).some((t) => ssTicketStatusMeta(t.status).needsYou),
    escalate, openEscalate: (seed) => setEscalate(seed || {}), closeEscalate: () => setEscalate(null),
  };
}

/* ── After a failed attempt: a light, dismissible prompt ───────────────────
   Fed by api.req (ssTicketNoteAttempt) so it covers the attempt sheet, the
   work-log wizard and Field Command one-taps without editing any of them. */
function ssTicketNoteAttempt(method, path, reqBody, resp) {
  try {
    const jobId = ssTicketAttemptPathJobId(method, path);
    if (!jobId) return;
    const result = reqBody && reqBody.result;
    if (!ssTicketFailedAttempt(result)) return;
    if (resp && resp.duplicate) return;
    window.dispatchEvent(new CustomEvent("ss-attempt-logged", {
      detail: { jobId, result: String(result), attemptRef: ssTicketAttemptRefFromResponse(resp) },
    }));
  } catch (_) { /* never let this affect the attempt save */ }
}
function TicketPromptHost() {
  const { jobs } = React.useContext(AppCtx);
  const [prompt, setPrompt] = useState(null);     // { jobId, result, attemptRef }
  const [sheet, setSheet] = useState(null);
  const timer = useRef(null);
  useEffect(() => {
    const on = (ev) => {
      const d = (ev && ev.detail) || {};
      if (!d.jobId) return;
      try { if (typeof document !== "undefined" && document.hidden) return; } catch (_) {}
      ssTicketsProbe().then((st) => {
        if (!st || !st.present || !st.enabled) return;
        // Let the attempt sheet finish closing first.
        setTimeout(() => {
          setPrompt(d);
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => setPrompt(null), 12000);
        }, 700);
      });
    };
    window.addEventListener("ss-attempt-logged", on);
    return () => { window.removeEventListener("ss-attempt-logged", on); if (timer.current) clearTimeout(timer.current); };
  }, []);
  const job = (prompt || sheet) ? ((jobs || []).find((j) => String(j.id) === String((sheet || prompt).jobId)) || null) : null;
  return (
    <>
      {prompt && !sheet && job && (
        <div role="status" style={{
          position: "absolute", left: 12, right: 12, bottom: "calc(env(safe-area-inset-bottom) + 92px)", zIndex: 260,
          maxWidth: 460, margin: "0 auto", background: C.white, border: `1.5px solid ${C.gold}`, borderRadius: 14,
          boxShadow: "0 8px 24px rgba(10,22,40,0.25)", padding: "10px 10px 10px 14px", display: "flex", alignItems: "center", gap: 10,
        }}>
          <LifeBuoy size={20} color={C.deep} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: C.deep }}>Need help with this job?</div>
          <button type="button" onClick={() => { setSheet(prompt); setPrompt(null); }} style={{
            padding: "8px 12px", borderRadius: 10, border: "none", background: C.deep, color: C.white,
            fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
          }}>Get help</button>
          <button type="button" onClick={() => setPrompt(null)} aria-label="Dismiss" style={{ ...iconBtnLight, width: 30, height: 30 }}><X size={15} color={C.steel} /></button>
        </div>
      )}
      {sheet && job && (
        <EscalateSheet job={job} seedCategory={ssTicketSeedCategory(sheet.result)} seedAttemptRef={sheet.attemptRef || ""}
          onClose={() => setSheet(null)} />
      )}
    </>
  );
}

/* [door-tags-server-2026-10-22] ── DOOR TAG ─────────────────────────────────
   A door tag is a printed notice the server posts on the door after an UNSUCCESSFUL
   attempt on a job that carries the door-tag add-on. The backend prints it with the
   job's documents; GET /api/jobs/:id/door-tag.pdf serves it on its own.
   Job fields arrive snake_case on the raw row (job._raw on a normalized job):
     door_tag_enabled  boolean  (explicit false wins over the add-on)
     door_tag_ref      6-digit reference printed on the tag
     add_ons           array that may contain 'door-tag'
   After a non-served attempt is saved (any capture path: attempt sheet, work-log
   wizard, Field Command — fed by api.req), DoorTagPromptHost asks "Did you post the
   door tag?" and POSTs /api/server/jobs/:id/door-tag-posted. The attempt itself is
   already saved; this follow-up can never undo or block it. No prices here. */
const SS_DOOR_TAG_REASONS = [
  "Gated / no door access",
  "Apartment lobby / controlled building",
  "Unsafe",
  "Client said no notice",
  "Business — handed to staff",
  "Vacant / wrong address",
  "No tag printed",
  "Other",
];
function ssDoorTagAddOns(row) {
  let a = row && (row.add_ons != null ? row.add_ons : row.addOns);
  if (typeof a === "string") { try { a = JSON.parse(a); } catch (_) { a = a.split(","); } }
  if (!Array.isArray(a)) return [];
  return a.map((x) => String((x && typeof x === "object") ? (x.key || x.id || x.slug || x.code || "") : (x || "")).trim().toLowerCase());
}
/* { ref } when the job needs a door tag, else null. Reads the normalized job and its raw row. */
function ssDoorTagInfo(job) {
  if (!job || typeof job !== "object") return null;
  const rows = [job, job._raw].filter((r) => r && typeof r === "object");
  let flag;
  let ref = "";
  let addOn = false;
  for (const r of rows) {
    const f = r.door_tag_enabled != null ? r.door_tag_enabled : r.doorTagEnabled;
    if (flag === undefined && f != null) flag = f;
    if (!ref) ref = String(r.door_tag_ref || r.doorTagRef || "").trim();
    if (!addOn && ssDoorTagAddOns(r).includes("door-tag")) addOn = true;
  }
  const on = flag === true || flag === "true" || (addOn && flag !== false && flag !== "false");
  return on ? { ref } : null;
}
/* Served outcomes never need a door tag. Everything else (no answer, not home, refused,
   bad address, gated, vacant, ...) is an unsuccessful attempt. */
function ssDoorTagIsServedResult(result) {
  const r = String(result || "").toLowerCase();
  return r === "served" || r === "sub-served" || r.indexOf("served-") === 0;
}
async function ssOpenDoorTagPdf(job) {
  const label = "Door tag · #" + (job.jobNumber || job.id);
  const blob = await ssFetchPdfBlob("/api/jobs/" + encodeURIComponent(job.id) + "/door-tag.pdf");
  /* Same viewer the signed-proofs PDF uses on both targets: auto-print on web, the
     viewer's own Print button on native. */
  ssOpenDocViewer({ title: label, blob, kind: "pdf", print: !ssIsNative(), filename: "door_tag_" + (job.jobNumber || job.id) + ".pdf" });
}
/* Called by api.req after every successful request; only acts on POST .../attempts. */
function ssDoorTagNoteAttempt(method, path, reqBody, resp) {
  try {
    const jobId = ssTicketAttemptPathJobId(method, path);
    if (!jobId) return;
    const result = reqBody && reqBody.result;
    if (!result || ssDoorTagIsServedResult(result)) return;
    const row = resp && typeof resp === "object" ? (resp.job && typeof resp.job === "object" ? resp.job : resp) : null;
    const atts = row && Array.isArray(row.attempts) ? row.attempts : null;
    let attemptIndex = null;
    if (resp && resp.attemptIndex != null && isFinite(Number(resp.attemptIndex))) attemptIndex = Number(resp.attemptIndex);
    else if (atts && atts.length) attemptIndex = atts.length - 1;
    window.dispatchEvent(new CustomEvent("ss-door-tag-check", {
      detail: { jobId: String(jobId), result: String(result), attemptIndex, row },
    }));
  } catch (_) { /* never let this affect the attempt save */ }
}
/* One GPS fix, same plugin/options as the attempt sheet. Resolves null on failure —
   never a substituted coordinate. */
function ssDoorTagGps() {
  return new Promise((resolve) => {
    const Geo = ssPlugin("Geolocation");
    if (Geo) {
      Geo.getCurrentPosition({ enableHighAccuracy: true, timeout: 8000 })
        .then((p) => resolve({ lat: Number(p.coords.latitude), lng: Number(p.coords.longitude), accuracy: Number(p.coords.accuracy || 0) }))
        .catch(() => resolve(null));
      return;
    }
    const geo = (typeof navigator !== "undefined" && navigator.geolocation) || null;
    if (!geo || typeof geo.getCurrentPosition !== "function") { resolve(null); return; }
    geo.getCurrentPosition(
      (p) => {
        const lat = Number(p && p.coords && p.coords.latitude);
        const lng = Number(p && p.coords && p.coords.longitude);
        resolve(isFinite(lat) && isFinite(lng) ? { lat, lng, accuracy: Number((p.coords && p.coords.accuracy) || 0) } : null);
      },
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  });
}

/* Job detail notice + Print door tag. Print only once the job is the server's own. */
function DoorTagNotice({ job, style }) {
  const info = ssDoorTagInfo(job);
  const [busy, setBusy] = useState(false);
  if (!info) return null;
  const canPrint = job.status === "active" || job.status === "completed";
  async function print() {
    if (busy) return;
    setBusy(true);
    try { await ssOpenDoorTagPdf(job); }
    catch (e) { ssToast("err", (e && e.message) ? String(e.message) : "Could not open the door tag"); }
    finally { setBusy(false); }
  }
  return (
    <div role="note" style={{
      margin: "10px 14px 0", padding: "10px 12px", background: C.warnL, border: `1.5px solid ${C.warn}`,
      borderRadius: 12, color: C.deep, fontSize: 13, lineHeight: 1.4, display: "flex", gap: 8, alignItems: "flex-start",
      ...(style || {}),
    }}>
      <Info size={16} color={C.warn} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong>Door tag required</strong> — post it on the door after any unsuccessful attempt. When you save that attempt, you'll be asked to confirm the posting with a photo.{/* [door-tag-notice-hint-2026-10-25] */}
        {info.ref ? <span style={{ display: "block", marginTop: 2, fontWeight: 700 }}>Reference # {info.ref}</span> : null}
        {canPrint ? (
          <button type="button" onClick={print} disabled={busy} style={{
            marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 10,
            background: C.deep, color: C.white, border: "none", fontSize: 12, fontWeight: 800,
            cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1, fontFamily: "inherit",
          }}>
            {busy ? <SpinnerDot /> : <Printer size={14} color={C.white} />} Print door tag
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* Mounted once per shell beside TicketPromptHost. */
function DoorTagPromptHost() {
  const { jobs } = React.useContext(AppCtx);
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const [pending, setPending] = useState(null); // { job, attemptIndex }
  useEffect(() => {
    const on = (ev) => {
      const d = (ev && ev.detail) || {};
      if (!d.jobId) return;
      /* Runs synchronously inside api.req, before the caller reloads jobs, so the
         cached job still holds the PRIOR attempts — its count is the new index. */
      const cached = (jobsRef.current || []).find((j) => String(j.id) === String(d.jobId)) || null;
      const job = cached || (d.row ? { ...d.row, id: String(d.jobId), jobNumber: d.row.job_number || d.jobId } : null);
      if (!job) return;
      if (!ssDoorTagInfo(cached) && !ssDoorTagInfo(d.row)) return;
      let idx = d.attemptIndex;
      if (idx == null && cached) {
        const prior = Array.isArray(cached.rawAttempts) ? cached.rawAttempts.length
          : (Array.isArray(cached._raw && cached._raw.attempts) ? cached._raw.attempts.length : null);
        if (prior != null) idx = prior;
      }
      setPending({ job, attemptIndex: idx, ref: (ssDoorTagInfo(cached) || ssDoorTagInfo(d.row) || {}).ref || "" });
    };
    window.addEventListener("ss-door-tag-check", on);
    return () => window.removeEventListener("ss-door-tag-check", on);
  }, []);
  if (!pending) return null;
  return <DoorTagFollowupSheet key={String(pending.job.id) + ":" + String(pending.attemptIndex)} pending={pending} onDone={() => setPending(null)} />;
}

function DoorTagFollowupSheet({ pending, onDone }) {
  const { reload } = React.useContext(AppCtx);
  const job = pending.job;
  const [choice, setChoice] = useState("");      // "" | "posted" | "not"
  const [reason, setReason] = useState("");
  const [otherText, setOtherText] = useState("");
  const [photo, setPhoto] = useState(null);      // { key, fileUrl }
  const [photoBusy, setPhotoBusy] = useState(false);
  const [gps, setGps] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [failedOnce, setFailedOnce] = useState(false);
  const inputRef = useRef(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  function grabGps() {
    setGpsBusy(true);
    ssDoorTagGps().then((g) => { if (alive.current) { setGps(g); setGpsBusy(false); } });
  }
  useEffect(() => { grabGps(); }, []);

  async function upload(bytes, mime, ext) {
    const rec = await ssUploadEvidence(api, {
      bytes, mime, fileName: `door-tag-${job.id}-${Date.now()}.${ext}`, context: "attempt-photo", jobId: job.id,
    });
    if (alive.current) setPhoto(rec);
  }
  async function takePhoto() {
    if (photoBusy) return;
    setErr("");
    const Camera = ssPlugin("Camera");
    if (!Camera) { if (inputRef.current) inputRef.current.click(); else setErr("Camera unavailable on this device."); return; }
    setPhotoBusy(true);
    try {
      const p = await Camera.getPhoto({ quality: 80, resultType: "base64", source: "CAMERA", saveToGallery: false });
      await upload(ssB64ToBytes(p.base64String), "image/jpeg", "jpg");
    } catch (e) { setErr((e && e.message) || "Photo capture failed"); }
    finally { if (alive.current) setPhotoBusy(false); }
  }
  async function onFile(ev) {
    const f = ev && ev.target && ev.target.files && ev.target.files[0];
    if (ev && ev.target) ev.target.value = "";
    if (!f) return;
    setPhotoBusy(true); setErr("");
    try {
      const bytes = await ssFileToBytes(f);
      const mime = ssImageMimeOf(f, bytes);
      if (!/^image\//.test(mime)) throw new Error("That file is not a photo");
      await upload(bytes, mime, ssExtForMime(mime));
    } catch (e) { setErr((e && e.message) || "Photo upload failed"); }
    finally { if (alive.current) setPhotoBusy(false); }
  }

  const gpsOk = !!(gps && isFinite(gps.lat) && isFinite(gps.lng));
  const otherMissing = reason === "Other" && String(otherText).trim().length < 3;
  const ready = choice === "posted" ? !!(photo && photo.key) && gpsOk
    : choice === "not" ? !!reason && !otherMissing : false;

  async function submit() {
    if (!ready || sending) return;
    setSending(true); setErr("");
    const body = {
      posted: choice === "posted",
      lat: gpsOk ? gps.lat : null, lng: gpsOk ? gps.lng : null, accuracy: gpsOk ? gps.accuracy : null,
      attemptIndex: pending.attemptIndex != null ? pending.attemptIndex : null,
    };
    if (choice === "posted") body.photoS3Key = photo.key;
    else {
      body.reason = reason === "Other" ? "Other: " + String(otherText).trim().slice(0, 200) : reason;
    }
    try {
      const r = await api.post("/api/server/jobs/" + encodeURIComponent(job.id) + "/door-tag-posted", body);
      if (!r || r.error || r.ok === false) throw new Error((r && r.error) || "Could not record the door tag");
      ssToast("ok", choice === "posted" ? "Door tag posting recorded" : "Recorded — door tag not posted");
      try { reload && reload(); } catch (_) {}
      onDone();
    } catch (e) {
      const msg = (e && e.status) || (e && e.message && !/failed to fetch|load failed|network/i.test(e.message))
        ? String((e && e.message) || "Could not record the door tag")
        : "No connection — the door tag answer was not sent. Try again.";
      ssToast("err", msg);
      if (alive.current) { setErr(msg); setFailedOnce(true); }
    } finally { if (alive.current) setSending(false); }
  }

  const chip = (active, label, onClick) => (
    <button type="button" key={label} onClick={onClick} style={{
      padding: "9px 12px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
      border: `1.5px solid ${active ? C.deep : C.steelL}`, background: active ? C.deep : C.white, color: active ? C.white : C.deep,
      textAlign: "left",
    }}>{label}</button>
  );
  const node = (
    <div role="dialog" aria-modal="true" aria-label="Did you post the door tag?" style={{
      position: "fixed", inset: 0, zIndex: 3000, display: "flex", alignItems: "flex-end", justifyContent: "center",
      background: "rgba(10,22,40,0.55)",
    }}>
      <div style={{
        width: "100%", maxWidth: 560, maxHeight: "calc(100% - env(safe-area-inset-top, 0px) - 24px)", background: C.bg, borderRadius: "24px 24px 0 0",
        display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 -10px 40px rgba(0,0,0,0.3)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)", boxSizing: "border-box",
      }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "18px 16px 12px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>
            DOOR TAG · JOB #{job.jobNumber || job.job_number || job.id}{pending.ref ? " · REF # " + pending.ref : ""}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, marginTop: 2 }}>Did you post the door tag?</div>
          <div style={{ fontSize: 12, color: C.steel, marginTop: 4, lineHeight: 1.4 }}>Your attempt is saved. This answer is required for this job.</div>
          <div style={{ marginTop: 10, padding: "9px 11px", background: C.warnL, border: `1px solid ${C.warn}`, borderRadius: 10, fontSize: 13, fontWeight: 700, color: C.deep, lineHeight: 1.4 }}>
            Never put the tag in or on a mailbox — use painter's tape on the door or frame.
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button type="button" onClick={() => { setChoice("posted"); setErr(""); }} style={{
              flex: 1, minHeight: 48, borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
              border: `1.5px solid ${C.ok}`, background: choice === "posted" ? C.ok : C.white, color: choice === "posted" ? "#fff" : C.ok,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}><CheckCircle2 size={18} /> Yes, posted</button>
            <button type="button" onClick={() => { setChoice("not"); setErr(""); }} style={{
              flex: 1, minHeight: 48, borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
              border: `1.5px solid ${C.err}`, background: choice === "not" ? C.err : C.white, color: choice === "not" ? "#fff" : C.err,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}><XCircle size={18} /> Couldn't post it</button>
          </div>

          {choice === "posted" ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, marginBottom: 6 }}>Photo of the posted tag (required)</div>
              <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={onFile} style={{ display: "none" }} />
              {photo && photo.key ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: C.okL, borderRadius: 10, color: C.ok, fontSize: 13, fontWeight: 700 }}>
                  <CheckCircle2 size={16} /> Photo uploaded
                  <button type="button" onClick={takePhoto} disabled={photoBusy} style={{ marginLeft: "auto", background: "none", border: "none", color: C.royal, fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>Retake</button>
                </div>
              ) : (
                <button type="button" onClick={takePhoto} disabled={photoBusy} style={{
                  width: "100%", minHeight: 48, borderRadius: 12, border: `1.5px dashed ${C.deep}`, background: C.white, color: C.deep,
                  fontSize: 14, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit",
                }}>{photoBusy ? <SpinnerDot color={C.deep} /> : <Camera size={18} />} {photoBusy ? "Uploading…" : "Take photo"}</button>
              )}
            </div>
          ) : null}

          {choice === "not" ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, marginBottom: 6 }}>Why not?</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {SS_DOOR_TAG_REASONS.map((r) => chip(reason === r, r, () => setReason(r)))}
              </div>
              {reason === "Other" ? (
                <input type="text" value={otherText} maxLength={200} onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Briefly, what happened?" style={{
                    marginTop: 8, width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 10,
                    border: `1.5px solid ${C.steelL}`, background: C.white, color: C.deep, fontSize: 14, fontFamily: "inherit",
                  }} />
              ) : null}
            </div>
          ) : null}

          {choice ? (
            <div style={{ marginTop: 10, fontSize: 12, color: gpsOk ? C.ok : C.warn, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
              <MapPin size={14} />
              {gpsBusy ? "Getting GPS…" : gpsOk ? `GPS captured (±${Math.round(gps.accuracy || 0)} m)` : "No GPS fix."}
              {!gpsBusy && !gpsOk ? (
                <button type="button" onClick={grabGps} style={{ background: "none", border: "none", color: C.royal, fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>Retry GPS</button>
              ) : null}
            </div>
          ) : null}
          {choice === "posted" && !gpsOk && !gpsBusy ? (
            <div style={{ fontSize: 12, color: C.steel, marginTop: 4 }}>A GPS fix is required to record a posted tag.</div>
          ) : null}
          {err ? <div role="alert" style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, background: C.errL, color: C.err, fontSize: 12, fontWeight: 700 }}>{err}</div> : null}
        </div>
        <div style={{ padding: "10px 16px 14px", background: C.white, borderTop: `1px solid ${C.steelXL}` }}>
          <button type="button" onClick={submit} disabled={!ready || sending} style={{
            width: "100%", minHeight: 50, borderRadius: 14, border: "none", fontSize: 15, fontWeight: 800, fontFamily: "inherit",
            background: ready ? C.deep : C.steelL, color: C.white, cursor: ready && !sending ? "pointer" : "not-allowed",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>{sending ? <SpinnerDot /> : <Check size={18} />} {failedOnce ? "Try again" : "Save door tag answer"}</button>
          {failedOnce ? (
            <button type="button" onClick={onDone} style={{
              width: "100%", marginTop: 6, background: "none", border: "none", color: C.steel, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}>Close — I'll report it to dispatch</button>
          ) : null}
        </div>
      </div>
    </div>
  );
  try { if (typeof document !== "undefined" && document.body) return createPortal(node, document.body); } catch (_) {}
  return node;
}

function JobDetailSheet({ job, onClose }) {
  // [claim-map-unmask-2026-07-27] claim routed through the shared useJobClaim hook
  // (same path the map claim bar uses — all 403 guard messages preserved).
  const { claim, busy: acceptBusy, err: acceptErr, bundlePrompt } = useJobClaim(() => onClose()); // [job-bundles-2026-10-13]
  function handleAccept() { return claim(job); }

  const [tab, setTab] = useState("brief");
  const [attemptOpen, setAttemptOpen] = useState(false);
  const [filingOpen, setFilingOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [posOpen, setPosOpen] = useState(false);
  const courthouseKind = courthouseServiceType(job);
  const courtSt = useCourtStatus(job); // [court-closed-warning-2026-10-08]
  /* [server-parity-web-2026-10-05] Print all (briefing + documents packet) and the
     one-page briefing sheet, from the header. Only meaningful once the job is
     the server's own — the packet endpoint refuses jobs they are not assigned to. */
  const print = useJobPrint(job);
  const canPrint = job.status === "active" || job.status === "completed";
  const sup = useJobSupport(job); // [support-tickets-2026-10-08]
  const focusTicketId = useSupportDeepLink(job, setTab); // [ticket-link-2026-10-08]

  return (
    <Sheet onClose={onClose}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
        padding: "18px 16px 14px", color: C.white,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <button onClick={onClose} style={iconBtn}>
            <ChevronLeft size={22} color={C.white} />
          </button>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: C.goldL }}>
            JOB #{job.jobNumber}
          </div>
          {/* [server-parity-web-2026-10-05] header actions: Briefing sheet · Print all */}
          {canPrint || sup.canCreate ? (
            <div style={{ display: "flex", gap: 6 }}>
              {/* [support-tickets-2026-10-08] Escalate / get help */}
              {sup.canCreate ? <EscalateButton compact={canPrint} onPress={() => sup.openEscalate()} badge={sup.openCount} /> : null}
              {canPrint ? <JobPrintButtons print={print} /> : null}
            </div>
          ) : <div style={{ width: 38 }} />}
        </div>

        {/* [claim-map-unmask-2026-07-27] Servee name always shown. The gold
            claim-to-reveal lock pill is removed entirely — servers evaluate
            the full record before accepting. */}
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 4 }}>{jobDisplay(job).name}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.85, marginBottom: 12 }}>
          <span>{job.type}</span>
          {/* [claim-map-unmask-2026-07-27] client/firm name no longer suppressed pre-claim */}
          {job.client && (<><span>·</span><span>{job.client}</span></>)}
        </div>

        {/* Pay + deadline strip */}
        <div style={{
          display: "flex", justifyContent: "space-between", padding: "12px 14px",
          background: "rgba(255,255,255,0.08)", borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.1)",
        }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", opacity: 0.6 }}>PAY</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.gold }}>{ssPayLabel(ssJobShownPay(job))/* [job-bundles-v2-2026-10-16] */}</div>
          </div>
          <div style={{ width: 1, background: "rgba(255,255,255,0.1)" }} />
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", opacity: 0.6 }}>DUE</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>{job.deadline}</div>
            {/* [field-list-attempt-due-2026-10-08] */}
            {(() => { const ad = ssAttemptDueInfo(job); return ad ? (
              <div style={{ fontSize: 10, fontWeight: 700, marginTop: 3,
                color: ad.tone === "overdue" ? "#ff8a80" : ad.tone === "today" ? C.goldL : "rgba(255,255,255,0.7)" }}>{ad.label}</div>
            ) : null; })()}
          </div>
          <div style={{ width: 1, background: "rgba(255,255,255,0.1)" }} />
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", opacity: 0.6 }}>DISTANCE</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>{ssFmtMiles(job.dist) || "—"}</div>
          </div>
        </div>
      </div>

      {/* [court-closed-warning-2026-10-08] courthouse closed / closing-soon warning */}
      {courtSt && courtSt.applies ? <CourtStatusBanner cs={courtSt} style={{ margin: "10px 14px 0" }} /> : null}
      <BulkServeBanner job={job} />{/* [field-bulk-serve-2026-10-08] */}
      <BundleNote job={job} />{/* [job-bundles-2026-10-13] */}
      <DoorTagNotice job={job} />{/* [door-tags-server-2026-10-22] */}
      {/* Tab pills */}
      <div style={{ background: C.white, padding: "12px 16px 0", borderBottom: `1px solid ${C.steelXL}` }}>
        <div style={{ display: "flex", gap: 4 }}>
          {[
            { id: "brief", label: "Brief", icon: FileText },
            { id: "attempts", label: "Attempts", icon: Clock },
            { id: "notes", label: "Notes", icon: Edit3 }, /* [server-parity-web-2026-10-05] */
            { id: "chat", label: "Chat", icon: MessageSquare },
            ...(sup.present ? [{ id: "support", label: "Support", icon: LifeBuoy, badge: sup.openCount, dot: sup.needsYou }] : []), /* [support-tickets-2026-10-08] */
          ].map(t => {
            const active = tab === t.id;
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                flex: 1, padding: "10px 8px", background: "none", border: "none",
                borderBottom: active ? `2.5px solid ${C.gold}` : "2.5px solid transparent",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer",
                color: active ? C.deep : C.steel,
              }}>
                <span style={{ position: "relative", display: "inline-flex" }}>
                  <Icon size={16} strokeWidth={active ? 2.4 : 1.8} />
                  {t.badge > 0 ? (
                    <span style={{
                      position: "absolute", top: -6, right: -10, minWidth: 15, height: 15, borderRadius: 8, padding: "0 4px",
                      background: t.dot ? C.ok : C.gold, color: "#fff", fontSize: 9, fontWeight: 800, lineHeight: "15px", textAlign: "center",
                    }}>{t.badge}</span>
                  ) : null}
                </span>
                <span style={{ fontSize: 11, fontWeight: active ? 800 : 600 }}>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: "auto", background: C.bg, paddingBottom: 80 }}>
        {tab === "brief" && <BriefTab job={job} onNav={() => setNavOpen(true)} onPos={() => setPosOpen(true)} />}
        {tab === "attempts" && <AttemptsTab job={job} onRecord={() => setAttemptOpen(true)} />}
        {tab === "notes" && <JobNotesTab job={job} />}{/* [server-parity-web-2026-10-05] */}
        {tab === "chat" && <ChatTab job={job} />}
        {tab === "support" && sup.present && (
          <SupportTab job={job} tickets={sup.tickets} err={sup.err} reload={sup.reload}
            canCreate={sup.canCreate} onEscalate={() => sup.openEscalate()} focusTicketId={focusTicketId} />
        )}{/* [support-tickets-2026-10-08] */}
      </div>

      {/* Sticky CTA */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, padding: "10px 14px 14px",
        background: C.white, borderTop: `1px solid ${C.steelXL}`,
      }}>
        {acceptErr && <div style={{ color: "#c0392b", fontSize: 12, marginBottom: 8, textAlign: "center" }}>{acceptErr}</div>}
        {job.status === "available" && job.network_claim_blocked === true && <SsNetworkBlockedNotice job={job} />}
        {job.status === "available" && job.network_claim_blocked !== true && (
          <button onClick={handleAccept} disabled={acceptBusy} style={{
            width: "100%", padding: 14, background: C.deep, color: C.white,
            border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            letterSpacing: "0.02em", boxShadow: `0 4px 12px ${C.deep}40`,
          }}>
            <Check size={18} /> {ssAcceptLabel(job)}{/* [job-bundles-2026-10-13] */}
          </button>
        )}
        {job.status === "active" && (
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setNavOpen(true)} style={{
              flex: 1, minHeight: 52, padding: "14px 16px", background: C.white, color: C.deep,
              border: `1.5px solid ${C.deep}`, borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <Navigation size={20} /> Navigate
            </button>
            {courthouseKind ? (
              <button onClick={() => setFilingOpen(true)} style={{
                flex: 1.2, minHeight: 52, padding: "14px 16px", background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
                color: C.deep, border: "none", borderRadius: 14, fontSize: 15, fontWeight: 800, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                boxShadow: `0 4px 10px ${C.gold}55`,
              }}>
                {courthouseKind === "document-retrieval"
                  ? (<><FolderOpen size={20} /> Retrieve documents</>)
                  : (<><FileSignature size={20} /> File documents</>)}
              </button>
            ) : (
              <button onClick={() => setAttemptOpen(true)} style={{
                flex: 1.2, minHeight: 52, padding: "14px 16px", background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
                color: C.deep, border: "none", borderRadius: 14, fontSize: 15, fontWeight: 800, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                boxShadow: `0 4px 10px ${C.gold}55`,
              }}>
                <Plus size={20} /> Log Attempt
              </button>
            )}
          </div>
        )}
      </div>

      {bundlePrompt}{/* [job-bundles-2026-10-13] */}
      {attemptOpen && <AttemptCapture job={job} onClose={() => setAttemptOpen(false)} />}
      {filingOpen && <FilingCompleteSheet job={job} kind={courthouseKind} onClose={() => setFilingOpen(false)} />}
      {navOpen && <NavigationSheet job={job} onClose={() => setNavOpen(false)} />}
      {/* [server-parity-web-2026-10-05] opens the admin-prepared proof (preview -> sign).
          [audit-fixes-2026-10-15] H25: the form picker / POSSheet fallback no longer exist. */}
      {posOpen && <ProofFlowSheet job={job} onClose={() => setPosOpen(false)} />}
      {sup.escalate && (
        <EscalateSheet job={job} seedCategory={sup.escalate.seedCategory} seedAttemptRef={sup.escalate.seedAttemptRef}
          onClose={sup.closeEscalate} onCreated={() => setTab("support")} />
      )}{/* [support-tickets-2026-10-08] */}
    </Sheet>
  );
}

/* [server-parity-web-2026-10-05] Header pair used by JobDetailSheet + InlineJobDetail. */
function JobPrintButtons({ print }) {
  const b = (which, label, Icon, onClick, title) => (
    <button onClick={onClick} disabled={!!print.busy} aria-label={title} title={title} style={{
      ...iconBtn, width: "auto", padding: "0 10px", gap: 6,
      opacity: print.busy && print.busy !== which ? 0.5 : 1,
      fontSize: 11, fontWeight: 800, color: C.white, letterSpacing: "0.04em", fontFamily: "inherit",
    }}>
      {print.busy === which ? <SpinnerDot /> : <Icon size={16} color={C.white} />}<span>{label}</span>
    </button>
  );
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {b("brief", "Briefing", FileText, print.briefing, "Briefing sheet")}
      {b("all", "Print all", Printer, print.printAll, "Print all (briefing + documents)")}
    </div>
  );
}

/* [server-parity-web-2026-10-05] ── NOTES TAB ──────────────────────────
   Server-visible job notes. GET/POST /api/server/jobs/:id/notes — the backend
   scopes to internal_notes rows with category='job' AND visibility='job' for a
   job the caller is assigned to, and always stamps the author from the JWT. */
/* [server-app-removals-2026-10-08] every delete needs a short reason (the backend records it in the audit log). */
function ssAskReason(what) {
  const v = window.prompt("Why are you deleting " + what + "? (a few words — kept in the record)", "");
  if (v == null) return null;
  const t = String(v).trim();
  if (t.length < 5) { ssToast("err", "Please type a reason of at least 5 characters."); return null; }
  return t;
}

function JobNotesTab({ job }) {
  const [notes, setNotes] = useState(null); // null = loading
  const [err, setErr] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const { user: _me } = React.useContext(AppCtx); /* [server-app-removals-2026-10-08] */
  async function delNote(n) {
    const reason = ssAskReason("this note"); if (!reason) return;
    try {
      await api.req("DELETE", `/api/server/jobs/${encodeURIComponent(job.id)}/notes/${encodeURIComponent(n.id)}`, { reason });
      setNotes((prev) => (prev || []).filter((x) => x.id !== n.id));
      ssToast("ok", "Note deleted");
    } catch (e) { ssToast("err", (e && e.message) || "Could not delete the note"); }
  }
  const load = React.useCallback(async () => {
    try {
      const r = await api.get(`/api/server/jobs/${encodeURIComponent(job.id)}/notes`);
      setNotes(Array.isArray(r && r.notes) ? r.notes : []);
      setErr("");
    } catch (e) {
      setNotes([]);
      setErr((e && e.message) || "Could not load notes");
    }
  }, [job && job.id]);
  useEffect(() => { setNotes(null); load(); }, [load]);

  async function add() {
    const content = draft.trim();
    if (!content || busy) return;
    if (content.length > 4000) { setErr("Notes are limited to 4,000 characters."); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/api/server/jobs/${encodeURIComponent(job.id)}/notes`, { content });
      const note = r && r.note;
      setDraft("");
      if (note) setNotes((prev) => [note, ...(prev || [])]);
      else await load();
      ssToast("ok", "Note added");
    } catch (e) {
      const msg = (e && e.message) || "Could not save note";
      setErr(msg); ssToast("err", msg);
    } finally { setBusy(false); }
  }
  const fmt = (ts) => {
    try {
      const d = new Date(ts); if (isNaN(d.getTime())) return "";
      return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch (_) { return ""; }
  };
  const canAdd = job.status === "active" || job.status === "completed";

  return (
    <div style={{ padding: "16px 14px" }}>
      {canAdd ? (
        <div style={{ background: C.white, borderRadius: 14, padding: 12, marginBottom: 12, border: `1px solid ${C.steelXL}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 6 }}>ADD A NOTE</div>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} maxLength={4000}
            placeholder="Gate code, best time to try, who answered the door…"
            style={{
              width: "100%", padding: "9px 10px", border: `1px solid ${C.steelXL}`, borderRadius: 8,
              fontSize: 13, color: C.deep, outline: "none", fontFamily: "inherit", boxSizing: "border-box", resize: "vertical",
              background: C.bgW,
            }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <div style={{ fontSize: 10, color: C.steel }}>{draft.length}/4000 · visible to dispatch</div>
            <button onClick={add} disabled={busy || !draft.trim()} style={{
              padding: "9px 14px", background: busy || !draft.trim() ? C.steelL : C.deep, color: C.white,
              border: "none", borderRadius: 10, fontSize: 12, fontWeight: 800, cursor: busy || !draft.trim() ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              {busy ? <SpinnerDot /> : <Plus size={14} />} {busy ? "Saving…" : "Add note"}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: C.steel, marginBottom: 12, lineHeight: 1.5 }}>Notes open once this job is assigned to you.</div>
      )}

      {err && (
        <div style={{ fontSize: 11, color: C.err, background: C.errL, padding: "8px 10px", borderRadius: 8, marginBottom: 10 }}>{err}</div>
      )}

      {notes === null && (
        <SkeletonGroup label="Loading notes">
          {[0, 1].map((i) => (
            <div key={i} style={{ background: C.white, borderRadius: 14, padding: 14, marginBottom: 10, border: `1px solid ${C.steelXL}` }}>
              <SkeletonLine width="40%" height={10} />
              <SkeletonLine width="90%" height={12} style={{ marginBottom: 0 }} />
            </div>
          ))}
        </SkeletonGroup>
      )}
      {notes !== null && notes.length === 0 && !err && (
        <div style={{ background: C.white, borderRadius: 14, padding: 24, textAlign: "center", border: `1.5px dashed ${C.steelL}` }}>
          <Edit3 size={24} color={C.steelL} />
          <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, marginTop: 8 }}>No notes on this job yet</div>
        </div>
      )}
      {notes !== null && notes.map((n) => (
        <div key={n.id} style={{ background: C.white, borderRadius: 14, padding: 12, marginBottom: 8, border: `1px solid ${C.steelXL}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.deep, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {n.created_by_name || n.created_by || "Note"}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <div style={{ fontSize: 10, color: C.steel }}>{fmt(n.created_at)}</div>
              {_me && n.created_by && String(n.created_by).toLowerCase() === String(_me.email || "").toLowerCase() && (
                <button onClick={() => delNote(n)} aria-label="Delete note" title="Delete note" style={{ background: "none", border: "none", padding: 2, cursor: "pointer" }}><Trash2 size={13} color={C.steel} /></button>
              )}
            </div>
          </div>
          {n.title && !/^Job .* note$/i.test(String(n.title)) ? (
            <div style={{ fontSize: 12, fontWeight: 700, color: C.deep, marginBottom: 2 }}>{n.title}</div>
          ) : null}
          <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{n.content}</div>
        </div>
      ))}
    </div>
  );
}

function BriefTab({ job, onNav, onPos }) {
  // Defensive: BriefTab is normally rendered with a fully-hydrated job (the
  // jobs list is the source of truth), but if a navigation race lands us
  // here before the parent finishes hydrating, render skeleton lines that
  // approximate the address + instructions + case-info cards.
  if (!job) {
    return (
      <div style={{ padding: "16px 14px" }}>
        <SkeletonGroup label="Loading job details">
          {[0,1,2].map(i => (
            <div key={i} style={{
              background: C.white, borderRadius: 14, padding: 14, marginBottom: 12,
              border: `1px solid ${C.steelXL}`,
            }}>
              <SkeletonLine width="30%" height={10} />
              <SkeletonLine width="80%" height={14} />
              <SkeletonLine width="55%" height={12} style={{ marginBottom: 0 }} />
            </div>
          ))}
        </SkeletonGroup>
      </div>
    );
  }
  // Courthouse jobs (in-person filing / document retrieval) show the clerk's
  // office context instead of a recipient/residence address.
  const courthouse = courthouseServiceType(job);
  if (courthouse) {
    return <CourthouseBriefTab job={job} kind={courthouse} onNav={onNav} />;
  }
  /* [server-parity-web-2026-10-05] Identity block — the subject photo (presigned
     through the same path as FieldSubjectPhoto) and the physical description
     (recipient_desc / defendant_physical_notes) were only on the Field View; the
     job detail is where most servers read the brief, so they belong here too.
     Shown only once the job is the server's own — a face, a DOB and a
     description on an unclaimed job would broadcast an identifiable person. */
  const subjectDesc = fieldSubjectDesc(job);
  const hasSubjectPhoto = !!subjectPhotoKey(job);
  const showIdentity = (job.status === "active" || job.status === "completed") && (hasSubjectPhoto || subjectDesc || job.defendantDob);
  const posLabel = (() => {
    const st = String(job.posStatus || "").toLowerCase();
    const form = job.posFormId || "POS";
    if (st === "pending_signature") return form + " · Ready to sign";
    if (st === "signed" || st === "delivered") return form + " · Signed";
    if (st === "filed") return form + " · Filed with the court";
    if (st === "pending_admin_review") return form + " · Draft with dispatch";
    if (st === "rejected") return form + " · Returned — fix & resubmit";
    return "Choose a form and prepare the proof";
  })();
  return (
    <div style={{ padding: "16px 14px" }}>
      {showIdentity && (
        <div style={{
          background: C.white, borderRadius: 14, padding: 14, marginBottom: 12,
          border: `1px solid ${C.steelXL}`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 8 }}>SUBJECT</div>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            {hasSubjectPhoto ? (
              <div style={{ width: 96, flexShrink: 0 }}>
                <FieldSubjectPhoto job={job} />
              </div>
            ) : null}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.deep, lineHeight: 1.2, wordBreak: "break-word" }}>{jobDisplay(job).name}</div>
              {job.defendantDob ? (
                <div style={{ fontSize: 12, color: C.steel, marginTop: 4 }}>DOB {job.defendantDob}</div>
              ) : null}
              <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginTop: 10 }}>PHYSICAL DESCRIPTION</div>
              <div style={{ fontSize: 13, color: subjectDesc ? C.ink : C.steel, lineHeight: 1.5, marginTop: 2, whiteSpace: "pre-wrap" }}>
                {subjectDesc || "Not on file"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Address card */}
      <div style={{
        background: C.white, borderRadius: 14, padding: 14, marginBottom: 12,
        border: `1px solid ${C.steelXL}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>SERVE ADDRESS</div>
          {/* [claim-map-unmask-2026-07-27] "Open in Maps" available pre-claim — gate is now
              "do we have an address", not "is it claimed". */}
          {!!job.address && (
            <button onClick={onNav} style={{
              display: "flex", alignItems: "center", gap: 4, background: C.infoL,
              color: C.info, padding: "4px 8px", borderRadius: 8, border: "none",
              fontSize: 11, fontWeight: 700, cursor: "pointer",
            }}>
              <Navigation size={12} /> Open
            </button>
          )}
        </div>
        {/* [claim-map-unmask-2026-07-27] real street address pre-claim — no masked state.
            [web-parity-2026-07-21] city/zip line only when we actually have one (no stray ", CA"). */}
        <div style={{ fontSize: 15, fontWeight: 700, color: C.deep, marginBottom: 2 }}>{job.address || "—"}</div>
        {jobDisplay(job).cityZip && <div style={{ fontSize: 13, color: C.steel }}>{jobDisplay(job).cityZip}</div>}
      </div>

      {/* Special instructions */}
      <div style={{
        background: C.warnL, borderRadius: 14, padding: 14, marginBottom: 12,
        border: `1px solid ${C.warn}33`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
          <Info size={14} color={C.warn} />
          <div style={{ fontSize: 11, fontWeight: 700, color: C.warn, letterSpacing: "0.06em" }}>SPECIAL INSTRUCTIONS</div>
        </div>
        {/* [web-parity-2026-07-21] strip backend [PAID …] audit marker from client-visible instructions */}
        <div style={{ fontSize: 13, color: "#7a5200", lineHeight: 1.5 }}>{String(job.instructions || "").replace(/\[\s*PAID[^\]]*\]/gi, "").trim()}</div>
      </div>

      {/* [claim-map-unmask-2026-07-27] Documents visible pre-claim — the server
          must be able to review the papers before accepting. PII gate removed. */}
      <JobDocumentsSection job={job} />
      {/* [eservice-2026-09-06] Read-only e-service summary; hides itself when none / not permitted */}
      <EServiceCard job={job} />

      {/* Case info */}
      <div style={{
        background: C.white, borderRadius: 14, padding: 14, marginBottom: 12,
        border: `1px solid ${C.steelXL}`,
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 8 }}>CASE INFO</div>
        <KV k="Case #" v={job.caseNo} />
        <KV k="Client" v={job.client} />
        <KV k="Service Type" v={job.type} last />
      </div>

      {/* POS shortcut if available */}
      {(ssAttemptCount(job) > 0 || String(job.posStatus || "") === "pending_signature") && ( /* [fc-crash-fix-2026-09-11] shape-safe count; [pos-sign-flow-2026-10-11] also when a proof is waiting for this server's signature */
        <button onClick={onPos} style={{
          width: "100%", padding: 14, background: C.white, borderRadius: 14, marginBottom: 12,
          border: `1px solid ${C.steelXL}`, display: "flex", alignItems: "center", gap: 12,
          cursor: "pointer", textAlign: "left",
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, background: C.goldXL,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FileSignature size={18} color={C.deep} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>
              {String(job.posStatus || "") === "pending_signature" ? "Sign Proof of Service" : "Proof of Service"}
            </div>
            <div style={{ fontSize: 11, color: C.steel }}>{posLabel}</div>{/* [server-parity-web-2026-10-05] live state, was a static literal */}
          </div>
          <ChevronRight size={18} color={C.steel} />
        </button>
      )}
    </div>
  );
}

function KV({ k, v, last }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", padding: "8px 0",
      borderBottom: last ? "none" : `1px solid ${C.steelXXL}`,
    }}>
      <div style={{ fontSize: 12, color: C.steel }}>{k}</div>
      <div style={{ fontSize: 12, color: C.deep, fontWeight: 600, textAlign: "right" }}>{v}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// COURTHOUSE BRIEF TAB — replaces the recipient/residence-centric brief for
// in-person filing ('court-filing') and document-retrieval jobs. Shows the
// clerk's-office context (court name / county / address) + filing details
// instead of a serve address + residence map.
// ═══════════════════════════════════════════════════════════════════════
function CourthouseBriefTab({ job, kind, onNav }) {
  const isRetrieval = kind === "document-retrieval";
  const courtLine1 = job.courtName || (job.courtCounty ? `${job.courtCounty} County Court` : "Courthouse");
  const courtLine2 = [job.courtAddress, job.courtCounty ? `${job.courtCounty} County` : ""].filter(Boolean).join(" · ");
  // [claim-map-unmask-2026-07-27] navigation available pre-claim — gate on having an address only
  const hasNav = !!(job.courtAddress || job.address);
  return (
    <div style={{ padding: "16px 14px" }}>
      {/* Courthouse card */}
      <div style={{
        background: C.white, borderRadius: 14, padding: 14, marginBottom: 12,
        border: `1px solid ${C.steelXL}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Briefcase size={13} color={C.steel} />
            <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>COURTHOUSE</div>
          </div>
          {hasNav && (
            <button onClick={onNav} style={{
              display: "flex", alignItems: "center", gap: 4, background: C.infoL,
              color: C.info, padding: "4px 8px", borderRadius: 8, border: "none",
              fontSize: 11, fontWeight: 700, cursor: "pointer",
            }}>
              <Navigation size={12} /> Open
            </button>
          )}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.deep, marginBottom: 2 }}>{courtLine1}</div>
        {courtLine2 && <div style={{ fontSize: 13, color: C.steel }}>{courtLine2}</div>}
      </div>

      {/* Task banner — tells the server exactly what to do at the courthouse */}
      <div style={{
        background: C.infoL, borderRadius: 14, padding: 14, marginBottom: 12,
        border: `1px solid ${C.info}33`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
          {isRetrieval ? <FolderOpen size={14} color={C.info} /> : <FileSignature size={14} color={C.info} />}
          <div style={{ fontSize: 11, fontWeight: 700, color: C.info, letterSpacing: "0.06em" }}>
            {isRetrieval ? "DOCUMENT RETRIEVAL" : "IN-PERSON FILING"}
          </div>
        </div>
        <div style={{ fontSize: 13, color: C.deep, lineHeight: 1.5 }}>
          {isRetrieval
            ? "Retrieve the requested documents from the clerk's office, then capture them in the app to complete this job."
            : "File the documents with the clerk in person, then upload the clerk-stamped copy to complete this job."}
        </div>
      </div>

      {/* Special instructions (only if present) */}
      {job.instructions ? (
        <div style={{
          background: C.warnL, borderRadius: 14, padding: 14, marginBottom: 12,
          border: `1px solid ${C.warn}33`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
            <Info size={14} color={C.warn} />
            <div style={{ fontSize: 11, fontWeight: 700, color: C.warn, letterSpacing: "0.06em" }}>SPECIAL INSTRUCTIONS</div>
          </div>
          {/* [web-parity-2026-07-21] strip backend [PAID …] audit marker from client-visible instructions */}
          <div style={{ fontSize: 13, color: "#7a5200", lineHeight: 1.5 }}>{String(job.instructions || "").replace(/\[\s*PAID[^\]]*\]/gi, "").trim()}</div>
        </div>
      ) : null}

      {/* [claim-map-unmask-2026-07-27] Documents visible pre-claim — PII gate removed. */}
      <JobDocumentsSection job={job} />

      {/* Case / filing info */}
      <div style={{
        background: C.white, borderRadius: 14, padding: 14, marginBottom: 12,
        border: `1px solid ${C.steelXL}`,
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 8 }}>CASE INFO</div>
        <KV k="Case #" v={job.caseNo || "—"} />
        <KV k="Client" v={job.client || "—"} />
        {job.courtFilingFee != null && <KV k="Est. filing fee" v={`$${job.courtFilingFee.toFixed(2)}`} />}
        <KV k="Service Type" v={isRetrieval ? "Document Retrieval" : "Court Filing (in-person)"} last />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ELECTRONIC SERVICE CARD — read-only summary of CCP §1010.6 e-serves
// linked to this job. [eservice-2026-09-06] Spec 00-BUILD-SPEC §3 / 04 §2.
// No send, no actions: the declarant must be the sender (§1013b) and the
// physical attempt/affidavit flows in this app must not be conflated with
// an e-serve. Data: GET /api/eservice/serves?job_id= (admin-gated on the
// backend) — uses a raw fetch so a 401/403 never tears down the session the
// way api.req() does. Any non-2xx (feature off, non-admin, not found) or an
// empty list hides the card entirely.
// ═══════════════════════════════════════════════════════════════════════
function EServiceCard({ job }) { // [eservice-2026-09-06]
  const [serves, setServes] = useState(null); // null = loading, [] = hide, [...] = show
  const jobId = job && job.id;

  React.useEffect(() => {
    let alive = true;
    if (!jobId) { setServes([]); return () => { alive = false; }; }
    (async () => {
      try {
        const r = await fetch(API_URL + "/api/eservice/serves?job_id=" + encodeURIComponent(jobId) + "&limit=20", {
          method: "GET", headers: api.headers(),
        });
        if (!r.ok) { if (alive) setServes([]); return; } // 401/403/404 → silent hide
        const body = await r.json().catch(() => ({}));
        const list = Array.isArray(body && body.serves) ? body.serves.filter(s => s && s.status !== "draft") : [];
        if (!list.length) { if (alive) setServes([]); return; }
        // Recipients live on the detail route; fetch a handful, best-effort.
        const withRcpts = await Promise.all(list.slice(0, 5).map(async (s) => {
          try {
            const d = await fetch(API_URL + "/api/eservice/serves/" + encodeURIComponent(s.id), { method: "GET", headers: api.headers() });
            if (!d.ok) return { ...s, recipients: [] };
            const db = await d.json().catch(() => ({}));
            return { ...s, recipients: Array.isArray(db && db.recipients) ? db.recipients : [] };
          } catch (_) { return { ...s, recipients: [] }; }
        }));
        if (alive) setServes(withRcpts.concat(list.slice(5).map(s => ({ ...s, recipients: [] }))));
      } catch (_) { if (alive) setServes([]); }
    })();
    return () => { alive = false; };
  }, [jobId]);

  if (!serves || !serves.length) return null;

  const maskName = (n) => { const s = String(n || "").trim(); return s ? s[0] + "•••" : ""; };
  const maskEmail = (e) => {
    const s = String(e || "").trim(); const at = s.indexOf("@");
    if (at < 1) return s ? s[0] + "•••" : "";
    return s[0] + "•••@" + s.slice(at + 1);
  };
  const fmtPT = (ts) => {
    if (!ts) return "";
    try { return new Date(ts).toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) + " PT"; }
    catch (_) { return String(ts); }
  };
  const fmtDay = (d) => {
    if (!d) return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
    if (m) { try { return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch (_) { return m[0]; } }
    try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch (_) { return String(d); }
  };
  const chip = (status) => {
    switch (String(status || "")) {
      case "sent": return { l: "Sent", bg: C.infoL, c: C.info };
      case "delivered": return { l: "Delivered", bg: C.okL, c: C.ok };
      case "proof_generated": return { l: "Proof signed", bg: C.okL, c: C.ok };
      case "bounced": return { l: "Bounced", bg: C.errL, c: C.err };
      case "not_served": return { l: "Not served", bg: C.errL, c: C.err };
      case "revoked": return { l: "Revoked", bg: C.steelXXL, c: C.steel };
      case "pending": return { l: "Pending", bg: C.steelXXL, c: C.steel };
      case "dropped": return { l: "Dropped", bg: C.errL, c: C.err };
      default: return { l: String(status || "—"), bg: C.steelXXL, c: C.steel };
    }
  };
  const served = (s) => !!s.sent_at && ["sent", "delivered", "proof_generated"].includes(String(s.status));

  return (
    <div style={{
      background: C.white, borderRadius: 14, padding: 14, marginBottom: 12,
      border: `1px solid ${C.steelXL}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 8 }}>
        <Mail size={14} color={C.steel} />
        <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>ELECTRONIC SERVICE</div>
      </div>
      {serves.map((s, i) => {
        const sc = chip(s.status);
        const rcpts = Array.isArray(s.recipients) ? s.recipients : [];
        return (
          <div key={s.id || i} style={{
            padding: "10px 0", borderBottom: i === serves.length - 1 ? "none" : `1px solid ${C.steelXXL}`,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.deep, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {s.public_ref ? "Ref " + s.public_ref : "E-serve #" + s.id}
                {s.case_number ? <span style={{ color: C.steel, fontWeight: 500 }}> · {s.case_number}</span> : null}
              </div>
              <Pill bg={sc.bg} color={sc.c}>{sc.l}</Pill>
            </div>
            {rcpts.length ? rcpts.map((r, j) => {
              const rc = chip(r.status);
              return (
                <div key={r.id || j} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 6 }}>
                  <div style={{ fontSize: 12, color: C.deep, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {maskName(r.name)}{r.email ? <span style={{ color: C.steel }}> · {maskEmail(r.email)}</span> : null}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: rc.c, whiteSpace: "nowrap" }}>{rc.l}</span>
                </div>
              );
            }) : (s.recipient_count != null ? (
              <div style={{ fontSize: 12, color: C.steel, marginTop: 6 }}>
                {s.recipient_count} {s.recipient_count === 1 ? "recipient" : "recipients"}{s.bounced_count ? ` · ${s.bounced_count} bounced` : ""}
              </div>
            ) : null)}
            {s.sent_at ? <div style={{ fontSize: 12, color: C.steel, marginTop: 6 }}>Sent {fmtPT(s.sent_at)}</div> : null}
            {s.deemed_served_date ? <div style={{ fontSize: 12, color: C.steel, marginTop: 2 }}>Deemed served {fmtDay(s.deemed_served_date)}</div> : null}
            {served(s) ? (
              <div style={{ fontSize: 11, color: C.steel, marginTop: 4, lineHeight: 1.4 }}>
                Response periods extend +2 court days after electronic service (CCP §1010.6(a)(3)(B)); exceptions apply.
              </div>
            ) : null}
            {s.proof_generated_at ? <div style={{ fontSize: 11, color: C.ok, fontWeight: 600, marginTop: 4 }}>Proof signed {fmtPT(s.proof_generated_at)}</div> : null}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// JOB DOCUMENTS — list of client-uploaded service papers for an assigned
// job. [doc-pipeline-2026-07-02] This section supersedes the web app's
// DocumentsTab (commit a39b47f): same presigned flow plus a camera document
// scanner. Marker retained so the ss-deploy staleness guard knows the
// doc-pipeline capability is present in this mobile-sourced copy.
// Pulls from GET /api/server/jobs/:id/documents (presigned 15-min S3
// URLs). View opens in Capacitor Browser (SFSafariViewController on iOS),
// Download fetches the bytes and writes them to Filesystem.Documents when
// the plugin is available; otherwise falls back to the same browser open
// so the user can use the OS "Save to Files" share sheet.
// ═══════════════════════════════════════════════════════════════════════
/* [workflow-audit-2026-10-28 V-42] Contract C5: GET /api/server/jobs/:id/documents flags the
   documents to be served with serveDocument:true. Older backends send no flag -> list unchanged. */
function ssIsServeDoc(d) {
  return !!d && (d.serveDocument === true || d.serve_document === true);
}
function ssSortServeDocs(list) {
  const arr = Array.isArray(list) ? list : [];
  return arr.map((d, i) => ({ d, i }))
    .sort((a, b) => (ssIsServeDoc(b.d) ? 1 : 0) - (ssIsServeDoc(a.d) ? 1 : 0) || a.i - b.i)
    .map((x) => x.d);
}

function JobDocumentsSection({ job }) {
  const { reload } = React.useContext(AppCtx);
  const [docs, setDocs] = useState(null); // null = loading, [] = empty, [...] = loaded
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);
  // Document scanner (Camera multi-page → assembled PDF) progress state.
  const [scanBusy, setScanBusy] = useState(false);
  const [scanStatus, setScanStatus] = useState(""); // e.g. "Uploading 2 of 3 pages…"
  // Capture-sheet state machine: open flag + array of captured base64 JPEG
  // pages. The sheet lets the user shoot one page at a time (Camera plugin),
  // remove the last page, and finish into a single assembled PDF.
  const [captureOpen, setCaptureOpen] = useState(false);
  /* [retrieval-scan-1000-2026-09-29] [server-parity-web-2026-10-05] ported from
     strikeserve-mobile. capturePages is an array of page objects:
     { id, idx, ver, up: 'pending'|'done'|'error', key, b64 (only until uploaded), thumb, err }.
     Each page uploads right after capture and its full-size base64 is dropped once stored, so a
     long retrieval no longer sits in phone memory; the server builds the PDF (up to 1,000 pages).
     Replaces the in-memory string[] of base64 JPEGs that was uploaded in one burst on Done. */
  const [capturePages, setCapturePages] = useState([]);
  const pagesRef = useRef([]);
  useEffect(() => { pagesRef.current = capturePages; }, [capturePages]);
  const scanSessionRef = useRef("");
  const nextIdxRef = useRef(0);
  const inflightRef = useRef(new Set());
  const uploadBusyRef = useRef(0);
  const doneVerRef = useRef(new Map());
  const failVerRef = useRef(new Map());
  const legacyUploadRef = useRef(false); // backend without /scan-pages/presign → old per-page path
  const [buildPhase, setBuildPhase] = useState(""); // "" | "uploading" | "building"
  const MAX_SCAN_PAGES = 1000;
  const [captureBusy, setCaptureBusy] = useState(false); // true while a getPhoto/upload is in flight
  // [job-doc-upload-2026-07-22] Upload from Photos/Files — plain <input type="file">
  // WITHOUT the capture attribute (capture forces the camera on iOS; omitting it
  // makes the browser/WKWebView present the system sheet with Photo Library /
  // Choose File). Reuses the same presign → PUT → /api/uploads/complete flow as
  // uploadScannedPage below. Ported from strikeserve-mobile/src/server-app.jsx.
  const uploadInputRef = useRef(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  async function onPickedUploadFiles(e) {
    const files = Array.from((e.target && e.target.files) || []);
    if (e.target) e.target.value = "";
    if (!files.length) return;
    setUploadBusy(true); setErr("");
    let done = 0;
    try {
      for (const file of files) {
        const fileName = file.name || ("upload-" + job.id + "-" + Date.now());
        /* [blob-xss-field-2026-09-19] accept="application/pdf,image/*" on the input
           below does NOT exclude SVG — image/svg+xml matches image/* — and accept is
           only a picker hint anyway. Coerce active types to octet-stream so S3 stores
           and serves them non-renderable. Both the presign and the PUT header use this
           value; they must match. See the ACTIVE-CONTENT GUARD note near openExternal. */
        const fileType = ssSafeUploadMime(fileName, file.type || "application/octet-stream");
        const presign = await api.post("/api/uploads/presign", {
          fileName, fileType, fileSize: file.size, context: "job-documents", jobId: job.id,
        });
        if (!presign || !presign.presignedUrl) throw new Error("No upload URL from backend");
        const put = await fetch(presign.presignedUrl, {
          method: "PUT", headers: { "Content-Type": fileType }, body: ssS3Body(file, fileType, file && file.name), /* [ios-binary-upload-2026-10-07] */
        });
        if (!put.ok) throw new Error("S3 upload failed: " + put.status);
        await api.post("/api/uploads/complete", {
          key: presign.key, fileName, fileSize: file.size, fileType,
          context: "job-documents", jobId: job.id,
        });
        done++;
      }
      toast("ok", done + (done === 1 ? " document" : " documents") + " added to job");
      try { if (typeof reload === "function") await reload(); } catch (_) {}
      try {
        const rr = await api.get(`/api/server/jobs/${job.id}/documents`);
        const arr = Array.isArray(rr) ? rr : (Array.isArray(rr && rr.documents) ? rr.documents : []);
        setDocs(arr);
      } catch (_) { /* list refresh is best-effort */ }
    } catch (e2) {
      const msg = (e2 && e2.message) ? String(e2.message) : "Upload failed";
      setErr(msg); toast("err", msg);
    } finally {
      setUploadBusy(false);
    }
  }

  React.useEffect(() => {
    let cancelled = false;
    setDocs(null); setErr("");
    api.get(`/api/server/jobs/${job.id}/documents`)
      .then(r => {
        if (cancelled) return;
        const arr = Array.isArray(r) ? r : (Array.isArray(r && r.documents) ? r.documents : []);
        setDocs(arr);
      })
      .catch(e => { if (!cancelled) { setErr(e.message || "Could not load documents"); setDocs([]); } });
    return () => { cancelled = true; };
  }, [job && job.id]);

  function fmtSize(b) {
    const n = Number(b || 0);
    if (!n) return "—";
    if (n < 1024) return n + " B";
    if (n < 1024 * 1024) return (n / 1024).toFixed(0) + " KB";
    return (n / 1024 / 1024).toFixed(1) + " MB";
  }

  async function handleView(d) {
    if (!d.presigned_download_url) { setErr("This document is not available for viewing"); return; }
    /* [blob-xss-field-2026-09-19] An SVG/HTML/XML document opened here renders in the
       in-app browser at the S3 bucket origin — a foreign origin, so it cannot touch
       this app's JWT or the Capacitor bridge, but it is still attacker-supplied script
       and markup displayed inside the app's chrome. Refuse the preview; legitimate job
       documents are PDFs and photos. PDFs and images fall through unchanged. */
    if (ssIsActiveDoc(d)) { setErr(SS_ACTIVE_DOC_MSG); return; }
    /* [server-parity-web-2026-10-05] Inline, full-screen, in-app. The presigned S3
       URL (foreign origin) goes straight into the viewer's <iframe>/<img> — never
       re-wrapped in a same-origin blob — and the viewer keeps an "Open in browser"
       fallback that hands the same URL to openExternal. */
    const kind = /^(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(String(d.kind || "")) || /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(String(d.filename || ""))
      ? "image" : "pdf";
    ssOpenDocViewer({ title: d.filename || "Document", url: String(d.presigned_download_url), externalUrl: String(d.presigned_download_url), kind, filename: d.filename || "" });
  }

  async function handleDownload(d) {
    if (!d.presigned_download_url) { setErr("This document is not available for download"); return; }
    setBusyId(d.id);
    try {
      const Filesystem = ssPlugin("Filesystem"); // [server-parity-web-2026-10-05]
      if (Filesystem) {
        // Native path: fetch bytes, base64, write to Documents directory.
        const r = await fetch(d.presigned_download_url);
        if (!r.ok) throw new Error("Download failed (" + r.status + ")");
        const blob = await r.blob();
        const b64 = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(String(fr.result).split(",")[1] || "");
          fr.onerror = () => reject(new Error("read failed"));
          fr.readAsDataURL(blob);
        });
        // Capacitor Filesystem.writeFile (directory: 'DOCUMENTS' on iOS — visible in Files app)
        await Filesystem.writeFile({
          path: `strikeserve/job-${job.id}/${d.filename || ("document-" + d.id + ".pdf")}`,
          data: b64,
          directory: "DOCUMENTS",
          recursive: true,
        });
        setErr(""); // success
      } else {
        // Web / Filesystem plugin not installed: hand off to OS — Safari's share
        // sheet has "Save to Files" which is the documented fallback for V1.
        /* [blob-xss-field-2026-09-19] This fallback is the live path today —
           @capacitor/filesystem is not in package.json, so the branch above never
           runs and "Download" is currently identical to "View". That makes the
           active-type gate apply here too: handing the URL to the browser RENDERS
           the file, it does not save it. The Filesystem branch above stays
           unguarded on purpose — writing bytes to disk cannot execute them. */
        if (ssIsActiveDoc(d)) { setErr(SS_ACTIVE_DOC_MSG); return; }
        await openExternal(String(d.presigned_download_url));
      }
    } catch (e) {
      setErr(e.message || "Download failed");
    } finally {
      setBusyId(null);
    }
  }

  // Upload one scanned page's base64 JPEG through the EXISTING upload flow
  // (presign → PUT to S3 → /api/uploads/complete) and return its S3 key.
  // Reused verbatim from AttemptSheet/FilingCompleteSheet, context "scanned-doc".
  async function uploadScannedPage(b64, idx) {
    const context = "scanned-doc";
    const fileName = `${context}-${job.id}-${Date.now()}-${idx}.jpg`;
    const approxSize = Math.floor(b64.length * 0.75);
    const presign = await api.post("/api/uploads/presign", {
      fileName, fileType: "image/jpeg", fileSize: approxSize,
      context, jobId: job.id,
    });
    if (!presign || !presign.presignedUrl) throw new Error("No upload URL from backend");
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const put = await fetch(presign.presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": "image/jpeg" },
      body: ssS3Body(bytes, "image/jpeg", "photo.jpg"), /* [ios-binary-upload-2026-10-07] */
    });
    if (!put.ok) throw new Error("S3 upload failed: " + put.status);
    await api.post("/api/uploads/complete", {
      key: presign.key, fileName, fileSize: approxSize, fileType: "image/jpeg",
      context, jobId: job.id,
    });
    return presign.key;
  }

  const toast = (kind, text) => {
    try { window.dispatchEvent(new CustomEvent("ss-toast", { detail: { kind, text } })); } catch (_) {}
  };

  // Scan documents → assemble one PDF attached to the job. Capacitor 6 cannot
  // use the iOS VisionKit scanner plugin, so we capture pages one at a time
  // with the ALREADY-INSTALLED @capacitor/camera plugin (same plugin used by
  // AttemptSheet/FilingCompleteSheet) and run our own multi-page loop. Opening
  // the capture sheet immediately takes the first photo; from there the user
  // can add more pages, remove the last, or finish into a single PDF. Degrades
  // gracefully when the camera/native shell is absent (toast, no crash) so the
  // esbuild bundle runs and web builds don't throw.
  /* [retrieval-scan-1000-2026-09-29] [server-parity-web-2026-10-05] — capture, background
     upload, server-side PDF build. On the web build (no Camera plugin) the Scan
     button still reports "needs the installed app build", exactly as before. */
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  function newScanSession() {
    const a = new Uint8Array(12);
    try { crypto.getRandomValues(a); } catch (_) { for (let i = 0; i < a.length; i++) a[i] = Math.floor(Math.random() * 256); }
    scanSessionRef.current = (ssIsNative() ? ssPlatform() : "web") + Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
    nextIdxRef.current = 0;
    inflightRef.current = new Set();
    doneVerRef.current = new Map();
    failVerRef.current = new Map();
  }
  function makeThumb(b64) {
    return new Promise((res) => {
      const im = new Image();
      im.onload = () => {
        try {
          const w = 128, h = Math.max(1, Math.round((im.height * 128) / (im.width || 1)));
          const c = document.createElement("canvas"); c.width = w; c.height = h;
          c.getContext("2d").drawImage(im, 0, 0, w, h);
          res(c.toDataURL("image/jpeg", 0.6));
        } catch (_) { res(null); }
      };
      im.onerror = () => res(null);
      im.src = "data:image/jpeg;base64," + b64;
    });
  }
  function b64ToBytes(b64) {
    const bin = atob(b64); const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  async function uploadPage(pg) {
    const ver = pg.ver;
    let lastErr = null;
    for (let attempt = 1; attempt <= 4; attempt++) {
      try {
        let key;
        if (legacyUploadRef.current) {
          key = await uploadScannedPage(pg.b64, pg.idx);
        } else {
          let pr;
          try {
            pr = await api.post(`/api/jobs/${job.id}/scan-pages/presign`, {
              sessionId: scanSessionRef.current,
              pages: [{ index: pg.idx, contentType: "image/jpeg", size: Math.floor(pg.b64.length * 0.75) }],
            });
          } catch (e) {
            // Backend not yet updated: fall back to the original per-page upload path.
            if (e && e.status === 404) { legacyUploadRef.current = true; key = await uploadScannedPage(pg.b64, pg.idx); }
            else throw e;
          }
          if (!key) {
            const slot = pr && pr.pages && pr.pages[0];
            if (!slot || !slot.url) throw new Error("No upload URL from server");
            const put = await fetch(slot.url, { method: "PUT", headers: { "Content-Type": "image/jpeg" }, body: ssS3Body(b64ToBytes(pg.b64), "image/jpeg", "page.jpg") /* [ios-binary-upload-2026-10-07] */ });
            if (!put.ok) { const e = new Error("Upload failed (" + put.status + ")"); e.status = put.status; throw e; }
            key = slot.key;
          }
        }
        doneVerRef.current.set(pg.id, ver);
        // Stored: drop the full-size base64, keep the thumbnail.
        setCapturePages((prev) => prev.map((p) => (p.id === pg.id && p.ver === ver ? { ...p, up: "done", key, b64: null, err: "" } : p)));
        return;
      } catch (e) {
        lastErr = e;
        if (e && e.status && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429) break;
        await sleep(e && e.status === 429 ? 15000 : 1500 * attempt);
      }
    }
    failVerRef.current.set(pg.id, ver);
    setCapturePages((prev) => prev.map((p) => (p.id === pg.id && p.ver === ver ? { ...p, up: "error", err: (lastErr && lastErr.message) || "Upload failed" } : p)));
  }
  function pumpUploads() {
    while (uploadBusyRef.current < 2) {
      const next = pagesRef.current.find((p) => p.up === "pending" && p.b64 && !inflightRef.current.has(p.id)
        && doneVerRef.current.get(p.id) !== p.ver && failVerRef.current.get(p.id) !== p.ver);
      if (!next) break;
      inflightRef.current.add(next.id); uploadBusyRef.current++;
      uploadPage(next).finally(() => { uploadBusyRef.current--; inflightRef.current.delete(next.id); pumpUploads(); });
    }
  }
  useEffect(() => { pumpUploads(); }, [capturePages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture ONE page via the Camera plugin and queue it for upload.
  // Returns true if a page was added, false if the user cancelled / no camera.
  async function captureOnePage() {
    const Camera = ssPlugin("Camera");
    if (!ssIsNative() || !Camera) {
      toast("err", "Scanning needs the installed app build");
      return false;
    }
    if (pagesRef.current.length >= MAX_SCAN_PAGES) {
      toast("err", "Maximum 1,000 pages per PDF — tap Done, then start another scan");
      return false;
    }
    try {
      const p = await Camera.getPhoto({
        quality: 80,
        width: 2400, // long enough for legal text; the server normalizes to 2000px anyway
        // String literals are accepted by the Capacitor bridge and avoid
        // pulling in @capacitor/camera ESM types in this hand-bundled build.
        resultType: "base64",
        source: "CAMERA",
        saveToGallery: false,
        allowEditing: false,
        correctOrientation: true,
      });
      const b64 = (p && (p.base64String || p.base64)) || "";
      if (!b64) return false;
      const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
      const idx = nextIdxRef.current++;
      setCapturePages((prev) => [...prev, { id, idx, ver: 0, up: "pending", key: null, b64: String(b64), thumb: null, err: "" }]);
      makeThumb(b64).then((t) => { if (t) setCapturePages((prev) => prev.map((x) => (x.id === id ? { ...x, thumb: t } : x))); });
      return true;
    } catch (e) {
      // The native camera throws on user cancel — treat that as a no-op.
      const msg = (e && e.message) ? String(e.message) : "";
      if (!/cancel/i.test(msg)) toast("err", msg || "Could not capture page");
      return false;
    }
  }

  // Open the capture sheet and immediately take the first page.
  async function startScan() {
    if (scanBusy || captureBusy) return;
    const Camera = ssPlugin("Camera");
    if (!ssIsNative() || !Camera) {
      toast("err", "Scanning needs the installed app build");
      return;
    }
    setErr("");
    newScanSession();
    setBuildPhase("");
    setCapturePages([]);
    setCaptureOpen(true);
    setCaptureBusy(true);
    try {
      const added = await captureOnePage();
      if (!added) {
        // User cancelled the very first shot — close the sheet (need ≥1 page).
        setCaptureOpen(false);
        setCapturePages([]);
      }
    } finally {
      setCaptureBusy(false);
    }
  }

  // "Add another page" — capture one more page into the queue.
  async function addAnotherPage() {
    if (captureBusy) return;
    setCaptureBusy(true);
    try { await captureOnePage(); }
    finally { setCaptureBusy(false); }
  }

  // Remove the most recently captured page (its stored image is simply not used).
  function removeLastPage() {
    if (captureBusy || scanBusy) return;
    setCapturePages((prev) => prev.slice(0, -1));
  }

  async function refreshDocs() {
    try { if (typeof reload === "function") await reload(); } catch (_) {}
    try {
      const rr = await api.get(`/api/server/jobs/${job.id}/documents`);
      const arr = Array.isArray(rr) ? rr : (Array.isArray(rr && rr.documents) ? rr.documents : []);
      setDocs(arr);
    } catch (_) { /* list refresh is best-effort */ }
  }

  // "Done — create PDF": wait for the background uploads, then have the server
  // build the PDF (up to 1,000 pages) and attach it to the job. Progress is polled.
  async function finishScan() {
    if (captureBusy || scanBusy) return;
    if (!pagesRef.current.length) return; // require ≥1 page
    setScanBusy(true);
    setErr("");
    try {
      // Retry anything that failed earlier.
      failVerRef.current.clear();
      setCapturePages((prev) => prev.map((p) => (p.up === "error" ? { ...p, up: "pending", err: "" } : p)));
      await sleep(50);
      setBuildPhase("uploading");
      for (;;) {
        const ps = pagesRef.current;
        const done = ps.filter((p) => p.up === "done").length;
        setScanStatus(`Uploading ${done} of ${ps.length} page${ps.length === 1 ? "" : "s"}…`);
        if (done === ps.length) break;
        const errs = ps.filter((p) => p.up === "error");
        if (errs.length && !ps.some((p) => p.up === "pending") && inflightRef.current.size === 0) {
          throw new Error(`${errs.length} page${errs.length > 1 ? "s" : ""} could not upload (${errs[0].err}). Check your signal and tap Done again — uploaded pages are kept.`);
        }
        await sleep(600);
      }
      const ps = pagesRef.current;
      const keys = ps.map((p) => p.key);
      setBuildPhase("building");
      setScanStatus("Starting PDF…");
      const r = await api.post(`/api/jobs/${job.id}/assemble-scan-pdf`, {
        pageKeys: keys,
        sessionId: scanSessionRef.current,
        name: `Retrieved documents ${new Date().toLocaleDateString()}`,
      });
      if (r && r.assemblyId) {
        let netErrs = 0;
        for (;;) {
          let st;
          try { st = await api.get(`/api/jobs/${job.id}/scan-assemblies/${r.assemblyId}`); netErrs = 0; }
          catch (e) {
            if (e && e.status && e.status < 500) throw e;
            if (++netErrs > 15) throw new Error("Lost connection while the PDF was building. It may still finish — check this job's documents.");
            await sleep(3000); continue;
          }
          setScanStatus(`Building PDF ${st.pagesDone || 0} of ${st.pageCount || keys.length} pages…`);
          if (st.status === "done") break;
          if (st.status === "failed") throw new Error(st.error || "The PDF could not be built");
          await sleep(2000);
        }
      } else if (!(r && (r.ok || r.pdf))) {
        throw new Error("The server did not accept the scan");
      }
      toast("ok", `Scanned ${keys.length} page${keys.length === 1 ? "" : "s"} — PDF added to job`);
      setCaptureOpen(false);
      setCapturePages([]);
      setBuildPhase("");
      await refreshDocs();
    } catch (e) {
      const msg = (e && e.message) ? String(e.message) : "";
      setErr(msg || "Document scan failed");
      toast("err", msg || "Document scan failed");
      setBuildPhase("");
    } finally {
      setScanBusy(false);
      setScanStatus("");
    }
  }

  // Close the capture sheet. While the server is building, closing is safe (the
  // PDF still lands on the job). With pages still uploading, confirm first.
  function cancelScan() {
    if (buildPhase === "building") {
      setCaptureOpen(false);
      toast("ok", "The PDF is still building — it will appear in this job's documents shortly");
      return;
    }
    if (scanBusy) return;
    if (pagesRef.current.length && !window.confirm("Discard this scan? Nothing has been saved to the job yet.")) return;
    setCaptureOpen(false);
    setCapturePages([]);
  }

  return (
    <div style={{
      background: C.white, borderRadius: 14, padding: 14, marginBottom: 12,
      border: `1px solid ${C.steelXL}`,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 10 }}>DOCUMENTS</div>

      {/* Loading: 2 skeleton rows so the layout doesn't jump when docs arrive */}
      {docs === null && (
        <div>
          {[0, 1].map(i => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: i === 0 ? 10 : 0 }}>
              <SkeletonRect width={36} height={44} radius={6} />
              <div style={{ flex: 1 }}>
                <SkeletonRect width="70%" height={12} style={{ marginBottom: 6 }} />
                <SkeletonRect width="40%" height={10} />
              </div>
              <SkeletonRect width={56} height={28} radius={8} />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {docs !== null && docs.length === 0 && !err && (
        <div style={{ fontSize: 12, color: C.steel, padding: "8px 0", lineHeight: 1.5 }}>
          No documents shared by the client for this job.
        </div>
      )}

      {/* Error banner — does not block any already-rendered docs */}
      {err && (
        <div style={{
          fontSize: 11, color: C.err, background: C.errL, padding: "8px 10px",
          borderRadius: 8, marginBottom: docs && docs.length ? 10 : 0,
        }}>{err}</div>
      )}

      {/* Document list */}
      {docs !== null && docs.length > 0 && ssSortServeDocs(docs).map((d, idx) => ( /* [workflow-audit-2026-10-28 V-42] to-be-served files first */
        <div key={d.id} style={{
          display: "flex", alignItems: "center", gap: 10,
          paddingTop: idx === 0 ? 0 : 10,
          borderTop: idx === 0 ? "none" : `1px solid ${C.steelXXL}`,
          marginTop: idx === 0 ? 0 : 10,
        }}>
          <div style={{
            width: 36, height: 44, borderRadius: 6, background: C.errL,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <FileText size={18} color={C.err} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: C.deep,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{d.filename}</div>
            <div style={{ fontSize: 11, color: C.steel, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {ssIsServeDoc(d) ? (
                /* [workflow-audit-2026-10-28 V-42] contract C5: the backend flags the files to hand the recipient */
                <span style={{ background: C.goldXL, color: C.deep, fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 6, letterSpacing: "0.02em" }}>To be served</span>
              ) : null}
              <span>{d.kind || "PDF"} · {fmtSize(d.size_bytes)}</span>
            </div>
          </div>
          <button
            onClick={() => handleView(d)}
            disabled={!d.presigned_download_url}
            aria-label={"View " + d.filename}
            style={{
              padding: "6px 10px", borderRadius: 8, background: C.bg,
              border: `1px solid ${C.steelXL}`, fontSize: 11, fontWeight: 700,
              color: d.presigned_download_url ? C.deep : C.steel,
              cursor: d.presigned_download_url ? "pointer" : "not-allowed",
            }}
          >View</button>
          <button
            onClick={() => handleDownload(d)}
            disabled={!d.presigned_download_url || busyId === d.id}
            aria-label={"Download " + d.filename}
            style={{
              width: 32, height: 32, borderRadius: 8, background: C.deep,
              border: "none", display: "flex", alignItems: "center", justifyContent: "center",
              cursor: d.presigned_download_url ? "pointer" : "not-allowed",
              opacity: busyId === d.id ? 0.6 : 1,
            }}
          >
            <Download size={14} color={C.white} />
          </button>
        </div>
      ))}

      {/* Scan documents — Camera multi-page capture → assembled PDF attached to
          this job. Hidden while the doc list is still loading. */}
      {docs !== null && (
        <button
          onClick={startScan}
          disabled={scanBusy || captureBusy}
          aria-label="Scan documents"
          style={{
            width: "100%", marginTop: (docs.length > 0 ? 12 : 0), padding: 12,
            background: (scanBusy || captureBusy) ? C.bg : C.white,
            border: `1.5px dashed ${C.steelL}`, borderRadius: 12,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            cursor: (scanBusy || captureBusy) ? "wait" : "pointer",
          }}
        >
          <ScanLine size={18} color={C.deep} />
          <span style={{ fontSize: 12, fontWeight: 700, color: C.deep }}>
            {scanBusy ? (scanStatus || "Scanning…") : "Scan documents"}
          </span>
        </button>
      )}

      {/* [job-doc-upload-2026-07-22] Upload from Photos or Files (saved photos,
          PDFs) — sibling of Scan documents above. */}
      {docs !== null && (
        <>
          <button
            onClick={() => { if (!uploadBusy && uploadInputRef.current) uploadInputRef.current.click(); }}
            disabled={uploadBusy || scanBusy || captureBusy}
            aria-label="Upload from Photos or Files"
            style={{
              width: "100%", marginTop: 8, padding: 12,
              background: uploadBusy ? C.bg : C.white,
              border: `1.5px dashed ${C.steelL}`, borderRadius: 12,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              cursor: uploadBusy ? "wait" : "pointer",
            }}
          >
            <Upload size={18} color={C.deep} />
            <span style={{ fontSize: 12, fontWeight: 700, color: C.deep }}>
              {uploadBusy ? "Uploading…" : "Upload from Photos or Files"}
            </span>
          </button>
          <input ref={uploadInputRef} type="file" accept="application/pdf,image/*" multiple
                 onChange={onPickedUploadFiles} style={{ display: "none" }} />
        </>
      )}

      {/* ── Multi-page capture sheet ─────────────────────────────────────
          Modal state machine over capturePages[]. Shows the running page
          count, lets the user add another page (Camera), remove the last
          page, or finish into one assembled PDF (requires ≥1 page). Matches
          the file's dark scrim + bottom-sheet styling. */}
      {captureOpen && (
        <>
          <div
            onClick={cancelScan}
            style={{
              position: "absolute", inset: 0, background: "rgba(10,22,40,0.55)", zIndex: 300,
            }}
          />
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            background: C.bg, borderRadius: "24px 24px 0 0",
            zIndex: 310, display: "flex", flexDirection: "column",
            maxHeight: "92%", overflow: "hidden",
            boxShadow: "0 -10px 40px rgba(0,0,0,0.3)",
            padding: "16px 16px calc(env(safe-area-inset-bottom) + 16px)",
          }}>
            <div style={{
              width: 40, height: 4, background: C.steelL, borderRadius: 2,
              margin: "0 auto 14px", flexShrink: 0,
            }} />

            {/* Header: title + running page count + close */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" }}>
                Scan documents
              </div>
              <button
                onClick={cancelScan}
                disabled={scanBusy && buildPhase !== "building"}
                aria-label="Cancel scan"
                style={{
                  width: 32, height: 32, borderRadius: 10, background: C.white,
                  border: `1px solid ${C.steelXL}`, display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: scanBusy ? "not-allowed" : "pointer", padding: 0,
                }}
              >
                <X size={16} color={C.steel} />
              </button>
            </div>
            <div style={{ fontSize: 12, color: C.steel, marginBottom: 14 }}>
              {capturePages.length === 0
                ? "No pages captured yet."
                : `${capturePages.length} of ${MAX_SCAN_PAGES} pages · ${capturePages.filter((p) => p.up === "done").length} uploaded${capturePages.some((p) => p.up === "error") ? " · some failed, Done retries them" : ""}`}
            </div>

            {/* Error banner */}
            {err && (
              <div style={{
                fontSize: 11, color: C.err, background: C.errL, padding: "8px 10px",
                borderRadius: 8, marginBottom: 12,
              }}>{err}</div>
            )}

            {/* Captured-page thumbnails (most-recent last). The last page can be
                removed; the rest are read-only previews. */}
            {capturePages.length > 0 && (
              <div style={{
                display: "flex", gap: 8, overflowX: "auto", paddingBottom: 6, marginBottom: 14,
              }}>
                {capturePages.map((pg, i) => {
                  const isLast = i === capturePages.length - 1;
                  return (
                    <div key={pg.id} style={{ position: "relative", flexShrink: 0 }}>
                      <div style={{
                        width: 64, height: 84, borderRadius: 8, overflow: "hidden",
                        border: `1px solid ${C.steelXL}`, background: C.white,
                      }}>
                        {(pg.thumb || pg.b64) ? (
                          <img
                            src={pg.thumb || `data:image/jpeg;base64,${pg.b64}`}
                            alt={`Page ${i + 1}`}
                            loading="lazy"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : null}
                      </div>
                      <span
                        title={pg.up === "done" ? "Uploaded" : pg.up === "error" ? `Upload failed: ${pg.err}` : "Uploading"}
                        style={{
                          position: "absolute", top: 4, left: 4, width: 8, height: 8, borderRadius: "50%",
                          background: pg.up === "done" ? "#22c55e" : pg.up === "error" ? "#ef4444" : "#f59e0b",
                          boxShadow: "0 0 0 1px rgba(0,0,0,0.35)",
                        }}
                      />
                      <div style={{
                        position: "absolute", bottom: 2, left: 2, fontSize: 9, fontWeight: 800,
                        color: C.white, background: "rgba(10,22,40,0.7)", borderRadius: 6, padding: "1px 5px",
                      }}>{i + 1}</div>
                      {isLast && !scanBusy && (
                        <button
                          onClick={removeLastPage}
                          disabled={captureBusy}
                          aria-label="Remove last page"
                          style={{
                            position: "absolute", top: -6, right: -6, width: 22, height: 22, borderRadius: "50%",
                            background: C.err, border: `2px solid ${C.bg}`, display: "flex",
                            alignItems: "center", justifyContent: "center", cursor: captureBusy ? "not-allowed" : "pointer", padding: 0,
                          }}
                        >
                          <Trash2 size={11} color={C.white} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Upload/assemble progress (shown while finishing) */}
            {scanBusy && scanStatus && (
              <div style={{ fontSize: 12, color: C.steel, marginBottom: 12, textAlign: "center" }}>
                {scanStatus}
              </div>
            )}

            {/* Actions: Add another page + Done. Done is disabled until ≥1 page. */}
            <button
              onClick={addAnotherPage}
              disabled={captureBusy || scanBusy || capturePages.length >= MAX_SCAN_PAGES}
              aria-label="Add another page"
              style={{
                width: "100%", padding: 13, marginBottom: 10,
                background: C.white, border: `1.5px dashed ${C.steelL}`, borderRadius: 12,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                cursor: (captureBusy || scanBusy) ? "wait" : "pointer",
              }}
            >
              <Camera size={18} color={C.deep} />
              <span style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>
                {captureBusy && !scanBusy ? "Opening camera…" : "Add another page"}
              </span>
            </button>
            <button
              onClick={finishScan}
              disabled={capturePages.length === 0 || captureBusy || scanBusy}
              aria-label="Done — create PDF"
              style={{
                width: "100%", padding: 14,
                background: (capturePages.length === 0 || captureBusy || scanBusy) ? C.steelL : C.deep,
                border: "none", borderRadius: 12,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                cursor: (capturePages.length === 0 || captureBusy || scanBusy) ? "not-allowed" : "pointer",
              }}
            >
              <Check size={18} color={C.white} strokeWidth={3} />
              <span style={{ fontSize: 14, fontWeight: 800, color: C.white }}>
                {scanBusy ? (scanStatus || "Creating PDF…") : "Done — create PDF"}
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* [attempt-photo-visibility-2026-07-27] Attempt photos were write-only in this app:
   takePhoto() uploads to S3 and submitAttempt() persists s3Key, but nothing here could
   ever read one back. Key precedence mirrors the backend's own shaping — s3Key is
   canonical, and the watermarker rewrites photo.s3Key IN PLACE, so never derive a
   "-wm" key on the client. */
function attemptPhotoKey(p) {
  if (!p) return null;
  if (typeof p === "string") return p;
  return p.s3Key || p.key || p.s3_key || null;
}

/* [attempt-photo-visibility-2026-07-27] POST /api/uploads/presign-download returns the
   same signed URL under three names for client back-compat; prefer downloadUrl. Valid
   1 hour. NOT the DocumentsTab flow — job documents arrive with a server-rendered
   d.presigned_download_url and need no call at all. */
async function presignAttemptPhoto(key) {
  const r = await api.post("/api/uploads/presign-download", { key });
  const url = r && (r.downloadUrl || r.url || r.presignedUrl);
  if (!url) throw new Error("No download URL returned");
  return String(url);
}

/* [attempt-photo-visibility-2026-07-27] Thumbnail strip for one attempt's photos.
   Every photo lands in exactly one of three VISIBLE states — resolving, resolved, or
   failed. A failure renders a labelled placeholder, never a silently broken <img>: on a
   sworn diligence record, "this photo could not be loaded" and "there is no photo" must
   not look the same. Tap enlarges into the same full-screen overlay pattern the QR
   scanner uses. All setState calls are gated on an alive ref — presign is a network
   round-trip and the sheet can close under it. */
function AttemptPhotoStrip({ photos }) {
  const list = (Array.isArray(photos) ? photos : []).filter(p => attemptPhotoKey(p));
  const [state, setState] = useState({}); // key -> { status: "loading"|"ok"|"error", url }
  const [zoom, setZoom] = useState(null); // { url, idx }
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Signature keeps the resolve effect stable across re-renders of the same photo set.
  const keysSig = list.map(p => attemptPhotoKey(p)).join("|");
  useEffect(() => {
    let cancelled = false;
    const keys = keysSig ? keysSig.split("|") : [];
    if (!keys.length) return undefined;
    setState(prev => {
      const next = { ...prev };
      keys.forEach(k => { if (!next[k] || next[k].status === "error" || next[k].status === "missing") next[k] = { status: "loading" }; });
      return next;
    });
    keys.forEach(k => {
      presignAttemptPhoto(k)
        .then(url => {
          if (cancelled || !aliveRef.current) return;
          setState(prev => ({ ...prev, [k]: { status: "ok", url } }));
        })
        .catch(e => {
          if (cancelled || !aliveRef.current) return;
          /* [pos-overhaul-2026-10-07:server-app] presign HEAD-checks the object now:
             404 {error:'missing_from_storage'} means the bytes never reached S3. */
          const missing = !!(e && e.status === 404 && /missing_from_storage/i.test(String((e.body && (e.body.error || e.body.code)) || e.message || "")));
          setState(prev => ({ ...prev, [k]: { status: missing ? "missing" : "error", err: missing ? "Missing from storage" : ((e && e.message) || "unavailable") } }));
        });
    });
    return () => { cancelled = true; };
  }, [keysSig]);

  if (!list.length) return null;

  return (
    <>
      <div style={{ display: "flex", gap: 8, overflowX: "auto", marginTop: 10, paddingBottom: 2 }}>
        {list.map((p, i) => {
          const k = attemptPhotoKey(p);
          const st = state[k] || { status: "loading" };
          const failed = st.status === "error" || st.status === "missing";
          return (
            <div
              key={k + "-" + i}
              onClick={st.status === "ok" ? () => setZoom({ url: st.url, idx: i }) : undefined}
              title={failed ? ("Photo unavailable: " + (st.err || "could not be loaded")) : undefined}
              style={{
                flexShrink: 0, width: 64, height: 64, borderRadius: 8, overflow: "hidden",
                border: `1px solid ${failed ? C.err : C.steelXL}`,
                background: failed ? C.errL : C.bg,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: st.status === "ok" ? "zoom-in" : "default",
              }}
            >
              {st.status === "ok" ? (
                <img
                  src={st.url}
                  alt={"Attempt photo " + (i + 1)}
                  onError={() => {
                    if (!aliveRef.current) return;
                    setState(prev => ({ ...prev, [k]: { status: "error", err: "image failed to load" } }));
                  }}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : failed ? (
                <div style={{ textAlign: "center", padding: 2 }}>
                  <AlertTriangle size={14} color={C.err} />
                  <div style={{ fontSize: 8, fontWeight: 800, color: C.err, lineHeight: 1.15, marginTop: 2 }}>{st.status === "missing" ? "Missing from storage" : "Photo unavailable"}</div>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: 2 }}>
                  <ImageIcon size={14} color={C.steelL} />
                  <div style={{ fontSize: 8, fontWeight: 700, color: C.steel, lineHeight: 1.15, marginTop: 2 }}>Loading…</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* [pos-overhaul-2026-10-07:server-app] */}
      {list.some(p => (state[attemptPhotoKey(p)] || {}).status === "missing") && (
        <div style={{ fontSize: 11, fontWeight: 700, color: C.err, marginTop: 6 }}>
          Missing from storage — add it again
        </div>
      )}

      {zoom && (
        <div onClick={() => setZoom(null)} style={{
          position: "fixed", inset: 0, zIndex: 9000,
          background: "rgba(8,12,20,0.94)", display: "flex", flexDirection: "column",
          paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 16px 8px" }}>
            <div style={{ color: C.white, fontSize: 16, fontWeight: 800, letterSpacing: "-0.01em" }}>
              Attempt photo {zoom.idx + 1} of {list.length}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setZoom(null); }}
              aria-label="Close photo"
              style={{
                width: 38, height: 38, borderRadius: 12, background: "rgba(255,255,255,0.12)",
                border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0,
              }}
            >
              <X size={22} color={C.white} />
            </button>
          </div>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 16px 18px", minHeight: 0 }}>
            <img src={zoom.url} alt={"Attempt photo " + (zoom.idx + 1)}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 12 }} />
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ATTEMPTS TAB — Custom time-window grid (not PROOF's checklist)
// ═══════════════════════════════════════════════════════════════════════
function AttemptsTab({ job, onRecord }) {
  const _baseAttempts = Array.isArray(job._raw && job._raw.attempts) ? job._raw.attempts : (Array.isArray(job.rawAttempts) ? job.rawAttempts : []);
  /* [pos-overhaul-2026-10-07:server-app] photos added to an attempt after the fact come
     back on the evidence response; overlay them so the row updates without waiting
     for the job list to reload (the open sheet holds a snapshot of the job). */
  const [attemptOverrides, setAttemptOverrides] = useState({});
  const realAttempts = _baseAttempts.map((a, i) => attemptOverrides[(a && a.id) || `idx-${i}`] || a);
  const { reload: _reloadJobs } = React.useContext(AppCtx);
  const _canAddEvidence = job.status === "active" || job.status === "completed";
  const windows = [
    { id: "day", label: "Weekday daytime", time: "Mon–Fri 7am – 6pm", icon: Sun },
    { id: "eve", label: "Weekday evening", time: "Mon–Fri 6pm – 10pm", icon: Moon },
    { id: "weekend", label: "Weekend", time: "Sat / Sun", icon: Calendar },
  ];

  /* [field-command-2026-09-11] `job.windows` is normalized from `j.windows`, and no
     such column exists on a job — so this grid has read a hardcoded all-false object
     since it was written and every job in the app shows "0/4 windows covered" no
     matter how many attempts it carries. Derive the coverage from the attempt
     timestamps the backend already writes. */
  const _dd = ssDeriveWindows(job);
  const _windowsCovered = _dd.windows;
  const completed = _dd.covered;
  /* [sub-serve-mailing-2026-09-18] The outstanding-work reminder for this job.
      Reads the same `mailDate` the backend's POS preflight reads, so what the
      server is told here and what blocks the proof cannot drift apart. */
  const _mailPending = ssJobMailingDue(job);

  return (
    <div style={{ padding: "16px 14px" }}>
      {_canAddEvidence ? <PosProfileBanner context="attempt" /> : null}{/* [pos-overhaul-2026-10-07:server-app] */}
      {/* [sub-serve-mailing-2026-09-18] Outstanding §415.20(b) follow-up mailing. */}
      {_mailPending && (
        <div style={{
          background: C.errL, border: `1.5px solid ${C.err}`, borderRadius: 14,
          padding: 14, marginBottom: 14,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={18} color={C.err} />
            <div style={{ fontSize: 13, fontWeight: 800, color: C.err }}>
              Follow-up mailing still owed — CCP §415.20(b)
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.deep, marginTop: 8, lineHeight: 1.55 }}>
            You left the copies on attempt #{Math.max(1, Number(_mailPending.attemptNumber) || (realAttempts.indexOf(_mailPending) + 1))}
            {_mailPending.subName ? ` with ${_mailPending.subName}` : ""}, and the follow-up copy
            has not been recorded as mailed. <b>Substituted service is not complete</b>, the 10-day
            period has not started, and the proof of service cannot issue until it is.
          </div>
          <div style={{ fontSize: 12, color: C.deep, marginTop: 8, lineHeight: 1.55 }}>
            <b>Do this:</b> mail a copy of everything you left, by first-class mail, to
            {" "}{jobDisplay(job).fullAddress || "the address where you left the copies"} — then
            record the date and the city you mailed it from.
          </div>
          <div style={{ fontSize: 11, color: C.steel, marginTop: 8 }}>
            Recording a completed mailing after the fact is not built yet — tell dispatch the date
            and city so it can be added to the attempt record.
          </div>
        </div>
      )}
      {/* Compliance card */}
      <div style={{
        background: `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
        borderRadius: 14, padding: 14, marginBottom: 14, color: C.white,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: C.goldL }}>DUE DILIGENCE</div>
            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>windows {completed}/3 · attempts {_dd.attempts}/{SS_ATTEMPTS_INCLUDED}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{
              width: 50, height: 50, borderRadius: "50%",
              background: `conic-gradient(${C.gold} ${completed * 120}deg, rgba(255,255,255,0.15) 0)`,
              display: "flex", alignItems: "center", justifyContent: "center", position: "relative",
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: "50%", background: C.deep,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, fontWeight: 800,
              }}>{Math.round((completed / 3) * 100)}%</div>
            </div>
          </div>
        </div>
        {/* [field-command-2026-09-11] California requires REASONABLE DILIGENCE, not a
            fixed number of attempts. Four windows is StrikeServe policy and has to be
            labelled as such — the previous wording read as a legal requirement. */}
        <div style={{ fontSize: 11, opacity: 0.8, lineHeight: 1.5 }}>
          Three windows is StrikeServe policy, not California law. The standard is reasonable diligence;
          attempts spread across different times and a weekend are the usual way to show it.
        </div>
      </div>

      {/* Window grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        {windows.map(w => {
          const done = !!_windowsCovered[w.id]; // [field-command-2026-09-11] derived, not the absent column
          const Icon = w.icon;
          return (
            <div key={w.id} style={{
              background: done ? C.okL : C.white, borderRadius: 14, padding: 14,
              border: `1.5px solid ${done ? C.ok : C.steelXL}`, position: "relative",
            }}>
              {done && (
                <div style={{
                  position: "absolute", top: 8, right: 8, width: 18, height: 18, borderRadius: "50%",
                  background: C.ok, display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Check size={12} color={C.white} strokeWidth={3} />
                </div>
              )}
              <Icon size={18} color={done ? C.ok : C.steel} />
              <div style={{ fontSize: 13, fontWeight: 800, color: done ? C.ok : C.deep, marginTop: 6 }}>{w.label}</div>
              <div style={{ fontSize: 10, color: C.steel, marginTop: 1 }}>{w.time}</div>
            </div>
          );
        })}
      </div>

      {/* Attempt history */}
      <div style={{ fontSize: 13, fontWeight: 800, color: C.deep, marginBottom: 10, letterSpacing: "-0.01em" }}>
        Attempt history
      </div>

      {realAttempts.length === 0 ? (
        <div style={{
          background: C.white, borderRadius: 14, padding: 24, textAlign: "center",
          border: `1.5px dashed ${C.steelL}`,
        }}>
          <Circle size={28} color={C.steelL} style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 13, color: C.steel, fontWeight: 600 }}>No previous attempts</div>
          <div style={{ fontSize: 11, color: C.steel, marginTop: 4 }}>Tap "Log Attempt" below to start</div>
        </div>
      ) : (
        // Drive each row from real attempt data — success flag, timestamp, and result string come from the API.
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {realAttempts.map((a, i) => {
            const result = String(a.result || a.outcome || "").toLowerCase(); /* [recon-server-web-2026-10-21] legacy a.outcome fallback, as the backend */
            /* [attempt-badge-served-2026-10-20] Same success test as the backend (server.js
               applyServiceAttempt / _ss_deriveServiceManner): 'served' | 'sub-served' | 'served-*'.
               Only the first two were checked, so served-personal, served-substitute-*,
               served-by-posting, served-registered-agent etc. all showed a red "Unsuccessful". */
            const success = result === "served" || result === "sub-served" || result.indexOf("served-") === 0;
            /* [attempt-photo-visibility-2026-07-27] The backend writes the attempt date as
               `date` (server.js newAttempt: `date: _b1_attemptDate`). None of attempted_at /
               timestamp / created_at is ever written, so every row rendered "Time unknown".
               `date` goes FIRST; the legacy names stay as a fallback for any other producer. */
            const ts = a.date || a.attempted_at || a.timestamp || a.created_at;
            const when = ts ? new Date(ts) : null;
            const label = when && !isNaN(when.getTime())
              ? when.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
              : "Time unknown";
            const _noteBase = a.notes || a.outcome || (success ? "Served" : (result || "No answer"));
            /* [court-closed-not-counted-2026-10-08] */
            const note = ssCountsTowardAttempts(a) ? _noteBase : ("Court closed — not counted toward the client's 3 attempts. " + _noteBase);
            return (
              /* [attempt-photo-visibility-2026-07-27] `a` already carries the evidence —
                 photos, photosRejected and photoCount were simply never handed to the row. */
              <AttemptRow
                key={a.id || i}
                num={i + 1}
                success={success}
                when={label}
                note={note}
                photos={a.photos}
                photosRejected={a.photosRejected}
                photoCount={a.photoCount}
                addPhoto={_canAddEvidence ? (
                  <AttemptAddPhoto job={job} attemptRef={(a && a.id) || `idx-${i}`}
                    onAdded={(att) => {
                      const ref = (a && a.id) || `idx-${i}`;
                      if (att && typeof att === "object") setAttemptOverrides(prev => ({ ...prev, [ref]: att }));
                      try { _reloadJobs && _reloadJobs(); } catch (_) {}
                    }} />
                ) : null}
              />
            );
          })}
        </div>
      )}

      {/* Record button */}
      <button onClick={onRecord} style={{
        width: "100%", marginTop: 14, padding: 14,
        background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
        color: C.deep, border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        boxShadow: `0 4px 12px ${C.gold}55`, letterSpacing: "0.02em",
      }}>
        <Plus size={18} /> Record a new attempt
      </button>
    </div>
  );
}

function AttemptRow({ num, success, when, note, photos, photosRejected, photoCount, addPhoto }) {
  // when/note default to the old placeholder only if the caller didn't pass real data (e.g. tests or legacy callers).
  const meta = (when || note)
    ? [when, note].filter(Boolean).join(" · ")
    : "Mon May 4, 3:42 PM · No answer";
  /* [attempt-photo-visibility-2026-07-27] Evidence state for this attempt.
     - _apvPhotos:   photos that actually carry an S3 reference (viewable).
     - _apvRejected: photos the backend recorded as FAILED to upload. No client has ever
       surfaced these; lost evidence must be visible on the face of the attempt.
     - _apvOrphanCount: photoCount claims photos but no references were stored (every
       attempt logged from the iOS app before today). Say so rather than showing nothing. */
  const _apvPhotos = Array.isArray(photos) ? photos.filter(p => attemptPhotoKey(p)) : [];
  const _apvRejected = Array.isArray(photosRejected) ? photosRejected.filter(Boolean) : [];
  const _apvOrphanCount = (Number(photoCount) > 0 && _apvPhotos.length === 0) ? Number(photoCount) : 0;
  return (
    <div style={{
      background: C.white, borderRadius: 12, padding: "12px 14px",
      border: `1px solid ${C.steelXL}`,
      boxShadow: `0 1px 4px ${C.deep}0d`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: success ? C.okL : C.errL,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: success ? C.ok : C.err, fontSize: 14, fontWeight: 800, flexShrink: 0,
        }}>{num}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>Attempt #{num}</div>
          <div style={{ fontSize: 11, color: C.steel, marginTop: 1 }}>{meta}</div>
        </div>
        <Pill bg={success ? C.okL : C.errL} color={success ? C.ok : C.err}>
          {success ? "Success" : "Unsuccessful"}
        </Pill>
      </div>

      {/* [attempt-photo-visibility-2026-07-27] Photo evidence */}
      {_apvPhotos.length > 0 && <AttemptPhotoStrip photos={_apvPhotos} />}

      {_apvRejected.length > 0 && (
        <div style={{
          marginTop: 10, background: C.errL, border: `1px solid ${C.err}`, borderRadius: 8,
          padding: "8px 10px", display: "flex", alignItems: "flex-start", gap: 8,
        }}>
          <AlertTriangle size={14} color={C.err} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: C.err }}>
              {_apvRejected.length} photo{_apvRejected.length === 1 ? "" : "s"} failed to upload — not saved with this attempt
            </div>
            <div style={{ fontSize: 10, color: C.err, opacity: 0.85, marginTop: 2, wordBreak: "break-word" }}>
              {_apvRejected.map(r => (r && r.name) || "unnamed").join(", ")}
            </div>
          </div>
        </div>
      )}

      {_apvOrphanCount > 0 && (
        <div style={{
          marginTop: 10, background: C.warnL, border: `1px solid ${C.warn}`, borderRadius: 8,
          padding: "8px 10px", display: "flex", alignItems: "flex-start", gap: 8,
        }}>
          <AlertTriangle size={14} color={C.warn} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 11, fontWeight: 700, color: C.warn }}>
            This attempt records {_apvOrphanCount} photo{_apvOrphanCount === 1 ? "" : "s"}, but no image reference was stored — the photo{_apvOrphanCount === 1 ? " is" : "s are"} not retrievable.
          </div>
        </div>
      )}
      {addPhoto /* [pos-overhaul-2026-10-07:server-app] */}
    </div>
  );
}

/* [pos-overhaul-2026-10-07:server-app] "Add photo" on an EXISTING attempt.
   Camera / photo library -> presign + PUT + /api/uploads/complete (context
   attempt-photo, so the key passes the backend's own-upload check) ->
   POST /api/jobs/:id/attempts/:attemptId/evidence. The backend stamps each photo
   addedLater:true; the attempt's date, GPS and result are never changed. */
function AttemptAddPhoto({ job, attemptRef, onAdded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { kind: "ok" | "err", text }
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  async function attach(recs) {
    const r = await api.post(`/api/jobs/${encodeURIComponent(job.id)}/attempts/${encodeURIComponent(attemptRef)}/evidence`, { photos: recs });
    if (!alive.current) return;
    const n = Number(r && r.added);
    setMsg({ kind: "ok", text: (isFinite(n) ? n : recs.length) === 1 ? "Photo added to this attempt." : `${isFinite(n) ? n : recs.length} photos added to this attempt.` });
    try { onAdded && onAdded(r && r.attempt); } catch (_) {}
  }
  function errText(e) {
    if (e && e.status === 404 && !(e.body && e.body.error)) return "Adding photos to an earlier attempt isn't available yet.";
    return (e && e.body && e.body.error) || (e && e.message) || "Could not add the photo.";
  }

  async function onPick(ev) {
    const files = Array.from((ev && ev.target && ev.target.files) || []);
    if (ev && ev.target) ev.target.value = "";
    if (!files.length) return;
    setBusy(true); setMsg(null);
    const recs = []; const failures = [];
    for (const f of files) {
      try {
        const bytes = await ssFileToBytes(f);
        const mime = ssImageMimeOf(f, bytes);
        if (!/^image\//.test(mime)) { failures.push(`${f.name}: not a photo`); continue; }
        const fileName = `attempt-${job.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ssExtForMime(mime)}`;
        const rec = await ssUploadEvidence(api, { bytes, mime, fileName, context: "attempt-photo", jobId: job.id });
        recs.push({ s3Key: rec.key, fileUrl: rec.fileUrl || null, name: f.name || fileName, size: bytes.byteLength,
          capturedAt: f.lastModified ? new Date(f.lastModified).toISOString() : null });
      } catch (e) { failures.push(`${f.name}: ${(e && e.message) || "upload failed"}`); }
    }
    try {
      if (recs.length) await attach(recs);
      if (failures.length && alive.current) setMsg({ kind: "err", text: `${failures.length} photo(s) were not added — ${failures.join("; ")}` });
    } catch (e) { if (alive.current) setMsg({ kind: "err", text: errText(e) }); }
    finally { if (alive.current) setBusy(false); }
  }

  async function add() {
    if (busy) return;
    const Camera = ssPlugin("Camera");
    if (!Camera) { if (inputRef.current) inputRef.current.click(); return; }
    setBusy(true); setMsg(null);
    try {
      const p = await Camera.getPhoto({
        quality: 85, resultType: "base64", source: "PROMPT", saveToGallery: false, correctOrientation: true,
        promptLabelHeader: "Add photo to this attempt", promptLabelPhoto: "Choose from library", promptLabelPicture: "Take photo",
      });
      const b64 = (p && (p.base64String || p.base64)) || "";
      if (!b64) return;
      const bytes = ssB64ToBytes(b64);
      const fmt = String((p && p.format) || "jpeg").toLowerCase();
      const mime = ssSniffImageMime(bytes, fmt === "heic" || fmt === "heif" ? "image/" + fmt : fmt === "png" ? "image/png" : "image/jpeg");
      const fileName = `attempt-${job.id}-${Date.now()}.${ssExtForMime(mime)}`;
      const rec = await ssUploadEvidence(api, { bytes, mime, fileName, context: "attempt-photo", jobId: job.id });
      await attach([{ s3Key: rec.key, fileUrl: rec.fileUrl || null, name: fileName, size: bytes.byteLength }]);
    } catch (e) {
      const m = (e && e.message) ? String(e.message) : "";
      if (!/cancel/i.test(m) && alive.current) setMsg({ kind: "err", text: errText(e) });
    } finally { if (alive.current) setBusy(false); }
  }

  return (
    <div style={{ marginTop: 10 }}>
      <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple onChange={onPick} style={{ display: "none" }} />
      <button type="button" onClick={add} disabled={busy} style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 9,
        background: C.white, border: `1.5px solid ${C.steelXL}`, color: C.deep, fontSize: 12, fontWeight: 800,
        cursor: busy ? "wait" : "pointer", fontFamily: "inherit",
      }}>{busy ? <SpinnerDot color={C.deep} /> : <Camera size={14} />} {busy ? "Adding…" : "Add photo"}</button>
      {msg && (
        <div style={{ fontSize: 11, fontWeight: 700, marginTop: 6, color: msg.kind === "ok" ? C.ok : C.err, lineHeight: 1.4 }}>{msg.text}</div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CHAT TAB
// ═══════════════════════════════════════════════════════════════════════
function ChatTab({ job }) {
  const { user } = React.useContext(AppCtx);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const clientEmail = job._raw?.client_email || job._raw?.created_by || null;
  const convId = clientEmail && user?.email
    ? (user.email < clientEmail ? `${user.email}-${clientEmail}` : `${clientEmail}-${user.email}`)
    : null;

  // load() is a ref'd async so the polling interval always calls the latest
  // closure (clientEmail/convId can change when the job's _raw hydrates).
  const loadRef = useRef(null);
  loadRef.current = async function load() {
    try {
      const all = await api.get("/api/messages");
      if (!Array.isArray(all)) return;
      // Filter to messages for this job (or this client convo if no job_id)
      const filtered = all.filter(m =>
        (job._raw && m.job_id === job._raw.id) ||
        (clientEmail && (m.conv_id === convId || m.sender_id === clientEmail || m.receiver_id === clientEmail))
      );
      setMessages(filtered);
      setErr("");
    } catch (e) {
      // Surface to the user instead of swallowing.
      setErr(e.message || "Couldn't load messages. Pull to retry.");
    }
  };

  // Poll every 5s while the tab is mounted so inbound messages appear without
  // reopening the tab. Cleared on unmount / job change.
  useEffect(() => {
    let alive = true;
    const run = () => { if (alive && loadRef.current) loadRef.current(); };
    run();
    const id = setInterval(run, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [job.id]);

  async function send() {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setErr("");
    try {
      // Send even when we don't have a resolved recipient locally — the
      // backend resolves the conversation/recipient from jobId/convId.
      await api.post("/api/messages", {
        to: clientEmail,
        convId,
        message: text,
        jobId: job._raw?.id,
      });
      setDraft("");
      if (loadRef.current) await loadRef.current();
    } catch (e) {
      setErr(e.message || "Message failed to send. Try again.");
    }
    finally { setSending(false); }
  }

  const canSend = !!draft.trim() && !sending;

  return (
    <div style={{ padding: 14, display: "flex", flexDirection: "column", height: "100%", gap: 10 }}>
      <div style={{
        padding: "8px 12px", background: C.infoL, borderRadius: 12,
        fontSize: 11, color: C.info, fontWeight: 600, textAlign: "center",
      }}>
        <Lock size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
        {clientEmail ? `Encrypted with ${job.client || clientEmail}` : "Encrypted · recipient assigned by office"}
      </div>

      {err && (
        <div style={{
          padding: "8px 12px", background: C.errL, borderRadius: 12,
          fontSize: 11, color: C.err, fontWeight: 600, textAlign: "center",
        }}>
          {err}
        </div>
      )}

      <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: "center", color: C.steel, fontSize: 12, marginTop: 24 }}>
            No messages yet. Start the conversation below.
          </div>
        )}
        {messages.map(m => (
          <ChatBubble key={m.id} from={m.sender_id === user?.email ? "me" : "them"}>{m.message}</ChatBubble>
        ))}
      </div>

      <div style={{
        display: "flex", alignItems: "center", gap: 6, padding: "8px 12px",
        background: C.white, borderRadius: 22, border: `1px solid ${C.steelXL}`,
      }}>
        <Paperclip size={18} color={C.steel} />
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => e.key === "Enter" && send()}
          placeholder={`Message ${job.client || "client"}...`}
          style={{
            flex: 1, border: "none", outline: "none", fontSize: 13, color: C.deep, background: "transparent",
          }}
        />
        <button onClick={send} disabled={!canSend} style={{
          width: 32, height: 32, borderRadius: "50%",
          background: canSend ? C.deep : C.steelL,
          display: "flex", alignItems: "center", justifyContent: "center", border: "none",
          cursor: canSend ? "pointer" : "not-allowed",
        }}>
          <Send size={14} color={C.white} />
        </button>
      </div>
    </div>
  );
}


function ChatBubble({ from, children }) {
  const isMe = from === "me";
  return (
    <div style={{
      alignSelf: isMe ? "flex-end" : "flex-start", maxWidth: "78%",
      padding: "9px 13px", borderRadius: 16,
      background: isMe ? C.deep : C.white,
      color: isMe ? C.white : C.deep,
      fontSize: 13, lineHeight: 1.4,
      borderBottomRightRadius: isMe ? 4 : 16,
      borderBottomLeftRadius: isMe ? 16 : 4,
      border: isMe ? "none" : `1px solid ${C.steelXL}`,
      boxShadow: "0 1px 3px rgba(15,40,96,0.05)",
    }}>{children}</div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ATTEMPT SHEET — Quick capture
// ═══════════════════════════════════════════════════════════════════════
// [web-parity-2026-07-21] Tap-first chip controls for the attempt detail form —
// mirrors native's ChipRow / ChipMultiRow so both platforms capture identical info.
function ChipRow({ label, options, value, onChange, color, allowCustom }) {
  const col = color || C.ok;
  const isCustom = value && !options.includes(value);
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.steel, marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {options.map((o) => (
          <button key={o} type="button" onClick={() => onChange(value === o ? "" : o)} style={{
            padding: "8px 12px", borderRadius: 999, cursor: "pointer", lineHeight: 1,
            border: `1.5px solid ${value === o ? col : C.steelXL}`,
            background: value === o ? col + "15" : C.white, color: value === o ? col : C.deep,
            fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
          }}>{o}</button>
        ))}
      </div>
      {allowCustom && (
        <input value={isCustom ? value : ""} onChange={(e) => onChange(e.target.value)} placeholder="Other…" style={{
          width: "100%", marginTop: 6, padding: 10, border: `1px solid ${C.steelXL}`, borderRadius: 10,
          fontSize: 13, fontFamily: "inherit", outline: "none", color: C.deep, background: C.white, boxSizing: "border-box",
        }} />
      )}
    </div>
  );
}

/* [verify-multi-2026-09-18] `allowCustom` and `labelColor` added — additive optional
   props, every existing caller is unaffected. allowCustom mirrors ChipRow's "Other…"
   box: the typed value is carried as one extra entry in the same array, so an
   operator can record a method the chip list does not offer without losing the
   chips they also selected. */
function ChipMultiRow({ label, options, values, onChange, color, allowCustom, labelColor }) {
  const col = color || C.ok;
  const list = Array.isArray(values) ? values : [];
  const toggle = (o) => onChange(list.includes(o) ? list.filter((x) => x !== o) : [...list, o]);
  const customVal = list.find((x) => !options.includes(x)) || "";
  const setCustom = (v) => {
    const kept = list.filter((x) => options.includes(x));
    onChange(v.trim() ? [...kept, v] : kept);
  };
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: labelColor || C.steel, marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {options.map((o) => {
          const on = list.includes(o);
          return (
            <button key={o} type="button" onClick={() => toggle(o)} style={{
              padding: "8px 12px", borderRadius: 999, cursor: "pointer", lineHeight: 1,
              border: `1.5px solid ${on ? col : C.steelXL}`,
              background: on ? col + "15" : C.white, color: on ? col : C.deep,
              fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
            }}>{on ? "✓ " : ""}{o}</button>
          );
        })}
      </div>
      {allowCustom && (
        <input value={customVal} onChange={(e) => setCustom(e.target.value)} placeholder="Other…" style={{
          width: "100%", marginTop: 6, padding: 10, border: `1px solid ${C.steelXL}`, borderRadius: 10,
          fontSize: 13, fontFamily: "inherit", outline: "none", color: C.deep, background: C.white, boxSizing: "border-box",
        }} />
      )}
    </div>
  );
}

/* [field-command-2026-09-11] `seedOutcome` pre-selects an outcome when the sheet is
   opened from the Field Command dash. It ONLY pre-selects — nothing is submitted and
   no detail field is pre-filled, so served / sub_served / refused still require the
   operator to complete and submit the sworn detail form. Omitted everywhere else, so
   every existing caller keeps the untouched "no outcome chosen yet" behaviour. */
/* [work-log-wizard-2026-09-20] ONE flag gate for every capture entry point.
   Six triggers render AttemptSheet from five places; branching at each would mean
   six reads of the flag and six merge-conflict hunks in the file every field chat
   touches. This keeps AttemptSheet's exact prop signature — job / onClose /
   seedOutcome — so each call site changes by a single identifier and nothing else.
   AttemptSheet itself is deliberately NOT modified: four markers landed inside it
   in the last two days, and any diff in that range will conflict. It gets deleted,
   not edited, once the wizard has produced sworn proofs that survived review. */
function AttemptCapture(props) {
  const on = useWorkLogWizard();
  return on ? <WorkLogWizard {...props} /> : <AttemptSheet {...props} />;
}
function AttemptSheet({ job, onClose, seedOutcome, bulk }) { // [field-bulk-serve-2026-10-08] bulk: see BulkServeSheet
  // [sub-serve-mailing-2026-09-18] `user` supplies the server's own city as the
  // last-resort default for the mailing origin.
  const { reload, user } = React.useContext(AppCtx);
  const [submitErr, setSubmitErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [gps, setGps] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  /* [browser-gps-fallback-2026-07-27] Why the operator has no fix, in plain words.
     This is never a substitute for coordinates: whenever it is set, `gps` stays null
     and the attempt is refused locally. */
  const [gpsErr, setGpsErr] = useState("");
  /* [attempt-geofence-2026-09-20] Warn — never block — when the fix is far from
     the service address. A server who genuinely needs to log from down the
     street, from a locked gate, or from the car must still be able to; this
     exists to catch the WRONG ADDRESS, not to police where someone stands.
     0.5 mi, not 0.25: a rural parcel on a long driveway, or an apartment
     complex whose geocoded pin lands on the leasing office, can sit legitimately
     outside a quarter mile, and a warning that cries wolf gets tapped through
     without being read. */
  const SS_GEOFENCE_MI = 0.5;
  /* Resolved once on open, so the check at submit costs nothing. Same
     ssFcResolveDest the briefing map uses, so the pin the operator actually
     looked at and the point this measures against cannot be different. */
  const [destCoord, setDestCoord] = useState(null);
  const [geoWarn, setGeoWarn] = useState(null);
  /* [court-closed-warning-2026-10-08] What the app is telling the server about the
     courthouse right now. When it already showed the court CLOSED and the server picks
     "Court closed — re-attempt", one confirmation tap is required; the attempt is never
     blocked (it is a sworn record of what happened). Both facts ride on the attempt. */
  const courtSt = useCourtStatus(job);
  const courtShownClosed = ssCourtShownClosed(courtSt);
  const [courtAck, setCourtAck] = useState(false);
  useEffect(() => {
    if (!job) return undefined;
    let cancelled = false;
    ssFcResolveDest(job).then((d) => {
      if (!cancelled && d && d.lat != null && isFinite(Number(d.lat))) setDestCoord(d);
    }).catch(() => { /* no pin — the check simply does not run */ });
    return () => { cancelled = true; };
  }, [job && job.id]);
  // Async geolocation callbacks can land after the sheet closes.
  const gpsAliveRef = useRef(true);
  useEffect(() => () => { gpsAliveRef.current = false; }, []);
  /* [browser-gps-fallback-2026-07-27] The ONE predicate for "we hold a real fix".
     The confirmation banner and the submit payload both read this, so what the sheet
     tells the operator and what is actually POSTed can no longer disagree. */
  const gpsOk = !!(gps && typeof gps.lat === "number" && typeof gps.lng === "number" && !isNaN(gps.lat) && !isNaN(gps.lng));
  const [photos, setPhotos] = useState([]); // array of { key, fileUrl, base64 }
  const [photoBusy, setPhotoBusy] = useState(false);

  // [web-parity-2026-07-21] Outcome-branching detail state — canonical §3 capture
  // (ATTEMPT_MODEL_CANONICAL_2026-07-21), identical fields to native. All of this
  // is folded into the single serviceDetails object at submit (§4).
  /* [verify-multi-2026-09-18] Was a single string. A serve is routinely verified
     more than one way in the same contact (subject states their name AND matches
     the photo AND shows a licence); recording only one understated the diligence
     on what becomes a sworn proof. Now an array — the backend accepts both. */
  const [identityVerifiedBy, setIdentityVerifiedBy] = useState([]);
  const [recipName, setRecipName] = useState("");
  const [relationship, setRelationship] = useState("");
  /* [registered-agent-2026-10-22] Registered agent company (e.g. CSC Lawyers Incorporating Service). Pre-filled from
     the order's registered agent (jobs.ra_name) when the client or office entered one. */
  const [raCompany, setRaCompany] = useState(String((job && (job.ra_name || job.raName)) || ""));
  /* [pos-capture-fix-2026-07-26] Where the copies were left, for POS-010 item
     5b(1)/(2)/(3). Starts EMPTY and is only ever set by an operator tap — see the
     required chip row below. The previous code hardcoded locType='home' at submit,
     which swore CCP 415.20(b) dwelling service even when service happened at an
     office or a mailing address. */
  const [subLocType, setSubLocType] = useState("");
  const [isAdult, setIsAdult] = useState(false);
  const [informedOfNature, setInformedOfNature] = useState(false);
  const [leftInPresence, setLeftInPresence] = useState(false);
  const [desc, setDesc] = useState({ gender: "", age: "", race: "", height: "", weight: "", hair: "", glasses: false });
  const [subjectFeatures, setSubjectFeatures] = useState([]);
  const [signsOfLife, setSignsOfLife] = useState([]); // no-contact observations
  const [indicators, setIndicators] = useState([]);   // bad-address indicators -> observations[]
  const [vacantSigns, setVacantSigns] = useState([]); // [attempt-outcomes-2026-10-07] vacant -> observations[]
  const [gateKinds, setGateKinds] = useState([]);     // [attempt-outcomes-2026-10-07] gated -> observations[]
  const [notesText, setNotesText] = useState("");     // [attempt-outcomes-2026-10-07] mirrors the notes textarea for the required-note gate
  const [vehiclePlate, setVehiclePlate] = useState("");
  const [unsafeReasons, setUnsafeReasons] = useState([]);
  const [talkedTo, setTalkedTo] = useState("");
  const [talkedToName, setTalkedToName] = useState("");
  /* [notice-capacity-capture-2026-09-19] POS-010 ITEM 6 — THE SWORN CAPACITY, AT THE DOOR.

     Item 6 is an INTAKE fact: it transcribes the "Notice to the Person Served" box
     counsel ticked on the SUM-100 (CCP §412.30), and the order form now captures it.
     So why is it here at all? Because YOU sign the proof under penalty of perjury and
     you are the only person who ever holds the actual summons. When the order is silent,
     or the intake operator read it wrong, your record is the better evidence and the
     backend lets it WIN over the job value (_ssResolveNoticeCapacity in server.js).

     STARTS EMPTY ON PURPOSE. The order's value is shown beside it as read-only context,
     never pre-selected: a control you never looked at must not post a sworn observation
     in your name. Leave it blank to accept what the order says; fill it only to correct
     or to supply what the order never recorded. */
  const [noticeMode, setNoticeMode] = useState("");
  const [noticeFictitiousName, setNoticeFictitiousName] = useState("");
  const [noticeOnBehalfName, setNoticeOnBehalfName] = useState("");
  const [ccpSection, setCcpSection] = useState("");
  const [ccpOther, setCcpOther] = useState("");

  /* [sub-serve-mailing-2026-09-18] CCP §415.20(b) follow-up mailing.
     THE MAILING USUALLY HAPPENS LATER — copies get left at 2pm and the envelope
     goes out the next morning — so this is a THREE-state control, not a date
     field. "" means the operator has not answered yet and the sheet will not
     submit a substituted serve; "not_yet" is an explicit, recorded answer that
     is NOT the same thing as a blank. */
  const [mailStatus, setMailStatus] = useState("");       // "" | "today" | "other" | "not_yet"
  const [mailDateIso, setMailDateIso] = useState("");     // only read when mailStatus === "other"
  const [mailCity, setMailCity] = useState(() => ssMailOriginDefault(job, user).city);
  const [mailState, setMailState] = useState(() => ssMailOriginDefault(job, user).state);

  /* [browser-gps-fallback-2026-07-27] Capture a real position fix.
     NATIVE PATH IS UNCHANGED — same plugin, same options, same Number() coercion
     (Capacitor Proxy objects don't always JSON-serialize cleanly).
     THE BUG: `if (!Geo) return;` bailed instantly in a plain browser, where
     window.Capacitor is undefined. `gps` stayed null and every submit came back 400
     "GPS coordinates are required for every serve attempt". This bundle is served to
     browsers at mobile.californialegalexpress.com as well as wrapped for iOS, so the
     attempt sheet was simply dead for anyone not on the installed app.
     NON-NEGOTIABLE: on failure we set an error and leave `gps` NULL. We do not
     substitute a coordinate, a city centroid, a last-known point, or a "sim" flag.
     Attempt GPS is sworn evidence on a proof of service; a manufactured coordinate
     that the backend then HMAC-seals is a false document, not a fallback. */
  function captureGps() {
    const Geo = ssPlugin("Geolocation"); // [server-parity-web-2026-10-05]
    if (Geo) {
      setGpsBusy(true);
      Geo.getCurrentPosition({ enableHighAccuracy: true, timeout: 8000 })
        .then(p => setGps({
          lat: Number(p.coords.latitude),
          lng: Number(p.coords.longitude),
          accuracy: Number(p.coords.accuracy || 0),
          ts: Number(p.timestamp || Date.now()),
        }))
        .catch(e => console.warn("[gps]", e.message))
        .finally(() => setGpsBusy(false));
      return;
    }
    // ── Browser path: no Capacitor bridge, use the W3C Geolocation API ──
    const geo = (typeof navigator !== "undefined" && navigator.geolocation) || null;
    if (!geo || typeof geo.getCurrentPosition !== "function") {
      setGpsBusy(false);
      setGpsErr("This browser cannot provide location. Log this attempt from the StrikeServe app.");
      return;
    }
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      setGpsBusy(false);
      setGpsErr("Location is blocked over an insecure connection. Open https://" + window.location.hostname + " and try again."); // [eservice-2026-09-06] tenant-neutral (parity audit)
      return;
    }
    setGpsBusy(true);
    setGpsErr("");
    geo.getCurrentPosition(
      (p) => {
        if (!gpsAliveRef.current) return;
        setGpsBusy(false);
        const lat = Number(p && p.coords && p.coords.latitude);
        const lng = Number(p && p.coords && p.coords.longitude);
        if (!isFinite(lat) || !isFinite(lng)) {
          setGpsErr("The browser returned an unreadable position. Tap Retry GPS.");
          return;
        }
        setGps({
          lat: lat,
          lng: lng,
          accuracy: Number((p.coords && p.coords.accuracy) || 0),
          ts: Number(p.timestamp || Date.now()),
        });
        setGpsErr("");
      },
      (err) => {
        if (!gpsAliveRef.current) return;
        setGpsBusy(false);
        // W3C GeolocationPositionError: 1 PERMISSION_DENIED, 2 POSITION_UNAVAILABLE, 3 TIMEOUT.
        const code = err && err.code;
        setGpsErr(
          code === 1
            ? "Location permission denied. Allow location for this site (address-bar icon → Site settings → Location), then tap Retry GPS."
          : code === 3
            ? "Location timed out after 20 seconds. Move where the sky is clearer, then tap Retry GPS."
          : code === 2
            ? "No position available from this device. Turn on location services, then tap Retry GPS."
            : ("Location failed" + ((err && err.message) ? ": " + err.message : "") + ". Tap Retry GPS.")
        );
        console.warn("[gps] browser geolocation failed", code, err && err.message);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }

  // Auto-grab GPS when the sheet opens. [browser-gps-fallback-2026-07-27]
  useEffect(() => { captureGps(); }, []);

  /* [browser-evidence-upload-2026-07-27] takePhoto was Capacitor-Camera-only and bailed
     with a bare "Camera unavailable" in a browser, so NO photo evidence could be attached
     to an attempt from mobile.californialegalexpress.com. Combined with the GPS bug that
     made the browser build unable to produce a complete proof of service at all.
     Native path is unchanged. In a browser we open a file input with
     capture="environment", which on iOS Safari and Android Chrome opens the rear camera
     directly and elsewhere falls back to a file picker. Both paths now go through the
     same ssUploadEvidence pipeline, so presign -> PUT -> complete cannot drift. */
  const photoInputRef = useRef(null);
  // [attempt-photo-library-2026-10-09] Photo Library: multi-select from Photos, same upload pipeline as the
  // browser camera path (onPhotoFilesPicked). Works in the installed app and the browser.
  const libraryInputRef = useRef(null);
  function pickFromLibrary() {
    if (photoBusy) return;
    setSubmitErr("");
    if (libraryInputRef.current) libraryInputRef.current.click();
    else setSubmitErr("Photo library unavailable on this device.");
  }

  async function takePhoto() {
    const Camera = ssPlugin("Camera"); // [server-parity-web-2026-10-05]
    if (!Camera) {
      // Browser: defer to the hidden <input type="file" capture="environment">.
      if (photoInputRef.current) { setSubmitErr(""); photoInputRef.current.click(); }
      else setSubmitErr("Camera unavailable on this device.");
      return;
    }
    setPhotoBusy(true); setSubmitErr("");
    try {
      const p = await Camera.getPhoto({
        quality: 80,
        resultType: "base64",
        source: "CAMERA",
        saveToGallery: false,
      });
      const rec = await ssUploadEvidence(api, {
        bytes: ssB64ToBytes(p.base64String),
        mime: "image/jpeg",
        fileName: `attempt-${job.id}-${Date.now()}.jpg`,
        context: "attempt-photo",
        jobId: job.id,
      });
      setPhotos(prev => [...prev, rec]);
    } catch (e) { setSubmitErr(e.message || "Photo capture failed"); }
    finally { setPhotoBusy(false); }
  }

  /* [browser-evidence-upload-2026-07-27] Browser file-input handler. Accepts multiple
     shots in one pick. Each upload is independent — one failure must not discard the
     others, since each is a separate piece of field evidence. */
  async function onPhotoFilesPicked(ev) {
    const files = Array.from((ev && ev.target && ev.target.files) || []);
    if (ev && ev.target) ev.target.value = ""; // allow re-picking the same file
    if (!files.length) return;
    setPhotoBusy(true); setSubmitErr("");
    const failures = [];
    for (const f of files) {
      try {
        const _ssBytes = await ssFileToBytes(f); // [pos-overhaul-2026-10-07:server-app] read once; sniff HEIC/empty types
        const mime = (/pdf/i.test(String(f.type || "")) || /\.pdf$/i.test(String(f.name || ""))) ? "application/pdf" : ssImageMimeOf(f, _ssBytes);
        if (!/^image\//.test(mime)) { failures.push(`${f.name}: not an image`); continue; }
        const ext = ssExtForMime(mime);
        const rec = await ssUploadEvidence(api, {
          bytes: _ssBytes,
          mime,
          fileName: `attempt-${job.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`,
          context: "attempt-photo",
          jobId: job.id,
        });
        setPhotos(prev => [...prev, rec]);
      } catch (e) { failures.push(`${f.name}: ${(e && e.message) || "upload failed"}`); }
    }
    setPhotoBusy(false);
    if (failures.length) setSubmitErr(`${failures.length} photo(s) failed to upload and were NOT saved as evidence — ${failures.join("; ")}`);
  }

  // Map mobile UI outcome IDs to backend-accepted result values
  /* [granular-result-codes-2026-07-27] The field app only ever emitted the coarse legacy
     codes, so every field serve arrived at the backend as an ambiguous bare 'served' —
     which the POS mapper and the sworn declaration text both read as PERSONAL service.
     A serve handed to a co-resident was therefore indistinguishable, in the record, from
     one handed to the defendant (job #26072701). Emit the granular codes the backend
     enum has accepted all along (server.js attempt result enum). */
  const RESULT_MAP = {
    served: "served-personal",
    sub_served: "served-substitute-home",
    registered_agent: "served-registered-agent", // [registered-agent-2026-10-22]
    no_answer: "no-answer",
    refused: "refused",
    unsafe: "other",
    not_home: "not-home",
    other: "other",
    // [attempt-outcomes-2026-10-07] bad_addr / moved / vacant / gated / court_closed
    ...SS_NONSERVICE_RESULT_MAP,
  };
  const notesRef = useRef(null);
  async function submitAttempt(result, notes, subName, subRelation) {
    if (!job || !job.id) return;
    /* [browser-gps-fallback-2026-07-27] Refuse locally rather than shipping a payload the
       backend will bounce with a bare 400. Same rule, stated where the operator can act on
       it. Nothing is ever invented to get past this check. */
    if (!gpsOk) {
      setSubmitErr(gpsErr || (gpsBusy
        ? "Still getting a GPS fix — wait a moment, then save again."
        : "No GPS fix. Tap Retry GPS above. Every serve attempt must carry real coordinates."));
      return;
    }
    /* [verify-multi-2026-09-18] Same rule as the disabled Save button, enforced at the
       one place every caller funnels through — the sheet can be opened pre-seeded with
       an outcome from the Field Command dash. */
    /* [registered-agent-2026-10-22] who the registered agent was is required: the person who took the papers and the agent company */
    if (result === "registered_agent" && (!String(recipName).trim() || !String(raCompany).trim())) {
      setSubmitErr("Enter who accepted the papers and the registered agent company (e.g. CSC Lawyers Incorporating Service).");
      return;
    }
    if ((result === "served" || result === "sub_served" || result === "registered_agent")
        && !(Array.isArray(identityVerifiedBy) && identityVerifiedBy.filter(Boolean).length)) {
      setSubmitErr("Select at least one way you verified this person's identity. If you could not verify it, record that explicitly.");
      return;
    }
    /* [served-photo-required-2026-07-20] [server-parity-web-2026-10-05] A serve needs
       proof. Enforced here, where every caller funnels through, against the mapped
       result code — the same union as the identity and mailing gates. */
    if (ssResultNeedsPhoto(RESULT_MAP[result] || result) && !(photos || []).some((p) => p && p.key)) {
      setSubmitErr("A proof photo is required to mark a serve. Add at least one photo before saving.");
      return;
    }
    /* [attempt-gate-2026-10-08] Every attempt, any outcome, needs the photo minimum.
       Backstop for the disabled Save button below. */
    if (ssUploadedPhotoCount(photos) < ssPhotosRequiredFor(result)) {
      setSubmitErr("Every attempt needs at least " + SS_ATTEMPT_MIN_PHOTOS + " photos (" + ssUploadedPhotoCount(photos) + " of " + SS_ATTEMPT_MIN_PHOTOS + " so far): front door / point of contact, house or unit number, and a wide shot of the property.");
      return;
    }
    /* [attempt-outcomes-2026-10-07] Required-note gate, enforced where every caller
       funnels through (mirrors the disabled Save button below). */
    if (ssOutcomeNoteMissing(result, notes, result === "bad_addr" ? indicators : (result === "vacant" ? vacantSigns : null))) {
      setSubmitErr(ssOutcomeNotePrompt(result));
      return;
    }
    /* [sub-serve-mailing-2026-09-18] A substituted serve may not be submitted with
       the CCP §415.20(b) mailing unrecorded. Either mailed — with the date and the
       city it went out from, both of which POS-010 item 5b(4) swears to — or an
       explicit "not mailed yet". Same shape as the identity gate above: enforced
       at the one place every caller funnels through, because the sheet can be
       opened pre-seeded with an outcome from the Field Command dash. */
    if (result === "sub_served") {
      const _t = ssTodayLocalISO();
      const _iso = mailStatus === "today" ? _t : (mailStatus === "other" ? String(mailDateIso || "").trim() : "");
      if (!mailStatus) {
        setSubmitErr("Record the follow-up mailing (CCP §415.20(b)): mailed today, mailed on another date, or not mailed yet. Substituted service is not complete until the copies are mailed.");
        return;
      }
      if (mailStatus !== "not_yet") {
        if (!_iso) { setSubmitErr("Pick the date the copies were mailed."); return; }
        if (_iso > _t) { setSubmitErr("The mailing date cannot be in the future."); return; }
        if (!String(mailCity).trim()) { setSubmitErr("Enter the city the copies were mailed from — POS-010 item 5b(4) is sworn to it."); return; }
        if (!/^[A-Za-z]{2}$/.test(String(mailState).trim())) { setSubmitErr("Enter the two-letter state the copies were mailed from."); return; }
      }
    }
    /* [court-closed-warning-2026-10-08] One confirmation tap when the app had already
       shown this court closed. Never a refusal: the tap is always available. */
    if (result === "court_closed" && courtShownClosed && !courtAck) {
      setSubmitErr("Tap \"I understand\" on the court-closed notice above, then save. The attempt will be recorded.");
      return;
    }
    /* [attempt-geofence-2026-09-20] Out-of-area check. The FIRST press past the
       radius shows the distance and returns; the second press goes through. It
       never refuses the attempt — losing a real attempt because the geocoder put
       the pin on the wrong side of a complex would be far worse than logging one
       from half a mile out. Skipped entirely when there is no fix (the backend
       refuses those anyway) or no resolvable destination. */
    const _geoMiles = (gpsOk && gps && destCoord)
      ? ssHaversineMiles({ lat: gps.lat, lng: gps.lng }, destCoord)
      : null;
    if (_geoMiles != null && _geoMiles > SS_GEOFENCE_MI && !geoWarn) {
      setGeoWarn({ miles: _geoMiles });
      setSubmitErr("");
      return;
    }
    setSubmitting(true); setSubmitErr("");
    try {
      /* [evidence-integrity-2026-07-24] The uploaded photos were being dropped here.
         takePhoto() does presign -> PUT -> /api/uploads/complete and stores {key,fileUrl},
         but this payload only sent photoCount, so the S3 references never reached
         jobs.attempts and the objects were orphaned in the bucket. photoCount is now
         derived from the verified array so it can never overstate the evidence. */
      const _eiPhotos = (photos || []).filter(p => p && p.key).map(p => ({
        name: p.name || ("attempt-" + job.id + "-" + Date.now() + ".jpg"),
        ts: p.ts || new Date().toISOString(),
        size: p.size || 0,
        s3Key: p.key,
        fileUrl: p.fileUrl || null,
        s3: true,
      }));
      const payload = {
        result: RESULT_MAP[result] || result,
        notes: notes || "",
        photoCount: _eiPhotos.length,
        photos: _eiPhotos,
        subName: subName || null,
        subRelation: subRelation || null,
      };
      // [court-closed-warning-2026-10-08] what the app showed at this moment + the confirmation tap
      if (result === "court_closed") {
        payload.courtStatusShown = courtSt && courtSt.applies && courtSt.status
          ? { open: !!courtSt.status.open, reason: courtSt.status.reason || null, at: courtSt.checkedAt || new Date().toISOString() }
          : null;
        payload.courtClosedAcknowledged = !!(courtShownClosed && courtAck);
      }
      /* [browser-gps-fallback-2026-07-27] Reuse the shared gpsOk predicate (declared with
         the state above) so the confirmation banner and this payload cannot disagree about
         whether a fix exists. The backend still rejects a missing gps — by design. */
      if (gpsOk) {
        payload.gps = { lat: gps.lat, lng: gps.lng };
        if (typeof gps.accuracy === "number" && !isNaN(gps.accuracy)) payload.gps.accuracy = gps.accuracy;
        /* [attempt-geofence-2026-09-20] Distance from the fix to the resolved
           service address, on EVERY attempt, not only the flagged ones — a
           figure that appears only on exceptions is an admission, whereas one
           present on all of them is corroboration. applyServiceAttempt stores
           `gps: att.gps || null` verbatim (server.js ~10801), so this persists
           with no backend change. */
        if (_geoMiles != null) payload.gps.distanceMiles = Math.round(_geoMiles * 100) / 100;
      }
      // [web-parity-2026-07-21] Canonical single serviceDetails object (§4 of
      // ATTEMPT_MODEL_CANONICAL_2026-07-21). This is the ONLY structured detail the
      // backend narrates + auto-fills the POS from (sanitizeServiceDetails +
      // composeServiceNarrative). Legacy top-level keys the backend ignores
      // (recipientDescription, recipientIsAdult, informedOfNature, leftInPresence,
      // signsOfLife, unsafeReasons, mailingRequired, vehiclePlate) are RETIRED —
      // folded in here. subName / subRelation / locType stay top-level (backend
      // reads those directly, not from serviceDetails).
      const contactMade = result === "served" || result === "sub_served" || result === "refused" || result === "registered_agent"; // [registered-agent-2026-10-22]
      const noContact   = result === "no_answer" || result === "not_home";
      const badAddress  = result === "bad_addr";
      // [attempt-outcomes-2026-10-07] moved records who reported it, like bad_addr
      const reportedBy  = badAddress || result === "moved";
      const _observations = [
        ...(noContact ? signsOfLife : []),
        ...(badAddress ? indicators : []),
        ...(result === "vacant" ? vacantSigns : []),
        ...(result === "gated" ? gateKinds : []),
      ].slice(0, 12);
      /* [verify-multi-2026-09-18] Trim + drop blanks, cap at 6. */
      const _idMethods = (Array.isArray(identityVerifiedBy) ? identityVerifiedBy : [identityVerifiedBy])
        .map((s) => String(s || "").trim()).filter(Boolean).slice(0, 6);
      payload.serviceDetails = {
        /* [verify-multi-2026-09-18] Array of methods. Empty -> null so an attempt
           with nothing captured stays "absent" exactly as before. */
        identityVerifiedBy: (contactMade && _idMethods.length) ? _idMethods : null,
        recipientName:      (contactMade && recipName.trim()) ? recipName.trim() : null,
        recipientIsAdult:   contactMade ? !!isAdult : false,
        informedOfNature:   contactMade ? !!informedOfNature : false,
        relationship:       (result === "sub_served" && relationship.trim()) ? relationship.trim() : null,
        leftInPresence:     (result === "sub_served" || result === "refused") ? !!leftInPresence : false,
        mailingRequired:    result === "sub_served",
        subjectSex:         (contactMade && desc.gender) ? desc.gender : null,
        subjectRace:        (contactMade && desc.race) ? desc.race : null,
        subjectAgeApprox:   (contactMade && desc.age) ? desc.age : null,
        subjectHeight:      (contactMade && desc.height) ? desc.height : null,
        subjectWeight:      (contactMade && desc.weight) ? desc.weight : null,
        subjectHair:        (contactMade && desc.hair) ? desc.hair : null,
        subjectGlasses:     contactMade ? !!desc.glasses : false,
        subjectFeatures:    contactMade ? subjectFeatures.slice(0, 12) : [],
        observations:       _observations,
        vehiclePlate:       (noContact && vehiclePlate.trim()) ? vehiclePlate.trim() : null,
        unsafeReasons:      result === "unsafe" ? (unsafeReasons.length ? unsafeReasons.slice(0, 12) : ["Unsafe conditions reported from the field"]) : [], // [attempt-gate-exemptions-2026-10-08] always non-empty: the backend photo exemption keys on it
        talkedTo:           ((noContact || reportedBy) && talkedTo.trim()) ? talkedTo.trim() : null,
        talkedToName:       ((noContact || reportedBy) && talkedToName.trim()) ? talkedToName.trim() : null,
        /* [notice-capacity-capture-2026-09-19] POS-010 item 6 override. Only sent on a
           contact-made outcome (there is no capacity to swear to when nobody took the
           papers) and only when the operator actually answered — null means "I did not
           override", which the backend resolves back to the job's intake value rather
           than blanking item 6. Out-of-branch fields are dropped here as well as
           server-side, so a mode the operator changed their mind about cannot leave a
           stray entity name behind. */
        noticeMode:           (contactMade && noticeMode) ? noticeMode : null,
        noticeFictitiousName: (contactMade && noticeMode === "fictitious" && noticeFictitiousName.trim())
                                ? noticeFictitiousName.trim() : null,
        noticeOnBehalfName:   (contactMade && noticeMode === "on_behalf" && noticeOnBehalfName.trim())
                                ? noticeOnBehalfName.trim() : null,
        ccpSection:           (contactMade && noticeMode === "on_behalf" && ccpSection) ? ccpSection : null,
        ccpOther:             (contactMade && noticeMode === "on_behalf" && ccpSection === "other" && ccpOther.trim())
                                ? ccpOther.trim() : null,
      };
      /* [registered-agent-2026-10-22] Registered agent: the backend builds POS-010 item 3b from these
         ("<person>, authorized employee of <company>, Registered Agent for <entity>").
         Personal delivery to the agent, so no substitute-service fields are sent. */
      if (result === "registered_agent") {
        payload.registeredAgentName = String(recipName).trim() || null;
        payload.registeredAgentCompany = String(raCompany).trim() || null;
        payload.registeredAgentEntity = String((job && (job.defendant || job.recipient_name || job.recipientName)) || "").trim() || null;
        payload.servedPersonName = String(recipName).trim() || null;
        payload.servedPersonRelationship = "Registered agent";
      }
      // Sub-service: backend's POS generator reads subName / subRelation / locType
      // directly at top level — keep them (NOT among the retired ignored keys).
      if (result === "sub_served") {
        payload.subName = recipName || subName || null;
        payload.subRelation = relationship || subRelation || null;
        /* [pos-capture-fix-2026-07-26] Was hardcoded 'home'. locType drives POS-010
           item 5b(1)/(2)/(3) — the sworn statement of WHERE the copies were left —
           so it must come from the operator, never a default. locTypeCaptured tells
           the backend this value was actually chosen by a human: mapJobToFormData
           will not tick 5b(2) on a bare 'home' without it, because every attempt
           stored before today carries an unverifiable hardcoded 'home'. Send the
           flag only when the value is one of the offered options. */
        const _locValid = SUB_LOC_TYPES.some((l) => l.id === subLocType);
        payload.locType = _locValid ? subLocType : null;
        if (_locValid) payload.locTypeCaptured = true;
        /* [sub-serve-mailing-2026-09-18] The §415.20(b) mailing, in the exact shape
           applyServiceAttempt persists (server.js ~9906):
             mailDate  validateString(_, 40)  -> POS-010 5b(4) "on (date)", printed VERBATIM
             mailCity  validateString(_, 120) -> POS-010 5b(4) "from (city)"
           Both are top-level on the attempt, NOT inside serviceDetails. */
        const _mt = ssTodayLocalISO();
        const _mIso = mailStatus === "today" ? _mt : (mailStatus === "other" ? String(mailDateIso || "").trim() : "");
        if (mailStatus !== "not_yet" && _mIso) {
          payload.mailDate = ssIsoToUsDate(_mIso);
          // City AND state, because the statute asks for the place of mailing and the
          // 120-char mailCity column is the only field the record has for it.
          payload.mailCity = String(mailCity).trim() + ", " + String(mailState).trim().toUpperCase();
        } else {
          /* NOT YET MAILED. mailDate and mailCity stay NULL deliberately.
             mapJobToFormData ticks sub_serve_mailed straight off served.mailDate
             (server.js ~14657) and validatePosPreflight hard-errors
             SUB_SERVE_MAILING_MISSING on a blank one — which is the CORRECT
             outcome here: the mailing has not happened, so the proof is not ready
             to issue. Any sentinel string ("PENDING", "NOT_YET") would print onto
             item 5b(4) AND tick that checkbox, swearing to a mailing that never
             went out. Nulls can never look like a completed mailing. */
          payload.mailDate = null;
          payload.mailCity = null;
          /* The EXPLICIT record, so this is distinguishable from a client that
             simply never asked. Attempt notes are persisted verbatim, surface in
             the attempt history, the accountability timeline and the client
             update email, and are bound to NO proof-of-service form field — so a
             plain sentence here is auditable without touching a sworn document. */
          payload.notes = [
            String(payload.notes || "").trim(),
            "CCP 415.20(b) follow-up mailing NOT YET SENT as of this attempt. Service is not complete and the 10-day period has not begun. [415.20b-mailing-pending]",
          ].filter(Boolean).join(" — ");
        }
      }
      if (bulk) { await bulk.submit(payload); return; } // [field-bulk-serve-2026-10-08] same validated payload -> POST /api/jobs/bulk-serve
      await api.post(`/api/jobs/${job.id}/attempts`, payload);
      // Update / end the iOS Live Activity to reflect the new attempt.
      // "served" / "sub-served" → end the Activity; everything else just
      // updates the stage + attempt counter. Helpers are no-ops off-iOS.
      try {
        const mapped = String(payload.result || "").toLowerCase();
        /* [attempt-badge-served-2026-10-20] served-* outcomes end the Live Activity too. */
        const isServed = mapped === "served" || mapped === "sub-served" || mapped.indexOf("served-") === 0;
        // attemptNumber: prior attempts + this one. Falls back to 1 if we
        // don't have the raw attempts array on the job object.
        const priorAttempts = Array.isArray(job._raw && job._raw.attempts)
          ? job._raw.attempts.length
          : (Array.isArray(job.rawAttempts) ? job.rawAttempts.length : 0);
        const attemptNumber = priorAttempts + 1;
        if (isServed) {
          endJobLiveActivity(job.id, "Served — proof captured");
        } else {
          const stageMap = {
            "no-answer": "Attempt logged — no answer",
            "not-home": "Attempt logged — not home",
            "bad-address": "Attempt logged — bad address",
            // [attempt-outcomes-2026-10-07]
            "wrong-address": "Attempt logged — bad address",
            "moved": "Attempt logged — moved",
            "vacant": "Attempt logged — vacant",
            "gate-locked": "Attempt logged — no access",
            "court-closed": "Attempt logged — court closed",
            "refused": "Attempt logged — refused",
            "other": "Attempt logged",
          };
          updateJobLiveActivity(job.id, {
            stage: stageMap[mapped] || "Attempt logged",
            attemptNumber,
            lat: (payload.gps && payload.gps.lat) || undefined,
            lng: (payload.gps && payload.gps.lng) || undefined,
            status: "attempting",
          });
        }
      } catch (_) { /* never let the Live Activity throw block the UI */ }
      await reload();
      onClose();
    } catch (e) {
      console.error("[attempt] FAIL", e);
      setSubmitErr((e && e.message) || "Could not save attempt");
    } finally { setSubmitting(false); }
  }

  // [field-command-2026-09-11] seedOutcome pre-selects; it never submits.
  const [outcome, setOutcome] = useState(seedOutcome || null);
  // [web-parity-2026-07-21] Canonical outcome set (§1) — adds sub_served / not_home
  // so the outcome-driven detail form can branch to all four disclosure groups.
  const outcomes = [
    { id: "served", label: "Served", color: C.ok, icon: CheckCircle2 },
    { id: "sub_served", label: "Sub-served", color: C.royal, icon: User },
    { id: "registered_agent", label: "Registered agent", color: C.ok, icon: CheckCircle2 }, // [registered-agent-2026-10-22]
    { id: "no_answer", label: "No answer", color: C.warn, icon: AlertCircle },
    { id: "not_home", label: "Not home", color: C.warn, icon: AlertCircle },
    { id: "refused", label: "Refused", color: C.err, icon: XCircle },
    { id: "bad_addr", label: "Bad address", color: C.err, icon: MapPin },
    // [attempt-outcomes-2026-10-07] non-service outcomes — see SS_NONSERVICE_RESULT_MAP
    { id: "moved", label: "Moved — not here", color: C.err, icon: ArrowRight },
    { id: "vacant", label: "Vacant", color: C.warn, icon: Home },
    { id: "gated", label: "Gated / no access", color: C.warn, icon: Lock },
    { id: "court_closed", label: "Court closed — re-attempt", color: C.warn, icon: Calendar },
    { id: "unsafe", label: "Unsafe", color: C.err, icon: ShieldAlert },
    { id: "other", label: "Other", color: C.steel, icon: MoreHorizontal },
  ].filter((o) => !bulk || SS_BULK_SERVE_OUTCOMES.includes(o.id)); // [field-bulk-serve-2026-10-08] successful service only

  // [web-parity-2026-07-21] Canonical chip options — identical to native (§3 of ATTEMPT_MODEL_CANONICAL_2026-07-21).
  const IDENTITY_VERIFIED = ["Verbal confirmation", "Matched photo/ID", "Admitted identity", "Named on mail/vehicle", "Refused to confirm", "Could not verify"];
  /* [verify-multi-2026-09-18] Identity verification is what distinguishes personal
     service on the named party from handing papers to whoever opened the door, so
     an outcome that asserts delivery to a person must record how identity was
     established — or record explicitly that it could not be. No-contact outcomes
     (no answer / not home / bad address / unsafe / other) never ask for it. */
  const _idRequired = outcome === "served" || outcome === "sub_served" || outcome === "registered_agent"; // [registered-agent-2026-10-22]
  const _raMissing = outcome === "registered_agent" && (!String(recipName).trim() || !String(raCompany).trim()); // [registered-agent-2026-10-22]
  const _idMissing = _idRequired && !(Array.isArray(identityVerifiedBy) && identityVerifiedBy.filter(Boolean).length);
  /* [served-photo-required-2026-07-20] [server-parity-web-2026-10-05] Save-button mirror
     of the submitAttempt photo gate, keyed on the granular mapped result. */
  const _mappedOutcome = outcome ? (RESULT_MAP[outcome] || outcome) : null;
  const _photoMissing = ssResultNeedsPhoto(_mappedOutcome) && !(photos || []).some((p) => p && p.key);
  /* [attempt-gate-2026-10-08] Every attempt: verified GPS + >= SS_ATTEMPT_MIN_PHOTOS. */
  const _photoCount = ssUploadedPhotoCount(photos);
  const _photosNeeded = ssPhotosRequiredFor(outcome);
  const _photosShort = _photoCount < _photosNeeded;
  const _gateBlocked = !gpsOk || _photosShort || _raMissing; // [registered-agent-2026-10-22] _raMissing: who the registered agent was
  /* [attempt-outcomes-2026-10-07] Save-button mirror of the submitAttempt note gate. */
  const _noteMissing = !!outcome && ssOutcomeNoteMissing(outcome, notesText,
    outcome === "bad_addr" ? indicators : (outcome === "vacant" ? vacantSigns : null));
  /* [sub-serve-mailing-2026-09-18] Save-button mirror of the submitAttempt gate.
     Unanswered blocks; "not mailed yet" does NOT block — it is a valid answer. */
  const _mailTodayIso = ssTodayLocalISO();
  const _mailIso = mailStatus === "today" ? _mailTodayIso : (mailStatus === "other" ? String(mailDateIso || "").trim() : "");
  const _mailMissing = outcome === "sub_served" && (
    !mailStatus || (
      mailStatus !== "not_yet" && (
        !_mailIso || _mailIso > _mailTodayIso
        || !String(mailCity).trim()
        || !/^[A-Za-z]{2}$/.test(String(mailState).trim())
      )
    )
  );
  const GENDER = ["Male", "Female", "Unknown"];
  const AGE = ["Under 18", "18–25", "26–35", "36–45", "46–55", "56–65", "65+"];
  const RACE = ["White", "Black", "Hispanic/Latino", "Asian", "Native American", "Middle Eastern", "Other/Unknown"];
  const HEIGHT = ["<5'0", "5'0–5'4", "5'5–5'9", "5'10–6'1", "6'2+"];
  const WEIGHT = ["<120", "120–150", "150–180", "180–220", "220+"];
  const HAIR = ["Black", "Brown", "Blonde", "Red", "Gray", "White", "Bald", "Other"];
  const FEATURES = ["Glasses", "Beard", "Mustache", "Facial hair", "Tattoos", "Piercings", "Bald", "Heavyset", "Slim", "Mask"];
  const RELATIONSHIPS = ["Spouse/partner", "Co-resident", "Family member", "Co-worker", "Manager/agent", "Other"];
  /* [pos-capture-fix-2026-07-26] Labels name the POS-010 item each option swears to.
     Values are the exact vocabulary mapJobToFormData expects for
     sub_serve_at_business / _at_home / _at_unknown — do not change them loosely. */
  const SUB_LOC_TYPES = [
    { id: "business", label: "Business — usual place of business  (5b(1))" },
    { id: "home",     label: "Dwelling / usual place of abode  (5b(2))" },
    { id: "unknown",  label: "Physical address unknown — usual mailing address  (5b(3))" },
  ];
  const SIGNS = ["Vehicle in driveway", "Lights on", "Noise/TV inside", "Dog present", "Blinds drawn", "Mail piled up", "Gate/security", "No answer at door"];
  /* [attempt-outcomes-2026-10-07] "Vacant" and "Moved/forwarded" are now outcomes of
     their own, so they are no longer bad-address indicators. */
  const BAD_ADDR_INDICATORS = ["No such address", "Wrong name", "Unit/suite not found", "Business closed"];
  const MOVED_TALKED = ["Occupant", "Neighbor", "Property mgr", "Other"];
  const VACANT_SIGNS = ["No furniture visible", "For sale/lease sign", "Mail piled up", "Utilities off", "Overgrown/unkept", "Boarded up"];
  const GATE_KINDS = ["Locked gate", "Call box — no answer", "Guard denied entry", "Locked lobby/door"];
  const NO_CONTACT_TALKED = ["Neighbor", "Property mgr", "Passerby", "None"];
  const BAD_ADDR_TALKED = ["Neighbor", "Property mgr", "Occupant", "None"];
  const UNSAFE = ["Aggressive person", "Weapon seen", "Dog/animal", "Locked gate", "Hazardous condition", "Other"];
  /* [notice-capacity-capture-2026-09-19] POS-010 item 6 vocabulary. The `id` values are
     the EXACT strings sanitizeServiceDetails enum-clamps against (server.js
     SS_NOTICE_MODES / SS_CCP_SECTIONS) — anything else is silently discarded at the
     write and item 6 stays blank, so do not retitle these loosely. */
  const NOTICE_MODES = [
    { id: "individual", label: "6a — as an individual defendant" },
    { id: "fictitious", label: "6b — under the fictitious name of…" },
    { id: "occupant",   label: "6c — as an occupant (CCP §415.46)" },
    { id: "on_behalf",  label: "6d — on behalf of an entity / minor / conservatee" },
  ];
  const CCP_SECTIONS = [
    { id: "416.10", label: "§416.10 — corporation" },
    { id: "416.20", label: "§416.20 — defunct corporation" },
    { id: "416.30", label: "§416.30 — joint stock company / association" },
    { id: "416.40", label: "§416.40 — association or partnership" },
    { id: "416.50", label: "§416.50 — public entity" },
    { id: "415.95", label: "§415.95 — business organization, form unknown" },
    { id: "416.60", label: "§416.60 — minor" },
    { id: "416.70", label: "§416.70 — ward or conservatee" },
    { id: "416.90", label: "§416.90 — authorized person" },
    { id: "415.46", label: "§415.46 — occupant" },
    { id: "other",  label: "other (specify)" },
  ];
  /* What the ORDER says, read-only. Shown so the operator can confirm at a glance
     instead of retyping — and so an order that says NOTHING is visibly an order that
     says nothing, which is the case where their answer is the only one there will be. */
  const _ncJobMode = String((job && (job.recipient_notice_mode || job.recipientNoticeMode)) || "").trim();
  const _ncJobSection = String((job && (job.recipient_ccp_section || job.recipientCcpSection)) || "").trim();
  const _ncJobName = String((job && (job.recipient_on_behalf_name || job.recipientOnBehalfName
    || job.recipient_fictitious_name || job.recipientFictitiousName)) || "").trim();
  const _ncJobLabel = _ncJobMode
    ? [(NOTICE_MODES.find((m) => m.id === _ncJobMode) || {}).label || _ncJobMode,
       _ncJobName, _ncJobSection ? "CCP §" + _ncJobSection : ""].filter(Boolean).join(" · ")
    : "";

  return (
    <Sheet onClose={onClose} small>
      {/* [attempt-sheet-scroll-fix-2026-07-25] flex+minHeight+overflow so this content div
          actually scrolls inside the fixed-height Sheet instead of being clipped by it. */}
      <div style={{ padding: "18px 16px 16px", flex: 1, minHeight: 0, overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>NEW ATTEMPT</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, marginTop: 2 }}>{jobDisplay(job).name}</div>
          </div>
          <button onClick={onClose} style={iconBtnLight}>
            <X size={20} color={C.deep} />
          </button>
        </div>
        <PosProfileBanner context="attempt" />{/* [pos-overhaul-2026-10-07:server-app] */}
        {bulk ? <BulkServeJobPicker bulk={bulk} /> : null}{/* [field-bulk-serve-2026-10-08] */}

        {/* GPS confirmation */}
        {/* [browser-gps-fallback-2026-07-27] This banner was a STATIC MOCK left over from the
            design pass: it unconditionally rendered "GPS captured" above the hardcoded string
            "38.59°N, 121.40°W · ±8m accuracy" and never referenced `gps` at all. It therefore
            reported a good fix in the browser build, where `gps` is null and the POST is
            refused — telling the operator the evidence was captured when nothing was. Worse,
            those literals are a fixed Sacramento point presented as a field reading. It now
            renders the SAME gpsOk state the payload is built from, shows the real numbers, and
            says plainly when there is no fix. */}
        <div style={{
          padding: 12, background: gpsOk ? C.okL : C.errL, borderRadius: 12, marginBottom: 14,
          display: "flex", alignItems: "center", gap: 10,
          border: `1px solid ${gpsOk ? C.ok + "33" : C.err}`,
        }}>
          {gpsOk ? <CheckCircle2 size={18} color={C.ok} /> : <AlertTriangle size={18} color={C.err} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: gpsOk ? C.ok : C.err }}>
              {gpsBusy ? "Getting GPS…" : (gpsOk ? "GPS captured" : "No GPS fix — attempt cannot be saved")}
            </div>
            <div style={{ fontSize: 10, color: gpsOk ? C.ok : C.err, opacity: 0.85, marginTop: 2, wordBreak: "break-word" }}>
              {gpsOk
                ? `${Math.abs(gps.lat).toFixed(5)}°${gps.lat >= 0 ? "N" : "S"}, ${Math.abs(gps.lng).toFixed(5)}°${gps.lng >= 0 ? "E" : "W"}${gps.accuracy ? ` · ±${Math.round(gps.accuracy)}m accuracy` : ""}`
                : (gpsBusy ? "Waiting for a location fix…" : (gpsErr || "Location not available on this device."))}
            </div>
          </div>
          {!gpsBusy && !gpsOk && (
            <button type="button" onClick={captureGps} style={{
              padding: "8px 12px", borderRadius: 999, border: `1.5px solid ${C.err}`,
              background: C.white, color: C.err, fontSize: 11, fontWeight: 800,
              cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            }}>Retry GPS</button>
          )}
        </div>

        {/* Outcome grid */}
        <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.06em", marginBottom: 8 }}>OUTCOME</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
          {outcomes.map(o => {
            const Icon = o.icon;
            const sel = outcome === o.id;
            return (
              <button key={o.id} onClick={() => setOutcome(o.id)} style={{
                padding: 14, background: sel ? `${o.color}15` : C.white,
                border: `1.5px solid ${sel ? o.color : C.steelXL}`, borderRadius: 12,
                display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textAlign: "left",
              }}>
                <Icon size={18} color={o.color} />
                <span style={{ fontSize: 13, fontWeight: 700, color: sel ? o.color : C.deep }}>{o.label}</span>
              </button>
            );
          })}
        </div>

        {/* [web-parity-2026-07-21] Outcome-driven detail form (§3 canonical, tap-first chips, native parity). */}
        {/* Contact made (served / sub_served / refused): identity + recipient + subject description. */}
        {/* [registered-agent-2026-10-22] Registered agent: who the registered agent was. */}
        {outcome === "registered_agent" && (
          <div style={{ marginBottom: 14, padding: 12, borderRadius: 12, border: "1px solid " + (_raMissing ? C.err : C.steelXL), background: C.white, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.06em" }}>WHO WAS THE REGISTERED AGENT?</div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.steel, marginBottom: 6 }}>Person who accepted the papers — required</div>
              <input type="text" value={recipName} onChange={e => setRecipName(e.target.value)} placeholder="e.g. Rebecca Vang"
                style={{ width: "100%", padding: 10, border: "1px solid " + C.steelXL, borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", color: C.deep, background: C.white, boxSizing: "border-box" }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.steel, marginBottom: 6 }}>Registered agent company — required</div>
              <input type="text" value={raCompany} onChange={e => setRaCompany(e.target.value)} placeholder="e.g. CSC Lawyers Incorporating Service"
                style={{ width: "100%", padding: 10, border: "1px solid " + C.steelXL, borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", color: C.deep, background: C.white, boxSizing: "border-box" }} />
              <div style={{ fontSize: 11, color: C.steel, marginTop: 4 }}>If the person IS the registered agent (no agent company), enter their name here too.</div>
            </div>
            <div style={{ fontSize: 11, color: C.steel }}>Served on behalf of: <b style={{ color: C.deep }}>{(job && (job.defendant || job.recipient_name || job.recipientName)) || "—"}</b></div>
            {_raMissing && <div style={{ fontSize: 11, color: C.err }}>Enter who accepted the papers and the registered agent company.</div>}
          </div>
        )}
        {(outcome === "served" || outcome === "sub_served" || outcome === "refused" || outcome === "registered_agent") && (
          <div style={{ marginBottom: 14, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.06em" }}>IDENTITY & RECIPIENT</div>
            {/* [verify-multi-2026-09-18] Multi-select: tick every way identity was
                actually verified. Required for served / sub_served (the submit
                button is gated below); optional for refused, where the papers
                were still delivered but identity may not have been established. */}
            <ChipMultiRow
              label={`How was identity verified? — select all that apply${_idRequired ? (_idMissing ? " — required" : "") : ""}`}
              labelColor={_idMissing ? C.err : undefined}
              options={IDENTITY_VERIFIED}
              values={identityVerifiedBy}
              onChange={setIdentityVerifiedBy}
              allowCustom
            />
            {_idMissing && (
              <div style={{ fontSize: 11, color: C.err, marginTop: -6 }}>
                Select at least one way you verified this person's identity. If you could not verify it, say so with "Could not verify" or "Refused to confirm".
              </div>
            )}
            {outcome !== "registered_agent" && <div>{/* [registered-agent-2026-10-22] the registered-agent box above asks for this name */}
              <div style={{ fontSize: 11, fontWeight: 600, color: C.steel, marginBottom: 6 }}>{outcome === "sub_served" ? "Substitute's name" : "Person served — name"}</div>
              {/* [person-served-onetap-2026-10-26] One tap fills the person served with the subject's name from the job, so the server doesn't retype it. */}
              {outcome === "served" && job && job.servee && job.servee !== "Unnamed" && recipName.trim() !== job.servee && (
                <button type="button" onClick={() => setRecipName(job.servee)}
                  style={{ marginBottom: 6, padding: "6px 10px", borderRadius: 999, border: "1px solid " + C.steelXL, background: C.white, color: C.deep, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  Use subject: {job.servee}
                </button>
              )}
              <input type="text" value={recipName} onChange={e => setRecipName(e.target.value)}
                placeholder={outcome === "served" ? "Name of person served" : "Name of recipient"}
                style={{
                  width: "100%", padding: 10, border: "1px solid " + C.steelXL, borderRadius: 10,
                  fontSize: 13, fontFamily: "inherit", outline: "none",
                  color: C.deep, background: C.white, boxSizing: "border-box",
                }} />
            </div>}
            {outcome === "sub_served" && (
              <ChipRow label="Relationship to subject" options={RELATIONSHIPS} value={relationship} onChange={setRelationship} color={C.royal} allowCustom />
            )}
            {/* [notice-capacity-capture-2026-09-19] ═══ POS-010 ITEM 6 — CAPACITY ═══
                Blank on 100% of proofs before this shipped. On a corporation, LLC,
                partnership, minor, conservatee or authorized agent, item 6 plus the
                §416.x subdivision is what makes the service effective at all; without
                it the proof never says in what capacity the papers were accepted, and
                a motion to quash wins on it. Optional here by design — the order is
                the primary source and an unanswered control posts nothing. */}
            <div style={{ padding: 10, borderRadius: 10, border: `1px solid ${C.steelXL}`, background: C.white }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, marginBottom: 4 }}>
                Notice to the Person Served — POS-010 item 6
              </div>
              {_ncJobLabel ? (
                <div style={{ fontSize: 11, color: C.deep, marginBottom: 8, lineHeight: 1.5 }}>
                  The order says: <b>{_ncJobLabel}</b>. Leave this blank to accept that — answer only to correct it.
                </div>
              ) : (
                <div style={{ fontSize: 11, color: C.warn, fontWeight: 600, marginBottom: 8, lineHeight: 1.5 }}>
                  The order does not record a capacity. If the summons names an entity, a minor or a conservatee, what you record here is the only thing item 6 will have.
                </div>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {NOTICE_MODES.map((m) => {
                  const on = noticeMode === m.id;
                  return (
                    <button key={m.id} type="button" onClick={() => setNoticeMode(on ? "" : m.id)} style={{
                      padding: "8px 12px", borderRadius: 999, cursor: "pointer", lineHeight: 1,
                      border: `1.5px solid ${on ? C.royal : C.steelXL}`,
                      background: on ? C.royal + "15" : C.white, color: on ? C.royal : C.deep,
                      fontSize: 12, fontWeight: 700, textAlign: "left",
                    }}>{on ? "✓ " : ""}{m.label}</button>
                  );
                })}
              </div>
              {noticeMode === "fictitious" && (
                <input value={noticeFictitiousName} onChange={(e) => setNoticeFictitiousName(e.target.value)}
                  placeholder="Fictitious name sued under (6b)" style={{
                    width: "100%", marginTop: 8, padding: 10, border: `1px solid ${C.steelXL}`, borderRadius: 10,
                    fontSize: 13, fontFamily: "inherit", outline: "none", color: C.deep, background: C.white, boxSizing: "border-box",
                  }} />
              )}
              {noticeMode === "on_behalf" && (
                <>
                  <input value={noticeOnBehalfName} onChange={(e) => setNoticeOnBehalfName(e.target.value)}
                    placeholder="On behalf of — entity / minor / conservatee (6d)" style={{
                      width: "100%", marginTop: 8, padding: 10, border: `1px solid ${C.steelXL}`, borderRadius: 10,
                      fontSize: 13, fontFamily: "inherit", outline: "none", color: C.deep, background: C.white, boxSizing: "border-box",
                    }} />
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.steel, margin: "8px 0 6px" }}>Code of Civil Procedure section (6d)</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {CCP_SECTIONS.map((x) => {
                      const on = ccpSection === x.id;
                      return (
                        <button key={x.id} type="button" onClick={() => setCcpSection(on ? "" : x.id)} style={{
                          padding: "7px 11px", borderRadius: 999, cursor: "pointer", lineHeight: 1,
                          border: `1.5px solid ${on ? C.royal : C.steelXL}`,
                          background: on ? C.royal + "15" : C.white, color: on ? C.royal : C.deep,
                          fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap",
                        }}>{on ? "✓ " : ""}{x.label}</button>
                      );
                    })}
                  </div>
                  {ccpSection === "other" && (
                    <input value={ccpOther} onChange={(e) => setCcpOther(e.target.value)}
                      placeholder="Other section — specify" style={{
                        width: "100%", marginTop: 8, padding: 10, border: `1px solid ${C.steelXL}`, borderRadius: 10,
                        fontSize: 13, fontFamily: "inherit", outline: "none", color: C.deep, background: C.white, boxSizing: "border-box",
                      }} />
                  )}
                </>
              )}
            </div>
            {/* [pos-capture-fix-2026-07-26] Required. Drives POS-010 item 5b(1)/(2)/(3),
                sworn under penalty of perjury — rendered as its own chip row rather than
                via ChipRow because the value sent to the backend ('business'/'home'/
                'unknown') must not be the visible label. */}
            {outcome === "sub_served" && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: subLocType ? C.steel : C.err, marginBottom: 6 }}>
                  Where did you leave the papers? {subLocType ? "" : "— required"}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {SUB_LOC_TYPES.map((o) => (
                    <button key={o.id} type="button" onClick={() => setSubLocType(subLocType === o.id ? "" : o.id)} style={{
                      padding: "8px 12px", borderRadius: 999, cursor: "pointer", lineHeight: 1,
                      border: `1.5px solid ${subLocType === o.id ? C.royal : (subLocType ? C.steelXL : C.err)}`,
                      background: subLocType === o.id ? C.royal + "15" : C.white,
                      color: subLocType === o.id ? C.royal : C.deep,
                      fontSize: 12, fontWeight: 700, textAlign: "left",
                    }}>{o.label}</button>
                  ))}
                </div>
                {!subLocType && (
                  <div style={{ fontSize: 11, color: C.err, marginTop: 6 }}>
                    You are swearing to this on the proof of service.
                  </div>
                )}
              </div>
            )}
            {/* [sub-serve-mailing-2026-09-18] CCP §415.20(b) follow-up mailing.
                Three explicit states. "Not mailed yet" is an ANSWER, not a blank —
                a blank is indistinguishable from nobody having asked, and the
                mailing is what makes substituted service complete. */}
            {outcome === "sub_served" && (
              <div style={{
                border: `1.5px solid ${_mailMissing ? C.err : C.steelXL}`, borderRadius: 12,
                padding: 12, background: _mailMissing ? C.errL : C.white,
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: _mailMissing ? C.err : C.deep, letterSpacing: "0.04em" }}>
                  FOLLOW-UP MAILING — CCP §415.20(b){mailStatus ? "" : " — required"}
                </div>
                <div style={{ fontSize: 11, color: C.steel, marginTop: 4, lineHeight: 1.5 }}>
                  Leaving the copies is only half of substituted service. A copy must also be
                  mailed, first class, to the person to be served at the address where you left
                  them. Service is complete on the 10th day <i>after</i> that mailing.
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                  {[
                    { id: "today",   label: "I mailed it today" },
                    { id: "other",   label: "Mailed on another date" },
                    { id: "not_yet", label: "Not mailed yet" },
                  ].map((o) => (
                    <button key={o.id} type="button" onClick={() => {
                      setMailStatus(o.id);
                      if (o.id === "today") setMailDateIso(ssTodayLocalISO());
                    }} style={{
                      padding: "8px 12px", borderRadius: 999, cursor: "pointer", lineHeight: 1,
                      border: `1.5px solid ${mailStatus === o.id ? (o.id === "not_yet" ? C.warn : C.royal) : (mailStatus ? C.steelXL : C.err)}`,
                      background: mailStatus === o.id ? (o.id === "not_yet" ? C.warn + "18" : C.royal + "15") : C.white,
                      color: mailStatus === o.id ? (o.id === "not_yet" ? C.warn : C.royal) : C.deep,
                      fontSize: 12, fontWeight: 700,
                    }}>{o.label}</button>
                  ))}
                </div>

                {mailStatus === "other" && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.steel, marginBottom: 6 }}>Date mailed</div>
                    <input type="date" value={mailDateIso} max={ssTodayLocalISO()}
                      onChange={(e) => setMailDateIso(e.target.value)}
                      style={{
                        width: "100%", padding: 10, border: "1px solid " + C.steelXL, borderRadius: 10,
                        fontSize: 13, fontFamily: "inherit", outline: "none",
                        color: C.deep, background: C.white, boxSizing: "border-box",
                      }} />
                  </div>
                )}

                {(mailStatus === "today" || mailStatus === "other") && (
                  <>
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <div style={{ flex: 3 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: C.steel, marginBottom: 6 }}>Mailed from — city</div>
                        <input type="text" value={mailCity} onChange={(e) => setMailCity(e.target.value)}
                          placeholder="City the envelope went out from"
                          style={{
                            width: "100%", padding: 10, border: "1px solid " + (String(mailCity).trim() ? C.steelXL : C.err), borderRadius: 10,
                            fontSize: 13, fontFamily: "inherit", outline: "none",
                            color: C.deep, background: C.white, boxSizing: "border-box",
                          }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: C.steel, marginBottom: 6 }}>State</div>
                        <input type="text" value={mailState} maxLength={2}
                          onChange={(e) => setMailState(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
                          placeholder="CA"
                          style={{
                            width: "100%", padding: 10, border: "1px solid " + (/^[A-Za-z]{2}$/.test(String(mailState).trim()) ? C.steelXL : C.err), borderRadius: 10,
                            fontSize: 13, fontFamily: "inherit", outline: "none", textTransform: "uppercase",
                            color: C.deep, background: C.white, boxSizing: "border-box",
                          }} />
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: C.steel, marginTop: 6 }}>
                      Pre-filled from where you left the copies. Change it if you mailed from a
                      different post office — the form asks where the mailing went out from.
                      {_mailIso && ssMailingCompleteOn(_mailIso)
                        ? ` Service completes ${ssMailingCompleteOn(_mailIso)}.`
                        : ""}
                    </div>
                  </>
                )}

                {mailStatus === "not_yet" && (
                  <div style={{
                    marginTop: 10, padding: 10, borderRadius: 10,
                    background: C.warn + "18", border: `1px solid ${C.warn}`,
                    fontSize: 11, color: C.deep, lineHeight: 1.5, fontWeight: 600,
                  }}>
                    Recorded: the mailing has not gone out. <b>Service is not complete</b> and the
                    10-day clock has not started. This job will show a “Mailing due” flag, and its
                    proof of service cannot issue, until the mailing is recorded.
                  </div>
                )}

                {_mailMissing && (
                  <div style={{ fontSize: 11, color: C.err, marginTop: 8, fontWeight: 700 }}>
                    {!mailStatus
                      ? "Answer this before saving. If the envelope has not gone out yet, say so — do not leave it blank."
                      : "Enter the date and the city + state the copies were mailed from."}
                  </div>
                )}
              </div>
            )}
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.deep, cursor: "pointer" }}>
              <input type="checkbox" checked={isAdult} onChange={e => setIsAdult(e.target.checked)} />
              <span>Recipient is 18+ years old</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.deep, cursor: "pointer" }}>
              <input type="checkbox" checked={informedOfNature} onChange={e => setInformedOfNature(e.target.checked)} />
              <span>I informed them of the general nature of the papers</span>
            </label>
            {(outcome === "sub_served" || outcome === "refused") && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.deep, cursor: "pointer" }}>
                <input type="checkbox" checked={leftInPresence} onChange={e => setLeftInPresence(e.target.checked)} />
                <span>{outcome === "refused" ? "I left the papers in their presence (CCP §415.21)" : "I left the papers in their presence"}</span>
              </label>
            )}
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.06em", marginTop: 2 }}>SUBJECT DESCRIPTION</div>
            <ChipRow label="Sex" options={GENDER} value={desc.gender} onChange={(v) => setDesc({ ...desc, gender: v })} />
            <ChipRow label="Race" options={RACE} value={desc.race} onChange={(v) => setDesc({ ...desc, race: v })} />
            <ChipRow label="Approx age" options={AGE} value={desc.age} onChange={(v) => setDesc({ ...desc, age: v })} />
            <ChipRow label="Height" options={HEIGHT} value={desc.height} onChange={(v) => setDesc({ ...desc, height: v })} allowCustom />
            <ChipRow label="Weight" options={WEIGHT} value={desc.weight} onChange={(v) => setDesc({ ...desc, weight: v })} allowCustom />
            <ChipRow label="Hair" options={HAIR} value={desc.hair} onChange={(v) => setDesc({ ...desc, hair: v })} />
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.deep, cursor: "pointer" }}>
              <input type="checkbox" checked={desc.glasses} onChange={e => setDesc({ ...desc, glasses: e.target.checked })} />
              <span>Wears glasses</span>
            </label>
            <ChipMultiRow label="Distinguishing features" options={FEATURES} values={subjectFeatures} onChange={setSubjectFeatures} />
          </div>
        )}

        {/* No contact (no_answer / not_home): observations + plate + talked-to. */}
        {(outcome === "no_answer" || outcome === "not_home") && (
          <div style={{ marginBottom: 14, display: "flex", flexDirection: "column", gap: 12 }}>
            <ChipMultiRow label="Observations / signs of life" options={SIGNS} values={signsOfLife} onChange={setSignsOfLife} color={C.warn} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.steel, marginBottom: 6 }}>Vehicle plate (if present)</div>
              <input type="text" value={vehiclePlate} onChange={e => setVehiclePlate(e.target.value.toUpperCase())}
                placeholder="License plate" maxLength={10}
                style={{
                  width: "100%", padding: 10, border: "1px solid " + C.steelXL, borderRadius: 10,
                  fontSize: 13, fontFamily: "monospace", outline: "none",
                  color: C.deep, background: C.white, boxSizing: "border-box", textTransform: "uppercase",
                }} />
            </div>
            <ChipRow label="Talked to a neighbor?" options={NO_CONTACT_TALKED} value={talkedTo} onChange={setTalkedTo} color={C.warn} allowCustom />
            {talkedTo && talkedTo !== "None" && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.steel, marginBottom: 6 }}>Their name (if provided)</div>
                <input type="text" value={talkedToName} onChange={e => setTalkedToName(e.target.value)} placeholder="name if provided"
                  style={{
                    width: "100%", padding: 10, border: "1px solid " + C.steelXL, borderRadius: 10,
                    fontSize: 13, fontFamily: "inherit", outline: "none",
                    color: C.deep, background: C.white, boxSizing: "border-box",
                  }} />
              </div>
            )}
          </div>
        )}

        {/* Bad address: indicators + talked-to. */}
        {outcome === "bad_addr" && (
          <div style={{ marginBottom: 14, display: "flex", flexDirection: "column", gap: 12 }}>
            <ChipMultiRow label="Indicator" options={BAD_ADDR_INDICATORS} values={indicators} onChange={setIndicators} color={C.err} />
            <ChipRow label="Talked to" options={BAD_ADDR_TALKED} value={talkedTo} onChange={setTalkedTo} color={C.err} allowCustom />
            {talkedTo && talkedTo !== "None" && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.steel, marginBottom: 6 }}>Their name (if provided)</div>
                <input type="text" value={talkedToName} onChange={e => setTalkedToName(e.target.value)} placeholder="name if provided"
                  style={{
                    width: "100%", padding: 10, border: "1px solid " + C.steelXL, borderRadius: 10,
                    fontSize: 13, fontFamily: "inherit", outline: "none",
                    color: C.deep, background: C.white, boxSizing: "border-box",
                  }} />
              </div>
            )}
          </div>
        )}

        {/* [attempt-outcomes-2026-10-07] Moved: who reported it. The note (required) carries what they said. */}
        {outcome === "moved" && (
          <div style={{ marginBottom: 14, display: "flex", flexDirection: "column", gap: 12 }}>
            <ChipRow label="Who told you?" options={MOVED_TALKED} value={talkedTo} onChange={setTalkedTo} color={C.err} allowCustom />
            {talkedTo && talkedTo !== "None" && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.steel, marginBottom: 6 }}>Their name (if provided)</div>
                <input type="text" value={talkedToName} onChange={e => setTalkedToName(e.target.value)} placeholder="name if provided"
                  style={{
                    width: "100%", padding: 10, border: "1px solid " + C.steelXL, borderRadius: 10,
                    fontSize: 13, fontFamily: "inherit", outline: "none",
                    color: C.deep, background: C.white, boxSizing: "border-box",
                  }} />
              </div>
            )}
          </div>
        )}

        {/* [attempt-outcomes-2026-10-07] Vacant: what was observed. */}
        {outcome === "vacant" && (
          <div style={{ marginBottom: 14 }}>
            <ChipMultiRow label="Signs of vacancy" options={VACANT_SIGNS} values={vacantSigns} onChange={setVacantSigns} color={C.warn} />
          </div>
        )}

        {/* [attempt-outcomes-2026-10-07] Gated / no access: what blocked access (optional). */}
        {outcome === "gated" && (
          <div style={{ marginBottom: 14 }}>
            <ChipMultiRow label="What blocked access" options={GATE_KINDS} values={gateKinds} onChange={setGateKinds} color={C.warn} />
          </div>
        )}

        {/* [attempt-outcomes-2026-10-07] Court closed: recorded as a non-service attempt. */}
        {outcome === "court_closed" && (
          <div style={{ marginBottom: 14 }}>
            {/* [court-closed-not-counted-2026-10-08] owner decision: on the server, not the client */}
            <div role="alert" style={{ padding: 10, borderRadius: 10, background: C.err + "14", border: `1.5px solid ${C.err}`, fontSize: 12, color: C.deep, lineHeight: 1.5, fontWeight: 700 }}>
              This attempt is on you, not the client. It does not use one of the client's 3 attempts. The app shows court closures before you go — check the court banner first.
            </div>
            {courtSt && courtSt.applies ? <CourtStatusBanner cs={courtSt} style={{ marginTop: 8 }} /> : null}
            {courtShownClosed ? (
              <button type="button" onClick={() => { setCourtAck((v) => !v); setSubmitErr(""); }} aria-pressed={courtAck} style={{
                marginTop: 8, width: "100%", padding: "10px 12px", borderRadius: 10, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 8, textAlign: "left",
                background: courtAck ? C.ok + "18" : C.white, border: `1.5px solid ${courtAck ? C.ok : C.err}`,
                color: C.deep, fontSize: 12, fontWeight: 700,
              }}>
                {courtAck ? <CheckCircle2 size={18} color={C.ok} /> : <Circle size={18} color={C.err} />}
                I understand — the app showed this court closed. Record the attempt.
              </button>
            ) : null}
            <div style={{ marginTop: 8, padding: 10, borderRadius: 10, background: C.warn + "18", border: `1px solid ${C.warn}`, fontSize: 11, color: C.deep, lineHeight: 1.5, fontWeight: 600 }}>
              Recorded in the attempt history with no service, marked "not counted". A re-attempt is required. Note which court and why it was closed.
            </div>
          </div>
        )}

        {/* Unsafe: reasons multi-chip. */}
        {outcome === "unsafe" && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.err, letterSpacing: "0.06em", marginBottom: 6 }}>UNSAFE REASON</div>
            <ChipMultiRow label="Reasons" options={UNSAFE} values={unsafeReasons} onChange={setUnsafeReasons} color={C.err} />
          </div>
        )}

        {/* Photo + voice */}
        {/* [browser-evidence-upload-2026-07-27] Hidden input backing takePhoto in the
            browser build. capture="environment" opens the rear camera directly on iOS
            Safari and Android Chrome; on desktop it degrades to a file picker. multiple
            allows several shots in one pick. */}
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={onPhotoFilesPicked}
          style={{ display: "none" }}
        />
        {/* [attempt-photo-library-2026-10-09] no capture attribute = Photos library picker, multi-select */}
        <input
          ref={libraryInputRef}
          type="file"
          accept="image/*,.heic,.heif"
          multiple
          onChange={onPhotoFilesPicked}
          style={{ display: "none" }}
        />
        {/* [attempt-gate-2026-10-08] Required photo types — plain guidance, no tagging. */}
        <div style={{ marginBottom: 8, padding: 10, borderRadius: 10, background: C.bg, border: `1px solid ${C.steelXL}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.deep, letterSpacing: "0.06em", marginBottom: 4 }}>
            REQUIRED PHOTOS (AT LEAST {SS_ATTEMPT_MIN_PHOTOS})
          </div>
          {SS_ATTEMPT_PHOTO_TYPES.map((t, i) => (
            <div key={t} style={{ fontSize: 12, color: C.deep, lineHeight: 1.5 }}>{(i + 1) + ". " + t}</div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <button onClick={takePhoto} disabled={photoBusy} style={{
            flex: 1, padding: 14, background: photos.length ? C.okL : C.white,
            border: `1.5px dashed ${photos.length ? C.ok : C.steelL}`, borderRadius: 12,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: photoBusy ? "wait" : "pointer",
          }}>
            <Camera size={20} color={photos.length ? C.ok : C.deep} />
            <span style={{ fontSize: 11, fontWeight: 700, color: photos.length ? C.ok : C.deep }}>
              {photoBusy ? "Uploading..." : (photos.length ? `${photos.length} photo${photos.length === 1 ? "" : "s"}` : "Take photo")}
            </span>
          </button>
          {/* [attempt-photo-library-2026-10-09] */}
          <button type="button" onClick={pickFromLibrary} disabled={photoBusy} style={{
            flex: 1, padding: 14, background: C.white,
            border: `1.5px dashed ${C.steelL}`, borderRadius: 12,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: photoBusy ? "wait" : "pointer",
          }}>
            <ImageIcon size={20} color={C.deep} />
            <span style={{ fontSize: 11, fontWeight: 700, color: C.deep }}>
              {photoBusy ? "Uploading..." : "From library"}
            </span>
          </button>
          {/* [browser-gps-fallback-2026-07-27] Read gpsOk, not bare `gps` truthiness, so this
              tile agrees with the banner and the payload on what counts as a fix. */}
          <div style={{
            flex: 1, padding: 14, background: gpsOk ? C.okL : C.bg,
            border: `1.5px dashed ${gpsOk ? C.ok : C.steelL}`, borderRadius: 12,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
          }}>
            <MapPin size={20} color={gpsOk ? C.ok : C.steel} />
            <span style={{ fontSize: 11, fontWeight: 700, color: gpsOk ? C.ok : C.steel }}>
              {gpsBusy ? "Getting GPS..." : (gpsOk ? "GPS locked" : "GPS unavailable")}
            </span>
          </div>
        </div>

        {/* Notes */}
        <textarea ref={notesRef} onChange={(e) => setNotesText(e.target.value)} onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: "smooth", block: "center" }), 350)} placeholder={ssOutcomeNotePrompt(outcome)} style={{
          width: "100%", padding: 0, border: `1px solid ${_noteMissing ? C.err : C.steelXL}`, borderRadius: 12,
          fontSize: 13, fontFamily: "inherit", resize: "none", minHeight: 70, outline: "none",
          color: C.deep, background: C.white, boxSizing: "border-box", marginBottom: 14,
        }} />

        {/* [attempt-geofence-2026-09-20] Amber, not red — this is a "check
            yourself" prompt, not a refusal, and the attempt is one more press
            away. Says the real distance and the real street so the operator can
            tell instantly whether they are on the wrong job or simply parked
            further out than usual. */}
        {geoWarn && (
          <div style={{
            marginBottom: 10, padding: 14, borderRadius: 4,
            background: C.warnL, borderLeft: `4px solid ${C.warn}`,
          }}>
            <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "0.04em", color: C.warn }}>
              OUTSIDE THE SERVICE AREA
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, lineHeight: 1.5, marginTop: 4 }}>
              You are about{" "}
              {geoWarn.miles < 10 ? geoWarn.miles.toFixed(1) : Math.round(geoWarn.miles)} mi from{" "}
              {jobDisplay(job).street || "the service address"}. Check you are at the right address and
              on the right job. Press Save again to log it anyway — the distance is recorded on the
              attempt either way.
            </div>
          </div>
        )}
        {/* [attempt-geofence-2026-09-20] was the literal #c0392b, which is the
            light-mode C.err typed out and stays dark red on a dark surface. */}
        {submitErr && <div style={{ color: C.err, fontSize: 12, marginBottom: 8, textAlign: "center" }}>{submitErr}</div>}
        {/* [pos-capture-fix-2026-07-26] Sub-service cannot be submitted until the
            operator has said where the papers were left — see SUB_LOC_TYPES. */}
        {/* [attempt-photo-visibility-2026-07-27] Save was gated on !outcome || submitting but
            NOT on photoBusy. A photo still uploading is not yet in `photos`, so tapping Save
            mid-upload silently omitted it from the payload and orphaned the S3 object. Gate on
            photoBusy and say so on the button, so the reason for the disabled state is obvious. */}
        {/* [conflict-resolution-2026-07-27] Both guards above are load-bearing and neither
            side of the stash-pop conflict contained the other. Resolved to the UNION: a
            sub-service with no location AND an in-flight photo upload each independently
            block submission. Taking either side alone would have silently dropped a fix. */}
        {/* [verify-multi-2026-09-18] A served / sub-served attempt must say how identity
            was established. Added to the same union as the guards above. */}
        {_idMissing && (
          <div style={{ color: C.err, fontSize: 12, marginBottom: 8, textAlign: "center", fontWeight: 600 }}>
            Select how you verified identity before saving a {outcome === "sub_served" ? "sub-service" : "serve"}.
          </div>
        )}
        {/* [served-photo-required-2026-07-20] Inline gate: a serve needs proof. */}
        {_photoMissing && (
          <div style={{ color: C.warn, fontSize: 12, marginBottom: 8, textAlign: "center", fontWeight: 600 }}>
            A proof photo is required to mark a serve.
          </div>
        )}
        {/* [sub-serve-mailing-2026-09-18] Added to the same union: a substituted serve
            must record the §415.20(b) mailing state before it can be saved. */}
        {_mailMissing && (
          <div style={{ color: C.err, fontSize: 12, marginBottom: 8, textAlign: "center", fontWeight: 600 }}>
            {!mailStatus
              ? "Record the follow-up mailing (CCP §415.20(b)) before saving — mailed today, mailed on another date, or not mailed yet."
              : "The mailing date and the city + state it was mailed from are both required."}
          </div>
        )}
        {/* [attempt-outcomes-2026-10-07] Required-note gate joins the same union. */}
        {_noteMissing && (
          <div style={{ color: C.err, fontSize: 12, marginBottom: 8, textAlign: "center", fontWeight: 600 }}>
            {ssOutcomeNotePrompt(outcome)}
          </div>
        )}
        {/* [attempt-gate-2026-10-08] Pre-save checklist: GPS + photo minimum, every outcome. */}
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          {[
            { ok: gpsOk, label: gpsBusy && !gpsOk ? "Getting GPS…" : "GPS verified" },
            { ok: !_photosShort, label: _photosNeeded ? `Photos: ${_photoCount} of ${_photosNeeded}` : "Photos optional (Unsafe)" },
          ].map((it) => (
            <div key={it.label} style={{
              flex: 1, display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", borderRadius: 10,
              background: it.ok ? C.okL : C.errL, border: `1px solid ${it.ok ? C.ok + "33" : C.err}`,
              fontSize: 12, fontWeight: 700, color: it.ok ? C.ok : C.err,
            }}>
              {it.ok ? <CheckCircle2 size={16} color={C.ok} /> : <Circle size={16} color={C.err} />}
              {it.label}
            </div>
          ))}
        </div>
        <button onClick={() => outcome && !submitting && !photoBusy && !_gateBlocked && !_idMissing && !_mailMissing && !_photoMissing && !_noteMissing && !(outcome === "sub_served" && !subLocType) && submitAttempt(outcome, notesRef.current ? notesRef.current.value : "", null, null)} disabled={!outcome || submitting || photoBusy || _gateBlocked || _idMissing || _mailMissing || _photoMissing || _noteMissing || (outcome === "sub_served" && !subLocType)} style={{
          width: "100%", padding: 14, background: (outcome && !photoBusy && !_gateBlocked && !_idMissing && !_mailMissing && !_photoMissing && !_noteMissing) ? C.deep : C.steelL, color: C.white,
          border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800, cursor: photoBusy ? "wait" : ((outcome && !_gateBlocked && !_idMissing && !_mailMissing && !_photoMissing && !_noteMissing) ? "pointer" : "not-allowed"),
        }}>
          {photoBusy ? "Uploading photo…" : (submitting ? "Saving..." : "Save attempt")}
        </button>
      </div>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// FILING / RETRIEVAL COMPLETE SHEET
// Replaces the serve attempt/affidavit flow for courthouse jobs. Captures the
// field-server's proof of completing an in-person court filing or a document
// retrieval, then POSTs /api/jobs/:id/filing-complete.
//   - court-filing:        GPS + clerk-stamped copy (stamped_copy_key) + filing
//                          date + actual fee paid + optional confirmation # + notes
//   - document-retrieval:  GPS + retrieved docs (retrieved_doc_keys[]) +
//                          certified-copy toggle + optional fee + notes
// Reuses the AttemptSheet upload flow verbatim: Camera.getPhoto(base64) →
// /api/uploads/presign → PUT to S3 → /api/uploads/complete → keep the returned
// key. GPS reuses the Capacitor Geolocation auto-grab pattern. Submit is gated
// on a valid GPS fix + at least one uploaded document.
// ═══════════════════════════════════════════════════════════════════════
function FilingCompleteSheet({ job, kind, onClose }) {
  const { reload } = React.useContext(AppCtx);
  const isRetrieval = kind === "document-retrieval";
  const [submitErr, setSubmitErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [gps, setGps] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  /* [browser-gps-fallback-2026-07-27] Plain-language reason there is no fix. Never a
     substitute for coordinates — whenever it is set, gps stays null and canSubmit is false. */
  const [gpsErr, setGpsErr] = useState("");
  const [docs, setDocs] = useState([]); // array of { key, fileUrl }
  const [docBusy, setDocBusy] = useState(false);
  const [certified, setCertified] = useState(false);
  // Filing date defaults to now; editable via a datetime-local input.
  const [filedAt, setFiledAt] = useState(() => {
    const d = new Date();
    // Local-time value for <input type="datetime-local"> (YYYY-MM-DDTHH:mm).
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const feeRef = useRef(null);
  const confRef = useRef(null);
  const notesRef = useRef(null);

  /* [browser-gps-fallback-2026-07-27] Same Capacitor-only defect as AttemptSheet, but the
     consequence here is harder: this sheet's canSubmit is `gpsValid && docs.length > 0`, so
     in a browser gpsValid was permanently false and the Save button could NEVER be enabled.
     In-person court filing and document retrieval simply could not be completed from
     mobile.californialegalexpress.com at all.
     Native path unchanged. Browser path added. On failure gps stays NULL and the reason is
     shown — no invented courthouse coordinate, for the same reason as the attempt sheet. */
  const fcGpsAliveRef = useRef(true);
  useEffect(() => () => { fcGpsAliveRef.current = false; }, []);
  function captureFilingGps() {
    const Geo = ssPlugin("Geolocation"); // [server-parity-web-2026-10-05]
    if (Geo) {
      setGpsBusy(true);
      Geo.getCurrentPosition({ enableHighAccuracy: true, timeout: 8000 })
        .then(p => setGps({
          lat: Number(p.coords.latitude),
          lng: Number(p.coords.longitude),
          accuracy: Number(p.coords.accuracy || 0),
          ts: Number(p.timestamp || Date.now()),
        }))
        .catch(e => console.warn("[gps]", e.message))
        .finally(() => setGpsBusy(false));
      return;
    }
    const geo = (typeof navigator !== "undefined" && navigator.geolocation) || null;
    if (!geo || typeof geo.getCurrentPosition !== "function") {
      setGpsBusy(false);
      setGpsErr("This browser cannot provide location. Complete this filing from the StrikeServe app.");
      return;
    }
    setGpsBusy(true); setGpsErr("");
    geo.getCurrentPosition(
      (p) => {
        if (!fcGpsAliveRef.current) return;
        setGpsBusy(false);
        const lat = Number(p && p.coords && p.coords.latitude);
        const lng = Number(p && p.coords && p.coords.longitude);
        if (!isFinite(lat) || !isFinite(lng)) { setGpsErr("Unreadable position returned. Tap Retry GPS."); return; }
        setGps({ lat: lat, lng: lng, accuracy: Number((p.coords && p.coords.accuracy) || 0), ts: Number(p.timestamp || Date.now()) });
        setGpsErr("");
      },
      (err) => {
        if (!fcGpsAliveRef.current) return;
        setGpsBusy(false);
        const code = err && err.code;
        setGpsErr(
          code === 1 ? "Location permission denied. Allow location for this site, then tap Retry GPS."
          : code === 3 ? "Location timed out. Step outside the building, then tap Retry GPS."
          : code === 2 ? "No position available. Turn on location services, then tap Retry GPS."
          : "Location failed. Tap Retry GPS."
        );
        console.warn("[gps] filing sheet geolocation failed", code, err && err.message);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }
  useEffect(() => { captureFilingGps(); }, []);

  // Reuses the AttemptSheet S3 upload flow verbatim, only the context label and
  // filename prefix differ so backend can bucket these uploads correctly.
  /* [browser-evidence-upload-2026-07-27] Same Capacitor-only defect as takePhoto. Because
     canSubmit requires docs.length > 0, a browser operator could never attach the
     clerk-stamped copy and so could never close out a filing — the Save button stayed
     disabled with no way forward. Native path unchanged; browser path added; both now
     share ssUploadEvidence. PDFs are accepted here as well as images, since a clerk
     increasingly returns a conformed copy as a PDF. */
  const docInputRef = useRef(null);

  async function addDocument() {
    const Camera = ssPlugin("Camera"); // [server-parity-web-2026-10-05]
    if (!Camera) {
      if (docInputRef.current) { setSubmitErr(""); docInputRef.current.click(); }
      else setSubmitErr("Camera unavailable on this device.");
      return;
    }
    setDocBusy(true); setSubmitErr("");
    try {
      const p = await Camera.getPhoto({
        quality: 80,
        resultType: "base64",
        source: "CAMERA",
        saveToGallery: false,
      });
      const context = isRetrieval ? "retrieved-doc" : "stamped-copy";
      const rec = await ssUploadEvidence(api, {
        bytes: ssB64ToBytes(p.base64String),
        mime: "image/jpeg",
        fileName: `${context}-${job.id}-${Date.now()}.jpg`,
        context,
        jobId: job.id,
      });
      setDocs(prev => [...prev, rec]);
    } catch (e) { setSubmitErr(e.message || "Document capture failed"); }
    finally { setDocBusy(false); }
  }

  /* [browser-evidence-upload-2026-07-27] Browser file-input handler for filing documents.
     Each upload is independent so one failure cannot discard the rest. */
  async function onDocFilesPicked(ev) {
    const files = Array.from((ev && ev.target && ev.target.files) || []);
    if (ev && ev.target) ev.target.value = "";
    if (!files.length) return;
    setDocBusy(true); setSubmitErr("");
    const context = isRetrieval ? "retrieved-doc" : "stamped-copy";
    const failures = [];
    for (const f of files) {
      try {
        const _ssBytes = await ssFileToBytes(f); // [pos-overhaul-2026-10-07:server-app] read once; sniff HEIC/empty types
        const mime = (/pdf/i.test(String(f.type || "")) || /\.pdf$/i.test(String(f.name || ""))) ? "application/pdf" : ssImageMimeOf(f, _ssBytes);
        if (!/^image\/|^application\/pdf$/.test(mime)) { failures.push(`${f.name}: not an image or PDF`); continue; }
        const ext = mime === "application/pdf" ? "pdf" : ssExtForMime(mime);
        const rec = await ssUploadEvidence(api, {
          bytes: _ssBytes,
          mime,
          fileName: `${context}-${job.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`,
          context,
          jobId: job.id,
        });
        setDocs(prev => [...prev, rec]);
      } catch (e) { failures.push(`${f.name}: ${(e && e.message) || "upload failed"}`); }
    }
    setDocBusy(false);
    if (failures.length) setSubmitErr(`${failures.length} document(s) failed to upload and were NOT saved — ${failures.join("; ")}`);
  }

  const gpsValid = !!(gps && typeof gps.lat === "number" && typeof gps.lng === "number" && !isNaN(gps.lat) && !isNaN(gps.lng));
  const canSubmit = gpsValid && docs.length > 0 && !submitting;

  async function submit() {
    if (!job || !job.id || !canSubmit) return;
    setSubmitting(true); setSubmitErr("");
    try {
      const filing_gps = { lat: gps.lat, lng: gps.lng };
      if (typeof gps.accuracy === "number" && !isNaN(gps.accuracy)) filing_gps.accuracy = gps.accuracy;
      const notes = notesRef.current ? notesRef.current.value : "";
      let payload;
      if (isRetrieval) {
        payload = {
          retrieved_doc_keys: docs.map(d => d.key),
          certified_copy: !!certified,
          filing_gps,
          notes: notes || undefined,
        };
        const feeVal = feeRef.current ? parseFloat(feeRef.current.value) : NaN;
        if (!isNaN(feeVal)) payload.court_filing_fee = feeVal;
      } else {
        // in-person court filing
        const feeVal = feeRef.current ? parseFloat(feeRef.current.value) : NaN;
        payload = {
          stamped_copy_key: docs[0].key,
          filed_at: new Date(filedAt).toISOString(),
          filing_gps,
          court_filing_fee: isNaN(feeVal) ? 0 : feeVal,
          notes: notes || undefined,
        };
        const conf = confRef.current ? confRef.current.value.trim() : "";
        if (conf) payload.filing_confirmation = conf;
      }
      await api.post(`/api/jobs/${job.id}/filing-complete`, payload);
      // End the iOS Live Activity — courthouse job is complete. No-op off-iOS.
      try { endJobLiveActivity(job.id, isRetrieval ? "Documents retrieved" : "Filed — stamped copy captured"); } catch (_) {}
      setDone(true);
      await reload();
      // Show success briefly, then close.
      setTimeout(() => { onClose(); }, 1100);
    } catch (e) {
      console.error("[filing-complete] FAIL", e);
      setSubmitErr((e && e.message) || "Could not complete job");
    } finally { setSubmitting(false); }
  }

  if (done) {
    return (
      <Sheet onClose={onClose} small>
        <div style={{ padding: "32px 16px 28px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 28, background: C.okL,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <CheckCircle2 size={30} color={C.ok} />
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.deep }}>
            {isRetrieval ? "Documents retrieved" : "Filing complete"}
          </div>
          <div style={{ fontSize: 13, color: C.steel, textAlign: "center" }}>
            {isRetrieval ? "The retrieval has been submitted." : "The stamped copy has been submitted."}
          </div>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet onClose={onClose} small>
      {/* [sheet-scroll-audit-2026-07-25] flex+minHeight+overflow so this scrolls instead of clipping. */}
      <div style={{ padding: "18px 16px 16px", flex: 1, minHeight: 0, overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>
              {isRetrieval ? "COMPLETE RETRIEVAL" : "COMPLETE FILING"}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, marginTop: 2 }}>
              {job.courtName || job.courtCounty || jobDisplay(job).name}
            </div>
          </div>
          <button onClick={onClose} style={iconBtnLight}>
            <X size={20} color={C.deep} />
          </button>
        </div>

        {/* GPS status */}
        <div style={{
          padding: 12, background: gpsValid ? C.okL : C.bg, borderRadius: 12, marginBottom: 14,
          display: "flex", alignItems: "center", gap: 10,
          border: `1px solid ${gpsValid ? C.ok + "33" : C.steelXL}`,
        }}>
          <MapPin size={18} color={gpsValid ? C.ok : C.steel} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: gpsValid ? C.ok : C.steel }}>
              {gpsBusy ? "Getting GPS..." : (gpsValid ? "GPS captured at courthouse" : "GPS required — enable location")}
            </div>
            {gpsValid && (
              <div style={{ fontSize: 10, color: C.ok, opacity: 0.8 }}>
                {gps.lat.toFixed(4)}, {gps.lng.toFixed(4)}{gps.accuracy ? ` · ±${Math.round(gps.accuracy)}m` : ""}
              </div>
            )}
            {/* [browser-gps-fallback-2026-07-27] Name the reason. Without this the operator
                sees a permanently disabled Save button and no explanation. */}
            {!gpsValid && !gpsBusy && gpsErr && (
              <div style={{ fontSize: 10, color: C.err, marginTop: 2, lineHeight: 1.35, wordBreak: "break-word" }}>{gpsErr}</div>
            )}
          </div>
          {!gpsBusy && !gpsValid && (
            <button type="button" onClick={captureFilingGps} style={{
              padding: "8px 12px", borderRadius: 999, border: `1.5px solid ${C.steel}`,
              background: C.white, color: C.deep, fontSize: 11, fontWeight: 800,
              cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            }}>Retry GPS</button>
          )}
        </div>

        {/* Document upload */}
        <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.06em", marginBottom: 8 }}>
          {isRetrieval ? "RETRIEVED DOCUMENTS" : "CLERK-STAMPED COPY"}
        </div>
        {/* [browser-evidence-upload-2026-07-27] Hidden input backing addDocument in the
            browser build. PDFs accepted alongside images — a clerk increasingly returns a
            conformed copy as a PDF rather than a stamped paper page. */}
        <input
          ref={docInputRef}
          type="file"
          accept="image/*,application/pdf"
          capture="environment"
          multiple
          onChange={onDocFilesPicked}
          style={{ display: "none" }}
        />
        <button onClick={addDocument} disabled={docBusy} style={{
          width: "100%", padding: 16, background: docs.length ? C.okL : C.white,
          border: `1.5px dashed ${docs.length ? C.ok : C.steelL}`, borderRadius: 12,
          display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
          cursor: docBusy ? "wait" : "pointer", marginBottom: 14,
        }}>
          <Camera size={22} color={docs.length ? C.ok : C.deep} />
          <span style={{ fontSize: 12, fontWeight: 700, color: docs.length ? C.ok : C.deep }}>
            {docBusy ? "Uploading..." : (docs.length
              ? `${docs.length} document${docs.length === 1 ? "" : "s"} uploaded${isRetrieval ? " · add more" : ""}`
              : (isRetrieval ? "Capture retrieved documents" : "Capture stamped copy"))}
          </span>
        </button>

        {/* In-person filing fields */}
        {!isRetrieval && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.06em", marginBottom: 6 }}>FILING DATE</div>
            <input
              type="datetime-local"
              value={filedAt}
              onChange={(e) => setFiledAt(e.target.value)}
              style={{
                width: "100%", padding: 12, border: `1px solid ${C.steelXL}`, borderRadius: 12,
                fontSize: 13, fontFamily: "inherit", outline: "none", color: C.deep,
                background: C.white, boxSizing: "border-box", marginBottom: 14,
              }}
            />
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.06em", marginBottom: 6 }}>ACTUAL FILING FEE PAID</div>
            <div style={{ position: "relative", marginBottom: 14 }}>
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, fontWeight: 700, color: C.steel }}>$</span>
              <input
                ref={feeRef} type="number" inputMode="decimal" step="0.01" min="0" placeholder="0.00"
                defaultValue={job.courtFilingFee != null ? job.courtFilingFee.toFixed(2) : ""}
                style={{
                  width: "100%", padding: "12px 12px 12px 26px", border: `1px solid ${C.steelXL}`, borderRadius: 12,
                  fontSize: 13, fontFamily: "inherit", outline: "none", color: C.deep,
                  background: C.white, boxSizing: "border-box",
                }}
              />
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.06em", marginBottom: 6 }}>CONFIRMATION # (OPTIONAL)</div>
            <input
              ref={confRef} type="text" placeholder="Clerk confirmation / receipt #"
              style={{
                width: "100%", padding: 12, border: `1px solid ${C.steelXL}`, borderRadius: 12,
                fontSize: 13, fontFamily: "inherit", outline: "none", color: C.deep,
                background: C.white, boxSizing: "border-box", marginBottom: 14,
              }}
            />
          </>
        )}

        {/* Retrieval-specific fields */}
        {isRetrieval && (
          <>
            <button onClick={() => setCertified(c => !c)} style={{
              width: "100%", padding: 14, background: certified ? C.okL : C.white,
              border: `1.5px solid ${certified ? C.ok : C.steelXL}`, borderRadius: 12,
              display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textAlign: "left", marginBottom: 14,
            }}>
              {certified ? <CheckCircle2 size={20} color={C.ok} /> : <Circle size={20} color={C.steel} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: certified ? C.ok : C.deep }}>Certified copy</div>
                <div style={{ fontSize: 11, color: C.steel }}>Toggle on if the clerk certified these copies</div>
              </div>
            </button>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.06em", marginBottom: 6 }}>FEE PAID (OPTIONAL)</div>
            <div style={{ position: "relative", marginBottom: 14 }}>
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, fontWeight: 700, color: C.steel }}>$</span>
              <input
                ref={feeRef} type="number" inputMode="decimal" step="0.01" min="0" placeholder="0.00"
                style={{
                  width: "100%", padding: "12px 12px 12px 26px", border: `1px solid ${C.steelXL}`, borderRadius: 12,
                  fontSize: 13, fontFamily: "inherit", outline: "none", color: C.deep,
                  background: C.white, boxSizing: "border-box",
                }}
              />
            </div>
          </>
        )}

        {/* Notes */}
        <textarea ref={notesRef} onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: "smooth", block: "center" }), 350)} placeholder="Notes (optional)..." style={{
          width: "100%", padding: 12, border: `1px solid ${C.steelXL}`, borderRadius: 12,
          fontSize: 13, fontFamily: "inherit", resize: "none", minHeight: 70, outline: "none",
          color: C.deep, background: C.white, boxSizing: "border-box", marginBottom: 14,
        }} />

        {submitErr && <div style={{ color: "#c0392b", fontSize: 12, marginBottom: 8, textAlign: "center" }}>{submitErr}</div>}
        <button onClick={submit} disabled={!canSubmit} style={{
          width: "100%", padding: 14, background: canSubmit ? C.deep : C.steelL, color: C.white,
          border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800,
          cursor: canSubmit ? "pointer" : "not-allowed",
        }}>
          {submitting
            ? "Submitting..."
            : (isRetrieval ? "Submit retrieval" : "Submit filing")}
        </button>
        {!canSubmit && !submitting && (
          <div style={{ fontSize: 11, color: C.steel, textAlign: "center", marginTop: 8 }}>
            {!gpsValid ? "Waiting for GPS" : (docs.length === 0 ? (isRetrieval ? "Upload at least one document" : "Upload the stamped copy") : "")}
          </div>
        )}
      </div>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// NAVIGATION HAND-OFF SHEET
// ═══════════════════════════════════════════════════════════════════════
function NavigationSheet({ job, onClose }) {
  // Courthouse jobs navigate to the clerk's office, not a recipient residence.
  const _destParts = courthouseServiceType(job) && job.courtAddress
    ? [job.courtAddress, job.courtCounty ? `${job.courtCounty} County` : ""]
    : [job.address, job.city, job.zip];
  const dest = encodeURIComponent(_destParts.filter(Boolean).join(", "));
  const urls = {
    apple: `https://maps.apple.com/?daddr=${dest}`,
    google: `https://www.google.com/maps/dir/?api=1&destination=${dest}`,
    waze: `https://waze.com/ul?q=${dest}&navigate=yes`,
  };
  const [launchErr, setLaunchErr] = useState("");
  function launch(id) {
    if (urls[id]) {
      try {
        window.location.href = urls[id];
        onClose();
      } catch (e) {
        setLaunchErr((e && e.message) || "Could not open map app");
      }
    }
  }
  const apps = [
    { id: "apple", label: "Apple Maps", desc: "iOS native handoff" },
    { id: "google", label: "Google Maps", desc: "Best for traffic + business info" },
    { id: "waze", label: "Waze", desc: "Live traffic + alerts" },
  ];
  return (
    <Sheet onClose={onClose} small>
      <div style={{ padding: "18px 16px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.deep }}>Open in</div>
          <button onClick={onClose} style={iconBtnLight}><X size={18} color={C.deep} /></button>
        </div>
        {/* [web-parity-2026-07-21] only append city when present (no stray trailing comma) */}
        <div style={{ fontSize: 12, color: C.steel, marginBottom: 14 }}>{job.address}{job.city ? `, ${job.city}` : ""}</div>

        {launchErr && (
          <div style={{ color: "#c0392b", fontSize: 12, padding: "8px 10px", background: "#fdecea", borderRadius: 8, marginBottom: 10 }}>{launchErr}</div>
        )}

        {apps.map(a => (
          <button onClick={() => launch(a.id)} key={a.id} style={{
            width: "100%", padding: 14, marginBottom: 8,
            background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 12,
            display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left",
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, background: C.bg,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Navigation size={18} color={C.deep} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>{a.label}</div>
              <div style={{ fontSize: 10, color: C.steel }}>{a.desc}</div>
            </div>
            <ExternalLink size={16} color={C.steel} />
          </button>
        ))}

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 11, color: C.steel }}>
          <input type="checkbox" /> Always use this app
        </div>
      </div>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// POS SIGNATURE SHEET — real POS-010 quick form
// ═══════════════════════════════════════════════════════════════════════
// Fields: method, substituted sub-fields, served_at, location, description,
// photo evidence, inline signature canvas. Submits to
// POST /api/jobs/:id/proof.
function POSSheet({ job, onClose }) {
  const { reload } = React.useContext(AppCtx);
  const METHODS = ["Personal", "Substituted", "Mail", "Posting", "Publication"];
  const [method, setMethod] = useState("Personal");
  const [personServed, setPersonServed] = useState("");
  const [relationship, setRelationship] = useState("");
  // Default served_at to "now" formatted for a datetime-local input.
  const _isoNow = (() => {
    const d = new Date();
    const pad = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();
  const [servedAt, setServedAt] = useState(_isoNow);
  const _defaultLocation = [job?.address, job?.city, job?.zip].filter(Boolean).join(", ") || "";
  const [location, setLocation] = useState(_defaultLocation);
  const [description, setDescription] = useState("");
  const [photoDataUrl, setPhotoDataUrl] = useState("");
  const fileInputRef = useRef(null);

  // Inline signature canvas — same pattern as SignatureSheet (L1644).
  const sigCanvasRef = useRef(null);
  const sigDrawing = useRef(false);
  const [sigHasInk, setSigHasInk] = useState(false);
  function sigPos(e) {
    const canvas = sigCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: (x / rect.width) * canvas.width, y: (y / rect.height) * canvas.height };
  }
  function sigStart(e) {
    e.preventDefault();
    sigDrawing.current = true;
    const p = sigPos(e);
    const ctx = sigCanvasRef.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  }
  function sigMove(e) {
    if (!sigDrawing.current) return;
    e.preventDefault();
    const p = sigPos(e);
    const ctx = sigCanvasRef.current.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0a1628";
    ctx.lineTo(p.x, p.y); ctx.stroke();
    setSigHasInk(true);
  }
  function sigEnd() { sigDrawing.current = false; }
  function sigClear() {
    const c = sigCanvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setSigHasInk(false);
  }

  function onPhotoPicked(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoDataUrl(String(reader.result || ""));
    reader.readAsDataURL(f);
  }

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  async function submit() {
    if (submitting) return;
    setErr("");
    if (!sigHasInk) { setErr("Please sign before submitting."); return; }
    setSubmitting(true);
    const signature_data_url = sigCanvasRef.current.toDataURL("image/png");
    const body = {
      method,
      person_served: method === "Substituted" ? personServed : null,
      relationship: method === "Substituted" ? relationship : null,
      served_at: servedAt ? new Date(servedAt).toISOString() : new Date().toISOString(),
      location,
      description: description || null,
      photo_data_url: photoDataUrl || null,
      signature_data_url,
    };
    try {
      await api.post(`/api/jobs/${job.id}/proof`, body);
      setOkMsg("Proof of service submitted.");
      try { reload && reload(); } catch (_) {}
      setTimeout(() => { onClose && onClose(); }, 800);
    } catch (e) {
      const msg = (e && e.message) || "";
      setErr(msg || "Could not submit proof.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: "18px 16px", overflow: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>POS-010</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, marginTop: 2 }}>Proof of Service</div>
          </div>
          <button onClick={onClose} style={iconBtnLight}><X size={20} color={C.deep} /></button>
        </div>

        {/* 1. Method of service — pill row */}
        <div style={{
          background: C.white, padding: 12, borderRadius: 14, marginBottom: 10,
          border: `1px solid ${C.steelXL}`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 8 }}>METHOD OF SERVICE</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {METHODS.map(m => (
              <button key={m} onClick={() => setMethod(m)} style={{
                padding: "7px 12px",
                background: method === m ? C.deep : C.bg,
                color: method === m ? C.white : C.deep,
                border: "none", borderRadius: 999,
                fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}>{m}</button>
            ))}
          </div>
        </div>

        {/* 2. Substituted sub-fields */}
        {method === "Substituted" && (
          <div style={{
            background: C.white, padding: 12, borderRadius: 14, marginBottom: 10,
            border: `1px solid ${C.steelXL}`,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 8 }}>SUBSTITUTED SERVICE</div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: C.steel, marginBottom: 4 }}>Name of person served at the location</div>
              <input value={personServed} onChange={e => setPersonServed(e.target.value)}
                placeholder="Jane Doe"
                style={{
                  width: "100%", padding: "9px 10px", border: `1px solid ${C.steelXL}`,
                  borderRadius: 8, fontSize: 13, color: C.deep, outline: "none",
                  fontFamily: "inherit", boxSizing: "border-box",
                }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.steel, marginBottom: 4 }}>Relationship to servee (optional)</div>
              <input value={relationship} onChange={e => setRelationship(e.target.value)}
                placeholder="Co-resident, co-worker, etc."
                style={{
                  width: "100%", padding: "9px 10px", border: `1px solid ${C.steelXL}`,
                  borderRadius: 8, fontSize: 13, color: C.deep, outline: "none",
                  fontFamily: "inherit", boxSizing: "border-box",
                }} />
            </div>
          </div>
        )}

        {/* 3. Date & time of service */}
        <div style={{
          background: C.white, padding: 12, borderRadius: 14, marginBottom: 10,
          border: `1px solid ${C.steelXL}`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 6 }}>DATE & TIME OF SERVICE</div>
          <input type="datetime-local" value={servedAt} onChange={e => setServedAt(e.target.value)}
            style={{
              width: "100%", padding: "9px 10px", border: `1px solid ${C.steelXL}`,
              borderRadius: 8, fontSize: 13, color: C.deep, outline: "none",
              fontFamily: "inherit", boxSizing: "border-box",
            }} />
        </div>

        {/* 4. Location of service */}
        <div style={{
          background: C.white, padding: 12, borderRadius: 14, marginBottom: 10,
          border: `1px solid ${C.steelXL}`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 6 }}>LOCATION OF SERVICE</div>
          {/* [address-picker-sweep-2026-07-28] CONVERT — the address printed on the
              POS-010, and the single highest-value field to standardize. Still
              pre-filled from the job and still fully hand-editable; there are no
              separate city/state/ZIP fields here, so the whole formatted line is
              kept and onAddressParsed is not used. */}
          <AddressAutocomplete value={location} onChange={setLocation}
            placeholder="Street, city, ZIP"
            style={{
              width: "100%", padding: "9px 10px", border: `1px solid ${C.steelXL}`,
              borderRadius: 8, fontSize: 13, color: C.deep, outline: "none",
              fontFamily: "inherit", boxSizing: "border-box",
            }} />
        </div>

        {/* 5. Description of person served */}
        <div style={{
          background: C.white, padding: 12, borderRadius: 14, marginBottom: 10,
          border: `1px solid ${C.steelXL}`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 6 }}>DESCRIPTION (OPTIONAL)</div>
          <textarea value={description} onChange={e => setDescription(e.target.value)}
            placeholder="Gender, hair, build, approx. age range"
            rows={2}
            style={{
              width: "100%", padding: "9px 10px", border: `1px solid ${C.steelXL}`,
              borderRadius: 8, fontSize: 13, color: C.deep, outline: "none",
              fontFamily: "inherit", boxSizing: "border-box", resize: "vertical",
            }} />
        </div>

        {/* 6. Photo evidence */}
        <div style={{
          background: C.white, padding: 12, borderRadius: 14, marginBottom: 10,
          border: `1px solid ${C.steelXL}`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 8 }}>PHOTO EVIDENCE</div>
          {photoDataUrl ? (
            <div style={{ marginBottom: 8 }}>
              <img src={photoDataUrl} alt="evidence"
                style={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 8, display: "block" }} />
            </div>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onPhotoPicked}
            style={{ display: "none" }}
          />
          <button onClick={() => fileInputRef.current && fileInputRef.current.click()} style={{
            width: "100%", padding: 11, background: C.bg, color: C.deep,
            border: `1.5px dashed ${C.steelL}`, borderRadius: 10,
            fontSize: 13, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <Camera size={16} color={C.deep} />
            {photoDataUrl ? "Replace photo" : "Take or choose photo"}
          </button>
        </div>

        {/* 7. Inline signature pad */}
        <div style={{
          background: C.white, padding: 12, borderRadius: 14, marginBottom: 14,
          border: `1px solid ${C.steelXL}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>YOUR SIGNATURE</div>
            <button onClick={sigClear} style={{
              background: "none", border: "none", color: C.steel, fontSize: 11, cursor: "pointer", padding: 0,
            }}>Clear</button>
          </div>
          <canvas
            ref={sigCanvasRef}
            width={600} height={160}
            onMouseDown={sigStart} onMouseMove={sigMove} onMouseUp={sigEnd} onMouseLeave={sigEnd}
            onTouchStart={sigStart} onTouchMove={sigMove} onTouchEnd={sigEnd}
            style={{
              width: "100%", height: 140, background: C.bg, borderRadius: 8,
              border: `1.5px dashed ${C.steelL}`, touchAction: "none", display: "block",
            }}
          />
          <div style={{ textAlign: "center", fontSize: 10, color: C.steel, marginTop: 4 }}>
            Sign with your finger
          </div>
        </div>

        {okMsg && (
          <div style={{
            marginBottom: 10, padding: "8px 10px", background: C.okL, color: C.ok,
            borderRadius: 8, fontSize: 12, fontWeight: 600,
          }}>{okMsg}</div>
        )}
        {err && (
          <div style={{
            marginBottom: 10, padding: "8px 10px", background: C.errL, color: C.err,
            borderRadius: 8, fontSize: 12, fontWeight: 600,
          }}>{err}</div>
        )}

        {/* 8. Submit */}
        <button onClick={submit} disabled={submitting} style={{
          width: "100%", padding: 14, background: submitting ? C.steelL : C.deep, color: C.white,
          border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800,
          cursor: submitting ? "wait" : "pointer",
        }}>
          {submitting ? "Submitting..." : "Submit signed POS"}
        </button>
      </div>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// [server-parity-web-2026-10-05] PROOF OF SERVICE FLOW
// ─────────────────────────────────────────────────────────────────────
// Tapping a proof never opens a blank form any more. For a job:
//   1. GET /api/forms/pos-status/:jobId  — pos_status, form id, final URL
//   2. GET /api/forms/pos-draft/:jobId   — presigned draft PDF (404 = none)
//   prepared  → preview the draft inline, "Sign" (POST /api/forms/sign/:jobId,
//               the existing SignatureSheet) when pending_signature, "Mark
//               filed" (POST /api/server/pos/:jobId/mark-filed) once signed,
//               "View signed" / "Print" when a final PDF exists.
//   nothing   → PosAdminPreparesNotice. Only the agency/admin prepares court
//               forms; the server waits for the draft, then signs it here, or
//               uploads a completed POS (UploadPosSheet).
// [audit-fixes-2026-10-15] H25: the server-side form picker (ProofFormPicker →
//   POST /api/server/jobs/:id/pos/prepare), its SS_POS_FORM_NAMES / SS_POS_FORM_RE
//   helpers, the onPrepared callback and the "Fill out manually" POSSheet mode
//   were removed. The backend already refuses that route (POS_ADMIN_PREPARES).
// ═══════════════════════════════════════════════════════════════════════

async function ssLoadPosState(jobId) {
  const out = { status: null, draftUrl: "", finalUrl: "", formId: null, diligence: false, evidence: false, err: "" };
  try {
    const st = await api.get(`/api/forms/pos-status/${encodeURIComponent(jobId)}`);
    out.status = (st && st.pos_status) || null;
    out.formId = (st && st.pos_form_id) || null;
    out.finalUrl = (st && st.downloadUrl) || "";
    out.diligence = !!(st && st.diligence_attached);
    out.evidence = !!(st && st.evidence_attached);
    out.signedAt = st && st.pos_signed_at; out.filedAt = st && st.pos_filed_at;
  } catch (e) { out.err = (e && e.message) || "Could not load proof status"; }
  try {
    const d = await api.get(`/api/forms/pos-draft/${encodeURIComponent(jobId)}`);
    if (d && d.draftUrl) {
      out.draftUrl = d.draftUrl;
      if (!out.formId) out.formId = d.posFormId || null;
      if (!out.status) out.status = d.posStatus || null;
      out.diligence = out.diligence || !!d.diligence_attached;
      out.evidence = out.evidence || !!d.evidence_attached;
    }
  } catch (e) { /* 404 = no draft prepared yet — that is the picker path */ }
  return out;
}

function PosStatusPill({ status }) {
  const st = String(status || "").toLowerCase();
  const cfg = st === "pending_signature" ? { label: "Ready to sign", color: C.warn, bg: C.warnL, Icon: PenLine }
    : (st === "signed" || st === "delivered") ? { label: "Signed · ready to file", color: C.ok, bg: C.okL, Icon: CheckCircle2 }
    : st === "filed" ? { label: "Filed", color: C.royal, bg: C.infoL, Icon: CheckCircle2 }
    : st === "pending_admin_review" ? { label: "With dispatch", color: C.steel, bg: C.steelXXL, Icon: Clock }
    : st === "rejected" ? { label: "Returned", color: C.err, bg: C.errL, Icon: XCircle }
    : { label: "Not prepared", color: C.steel, bg: C.steelXXL, Icon: Edit3 };
  return <Pill bg={cfg.bg} color={cfg.color} icon={cfg.Icon}>{cfg.label}</Pill>;
}

// ═══════════════════════════════════════════════════════════════════════
// [pos-overhaul-2026-10-07:server-app] SIGNATURES INBOX + REVIEW-BEFORE-SIGN
// ─────────────────────────────────────────────────────────────────────
// Contract (CONTRACT.md, "Server signing"):
//   GET  /api/server/pos-queue        -> the inbox (pending_signature rows)
//   GET  /api/jobs/:id/pos/draft      -> { url, sha256, formId, posStatus, source }
//   POST /api/forms/sign/:jobId       -> { signatureData, sign_city,
//                                          expected_pdf_sha256, attestation }
//   409 "document changed"  -> reload the draft and make the server review again
//   403                     -> only the assigned process server can sign
// The server reads the actual PDF, ticks the penalty-of-perjury declaration,
// confirms the city of signing, then applies a saved or freshly drawn signature.
// Older backends (no /pos/draft, queue grouped by status) are handled: the draft
// falls back to /api/forms/pos-draft and the hash is simply omitted.
// ═══════════════════════════════════════════════════════════════════════
const SS_POS_ATTEST_TEXT = "I have read this proof of service and declare under penalty of perjury under the laws of the State of California that it is true and correct";
/* [pos-sign-location-consent-2026-10-07] Shown under the declaration in the same tick box. */
const SS_POS_LOCATION_CONSENT_TEXT = "I agree that StrikeServe may use my device location when I submit this proof of service, to record the city where I signed it (the place of execution) and the location of my signature in the signing record. If my location is unavailable, the city on my profile is used.";

function ssPacificToday() {
  try {
    return new Date().toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch (_) {
    return new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  }
}
function ssPacificIsoDate() {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  } catch (_) { return new Date().toISOString().slice(0, 10); }
}
function ssFmtRequested(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  try {
    return d.toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (_) { return d.toLocaleString(); }
}
/* "2110 K Street, Sacramento, CA 95816" -> "Sacramento". Profile fallback only. */
function ssCityFromAddress(addr) {
  const parts = String(addr || "").split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 3) return parts[parts.length - 2].replace(/\s+[A-Z]{2}(\s+\d{5}(-\d{4})?)?$/, "").trim();
  return "";
}

/* Draft PDF + its hash. Falls back to the legacy draft route on 404 so an older
   backend still lets the server read what they are signing (no hash -> the
   backend logs 'legacy-client'). 403 is passed through for the caller. */
/* [pos-sign-flow-2026-10-11] Plain-words location of a signature box on a US Letter page. */
function ssSigBoxWhere(b) {
  const cx = (Number(b.x) || 0) + (Number(b.w) || 0) / 2;
  const cy = (Number(b.y) || 0) + (Number(b.h) || 0) / 2;
  const v = cy < 264 ? "bottom" : cy > 528 ? "top" : "middle";
  const h = cx < 204 ? "left" : cx > 408 ? "right" : "center";
  return v === "middle" && h === "center" ? "in the middle of the page" : `${v} ${h} of the page`;
}

async function ssLoadPosDraft(jobId) {
  const id = encodeURIComponent(jobId);
  try {
    const r = await api.get(`/api/jobs/${id}/pos/draft`);
    const url = (r && (r.url || r.draftUrl)) || "";
    if (!url) {
      const e = new Error((r && r.error) || "There is no prepared proof of service on this job yet.");
      e.status = 404; e.body = r || {};
      throw e;
    }
    return {
      url,
      sha256: (r && (r.sha256 || r.pdf_sha256 || r.pdfSha256)) || null,
      formId: (r && (r.formId || r.posFormId || r.pos_form_id)) || null,
      posStatus: (r && (r.posStatus || r.pos_status)) || null,
      source: (r && (r.source || r.posSource || r.pos_source)) || null,
      sigBox: (r && r.sigBox && typeof r.sigBox === "object") ? r.sigBox : null, /* [pos-sign-flow-2026-10-11] where the signature is stamped */
      legacy: false,
    };
  } catch (e) {
    if (!(e && e.status === 404)) throw e;
    try {
      const d = await api.get(`/api/forms/pos-draft/${id}`);
      if (d && d.draftUrl) {
        return { url: d.draftUrl, sha256: d.sha256 || null, formId: d.posFormId || null, posStatus: d.posStatus || null, source: d.source || null, legacy: true };
      }
    } catch (_) { /* fall through to the original error */ }
    throw e;
  }
}

/* GET /api/server/pos-queue in either shape: the contract's flat rows
   (jobId, jobNumber, recipient, caseNumber, formId, source, requestedAt) or the
   older { awaiting_signature:[...], signed_ready_to_file, filed } grouping.
   Returns ONLY the items waiting for this server's signature. */
function ssNormalizePosQueue(r) {
  let rows = [];
  if (Array.isArray(r)) rows = r;
  else if (r && Array.isArray(r.items)) rows = r.items;
  else if (r && Array.isArray(r.pending_signature)) rows = r.pending_signature;
  else if (r && Array.isArray(r.awaiting_signature)) rows = r.awaiting_signature;
  return rows
    .filter((x) => x && String(x.pos_status || x.posStatus || "pending_signature").toLowerCase() === "pending_signature")
    .map((x) => {
      const jobId = x.jobId != null ? x.jobId : (x.job_id != null ? x.job_id : x.id);
      return {
        jobId: jobId != null ? String(jobId) : "",
        jobNumber: x.jobNumber || x.job_number || jobId,
        recipient: x.recipient || x.recipient_name || x.recipientName || x.defendant || "Unnamed",
        caseNumber: x.caseNumber || x.case_number || "",
        formId: x.formId || x.pos_form_id || x.posFormId || "POS",
        source: x.source || x.pos_source || "generated",
        requestedAt: x.requestedAt || x.requested_at || x.pos_requested_at || x.pos_updated_at || x.updated_at || null,
        court: x.court_name || x.courtName || "",
      };
    })
    .filter((x) => x.jobId);
}
async function __ssFetchPosSignCount() {
  const r = await api.get("/api/server/pos-queue");
  return ssNormalizePosQueue(r).length;
}
function usePosSignCount() { return useBadge("posqueue", __ssFetchPosSignCount, 0); }
function ssRefreshPosSignCount() {
  __ssFetchPosSignCount().then((n) => __ssBadgeSet("posqueue", n)).catch(() => { /* keep last */ });
}

/* Open the review-and-sign sheet from anywhere (inbox, push tap, bell, job). */
function ssOpenSign(jobId, meta) {
  if (!jobId) return;
  try { window.dispatchEvent(new CustomEvent("ss-open-sign", { detail: { jobId: String(jobId), meta: meta || null } })); } catch (_) {}
}

/* Best-effort city for the signing line: device location -> /api/geocode/reverse.
   Never blocks the sheet; the server confirms or edits whatever comes back. */
async function ssGuessSignCity() {
  let coords = null;
  try {
    const Geo = ssPlugin("Geolocation");
    if (Geo) {
      const p = await Geo.getCurrentPosition({ enableHighAccuracy: false, timeout: 8000 });
      coords = p && p.coords;
    } else if (typeof navigator !== "undefined" && navigator.geolocation && (typeof window === "undefined" || window.isSecureContext !== false)) {
      coords = await new Promise((res) => {
        try {
          navigator.geolocation.getCurrentPosition((p) => res(p && p.coords), () => res(null), { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
        } catch (_) { res(null); }
      });
    }
  } catch (_) { coords = null; }
  if (!coords || !isFinite(Number(coords.latitude)) || !isFinite(Number(coords.longitude))) return "";
  /* [pos-sign-location-consent-2026-10-07] keep the fix so submit can send it as gps */
  try { ssGuessSignCity.lastCoords = { lat: Number(coords.latitude), lng: Number(coords.longitude), accuracy: isFinite(Number(coords.accuracy)) ? Number(coords.accuracy) : null }; } catch (_) {}
  try {
    const r = await api.get(`/api/geocode/reverse?lat=${encodeURIComponent(coords.latitude)}&lng=${encodeURIComponent(coords.longitude)}`);
    return String((r && r.city) || "").trim();
  } catch (_) { return ""; }
}

/* [esign-evidence-v2-2026-10-18] Signer context for the backend's hash-chained e-signature evidence log
   (cle-backend esign-evidence-v2-2026-10-18, lib/pos/evidence.js captureSignerContext): device, time zone,
   device clock and one idempotency id per signing act. Recorded as reported; the server clock is
   authoritative. Older backends ignore the extra fields. */
function ssEsignUuid() {
  try { if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID(); } catch (_) {}
  /* [recon-server-web-2026-10-21] RFC 4122 v4 from crypto.getRandomValues before any Math.random fallback */
  try {
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      const b = crypto.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
      const h = Array.from(b, (x) => (x + 0x100).toString(16).slice(1)).join("");
      return h.slice(0, 8) + "-" + h.slice(8, 12) + "-" + h.slice(12, 16) + "-" + h.slice(16, 20) + "-" + h.slice(20);
    }
  } catch (_) {}
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => { const r = Math.random() * 16 | 0; return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16); });
}
function ssEsignClientContext() {
  const out = {};
  try {
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    const plat = ssIsNative() ? ssPlatform() : "web";
    const osm = ua.match(/OS (\d+[_.]\d+(?:[_.]\d+)?)/) || ua.match(/Android (\d+(?:\.\d+)*)/);
    out.device = {
      platform: plat,
      model: /iPad/.test(ua) ? "iPad" : /iPhone/.test(ua) ? "iPhone" : ((typeof navigator !== "undefined" && navigator.platform) || undefined),
      os: osm ? (/Android/.test(ua) ? "Android " : "iOS ") + osm[1].replace(/_/g, ".") : undefined,
      app_version: (ssIsNative() ? "StrikeServe iOS " : "StrikeServe web ") + "1.0.0-beta",
      screen: (typeof window !== "undefined" && window.screen) ? window.screen.width + "x" + window.screen.height : undefined,
      lang: (typeof navigator !== "undefined" && navigator.language) || undefined,
    };
  } catch (_) {}
  try { out.client_tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_) {}
  const now = new Date();
  out.client_utc_offset_min = -now.getTimezoneOffset();
  out.client_ts = now.toISOString();
  return out;
}

function PosSignReviewSheet({ jobId, meta, onClose, onSigned }) {
  const { user, reload } = React.useContext(AppCtx);
  const [signRequestId] = useState(() => ssEsignUuid()); /* [esign-evidence-v2-2026-10-18] one id per signing act */
  const m = meta || {};
  const [draft, setDraft] = useState(null);          // null = loading
  const [loadErr, setLoadErr] = useState(null);      // { kind: "forbidden" | "error", msg }
  const [changedNote, setChangedNote] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [city, setCity] = useState("");
  const [cityConfirmed, setCityConfirmed] = useState(false);
  const [citySrc, setCitySrc] = useState("");        // "gps" | "profile" | ""
  const cityTouched = useRef(false);
  const [sigChoice, setSigChoice] = useState("");    // "saved" | "drawn" | ""
  const [drawnSig, setDrawnSig] = useState("");
  const [drawing, setDrawing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  /* [pos-report-error-2026-10-18] "Something's wrong" instead of signing. */
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reportErr, setReportErr] = useState("");
  const [reported, setReported] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  async function sendReport() {
    const t = reportText.trim();
    if (t.length < 5) { setReportErr("Say what is wrong on the proof of service."); return; }
    setReportBusy(true); setReportErr("");
    try {
      await api.post(`/api/forms/sign/${encodeURIComponent(jobId)}/report-error`, { problem: t });
      if (!alive.current) return;
      setReported(true); setReportOpen(false);
    } catch (e) {
      if (!alive.current) return;
      const b = (e && e.body) || {};
      setReportErr(b.error || (e && e.message) || "Could not send the report. Try again.");
    } finally { if (alive.current) setReportBusy(false); }
  }

  const savedSig = React.useMemo(() => {
    const s = user && typeof user.signature === "string" ? user.signature : "";
    if (/^data:image\//.test(s)) return s;
    try { const c = localStorage.getItem("ss_signature_v1") || ""; return /^data:image\//.test(c) ? c : ""; } catch (_) { return ""; }
  }, [user && user.signature]);

  const load = useCallback(async () => {
    setDraft(null); setLoadErr(null);
    try {
      const d = await ssLoadPosDraft(jobId);
      if (alive.current) setDraft(d);
    } catch (e) {
      if (!alive.current) return;
      if (e && e.status === 403) {
        setLoadErr({ kind: "forbidden", msg: (e.body && e.body.error) || "Only the assigned process server can sign this proof of service." });
      } else {
        setLoadErr({ kind: "error", msg: (e && e.message) || "Could not load the proof of service." });
      }
    }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  /* City of signing: profile city first (instant), then a GPS reverse-geocode if
     the server has not typed anything. Either way it must be confirmed. */
  /* [pos-sign-location-consent-2026-10-07] No city field any more. The profile city is the
     fallback; the device location is read only AFTER the server ticks the declaration, which
     includes consent to location use. Whatever is found is sent; the backend falls back to the
     profile when nothing is. */
  const [gps, setGps] = useState(null);
  /* [workflow-audit-2026-10-28 V-22] "City where you are signing" input. Shown when the
     backend answers 400 SIGN_CITY_REQUIRED / requiresInput:'sign_city', or when the device gave no
     location after the declaration was ticked. Prefilled with the profile city; the typed value is
     sent as sign_city on the next submit. */
  const [cityNeeded, setCityNeeded] = useState(false);   // backend asked for it
  const [gpsUnavailable, setGpsUnavailable] = useState(false);
  useEffect(() => {
    const prof = String((user && (user.city || user.signCity || user.sign_city)) || "").trim() || ssCityFromAddress(user && user.address);
    if (prof && !cityTouched.current) { setCity(prof); setCitySrc("profile"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);
  useEffect(() => {
    if (!agreed) return;
    let dead = false;
    try { ssGuessSignCity.lastCoords = null; } catch (_) {} /* [workflow-audit-2026-10-28 V-22] no stale fix from an earlier sheet */
    ssGuessSignCity().then((c) => {
      if (dead || !alive.current) return;
      const g = ssGuessSignCity.lastCoords || null;
      if (g) setGps(g);
      if (c && !cityTouched.current) { setCity(c); setCitySrc("gps"); }
      setGpsUnavailable(!c); /* [workflow-audit-2026-10-28 V-22] no fix or no city from it -> ask */
    });
    return () => { dead = true; };
  }, [agreed, jobId]);

  const sig = sigChoice === "saved" ? savedSig : (sigChoice === "drawn" ? drawnSig : "");
  const missing = [];
  if (!agreed) missing.push("tick the declaration");
  if (!sig) missing.push("choose a signature");
  const showCityInput = cityNeeded || (agreed && gpsUnavailable); /* [workflow-audit-2026-10-28 V-22] */
  if (cityNeeded && !city.trim()) missing.push("enter the city where you are signing");
  const canSubmit = !!draft && !busy && missing.length === 0;
  const formLabel = (draft && draft.formId) || m.formId || "Proof of Service";
  const today = ssPacificToday();

  async function submit() {
    if (!canSubmit) return;
    setBusy(true); setErr(""); setChangedNote("");
    const body = {
      signatureData: sig,
      signature: sig, // legacy field name — older backends read req.body.signature
      signatureType: sigChoice === "saved" ? "saved" : "draw",
      sign_city: city.trim(),
      sign_date: ssPacificIsoDate(),
      location_consent: true,
      location_consent_text: SS_POS_LOCATION_CONSENT_TEXT,
      ...(gps ? { gps } : {}),
      attestation: { accepted: true, text: SS_POS_ATTEST_TEXT, acceptedAt: new Date().toISOString() },
      attestationAccepted: true,
      attestation_accepted: true,
      ...ssEsignClientContext(), /* [esign-evidence-v2-2026-10-18] */
      sign_request_id: signRequestId,
    };
    if (draft && draft.sha256) body.expected_pdf_sha256 = draft.sha256;
    try {
      await api.post(`/api/forms/sign/${encodeURIComponent(jobId)}`, body);
      if (!alive.current) return;
      setDone(true);
      ssInvalidateBadge("posqueue");
      ssRefreshPosSignCount();
      try { reload && reload(); } catch (_) {}
      try { onSigned && onSigned(); } catch (_) {}
    } catch (e) {
      if (!alive.current) return;
      const b = (e && e.body) || {};
      const msg = b.error || (e && e.message) || "";
      const changed = e && e.status === 409 && (/changed|review it again|sha|hash/i.test(msg) || /CHANGED|HASH/i.test(String(b.code || "")));
      /* [workflow-audit-2026-10-28 V-22] backend could not work out the city: ask for it */
      const needCity = String(b.code || "") === "SIGN_CITY_REQUIRED" || String(b.requiresInput || b.requires_input || "") === "sign_city";
      if (needCity) {
        setCityNeeded(true);
        setErr(city.trim()
          ? "Confirm the city where you are signing, then tap Sign again."
          : "Enter the city where you are signing, then tap Sign again.");
      } else if (changed) {
        setAgreed(false);
        setChangedNote("The document changed since you opened it. Please review it again.");
        await load();
      } else if (e && e.status === 403) {
        setErr(msg || "Only the assigned process server can sign this proof of service.");
      } else {
        setErr(msg || "Could not sign the proof of service. Try again.");
      }
    } finally { if (alive.current) setBusy(false); }
  }

  const label = (t) => <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 6 }}>{t}</div>;
  const card = { background: C.white, borderRadius: 14, border: `1px solid ${C.steelXL}`, padding: 12, marginBottom: 12 };
  const native = ssIsNative();

  return (
    <Sheet onClose={busy ? () => {} : onClose}>
      <div style={{ padding: "14px 16px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexShrink: 0 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>
            {formLabel} · JOB #{m.jobNumber || jobId}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, marginTop: 2 }}>{done ? "Signed" : "Review & sign"}</div>
        </div>
        <button onClick={onClose} disabled={busy} style={{ ...iconBtnLight, width: 36, height: 36 }} aria-label="Close"><X size={20} color={C.deep} /></button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "0 16px 24px" }}>
        {done ? (
          <div style={{ ...card, textAlign: "center", padding: "28px 18px" }}>
            <div style={{ width: 60, height: 60, borderRadius: "50%", background: C.okL, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
              <CheckCircle2 size={32} color={C.ok} />
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.deep }}>Signed. Sent to the office.</div>
            <div style={{ fontSize: 12, color: C.steel, marginTop: 6, lineHeight: 1.5 }}>Job #{m.jobNumber || jobId} · {formLabel}</div>
            <button onClick={onClose} style={{
              marginTop: 18, width: "100%", padding: 14, background: C.deep, color: C.white, border: "none",
              borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
            }}>Done</button>
          </div>
        ) : loadErr ? (
          <div style={{ ...card, borderColor: loadErr.kind === "forbidden" ? C.err : C.steelXL }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              {loadErr.kind === "forbidden" ? <Lock size={16} color={C.err} /> : <AlertTriangle size={16} color={C.warn} />}
              <div style={{ fontSize: 13, fontWeight: 800, color: loadErr.kind === "forbidden" ? C.err : C.deep }}>
                {loadErr.kind === "forbidden" ? "You can't sign this proof" : "Proof of service not available"}
              </div>
            </div>
            <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.5 }}>{loadErr.msg}</div>
            {loadErr.kind !== "forbidden" && (
              <button onClick={load} style={{ marginTop: 10, background: "none", border: "none", padding: 0, color: C.royal, fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>Try again</button>
            )}
          </div>
        ) : draft === null ? (
          <SkeletonGroup label="Loading proof of service">
            <SkeletonRect width="100%" height={300} radius={12} />
          </SkeletonGroup>
        ) : (
          <>
            {changedNote && (
              <div style={{ display: "flex", gap: 8, background: C.warnL, border: `1px solid ${C.warn}`, borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
                <AlertTriangle size={16} color={C.warn} style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 12, fontWeight: 700, color: C.deep, lineHeight: 1.45 }}>{changedNote}</div>
              </div>
            )}

            {(m.recipient || m.caseNumber || m.requestedAt) ? (
              <div style={{ ...card, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div style={{ minWidth: 0 }}>{label("RECIPIENT")}<div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>{m.recipient || "—"}</div></div>
                <div style={{ minWidth: 0 }}>{label("CASE")}<div style={{ fontSize: 13, fontWeight: 700, color: C.deep, wordBreak: "break-word" }}>{m.caseNumber || "—"}</div></div>
                <div style={{ minWidth: 0 }}>{label("FORM")}<div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>{formLabel}</div></div>
                <div style={{ minWidth: 0 }}>{label("REQUESTED")}<div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>{ssFmtRequested(m.requestedAt) || "—"}</div></div>
              </div>
            ) : null}

            {/* The document itself */}
            <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.steelXL}`, overflow: "hidden", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "10px 12px", borderBottom: `1px solid ${C.steelXXL}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>READ EVERY PAGE BEFORE SIGNING</div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={() => ssOpenDocViewer({ title: `${formLabel} · #${m.jobNumber || jobId}`, url: draft.url, externalUrl: draft.url, kind: "pdf", filename: `${formLabel}_${m.jobNumber || jobId}.pdf` })}
                    style={{ display: "flex", alignItems: "center", gap: 4, background: C.infoL, color: C.info, padding: "4px 8px", borderRadius: 8, border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    <Eye size={12} /> Full screen
                  </button>
                  {native && (
                    /* iOS WKWebView can paint only the first page of a PDF inside an
                       iframe — the in-app browser (SFSafariViewController) shows all of it. */
                    <button onClick={() => openExternal(draft.url)}
                      style={{ display: "flex", alignItems: "center", gap: 4, background: C.goldXL, color: C.deep, padding: "4px 8px", borderRadius: 8, border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                      <ExternalLink size={12} /> All pages
                    </button>
                  )}
                </div>
              </div>
              <iframe title="Proof of service to sign" src={draft.url}
                style={{ width: "100%", height: "min(55vh, 560px)", border: "none", display: "block", background: C.white }} />
              {/* [pos-sign-flow-2026-10-11] where the office placed the signature (PDF points, bottom-left origin, Letter 612x792) */}
              {draft.sigBox && Number(draft.sigBox.page) >= 1 ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderTop: `1px solid ${C.steelXXL}`, background: C.goldXL, fontSize: 12, fontWeight: 600, color: C.deep }}>
                  <FileSignature size={15} color={C.deep} />
                  <span>Your signature will be placed on page {Number(draft.sigBox.page)}, {ssSigBoxWhere(draft.sigBox)}.</span>
                </div>
              ) : null}
            </div>

            {/* Declaration */}
            <button type="button" onClick={() => setAgreed((v) => !v)} aria-pressed={agreed} style={{
              ...card, width: "100%", textAlign: "left", display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer",
              border: `1.5px solid ${agreed ? C.ok : C.steelXL}`, background: agreed ? C.okL : C.white, fontFamily: "inherit",
            }}>
              <span style={{
                width: 24, height: 24, borderRadius: 7, flexShrink: 0, marginTop: 1,
                border: `2px solid ${agreed ? C.ok : C.steelL}`, background: agreed ? C.ok : C.white,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>{agreed ? <Check size={15} color={C.white} strokeWidth={3} /> : null}</span>
              <span style={{ fontSize: 13, color: C.deep, lineHeight: 1.5, fontWeight: 600 }}>
                {SS_POS_ATTEST_TEXT}.
                <span style={{ display: "block", marginTop: 8, fontSize: 12, fontWeight: 500, color: C.ink }}>{SS_POS_LOCATION_CONSENT_TEXT}</span>
              </span>
            </button>

            {/* [pos-sign-location-consent-2026-10-07] Date + place, read-only (no typing). */}
            <div style={card}>
              {label("DATE SIGNED")}
              <div style={{ fontSize: 14, fontWeight: 700, color: C.deep }}>{today} <span style={{ fontSize: 11, fontWeight: 600, color: C.steel }}>(Pacific time)</span></div>
              {showCityInput ? (
                /* [workflow-audit-2026-10-28 V-22] place of execution (CCP 2015.5) when location is unavailable */
                <div style={{ marginTop: 10 }}>
                  <label htmlFor="ss-sign-city" style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.deep, marginBottom: 6 }}>City where you are signing</label>
                  <input id="ss-sign-city" type="text" value={city} maxLength={100} autoComplete="address-level2" autoCapitalize="words"
                    onChange={(e) => { cityTouched.current = true; setCity(e.target.value); setCitySrc("typed"); }}
                    placeholder="e.g. Sacramento"
                    style={{ width: "100%", boxSizing: "border-box", borderRadius: 10, border: `1px solid ${cityNeeded && !city.trim() ? C.err : C.steelL}`, padding: "10px 12px", fontSize: 16, fontFamily: "inherit", color: C.deep, background: C.white }} />
                  <div style={{ fontSize: 11, color: C.steel, marginTop: 4, lineHeight: 1.4 }}>
                    {cityNeeded ? "We could not get your location. Type the city you are in right now." : "Your location is unavailable. Check this is the city you are in right now."}
                  </div>
                </div>
              ) : agreed && city.trim() ? (
                <div style={{ fontSize: 11, color: C.steel, marginTop: 6 }}>Signed in {city.trim()} {citySrc === "gps" ? "(from your location)" : citySrc === "typed" ? "(you entered)" : "(from your profile)"}</div>
              ) : null}
            </div>

            {/* Signature */}
            <div style={card}>
              {label("YOUR SIGNATURE")}
              {!drawing && savedSig ? (
                <button type="button" onClick={() => setSigChoice("saved")} aria-pressed={sigChoice === "saved"} style={{
                  width: "100%", padding: 8, borderRadius: 10, cursor: "pointer", fontFamily: "inherit", marginBottom: 8,
                  border: `2px solid ${sigChoice === "saved" ? C.ok : C.steelXL}`, background: C.bgW, textAlign: "center",
                }}>
                  <img src={savedSig} alt="Your saved signature" style={{ maxWidth: "100%", maxHeight: 110, objectFit: "contain", display: "inline-block" }} />
                  <div style={{ fontSize: 11, fontWeight: 800, color: sigChoice === "saved" ? C.ok : C.royal, marginTop: 4 }}>
                    {sigChoice === "saved" ? "Saved signature applied" : "Tap to apply your saved signature"}
                  </div>
                </button>
              ) : null}
              {!drawing && drawnSig ? (
                <button type="button" onClick={() => setSigChoice("drawn")} aria-pressed={sigChoice === "drawn"} style={{
                  width: "100%", padding: 8, borderRadius: 10, cursor: "pointer", fontFamily: "inherit", marginBottom: 8,
                  border: `2px solid ${sigChoice === "drawn" ? C.ok : C.steelXL}`, background: C.bgW, textAlign: "center",
                }}>
                  <img src={drawnSig} alt="Signature you drew" style={{ maxWidth: "100%", maxHeight: 110, objectFit: "contain", display: "inline-block" }} />
                  <div style={{ fontSize: 11, fontWeight: 800, color: sigChoice === "drawn" ? C.ok : C.royal, marginTop: 4 }}>
                    {sigChoice === "drawn" ? "New signature applied" : "Tap to apply this signature"}
                  </div>
                </button>
              ) : null}
              {drawing ? (
                <ProfileSignaturePad
                  busy={false}
                  saveLabel="Use this signature"
                  onSave={(dataUrl) => { setDrawnSig(dataUrl); setSigChoice("drawn"); setDrawing(false); }}
                  onCancel={() => setDrawing(false)}
                />
              ) : (
                <button type="button" onClick={() => setDrawing(true)} style={{
                  width: "100%", padding: 11, background: C.white, color: C.deep, border: `1.5px solid ${C.deep}`,
                  borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}><PenLine size={15} /> {drawnSig || savedSig ? "Draw a new signature" : "Draw your signature"}</button>
              )}
            </div>

            {err && (
              <div style={{ color: C.err, fontSize: 12, fontWeight: 600, padding: "9px 11px", background: C.errL, borderRadius: 8, marginBottom: 12, lineHeight: 1.45 }}>{err}</div>
            )}

            <button onClick={submit} disabled={!canSubmit} style={{
              width: "100%", minHeight: 52, padding: 14, border: "none", borderRadius: 14, fontFamily: "inherit",
              background: canSubmit ? `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)` : C.steelL,
              color: canSubmit ? C.deep : C.white, fontSize: 15, fontWeight: 800, cursor: canSubmit ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>{busy ? <SpinnerDot color={C.deep} /> : <FileSignature size={18} />} {busy ? "Signing…" : "Sign proof of service"}</button>
            {missing.length > 0 && !busy && (
              <div style={{ fontSize: 11, color: C.steel, marginTop: 8, textAlign: "center" }}>To sign: {missing.join(" · ")}</div>
            )}

            {/* [pos-report-error-2026-10-18] You sign under penalty of perjury: report anything wrong instead of signing. */}
            <div style={{ marginTop: 16, borderTop: `1px solid ${C.steelXL}`, paddingTop: 12 }}>
              {reported ? (
                <div style={{ fontSize: 12, fontWeight: 600, color: C.deep, background: C.steelXL, borderRadius: 8, padding: "9px 11px", lineHeight: 1.45 }}>
                  Sent to the office. Do not sign this version. They will correct it and send it back for your signature.
                </div>
              ) : !reportOpen ? (
                <button type="button" onClick={() => { setReportOpen(true); setReportErr(""); }} disabled={busy} style={{
                  width: "100%", minHeight: 44, padding: 10, borderRadius: 12, border: `1px solid ${C.err}`, background: "transparent",
                  color: C.err, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}><AlertTriangle size={15} /> Something's wrong? Report an error</button>
              ) : (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.deep, marginBottom: 6 }}>What is wrong on this proof of service?</div>
                  <textarea value={reportText} onChange={(e) => setReportText(e.target.value)} maxLength={2000} rows={3}
                    placeholder="e.g. Item 4 has the wrong address; I served at the side entrance on Elm St."
                    style={{ width: "100%", boxSizing: "border-box", borderRadius: 10, border: `1px solid ${C.steelL}`, padding: 10, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
                  {reportErr && <div style={{ color: C.err, fontSize: 12, fontWeight: 600, marginTop: 6 }}>{reportErr}</div>}
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button type="button" onClick={() => setReportOpen(false)} disabled={reportBusy} style={{
                      flex: 1, minHeight: 42, borderRadius: 10, border: `1px solid ${C.steelL}`, background: "transparent",
                      color: C.steel, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                    <button type="button" onClick={sendReport} disabled={reportBusy} style={{
                      flex: 2, minHeight: 42, borderRadius: 10, border: "none", background: C.err,
                      color: C.white, fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>{reportBusy ? "Sending…" : "Send to the office"}</button>
                  </div>
                  <div style={{ fontSize: 11, color: C.steel, marginTop: 6, lineHeight: 1.4 }}>The proof is not signed. The office fixes it and sends it back to you.</div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}

/* ── Upload a proof of service from the server side ───────────────────────
   POST /api/affidavits/:jobId/upload with isSigned REQUIRED. The PDF goes to the
   office's work queue — never straight to the client, and no payout on upload. */
function UploadPosSheet({ job, onClose, onDone }) {
  const { reload } = React.useContext(AppCtx);
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [isSigned, setIsSigned] = useState(null); // null = not answered (required)
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [conflict, setConflict] = useState(false);
  const [done, setDone] = useState(false);

  function pick(ev) {
    const f = ev && ev.target && ev.target.files && ev.target.files[0];
    if (ev && ev.target) ev.target.value = "";
    if (!f) return;
    const isPdf = /pdf/i.test(String(f.type || "")) || /\.pdf$/i.test(String(f.name || ""));
    if (!isPdf) { setErr("Choose a PDF file."); return; }
    setErr(""); setConflict(false); setFile(f);
  }

  async function upload(replace) {
    if (!file || isSigned === null || busy) return;
    setBusy(true); setErr("");
    try {
      const bytes = await ssFileToBytes(file);
      const magic = String.fromCharCode.apply(null, Array.from(bytes.slice(0, 5)));
      if (magic !== "%PDF-") throw new Error("That file is not a valid PDF.");
      const safeName = (String(file.name || "proof-of-service.pdf").replace(/[^\w.\- ]+/g, "_").slice(0, 120)) || "proof-of-service.pdf";
      const fileName = /\.pdf$/i.test(safeName) ? safeName : safeName + ".pdf";
      const rec = await ssUploadEvidence(api, { bytes, mime: "application/pdf", fileName, context: "affidavit", jobId: job.id });
      await api.post(`/api/affidavits/${encodeURIComponent(job.id)}/upload`, {
        jobId: job.id, fileName, s3Key: rec.key, url: rec.fileUrl || "", fileType: "application/pdf", fileSize: bytes.byteLength,
        notes: notes.trim(), origin: "uploaded", isSigned: isSigned === true,
        ...(job.posFormId ? { formId: job.posFormId } : {}),
        ...(replace ? { replace: true } : {}),
      });
      setDone(true); setConflict(false);
      ssInvalidateBadge("posqueue"); ssRefreshPosSignCount();
      try { reload && reload(); } catch (_) {}
      try { onDone && onDone(); } catch (_) {}
    } catch (e) {
      const msg = (e && e.body && e.body.error) || (e && e.message) || "Upload failed";
      if (e && e.status === 409) { setConflict(true); setErr(msg); }
      else if (e && e.status === 403) setErr(msg || "Only the process server assigned to this job can upload its proof of service.");
      else setErr(msg);
    } finally { setBusy(false); }
  }

  const yesNo = (val, text) => (
    <button type="button" onClick={() => setIsSigned(val)} aria-pressed={isSigned === val} style={{
      flex: 1, padding: "12px 10px", borderRadius: 12, cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 800,
      border: `2px solid ${isSigned === val ? C.deep : C.steelXL}`, background: isSigned === val ? C.deep : C.white,
      color: isSigned === val ? C.white : C.deep,
    }}>{text}</button>
  );
  const canUpload = !!file && isSigned !== null && !busy;

  return (
    <Sheet onClose={busy ? () => {} : onClose} small>
      <div style={{ padding: 18, overflowY: "auto", flex: 1, minHeight: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>JOB #{job.jobNumber}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, marginTop: 2 }}>Upload proof of service</div>
          </div>
          <button onClick={onClose} disabled={busy} style={iconBtnLight} aria-label="Close"><X size={20} color={C.deep} /></button>
        </div>
        {done ? (
          <div style={{ textAlign: "center", padding: "18px 6px 6px" }}>
            <CheckCircle2 size={40} color={C.ok} />
            <div style={{ fontSize: 15, fontWeight: 800, color: C.deep, marginTop: 10, lineHeight: 1.45 }}>
              Sent to the office for review. You'll be paid after an admin approves it.
            </div>
            <button onClick={onClose} style={{ marginTop: 16, width: "100%", padding: 13, background: C.deep, color: C.white, border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>Done</button>
          </div>
        ) : (
          <>
            <input ref={inputRef} type="file" accept="application/pdf,.pdf" onChange={pick} style={{ display: "none" }} />
            <button type="button" onClick={() => inputRef.current && inputRef.current.click()} disabled={busy} style={{
              width: "100%", padding: 14, borderRadius: 12, border: `1.5px dashed ${file ? C.ok : C.steelL}`, background: file ? C.okL : C.white,
              display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontFamily: "inherit", textAlign: "left",
            }}>
              {file ? <FileText size={20} color={C.ok} /> : <Upload size={20} color={C.deep} />}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: C.deep, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file ? file.name : "Choose the PDF"}</div>
                <div style={{ fontSize: 11, color: C.steel, marginTop: 2 }}>{file ? `${Math.max(1, Math.round(file.size / 1024))} KB · tap to change` : "PDF only"}</div>
              </div>
            </button>

            <div style={{ fontSize: 13, fontWeight: 800, color: C.deep, margin: "16px 0 8px" }}>Is it already signed by you? <span style={{ color: C.err }}>*</span></div>
            <div style={{ display: "flex", gap: 8 }}>
              {yesNo(true, "Yes")}
              {yesNo(false, "No")}
            </div>
            {isSigned === false && (
              <div style={{ fontSize: 11, color: C.steel, marginTop: 8, lineHeight: 1.5 }}>The office will send it back to you here to review and sign.</div>
            )}

            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={2000} placeholder="Anything the office should know (optional)"
              style={{ width: "100%", marginTop: 14, padding: "9px 10px", border: `1px solid ${C.steelXL}`, borderRadius: 8, fontSize: 13, color: C.deep, outline: "none", fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", background: C.white }} />

            {err && <div style={{ fontSize: 12, color: C.err, background: C.errL, padding: "8px 10px", borderRadius: 8, marginTop: 10, lineHeight: 1.45 }}>{err}</div>}
            {conflict && (
              <button onClick={() => upload(true)} disabled={busy} style={{
                width: "100%", marginTop: 8, padding: 11, background: C.white, color: C.err, border: `1.5px solid ${C.err}`,
                borderRadius: 12, fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
              }}>Replace the file already on this job</button>
            )}

            <button onClick={() => upload(false)} disabled={!canUpload} style={{
              width: "100%", padding: 14, marginTop: 12, background: canUpload ? C.deep : C.steelL, color: C.white,
              border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: canUpload ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit",
            }}>{busy ? <SpinnerDot /> : <Upload size={18} />} {busy ? "Uploading…" : "Send to the office"}</button>
            {!busy && (!file || isSigned === null) && (
              <div style={{ fontSize: 11, color: C.steel, marginTop: 8, textAlign: "center" }}>
                {!file ? "Choose the PDF" : "Answer whether it is already signed by you"}
              </div>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}

/* ── POS profile completeness (item 7 of the POS uses the SIGNING server's profile) ──
   GET /api/auth/profile -> { posProfileComplete, posProfileMissing:[...] }. Unknown
   (older backend) renders nothing. */
const SS_POS_MISSING_LABELS = {
  name: "full name", address: "address", phone: "phone",
  reg_number: "registration number", regNumber: "registration number",
  county: "registration county", reg_county: "registration county",
  registration_role: "registration role (owner, employee or independent contractor)",
  registrationRole: "registration role (owner, employee or independent contractor)",
};
async function __ssFetchPosProfile() {
  const pick = (r) => (r && typeof r.posProfileComplete === "boolean")
    ? { complete: r.posProfileComplete, missing: Array.isArray(r.posProfileMissing) ? r.posProfileMissing : [] }
    : null;
  try { const p = pick(await api.get("/api/auth/profile")); if (p) return p; } catch (_) { /* older backend */ }
  try { const p = pick(await api.get("/api/auth/me")); if (p) return p; } catch (_) { /* ignore */ }
  return { complete: null, missing: [] };
}
function usePosProfile() { return useBadge("posprofile", __ssFetchPosProfile, { complete: null, missing: [] }); }
function ssRefreshPosProfile() {
  ssInvalidateBadge("posprofile");
  __ssFetchPosProfile().then((v) => __ssBadgeSet("posprofile", v)).catch(() => {});
}
function PosProfileBanner({ context }) {
  const st = usePosProfile();
  if (!st || st.complete !== false) return null;
  const items = (st.missing || []).map((k) => SS_POS_MISSING_LABELS[k] || String(k).replace(/_/g, " "));
  return (
    <div role="alert" style={{ background: C.warnL, border: `1px solid ${C.warn}`, borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <AlertTriangle size={16} color={C.warn} />
        <div style={{ fontSize: 13, fontWeight: 800, color: C.deep }}>
          {context === "attempt" ? "Your proof-of-service profile is incomplete" : "Finish your profile so your proofs of service are complete"}
        </div>
      </div>
      <div style={{ fontSize: 12, color: C.ink, marginTop: 6, lineHeight: 1.5 }}>
        Your proofs of service print your details as the server. Missing: {items.length ? items.join(", ") : "some required details"}.
      </div>
      <button onClick={() => { try { window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: "profile" })); } catch (_) {} }} style={{
        marginTop: 8, padding: "7px 12px", background: C.deep, color: C.white, border: "none", borderRadius: 9,
        fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
      }}>Update profile</button>
    </div>
  );
}

/* [pos-overhaul-2026-10-07:server-app] iPhone photos: a picked File may arrive with
   an empty or generic type, or a .heic name. Sniff the bytes so the presign fileType
   and the S3 PUT Content-Type say what the object really is (HEIC -> image/heic, which
   the backend's heic-worker converts to a viewable JPEG). */
function ssSniffImageMime(bytes, fallback) {
  try {
    const b = bytes && bytes.length ? bytes : null;
    if (b && b.length >= 12) {
      if (b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) return "image/jpeg";
      if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) return "image/png";
      if (b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46) return "image/gif";
      if (b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) return "image/webp";
      const box = String.fromCharCode(b[4], b[5], b[6], b[7]);
      if (box === "ftyp") {
        const brand = String.fromCharCode(b[8], b[9], b[10], b[11]).toLowerCase();
        if (/^(heic|heix|hevc|hevx|heim|heis)$/.test(brand)) return "image/heic";
        if (/^(mif1|msf1|heif)$/.test(brand)) return "image/heif";
      }
    }
  } catch (_) { /* fall through */ }
  return fallback;
}
function ssImageMimeOf(file, bytes) {
  const n = String((file && file.name) || "").toLowerCase();
  const t = String((file && file.type) || "").toLowerCase();
  let fallback = t && t !== "application/octet-stream" ? t
    : /\.heic$/.test(n) ? "image/heic" : /\.heif$/.test(n) ? "image/heif"
    : /\.png$/.test(n) ? "image/png" : /\.webp$/.test(n) ? "image/webp" : /\.gif$/.test(n) ? "image/gif" : "image/jpeg";
  return ssSniffImageMime(bytes, fallback);
}
function ssExtForMime(mime) {
  const m = String(mime || "").toLowerCase();
  return m === "image/jpeg" ? "jpg" : m === "image/heic" ? "heic" : m === "image/heif" ? "heif" : (m.split("/")[1] || "jpg").replace(/[^a-z0-9]/g, "") || "jpg";
}

function ProofFlowSheet({ job, onClose }) {
  const { user, reload } = React.useContext(AppCtx);
  const [pos, setPos] = useState(null); // null = loading
  const [mode, setMode] = useState("state"); // state | pick  — [audit-fixes-2026-10-15] H25: "manual" (POSSheet) removed
  const [signOpen, setSignOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false); // [pos-overhaul-2026-10-07:server-app]
  const [busy, setBusy] = useState("");

  const load = React.useCallback(async () => {
    const st = await ssLoadPosState(job.id);
    setPos(st);
    /* [pos-overhaul-2026-10-07:server-app] pending_signature with no stored draft key is
       now rendered on the fly by GET /api/jobs/:id/pos/draft — it is signable, not "pick". */
    setMode(st.draftUrl || st.finalUrl || String(st.status || "").toLowerCase() === "pending_signature" ? "state" : "pick"); // [audit-fixes-2026-10-15] H25
  }, [job && job.id]);
  useEffect(() => { setPos(null); load(); }, [load]);

  const proofFor = {
    jobId: String(job.id), jobNumber: job.jobNumber, servee: job.servee,
    form: (pos && pos.formId) || "POS-010",
  };

  /* [audit-fixes-2026-10-15] H25: onPrepared (the POST /api/server/jobs/:id/pos/prepare
     success path) was removed with ProofFormPicker — servers never prepare court forms. */

  async function markFiled(method, notes) {
    setBusy("file");
    try {
      await api.post(`/api/server/pos/${encodeURIComponent(job.id)}/mark-filed`, { method, notes: notes || "" });
      ssToast("ok", "Proof marked filed with the court");
      setFileOpen(false);
      await load();
      try { reload && reload(); } catch (_) {}
    } catch (e) { ssToast("err", (e && e.message) || "Could not mark the proof filed"); }
    finally { setBusy(""); }
  }
  async function printSigned() {
    setBusy("print");
    try { await ssPrintSignedProofs([job.id]); }
    catch (e) { ssToast("err", (e && e.message) || "Could not build the PDF"); }
    finally { setBusy(""); }
  }

  const st = String((pos && pos.status) || "").toLowerCase();
  const previewUrl = (pos && (pos.finalUrl || pos.draftUrl)) || "";
  const previewIsFinal = !!(pos && pos.finalUrl);
  const canSign = st === "pending_signature"; // [pos-overhaul-2026-10-07:server-app] draft is fetched by the review sheet
  const canUploadPos = (job.status === "active" || job.status === "completed") && st !== "filed" && st !== "signed" && st !== "delivered";
  const canFile = st === "signed" || st === "delivered";
  const isFiled = st === "filed";

  const headerBtn = { ...iconBtnLight, width: 36, height: 36 };
  const primary = (label, Icon, onClick, disabled) => (
    <button onClick={onClick} disabled={disabled} style={{
      flex: 1, minHeight: 48, padding: "0 14px", background: disabled ? C.steelL : `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
      color: C.deep, border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: disabled ? "not-allowed" : "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit",
    }}><Icon size={18} /> {label}</button>
  );
  const secondary = (label, Icon, onClick, disabled, which) => (
    <button onClick={onClick} disabled={disabled} style={{
      flex: 1, minHeight: 48, padding: "0 12px", background: C.white, color: C.deep,
      border: `1.5px solid ${C.deep}`, borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit", opacity: disabled ? 0.6 : 1,
    }}>{busy === which ? <SpinnerDot color={C.deep} /> : <Icon size={16} />} {label}</button>
  );

  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: "14px 16px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexShrink: 0 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>
            {proofFor.form} · JOB #{job.jobNumber}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Proof of Service</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {pos ? <PosStatusPill status={pos.status} /> : null}
          <button onClick={onClose} style={headerBtn} aria-label="Close"><X size={20} color={C.deep} /></button>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "0 16px 16px" }}>
        {pos === null && (
          <SkeletonGroup label="Loading proof of service">
            <SkeletonRect width="100%" height={260} radius={12} />
          </SkeletonGroup>
        )}

        {pos && pos.err && !pos.draftUrl && !pos.finalUrl && (
          <div style={{ fontSize: 11, color: C.err, background: C.errL, padding: "8px 10px", borderRadius: 8, marginBottom: 10 }}>{pos.err}</div>
        )}

        {/* [pos-admin-prepare-2026-10-09] Owner decision: only the admin team prepares proofs of service. The form
            picker and "Fill out manually" were removed for the process server (backend refuses
            POST /api/server/jobs/:id/pos/prepare with POS_ADMIN_PREPARES). Signing is unchanged. */}
        {pos && mode === "pick" && <PosAdminPreparesNotice />}

        {/* [pos-overhaul-2026-10-07:server-app] Upload a completed POS (goes to the office's review queue). */}
        {/* [audit-fixes-2026-10-15] H25: `mode !== "manual"` guard dropped — the manual mode no longer exists. */}
        {pos && canUploadPos && (
          <button onClick={() => setUploadOpen(true)} style={{
            width: "100%", marginTop: 12, padding: 12, background: C.white, border: `1.5px solid ${C.steelXL}`, borderRadius: 12,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 13, fontWeight: 800, color: C.deep,
            cursor: "pointer", fontFamily: "inherit",
          }}><Upload size={16} /> Upload proof of service</button>
        )}

        {pos && mode === "state" && (
          <>
            {/* Preview — the actual PDF the server is about to swear to */}
            <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.steelXL}`, overflow: "hidden", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderBottom: `1px solid ${C.steelXXL}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>
                  {previewIsFinal ? "SIGNED PROOF" : "PREPARED — REVIEW BEFORE SIGNING"}
                  {pos.diligence ? " · +DILIGENCE" : ""}{pos.evidence ? " · +EVIDENCE" : ""}
                </div>
                {previewUrl ? (
                  <button onClick={() => ssOpenDocViewer({ title: `${proofFor.form} · #${job.jobNumber}`, url: previewUrl, externalUrl: /^https?:/i.test(previewUrl) ? previewUrl : "", kind: "pdf", filename: `${proofFor.form}_${job.jobNumber}.pdf` })}
                    style={{ display: "flex", alignItems: "center", gap: 4, background: C.infoL, color: C.info, padding: "4px 8px", borderRadius: 8, border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    <Eye size={12} /> Full screen
                  </button>
                ) : null}
              </div>
              {previewUrl ? (
                <iframe title="Proof of service preview" src={previewUrl}
                  style={{ width: "100%", height: "min(52vh, 520px)", border: "none", display: "block", background: C.white }} />
              ) : (
                <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: C.steel }}>No PDF to preview yet.</div>
              )}
            </div>

            {st === "rejected" && (job._raw && (job._raw.pos_admin_notes || job._raw.pos_rejection_reason)) ? (
              <div style={{ background: C.errL, borderRadius: 8, padding: "8px 10px", marginBottom: 10, display: "flex", gap: 6 }}>
                <AlertTriangle size={12} color={C.err} style={{ marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 11, color: C.err, fontWeight: 600 }}>{job._raw.pos_admin_notes || job._raw.pos_rejection_reason}</div>
              </div>
            ) : null}

            {/* Actions */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {canSign && primary("Sign", PenLine, () => setSignOpen(true), false)}
              {canFile && primary("Mark filed", CheckCircle2, () => setFileOpen(true), busy === "file")}
              {(canFile || isFiled) && secondary("Print", Printer, printSigned, !!busy, "print")}
              {isFiled && pos.finalUrl && secondary("View signed", Eye, () => ssOpenDocViewer({ title: `${proofFor.form} · #${job.jobNumber}`, url: pos.finalUrl, externalUrl: pos.finalUrl, kind: "pdf" }), false, "view")}
            </div>
            {st === "pending_admin_review" ? (
              <div style={{ fontSize: 12, color: C.steel, marginTop: 10, lineHeight: 1.5 }}>Dispatch is still reviewing this proof. You will be able to sign it here once it is released.</div>
            ) : null}
            {canSign ? (
              <div style={{ fontSize: 11, color: C.steel, marginTop: 10, lineHeight: 1.5 }}>Read the whole document first. Signing declares under penalty of perjury that it is true and correct.</div>
            ) : null}
          </>
        )}
      </div>

      {/* [pos-overhaul-2026-10-07:server-app] review-before-sign replaces the bare SignatureSheet */}
      {signOpen && (
        <PosSignReviewSheet jobId={String(job.id)}
          meta={{ jobNumber: job.jobNumber, recipient: job.servee, caseNumber: job.caseNo, formId: proofFor.form }}
          onClose={() => { setSignOpen(false); load(); try { reload && reload(); } catch (_) {} }} />
      )}
      {uploadOpen && <UploadPosSheet job={job} onClose={() => { setUploadOpen(false); load(); }} />}
      {fileOpen && <MarkFiledSheet busy={busy === "file"} onClose={() => setFileOpen(false)} onSubmit={markFiled} />}
      {/* [audit-fixes-2026-10-15] H25: the "manual" POSSheet mount (reachable only from the removed picker) is gone. */}
    </Sheet>
  );
}

/* [pos-admin-prepare-2026-10-09] Shown where the form picker used to be.
   [audit-fixes-2026-10-15] H25: ProofFormPicker itself (and its POST
   /api/server/jobs/:id/pos/prepare call) was deleted from this file. */
function PosAdminPreparesNotice() {
  return (
    <div style={{ background: C.white, border: `1.5px solid ${C.steelXL}`, borderRadius: 12, padding: 16, marginTop: 4 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: C.deep, marginBottom: 6 }}>Your office prepares this proof of service</div>
      <div style={{ fontSize: 12, color: C.steel, lineHeight: 1.5 }}>
        Once the office has prepared it, it will appear here ready for your signature and you will get a notification.
        Nothing to fill out on your side.
      </div>
    </div>
  );
}

function MarkFiledSheet({ busy, onClose, onSubmit }) {
  const [method, setMethod] = useState("e_filed");
  const [notes, setNotes] = useState("");
  const METHODS = [["e_filed", "E-filed"], ["walk_in", "Walk-in at clerk"], ["mailed", "Mailed"], ["other", "Other"]];
  return (
    <Sheet onClose={onClose} small>
      <div style={{ padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.deep }}>Mark filed with the court</div>
          <button onClick={onClose} style={iconBtnLight}><X size={20} color={C.deep} /></button>
        </div>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", marginBottom: 8 }}>HOW WAS IT FILED?</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {METHODS.map(([id, label]) => (
            <button key={id} onClick={() => setMethod(id)} style={{
              padding: "8px 12px", background: method === id ? C.deep : C.bg, color: method === id ? C.white : C.deep,
              border: "none", borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}>{label}</button>
          ))}
        </div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={2000} placeholder="Confirmation number, clerk, anything dispatch should know (optional)"
          style={{ width: "100%", padding: "9px 10px", border: `1px solid ${C.steelXL}`, borderRadius: 8, fontSize: 13, color: C.deep, outline: "none", fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", background: C.white }} />
        <button onClick={() => onSubmit(method, notes)} disabled={busy} style={{
          width: "100%", padding: 14, marginTop: 12, background: busy ? C.steelL : C.deep, color: C.white,
          border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: busy ? "wait" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>{busy ? <SpinnerDot /> : <CheckCircle2 size={18} />} {busy ? "Saving…" : "Mark filed"}</button>
      </div>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PROOFS SCREEN — POS forms, signed affidavits, submission tracking
// [server-parity-web-2026-10-05] Driven by GET /api/server/pos-queue (the
// server's POS records grouped by status) merged with the job list, so every
// assigned job appears — prepared or not — and tapping one opens the flow
// above, never a blank form. Signed proofs can be multi-selected and printed
// merged (POST /api/server/pos/print-merged).
// ═══════════════════════════════════════════════════════════════════════
function ProofsScreen() {
  const { jobs, reload } = React.useContext(AppCtx);
  const [filter, setFilter] = useState("all");
  const [openJob, setOpenJob] = useState(null);
  const [queue, setQueue] = useState(null); // null = loading
  const [qErr, setQErr] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [printBusy, setPrintBusy] = useState(false);
  const [signItem, setSignItem] = useState(null); // [pos-overhaul-2026-10-07:server-app]

  const loadQueue = React.useCallback(async () => {
    try {
      const r = await api.get("/api/server/pos-queue");
      setQueue(r || {}); setQErr("");
      __ssBadgeSet("posqueue", ssNormalizePosQueue(r).length); // [pos-overhaul-2026-10-07:server-app]
    } catch (e) { setQueue({}); setQErr((e && e.message) || "Could not load the proof queue"); }
  }, []);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  /* [pos-overhaul-2026-10-07:server-app] Signatures inbox — straight from the queue
     endpoint (not the paginated job list), so every proof waiting on this server shows. */
  const inbox = React.useMemo(() => ssNormalizePosQueue(queue || {}), [queue]);

  // Queue rows by job id
  const byId = React.useMemo(() => {
    const m = new Map();
    const q = queue || {};
    /* [pos-overhaul-2026-10-07:server-app] flat-row queue shape (contract) */
    if (Array.isArray(q) || Array.isArray(q.items)) {
      (Array.isArray(q) ? q : q.items).forEach((row) => {
        if (!row) return;
        const st = String(row.pos_status || row.posStatus || "pending_signature").toLowerCase();
        const _st = st === "pending_signature" ? "ready_to_sign" : (st === "signed" || st === "delivered") ? "ready_to_file" : st === "filed" ? "filed" : null;
        if (!_st) return;
        const id = String(row.jobId != null ? row.jobId : (row.job_id != null ? row.job_id : row.id));
        m.set(id, { ...row, id, pos_status: st, pos_form_id: row.pos_form_id || row.formId, job_number: row.job_number || row.jobNumber,
          recipient_name: row.recipient_name || row.recipient, case_number: row.case_number || row.caseNumber, _st });
      });
      return m;
    }
    [["awaiting_signature", "ready_to_sign"], ["signed_ready_to_file", "ready_to_file"], ["filed", "filed"]].forEach(([k, st]) => {
      (Array.isArray(q[k]) ? q[k] : []).forEach((row) => m.set(String(row.id), { ...row, _st: st }));
    });
    return m;
  }, [queue]);

  const proofs = React.useMemo(() => {
    const out = [];
    const seen = new Set();
    (jobs || []).forEach((j) => {
      if (!(j.status === "active" || j.status === "completed")) return;
      const raw = j._raw || {};
      const q = byId.get(String(j.id));
      const posSt = String((q && q.pos_status) || raw.pos_status || "").toLowerCase();
      let status = "not_prepared";
      if (q) status = q._st;
      else if (posSt === "pending_signature") status = "ready_to_sign";
      else if (posSt === "signed" || posSt === "delivered") status = "ready_to_file";
      else if (posSt === "filed") status = "filed";
      else if (posSt === "pending_admin_review") status = "with_dispatch";
      else if (posSt === "rejected") status = "rejected";
      seen.add(String(j.id));
      out.push({
        id: "P-" + String(j.id), job: j, jobId: String(j.id), jobNumber: j.jobNumber, servee: j.servee,
        form: (q && q.pos_form_id) || raw.pos_form_id || "—", status, posStatus: posSt,
        caseNo: j.caseNo, date: j.deadline,
        court: (q && q.court_name) || j.courtName || "",
        reason: raw.pos_admin_notes || raw.pos_rejection_reason || null,
        signedAt: q && q.pos_signed_at, filedAt: q && q.pos_filed_at, filedMethod: q && q.pos_filed_method,
      });
    });
    // Queue rows for jobs that are not in the (possibly paged) job list.
    byId.forEach((q, id) => {
      if (seen.has(id)) return;
      out.push({
        id: "P-" + id, job: null, jobId: id, jobNumber: q.job_number || id, servee: q.recipient_name || q.defendant || "Unnamed",
        form: q.pos_form_id || "—", status: q._st, posStatus: String(q.pos_status || ""), caseNo: q.case_number || "",
        date: q.serve_by_date || q.target_date || "", court: q.court_name || "", reason: null,
        signedAt: q.pos_signed_at, filedAt: q.pos_filed_at, filedMethod: q.pos_filed_method,
      });
    });
    const rank = { ready_to_sign: 0, rejected: 1, ready_to_file: 2, not_prepared: 3, with_dispatch: 4, filed: 5 };
    return out.sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9) || String(b.jobId).localeCompare(String(a.jobId), undefined, { numeric: true }));
  }, [jobs, byId]);

  const counts = {
    all: proofs.length,
    ready_to_sign: proofs.filter((p) => p.status === "ready_to_sign").length,
    ready_to_file: proofs.filter((p) => p.status === "ready_to_file").length,
    not_prepared: proofs.filter((p) => p.status === "not_prepared").length,
    filed: proofs.filter((p) => p.status === "filed").length,
    rejected: proofs.filter((p) => p.status === "rejected").length,
  };
  const filtered = proofs.filter((p) => filter === "all" ? true : p.status === filter);
  const printable = proofs.filter((p) => p.status === "ready_to_file" || p.status === "filed");
  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  async function printSelected() {
    const ids = Array.from(selected);
    if (!ids.length || printBusy) return;
    setPrintBusy(true);
    try { await ssPrintSignedProofs(ids); }
    catch (e) { ssToast("err", (e && e.message) || "Could not build the merged PDF"); }
    finally { setPrintBusy(false); }
  }

  function openProof(p) {
    /* [pos-overhaul-2026-10-07:server-app] "Review & sign" goes straight to review-before-sign. */
    if (p.status === "ready_to_sign") {
      const q = inbox.find((x) => x.jobId === String(p.jobId));
      setSignItem(q || { jobId: String(p.jobId), jobNumber: p.jobNumber, recipient: p.servee, caseNumber: p.caseNo, formId: p.form !== "—" ? p.form : "" });
      return;
    }
    const j = p.job || {
      id: p.jobId, jobNumber: p.jobNumber, servee: p.servee, caseNo: p.caseNo, status: "completed",
      posStatus: p.posStatus, posFormId: p.form !== "—" ? p.form : null, rawAttempts: [], _raw: {},
    };
    setOpenJob(j);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "12px 16px 0", background: C.bg }}>
        <div style={{
          background: `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
          borderRadius: 16, padding: 14, color: C.white, marginBottom: 12,
          boxShadow: `0 8px 20px ${C.royal}33`,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.goldL, letterSpacing: "0.1em" }}>NEEDS ACTION</div>
              <div style={{ fontSize: 24, fontWeight: 800, marginTop: 2 }}>{counts.ready_to_sign + counts.ready_to_file + counts.rejected + counts.not_prepared}</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
                {counts.ready_to_sign} to sign · {counts.ready_to_file} to file · {counts.not_prepared} to prepare
              </div>
            </div>
            <FileSignature size={36} color={C.goldL} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 10 }}>
          <FilterChip label="All" count={counts.all} active={filter === "all"} onPress={() => setFilter("all")} />
          <FilterChip label="To sign" count={counts.ready_to_sign} active={filter === "ready_to_sign"} onPress={() => setFilter("ready_to_sign")} />
          <FilterChip label="To file" count={counts.ready_to_file} active={filter === "ready_to_file"} onPress={() => setFilter("ready_to_file")} />
          <FilterChip label="Not prepared" count={counts.not_prepared} active={filter === "not_prepared"} onPress={() => setFilter("not_prepared")} />
          <FilterChip label="Filed" count={counts.filed} active={filter === "filed"} onPress={() => setFilter("filed")} />
          {counts.rejected ? <FilterChip label="Returned" count={counts.rejected} active={filter === "rejected"} onPress={() => setFilter("rejected")} /> : null}
        </div>
        {printable.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 10 }}>
            <div style={{ fontSize: 11, color: C.steel, flex: 1 }}>
              {selected.size ? `${selected.size} signed proof${selected.size === 1 ? "" : "s"} selected` : "Tick signed proofs to print them as one PDF"}
            </div>
            {selected.size ? (
              <button onClick={() => setSelected(new Set())} style={{ background: "none", border: "none", fontSize: 11, fontWeight: 700, color: C.steel, cursor: "pointer", fontFamily: "inherit" }}>Clear</button>
            ) : (
              <button onClick={() => setSelected(new Set(printable.map((p) => p.jobId)))} style={{ background: "none", border: "none", fontSize: 11, fontWeight: 700, color: C.royal, cursor: "pointer", fontFamily: "inherit" }}>Select all</button>
            )}
            <button onClick={printSelected} disabled={!selected.size || printBusy} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 10,
              background: selected.size && !printBusy ? C.deep : C.steelL, color: C.white, border: "none",
              fontSize: 12, fontWeight: 800, cursor: selected.size && !printBusy ? "pointer" : "not-allowed", fontFamily: "inherit",
            }}>{printBusy ? <SpinnerDot /> : <Printer size={14} />} Print signed proofs</button>
          </div>
        )}
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "4px 16px 100px" }}>
        {qErr && <div style={{ fontSize: 11, color: C.err, background: C.errL, padding: "8px 10px", borderRadius: 8, marginBottom: 10 }}>{qErr}</div>}
        {/* [pos-overhaul-2026-10-07:server-app] SIGNATURES INBOX */}
        {inbox.length > 0 && (
          <div style={{ background: C.goldXL, border: `1.5px solid ${C.gold}`, borderRadius: 14, padding: 12, marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <PenLine size={16} color={C.deep} />
              <div style={{ fontSize: 13, fontWeight: 800, color: C.deep, flex: 1 }}>Waiting for your signature</div>
              <span style={{ minWidth: 22, height: 22, padding: "0 7px", borderRadius: 11, background: C.err, color: C.white, fontSize: 12, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{inbox.length}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {inbox.map((it) => (
                <button key={it.jobId} onClick={() => setSignItem(it)} style={{
                  width: "100%", textAlign: "left", background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 12, padding: 12,
                  display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontFamily: "inherit",
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.deep, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.recipient}</div>
                    <div style={{ fontSize: 11, color: C.steel, marginTop: 2 }}>
                      {it.caseNumber ? `Case ${it.caseNumber}` : `Job #${it.jobNumber}`} · {it.formId}{it.source === "uploaded" ? " · uploaded" : ""}
                    </div>
                    {it.requestedAt ? <div style={{ fontSize: 11, color: C.steel, marginTop: 1 }}>Requested {ssFmtRequested(it.requestedAt)}</div> : null}
                  </div>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "8px 10px", borderRadius: 10, background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`, color: C.deep, fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                    <PenLine size={13} /> Sign
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        {queue === null && (
          <SkeletonGroup label="Loading proofs">
            {[0, 1, 2].map((i) => <SkeletonCard key={i} height={120} />)}
          </SkeletonGroup>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map((p) => (
            <ProofCard key={p.id} proof={p} onAction={() => openProof(p)}
              selectable={p.status === "ready_to_file" || p.status === "filed"}
              selected={selected.has(p.jobId)} onToggle={() => toggleSel(p.jobId)} />
          ))}
        </div>
        {queue !== null && filtered.length === 0 && (
          <div style={{
            background: C.white, borderRadius: 14, padding: 32, textAlign: "center",
            border: `1.5px dashed ${C.steelL}`, marginTop: 12,
          }}>
            <CheckCircle2 size={32} color={C.ok} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 13, color: C.deep, fontWeight: 700 }}>All caught up</div>
            <div style={{ fontSize: 11, color: C.steel, marginTop: 4 }}>No proofs in this category</div>
          </div>
        )}
      </div>
      {openJob && <ProofFlowSheet job={openJob} onClose={() => { setOpenJob(null); loadQueue(); try { reload && reload(); } catch (_) {} }} />}
      {signItem && <PosSignReviewSheet jobId={signItem.jobId} meta={signItem} onClose={() => { setSignItem(null); loadQueue(); try { reload && reload(); } catch (_) {} }} />}
    </div>
  );
}


function ProofCard({ proof, onAction, selectable, selected, onToggle }) {
  /* [server-parity-web-2026-10-05] statuses now mirror the POS queue; every CTA
     opens the proof flow (preview -> sign / file / print), never a blank form. */
  const config = {
    ready_to_sign: { label: "Ready to sign", color: C.warn, bg: C.warnL, icon: PenLine, cta: "Review & sign" },
    ready_to_file: { label: "Signed · to file", color: C.ok, bg: C.okL, icon: CheckCircle2, cta: "Mark filed" },
    filed: { label: "Filed", color: C.royal, bg: C.infoL, icon: CheckCircle2, cta: "View" },
    not_prepared: { label: "Not prepared", color: C.steel, bg: C.steelXXL, icon: Edit3, cta: "Choose a form" },
    with_dispatch: { label: "With dispatch", color: C.steel, bg: C.steelXXL, icon: Clock, cta: "View draft" },
    rejected: { label: "Returned", color: C.err, bg: C.errL, icon: XCircle, cta: "Fix & resubmit" },
    draft: { label: "Draft", color: C.steel, bg: C.steelXXL, icon: Edit3, cta: "Continue" },
    submitted: { label: "Submitted", color: C.ok, bg: C.okL, icon: CheckCircle2, cta: "View" },
  };
  const s = config[proof.status] || config.not_prepared;
  const Icon = s.icon;

  return (
    <div style={{
      background: C.white, borderRadius: 14, padding: 14,
      border: `1px solid ${selected ? C.royal : C.steelXL}`,
      boxShadow: "0 1px 3px rgba(15,40,96,0.04)",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
        {selectable ? (
          <button onClick={onToggle} aria-label={selected ? "Deselect proof" : "Select proof for printing"} style={{
            width: 24, height: 24, borderRadius: 7, border: `2px solid ${selected ? C.royal : C.steelL}`, background: selected ? C.royal : C.white,
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0, flexShrink: 0, marginTop: 2,
          }}>{selected ? <Check size={14} color={C.white} strokeWidth={3} /> : null}</button>
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.deep, letterSpacing: "-0.01em" }}>{proof.servee}</div>
          <div style={{ fontSize: 11, color: C.steel, marginTop: 2 }}>Job #{proof.jobNumber || proof.jobId}{proof.caseNo ? ` · Case ${proof.caseNo}` : ""}</div>
        </div>
        <Pill bg={s.bg} color={s.color} icon={Icon}>{s.label}</Pill>
      </div>

      <div style={{
        display: "flex", gap: 8, padding: "10px 0",
        borderTop: `1px solid ${C.steelXXL}`, borderBottom: `1px solid ${C.steelXXL}`, margin: "6px 0 10px",
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: C.steel, letterSpacing: "0.08em" }}>FORM</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.deep, marginTop: 2 }}>{proof.form}</div>
        </div>
        <div style={{ width: 1, background: C.steelXXL }} />
        <div style={{ flex: 1.4, minWidth: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: C.steel, letterSpacing: "0.08em" }}>COURT</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.deep, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{proof.court || "—"}</div>
        </div>
        <div style={{ width: 1, background: C.steelXXL }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: C.steel, letterSpacing: "0.08em" }}>{proof.status === "filed" ? "FILED" : proof.status === "ready_to_file" ? "SIGNED" : "DUE"}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.deep, marginTop: 2 }}>
            {(() => {
              const v = proof.status === "filed" ? proof.filedAt : proof.status === "ready_to_file" ? proof.signedAt : proof.date;
              if (!v) return "—";
              const d = new Date(v); return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
            })()}
          </div>
        </div>
      </div>

      {proof.reason && (
        <div style={{
          background: C.errL, borderRadius: 8, padding: "8px 10px", marginBottom: 10,
          display: "flex", alignItems: "flex-start", gap: 6,
        }}>
          <AlertTriangle size={12} color={C.err} style={{ marginTop: 1, flexShrink: 0 }} />
          <div style={{ fontSize: 11, color: C.err, fontWeight: 600 }}>{proof.reason}</div>
        </div>
      )}

      <div style={{ display: "flex", gap: 6 }}>
        <button style={{
          flex: 1, padding: "10px 12px",
          background: proof.status === "ready_to_sign" ? `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)` : C.deep,
          color: proof.status === "ready_to_sign" ? C.deep : C.white,
          border: "none", borderRadius: 10, fontSize: 12, fontWeight: 800, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
          boxShadow: proof.status === "ready_to_sign" ? `0 3px 8px ${C.gold}55` : "none",
        }} onClick={onAction}>
          <Icon size={14} /> {s.cta}
        </button>
        {/* [server-parity-web-2026-10-05] Print a single signed proof (merged endpoint, one id). */}
        {(proof.status === "ready_to_file" || proof.status === "filed") && (
          <button onClick={() => ssPrintSignedProofs([proof.jobId]).catch((e) => ssToast("err", (e && e.message) || "Could not build the PDF"))} style={{
            width: 38, padding: "10px 0", background: C.white, border: `1px solid ${C.steelXL}`,
            borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }} aria-label="Print signed proof">
            <Printer size={14} color={C.deep} />
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// EARNINGS SCREEN
// ═══════════════════════════════════════════════════════════════════════
// ─────────────────────────────────────────────────────────────────────────
// EarningsScreen — wired to /api/earnings/summary with localStorage hydration.
// Expected backend shape (defensive — any field may be missing):
//   {
//     pending_payout: number,
//     today: number, this_week: number, this_month: number,
//     today_trend?: number, this_week_trend?: number, this_month_trend?: number,
//     stripe_status: "none" | "pending" | "connected" | "restricted",
//     stripe_dashboard_url?: string,
//     recent_payouts: [{ id, amount, date, status, stripe_payout_id }]
//   }
// On 404 / network error we render "Earnings data unavailable" + last cache.
// ─────────────────────────────────────────────────────────────────────────
const SS_EARNINGS_CACHE_KEY = "ss_earnings_cache";

function ssFmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}
function ssFmtMoneyShort(n) {
  // No-cents version for the compact period cards.
  const v = Math.round(Number(n || 0));
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function ssStripePill(status) {
  switch (String(status || "none").toLowerCase()) {
    case "connected": return { bg: C.okL, color: C.ok, label: "Connected" };
    case "pending":   return { bg: C.warnL, color: C.warn, label: "Pending" };
    case "restricted":return { bg: C.errL, color: C.err, label: "Restricted" };
    case "none":
    default:          return { bg: C.steelXXL, color: C.steel, label: "Not connected" };
  }
}

function EarningsScreen() {
  // [tier-collapse-2026-09-20] `user` pulled from AppCtx so the instant-payout fee
  // disclosure can be tier-aware (Pro pays no fee).
  const { isTablet, user } = React.useContext(AppCtx);

  // Hydrate instantly from localStorage so the first paint isn't $0 / "no data".
  const [summary, setSummary] = useState(() => {
    try {
      const cached = localStorage.getItem(SS_EARNINGS_CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch {}
    return null;
  });
  const [unavailable, setUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null); // {kind:"ok"|"err", text}
  const [taxOpen, setTaxOpen] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/api/earnings/summary");
      if (r && typeof r === "object") {
        setSummary(r);
        setUnavailable(false);
        try { localStorage.setItem(SS_EARNINGS_CACHE_KEY, JSON.stringify(r)); } catch {}
      }
    } catch (e) {
      // 404 / network — keep cached values, but surface the unavailable flag.
      setUnavailable(true);
      console.warn("[earnings] summary failed:", e && e.message);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Auto-dismiss toast.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  const data = summary || {};
  const pending = Number(data.pending_payout || 0);
  const stripeStatus = String(data.stripe_status || "none").toLowerCase();
  const stripePill = ssStripePill(stripeStatus);
  const canCashOut = pending > 0 && stripeStatus === "connected";
  const payouts = Array.isArray(data.recent_payouts) ? data.recent_payouts : [];

  async function cashOut() {
    if (!canCashOut || busy) return;
    // [instant-fee-disclosure-2026-06-23] Point-of-action disclosure of the 1.5%
    // instant-payout fee (consented in the onboarding agreement; restated here at
    // tap). Ported from the web server app during [tenant-brand-2026-07-21].
    // [tier-collapse-2026-09-20] Now tier-aware: a Pro subscription covers the
    // instant-payout fee, so subscribers are told they receive the full amount.
    // Everyone else still gets the full 1.5% deduction spelled out in dollars before
    // they authorize it -- Stripe requires the fee be disclosed clearly and
    // conspicuously at the point of action, so this disclosure is not weakened --
    // plus a line naming Pro as the way to remove it.
    const amt = Number(pending || 0);
    const feeCovered = SS_PRO_ENABLED && ssIsPremium(user); /* [pro-disabled-2026-10-06] */
    let confirmMsg;
    if (feeCovered) {
      confirmMsg =
        `Instant payout of $${amt.toFixed(2)}\n\n` +
        `Your Pro plan covers the instant-payout fee, so you'll receive the full $${amt.toFixed(2)} within minutes.\n\n` +
        `Standard payouts arrive on the normal schedule at no charge. Continue with the instant payout?`;
    } else {
      const fee = Math.round(amt * 0.015 * 100) / 100;
      const net = Math.round((amt - fee) * 100) / 100;
      confirmMsg =
        `Instant payout of $${amt.toFixed(2)}\n\n` +
        `A 1.5% instant-payout fee ($${fee.toFixed(2)}) is deducted, so you'll receive about $${net.toFixed(2)} within minutes.\n\n` +
        (SS_PRO_ENABLED ? `Pro subscribers pay no instant-payout fee.\n\n` : ``) + /* [pro-disabled-2026-10-06] */
        `Standard payouts arrive on the normal schedule at no charge. Continue with the instant payout?`;
    }
    const ok = window.confirm(confirmMsg);
    if (!ok) return;
    setBusy(true);
    try {
      await api.post("/api/earnings/cash-out", {});
      setToast({ kind: "ok", text: "Cash-out initiated. Funds typically arrive within minutes." });
      await reload();
    } catch (e) {
      setToast({ kind: "err", text: e && e.message ? e.message : "Cash-out failed. Try again." });
    } finally {
      setBusy(false);
    }
  }

  async function openStripeDashboard() {
    if (stripeStatus === "none") {
      // Fetch the Stripe Connect onboarding link with the authenticated api
      // helper FIRST, then open the returned URL. Opening the Bearer-protected
      // /api/stripe/connect-link directly in SFSafariViewController sent no auth
      // header -> 401. Backend now returns { url } JSON instead of a redirect.
      try {
        const r = await api.get("/api/stripe/connect-link");
        if (r && r.url) openExternal(r.url);
        else setToast({ kind: "err", text: "Could not start Stripe setup. Try again." });
      } catch (e) {
        setToast({ kind: "err", text: "Could not start Stripe setup." });
      }
      return;
    }
    const url = data.stripe_dashboard_url;
    if (url) openExternal(String(url));
    else setToast({ kind: "err", text: "Stripe dashboard URL not available yet." });
  }

  // Cold-load skeleton: no localStorage cache AND the live fetch hasn't
  // landed yet. SkeletonLine x5 stand in for the 5 metric rows
  // (pending payout, today, week, month, recent payouts) so the layout
  // doesn't pop when summary hydrates.
  if (summary === null && !unavailable) {
    return (
      <div style={{ padding: "16px 16px 100px" }}>
        <SkeletonGroup label="Loading earnings">
          <div style={{
            background: C.white, borderRadius: 18, padding: 16, marginBottom: 14,
            border: `1px solid ${C.steelXL}`,
          }}>
            <SkeletonLine width="35%" height={10} />
            <SkeletonLine width="55%" height={32} />
            <SkeletonLine width="50%" height={11} style={{ marginBottom: 0 }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 18 }}>
            {[0,1,2].map(i => (
              <div key={i} style={{
                background: C.white, border: `1px solid ${C.steelXL}`,
                borderRadius: 12, padding: 12,
              }}>
                <SkeletonLine width="60%" height={10} />
                <SkeletonLine width="80%" height={16} style={{ marginBottom: 0 }} />
              </div>
            ))}
          </div>
          <SkeletonLine width="100%" height={48} />
          <SkeletonLine width="100%" height={48} style={{ marginBottom: 0 }} />
        </SkeletonGroup>
      </div>
    );
  }
  return (
    <div style={{ padding: "16px 16px 100px" }}>
      {/* Pending payout */}
      <div style={{
        background: `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
        borderRadius: 18, padding: 16, color: C.white, marginBottom: 14,
        boxShadow: `0 10px 24px ${C.deep}44`,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: C.goldL }}>PENDING PAYOUT</div>
        <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: "-0.02em", marginTop: 2 }}>
          {ssFmtMoney(pending)}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 12, opacity: 0.95 }}>
          <span style={{ opacity: 0.8 }}>Stripe status:</span>
          <span style={{
            padding: "2px 8px", borderRadius: 10,
            background: stripePill.bg, color: stripePill.color,
            fontSize: 11, fontWeight: 800, letterSpacing: "0.02em",
          }}>{stripePill.label}</span>
        </div>
        {unavailable && (
          <div style={{ marginTop: 8, fontSize: 11, opacity: 0.85, fontStyle: "italic" }}>
            Earnings data unavailable — showing last cached values.
          </div>
        )}
        {canCashOut && (
          <>
            <button onClick={cashOut} disabled={busy} style={{
              marginTop: 14, padding: "8px 16px", borderRadius: 20,
              background: busy ? C.steel : `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
              color: C.deep, border: "none", fontSize: 13, fontWeight: 800,
              cursor: busy ? "wait" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 5,
              boxShadow: busy ? "none" : `0 4px 10px ${C.gold}55`,
            }}>
              <Zap size={14} /> {busy ? "Processing…" : `Cash out ${ssFmtMoneyShort(pending)}`}
            </button>
            {/* [tier-collapse-2026-09-20] Persistent fee disclosure. The confirm()
                in cashOut() only appears after the tap; Stripe wants the instant-payout
                fee disclosed clearly and conspicuously, so it is also stated here, on
                the button itself, where it is readable before anyone commits. */}
            <div style={{ marginTop: 8, fontSize: 11, opacity: 0.85, lineHeight: 1.4, maxWidth: 380 }}>
              {(SS_PRO_ENABLED && ssIsPremium(user)) /* [pro-disabled-2026-10-06] */
                ? "Instant payout fee is covered by your Pro plan — you receive the full amount."
                : (SS_PRO_ENABLED
                  ? "Instant payouts carry a 1.5% fee; standard payouts are free. Pro subscribers pay no instant-payout fee."
                  : "Instant payouts carry a 1.5% fee; standard payouts are free.")}
            </div>
          </>
        )}
      </div>

      {/* Period stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 18 }}>
        <PeriodStat label="Today" value={ssFmtMoneyShort(data.today)} trend={data.today_trend} />
        <PeriodStat label="Week"  value={ssFmtMoneyShort(data.this_week)} trend={data.this_week_trend} />
        <PeriodStat label="Month" value={ssFmtMoneyShort(data.this_month)} trend={data.this_month_trend} />
      </div>

      {/* Quick actions */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18 }}>
        <QuickAction
          icon={ExternalLink}
          label={stripeStatus === "none" ? "Connect Stripe" : "Stripe Dashboard"}
          sub={stripeStatus === "none" ? "Set up payouts" : "Edit payout method"}
          onPress={openStripeDashboard}
        />
        <QuickAction
          icon={Receipt}
          label="Tax Center"
          sub="W-9 · 1099-NEC · Mileage"
          onPress={() => setTaxOpen(true)}
        />
        <QuickAction
          icon={Route}
          label="Mileage Log"
          sub="Tax record-keeping"
          onPress={() => {
            // Bridge to AppInner via the same CustomEvent the notification
            // sheet uses to deep-link into drawer screens.
            try { window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: "mileage" })); } catch (_) {}
          }}
        />
      </div>

      {/* Recent payouts — chevron dropped until a full earnings-detail screen exists; the inline list is already complete. */}
      <SectionHeader title="Recent payouts" />
      <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.steelXL}` }}>
        {payouts.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: C.steel, fontSize: 13 }}>
            No payouts yet
          </div>
        )}
        {payouts.slice(0, 8).map((p, i) => {
          const amt = Number(p.amount || 0);
          const date = p.date || p.created_at;
          const stat = String(p.status || "pending").toLowerCase();
          const statusLabel = stat === "paid" ? "Paid" : stat === "failed" ? "Failed" : (p.status || "Pending");
          const statusBg = stat === "paid" ? C.okL : stat === "failed" ? C.errL : C.warnL;
          const statusColor = stat === "paid" ? C.ok : stat === "failed" ? C.err : C.warn;
          return (
            <PayoutRow
              key={p.id || p.stripe_payout_id || i}
              date={date ? new Date(date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
              amount={amt}
              method={p.stripe_payout_id ? `Stripe · ${String(p.stripe_payout_id).slice(-6)}` : "Stripe"}
              status={statusLabel}
              statusBg={statusBg}
              statusColor={statusColor}
              last={i === Math.min(payouts.length, 8) - 1}
            />
          );
        })}
      </div>

      {/* Tax center inline sheet */}
      {taxOpen && <TaxDocumentsSheet onClose={() => setTaxOpen(false)} />}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", left: "50%", transform: "translateX(-50%)",
          bottom: isTablet ? 120 : 110, zIndex: 250,
          padding: "10px 16px", maxWidth: 360,
          borderRadius: 12, fontSize: 13, fontWeight: 700,
          background: toast.kind === "ok" ? C.ok : C.err,
          color: C.white, boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
        }}>{toast.text}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TaxDocumentsSheet — full-screen list of W-9 / 1099-NEC, fetched lazily.
// Backend: GET /api/earnings/tax-documents
// Expected: [{ id, type ("W-9" | "1099-NEC"), year, status, download_url }]
// ─────────────────────────────────────────────────────────────────────────
function TaxDocumentsSheet({ onClose }) {
  const [docs, setDocs] = useState(null); // null=loading, []=none, [...]=docs
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/api/earnings/tax-documents");
        if (!alive) return;
        setDocs(Array.isArray(r) ? r : []);
      } catch (e) {
        if (!alive) return;
        setDocs([]);
        setLoadErr(true);
        console.warn("[tax-docs] load failed:", e && e.message);
      }
    })();
    return () => { alive = false; };
  }, []);

  function openMileage() {
    window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: "documents" }));
    onClose();
  }

  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: "16px 16px 12px", borderBottom: `1px solid ${C.steelXL}`, background: C.white }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>TAX CENTER</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, letterSpacing: "-0.01em", marginTop: 2 }}>
              Tax Documents
            </div>
          </div>
          <button onClick={onClose} style={iconBtnLight}><X size={20} color={C.deep} /></button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "12px 14px 24px" }}>
        {docs === null && (
          <div style={{ padding: 32, textAlign: "center", color: C.steel, fontSize: 12 }}>Loading documents…</div>
        )}
        {docs !== null && docs.length === 0 && (
          <div style={{
            padding: "40px 24px", textAlign: "center", color: C.steel,
            background: C.white, border: `1px dashed ${C.steelL}`, borderRadius: 14,
          }}>
            <FileText size={28} color={C.steel} />
            <div style={{ fontSize: 14, fontWeight: 800, color: C.deep, marginTop: 8 }}>
              No tax documents yet
            </div>
            <div style={{ fontSize: 12, marginTop: 4, maxWidth: 280, marginLeft: "auto", marginRight: "auto" }}>
              1099-NEC forms are generated each January for the prior tax year. W-9 appears once your Stripe Connect onboarding is complete.
            </div>
            {loadErr && (
              <div style={{ fontSize: 11, marginTop: 10, opacity: 0.7 }}>Tax service unavailable.</div>
            )}
          </div>
        )}
        {docs && docs.length > 0 && docs.map((d, i) => (
          <TaxDocRow key={d.id || `${d.type}-${d.year}-${i}`} doc={d} last={i === docs.length - 1} />
        ))}

        {/* Footer note + mileage shortcut. */}
        <div style={{
          marginTop: 16, padding: 12, background: C.white,
          border: `1px solid ${C.steelXL}`, borderRadius: 12, fontSize: 12, color: C.steel, lineHeight: 1.5,
        }}>
          Mileage logs (from your tracked routes) are accessible in the Mileage section of the Account tab.
          <button onClick={openMileage} style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            marginTop: 8, padding: 0, background: "none", border: "none",
            color: C.deep, fontSize: 12, fontWeight: 800, cursor: "pointer",
          }}>
            Open Documents <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </Sheet>
  );
}

function TaxDocRow({ doc, last }) {
  const status = String(doc.status || "ready").toLowerCase();
  const pill =
    status === "ready" || status === "available" ? { bg: C.okL, color: C.ok, label: "Ready" }
    : status === "pending" ? { bg: C.warnL, color: C.warn, label: "Pending" }
    : status === "not_required" ? { bg: C.steelXXL, color: C.steel, label: "Not required" }
    : { bg: C.steelXXL, color: C.steel, label: doc.status || "—" };
  const canDownload = (status === "ready" || status === "available") && !!doc.download_url;
  function download() {
    if (canDownload) window.location.href = String(doc.download_url);
  }
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "14px 14px", background: C.white,
      border: `1px solid ${C.steelXL}`, borderTopLeftRadius: 12, borderTopRightRadius: 12,
      borderBottomLeftRadius: last ? 12 : 0, borderBottomRightRadius: last ? 12 : 0,
      marginBottom: last ? 0 : -1,
    }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10, background: C.goldXL,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <FileText size={18} color={C.deep} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.deep }}>{doc.type || "Tax document"}</div>
        <div style={{ fontSize: 11, color: C.steel, marginTop: 2 }}>
          {doc.year ? `Tax year ${doc.year}` : ""}
        </div>
      </div>
      <span style={{
        padding: "3px 10px", borderRadius: 10,
        background: pill.bg, color: pill.color, fontSize: 10, fontWeight: 800, letterSpacing: "0.04em",
      }}>{pill.label}</span>
      <button
        onClick={download}
        disabled={!canDownload}
        style={{
          width: 34, height: 34, borderRadius: 10,
          background: canDownload ? C.bg : C.steelXXL,
          border: `1px solid ${C.steelXL}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: canDownload ? "pointer" : "not-allowed", padding: 0,
          opacity: canDownload ? 1 : 0.5,
        }}
        aria-label={`Download ${doc.type || "document"}`}>
        <Download size={16} color={C.deep} />
      </button>
    </div>
  );
}


function PeriodStat({ label, value, trend }) {
  // trend > 0 means up, < 0 means down. Null/undefined → no trend arrow shown.
  const hasTrend = trend != null && !isNaN(Number(trend)) && Number(trend) !== 0;
  const up = Number(trend) >= 0;
  return (
    <div style={{
      background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 12, padding: 10,
      display: "flex", flexDirection: "row", alignItems: "center", gap: 8,
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 8, background: hasTrend ? (up ? C.okL : C.errL) : C.steelXXL,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        {hasTrend
          ? (up ? <TrendingUp size={16} color={C.ok} /> : <TrendingDown size={16} color={C.err} />)
          : <DollarSign size={14} color={C.steel} />}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: C.steel, letterSpacing: "0.08em" }}>{String(label).toUpperCase()}</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.deep, letterSpacing: "-0.01em" }}>{value}</div>
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, sub, onPress }) {
  return (
    <button onClick={onPress} style={{
      background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 12, padding: 14,
      display: "flex", flexDirection: "row", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left",
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10, background: C.goldXL,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <Icon size={18} color={C.deep} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>{label}</div>
        <div style={{ fontSize: 11, color: C.steel }}>{sub}</div>
      </div>
      <ChevronRight size={16} color={C.steel} />
    </button>
  );
}

function PayoutRow({ date, amount, method, status, statusBg, statusColor, last }) {
  const bg = statusBg || C.okL;
  const fg = statusColor || C.ok;
  return (
    <div style={{
      padding: "12px 14px", display: "flex", alignItems: "center", gap: 10,
      borderBottom: last ? "none" : `1px solid ${C.steelXXL}`,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 10, background: bg,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <ArrowDownRight size={16} color={fg} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>{ssFmtMoney(amount)}</div>
        <div style={{ fontSize: 10, color: C.steel }}>{date} · {method}</div>
      </div>
      <Pill bg={bg} color={fg}>{status}</Pill>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ACCOUNT SCREEN
// ═══════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════
// [server-parity-web-2026-10-05] LIVE BADGES — training, messages, compliance
// Each is a tiny shared cache (60s) so Drawer, TabletSidebar and AccountScreen
// mounting in the same minute do not each refetch. Values that were literals
// ("3", "Registration valid · Bond active") now come from the same endpoints
// the admin web reads.
// ═══════════════════════════════════════════════════════════════════════
const __ssBadgeCache = { training: null, messages: null, compliance: null };
const __ssBadgeListeners = new Set();
function __ssBadgeSet(key, value) {
  __ssBadgeCache[key] = { value, at: Date.now() };
  __ssBadgeListeners.forEach((fn) => { try { fn(); } catch (_) {} });
}
function __ssBadgeFresh(key) {
  const c = __ssBadgeCache[key];
  return c && (Date.now() - c.at) < 60000 ? c : null;
}
function ssInvalidateBadge(key) { if (__ssBadgeCache[key]) __ssBadgeCache[key].at = 0; }
function useBadge(key, fetcher, fallback) {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    __ssBadgeListeners.add(fn);
    if (!__ssBadgeFresh(key)) {
      Promise.resolve().then(fetcher).then((v) => __ssBadgeSet(key, v)).catch(() => { /* keep last */ });
    }
    return () => __ssBadgeListeners.delete(fn);
  }, [key, fetcher]);
  const c = __ssBadgeCache[key];
  return c ? c.value : fallback;
}

/* Required training videos still to watch. GET /api/training/progress carries
   required_videos_total / _completed (routes/training.js buildProgressPayload);
   the local completion map + cached catalog fill in when the API is unreachable. */
async function __ssFetchTrainingBadge() {
  try {
    const r = await api.get("/api/training/progress");
    const total = Number(r && r.required_videos_total);
    const done = Number(r && r.required_videos_completed);
    if (Number.isFinite(total) && Number.isFinite(done)) return Math.max(0, total - done);
  } catch (_) { /* fall through */ }
  try {
    const cat = loadTrainingCatalogCache();
    const map = loadTrainingCompletion();
    const req = (Array.isArray(cat) ? cat : []).filter((v) => v && (v.required || String(v.category || "").toLowerCase() === "required"));
    if (!req.length) return 0;
    return req.filter((v) => !map[v.id]).length;
  } catch (_) { return 0; }
}
function useTrainingBadge() { return useBadge("training", __ssFetchTrainingBadge, 0); }

/* [messaging-v2-2026-10-08] Shared helpers for the server's single Dispatch thread.
   Contract: a non-staff user has exactly one conversation, `admin-<lowercased own
   email>`, computed by the backend. The client only ever sends that id back. */
function ssMyEmail() {
  try { return String((JSON.parse(localStorage.getItem(SS_USER_KEY) || "{}") || {}).email || "").trim().toLowerCase(); } catch (_) { return ""; }
}
function ssDispatchConvId(email) {
  const e = String(email || "").trim().toLowerCase();
  return e ? "admin-" + e : "";
}
/* Normalize a v2 message ({ id, convId, from, ... }) or a legacy row
   ({ id, conv_id, sender_id, receiver_id, read, ... }) into one shape. */
function ssNormMsg(m) {
  if (!m || typeof m !== "object") return null;
  const readBy = Array.isArray(m.readBy) ? m.readBy.map((x) => String(x || "").toLowerCase()) : null;
  return {
    id: m.id,
    convId: m.convId || m.conv_id || "",
    from: String(m.from || m.sender_id || ""),
    fromName: m.fromName || m.sender_name || "",
    to: String(m.to || m.receiver_id || ""),
    text: m.deleted || m.deleted_at ? null : (m.text != null ? m.text : (m.message != null ? m.message : "")),
    subject: m.subject || "",
    createdAt: m.createdAt || m.created_at || m.ts || null,
    editedAt: m.editedAt || m.edited_at || null,
    deleted: !!(m.deleted || m.deleted_at),
    readBy,
    read: m.read,
  };
}
/* Is this (normalized) message unread for `me`? v2: not in readBy; legacy: read===false
   and addressed to me. Own messages are never unread. */
function ssMsgUnreadFor(m, me) {
  if (!m || !me || m.deleted) return false;
  if (String(m.from || "").toLowerCase() === me) return false;
  if (Array.isArray(m.readBy)) return !m.readBy.includes(me);
  return m.read === false && (!m.to || String(m.to).toLowerCase() === me);
}
/* Load the Dispatch thread. GET /api/messages?conv=<id>&limit=&before= (v2). An old
   backend either 404s that form or ignores `conv` and returns the legacy array of every
   row the caller participates in — both fall back to filtering the legacy list
   (case-insensitive conv_id match, since old rows kept the JWT email's casing).
   Returns { messages, legacy, hasMore }. */
async function ssFetchDispatchThread(convId, before, limit) {
  const lim = limit || 100;
  let r = null; let legacy = false;
  try {
    r = await api.get("/api/messages?conv=" + encodeURIComponent(convId) + "&limit=" + lim + (before ? "&before=" + encodeURIComponent(before) : ""));
  } catch (e) {
    if (e && e.status === 404) { legacy = true; r = await api.get("/api/messages"); }
    else throw e;
  }
  if (Array.isArray(r)) legacy = true;
  if (legacy) {
    const want = String(convId || "").toLowerCase();
    const rows = (Array.isArray(r) ? r : []).map(ssNormMsg).filter((m) => m && String(m.convId || "").toLowerCase() === want);
    return { messages: rows, legacy: true, hasMore: false };
  }
  const list = (r && Array.isArray(r.messages) ? r.messages : []).map(ssNormMsg).filter(Boolean);
  return { messages: list, legacy: false, hasMore: list.length >= lim };
}

/* Unread messages in my Dispatch thread. v2: GET /api/messages/conversations (one row
   for a non-staff user, with `unread`). Old backend (404): count unread legacy rows. */
async function __ssFetchUnreadMessages() {
  const me = ssMyEmail();
  try {
    const r = await api.get("/api/messages/conversations");
    const list = r && Array.isArray(r.conversations) ? r.conversations : null;
    if (list) return list.reduce((n, c) => n + (Number(c && c.unread) || 0), 0);
  } catch (e) { if (!(e && e.status === 404)) throw e; }
  const rows = await api.get("/api/messages");
  if (!Array.isArray(rows)) return 0;
  return rows.map(ssNormMsg).filter((m) => ssMsgUnreadFor(m, me)).length;
}

/* [messaging-v2-2026-10-08] REALTIME — one shared EventSource on GET /api/stream.
   The app authenticates API calls with a Bearer JWT and EventSource cannot set
   headers; cle-backend lib/realtime.js streamHandler accepts the same JWT as a
   ?token= query param (its documented frontend usage), so we pass api._jwt there.
   Opened only while something subscribes; closed on sign-out. If the stream cannot
   be opened (no EventSource, 401, network) the callers' polling still runs. */
const __ssRt = { es: null, token: "", subs: new Set(), fails: 0, failTok: "", timer: null };
function __ssRtClose() {
  if (__ssRt.timer) { clearTimeout(__ssRt.timer); __ssRt.timer = null; }
  if (__ssRt.es) { try { __ssRt.es.close(); } catch (_) {} }
  __ssRt.es = null; __ssRt.token = "";
}
function __ssRtEnsure() {
  const tok = api._jwt || "";
  if (!__ssRt.subs.size || !tok || typeof EventSource === "undefined") { if (!__ssRt.subs.size || !tok) __ssRtClose(); return; }
  if (__ssRt.es && __ssRt.token === tok) return;
  if (__ssRt.failTok !== tok) { __ssRt.fails = 0; __ssRt.failTok = tok; } // new sign-in: try again
  __ssRtClose();
  if (__ssRt.fails >= 6) return; // give up; polling covers it
  try {
    const es = new EventSource(API_URL + "/api/stream?token=" + encodeURIComponent(tok));
    __ssRt.es = es; __ssRt.token = tok;
    es.onopen = () => { __ssRt.fails = 0; };
    es.onmessage = (ev) => {
      let p = null; try { p = JSON.parse(ev && ev.data); } catch (_) { return; }
      if (!p || typeof p !== "object") return;
      __ssRt.subs.forEach((fn) => { try { fn(p); } catch (_) {} });
    };
    es.onerror = () => {
      // CLOSED (2) = the browser will not retry (e.g. 401). Back off and retry ourselves.
      if (es.readyState === 2 && __ssRt.es === es) {
        __ssRt.es = null; __ssRt.token = ""; __ssRt.fails++;
        const wait = Math.min(60000, 5000 * Math.pow(2, __ssRt.fails - 1));
        __ssRt.timer = setTimeout(() => { __ssRt.timer = null; __ssRtEnsure(); }, wait);
      }
    };
  } catch (_) { __ssRt.es = null; __ssRt.fails++; }
}
function ssRealtimeSubscribe(fn) {
  __ssRt.subs.add(fn);
  __ssRtEnsure();
  return () => { __ssRt.subs.delete(fn); if (!__ssRt.subs.size) __ssRtClose(); };
}
if (typeof window !== "undefined") {
  try { window.addEventListener("ss-session-expired", () => { __ssRtClose(); __ssRt.fails = 0; }); } catch (_) {}
}

/* Badge: shared 60 s poll (while any badge is mounted) + refresh on a realtime
   {type:'message'} event. One timer / one subscription however many badges mount. */
const __ssMsgBadge = { refs: 0, timer: null, unsub: null };
function __ssMsgBadgeRefresh() {
  ssInvalidateBadge("messages");
  Promise.resolve().then(__ssFetchUnreadMessages).then((v) => __ssBadgeSet("messages", v)).catch(() => {});
}
function useUnreadMessages() {
  useEffect(() => {
    __ssMsgBadge.refs++;
    if (__ssMsgBadge.refs === 1) {
      __ssMsgBadge.timer = setInterval(__ssMsgBadgeRefresh, 60000);
      __ssMsgBadge.unsub = ssRealtimeSubscribe((p) => { if (p && p.type === "message") __ssMsgBadgeRefresh(); });
    }
    return () => {
      __ssMsgBadge.refs--;
      if (__ssMsgBadge.refs <= 0) {
        __ssMsgBadge.refs = 0;
        if (__ssMsgBadge.timer) clearInterval(__ssMsgBadge.timer);
        if (__ssMsgBadge.unsub) __ssMsgBadge.unsub();
        __ssMsgBadge.timer = null; __ssMsgBadge.unsub = null;
      }
    };
  }, []);
  return useBadge("messages", __ssFetchUnreadMessages, 0);
}

/* Registration + bond state from the server's own application
   (GET /api/applications/mine spreads server_applications.details, which is where
   regExpDate / bondExpDate live — the same keys the backend's expiry-reminder
   cron reads). Returns a one-line summary for the Compliance menu item. */
function ssExpiryWord(label, iso, present) {
  if (!present && !iso) return label + " not on file";
  if (!iso) return label + " on file";
  const d = new Date(iso + (String(iso).length === 10 ? "T12:00:00" : ""));
  if (isNaN(d.getTime())) return label + " on file";
  const days = Math.round((d.getTime() - Date.now()) / 86400000);
  const when = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: days > 300 || days < -300 ? "numeric" : undefined });
  if (days < 0) return label + " expired " + when;
  if (days <= 30) return label + " expires " + when;
  return label + " valid";
}
async function __ssFetchComplianceSummary() {
  let user = null;
  try { user = JSON.parse(localStorage.getItem(SS_USER_KEY) || "null"); } catch (_) {}
  const r = await api.get("/api/applications/mine");
  const a = (r && r.application) || null;
  const exempt = !!(user && (user.exempt_from_registration || user.exemptFromRegistration || user.is_sheriff_or_marshal));
  if (!a) return exempt ? "Registration exempt · No bond on file" : "No application on file";
  const reg = exempt ? "Registration exempt" : ssExpiryWord("Registration", a.regExpDate, !!(a.regNumber || a.ps_registration || a.regCert));
  const bond = ssExpiryWord("Bond", a.bondExpDate, !!(a.bondNumber || a.bond || a.bondDoc));
  return reg + " · " + bond;
}
function useComplianceSummary() { return useBadge("compliance", __ssFetchComplianceSummary, "Checking…"); }

// ═══════════════════════════════════════════════════════════════════════
// [messaging-v2-2026-10-08] MESSAGES — the server's single Dispatch thread
// Contract (messaging-contract.md): a non-staff user has exactly ONE conversation,
// `admin-<lowercased own email>`, a shared inbox every staff user of the tenant
// reads and answers. No recipient choice, no list.
//   Load:   GET /api/messages?conv=<id> (newest 100, ascending) + "Load earlier"
//           with &before=<oldest createdAt>. Old backend → legacy GET /api/messages
//           filtered to this thread (ssFetchDispatchThread).
//   Send:   POST /api/messages { convId, text }. A failed send stays in the list as
//           "Not sent — tap to retry" with the server's error text.
//   Read:   POST /api/messages/mark-read { convId } while the drawer is open
//           (conversationId too, for the pre-v2 backend).
//   Delete: own messages only, DELETE /api/messages/:id { reason } (ssAskReason);
//           never offered for an unsent local bubble.
//   Live:   ssRealtimeSubscribe → refetch on { type:'message' } for this thread;
//           polling fallback every 20 s while the drawer is open.
// Replaces the [server-parity-web-2026-10-05] inbox, which lowercased its id while
// the old backend matched case-sensitively, polled every 15 s and listed threads.
// Job chats stay in each job's Chat tab.
// ═══════════════════════════════════════════════════════════════════════
function MessagesScreen() {
  const { user } = React.useContext(AppCtx);
  const me = String((user && user.email) || ssMyEmail() || "").trim().toLowerCase();
  const convId = ssDispatchConvId(me);
  const [msgs, setMsgs] = useState(null);       // server messages (normalized), ascending
  const [pending, setPending] = useState([]);   // local bubbles: { localId, text, status: 'sending'|'failed', error }
  const [err, setErr] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [legacy, setLegacy] = useState(false);
  const [draft, setDraft] = useState("");
  const endRef = useRef(null);
  const lastCountRef = useRef(0);
  const markingRef = useRef(false);
  const loadedRef = useRef(false);

  // Refetch the newest page and merge it over what we have (keeps "Load earlier" pages,
  // picks up edits/deletes inside the newest window).
  const load = React.useCallback(async () => {
    if (!convId) return;
    try {
      const r = await ssFetchDispatchThread(convId);
      setLegacy(r.legacy);
      if (!loadedRef.current || r.legacy) setHasMore(r.hasMore); // later refetches keep the "Load earlier" state
      loadedRef.current = true;
      setMsgs((prev) => {
        if (!prev || r.legacy) return r.messages;
        const fresh = new Map(r.messages.map((m) => [String(m.id), m]));
        const oldestFresh = r.messages.length ? new Date(r.messages[0].createdAt || 0).getTime() : Infinity;
        const older = prev.filter((m) => !fresh.has(String(m.id)) && new Date(m.createdAt || 0).getTime() < oldestFresh);
        return older.concat(r.messages);
      });
      setErr("");
    } catch (e) {
      setMsgs((prev) => prev || []);
      setErr((e && e.message) || "Could not load messages");
    }
  }, [convId]);

  // Initial load + 20 s fallback poll while open + realtime refetch.
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const unsub = ssRealtimeSubscribe((p) => {
      if (!p || p.type !== "message") return;
      if (p.convId && String(p.convId).toLowerCase() !== convId) return;
      load();
    });
    return () => { clearInterval(t); unsub(); };
  }, [load, convId]);

  // Mark read while the drawer is open whenever something unread from staff is shown.
  useEffect(() => {
    if (!msgs || !convId || markingRef.current) return;
    const firstUnread = msgs.find((m) => ssMsgUnreadFor(m, me));
    if (!firstUnread) return;
    markingRef.current = true;
    // Pre-v2 backend matches conversationId case-sensitively: send the row's own conv_id there.
    api.post("/api/messages/mark-read", { convId, conversationId: legacy && firstUnread.convId ? firstUnread.convId : convId })
      .then(() => {
        setMsgs((prev) => (prev || []).map((m) => (ssMsgUnreadFor(m, me) ? { ...m, read: true, readBy: Array.isArray(m.readBy) ? m.readBy.concat(me) : m.readBy } : m)));
        __ssMsgBadgeRefresh();
      })
      .catch(() => {})
      .finally(() => { markingRef.current = false; });
  }, [msgs, convId, me, legacy]);

  // Scroll to the newest bubble when the count grows at the bottom (not on "Load earlier").
  const total = (msgs ? msgs.length : 0) + pending.length;
  const newestKey = pending.length ? pending[pending.length - 1].localId : (msgs && msgs.length ? String(msgs[msgs.length - 1].id) : "");
  useEffect(() => {
    if (total !== lastCountRef.current) {
      lastCountRef.current = total;
      try { endRef.current && endRef.current.scrollIntoView({ block: "end" }); } catch (_) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newestKey]);

  async function loadEarlier() {
    if (loadingMore || !msgs || !msgs.length) return;
    const before = msgs[0].createdAt;
    if (!before) { setHasMore(false); return; }
    setLoadingMore(true);
    try {
      const r = await ssFetchDispatchThread(convId, before);
      if (r.legacy) { setHasMore(false); return; }
      setMsgs((prev) => {
        const have = new Set((prev || []).map((m) => String(m.id)));
        return r.messages.filter((m) => !have.has(String(m.id))).concat(prev || []);
      });
      setHasMore(r.hasMore);
    } catch (e) { ssToast("err", (e && e.message) || "Could not load earlier messages"); }
    finally { setLoadingMore(false); }
  }

  async function deliver(localId, text) {
    setPending((p) => p.map((x) => (x.localId === localId ? { ...x, status: "sending", error: "" } : x)));
    try {
      const r = await api.post("/api/messages", { convId, text });
      const saved = ssNormMsg(r && r.message);
      setPending((p) => p.filter((x) => x.localId !== localId));
      if (saved && saved.id != null) {
        setMsgs((prev) => ((prev || []).some((m) => String(m.id) === String(saved.id)) ? prev : (prev || []).concat(saved)));
      }
      load();
    } catch (e) {
      const why = (e && e.message) || "Could not send";
      setPending((p) => p.map((x) => (x.localId === localId ? { ...x, status: "failed", error: why } : x)));
    }
  }
  function send() {
    const text = draft.trim();
    if (!text || !convId) return;
    const localId = "local-" + Date.now() + "-" + Math.random().toString(36).slice(2, 7);
    setPending((p) => p.concat({ localId, text, status: "sending", error: "", createdAt: new Date().toISOString() }));
    setDraft("");
    deliver(localId, text);
  }
  function retry(x) { if (x && x.status === "failed") deliver(x.localId, x.text); }
  function discard(x) { setPending((p) => p.filter((y) => y.localId !== x.localId)); }

  async function delMsg(m) {
    // Never for unsent local bubbles — those have no server id (handled by discard()).
    if (!m || m.id == null || /^local-/.test(String(m.id)) || m.deleted) return;
    if (String(m.from || "").toLowerCase() !== me) return;
    const reason = ssAskReason("this message"); if (!reason) return;
    try {
      await api.req("DELETE", "/api/messages/" + encodeURIComponent(m.id), { reason });
      setMsgs((prev) => (prev || []).map((x) => (String(x.id) === String(m.id) ? { ...x, deleted: true, text: null } : x)));
      ssToast("ok", "Message deleted");
      load();
    } catch (e) { ssToast("err", (e && e.message) || "Could not delete the message"); }
  }

  const fmt = (ts) => { try { const d = new Date(ts); return isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch (_) { return ""; } };
  const bubble = (mine, faded) => ({
    background: mine ? C.deep : C.white, color: mine ? C.white : C.ink,
    border: mine ? "none" : `1px solid ${C.steelXL}`, borderRadius: 14, padding: "9px 12px",
    fontSize: 13, lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word", opacity: faded ? 0.6 : 1,
  });

  if (!convId) {
    return <DInlineAlert kind="err">Sign in again to message dispatch.</DInlineAlert>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "60vh" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: C.goldXL, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Headphones size={17} color={C.deep} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.deep }}>Dispatch</div>
          <div style={{ fontSize: 11, color: C.steel }}>The office team sees and answers this thread.</div>
        </div>
      </div>
      {err && <DInlineAlert kind="err">{err}</DInlineAlert>}
      {msgs === null && <DCard><div style={{ padding: 16, fontSize: 12, color: C.steel }}>Loading messages…</div></DCard>}
      {msgs !== null && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
          {hasMore && !legacy && (
            <button onClick={loadEarlier} disabled={loadingMore} style={{ alignSelf: "center", background: "none", border: `1px solid ${C.steelXL}`, borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 700, color: C.royal, cursor: loadingMore ? "default" : "pointer", fontFamily: "inherit" }}>
              {loadingMore ? "Loading…" : "Load earlier"}
            </button>
          )}
          {msgs.length === 0 && pending.length === 0 && (
            <div style={{ fontSize: 12, color: C.steel, lineHeight: 1.5, padding: "8px 0" }}>
              Start a conversation with dispatch — the office sees it in their inbox.
            </div>
          )}
          {msgs.map((m) => {
            const mine = String(m.from || "").toLowerCase() === me;
            return (
              <div key={String(m.id)} style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "85%" }}>
                <div style={bubble(mine, m.deleted)}>
                  {m.deleted ? <span style={{ fontStyle: "italic" }}>Message deleted</span> : (
                    <>
                      {m.subject ? <div style={{ fontWeight: 800, marginBottom: 2 }}>{m.subject}</div> : null}
                      {m.text}
                    </>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: mine ? "flex-end" : "flex-start", gap: 6, fontSize: 10, color: C.steel, marginTop: 2 }}>
                  <span>{mine ? "You" : (m.fromName || m.from || "Dispatch")} · {fmt(m.createdAt)}{m.editedAt && !m.deleted ? " · edited" : ""}</span>
                  {mine && !m.deleted && m.id != null && (
                    <button onClick={() => delMsg(m)} aria-label="Delete message" title="Delete message" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex" }}>
                      <Trash2 size={11} color={C.steel} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {pending.map((x) => (
            <div key={x.localId} style={{ alignSelf: "flex-end", maxWidth: "85%" }}>
              <div
                role={x.status === "failed" ? "button" : undefined}
                onClick={() => retry(x)}
                style={{ ...bubble(true, x.status === "sending"), cursor: x.status === "failed" ? "pointer" : "default", border: x.status === "failed" ? `1px solid ${C.err}` : "none" }}>
                {x.text}
              </div>
              {x.status === "sending" ? (
                <div style={{ fontSize: 10, color: C.steel, marginTop: 2, textAlign: "right" }}>Sending…</div>
              ) : (
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, fontSize: 10, marginTop: 2 }}>
                  <button onClick={() => retry(x)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: C.err, fontWeight: 700, fontSize: 10, fontFamily: "inherit", textAlign: "right" }}>
                    Not sent — tap to retry{x.error ? " · " + x.error : ""}
                  </button>
                  <button onClick={() => discard(x)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: C.steel, fontSize: 10, fontFamily: "inherit" }}>Discard</button>
                </div>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", position: "sticky", bottom: 0, background: C.bg, paddingTop: 8 }}>
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="Message dispatch…"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
          style={{ flex: 1, padding: "9px 10px", border: `1px solid ${C.steelXL}`, borderRadius: 10, fontSize: 13, color: C.deep, outline: "none", fontFamily: "inherit", resize: "none", background: C.white }} />
        <button onClick={send} disabled={!draft.trim()} aria-label="Send" style={{
          width: 44, height: 44, borderRadius: 12, border: "none", background: !draft.trim() ? C.steelL : C.deep, color: C.white,
          display: "flex", alignItems: "center", justifyContent: "center", cursor: !draft.trim() ? "not-allowed" : "pointer", flexShrink: 0,
        }}><Send size={18} /></button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// [server-parity-web-2026-10-05] AVAILABILITY — simple weekly editor
// Same data the admin web's ServerCalendar (mode="self") reads and writes:
// users.availability (JSONB) holds { [serverEmail]: { "YYYY-MM-DD": "available"
// | "unavailable" | "partial" } } plus the `discoverable` flag HomeScreen's
// shift toggle sets. Persisted with PATCH /api/auth/profile { availability }
// and re-read from GET /api/auth/me so the admin calendar and this screen can
// never disagree. Days with no entry count as available.
// ═══════════════════════════════════════════════════════════════════════
function AvailabilityScreen() {
  const { user, setUser } = React.useContext(AppCtx);
  const me = String((user && user.email) || "");
  const [weekStart, setWeekStart] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - d.getDay()); return d; });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const avail = (user && user.availability && typeof user.availability === "object") ? user.availability : {};
  const mine = (avail[me] && typeof avail[me] === "object") ? avail[me] : {};
  const key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; });
  const cycle = { available: "unavailable", unavailable: "partial", partial: "available" };
  const palette = { available: { bg: C.okL, fg: C.ok, label: "Available" }, unavailable: { bg: C.errL, fg: C.err, label: "Unavailable" }, partial: { bg: C.warnL, fg: C.warn, label: "Partial" } };

  async function save(nextMine) {
    if (busy) return;
    setBusy(true); setErr("");
    const next = { ...avail, [me]: nextMine };
    try {
      await api.patch("/api/auth/profile", { availability: next });
      let fresh = null;
      try { fresh = await api.me(); } catch (_) {}
      const u = fresh || { ...user, availability: next };
      if (typeof setUser === "function") setUser(u);
      try { localStorage.setItem(SS_USER_KEY, JSON.stringify(u)); } catch (_) {}
    } catch (e) { setErr((e && e.message) || "Could not save availability"); }
    finally { setBusy(false); }
  }
  const toggle = (d) => { const k = key(d); const cur = mine[k] || "available"; save({ ...mine, [k]: cycle[cur] }); };
  const setAll = (status) => {
    const n = { ...mine };
    days.forEach((d) => { const dow = d.getDay(); n[key(d)] = status === "weekdays" ? ((dow === 0 || dow === 6) ? "unavailable" : "available") : status; });
    save(n);
  };
  const shift = (n) => setWeekStart((w) => { const d = new Date(w); d.setDate(d.getDate() + n * 7); return d; });
  const today = key(new Date());
  const range = `${days[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  return (
    <>
      <DSectionLabel>My availability</DSectionLabel>
      {err && <DInlineAlert kind="err">{err}</DInlineAlert>}
      <DCard>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderBottom: `1px solid ${C.steelXXL}` }}>
          <button onClick={() => shift(-1)} aria-label="Previous week" style={iconBtnLight}><ChevronLeft size={18} color={C.deep} /></button>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.deep }}>{range}</div>
          <button onClick={() => shift(1)} aria-label="Next week" style={iconBtnLight}><ChevronRight size={18} color={C.deep} /></button>
        </div>
        {days.map((d, i) => {
          const k = key(d); const st = mine[k] || "available"; const p = palette[st];
          return (
            <button key={k} onClick={() => toggle(d)} disabled={busy} style={{
              width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "11px 12px", background: "none", border: "none",
              borderBottom: i < 6 ? `1px solid ${C.steelXXL}` : "none", cursor: busy ? "wait" : "pointer", textAlign: "left", fontFamily: "inherit",
            }}>
              <div style={{ width: 44 }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: k === today ? C.gold : C.steel }}>{d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase()}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: C.deep }}>{d.getDate()}</div>
              </div>
              <div style={{ flex: 1, fontSize: 12, color: C.steel }}>{k === today ? "Today" : d.toLocaleDateString(undefined, { month: "long", day: "numeric" })}</div>
              <div style={{ padding: "5px 10px", borderRadius: 999, background: p.bg, color: p.fg, fontSize: 11, fontWeight: 800, minWidth: 92, textAlign: "center" }}>{p.label}</div>
            </button>
          );
        })}
      </DCard>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
        {[["available", "All available"], ["weekdays", "Weekdays only"], ["unavailable", "Week off"]].map(([id, label]) => (
          <button key={id} onClick={() => setAll(id)} disabled={busy} style={{
            padding: "8px 12px", background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 999,
            fontSize: 12, fontWeight: 700, color: C.deep, cursor: busy ? "wait" : "pointer", fontFamily: "inherit",
          }}>{label}</button>
        ))}
      </div>
      <div style={{ fontSize: 11, color: C.steel, marginTop: 10, lineHeight: 1.5 }}>
        Tap a day to cycle Available → Unavailable → Partial. Dispatch sees this on the server calendar when assigning work.
        {busy ? " Saving…" : ""}
      </div>
    </>
  );
}

function AccountScreen({ setDrawerScreen }) {
  const { user, logout } = React.useContext(AppCtx);
  const isPremium = ssIsPremium(user);
  const trainingBadge = useTrainingBadge();      // [server-parity-web-2026-10-05]
  const unreadMessages = useUnreadMessages();     // [server-parity-web-2026-10-05]
  const complianceSub = useComplianceSummary();   // [server-parity-web-2026-10-05]

  return (
    <div style={{ padding: "16px 16px 100px" }}>
      {/* Profile card */}
      <div style={{
        background: C.white, borderRadius: 18, padding: 16, marginBottom: 14,
        border: `1px solid ${C.steelXL}`, display: "flex", alignItems: "center", gap: 14,
      }}>
        <Avatar user={user} size={56} fontSize={22} />
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.deep, letterSpacing: "-0.01em" }}>{user?.name || "Server"}</div>
            <ProBadge user={user} />
          </div>
          <div style={{ fontSize: 11, color: C.steel, marginTop: 2 }}>{(user?.role === "client") ? "Client" : "Process Server"}{user?.county ? ` · ${user.county} County` : ""}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Star size={11} color={C.gold} fill={C.gold} />
              <span style={{ fontSize: 11, fontWeight: 700, color: C.deep }}>
                {(user?.rating != null && Number.isFinite(Number(user.rating)))
                  ? Number(user.rating).toFixed(2)
                  : "—"}
              </span>
            </div>
            <div style={{ width: 1, height: 10, background: C.steelL }} />
            <div style={{ fontSize: 11, color: C.steel }}>
              {(user?.total_serves != null && Number.isFinite(Number(user.total_serves)))
                ? `${Number(user.total_serves)} serves`
                : "No serves yet"}
            </div>
          </div>
        </div>
      </div>

      {/* Tier card — tap to open the in-app Plan & billing screen, which shows
          the live plan/status from /api/subscriptions/my and drives subscribe
          (Stripe Checkout) / manage (Stripe Portal) through the in-app browser. */}
      {SS_PRO_ENABLED && (!ssNoPurchase() || isPremium) && ( /* [ios-no-purchase-2026-10-05] [pro-disabled-2026-10-06] */
      <button
        onClick={() => setDrawerScreen("billing")}
        style={{
          width: "100%", textAlign: "left", cursor: "pointer",
          background: `linear-gradient(135deg, ${C.purple} 0%, #5a23c4 100%)`,
          border: "none",
          borderRadius: 14, padding: 14, marginBottom: 14, color: C.white, position: "relative", overflow: "hidden",
        }}>
        <div style={{
          position: "absolute", top: -20, right: -20, width: 80, height: 80, borderRadius: "50%",
          background: "rgba(255,255,255,0.1)",
        }} />
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <Award size={14} color={C.goldL} />
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em" }}>
            {isPremium ? "PRO MEMBER" : "UPGRADE TO PRO"}
          </div>
          <ChevronRight size={14} color={C.white} style={{ marginLeft: "auto", opacity: 0.85 }} />
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 2 }}>
          {isPremium ? "Priority job access active" : "Get priority job access"}
        </div>
        {/* [tier-collapse-2026-09-20] Price reads from the single source of truth
            (SS_SERVER_PLANS) instead of a hardcoded literal. */}
        <div style={{ fontSize: 11, opacity: 0.85 }}>
          {ssNoPurchase()
            ? "View your plan"
            : (isPremium
              ? `${SS_SERVER_PLANS[0].price} · View plan & billing`
              : `${SS_SERVER_PLANS[0].price} · View plans & subscribe`)}
        </div>
      </button>
      )}

      {/* Menu sections */}
      <MenuSection title="Account">
        <MenuItem icon={User} label="Edit profile" onPress={() => setDrawerScreen("profile")} />
        <MenuItem icon={MapPin} label="Coverage areas" onPress={() => setDrawerScreen("coverage")}
          sub={ssCoverageSummary(user)} />{/* [account-coverage-sub-2026-10-08] */}
        <MenuItem icon={FileText} label="Compliance documents" sub={complianceSub} onPress={() => setDrawerScreen("documents")} />{/* [server-parity-web-2026-10-05] live, was a static literal */}
        <MenuItem icon={Lock} label="Change password" onPress={() => setDrawerScreen("settings")} />
        <MenuItem icon={Bell} label="Notifications" sub="Push · SMS · Email" onPress={() => setDrawerScreen("settings")} />
        <MenuItem icon={Calendar} label="Availability" sub="Days you can take work" onPress={() => setDrawerScreen("availability")} />{/* [server-parity-web-2026-10-05] */}
        {/* [account-delete-2026-09-30] */}
        <MenuItem icon={Trash2} label="Delete account" sub="Permanently delete your account and data" last onPress={() => setDrawerScreen("deleteAccount")} />
      </MenuSection>

      <MenuSection title="Payment">
        {SS_PRO_ENABLED && <MenuItem icon={CreditCard} label="Plan & billing" sub={ssNoPurchase() ? "View your plan" : (isPremium ? "Manage your subscription" : "View plans & subscribe")} onPress={() => setDrawerScreen("billing")} />}{/* [pro-disabled-2026-10-06] */}
        <MenuItem
          icon={ExternalLink}
          label="Edit Stripe profile"
          external
          onPress={async () => {
            // Fetch the auth-protected Connect link with the api helper (sends the
            // Bearer token) BEFORE opening it. Opening the raw endpoint in
            // SFSafariViewController sent no auth -> 401. Backend returns { url }.
            const toast = (kind, text) => {
              try { window.dispatchEvent(new CustomEvent("ss-toast", { detail: { kind, text } })); } catch (_) {}
            };
            try {
              const r = await api.get("/api/stripe/connect-link");
              if (r && r.url) openExternal(r.url);
              else toast("err", "Could not start Stripe setup. Try again.");
            } catch (e) {
              toast("err", "Could not start Stripe setup.");
            }
          }}
        />
        <MenuItem icon={Route} label="Mileage Log" sub="Tax record-keeping" onPress={() => setDrawerScreen("mileage")} />
        <MenuItem icon={Gift} label="Referrals" sub="Your code & rewards" onPress={() => setDrawerScreen("referrals")} />{/* [server-referrals-screen-2026-10-08] */}
        <MenuItem icon={Receipt} label="Tax documents" sub="W-9 · 2025 1099-NEC" last onPress={() => setDrawerScreen("documents")} />
      </MenuSection>

      <MenuSection title="Support">
        <MenuItem icon={GraduationCap} label="Training videos" badge={trainingBadge || undefined} sub={trainingBadge ? `${trainingBadge} required video${trainingBadge === 1 ? "" : "s"} left` : "All required videos done"} onPress={() => setDrawerScreen("training")} />{/* [server-parity-web-2026-10-05] */}
        <MenuItem icon={MessageSquare} label="Messages" badge={unreadMessages || undefined} sub="Dispatch & support threads" onPress={() => setDrawerScreen("messages")} />{/* [server-parity-web-2026-10-05] */}
        <MenuItem icon={FolderOpen} label="My documents" onPress={() => setDrawerScreen("documents")} />
        <MenuItem icon={HelpCircle} label="Help center" external onPress={() => openExternal("https://www.strikeserve.com/help")} />
        <MenuItem icon={MessageSquare} label="Contact support" sub="Avg reply 8 min" external last onPress={() => setDrawerScreen("support")} />
      </MenuSection>

      {/* Logout */}
      <button onClick={logout} style={{
        width: "100%", padding: 14, marginTop: 14, background: C.white,
        border: `1px solid ${C.errL}`, borderRadius: 14,
        display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <LogOut size={18} color={C.err} />
          <span style={{ fontSize: 14, fontWeight: 700, color: C.err }}>Log out</span>
        </div>
        <ChevronRight size={16} color={C.err} />
      </button>

      <div style={{ textAlign: "center", marginTop: 18, fontSize: 10, color: C.steel }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, marginBottom: 4 }}>
          <span style={{ fontWeight: 800, color: C.deep }}>Strike</span>
          <span style={{ fontWeight: 400, fontStyle: "italic", color: C.gold }}>Serve</span>
        </div>
        <div>Server App v1.0.0 (build 2026.05.07)</div>
      </div>
    </div>
  );
}

function MenuSection({ title, children }) {
  return (
    <>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", margin: "14px 4px 6px" }}>
        {title.toUpperCase()}
      </div>
      <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.steelXL}`, overflow: "hidden" }}>
        {children}
      </div>
    </>
  );
}

function MenuItem({ icon: Icon, label, sub, badge, external, last, onPress }) {
  return (
    <button onClick={onPress} style={{
      width: "100%", padding: "13px 14px", background: "none", border: "none",
      borderBottom: last ? "none" : `1px solid ${C.steelXXL}`,
      display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left",
    }}>
      <Icon size={18} color={C.deep} strokeWidth={1.8} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.deep }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: C.steel, marginTop: 1 }}>{sub}</div>}
      </div>
      {badge && (
        <div style={{
          minWidth: 18, height: 18, padding: "0 5px", background: C.gold, color: C.deep,
          borderRadius: 9, fontSize: 10, fontWeight: 800,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>{badge}</div>
      )}
      {external ? <ExternalLink size={14} color={C.steel} /> : <ChevronRight size={16} color={C.steel} />}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// HAMBURGER DRAWER
// ═══════════════════════════════════════════════════════════════════════
function Drawer({ onClose, setDrawerScreen }) {
  // Pull the real signed-in user + logout from AppCtx (matches SettingsScreen).
  const { user, logout } = React.useContext(AppCtx);
  const trainingBadge = useTrainingBadge(); // [server-parity-web-2026-10-05] real count, was the literal 3
  const unreadMessages = useUnreadMessages();
  const items = [
    { id: "profile", icon: User, label: "Profile" },
    { id: "messages", icon: MessageSquare, label: "Messages", badge: unreadMessages || undefined }, // [server-parity-web-2026-10-05]
    { id: "availability", icon: Calendar, label: "Availability" },                                 // [server-parity-web-2026-10-05]
    { id: "settings", icon: Settings, label: "Settings" },
    { id: "training", icon: GraduationCap, label: "Training videos", badge: trainingBadge || undefined },
    { id: "coverage", icon: MapPin, label: "Coverage areas" },
    { id: "documents", icon: FolderOpen, label: "My documents" },
    { id: "mileage", icon: Route, label: "Mileage Log" },
    { id: "referrals", icon: Gift, label: "Referrals" }, // [server-referrals-screen-2026-10-08]
    { id: "support", icon: HelpCircle, label: "Help & support" },
  ];

  // Derive display name + initials + subline from the real user.
  // Backend uses `name` (single field) per ProfileScreen; we also accept
  // first_name/last_name in case the schema ever splits them, and fall back
  // to the email's first letter when nothing else is available.
  const firstName = user?.first_name || (user?.name ? String(user.name).split(" ")[0] : "");
  const lastName  = user?.last_name  || (user?.name ? String(user.name).split(" ").slice(1).join(" ") : "");
  const displayName = [firstName, lastName].filter(Boolean).join(" ").trim()
    || user?.email
    || "Server";
  const initials = (() => {
    const parts = [firstName, lastName].filter(Boolean);
    if (parts.length) return parts.map(s => s[0]).join("").slice(0, 2).toUpperCase();
    if (user?.email) return String(user.email).trim()[0].toUpperCase();
    return "S";
  })();
  const tierStr = user?.tier ? `${user.tier} · ` : "";
  const cityStr = user?.home_city || user?.city || "";
  const subline = `${tierStr}${cityStr}`; // empty string if neither

  return (
    <>
      <Backdrop onClose={onClose} />
      <div style={{
        position: "absolute", top: 0, left: 0, bottom: 0,
        width: typeof window !== "undefined" && window.innerWidth >= 768 ? 260 : 290,
        maxWidth: "75%",
        background: C.white, zIndex: 200, padding: "60px 0 20px",
        display: "flex", flexDirection: "column",
        animation: "slideIn 0.25s ease-out",
        boxShadow: "8px 0 30px rgba(0,0,0,0.3)",
      }}>
        <div style={{ padding: "0 18px 20px", borderBottom: `1px solid ${C.steelXL}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Avatar user={user} size={44} fontSize={16} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{
                  fontSize: 14, fontWeight: 800, color: C.deep,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{displayName}</div>
                <ProBadge user={user} />
              </div>
              {subline && (
                <div style={{ fontSize: 10, color: C.steel }}>{subline}</div>
              )}
            </div>
          </div>
        </div>
        <div style={{ flex: 1, padding: 0, overflow: "auto" }}>
          {items.map(it => {
            const Icon = it.icon;
            return (
              <button key={it.id} onClick={() => setDrawerScreen(it.id)} style={{
                width: "100%", padding: "12px 14px", marginBottom: 4,
                background: "none", border: "none", borderRadius: 12,
                display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left",
              }}>
                <Icon size={18} color={C.deep} strokeWidth={1.8} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.deep }}>{it.label}</span>
                {it.badge && (
                  <div style={{
                    minWidth: 18, height: 18, padding: "0 5px", background: C.gold, color: C.deep,
                    borderRadius: 9, fontSize: 10, fontWeight: 800,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>{it.badge}</div>
                )}
              </button>
            );
          })}
        </div>
        {/* Plan & billing — opens the in-app Billing screen (live plan + manage). */}
        {SS_PRO_ENABLED && ( /* [pro-disabled-2026-10-06] */
        <div style={{ padding: "8px 18px 0" }}>
          <button
            onClick={() => setDrawerScreen("billing")}
            style={{
              width: "100%", padding: "10px 0", background: "none", border: "none",
              display: "flex", alignItems: "center", gap: 10, cursor: "pointer", color: C.deep,
            }}>
            <CreditCard size={16} color={C.deep} />
            <span style={{ flex: 1, textAlign: "left", fontSize: 13, fontWeight: 600 }}>Plan &amp; billing</span>
            <ChevronRight size={14} color={C.steel} />
          </button>
        </div>
        )}
        <div style={{ padding: "12px 18px", borderTop: `1px solid ${C.steelXL}` }}>
          <button
            onClick={() => { if (onClose) onClose(); logout(); }}
            style={{
              width: "100%", padding: 0, background: "none", border: "none",
              display: "flex", alignItems: "center", gap: 10, cursor: "pointer", color: C.err,
            }}>
            <LogOut size={16} />
            <span style={{ fontSize: 13, fontWeight: 700 }}>Log out</span>
          </button>
        </div>
      </div>
      <style>{`@keyframes slideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// NOTIFICATIONS SHEET
// ═══════════════════════════════════════════════════════════════════════
// Relative time helper for notification timestamps.
// "2m ago" / "3h ago" / "Yesterday" / "3d ago" / "Mar 12"
function ssRelativeTime(ts) {
  if (!ts) return "";
  const t = typeof ts === "number" ? ts : Date.parse(ts);
  if (!t || isNaN(t)) return "";
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "Yesterday";
  if (day < 7) return `${day}d ago`;
  const d = new Date(t);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
}

// Map a notification `type` string to an icon + color from the C palette.
function ssNotifVisual(type) {
  switch (String(type || "").toLowerCase()) {
    case "job_assigned":
    case "job":
      return { Icon: Briefcase, color: C.deep };
    case "attempt_due":
    case "deadline":
      return { Icon: Clock, color: C.warn };
    case "payment":
    case "payout":
      return { Icon: DollarSign, color: C.ok };
    case "system":
    default:
      return { Icon: Bell, color: C.steel };
  }
}

function NotificationsSheet({ onClose }) {
  // null = loading, [] = empty (or 404), [...] = real list
  const [notifs, setNotifs] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const { setUnreadNotifs } = React.useContext(AppCtx);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/api/notifications");
        if (!alive) return;
        const arr = Array.isArray(r) ? r : [];
        setNotifs(arr);
        if (setUnreadNotifs) setUnreadNotifs(arr.filter(n => !n.read).length);
      } catch (e) {
        if (!alive) return;
        // 404 / network / etc. — fall back to empty state without crashing.
        setNotifs([]);
        setLoadErr(true);
        console.warn("[notifs] load failed:", e && e.message);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Tap a notification → optimistically flip its read flag, PATCH server,
  // then follow action_url if present.
  async function markRead(id) {
    setNotifs(prev => {
      const next = (prev || []).map(n => n.id === id ? { ...n, read: true } : n);
      if (setUnreadNotifs) setUnreadNotifs(next.filter(n => !n.read).length);
      return next;
    });
    try { await api.patch(`/api/notifications/${id}`, { read: true }); }
    catch (e) { console.warn("[notifs] mark-read failed:", e && e.message); }
  }
  async function handleTap(n) {
    if (!n.read) await markRead(n.id);
    /* [pos-overhaul-2026-10-07:server-app] "sign:<jobId>" / type pos_signature_required */
    {
      const d = (n && typeof n.data === "object" && n.data) || {};
      const link = String(n.action_url || n.link || d.link || "");
      const sm = link.match(/^sign:(.+)$/i);
      const sid = sm ? sm[1] : (String(n.type || d.type || "") === "pos_signature_required" ? (d.jobId || d.job_id || n.job_id) : null);
      if (sid) {
        ssOpenSign(String(sid));
        onClose();
        return;
      }
    }
    /* [job-bundles-v2-2026-10-16] "bundle:<id>" / type bundle_offer -> bundle offer review */
    {
      const d = (n && typeof n.data === "object" && n.data) || {};
      if (ssOpenBundleLink(n.action_url || n.link || d.action_url || d.link, { ...d, type: n.type || d.type, bundle_id: d.bundle_id != null ? d.bundle_id : n.bundle_id })) { onClose(); return; }
    }
    if (n.action_url) {
      try {
        const url = String(n.action_url);
        if (/^https?:\/\//i.test(url)) {
          window.open(url, "_blank", "noopener");
        } else {
          // Internal route — let AppInner handle it. Conventions:
          //   "drawer:settings"     → open a drawer screen
          //   "job:<id>"            → open that job's detail in-app
          //   "/jobs/<id>"          → same (route-style)
          //   "/settings"           → generic route fallback
          const dm = url.match(/^drawer:([a-z_-]+)$/i);
          if (dm && DRAWER_IDS.includes(dm[1])) { /* [server-referral-links-2026-10-08] validated */
            window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: dm[1] }));
            onClose();
            return;
          }
          /* [messaging-v2-2026-10-08] "msg:<convId>" → the Messages drawer (Dispatch thread). */
          if (ssOpenMsgLink(url)) { onClose(); return; }
          const jm = url.match(/^job:(.+)$/i) || url.match(/^\/jobs\/(.+)$/i);
          if (jm) {
            window.dispatchEvent(new CustomEvent("ss-open-job", { detail: String(jm[1]) }));
            onClose();
            return;
          }
          /* [ticket-link-2026-10-08] "ticket:<id>" (cle-backend routes/support-tickets.js) →
             that ticket's thread: the job's Support tab when it has a job, else a ticket sheet. */
          const tkm = url.match(/^ticket:(.+)$/i);
          if (tkm) {
            const _tj = n && (n.job_id != null ? n.job_id : n.jobId);
            window.dispatchEvent(new CustomEvent("ss-open-ticket", { detail: { ticketId: String(tkm[1]), jobId: _tj != null && String(_tj) !== "" ? String(_tj) : "" } }));
            onClose();
            return;
          }
          // [notif-link-2026-08-08] "tab:<name>" — for notifications whose subject
          // is a whole section rather than one record (payouts, earnings). Without
          // this the only in-app targets were a single job or a drawer screen, and
          // payout notifications had nowhere honest to point: BillingScreen is
          // subscriptions only, so drawer:billing would have been the wrong room.
          const tm = url.match(/^tab:([a-z]+)$/i);
          if (tm && TAB_IDS.includes(tm[1].toLowerCase())) {
            window.dispatchEvent(new CustomEvent("ss-open-tab", { detail: tm[1].toLowerCase() }));
            onClose();
            return;
          }
          /* [client-notif-links-2026-10-08] "page:<name>" (written for the web portal) → the matching screen here.
             [server-referral-links-2026-10-08] mapping moved to ssPageLinkTarget (shared with the push-tap
             handler). [server-referrals-screen-2026-10-08] page:referrals → the Referrals drawer. */
          const pm = url.match(/^page:([a-z-]+)$/i);
          if (pm && ssOpenPageLink(pm[1])) { onClose(); return; }
          /* [server-referrals-screen-2026-10-08] "work-item:<id>" — @mention notifications from
             cle-backend routes/work-queue.js. Every /api/work-items route (incl. GET /api/work-items/:id)
             is gated by queueStaff (QUEUE_ROLES: admin/owner/manager/dispatcher/administrator/
             superadmin/super_admin/office_staff), so a server-role user gets 403 and the
             notification row carries no job id — there is nothing in this app to open. Show the
             notification's own text, leave it read (marked above), close the sheet. */
          if (/^work-item:/i.test(url)) {
            const _wt = String((n && (n.message || n.title)) || "").trim();
            ssToast("ok", (_wt ? _wt.slice(0, 160) : "You were mentioned") + " — this is on the office work queue, not in this app.");
            onClose();
            return;
          }
          // Unrecognised internal target (admin-only page:*, work-item:*, sms:*, unknown
          // drawer/tab ids). Deliberately do NOT fall through to window.location.href —
          // a hard navigation blanks the Capacitor webview and there is no way back.
          // [server-referral-links-2026-10-08] The notification is already marked read
          // above; close the sheet rather than leave a dead tap with no feedback.
          console.warn("[notifs] unroutable action_url:", url);
          onClose();
        }
      } catch (e) { console.warn("[notifs] nav failed:", e && e.message); }
      return;
    }
    /* [notif-dismiss-2026-10-08] no link recorded: a notification about a job still opens that job. */
    const _jid = n && (n.job_id != null ? n.job_id : n.jobId);
    if (_jid != null && String(_jid) !== "") {
      window.dispatchEvent(new CustomEvent("ss-open-job", { detail: String(_jid) }));
      onClose();
    }
  }

  /* [notif-dismiss-2026-10-08] Dismiss one / Clear all — saved on the server (POST /api/notifications/dismiss),
     so cleared notifications stay gone on every device. Nothing is deleted on the server. */
  async function dismissOne(n, e) {
    if (e) e.stopPropagation();
    setNotifs(prev => {
      const next = (prev || []).filter(x => x.id !== n.id);
      if (setUnreadNotifs) setUnreadNotifs(next.filter(x => !x.read).length);
      return next;
    });
    try { await api.post("/api/notifications/dismiss", { ids: [n.id] }); }
    catch (err) { console.warn("[notifs] dismiss failed:", err && err.message); }
  }
  async function clearAll() {
    if (!window.confirm("Clear all notifications?")) return;
    setNotifs([]);
    if (setUnreadNotifs) setUnreadNotifs(0);
    try { await api.post("/api/notifications/dismiss", { all: true }); }
    catch (err) { console.warn("[notifs] clear-all failed:", err && err.message); }
  }

  async function markAllRead() {
    setNotifs(prev => {
      const next = (prev || []).map(n => ({ ...n, read: true }));
      if (setUnreadNotifs) setUnreadNotifs(0);
      return next;
    });
    try { await api.post("/api/notifications/mark-read", {}); }
    catch (e) { console.warn("[notifs] mark-all failed:", e && e.message); }
  }

  function openPrefs() {
    window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: "settings" }));
    onClose();
  }

  const hasAny = Array.isArray(notifs) && notifs.length > 0;
  const unreadCount = hasAny ? notifs.filter(n => !n.read).length : 0;

  return (
    <Sheet onClose={onClose}>
      {/* Header */}
      <div style={{
        padding: "16px 16px 12px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: `1px solid ${C.steelXL}`, background: C.white,
      }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, letterSpacing: "-0.01em" }}>Notifications</div>
          {hasAny && unreadCount > 0 && (
            <div style={{ fontSize: 11, color: C.steel, marginTop: 2 }}>{unreadCount} unread</div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {hasAny && (
            <button onClick={clearAll} style={{
              padding: "6px 12px", borderRadius: 14,
              background: C.white, border: `1px solid ${C.steelXL}`,
              color: C.deep, fontSize: 11, fontWeight: 800, cursor: "pointer",
            }}>Clear all</button>
          )}
          {hasAny && unreadCount > 0 && (
            <button onClick={markAllRead} style={{
              padding: "6px 12px", borderRadius: 14,
              background: C.goldXL, border: `1px solid ${C.gold}`,
              color: C.deep, fontSize: 11, fontWeight: 800, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 4,
            }}>
              <Check size={12} strokeWidth={3} /> Mark all read
            </button>
          )}
          <button onClick={onClose} style={iconBtnLight}><X size={20} color={C.deep} /></button>
        </div>
      </div>

      {/* List body */}
      <div style={{ padding: "12px 12px 90px", flex: 1, overflow: "auto" }}>
        {notifs === null && (
          <SkeletonGroup label="Loading notifications" style={{ padding: "8px 4px" }}>
            {[0,1,2].map(i => (
              <div key={i} style={{
                width: "100%", padding: "12px 12px 12px 10px", marginBottom: 6,
                borderRadius: 12, background: C.white,
                border: `1px solid ${C.steelXL}`, borderLeft: `4px solid ${C.steelXL}`,
                display: "flex", gap: 10, alignItems: "flex-start",
              }}>
                <SkeletonCircle size={40} />
                <div style={{ flex: 1 }}>
                  <SkeletonLine width="60%" height={13} />
                  <SkeletonLine width="90%" height={11} />
                  <SkeletonLine width="40%" height={11} style={{ marginBottom: 0 }} />
                </div>
              </div>
            ))}
          </SkeletonGroup>
        )}
        {notifs !== null && !hasAny && (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            padding: "60px 24px 40px", textAlign: "center", color: C.steel,
          }}>
            <div style={{
              width: 72, height: 72, borderRadius: 36, background: C.steelXXL,
              display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16,
            }}>
              <Bell size={32} color={C.steel} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.deep, marginBottom: 6 }}>
              You're all caught up
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5, maxWidth: 280 }}>
              We'll notify you when new jobs open within your 30-mile service area.{/* [server-zone-30-2026-10-18] */}
            </div>
            {loadErr && (
              <div style={{ fontSize: 11, color: C.steel, marginTop: 10, opacity: 0.7 }}>
                Notifications service unavailable.
              </div>
            )}
          </div>
        )}
        {hasAny && notifs.map(n => {
          const { Icon, color } = ssNotifVisual(n.type);
          const unread = !n.read;
          return (
            <button
              key={n.id}
              onClick={() => handleTap(n)}
              style={{
                width: "100%", textAlign: "left",
                padding: "12px 12px 12px 10px", marginBottom: 6,
                borderRadius: 12,
                background: unread ? C.white : "transparent",
                border: unread ? `1px solid ${C.steelXL}` : "1px solid transparent",
                borderLeft: unread ? `4px solid ${C.deep}` : "4px solid transparent",
                display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer",
              }}>
              <div style={{
                width: 40, height: 40, borderRadius: 20, background: `${color}1f`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <Icon size={18} color={color} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 2 }}>
                  <div style={{
                    fontSize: 13, fontWeight: unread ? 800 : 600, color: C.deep,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{n.title || "Notification"}</div>
                  <div style={{ fontSize: 10, color: C.steel, whiteSpace: "nowrap", flexShrink: 0 }}>
                    {ssRelativeTime(n.created_at)}
                  </div>
                </div>
                {(n.body || n.message) && (
                  <div style={{ fontSize: 13, color: C.steel, lineHeight: 1.4 }}>{n.body || n.message /* [notif-dismiss-2026-10-08] the API sends `message`; `body` alone hid every notification's text */}</div>
                )}
              </div>
              <span role="button" aria-label="Dismiss notification" title="Dismiss" onClick={(e) => dismissOne(n, e)}
                style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", color: C.steel, cursor: "pointer" }}>
                <X size={14} color={C.steel} />
              </span>
            </button>
          );
        })}
      </div>

      {/* Sticky footer — link to notification preferences. */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        padding: "10px 14px 14px", background: C.white,
        borderTop: `1px solid ${C.steelXL}`,
      }}>
        <button onClick={openPrefs} style={{
          width: "100%", padding: 12, background: C.bg,
          border: `1px solid ${C.steelXL}`, borderRadius: 12,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: "pointer", color: C.deep,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Settings size={16} color={C.deep} />
            <span style={{ fontSize: 13, fontWeight: 700 }}>Manage notification preferences</span>
          </div>
          <ChevronRight size={16} color={C.steel} />
        </button>
      </div>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SAFETY SHEET
// ═══════════════════════════════════════════════════════════════════════
// Hold-to-arm panic + three wired SafetyActions + Log incident modal.
// Backend failures surface a warning toast so the user knows the alert
// didn't actually go through — safety actions must be loud about failure.
function SafetySheet({ onClose }) {
  const [holding, setHolding] = useState(false);
  const [armProgress, setArmProgress] = useState(0);
  const [armed, setArmed] = useState(false);
  const [armedAtLabel, setArmedAtLabel] = useState("");
  const [toast, setToast] = useState(null); // {kind, text}
  const [sharingLocation, setSharingLocation] = useState(false);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const startedAtRef = useRef(0);

  // Auto-dismiss toasts after ~3.2s (same cadence as EarningsScreen).
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  // Get current coords for safety-action payloads. Resolves to nulls
  // rather than rejecting so the alert still fires when permission is denied.
  function getCoords() {
    return new Promise(resolve => {
      if (!navigator.geolocation) { resolve({ lat: null, lng: null, accuracy: null }); return; }
      navigator.geolocation.getCurrentPosition(
        pos => resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
        () => resolve({ lat: null, lng: null, accuracy: null }),
        { maximumAge: 30000, timeout: 4000, enableHighAccuracy: true }
      );
    });
  }

  function clearArmTimers() {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }

  function startHold() {
    if (armed) return;
    setHolding(true);
    startedAtRef.current = Date.now();
    setArmProgress(0);
    const tick = () => {
      const elapsed = Date.now() - startedAtRef.current;
      const pct = Math.min(100, (elapsed / 3000) * 100);
      setArmProgress(pct);
      if (pct < 100) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    timerRef.current = setTimeout(async () => {
      // Armed — fire dispatch
      setArmed(true);
      setHolding(false);
      setArmProgress(100);
      const when = new Date();
      const stamp = when.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
      setArmedAtLabel(stamp);
      const coords = await getCoords();
      try {
        await api.post("/api/safety/panic", { ...coords, job_id: null });
      } catch (e) {
        console.warn("[safety] panic POST failed:", e && e.message);
        setToast({ kind: "err", text: "Alert failed to send — call dispatch directly" });
      }
    }, 3000);
  }

  function cancelHold() {
    // Only resets if not yet armed (timer fired).
    if (armed) return;
    clearArmTimers();
    setHolding(false);
    setArmProgress(0);
  }

  function cancelAlert() {
    clearArmTimers();
    setArmed(false);
    setHolding(false);
    setArmProgress(0);
    setArmedAtLabel("");
    setToast({ kind: "ok", text: "Alert cancelled" });
  }

  // Tear down timers on unmount.
  useEffect(() => () => clearArmTimers(), []);

  // Cancel an in-progress hold if the user starts scrolling — covers the case
  // where they begin pressing the panic button but then drag/scroll the sheet
  // to dismiss. `pointerleave` doesn't fire reliably on momentum scrolls in
  // WKWebView, so attach scroll/wheel listeners only while `holding` is true
  // and detach as soon as the hold ends (or the sheet unmounts).
  useEffect(() => {
    if (!holding) return;
    const onScrollCancel = () => cancelHold();
    window.addEventListener("scroll", onScrollCancel, { passive: true, capture: true });
    window.addEventListener("wheel", onScrollCancel, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScrollCancel, { capture: true });
      window.removeEventListener("wheel", onScrollCancel);
    };
  }, [holding]);

  // === SafetyAction handlers ===
  async function onShareLocation() {
    const coords = await getCoords();
    try {
      await api.post("/api/safety/share-location", {
        duration_minutes: 30,
        lat: coords.lat, lng: coords.lng,
      });
      setSharingLocation(true);
      setToast({ kind: "ok", text: "Sharing location for 30 min" });
    } catch (e) {
      console.warn("[safety] share-location:", e && e.message);
      setToast({ kind: "err", text: "Could not start location sharing" });
    }
  }
  async function onCallDispatch() {
    /* [server-parity-web-2026-10-05] Never dial `tel:+1` with an empty number — the
       resolver returns "" when no support line is configured, and the old code
       would still navigate to tel:+1. Say so instead. */
    const digits = String(SUPPORT_PHONE || "").replace(/\D/g, "");
    if (!digits) {
      setToast({ kind: "err", text: "No dispatch line is configured for this account yet." });
      return;
    }
    // Fire-and-forget audit log; do not await before dialing.
    try { api.post("/api/safety/dispatch-call", { job_id: null }).catch(() => {}); } catch (_) {}
    // Use the support number from the centralised SUPPORT_PHONE resolver.
    const tel = digits.length === 11 && digits.startsWith("1") ? `tel:+${digits}` : `tel:+1${digits}`;
    try { window.location.href = tel; } catch (_) {}
  }
  async function onFlagUnsafe() {
    const coords = await getCoords();
    try {
      await api.post("/api/safety/flag-unsafe", {
        address: null, lat: coords.lat, lng: coords.lng, job_id: null, note: null,
      });
      setToast({ kind: "ok", text: "Address flagged as unsafe" });
    } catch (e) {
      console.warn("[safety] flag-unsafe:", e && e.message);
      setToast({ kind: "err", text: "Could not flag address" });
    }
  }

  return (
    <Sheet onClose={onClose} small>
      {/* [sheet-scroll-audit-2026-07-25] flex+minHeight+overflow so this scrolls instead of clipping. */}
      <div style={{ padding: "20px 16px", flex: 1, minHeight: 0, overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>SAFETY</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, marginTop: 2 }}>I need help</div>
          </div>
          <button onClick={onClose} style={iconBtnLight}><X size={20} color={C.deep} /></button>
        </div>

        {/* Panic — hold to arm */}
        {!armed ? (
          <button
            onPointerDown={startHold}
            onPointerUp={cancelHold}
            onPointerLeave={cancelHold}
            onPointerCancel={cancelHold}
            style={{
              width: "100%", padding: "20px", marginBottom: 12,
              position: "relative", overflow: "hidden",
              background: holding ? `linear-gradient(135deg, ${C.err} 0%, #8a2118 100%)` : C.white,
              border: `2px solid ${C.err}`, borderRadius: 14,
              display: "flex", alignItems: "center", gap: 14, cursor: "pointer",
              transition: "background 0.15s",
              touchAction: "none",
            }}>
            {/* Progress bar — fills as the user holds */}
            <div style={{
              position: "absolute", left: 0, bottom: 0, height: 4,
              width: `${armProgress}%`, background: holding ? C.goldL : C.err,
              transition: "width 0.05s linear",
            }} />
            <div style={{
              width: 50, height: 50, borderRadius: "50%",
              background: holding ? "rgba(255,255,255,0.2)" : C.errL,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <Siren size={24} color={holding ? C.white : C.err} />
            </div>
            <div style={{ textAlign: "left", flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: holding ? C.white : C.err }}>
                {holding ? `Hold to arm... ${Math.max(1, Math.ceil((100 - armProgress) / 33.4))}` : "Panic alert"}
              </div>
              <div style={{ fontSize: 11, color: holding ? "rgba(255,255,255,0.8)" : C.steel, marginTop: 2 }}>
                Hold 3 seconds to alert dispatch + share location
              </div>
            </div>
          </button>
        ) : (
          <div style={{
            width: "100%", padding: "16px", marginBottom: 12,
            background: `linear-gradient(135deg, ${C.err} 0%, #8a2118 100%)`,
            border: `2px solid ${C.err}`, borderRadius: 14, color: C.white,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Siren size={22} color={C.white} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 800 }}>Dispatch alerted at {armedAtLabel}</div>
                <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>Share to dispatch active</div>
              </div>
            </div>
            <button onClick={cancelAlert} style={{
              width: "100%", padding: 9, background: "rgba(255,255,255,0.18)", color: C.white,
              border: "1px solid rgba(255,255,255,0.4)", borderRadius: 10,
              fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>Cancel alert</button>
          </div>
        )}

        {/* Other safety actions */}
        <SafetyAction icon={LifeBuoy} label="Share location 30 min"
          sub={sharingLocation ? "Sharing now · with emergency contact" : "With emergency contact"}
          color={C.warn} dot={sharingLocation ? C.ok : null}
          onClick={onShareLocation} />
        {SUPPORT_PHONE ? (
          <SafetyAction icon={Phone} label="Call dispatch"
            sub={`${formatSupportPhone(SUPPORT_PHONE)} · 24/7 line`}
            color={C.info}
            onClick={onCallDispatch} />
        ) : null}
        <SafetyAction icon={ShieldAlert} label="Flag this address as unsafe"
          sub="Warns next server who accepts"
          color={C.deep}
          onClick={onFlagUnsafe} />

        {/* Log incident */}
        <button onClick={() => setIncidentOpen(true)} style={{
          width: "100%", padding: 10, marginTop: 4,
          background: "none", color: C.steel,
          border: `1px dashed ${C.steelL}`, borderRadius: 10,
          fontSize: 12, fontWeight: 700, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          <FileText size={14} color={C.steel} />
          Log incident
        </button>

        {toast && (
          <div style={{
            marginTop: 12, padding: "10px 12px",
            background: toast.kind === "err" ? C.errL : C.okL,
            color: toast.kind === "err" ? C.err : C.ok,
            borderRadius: 10, fontSize: 12, fontWeight: 700,
            display: "flex", alignItems: "center", gap: 6,
          }}>
            {toast.kind === "err"
              ? <AlertCircle size={14} color={C.err} />
              : <CheckCircle2 size={14} color={C.ok} />}
            {toast.text}
          </div>
        )}
      </div>
      {incidentOpen && <IncidentLogSheet onClose={() => setIncidentOpen(false)}
        onLogged={() => { setIncidentOpen(false); setToast({ kind: "ok", text: "Incident logged" }); }} />}
    </Sheet>
  );
}

function SafetyAction({ icon: Icon, label, sub, color, dot, onClick }) {
  return (
    <button onClick={onClick} style={{
      width: "100%", padding: 14, marginBottom: 8,
      background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 12,
      display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left",
    }}>
      <Icon size={20} color={color} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, display: "flex", alignItems: "center", gap: 6 }}>
          {label}
          {dot && <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot, display: "inline-block" }} />}
        </div>
        <div style={{ fontSize: 10, color: C.steel }}>{sub}</div>
      </div>
      <ChevronRight size={16} color={C.steel} />
    </button>
  );
}

// Small modal for logging a safety incident after the fact.
function IncidentLogSheet({ onClose, onLogged }) {
  const TYPES = ["Verbal threat", "Physical", "Property damage", "Refused service"];
  const [type, setType] = useState(TYPES[0]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit() {
    if (busy) return;
    setBusy(true); setErr("");
    let coords = { lat: null, lng: null };
    if (navigator.geolocation) {
      coords = await new Promise(resolve => {
        navigator.geolocation.getCurrentPosition(
          p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => resolve({ lat: null, lng: null }),
          { maximumAge: 30000, timeout: 4000 }
        );
      });
    }
    try {
      await api.post("/api/safety/incident", { type, note, ...coords, job_id: null });
    } catch (e) {
      if (!/404|not found/i.test((e && e.message) || "")) {
        setErr((e && e.message) || "Could not log incident");
        setBusy(false);
        return;
      }
      console.warn("[safety] /api/safety/incident 404 — captured locally");
    }
    setBusy(false);
    onLogged && onLogged();
  }

  return (
    <Sheet onClose={onClose} small>
      {/* [sheet-scroll-audit-2026-07-25] flex+minHeight+overflow so this scrolls instead of clipping. */}
      <div style={{ padding: "18px 16px", flex: 1, minHeight: 0, overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>SAFETY</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.deep, marginTop: 2 }}>Log incident</div>
          </div>
          <button onClick={onClose} style={iconBtnLight}><X size={20} color={C.deep} /></button>
        </div>
        <div style={{ fontSize: 11, color: C.steel, marginBottom: 6 }}>Type</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {TYPES.map(t => (
            <button key={t} onClick={() => setType(t)} style={{
              padding: "7px 11px",
              background: type === t ? C.deep : C.bg,
              color: type === t ? C.white : C.deep,
              border: "none", borderRadius: 999,
              fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>{t}</button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: C.steel, marginBottom: 6 }}>Note</div>
        <textarea value={note} onChange={e => setNote(e.target.value)}
          rows={3} placeholder="Brief description of what happened"
          style={{
            width: "100%", padding: "9px 10px", border: `1px solid ${C.steelXL}`,
            borderRadius: 8, fontSize: 13, color: C.deep, outline: "none",
            fontFamily: "inherit", boxSizing: "border-box", resize: "vertical",
            marginBottom: 10,
          }} />
        {err && (
          <div style={{
            marginBottom: 10, padding: "8px 10px", background: C.errL, color: C.err,
            borderRadius: 8, fontSize: 12, fontWeight: 600,
          }}>{err}</div>
        )}
        <button onClick={submit} disabled={busy} style={{
          width: "100%", padding: 12, background: busy ? C.steelL : C.deep, color: C.white,
          border: "none", borderRadius: 12, fontSize: 13, fontWeight: 800,
          cursor: busy ? "wait" : "pointer",
        }}>{busy ? "Logging..." : "Log incident"}</button>
      </div>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ROUTE PLANNER — "Plan My Day"
// ═══════════════════════════════════════════════════════════════════════
// Haversine distance (miles) between two {lat,lng} points.
/* [distance-fix-2026-09-20] Null-safe wrapper. ssHaversineMiles returns 0 for
   missing input, which is exactly how "0 mi away" got onto all 90 rows in the
   first place — calling it naively reproduces the same lie through a new code
   path. A distance that cannot be computed is null, never zero. */
function ssJobMiles(origin, job) {
  if (!origin || origin.lat == null || origin.lng == null) return null;
  if (!job || job.lat == null || job.lng == null) return null;
  const n = ssHaversineMiles(origin, { lat: Number(job.lat), lng: Number(job.lng) });
  return isFinite(n) ? n : null;
}

/* [money-fmt-2026-09-21] Currency, formatted once. Three sites rendered
   `${job.pay}.00`, which is correct only when the payout happens to be a whole
   dollar — a $18.43 job printed "$18.43.00". Whole-dollar jobs are common
   enough ($75, $95, $125) that it went unnoticed. */
function ssMoney(n) {
  const v = Number(n);
  return "$" + (isFinite(v) ? v : 0).toFixed(2);
}

/* Display form. Same convention the rest of the file uses (ssFcMiles). */
function ssFmtMiles(n) {
  if (n == null || !isFinite(n)) return null;
  return (n < 10 ? n.toFixed(1) : String(Math.round(n))) + " mi";
}

/* [server-zone-30-2026-10-18] The server's admin-set home base (users.home_lat/home_lng/
   home_address), or null. The backend shows available jobs only within SS_ZONE_MILES of it. */
const SS_ZONE_MILES = 30;
function ssHomeBase(user) {
  if (!user) return null;
  const rawLat = user.home_lat != null ? user.home_lat : user.homeLat;
  const rawLng = user.home_lng != null ? user.home_lng : user.homeLng;
  if (rawLat == null || rawLng == null || rawLat === "" || rawLng === "") return null;
  const lat = Number(rawLat), lng = Number(rawLng);
  if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) return null;
  const address = String(user.home_address || user.homeAddress || "").trim();
  return { lat, lng, address };
}

/* [distance-fix-2026-09-20] Where we measure FROM. Deliberately stable rather
   than pinpoint: a number that ticks every second while driving is unreadable
   at a glance, and re-rendering a 90-row list at 1Hz janks the scroll.
     1. a LIVE fix, but only while genuinely fresh (SS_FC_STALE_MS = 25s)
     2. else the persisted coverage centre — which is also what the backend's
        own radius filter measures against, so the number the operator sees
        agrees with the reason the job is in their list at all
     3. else one ad-hoc fix on mount
     4. else null -> every consumer renders a dash
   Updates only when the origin has moved more than a quarter mile, so the list
   stays visually still.
   [server-zone-30-2026-10-18] 0. the admin-set HOME BASE wins whenever it is set: it is what
   the backend's 30-mile filter measures against, so "x mi" matches why a job is listed.
   Steps 1-4 above only apply to a server with no home base. */
function useDistanceOrigin(user) {
  const [origin, setOrigin] = useState(null);
  const originRef = useRef(null);

  const offer = useCallback((next) => {
    if (!next || next.lat == null || next.lng == null) return;
    const prev = originRef.current;
    if (prev && ssHaversineMiles(prev, next) < 0.25) return;
    originRef.current = next;
    setOrigin(next);
  }, []);

  // Seed from the coverage centre immediately — this is the browsing-from-home
  // case, where no watcher is running and there is no live fix to be had.
  useEffect(() => {
    const lat = Number(user && (user.coverageLat != null ? user.coverageLat : user.coverage_lat));
    const lng = Number(user && (user.coverageLng != null ? user.coverageLng : user.coverage_lng));
    if (isFinite(lat) && isFinite(lng) && (lat !== 0 || lng !== 0)) {
      if (!originRef.current) { originRef.current = { lat: lat, lng: lng }; setOrigin({ lat: lat, lng: lng }); }
    }
  }, [user && user.coverageLat, user && user.coverageLng]);

  // Opportunistic upgrade from the shared motion bus. No new watcher is started
  // here — this only listens to one another surface already runs.
  useEffect(() => {
    const un = ssSubscribeMotion(() => {
      try {
        if (SS_FC_MOTION.lat == null || !SS_FC_MOTION.ts) return;
        if (Date.now() - SS_FC_MOTION.ts > SS_FC_STALE_MS) return;
        offer({ lat: SS_FC_MOTION.lat, lng: SS_FC_MOTION.lng });
      } catch (_) {}
    });
    return un;
  }, [offer]);

  // One ad-hoc fix on mount, so a server with no coverage centre set still gets
  // real distances without waiting for a driving surface to open.
  useEffect(() => {
    const geo = (typeof navigator !== "undefined" && navigator.geolocation) || null;
    if (!geo || typeof geo.getCurrentPosition !== "function") return;
    let dead = false;
    try {
      geo.getCurrentPosition(
        (p) => {
          if (dead) return;
          const lat = Number(p && p.coords && p.coords.latitude);
          const lng = Number(p && p.coords && p.coords.longitude);
          if (isFinite(lat) && isFinite(lng)) offer({ lat: lat, lng: lng });
        },
        () => {},
        { maximumAge: 60000, timeout: 8000, enableHighAccuracy: false }
      );
    } catch (_) {}
    return () => { dead = true; };
  }, [offer]);

  // [server-zone-30-2026-10-18] Home base first (stable object while the coords are unchanged).
  const hb = ssHomeBase(user);
  const hbLat = hb ? hb.lat : null, hbLng = hb ? hb.lng : null;
  const homeOrigin = React.useMemo(() => (hbLat != null && hbLng != null ? { lat: hbLat, lng: hbLng } : null), [hbLat, hbLng]);
  return homeOrigin || origin;
}

function ssHaversineMiles(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return 0;
  const R = 3958.7613; // earth radius, miles
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/* [route-order-2026-09-21] The planned stop order, persisted.
   RoutePlanner held its order in component state, so closing the planner threw
   it away — and Field Command took whatever order /api/jobs returned
   (created_at DESC). A server could plan a seven-stop route and the dash would
   pager them in a completely unrelated sequence.
   localStorage rather than the server: the planner is used in a parking lot on
   one device, and the order has to survive a dead zone. The backend already
   receives ordered_stop_ids on mileage-session start, so nothing here replaces
   a record of account. */
const SS_ROUTE_ORDER_KEY = "ss_route_order";

function ssReadRouteOrder() {
  try {
    const raw = localStorage.getItem(SS_ROUTE_ORDER_KEY);
    if (!raw) return [];
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p.map(String) : [];
  } catch (_) { return []; }
}

function ssSaveRouteOrder(ids) {
  const list = Array.isArray(ids) ? ids.map(String) : [];
  try { localStorage.setItem(SS_ROUTE_ORDER_KEY, JSON.stringify(list)); } catch (_) {}
  // Same-tab notification — the `storage` event only fires in OTHER tabs, and
  // the planner and the dash live in one.
  try { window.dispatchEvent(new CustomEvent("ss-route-order", { detail: list })); } catch (_) {}
}

// Nearest-neighbor TSP heuristic. Starts at `origin`, greedily picks the
// closest unvisited stop. Returns ordered ids.
function ssNearestNeighborOrder(origin, points) {
  const remaining = points.slice();
  const order = [];
  let cur = origin;
  while (remaining.length) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = ssHaversineMiles(cur, remaining[i]);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    const next = remaining.splice(bestIdx, 1)[0];
    order.push(next.id);
    cur = next;
  }
  return order;
}

/* [pro-disabled-2026-10-06] Individual-server Pro subscription: OFF. Montana, 2026-10-06 — Pro was blocking
   servers from claiming jobs and complicating App Store review, so it is disconnected until
   a deliberate decision to charge for it again. While false: no PRO badge, no Plan & billing
   entry anywhere, no upgrade sheet, mileage tracking open to every server, and the instant-
   payout notice carries no Pro line. The backend switch is PRO_SUBSCRIPTIONS_ENABLED in
   Railway — turn both on together. Agency plans are a separate product, unaffected. */
const SS_PRO_ENABLED = false;

// Detect premium tier from whatever signal the backend exposes. We probe several
// common shapes so this keeps working as the field is finalized.
function ssIsPremium(user) {
  if (!user) return false;
  const tier = String(user.tier || user.plan || user.subscription_tier || "").toLowerCase();
  // [tier-collapse-2026-09-20] "premium" stays in this check deliberately. Premium is
  // retired and no longer sold, but grandfathered subscribers still carry a stored
  // tier of "premium" until the backend migrates them to "pro". Dropping it here
  // would strip access from users who are still being billed for it.
  if (tier === "pro" || tier === "premium" || tier === "plus") return true;
  if (user.priority_access === true) return true;
  if (user.is_pro === true || user.isPro === true) return true;
  /* [pro-override-2026-09-21] Manual grant from the admin portal, independent of
     Stripe. Staff and comped accounts need the Pro features (mileage tracking,
     priority access, no instant-payout fee) without a subscription, and an
     entitlement that can ONLY come from a payment processor is one the owner
     cannot administer. Checked before any billing state so a revoked or lapsed
     subscription never strips a deliberate grant. */
  if (user.pro_override === true || user.proOverride === true) return true;
  if (user.subscription && String(user.subscription.tier || user.subscription.plan || "").toLowerCase() === "pro") return true;
  return false;
}

// Resolve the user's avatar image URL from whichever field the backend
// populates (it derives the avatar from the apply-step selfie). Returns null
// when none is present so callers fall back to initials.
function ssAvatarUrl(user) {
  if (!user) return null;
  const u = user.avatar_url || user.selfie_url || user.photo_url || user.avatar || user.photo || null;
  return (typeof u === "string" && u.trim()) ? u : null;
}

// Circular avatar: renders an <img> when the user has a photo, else the gold
// initials chip. `size` controls diameter; `fontSize` the initials size.
function Avatar({ user, size = 44, fontSize = 16 }) {
  const url = ssAvatarUrl(user);
  const initials = ((user?.name || "S").split(" ").map(s => s[0]).join("").slice(0, 2)).toUpperCase();
  const [broken, setBroken] = React.useState(false);
  if (url && !broken) {
    return (
      <img
        src={url}
        alt={user?.name || "Avatar"}
        onError={() => setBroken(true)}
        style={{
          width: size, height: size, borderRadius: "50%", objectFit: "cover",
          flexShrink: 0, border: `1.5px solid ${C.goldM}`, background: C.steelXL,
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize, fontWeight: 800, color: C.deep, flexShrink: 0,
    }}>{initials}</div>
  );
}

// Small gold "PRO" pill shown next to premium users' names.
function ProBadge({ user, style }) {
  if (!SS_PRO_ENABLED || !ssIsPremium(user)) return null; /* [pro-disabled-2026-10-06] */
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 3,
      background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
      color: C.deep, fontSize: 9, fontWeight: 800, letterSpacing: "0.08em",
      padding: "2px 7px", borderRadius: 8, lineHeight: 1.2,
      boxShadow: `0 1px 4px ${C.gold}55`, ...(style || {}),
    }}>
      <Sparkles size={9} strokeWidth={2.6} /> PRO
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ROUTE PLANNER — "Plan My Day"
// Nearest-neighbor route across selected stops, manual reorder, optional
// mileage tracking (premium), and one-tap handoff to Apple Maps.
// ═══════════════════════════════════════════════════════════════════════
function RoutePlanner({ onClose }) {
  const { user, jobs, setRoutePolyline } = React.useContext(AppCtx);

  // Source jobs the planner can consider: anything not completed.
  const candidateJobs = React.useMemo(
    () => (jobs || []).filter(j => j && j.status !== "completed"),
    [jobs]
  );

  // Default selection = all user's CLAIMED (active) jobs.
  const defaultSelectedIds = React.useMemo(() => {
    const claimed = candidateJobs.filter(j => j.status === "active").map(j => j.id);
    return new Set(claimed);
  }, [candidateJobs]);

  const [selectedIds, setSelectedIds] = useState(defaultSelectedIds);
  // Re-seed selection when the upstream candidates change (e.g., a new claim).
  useEffect(() => { setSelectedIds(defaultSelectedIds); /* eslint-disable-next-line */ }, [candidateJobs.map(j => j.id + ":" + j.status).join("|")]);

  // Per-job coords. We re-geocode locally — results are localStorage-cached,
  // so on a warm app this resolves synchronously after the first paint.
  const [coords, setCoords] = useState({});
  useEffect(() => {
    let cancelled = false;
    async function geocodeJob(j) {
      // [claim-map-unmask-2026-07-27] Route unclaimed jobs too — a server plans the
      // day around what they intend to accept, so every job with an address geocodes.
      if (!j || !j.address) return null;
      const cacheKey = `ss_geo_${j.address}|${j.city || ""}|${j.zip || ""}`.toLowerCase();
      const cached = localStorage.getItem(cacheKey);
      if (cached) { try { return JSON.parse(cached); } catch {} }
      // Prefer coords baked into the job record if the API already provided them.
      if (j.lat != null && j.lng != null) return { lat: Number(j.lat), lng: Number(j.lng) };
      const q = [j.address, j.city, j.zip].filter(Boolean).join(", ");
      try {
        const r = await api.get(`/api/geocode?q=${encodeURIComponent(q)}`);
        if (Array.isArray(r) && r.length && r[0].lat) {
          const c = { lat: parseFloat(r[0].lat), lng: parseFloat(r[0].lon) };
          localStorage.setItem(cacheKey, JSON.stringify(c));
          return c;
        }
      } catch (e) { console.warn("[routeplanner geocode]", j.id, e.message); }
      return null;
    }
    (async () => {
      for (const j of candidateJobs) {
        if (coords[j.id]) continue;
        const c = await geocodeJob(j);
        if (cancelled) return;
        if (c) setCoords(prev => ({ ...prev, [j.id]: c }));
        await new Promise(r => setTimeout(r, 200));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateJobs, coords]);

  // User's current GPS — used as the route origin.
  const [origin, setOrigin] = useState(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => { if (!cancelled) setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
      () => { /* permission denied — origin stays null, route still works from first stop */ },
      { maximumAge: 60000, timeout: 8000, enableHighAccuracy: false }
    );
    return () => { cancelled = true; };
  }, []);

  // Ordering mode: "auto" runs nearest-neighbor; "custom" preserves manual order.
  const [orderMode, setOrderMode] = useState("auto");
  const [customOrder, setCustomOrder] = useState([]); // array of job ids

  // Resolve the active ordered list of jobs.
  const orderedJobs = React.useMemo(() => {
    const pool = candidateJobs.filter(j => selectedIds.has(j.id));
    if (pool.length === 0) return [];
    if (orderMode === "custom" && customOrder.length) {
      const byId = new Map(pool.map(j => [j.id, j]));
      const fromCustom = customOrder.map(id => byId.get(id)).filter(Boolean);
      // Append any newly-selected stops that aren't in customOrder yet.
      for (const j of pool) if (!customOrder.includes(j.id)) fromCustom.push(j);
      return fromCustom;
    }
    // Auto: nearest-neighbor from origin (or from first stop if no GPS).
    const withCoords = pool.map(j => ({ id: j.id, ...(coords[j.id] || {}) })).filter(p => p.lat != null);
    const withoutCoords = pool.filter(j => !coords[j.id]);
    if (withCoords.length === 0) return pool; // can't optimize without coords yet
    const startFrom = origin || { lat: withCoords[0].lat, lng: withCoords[0].lng };
    const orderIds = ssNearestNeighborOrder(startFrom, withCoords);
    const byId = new Map(pool.map(j => [j.id, j]));
    return [...orderIds.map(id => byId.get(id)).filter(Boolean), ...withoutCoords];
  }, [candidateJobs, selectedIds, coords, origin, orderMode, customOrder]);

  /* [route-order-2026-09-21] Publish the order so Field Command pages the stops
     in the sequence that was actually planned. Writes on every change — manual
     reorder, re-optimize, selection change — so the dash can never be following
     a stale route the operator already abandoned. */
  useEffect(() => {
    ssSaveRouteOrder(orderedJobs.map((j) => j.id));
  }, [orderedJobs]);

  // Totals: drive miles via haversine + on-site time per stop. 25mph average.
  const totals = React.useMemo(() => {
    let miles = 0;
    let prev = origin;
    for (const j of orderedJobs) {
      const c = coords[j.id];
      if (prev && c) miles += ssHaversineMiles(prev, c);
      if (c) prev = c;
    }
    const driveMin = Math.round((miles / 25) * 60);
    const onSiteMin = orderedJobs.length * 18;
    const totalMin = driveMin + onSiteMin;
    const earn = orderedJobs.reduce((s, j) => s + (Number(j.pay) || 0), 0);
    return { miles: miles.toFixed(1), driveMin, totalMin, earn };
  }, [orderedJobs, coords, origin]);

  // ── Selection / reorder handlers ─────────────────────────────────────
  function toggleStop(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    // Selection change invalidates manual order.
    if (orderMode === "custom") {
      setCustomOrder(prev => prev.filter(x => x !== id));
    }
  }
  function moveStop(idx, delta) {
    const ids = orderedJobs.map(j => j.id);
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= ids.length) return;
    const next = ids.slice();
    const [moved] = next.splice(idx, 1);
    next.splice(newIdx, 0, moved);
    setCustomOrder(next);
    setOrderMode("custom");
  }
  function reoptimize() {
    setCustomOrder([]);
    setOrderMode("auto");
  }

  // ── Mileage tracking ─────────────────────────────────────────────────
  // Two-layer architecture:
  //   * React state (trackPoints) drives the live "tracking active — X.XX mi"
  //     banner so it ticks smoothly while the watch fires.
  //   * A localStorage buffer (ss_mileage_buffer) accumulates un-uploaded
  //     points and survives backgrounding. Every 30s we POST the buffer to
  //     /api/mileage/sessions/:id/ping and clear it on success; failed pings
  //     retain the buffer (capped at 7200 entries / ~2hr) for retry.
  // Total miles is computed from the live React buffer so the UI is responsive
  // even if the ping endpoint is down.
  const DISCLAIMER_KEY = "ss_mileage_disclaimer_v1";
  const isPremium = ssIsPremium(user);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [trackPoints, setTrackPoints] = useState([]);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(SS_MILEAGE_SESSION_KEY) || null; } catch { return null; }
  });
  const [trackingError, setTrackingError] = useState("");
  const [summary, setSummary] = useState(null); // { miles, minutes, points }
  const watchIdRef = useRef(null);
  const pingTimerRef = useRef(null);
  const bufferRef = useRef(ssLoadMileageBuffer()); // un-uploaded points
  const totalMilesRef = useRef(0);                  // running total for ping

  function appendToBuffer(point) {
    const next = bufferRef.current.concat([point]);
    if (next.length > SS_MILEAGE_MAX_BUFFER) {
      // 2-hour cap reached — drop oldest entries so we don't OOM localStorage.
      bufferRef.current = next.slice(next.length - SS_MILEAGE_MAX_BUFFER);
    } else {
      bufferRef.current = next;
    }
    ssSaveMileageBuffer(bufferRef.current);
  }

  async function flushBuffer() {
    const sid = sessionId;
    if (!sid) return;
    const pending = bufferRef.current.slice();
    if (pending.length === 0) return;
    try {
      await api.post("/api/mileage/sessions/" + encodeURIComponent(sid) + "/ping", {
        points: pending.map(p => ({
          lat: p.lat,
          lng: p.lng,
          accuracy_m: p.acc != null ? p.acc : null,
          captured_at: new Date(p.t || Date.now()).toISOString(),
        })),
        total_miles: totalMilesRef.current,
      });
      // Success — drop the points we just sent. New points may have arrived
      // mid-flight; keep anything appended after our snapshot.
      const sent = new Set(pending.map(p => p.t + ":" + p.lat + ":" + p.lng));
      bufferRef.current = bufferRef.current.filter(
        p => !sent.has(p.t + ":" + p.lat + ":" + p.lng)
      );
      ssSaveMileageBuffer(bufferRef.current);
      setTrackingError("");
    } catch (e) {
      // Retain buffer for next interval. Surface a friendly inline notice
      // so the user knows logging is queued, not lost.
      console.warn("[mileage] ping failed:", e && e.message);
      setTrackingError("Mileage upload queued — retrying...");
    }
  }

  async function startTracking() {
    if (!navigator.geolocation) {
      setTrackingError("Location services unavailable on this device.");
      return;
    }
    setTrackingError("");
    setTrackPoints([]);
    bufferRef.current = [];
    ssClearMileageBuffer();
    totalMilesRef.current = 0;

    // 1. Persist the session start. If the backend is offline we still let
    // tracking continue locally — points buffer until /start succeeds via the
    // retry below, then ping flushes start the next cycle.
    let sid = null;
    try {
      const r = await api.post("/api/mileage/sessions/start", {
        route_id: null,
        started_at: new Date().toISOString(),
        ordered_stop_ids: orderedJobs.map(j => j.id),
      });
      if (r && r.session_id != null) sid = String(r.session_id);
      if (r && r.unavailable) {
        setTrackingError("Mileage log unavailable — tracking locally only.");
      }
    } catch (e) {
      console.warn("[mileage] start failed:", e && e.message);
      setTrackingError("Could not save session start — tracking locally only.");
    }
    if (sid) {
      setSessionId(sid);
      try { localStorage.setItem(SS_MILEAGE_SESSION_KEY, sid); } catch (_) {}
    }

    setTracking(true);

    // 2. Wire the geo watch — append to both the React buffer (for live UI)
    // and the persistent buffer (for the next ping flush).
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const point = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          t: Date.now(),
          acc: pos.coords.accuracy || 0,
        };
        /* [field-command-2026-09-11] pos.coords.speed (m/s) and .heading (deg) were
           being discarded here — this watch is the only continuous position source
           the app runs. Publish them to the motion bus so the Field Command dash's
           motion lockout reads real device speed rather than re-deriving it from
           successive fixes. Mileage bookkeeping below is untouched. */
        try {
          ssPublishMotion({
            lat: point.lat,
            lng: point.lng,
            accuracy: point.acc,
            speed: (pos.coords && pos.coords.speed != null) ? Number(pos.coords.speed) : null,
            heading: (pos.coords && pos.coords.heading != null) ? Number(pos.coords.heading) : null,
            ts: point.t,
          });
        } catch (_) { /* never let the motion bus break mileage tracking */ }
        setTrackPoints(prev => {
          const next = prev.concat([point]);
          if (next.length >= 2) {
            // Update the running miles total used by ping payloads.
            const last = next[next.length - 1];
            const prevPt = next[next.length - 2];
            totalMilesRef.current += ssHaversineMiles(prevPt, last);
          }
          return next;
        });
        appendToBuffer(point);
      },
      (err) => console.warn("[mileage] watch error:", err && err.message),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );

    // 3. Kick off the 30s ping loop. We schedule via setInterval so the
    // cadence is independent of point arrival rate.
    if (pingTimerRef.current) clearInterval(pingTimerRef.current);
    pingTimerRef.current = setInterval(() => { flushBuffer(); }, SS_MILEAGE_PING_INTERVAL_MS);
  }

  async function stopTracking() {
    if (watchIdRef.current != null && navigator.geolocation) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch (_) {}
    }
    watchIdRef.current = null;
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
    setTracking(false);

    // Final flush so the closing POST has the latest points server-side too.
    try { await flushBuffer(); } catch (_) {}

    const sid = sessionId;
    const finalMiles = totalMilesRef.current;
    const finalPoints = trackPoints.length;
    const startTs = trackPoints[0] && trackPoints[0].t;
    const durationMinutes = startTs ? Math.max(0, Math.round((Date.now() - startTs) / 60000)) : 0;

    if (sid) {
      try {
        await api.post("/api/mileage/sessions/" + encodeURIComponent(sid) + "/end", {
          ended_at: new Date().toISOString(),
          total_miles: finalMiles,
          total_points: finalPoints,
        });
      } catch (e) {
        console.warn("[mileage] end failed:", e && e.message);
      }
    }
    // Clear local session bookkeeping regardless — the buffer either flushed
    // or remains as orphaned points (cleared below).
    try { localStorage.removeItem(SS_MILEAGE_SESSION_KEY); } catch (_) {}
    ssClearMileageBuffer();
    setSessionId(null);
    bufferRef.current = [];

    // Show "Tracked X.XX mi over Y minutes" summary unless this was a
    // never-started cleanup (e.g. unmount with no points captured).
    if (finalMiles > 0 || finalPoints > 0) {
      setSummary({
        miles: finalMiles,
        minutes: durationMinutes,
        points: finalPoints,
      });
    }
  }

  // Cleanup on unmount: stop the geo watch + ping timer. We intentionally
  // do NOT call the full stopTracking() (which posts to /end) because the
  // user may just be navigating between tabs — the persistent session id
  // + buffer survive in localStorage and resume when RoutePlanner remounts.
  // NB: the original effect had `[]` deps, which captured a stale flushBuffer
  // closure and silently no-op'd when `sessionId` was set after the first mount.
  // We now stash the live flushBuffer in a ref so cleanup always calls the
  // up-to-date version (which reads the latest sessionId + bufferRef contents).
  const flushBufferRef = useRef(flushBuffer);
  useEffect(() => { flushBufferRef.current = flushBuffer; });
  useEffect(() => () => {
    // Fire-and-forget last flush so any points still in the local buffer
    // make it to the server before we tear down the watch + ping timer.
    try { flushBufferRef.current().catch(() => {}); } catch (_) {}
    if (watchIdRef.current != null && navigator.geolocation) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch (_) {}
    }
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  const trackedMiles = React.useMemo(() => {
    if (trackPoints.length < 2) return 0;
    let m = 0;
    for (let i = 1; i < trackPoints.length; i++) m += ssHaversineMiles(trackPoints[i - 1], trackPoints[i]);
    return m;
  }, [trackPoints]);

  function onTrackButtonPress() {
    if (SS_PRO_ENABLED && !isPremium) { if (!ssNoPurchase()) setUpsellOpen(true); return; } // [ios-no-purchase-2026-10-05] [pro-disabled-2026-10-06] mileage open to every server
    if (tracking) { stopTracking(); return; }
    const agreed = (() => { try { return localStorage.getItem(DISCLAIMER_KEY) === "agreed"; } catch { return false; } })();
    if (!agreed) { setDisclaimerOpen(true); return; }
    startTracking();
  }

  // ── In-progress session recovery ─────────────────────────────────────
  // RoutePlanner persists sessionId + buffer to localStorage so a tab-switch
  // doesn't lose the trip. But we don't auto-resume the watch — that gets
  // gnarly fast (geo permission re-prompt, timer state, race with /end).
  // Instead, when we mount and find a stale sessionId without active tracking,
  // surface a banner so the user can either Stop & Save or Discard.
  const hasOrphanSession = !!sessionId && !tracking;
  async function stopAndSavePersisted() {
    const sid = sessionId;
    if (!sid) return;
    // Best-effort final flush before close so any queued points are recorded.
    try { await flushBuffer(); } catch (_) {}
    try {
      await api.post("/api/mileage/sessions/" + encodeURIComponent(sid) + "/end", {
        ended_at: new Date().toISOString(),
        total_miles: totalMilesRef.current || 0,
        total_points: bufferRef.current ? bufferRef.current.length : 0,
      });
    } catch (e) {
      console.warn("[mileage] orphan end failed:", e && e.message);
    }
    try { localStorage.removeItem(SS_MILEAGE_SESSION_KEY); } catch (_) {}
    ssClearMileageBuffer();
    bufferRef.current = [];
    setSessionId(null);
  }
  function discardPersisted() {
    try { localStorage.removeItem(SS_MILEAGE_SESSION_KEY); } catch (_) {}
    ssClearMileageBuffer();
    bufferRef.current = [];
    setSessionId(null);
  }
  function acceptDisclaimer() {
    try { localStorage.setItem(DISCLAIMER_KEY, "agreed"); } catch (_) {}
    setDisclaimerOpen(false);
    startTracking();
  }

  // Build an ordered {lat,lng} array of stops that have geocoded coords. Used
  // by both "Show on map" and "Open in Apple Maps" — both confirm the route.
  function orderedStopCoords() {
    return orderedJobs
      .map(j => coords[j.id])
      .filter(c => c && c.lat != null && c.lng != null)
      .map(c => ({ lat: c.lat, lng: c.lng }));
  }

  // Publish the ordered stops to AppCtx so MapView can draw a gold polyline +
  // numbered markers. Closes the planner — the user lands back on the Map tab
  // with the route overlay visible.
  function showOnMap() {
    const stops = orderedStopCoords();
    if (stops.length < 2) return;
    setRoutePolyline(origin ? [origin, ...stops] : stops);
    onClose();
  }

  // ── Apple Maps handoff ───────────────────────────────────────────────
  function openInAppleMaps() {
    const stops = orderedStopCoords();
    if (stops.length === 0) return;
    // Publish the polyline so when the user returns from Apple Maps they see
    // the same route highlighted on the in-app map.
    setRoutePolyline(stops);
    const stopsStr = stops.map(c => `${c.lat},${c.lng}`);
    const saddr = origin ? `${origin.lat},${origin.lng}` : "";
    const daddrParts = stopsStr.join("+to:");
    const url = saddr
      ? `https://maps.apple.com/?saddr=${saddr}&daddr=${daddrParts}`
      : `https://maps.apple.com/?daddr=${daddrParts}`;
    onClose();
    /* [field-command-2026-09-11] Hand off in a NEW context. `window.location.href`
       navigated the web app itself to maps.apple.com, which lets iOS evict the
       WebView — the server comes back from Maps to a cold boot with their planned
       route gone. window.open is called straight out of this click handler so it is
       not treated as a popup; if it is blocked anyway we fall back to the old
       navigation rather than leaving the button dead. */
    let opened = null;
    try { opened = window.open(url, "_blank", "noopener,noreferrer"); } catch (_) { opened = null; }
    if (!opened) { try { window.location.href = url; } catch (_) {} }
  }

  // ── Empty state: fewer than 2 claimed jobs ───────────────────────────
  const claimedCount = candidateJobs.filter(j => j.status === "active").length;
  if (claimedCount < 2) {
    return (
      <Sheet onClose={onClose}>
        <div style={{
          padding: "18px 16px 14px",
          background: `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
          color: C.white,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.goldL, letterSpacing: "0.1em" }}>PLAN MY DAY</div>
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>Route Planner</div>
            </div>
            <button onClick={onClose} style={iconBtn}><X size={20} color={C.white} /></button>
          </div>
        </div>
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          padding: "32px 28px", textAlign: "center", color: C.steel,
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: 36, background: C.steelXXL,
            display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18,
          }}>
            <Route size={32} color={C.steel} />
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.deep, marginBottom: 8 }}>
            Plan My Day needs at least 2 stops.
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.5, maxWidth: 280 }}>
            Claim jobs from the Map or List view first, then come back here to build an optimized route.
          </div>
        </div>
      </Sheet>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────
  return (
    <Sheet onClose={onClose}>
      {/* Header */}
      <div style={{
        padding: "18px 16px 14px",
        background: `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
        color: C.white,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.goldL, letterSpacing: "0.1em" }}>PLAN MY DAY</div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>{orderedJobs.length}-stop route</div>
          </div>
          <button onClick={onClose} style={iconBtn}><X size={20} color={C.white} /></button>
        </div>

        {/* Stats strip */}
        <div style={{
          display: "flex", justifyContent: "space-between", padding: "10px 12px",
          background: "rgba(255,255,255,0.08)", borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.1)",
        }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, opacity: 0.6, letterSpacing: "0.1em" }}>STOPS</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>{orderedJobs.length}</div>
          </div>
          <div style={{ width: 1, background: "rgba(255,255,255,0.1)" }} />
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, opacity: 0.6, letterSpacing: "0.1em" }}>DRIVE</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>
              {Math.floor(totals.driveMin / 60)}h {totals.driveMin % 60}m
            </div>
          </div>
          <div style={{ width: 1, background: "rgba(255,255,255,0.1)" }} />
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, opacity: 0.6, letterSpacing: "0.1em" }}>MILES</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>{totals.miles}</div>
          </div>
          <div style={{ width: 1, background: "rgba(255,255,255,0.1)" }} />
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, opacity: 0.6, letterSpacing: "0.1em" }}>EARN</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2, color: C.gold }}>${totals.earn}</div>
          </div>
        </div>

        {/* Mode controls */}
        <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "5px 10px", borderRadius: 14,
            background: orderMode === "custom" ? "rgba(255,255,255,0.18)" : "rgba(201,168,76,0.22)",
            border: `1px solid ${orderMode === "custom" ? "rgba(255,255,255,0.25)" : "rgba(201,168,76,0.5)"}`,
            fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
            color: orderMode === "custom" ? C.white : C.goldL,
          }}>
            {orderMode === "custom" ? "MANUAL ORDER" : "OPTIMIZED"}
          </div>
          <button onClick={reoptimize} style={{
            padding: "6px 12px", background: "rgba(255,255,255,0.15)",
            color: C.white, border: "1px solid rgba(255,255,255,0.3)", borderRadius: 18,
            fontSize: 11, fontWeight: 700, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 5,
          }}>
            <RotateCcw size={11} /> Re-optimize
          </button>
        </div>

        {/* Tracking banner */}
        {tracking && (
          <div style={{
            marginTop: 10, padding: "8px 12px", borderRadius: 10,
            background: "rgba(45,138,78,0.18)", border: "1px solid rgba(45,138,78,0.45)",
            display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, background: C.ok, boxShadow: `0 0 0 4px rgba(45,138,78,0.3)` }} />
            Tracking active — {trackedMiles.toFixed(2)} mi, {orderedJobs.length} stops
          </div>
        )}
        {trackingError && (
          <div style={{
            marginTop: 8, padding: "8px 12px", borderRadius: 10,
            background: "rgba(212,135,14,0.18)", border: "1px solid rgba(212,135,14,0.45)",
            fontSize: 11, fontWeight: 600, color: C.white,
          }}>
            {trackingError}
          </div>
        )}
      </div>

      {/* In-progress session banner — surfaces an orphaned sessionId so the
          user can close it out or throw it away. Real resume isn't supported
          yet; this is the safe handoff. */}
      {hasOrphanSession && (
        <div style={{
          margin: "10px 12px 0",
          padding: "10px 12px",
          borderRadius: 10,
          background: C.goldXL,
          border: `1px solid ${C.gold}`,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <Route size={16} color={C.deep} />
          <div style={{ flex: 1, fontSize: 12, fontWeight: 600, color: C.deep }}>
            You have an in-progress mileage session.
          </div>
          <button onClick={stopAndSavePersisted} style={{
            padding: "6px 10px", background: C.deep, color: C.white,
            border: "none", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer",
          }}>Stop &amp; Save</button>
          <button onClick={discardPersisted} style={{
            padding: "6px 10px", background: "transparent", color: C.deep,
            border: `1px solid ${C.steel}`, borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer",
          }}>Discard</button>
        </div>
      )}

      {/* Scrollable body */}
      <div style={{ flex: 1, overflow: "auto", padding: "14px 12px 220px" }}>
        {/* Selection section */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 4px 8px",
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>
            SELECT STOPS
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.deep }}>
            {selectedIds.size} of {candidateJobs.length} selected
          </div>
        </div>
        <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.steelXL}`, marginBottom: 16, overflow: "hidden" }}>
          {candidateJobs.map((j, i) => {
            const checked = selectedIds.has(j.id);
            const dx = jobDisplay(j);
            return (
              <button
                key={j.id}
                onClick={() => toggleStop(j.id)}
                style={{
                  display: "flex", width: "100%", alignItems: "center", gap: 10,
                  padding: "10px 12px",
                  background: "transparent", border: "none", textAlign: "left", cursor: "pointer",
                  borderTop: i === 0 ? "none" : `1px solid ${C.steelXXL}`,
                }}>
                <div style={{
                  width: 22, height: 22, borderRadius: 6,
                  background: checked ? C.deep : C.white,
                  border: `2px solid ${checked ? C.deep : C.steelL}`,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  {checked && <Check size={14} color={C.white} strokeWidth={3} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* [claim-map-unmask-2026-07-27] real servee name pre-claim */}
                  <div style={{
                    fontSize: 13, fontWeight: 700, color: C.deep,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{dx.name}</div>
                  <div style={{ fontSize: 11, color: C.steel, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {dx.streetCity}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
                  <PriorityPill priority={j.priority} />
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.ok }}>{ssPayLabel(j.pay)}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Ordered route list */}
        <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", padding: "0 4px 8px" }}>
          {orderMode === "custom" ? "MANUAL ORDER" : "OPTIMIZED ORDER"}
        </div>
        {orderedJobs.length === 0 && (
          <div style={{
            padding: 20, textAlign: "center", color: C.steel, fontSize: 12,
            background: C.white, borderRadius: 12, border: `1px dashed ${C.steelL}`,
          }}>
            Pick at least one stop above to build a route.
          </div>
        )}
        {orderedJobs.map((j, i) => {
          const dx = jobDisplay(j);
          return (
            <div key={j.id} style={{ display: "flex", gap: 10, marginBottom: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 6 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: "50%",
                  background: ssIsPriorityJob(j) ? C.err : C.deep,
                  color: C.white, fontSize: 11, fontWeight: 800,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: `2px solid ${C.white}`, boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                }}>{i + 1}</div>
                {i < orderedJobs.length - 1 && (
                  <div style={{ width: 2, height: 32, background: C.steelL, marginTop: 4 }} />
                )}
              </div>
              <div style={{
                flex: 1, background: C.white, borderRadius: 12, padding: "10px 12px",
                border: `1px solid ${C.steelXL}`,
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                    {/* [claim-map-unmask-2026-07-27] real servee name pre-claim */}
                    <div style={{
                      fontSize: 13, fontWeight: 800, color: C.deep,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 6,
                    }}>{dx.name}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: C.ok }}>{ssPayLabel(j.pay)}</div>
                  </div>
                  <div style={{ fontSize: 11, color: C.steel, marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {dx.streetCity}
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 10, color: C.steel }}>
                    <PriorityPill priority={j.priority} />
                    {j.dist != null && <span>{ssFmtMiles(Number(j.dist)) || "—"}</span>/* [distance-round-2026-10-08] */}
                    {j.dist != null && <span>·</span>}
                    <span>~18 min on site</span>
                  </div>
                </div>
                {/* Chevrons used in lieu of drag — script narrates "tap to reorder". Touch-drag was descoped to keep RoutePlanner sheet-portable. */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <button
                    onClick={() => moveStop(i, -1)}
                    disabled={i === 0}
                    aria-label="Move stop up"
                    style={{
                      width: 30, height: 30, borderRadius: 8,
                      background: i === 0 ? C.steelXXL : C.bg,
                      border: `1px solid ${C.steelXL}`, cursor: i === 0 ? "default" : "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      opacity: i === 0 ? 0.4 : 1, padding: 0,
                    }}>
                    <ChevronUp size={16} color={C.deep} />
                  </button>
                  <button
                    onClick={() => moveStop(i, 1)}
                    disabled={i === orderedJobs.length - 1}
                    aria-label="Move stop down"
                    style={{
                      width: 30, height: 30, borderRadius: 8,
                      background: i === orderedJobs.length - 1 ? C.steelXXL : C.bg,
                      border: `1px solid ${C.steelXL}`, cursor: i === orderedJobs.length - 1 ? "default" : "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      opacity: i === orderedJobs.length - 1 ? 0.4 : 1, padding: 0,
                    }}>
                    <ChevronDown size={16} color={C.deep} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Sticky CTAs */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, padding: "10px 14px 14px",
        background: C.white, borderTop: `1px solid ${C.steelXL}`,
        display: "flex", flexDirection: "column", gap: 8,
      }}>
        <button
          onClick={onTrackButtonPress}
          style={{
            width: "100%", padding: 13,
            background: isPremium
              ? (tracking
                  ? `linear-gradient(135deg, ${C.err} 0%, #8e2a20 100%)`
                  : `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`)
              : C.steelXXL,
            color: isPremium ? (tracking ? C.white : C.deep) : C.steel,
            border: isPremium ? "none" : `1px solid ${C.steelL}`,
            borderRadius: 14, fontSize: 14, fontWeight: 800, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            boxShadow: isPremium && !tracking ? `0 4px 12px ${C.gold}55` : "none",
          }}>
          {!isPremium && <Lock size={16} />}
          {isPremium && !tracking && <Sparkles size={16} />}
          {isPremium && tracking && <Circle size={16} fill={C.white} />}
          {!isPremium
            ? (ssNoPurchase() ? "Track mileage (Pro feature)" : "Track mileage (Pro feature — upgrade to enable)")
            : (tracking
                ? `Stop tracking  ·  ${trackedMiles.toFixed(2)} mi`
                : "Track mileage for this route")}
        </button>
        <button
          onClick={showOnMap}
          disabled={orderedStopCoords().length < 2}
          style={{
            width: "100%", padding: 13,
            background: C.white,
            color: C.deep,
            border: `1.5px solid ${C.gold}`,
            borderRadius: 14, fontSize: 14, fontWeight: 800,
            cursor: orderedStopCoords().length < 2 ? "default" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            opacity: orderedStopCoords().length < 2 ? 0.5 : 1,
          }}>
          <MapIcon size={18} color={C.deep} /> Show route on map
        </button>
        <button
          onClick={openInAppleMaps}
          disabled={orderedJobs.length === 0}
          style={{
            width: "100%", padding: 14,
            background: orderedJobs.length === 0
              ? C.steelXXL
              : `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
            color: orderedJobs.length === 0 ? C.steel : C.white,
            border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800,
            cursor: orderedJobs.length === 0 ? "default" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
          <Navigation size={18} /> Open route in Apple Maps
        </button>
      </div>

      {/* Disclaimer modal */}
      {disclaimerOpen && (
        <Sheet onClose={() => setDisclaimerOpen(false)} small>
          <div style={{ padding: "16px 18px 8px" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.deep, marginBottom: 8 }}>
              Mileage Tracking — Tax Disclaimer
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.55, color: C.ink }}>
              Mileage tracked through this feature is for personal record-keeping only.
              StrikeServe does not provide tax advice. You are solely responsible for the
              accuracy of your records and consultation with a qualified tax professional.
              Mileage logs are stored on your device only and are not shared with
              StrikeServe, clients, or the IRS. By continuing you accept these terms.
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, padding: "14px 18px 18px" }}>
            <button
              onClick={() => setDisclaimerOpen(false)}
              style={{
                flex: 1, padding: 12, background: C.bg, color: C.deep,
                border: `1px solid ${C.steelL}`, borderRadius: 12,
                fontSize: 13, fontWeight: 800, cursor: "pointer",
              }}>
              Cancel
            </button>
            <button
              onClick={acceptDisclaimer}
              style={{
                flex: 1.4, padding: 12,
                background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
                color: C.deep, border: "none", borderRadius: 12,
                fontSize: 13, fontWeight: 800, cursor: "pointer",
                boxShadow: `0 4px 12px ${C.gold}55`,
              }}>
              I Agree, Start Tracking
            </button>
          </div>
        </Sheet>
      )}

      {/* Upsell modal */}
      {SS_PRO_ENABLED && upsellOpen && !ssNoPurchase() && ( /* [ios-no-purchase-2026-10-05] [pro-disabled-2026-10-06] */
        <Sheet onClose={() => setUpsellOpen(false)} small>
          <div style={{ padding: "18px 18px 8px", textAlign: "center" }}>
            <div style={{
              width: 56, height: 56, borderRadius: 28, margin: "0 auto 12px",
              background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 6px 18px ${C.gold}55`,
            }}>
              <Sparkles size={26} color={C.deep} />
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, marginBottom: 6 }}>
              Upgrade to Pro
            </div>
            {/* [tier-collapse-2026-09-20] Price reads from SS_SERVER_PLANS instead of a
                hardcoded literal, and free instant payouts are named as a Pro benefit. */}
            <div style={{ fontSize: 13, lineHeight: 1.5, color: C.ink, maxWidth: 320, margin: "0 auto" }}>
              Unlock route mileage tracking, priority job access, free instant payouts (no 1.5% fee), and tax-ready PDFs for {SS_SERVER_PLANS[0].price.replace("/mo", "/month")}. You'll be redirected to strikeserve.com to manage your subscription — no in-app purchase.
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, padding: "14px 18px 18px" }}>
            <button
              onClick={() => setUpsellOpen(false)}
              style={{
                flex: 1, padding: 12, background: C.bg, color: C.deep,
                border: `1px solid ${C.steelL}`, borderRadius: 12,
                fontSize: 13, fontWeight: 800, cursor: "pointer",
              }}>
              Not now
            </button>
            <button
              onClick={() => { setUpsellOpen(false); ssStartCheckout("pro"); }}
              style={{
                flex: 1.4, padding: 12,
                background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
                color: C.deep, border: "none", borderRadius: 12,
                fontSize: 13, fontWeight: 800, cursor: "pointer",
                boxShadow: `0 4px 12px ${C.gold}55`,
              }}>
              Upgrade
            </button>
          </div>
        </Sheet>
      )}

      {/* Post-tracking summary modal — confirms we wrote the session to
          the user's Mileage Log and links straight to it. */}
      {summary && (
        <Sheet onClose={() => setSummary(null)} small>
          <div style={{ padding: "20px 18px 8px", textAlign: "center" }}>
            <div style={{
              width: 56, height: 56, borderRadius: 28, margin: "0 auto 12px",
              background: `linear-gradient(135deg, ${C.ok} 0%, #1f6f3c 100%)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 6px 18px ${C.ok}55`,
            }}>
              <CheckCircle2 size={28} color={C.white} />
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, marginBottom: 6 }}>
              Tracked {Number(summary.miles).toFixed(2)} mi over {summary.minutes} minutes.
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.5, color: C.ink, maxWidth: 320, margin: "0 auto" }}>
              Saved to your Mileage Log. Mileage logs are for personal tax record-keeping. StrikeServe does not provide tax advice.
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, padding: "14px 18px 18px" }}>
            <button
              onClick={() => setSummary(null)}
              style={{
                flex: 1, padding: 12, background: C.bg, color: C.deep,
                border: `1px solid ${C.steelL}`, borderRadius: 12,
                fontSize: 13, fontWeight: 800, cursor: "pointer",
              }}>
              Done
            </button>
            <button
              onClick={() => {
                setSummary(null);
                // Cross-component bridge → AppInner picks this up and opens
                // the mileage drawer screen.
                try { window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: "mileage" })); } catch (_) {}
                onClose && onClose();
              }}
              style={{
                flex: 1.4, padding: 12,
                background: `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
                color: C.white, border: "none", borderRadius: 12,
                fontSize: 13, fontWeight: 800, cursor: "pointer",
              }}>
              View Mileage Log
            </button>
          </div>
        </Sheet>
      )}
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// [onboarding-wizard-2026-06-23] POST-APPROVAL ONBOARDING WIZARD
// Mirrors the web build's OnboardingWizard (cle-server-web server-app.jsx):
// 6 steps — agreement → training → payout → signature → profile → w9.
// Reuses this native app's existing Stripe Connect (Account tab), signature +
// profile editing (ProfileScreen / "profile" drawer), service areas
// (CoverageScreen / "coverage" drawer) and TrainingScreen ("training" drawer)
// for those steps; builds the agreement + W-9 steps inline.
// Toasts use the native `ss-toast` event ({ text, kind: "ok"|"err" }); the
// training step opens the drawer via the native `ss-open-drawer-screen` event.
// ═══════════════════════════════════════════════════════════════════════
// [cert-of-completion-2026-07-03] StrikeServe "Certificate of Completion" — native build.
// In-app branded view; Save/Print opens the public print page in Safari (SFSafariViewController)
// where iOS provides native Print (AirPrint) + Save-PDF/Share.
const SS_CERT_LOGO = "data:image/png;base64,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";
const SS_CERT_SHIELD = "data:image/png;base64,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";
const SS_CERT_SEAL1 = "M110.0 0.0 L118.4 18.4 L130.3 2.0 L135.0 21.7 L149.8 7.9 L150.5 28.2 L167.8 17.5 L164.6 37.5 L183.5 30.5 L176.5 49.4 L196.5 46.2 L185.8 63.5 L206.1 64.2 L192.3 79.0 L212.0 83.7 L195.6 95.6 L214.0 104.0 L195.6 112.4 L212.0 124.3 L192.3 129.0 L206.1 143.8 L185.8 144.5 L196.5 161.8 L176.5 158.6 L183.5 177.5 L164.6 170.5 L167.8 190.5 L150.5 179.8 L149.8 200.1 L135.0 186.3 L130.3 206.0 L118.4 189.6 L110.0 208.0 L101.6 189.6 L89.7 206.0 L85.0 186.3 L70.2 200.1 L69.5 179.8 L52.2 190.5 L55.4 170.5 L36.5 177.5 L43.5 158.6 L23.5 161.8 L34.2 144.5 L13.9 143.8 L27.7 129.0 L8.0 124.3 L24.4 112.4 L6.0 104.0 L24.4 95.6 L8.0 83.7 L27.7 79.0 L13.9 64.2 L34.2 63.5 L23.5 46.2 L43.5 49.4 L36.5 30.5 L55.4 37.5 L52.2 17.5 L69.5 28.2 L70.2 7.9 L85.0 21.7 L89.7 2.0 L101.6 18.4 Z";
const SS_CERT_SEAL2 = "M120.2 0.5 L126.8 19.7 L140.2 4.5 L142.9 24.5 L159.0 12.3 L157.8 32.5 L176.0 23.6 L170.8 43.2 L190.4 38.0 L181.5 56.2 L201.7 55.0 L189.5 71.1 L209.5 73.8 L194.3 87.2 L213.5 93.8 L196.0 104.0 L213.5 114.2 L194.3 120.8 L209.5 134.2 L189.5 136.9 L201.7 153.0 L181.5 151.8 L190.4 170.0 L170.8 164.8 L176.0 184.4 L157.8 175.5 L159.0 195.7 L142.9 183.5 L140.2 203.5 L126.8 188.3 L120.2 207.5 L110.0 190.0 L99.8 207.5 L93.2 188.3 L79.8 203.5 L77.1 183.5 L61.0 195.7 L62.2 175.5 L44.0 184.4 L49.2 164.8 L29.6 170.0 L38.5 151.8 L18.3 153.0 L30.5 136.9 L10.5 134.2 L25.7 120.8 L6.5 114.2 L24.0 104.0 L6.5 93.8 L25.7 87.2 L10.5 73.8 L30.5 71.1 L18.3 55.0 L38.5 56.2 L29.6 38.0 L49.2 43.2 L44.0 23.6 L62.2 32.5 L61.0 12.3 L77.1 24.5 L79.8 4.5 L93.2 19.7 L99.8 0.5 L110.0 18.0 Z";
const SS_STATE_NAMES = { AL:"Alabama", AK:"Alaska", AZ:"Arizona", AR:"Arkansas", CA:"California", CO:"Colorado", CT:"Connecticut", DE:"Delaware", FL:"Florida", GA:"Georgia", HI:"Hawaii", ID:"Idaho", IL:"Illinois", IN:"Indiana", IA:"Iowa", KS:"Kansas", KY:"Kentucky", LA:"Louisiana", ME:"Maine", MD:"Maryland", MA:"Massachusetts", MI:"Michigan", MN:"Minnesota", MS:"Mississippi", MO:"Missouri", MT:"Montana", NE:"Nebraska", NV:"Nevada", NH:"New Hampshire", NJ:"New Jersey", NM:"New Mexico", NY:"New York", NC:"North Carolina", ND:"North Dakota", OH:"Ohio", OK:"Oklahoma", OR:"Oregon", PA:"Pennsylvania", RI:"Rhode Island", SC:"South Carolina", SD:"South Dakota", TN:"Tennessee", TX:"Texas", UT:"Utah", VT:"Vermont", VA:"Virginia", WA:"Washington", WV:"West Virginia", WI:"Wisconsin", WY:"Wyoming", DC:"District of Columbia" };
function ssCertStateName(code){ if(!code) return "California"; const k=String(code).trim().toUpperCase(); if(SS_STATE_NAMES[k]) return SS_STATE_NAMES[k]; return String(code).length>2?String(code):"California"; }
let _ssOnbCompletedAt=null;
function ssCertFmtDate(v){ if(!v) return ""; try{ const d=new Date(v); if(isNaN(d.getTime())) return ""; return d.toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"}); }catch(_){ return ""; } }
function ssCertData(user){ const u=user||{}; const serverName=(u.name||u.fullName||"").trim()||"Process Server"; const completionDate=ssCertFmtDate(u.onboardingCompletedAt||u.onboarding_completed_at||_ssOnbCompletedAt)||new Date().toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"}); const jurisdiction=ssCertStateName(u.state); const signatory="Montana Hull, C.E.O."; const signName=signatory.split(",")[0].trim(); return {serverName,completionDate,jurisdiction,signatory,signName}; }
function ssCertEsc(s){ return String(s==null?"":s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;"); }
function ssCertInnerHTML(V){
  const corner='<path d="M4 4 C46 4 74 8 74 4 M4 4 C4 46 8 74 4 74 M12 12 C40 12 58 24 58 12 M12 12 C12 40 24 58 12 58" stroke="#c9a84c" stroke-width="1.4" stroke-linecap="round"></path><circle cx="12" cy="12" r="3" fill="#c9a84c"></circle><path d="M20 20 C34 20 44 30 44 44" stroke="#1a3a7a" stroke-width="1" opacity="0.5"></path>';
  return ''
    + '<div style="position:absolute;inset:20px;border:2.5px solid #1a3a7a;pointer-events:none;"></div>'
    + '<div style="position:absolute;inset:26px;border:1px solid #c9a84c;pointer-events:none;"></div>'
    + '<div style="position:absolute;inset:34px;border:1px solid #b9c7df;pointer-events:none;"></div>'
    + '<svg style="position:absolute;top:26px;left:26px;pointer-events:none;" width="96" height="96" viewBox="0 0 100 100" fill="none">' + corner + '</svg>'
    + '<svg style="position:absolute;top:26px;right:26px;pointer-events:none;transform:scaleX(-1);" width="96" height="96" viewBox="0 0 100 100" fill="none">' + corner + '</svg>'
    + '<svg style="position:absolute;bottom:26px;left:26px;pointer-events:none;transform:scaleY(-1);" width="96" height="96" viewBox="0 0 100 100" fill="none">' + corner + '</svg>'
    + '<svg style="position:absolute;bottom:26px;right:26px;pointer-events:none;transform:scale(-1,-1);" width="96" height="96" viewBox="0 0 100 100" fill="none">' + corner + '</svg>'
    + '<img src="' + SS_CERT_SHIELD + '" alt="" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-46%);pointer-events:none;opacity:0.05;width:430px;height:auto;">'
    + '<div style="position:relative;height:100%;display:flex;flex-direction:column;align-items:center;text-align:center;padding:62px 96px;box-sizing:border-box;">'
    +   '<img src="' + SS_CERT_LOGO + '" alt="StrikeServe" style="height:124px;width:auto;display:block;">'
    +   '<div style="display:flex;align-items:center;gap:16px;margin-top:20px;"><span style="width:40px;height:1.5px;background:#c9a84c;display:block;"></span><span style="font-family:\'Inter\',sans-serif;font-weight:600;font-size:12px;letter-spacing:0.36em;color:#c9a84c;text-transform:uppercase;">Certificate of Completion</span><span style="width:40px;height:1.5px;background:#c9a84c;display:block;"></span></div>'
    +   '<div style="flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;">'
    +     '<div style="font-family:\'Source Serif 4\',serif;font-weight:700;font-style:italic;font-size:46px;line-height:1;color:#1a3a7a;letter-spacing:-0.01em;">Congratulations</div>'
    +     '<div style="font-family:\'Source Serif 4\',serif;font-style:italic;font-size:18px;color:#6a5a34;margin-top:14px;">This certificate is proudly presented to</div>'
    +     '<div style="font-family:\'Source Serif 4\',serif;font-weight:700;font-size:54px;line-height:1.04;color:#15233f;letter-spacing:-0.01em;margin-top:8px;">' + ssCertEsc(V.serverName) + '</div>'
    +     '<div style="display:flex;align-items:center;gap:10px;margin:16px 0 22px;"><span style="width:70px;height:1.5px;background:#c9a84c;"></span><span style="width:7px;height:7px;background:#c9a84c;transform:rotate(45deg);"></span><span style="width:70px;height:1.5px;background:#c9a84c;"></span></div>'
    +     '<p style="font-family:\'Source Serif 4\',serif;font-size:17.5px;line-height:1.7;color:#3b4a6b;max-width:660px;margin:0;">in recognition of having successfully completed the <strong style="font-weight:600;color:#15233f;">StrikeServe Server Onboarding</strong> program.</p>'
    +   '</div>'
    +   '<div style="display:flex;align-items:flex-end;justify-content:space-between;width:100%;gap:24px;">'
    +     '<div style="flex:1;display:flex;flex-direction:column;align-items:center;"><div style="font-family:\'Source Serif 4\',serif;font-size:21px;color:#15233f;margin-bottom:8px;">' + ssCertEsc(V.completionDate) + '</div><div style="width:212px;height:1.5px;background:#1a3a7a;"></div><div style="font-family:\'Inter\',sans-serif;font-size:10.5px;font-weight:600;letter-spacing:0.16em;text-transform:uppercase;color:#8a8d93;margin-top:8px;">Date of completion</div></div>'
    +     '<div style="flex:0 0 auto;position:relative;display:flex;justify-content:center;margin-bottom:-6px;">'
    +       '<svg width="168" height="192" viewBox="0 0 220 250">'
    +         '<path d="M86 150 L74 244 L100 224 L110 246 L120 224 L146 244 L134 150 Z" fill="#1a3a7a"></path><path d="M90 152 L82 232 L100 218 L100 152 Z" fill="#0f2860"></path><path d="M130 152 L138 232 L120 218 L120 152 Z" fill="#0f2860"></path>'
    +         '<path d="' + SS_CERT_SEAL1 + '" fill="#c9a84c" opacity="0.9"></path><path d="' + SS_CERT_SEAL2 + '" fill="#e8d48b" opacity="0.55"></path>'
    +         '<circle cx="110" cy="104" r="88" fill="#c9a84c"></circle><circle cx="110" cy="104" r="84" fill="#faf6ea"></circle><circle cx="110" cy="104" r="83" fill="none" stroke="#c9a84c" stroke-width="4" stroke-dasharray="1.5 5.5"></circle><circle cx="110" cy="104" r="74" fill="#1a3a7a"></circle><circle cx="110" cy="104" r="60" fill="#faf6ea"></circle><circle cx="110" cy="104" r="60" fill="none" stroke="#c9a84c" stroke-width="1"></circle>'
    +         '<defs><path id="ssSealTop" d="M110 104 m -67 0 a 67 67 0 1 1 134 0" fill="none"></path><path id="ssSealBot" d="M110 104 m 67 0 a 67 67 0 1 1 -134 0" fill="none"></path></defs>'
    +         '<text><textPath href="#ssSealTop" startOffset="50%" text-anchor="middle" style="font-family:\'Inter\',sans-serif;font-size:11px;font-weight:700;letter-spacing:2.6px;fill:#e8d48b;">STRIKESERVE CERTIFIED</textPath></text>'
    +         '<text><textPath href="#ssSealBot" startOffset="50%" text-anchor="middle" style="font-family:\'Inter\',sans-serif;font-size:9px;font-weight:600;letter-spacing:2px;fill:#e8d48b;">★ ONBOARDING TRAINING ★</textPath></text>'
    +       '</svg>'
    +       '<img src="' + SS_CERT_SHIELD + '" alt="" style="position:absolute;top:80px;left:84px;transform:translate(-50%,-50%);width:52px;height:auto;pointer-events:none;">'
    +     '</div>'
    +     '<div style="flex:1;display:flex;flex-direction:column;align-items:center;"><div style="font-family:\'Alex Brush\',cursive;font-size:48px;color:#1a3a7a;margin-bottom:-2px;line-height:1;">' + ssCertEsc(V.signName) + '</div><div style="width:212px;height:1.5px;background:#1a3a7a;"></div><div style="font-family:\'Inter\',sans-serif;font-size:10.5px;font-weight:600;letter-spacing:0.16em;text-transform:uppercase;color:#8a8d93;margin-top:8px;">' + ssCertEsc(V.signatory) + '</div></div>'
    +   '</div>'
    + '</div>';
}
function StrikeServeCertificate({ user, onClose }){
  const V = ssCertData(user||{});
  const [scale,setScale] = useState(1);
  useEffect(()=>{ const fit=()=>setScale(Math.min(1,(window.innerWidth-24)/1056)); fit(); window.addEventListener("resize",fit); return()=>window.removeEventListener("resize",fit); },[]);
  useEffect(()=>{ if(document.getElementById("ss-cert-fonts"))return; const l=document.createElement("link"); l.id="ss-cert-fonts"; l.rel="stylesheet"; l.href="https://fonts.googleapis.com/css2?family=Alex+Brush&family=Inter:wght@400;500;600;700&family=Source+Serif+4:ital,wght@0,400;0,600;0,700;1,400;1,700&display=swap"; document.head.appendChild(l); },[]);
  const openPrint=()=>{ const q=new URLSearchParams({cert:"1",name:V.serverName,date:V.completionDate,state:V.jurisdiction,sig:V.signatory}); openExternal("https://app.strikeserve.com/?"+q.toString()); };
  return (
    <div style={{position:"fixed",inset:0,zIndex:100000,background:"#eef0f4",display:"flex",flexDirection:"column",overflow:"auto"}}>
      <div style={{position:"sticky",top:0,zIndex:2,display:"flex",gap:10,alignItems:"center",justifyContent:"center",paddingTop:"calc(env(safe-area-inset-top, 44px) + 12px)",paddingRight:12,paddingBottom:12,paddingLeft:12,background:"rgba(255,255,255,.94)",boxShadow:"0 1px 6px rgba(15,40,96,.12)"}}>
        <button onClick={openPrint} style={{fontFamily:"'Inter',sans-serif",fontWeight:700,fontSize:13,padding:"10px 18px",borderRadius:10,border:"none",cursor:"pointer",background:"#1a3a7a",color:"#fff"}}>Save / Print</button>
        <button onClick={onClose} style={{fontFamily:"'Inter',sans-serif",fontWeight:700,fontSize:13,padding:"10px 16px",borderRadius:10,border:"none",cursor:"pointer",background:"#e8eaed",color:"#0f2860"}}>Close</button>
      </div>
      <div style={{flex:1,display:"flex",justifyContent:"center",alignItems:"flex-start",padding:"20px 12px"}}>
        <div style={{width:Math.round(1056*scale),height:Math.round(816*scale)}}>
          <div style={{width:1056,height:816,transformOrigin:"top left",transform:`scale(${scale})`}}>
            <div style={{position:"relative",width:1056,height:816,background:"#faf8f4",boxShadow:"0 8px 24px rgba(15,40,96,.12)",overflow:"hidden",boxSizing:"border-box",fontFamily:"'Inter',sans-serif"}} dangerouslySetInnerHTML={{__html: ssCertInnerHTML(V)}} />
          </div>
        </div>
      </div>
    </div>
  );
}

function OnboardingWizard({ user, locked = false, goToAccount, onDone }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [agreeBusy, setAgreeBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [agreePay, setAgreePay] = useState(false);
  // [payout-direct-2026-07-01] Busy flag while minting the Stripe Connect onboarding link.
  const [payoutBusy, setPayoutBusy] = useState(false);

  function _toast(text, kind) {
    try { window.dispatchEvent(new CustomEvent("ss-toast", { detail: { text, kind: kind || "ok" } })); } catch (_) {}
  }

  // Step catalog — order mirrors the backend nextStep ordering.
  const STEPS = [
    { key: "agreement", icon: Shield,        title: "Independent Contractor Agreement", sub: "Review and accept the StrikeServe terms." },
    { key: "training",  icon: GraduationCap, title: "Complete Required Training",        sub: "Finish the required onboarding videos." },
    { key: "payout",    icon: DollarSign,    title: "Set Up Payouts",                    sub: "Connect your bank via Stripe to get paid." },
    { key: "signature", icon: PenLine,       title: "Add Your Signature",                sub: "Used to sign affidavits of service." },
    { key: "profile",   icon: MapPin,        title: "Set Your Service Areas",            sub: "Tell us where you serve." },
    { key: "w9",        icon: FileText,      title: "Complete Form W-9",                 sub: "Required for 1099 contractor payments." },
  ];

  const fetchStatus = useCallback(async () => {
    setErr(null);
    try {
      const s = await api.get("/api/onboarding/status");
      if (!s) { setErr("Could not load your onboarding status. Please try again."); setLoading(false); return null; }
      setStatus(s);
      // Broadcast so the app-level gate refreshes (true → unlock instantly).
      try { window.dispatchEvent(new CustomEvent("cle-onboarding-updated", { detail: { onboardingComplete: !!s.onboardingComplete } })); } catch (_) {}
      return s;
    } catch (e) {
      setErr((e && e.message) || "Could not load your onboarding status.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  // Refetch when any step (incl. signature/profile saved on the Account tab,
  // or the W-9 submit) dispatches the shared event without a boolean payload.
  useEffect(() => {
    const onUpd = (e) => { if (!(e && e.detail && typeof e.detail.onboardingComplete === "boolean")) fetchStatus(); };
    window.addEventListener("cle-onboarding-updated", onUpd);
    return () => window.removeEventListener("cle-onboarding-updated", onUpd);
  }, [fetchStatus]);

  const steps = (status && status.steps) ? status.steps : {};
  const isDone = (k) => !!(steps[k] && steps[k].complete);
  const doneCount = STEPS.filter(s => isDone(s.key)).length;
  const currentKey = (status && status.nextStep)
    || (STEPS.find(s => !isDone(s.key)) || {}).key
    || null;

  async function acceptAgreement() {
    setAgreeBusy(true);
    try {
      const s = await api.post("/api/onboarding/agreement", { version: "v2" });
      if (s && s.steps) {
        setStatus(s);
        try { window.dispatchEvent(new CustomEvent("cle-onboarding-updated", { detail: { onboardingComplete: !!s.onboardingComplete } })); } catch (_) {}
        _toast("Agreement accepted", "ok");
      } else {
        await fetchStatus();
      }
    } catch (e) {
      _toast((e && e.body && e.body.error) || (e && e.message) || "Could not record your acceptance.", "err");
    } finally {
      setAgreeBusy(false);
    }
  }

  // [onboarding-fixes-2026-07-01] "I've done this" now reports what changed instead of
  // silently refetching (the old behavior looked like a dead button when the backend
  // still saw the step as incomplete).
  async function refreshStatus() {
    const prevNext = status && status.nextStep;
    const s = await fetchStatus();
    if (!s) { _toast("Could not refresh your onboarding status — please try again", "err"); return; }
    if (s.onboardingComplete) { _toast("All steps complete — you can now claim jobs", "ok"); return; }
    if (prevNext && s.nextStep !== prevNext) {
      const nxt = STEPS.find(x => x.key === s.nextStep);
      _toast("Step complete — next up: " + ((nxt && nxt.title) || s.nextStep), "ok");
    } else {
      _toast("This step still shows incomplete — finish it, then tap again", "err");
    }
  }

  // [payout-direct-2026-07-01] Send the server straight into Stripe-hosted Connect
  // onboarding (POST /api/connect/onboard → accountLinks URL) instead of parking
  // them on the Account/settings tab. Opens in SFSafariViewController via
  // openExternal; falls back to the Account tab on error.
  async function startPayoutSetup() {
    setPayoutBusy(true);
    try {
      const r = await api.post("/api/connect/onboard", {});
      if (r && r.url) { openExternal(r.url); return; }
      _toast((r && r.error) || "Could not start Stripe setup — opening your Account tab", "err");
      if (goToAccount) goToAccount(null);
    } catch (e) {
      _toast((e && e.body && e.body.error) || (e && e.message) || "Could not start Stripe setup — opening your Account tab", "err");
      if (goToAccount) goToAccount(null);
    } finally {
      setPayoutBusy(false);
    }
  }

  const card = { background: C.white, borderRadius: 16, border: `1px solid ${C.steelXL}`, padding: 16, marginBottom: 12 };
  const primaryBtn = (extra = {}) => ({
    padding: "11px 16px", background: C.deep, color: C.white, border: "none", borderRadius: 12,
    fontSize: 13, fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, ...extra,
  });
  const ghostBtn = {
    padding: "11px 16px", background: C.white, color: C.deep, border: `1.5px solid ${C.deep}`, borderRadius: 12,
    fontSize: 13, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
  };

  // ── Loading / error / success states ──
  if (loading) {
    return <div style={{ padding: "24px 16px" }}>
      <div style={{ ...card, textAlign: "center", padding: "40px 24px", color: C.steel, fontSize: 13 }}>Loading your onboarding…</div>
    </div>;
  }
  if (err && !status) {
    return <div style={{ padding: "24px 16px" }}>
      <div style={{ ...card, textAlign: "center", padding: "32px 24px", borderLeft: `4px solid ${C.err}` }}>
        <AlertTriangle size={28} color={C.err} />
        <div style={{ fontWeight: 800, fontSize: 15, color: C.deep, margin: "10px 0 6px" }}>Couldn't load onboarding</div>
        <div style={{ fontSize: 12.5, color: C.steel, marginBottom: 16 }}>{err}</div>
        <button style={primaryBtn()} onClick={() => { setLoading(true); fetchStatus(); }}><RotateCcw size={14} /> Try again</button>
      </div>
    </div>;
  }
  if (status && status.onboardingComplete) {
    return <div style={{ padding: "24px 16px" }}>
      <div style={{ ...card, textAlign: "center", padding: "40px 24px" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: C.okL, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <CheckCircle2 size={34} color={C.ok} />
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.deep, marginBottom: 8 }}>You're fully onboarded</div>
        <div style={{ fontSize: 13, color: C.steel, maxWidth: 320, margin: "0 auto 20px", lineHeight: 1.55 }}>
          Every step is complete — you can now claim jobs and start serving with StrikeServe.
        </div>
        <button style={primaryBtn()} onClick={() => onDone && onDone()}>Browse jobs <ArrowRight size={14} /></button>
        {/* [cert-of-completion-2026-07-03] */}
        <button onClick={() => window.dispatchEvent(new CustomEvent("ss-open-certificate"))} style={{ marginTop: 10, width: "100%", padding: "11px 16px", background: C.white, color: C.deep, border: `1px solid ${C.steelXL}`, borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>View your Certificate of Completion</button>
      </div>
    </div>;
  }

  // ── Per-step action (only the current step expands) ──
  const StepAction = ({ stepKey }) => {
    const navToAccount = (label, screen) => (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <button style={primaryBtn()} onClick={() => goToAccount && goToAccount(screen)}>{label} <ArrowRight size={14} /></button>
        <button style={ghostBtn} onClick={refreshStatus}><RotateCcw size={13} /> I've done this</button>
      </div>
    );

    if (stepKey === "agreement") {
      const block = { background: C.bgW, borderRadius: 12, padding: "14px 16px", fontSize: 12.5, color: C.ink, lineHeight: 1.6, marginTop: 12 };
      const head = { fontWeight: 800, color: C.deep, marginBottom: 6, fontSize: 13 };
      const li = { marginBottom: 5 };
      return <div style={{ marginTop: 12 }}>
        <div style={block}>
          <div style={head}>StrikeServe Independent Contractor Agreement</div>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: C.steel, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>Summary</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li style={li}>You serve as an <strong>independent contractor</strong>, not an employee of StrikeServe.</li>
            <li style={li}>You set your own schedule and choose which jobs to claim and complete.</li>
            <li style={li}>You are responsible for your own taxes; payments are reported on a 1099 (hence the W-9 step).</li>
            <li style={li}>You will perform service of process lawfully and per the rules of the relevant jurisdiction.</li>
            <li style={li}>You agree to keep case information confidential and to complete affidavits truthfully.</li>
            <li style={{ marginBottom: 0 }}>Either party may end this relationship at any time, consistent with applicable law.</li>
          </ul>
        </div>
        <div style={block}>
          <div style={head}>Payment &amp; Electronic Payout Authorization</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li style={li}>StrikeServe pays contractors <strong>electronically through Stripe</strong>, our third-party payment processor. You authorize StrikeServe to initiate payouts to the bank account or debit card you connect through Stripe.</li>
            <li style={li}>You will set up and verify your own payout account directly with Stripe. <strong>StrikeServe never sees or stores your full bank account or card numbers</strong> — that information is held by Stripe.</li>
            {/* [tier-collapse-2026-09-20] Added only the final Pro carve-out sentence; the rest of this countersigned clause is unchanged. */}
            <li style={li}>Standard payouts are sent to your connected account at no charge. If you choose an <strong>optional instant payout</strong>, you authorize StrikeServe to deduct a <strong>1.5% instant-payout fee</strong> from that payout to cover processing costs. Standard payouts remain free. If you hold an active <strong>Pro subscription</strong>, StrikeServe covers this instant-payout fee and no deduction is taken from your instant payouts while that subscription is active.</li>
            <li style={li}>You authorize StrikeServe and Stripe to debit or reverse a payout to correct an error, duplicate, or amount sent in error.</li>
            <li style={{ marginBottom: 0 }}>This authorization stays in effect while you are an active StrikeServe contractor. You may revoke it by closing your account or contacting support, which will end your ability to receive payouts.</li>
          </ul>
          <div style={{ marginTop: 10, fontSize: 10.5, color: C.steel, fontStyle: "italic" }}>Your actual bank details are entered and stored only with Stripe, not StrikeServe.</div>
        </div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 9, marginTop: 12, fontSize: 12.5, color: C.ink, cursor: "pointer" }}>
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 2 }} />
          <span>I have read and agree to the StrikeServe Independent Contractor Agreement.</span>
        </label>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 9, marginTop: 8, fontSize: 12.5, color: C.ink, cursor: "pointer" }}>
          <input type="checkbox" checked={agreePay} onChange={e => setAgreePay(e.target.checked)} style={{ marginTop: 2 }} />
          {/* [tier-collapse-2026-09-20] Added only the parenthetical Pro carve-out. */}
          <span>I authorize electronic payouts through Stripe and acknowledge the optional 1.5% instant-payout fee (waived while an active Pro subscription is in effect).</span>
        </label>
        <div style={{ marginTop: 12 }}>
          <button style={primaryBtn({ background: (!agreed || !agreePay || agreeBusy) ? C.steelL : C.deep, cursor: (!agreed || !agreePay || agreeBusy) ? "not-allowed" : "pointer" })}
            disabled={!agreed || !agreePay || agreeBusy} onClick={acceptAgreement}>
            {agreeBusy ? "Recording…" : "I agree"} <Check size={14} />
          </button>
        </div>
      </div>;
    }

    if (stepKey === "training") {
      const t = steps.training || {};
      const total = t.required_total != null ? t.required_total : null;
      const done = t.required_completed != null ? t.required_completed : null;
      return <div style={{ marginTop: 12 }}>
        {total != null && <div style={{ fontSize: 12.5, color: C.steel, marginBottom: 10 }}>{done || 0} of {total} required videos complete.</div>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={primaryBtn()} onClick={() => { try { window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: "training" })); } catch (_) {} }}>Open training <ArrowRight size={14} /></button>
          <button style={ghostBtn} onClick={refreshStatus}><RotateCcw size={13} /> I've done this</button>
        </div>
      </div>;
    }

    if (stepKey === "payout") {
      // [payout-direct-2026-07-01] Straight to Stripe-hosted Connect onboarding —
      // do NOT route to the Account/settings tab (old behavior stranded applicants there).
      return <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <button style={primaryBtn({ opacity: payoutBusy ? 0.7 : 1 })} disabled={payoutBusy} onClick={startPayoutSetup}>{payoutBusy ? "Opening Stripe…" : "Set up payouts"} <ArrowRight size={14} /></button>
        <button style={ghostBtn} onClick={refreshStatus}><RotateCcw size={13} /> I've done this</button>
      </div>;
    }
    if (stepKey === "signature") return navToAccount("Add your signature", "profile");
    if (stepKey === "profile")   return navToAccount("Set your service areas", "coverage");

    if (stepKey === "w9") {
      return <div style={{ marginTop: 12 }}>
        <MobileW9Form user={user} onComplete={(res) => {
          if (res && res.steps) {
            setStatus(res);
            try { window.dispatchEvent(new CustomEvent("cle-onboarding-updated", { detail: { onboardingComplete: !!res.onboardingComplete } })); } catch (_) {}
          } else {
            fetchStatus();
          }
        }} />
      </div>;
    }
    return null;
  };

  // ── Vertical stepper ──
  return <div style={{ padding: "16px 16px 100px" }}>
    {locked && (
      <div style={{ background: C.goldXL, border: `1px solid ${C.goldM}`, borderRadius: 16, padding: "14px 16px", marginBottom: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ width: 40, height: 40, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: C.deep }}>
          <Lock size={18} color={C.white} />
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.deep, lineHeight: 1.3 }}>Your account is locked until you finish onboarding</div>
          <div style={{ fontSize: 12, color: C.steel, marginTop: 4, lineHeight: 1.5 }}>Complete each step so we have your agreement, payout details, signature, and W-9 on file. The full app unlocks the moment you're done.</div>
        </div>
      </div>
    )}

    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: C.deep }}>Finish setting up your account</div>
      <div style={{ fontSize: 12.5, color: C.steel, marginTop: 4 }}>Complete these steps to start claiming jobs on StrikeServe.</div>
    </div>

    {/* Progress */}
    <div style={{ ...card, padding: "14px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontWeight: 800, fontSize: 13, color: C.deep }}>{doneCount} of {STEPS.length} complete</div>
        {err && <button style={{ ...ghostBtn, padding: "6px 10px", fontSize: 11 }} onClick={fetchStatus}><RotateCcw size={12} /> Refresh</button>}
      </div>
      <div style={{ height: 8, borderRadius: 6, background: C.steelXL, overflow: "hidden" }}>
        <div style={{ height: "100%", width: (STEPS.length ? Math.round((doneCount / STEPS.length) * 100) : 0) + "%", background: `linear-gradient(90deg, ${C.deep}, ${C.royal})`, borderRadius: 6, transition: "width .3s" }} />
      </div>
    </div>

    {STEPS.map((s) => {
      const done = isDone(s.key);
      const current = !done && s.key === currentKey;
      const muted = !done && !current;
      const stripe = done ? C.deep : current ? C.royal : C.steelXL;
      const Ico = s.icon;
      return <div key={s.key} style={{ ...card, marginBottom: 12, borderLeft: `4px solid ${stripe}`, opacity: muted ? 0.72 : 1 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
            background: done ? C.deep : current ? C.goldXL : "transparent",
            border: done ? "none" : `1.5px solid ${current ? C.deep : C.steelL}` }}>
            {done ? <Check size={17} color={C.white} /> : <Ico size={16} color={current ? C.deep : C.steel} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: muted ? C.steel : C.deep }}>{s.title}</div>
              {done && <span style={{ fontSize: 10, fontWeight: 800, color: C.ok, background: C.okL, padding: "2px 7px", borderRadius: 6 }}>Done</span>}
              {current && <span style={{ fontSize: 10, fontWeight: 800, color: C.deep, background: C.goldXL, padding: "2px 7px", borderRadius: 6 }}>Current</span>}
            </div>
            <div style={{ fontSize: 12, color: C.steel, marginTop: 2 }}>{s.sub}</div>
            {/* [w9-remount-fix-2026-06-23] Call as a function, NOT <StepAction/>. StepAction is
                defined inside this component, so rendering it as an element gives it a new identity
                every re-render (the 15s/25s background polls trigger these) → React remounts the
                whole step → the W-9 form wipes on a timer. Invoking it returns the JSX inline so
                the form stays mounted and keeps its values. */}
            {current && StepAction({ stepKey: s.key })}
          </div>
        </div>
      </div>;
    })}
  </div>;
}

// IRS federal tax classifications for the W-9 (Part I). Mirrors the web app.
const W9_TAX_CLASSES = [
  { code: "individual_sole_prop", label: "Individual / sole proprietor or single-member LLC" },
  { code: "c_corp",   label: "C corporation" },
  { code: "s_corp",   label: "S corporation" },
  { code: "partnership", label: "Partnership" },
  { code: "trust_estate", label: "Trust / estate" },
  { code: "llc",      label: "Limited liability company" },
  { code: "other",    label: "Other" },
];

// ═══════════════════════════════════════════════════════════════════════
// [onboarding-wizard-2026-06-23] MOBILE FORM W-9
// In-app fillable W-9 → POST /api/onboarding/w9. The backend validates the
// 9-digit TIN and stores ONLY the last 4 server-side. SECURITY: the full TIN
// lives only in component state and is cleared immediately after submit —
// never logged, never persisted client-side.
// ═══════════════════════════════════════════════════════════════════════
// [w9-draft-persist-2026-07-01] Module-scoped in-memory draft so a background poll,
// parent re-render, or component remount can NEVER wipe half-typed W-9 data. Survives
// remounts because it lives outside React; never written to disk (the full TIN stays
// memory-only); cleared on successful submit.
const _w9Draft = { name: null, biz: null, addr: null, city: null, state: null, zip: null, taxClass: null, tinType: null, tin: null, certified: null };
function _w9DraftClear() { Object.keys(_w9Draft).forEach(k => { _w9Draft[k] = null; }); }

function MobileW9Form({ user, onComplete }) {
  // [w9-autofill-fix-2026-06-23] Text fields are UNCONTROLLED (defaultValue + ref) and read
  // from the DOM on submit. iOS WKWebView autofill/prefill sets the field value WITHOUT
  // firing React onChange, so a controlled value={state} read "" even when the field
  // visibly showed the name → "enter your legal name" + nothing saved. Reading refs submits
  // exactly what's shown, regardless of how it got there (typed, prefilled, or autofilled).
  const nameRef = useRef(null);
  const bizRef = useRef(null);
  const addrRef = useRef(null);
  const cityRef = useRef(null);
  const stateRef = useRef(null);
  const zipRef = useRef(null);
  const [taxClass, setTaxClass] = useState(_w9Draft.taxClass ?? "individual_sole_prop");
  const [tinType, setTinType] = useState(_w9Draft.tinType ?? "ssn"); // ssn | ein
  const [tin, setTin] = useState(_w9Draft.tin ?? ""); // FULL digits — memory-only, cleared on submit
  const [certified, setCertified] = useState(_w9Draft.certified ?? false);
  const [submitting, setSubmitting] = useState(false);
  // [w9-draft-persist-2026-07-01] Log mounts so a remount loop is visible in the console.
  useEffect(() => { console.log("[w9] MobileW9Form mounted", new Date().toISOString()); }, []);

  function _toast(text, kind) {
    try { window.dispatchEvent(new CustomEvent("ss-toast", { detail: { text, kind: kind || "ok" } })); } catch (_) {}
  }

  const tinDigits = (tin || "").replace(/\D/g, "").slice(0, 9);
  const formatTin = (d) => {
    if (tinType === "ein") return d.length <= 2 ? d : d.slice(0, 2) + "-" + d.slice(2);
    if (d.length <= 3) return d;
    if (d.length <= 5) return d.slice(0, 3) + "-" + d.slice(3);
    return d.slice(0, 3) + "-" + d.slice(3, 5) + "-" + d.slice(5);
  };

  const inp = {
    width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.steelL}`,
    fontSize: 13, color: C.deep, fontWeight: 600, background: C.white, fontFamily: "inherit",
  };
  const lbl = { fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.06em", marginBottom: 4, textTransform: "uppercase" };
  const fg = { marginBottom: 12 };

  async function submit() {
    // Read LIVE DOM values (handles iOS autofill/prefill that never fired onChange).
    const rv = (r) => (r.current && typeof r.current.value === "string" ? r.current.value : "");
    const ln = rv(nameRef).trim();
    const addr = rv(addrRef).trim();
    const biz = rv(bizRef).trim();
    const cty = rv(cityRef).trim();
    const st = rv(stateRef).trim().toUpperCase().slice(0, 2);
    const zp = rv(zipRef).trim();
    if (!ln) { _toast("Enter your legal name (as shown on your tax return)", "err"); return; }
    if (!addr) { _toast("Enter your address", "err"); return; }
    if (tinDigits.length !== 9) { _toast("Enter your full 9-digit " + (tinType === "ein" ? "EIN" : "SSN"), "err"); return; }
    if (!certified) { _toast("You must certify the W-9 (Part II) to continue", "err"); return; }

    // [w9-fieldmap-fix-2026-06-23] Body keys MUST match POST /api/onboarding/w9, which reads
    // legal_name (NOT name), tin_type, signature, and certified. Sending `name` made the
    // backend see an empty legal_name → "Legal name is required" no matter what was typed.
    const body = {
      legal_name: ln,
      business_name: biz,
      tax_classification: taxClass,
      address: addr,
      city: cty,
      state: st,
      zip: zp,
      tin_type: tinType,
      tin: tinDigits,
      signature: (user && user.signature) || "",
      certified: certified,
    };

    setSubmitting(true);
    try {
      const res = await api.post("/api/onboarding/w9", body);
      // SECURITY: clear the full TIN from state immediately after submit.
      setTin("");
      _w9DraftClear();
      _toast("W-9 submitted — thank you", "ok");
      try { window.dispatchEvent(new CustomEvent("cle-onboarding-updated", { detail: { onboardingComplete: !!(res && res.onboardingComplete) } })); } catch (_) {}
      onComplete && onComplete(res);
    } catch (e) {
      _toast((e && e.body && e.body.error) || (e && e.message) || "Could not submit your W-9. Please try again.", "err");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.steelXL}`, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: C.goldXL, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <FileText size={20} color={C.deep} />
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.deep }}>Form W-9 — Request for Taxpayer ID</div>
          <div style={{ fontSize: 11, color: C.steel }}>Fill this out in the app — required for 1099 contractor payments.</div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "9px 12px", background: C.bgW, borderRadius: 8, fontSize: 11, color: C.ink, margin: "10px 0 16px", lineHeight: 1.5 }}>
        <Lock size={14} color={C.deep} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>Your taxpayer ID is sent securely and never stored in full — we keep only the last 4 digits on file.</span>
      </div>

      <div style={fg}><div style={lbl}>Name (as shown on your income tax return) *</div>
        <input style={inp} ref={nameRef} defaultValue={_w9Draft.name ?? ((user && user.name) || "")} onInput={e => { _w9Draft.name = e.target.value; }} autoComplete="name" placeholder="Full legal name" /></div>
      <div style={fg}><div style={lbl}>Business name / disregarded entity (if different)</div>
        <input style={inp} ref={bizRef} defaultValue={_w9Draft.biz ?? ""} onInput={e => { _w9Draft.biz = e.target.value; }} placeholder="Optional" /></div>

      <div style={fg}><div style={lbl}>Federal tax classification *</div>
        <select style={inp} value={taxClass} onChange={e => { _w9Draft.taxClass = e.target.value; setTaxClass(e.target.value); }}>
          {W9_TAX_CLASSES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select></div>

      {/* [address-picker-sweep-2026-07-28] CONVERT — W-9 street address. Stays
          UNCONTROLLED (defaultValue + ref, read from the DOM at submit) so the iOS
          WKWebView autofill fix [w9-autofill-fix-2026-06-23] is preserved exactly;
          AddressAutocomplete writes the picked value straight into the DOM node and
          keeps updating _w9Draft, so both the ref read and the draft stay correct.
          onAddressParsed pokes the sibling city/state/ZIP refs the same way. */}
      <div style={fg}><div style={lbl}>Address (number, street, apt/suite) *</div>
        <AddressAutocomplete
          style={inp}
          inputRef={addrRef}
          defaultValue={_w9Draft.addr ?? ((user && user.address) || "")}
          onChange={v => { _w9Draft.addr = v; }}
          onAddressParsed={p => {
            const put = (ref, key, val) => {
              if (!val) return;
              if (ref.current) ref.current.value = val;
              _w9Draft[key] = val;
            };
            put(addrRef, "addr", p.street);
            put(cityRef, "city", p.city);
            put(stateRef, "state", p.state ? String(p.state).toUpperCase().slice(0, 2) : "");
            put(zipRef, "zip", p.zip ? String(p.zip).replace(/[^\d-]/g, "").slice(0, 10) : "");
          }}
          autoComplete="street-address" placeholder="Street address" /></div>
      <div style={{ display: "flex", gap: 8, ...fg }}>
        <div style={{ flex: 2 }}><div style={lbl}>City</div><input style={inp} ref={cityRef} defaultValue={_w9Draft.city ?? ((user && user.city) || "")} onInput={e => { _w9Draft.city = e.target.value; }} autoComplete="address-level2" /></div>
        <div style={{ flex: 1 }}><div style={lbl}>State</div><input style={inp} ref={stateRef} defaultValue={_w9Draft.state ?? ((user && user.state) || "")} onInput={e => { _w9Draft.state = e.target.value; }} maxLength={2} placeholder="CA" /></div>
        <div style={{ flex: 1.2 }}><div style={lbl}>ZIP</div><input style={inp} ref={zipRef} defaultValue={_w9Draft.zip ?? ((user && user.zip) || "")} onInput={e => { _w9Draft.zip = e.target.value; }} inputMode="numeric" maxLength={10} placeholder="00000" /></div>
      </div>

      <div style={{ borderTop: `1px solid ${C.steelXL}`, margin: "6px 0 14px", paddingTop: 14 }}>
        <div style={{ ...lbl, marginBottom: 8 }}>Part I — Taxpayer Identification Number (TIN) *</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          {[{ k: "ssn", l: "SSN" }, { k: "ein", l: "EIN" }].map(t =>
            <button key={t.k} onClick={() => { _w9Draft.tinType = t.k; setTinType(t.k); }} style={{
              padding: "7px 16px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
              background: tinType === t.k ? C.deep : C.white, color: tinType === t.k ? C.white : C.deep,
              border: `1px solid ${tinType === t.k ? C.deep : C.steelL}`,
            }}>{t.l}</button>)}
        </div>
        <input style={{ ...inp, maxWidth: 240, fontFamily: "monospace", letterSpacing: 1 }}
          inputMode="numeric" autoComplete="off"
          value={formatTin(tinDigits)}
          onChange={e => { const d = e.target.value.replace(/\D/g, "").slice(0, 9); _w9Draft.tin = d; setTin(d); }}
          placeholder={tinType === "ein" ? "12-3456789" : "123-45-6789"} />
        <div style={{ fontSize: 11, color: C.steel, marginTop: 5 }}>Enter all 9 digits of your {tinType === "ein" ? "Employer Identification Number" : "Social Security Number"}.</div>
      </div>

      <div style={{ borderTop: `1px solid ${C.steelXL}`, paddingTop: 14, marginBottom: 14 }}>
        <div style={{ ...lbl, marginBottom: 8 }}>Part II — Certification</div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", fontSize: 11.5, color: C.ink, lineHeight: 1.55 }}>
          <input type="checkbox" checked={certified} onChange={e => { _w9Draft.certified = e.target.checked; setCertified(e.target.checked); }} style={{ marginTop: 3, flexShrink: 0 }} />
          <span>Under penalties of perjury, I certify that: (1) the number shown on this form is my correct taxpayer identification number; (2) I am not subject to backup withholding because I am exempt, have not been notified by the IRS that I am subject to backup withholding, or the IRS has notified me that I am no longer subject to it; (3) I am a U.S. citizen or other U.S. person; and (4) any FATCA code(s) entered indicating exemption from FATCA reporting is correct. *</span>
        </label>
      </div>

      <button onClick={submit} disabled={submitting} style={{
        width: "100%", padding: 14, background: submitting ? C.steelL : C.deep, color: C.white,
        border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: submitting ? "not-allowed" : "pointer",
      }}>{submitting ? "Submitting…" : "Submit W-9"}</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// DRAWER SCREEN ROUTER
// ═══════════════════════════════════════════════════════════════════════
function DrawerScreen({ screen, onClose }) {
  const titles = {
    profile: "Profile",
    settings: "Settings",
    training: "Training",
    coverage: "Coverage areas",
    documents: "My documents",
    support: "Help & support",
    mileage: "Mileage Log",
    billing: "Plan & billing",
    deleteAccount: "Delete account", // [account-delete-2026-09-30]
    messages: "Messages",            // [server-parity-web-2026-10-05]
    availability: "Availability",    // [server-parity-web-2026-10-05]
    referrals: "Referrals",          // [server-referrals-screen-2026-10-08]
  };

  return (
    <Sheet onClose={onClose}>
      <div style={{ background: C.deep, padding: "16px 16px 14px", color: C.white }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button onClick={onClose} style={iconBtn}>
            <ChevronLeft size={22} color={C.white} />
          </button>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{titles[screen]}</div>
          <div style={{ width: 38 }} />
        </div>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "16px 14px 100px" }}>
        {screen === "training" && <TrainingScreen />}
        {screen === "settings" && <SettingsScreen onClose={onClose} />}
        {screen === "profile" && <ProfileScreen />}
        {screen === "coverage" && <CoverageScreen />}
        {screen === "documents" && <DocumentsScreen />}
        {screen === "support" && <SupportScreen />}
        {screen === "mileage" && <MileageLogScreen />}
        {screen === "billing" && <BillingScreen />}
        {screen === "deleteAccount" && <DeleteAccountPanel onCancel={onClose} />}
        {screen === "messages" && <MessagesScreen />}{/* [server-parity-web-2026-10-05] */}
        {screen === "availability" && <AvailabilityScreen />}{/* [server-parity-web-2026-10-05] */}
        {screen === "referrals" && <ReferralsScreen />}{/* [server-referrals-screen-2026-10-08] */}
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// BILLING / SUBSCRIPTION — shows the server's current plan + status from
// GET /api/subscriptions/my, and drives subscribe (Stripe Checkout) / manage
// (Stripe Customer Portal) in-app. Refetches on app-resume and when the
// in-app browser closes so the displayed plan updates after a Stripe round-trip.
// ─────────────────────────────────────────────────────────────────────────
// Pretty status pill config keyed on the subscription status string.
function ssSubStatusPill(status) {
  switch (String(status || "none").toLowerCase()) {
    case "active":    return { bg: C.okL, color: C.ok, label: "Active", icon: CheckCircle2 };
    case "trialing":  return { bg: C.infoL, color: C.info, label: "Trial", icon: Sparkles };
    case "canceling": return { bg: C.warnL, color: C.warn, label: "Canceling", icon: AlertTriangle };
    case "past_due":  return { bg: C.errL, color: C.err, label: "Past due", icon: AlertCircle };
    case "canceled":  return { bg: C.steelXL, color: C.steel, label: "Canceled", icon: XCircle };
    default:          return { bg: C.steelXL, color: C.steel, label: "No plan", icon: Circle };
  }
}
// Human plan label. Backend stores free/pro/premium.
// [tier-collapse-2026-09-20] "premium" is a retired tier. Grandfathered rows keep
// that stored value until the backend migration rewrites them, but every surface
// must read "Pro": Pro is now the only paid plan and carries the same benefits, so
// showing a retired plan name to a paying subscriber would just be confusing.
function ssPlanLabel(plan) {
  const p = String(plan || "free").toLowerCase();
  if (p === "pro") return "Pro";
  if (p === "premium") return "Pro";
  if (p === "free" || !p) return "Free";
  return p.charAt(0).toUpperCase() + p.slice(1);
}
// Plans the server can subscribe to (matches PLAN_PRICE_MAP on the backend).
// [tier-collapse-2026-09-20] The two paid tiers collapsed into ONE plan: Pro at
// $10/mo now carries every former Premium benefit, and Premium ($25/mo) is retired
// and no longer sold. Feature bullets reflect actual backend gates: free servers
// are capped to jobs ≤ $25 (Pro sees the full pool), Pro gets the 2-minute new-job
// head start, the mileage tracker, and instant payouts with the 1.5% fee waived.
// Grandfathered "premium" rows still render as Pro (ssPlanLabel) and still pass the
// entitlement check (ssIsPremium) until the backend migration runs.
const SS_SERVER_PLANS = [
  {
    id: "pro", name: "Pro", price: "$10/mo", popular: true,
    features: [
      "See every job — no $25 cap",
      "2-minute head start on new jobs",
      "Free instant payouts (no 1.5% fee)",
      "Mileage tracker for deductions",
      "Advanced earnings & tax tools",
    ],
  },
];

function BillingScreen() {
  const [sub, setSub] = React.useState(null);   // null = loading, {} = loaded
  const [err, setErr] = React.useState("");
  const [busy, setBusy] = React.useState(false); // checkout/portal in flight

  const toast = React.useCallback((kind, text) => {
    try { window.dispatchEvent(new CustomEvent("ss-toast", { detail: { kind, text } })); } catch (_) {}
  }, []);

  const load = React.useCallback(async () => {
    try {
      setErr("");
      const r = await api.get("/api/subscriptions/my");
      setSub(r && typeof r === "object" ? r : { plan: "free", status: "none" });
    } catch (e) {
      setErr((e && e.message) ? e.message : "Could not load your plan");
      setSub((prev) => prev || {}); // leave whatever we had; render error banner
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Refetch when the user returns from the Stripe Checkout / Portal page so the
  // displayed plan reflects the change. We listen for BOTH the Browser plugin's
  // browserFinished event (in-app SFSafariViewController dismissed) and the
  // App appStateChange resume (covers _system / external-Safari fallback).
  React.useEffect(() => {
    let alive = true;
    const removers = [];
    const refresh = () => { if (alive) load(); };
    try {
      const P = { Browser: ssPlugin("Browser"), App: ssPlugin("App") }; // [server-parity-web-2026-10-05]
      const attach = (plugin, evt) => {
        if (plugin && typeof plugin.addListener === "function") {
          const h = plugin.addListener(evt, refresh);
          if (h && typeof h.then === "function") h.then((hh) => { if (alive) removers.push(hh); else hh && hh.remove && hh.remove(); });
          else removers.push(h);
        }
      };
      if (P.Browser || P.App) {
        attach(P.Browser, "browserFinished");
        attach(P.App, "appStateChange"); // fires on resume; harmless extra refresh
      }
    } catch (_) { /* plugins missing — manual Refresh button still works */ }
    return () => {
      alive = false;
      removers.forEach((h) => { try { h && h.remove && h.remove(); } catch (_) {} });
    };
  }, [load]);

  async function subscribe(planId) {
    if (busy) return;
    setBusy(true);
    try {
      const ok = await ssStartCheckout(planId);
      if (!ok) toast("info", "Opening plans on strikeserve.com…");
    } catch (_) { toast("err", "Could not start checkout. Try again."); }
    finally { setBusy(false); }
  }
  async function manage() {
    if (busy) return;
    setBusy(true);
    try {
      const ok = await ssOpenBillingPortal();
      if (!ok) toast("info", "Opening billing on strikeserve.com…");
    } catch (_) { toast("err", "Could not open billing. Try again."); }
    finally { setBusy(false); }
  }

  function fmtDate(ts) {
    if (!ts) return null;
    const d = new Date(ts);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }

  // ── Loading ──
  if (sub == null) {
    return <DCard><div style={{ padding: 18, fontSize: 12, color: C.steel }}>Loading your plan…</div></DCard>;
  }

  const status = String(sub.status || "none").toLowerCase();
  const hasPlan = status === "active" || status === "trialing" || status === "canceling" || status === "past_due";
  const planLabel = ssPlanLabel(sub.plan);
  const pill = ssSubStatusPill(status);
  const renewal = fmtDate(sub.current_period_end);
  const trialEnds = fmtDate(sub.trial_ends_at);
  const cancelAt = fmtDate(sub.cancel_at);
  const isCanceling = status === "canceling" || !!sub.cancel_at;

  return (
    <>
      {err && (
        <div style={{
          marginBottom: 12, padding: "10px 12px", borderRadius: 10,
          background: C.errL, color: C.err, fontSize: 12, fontWeight: 600,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        }}>
          <span>{err}</span>
          <button onClick={load} style={{
            background: "none", border: "none", color: C.err, fontWeight: 800,
            fontSize: 12, cursor: "pointer", textDecoration: "underline", padding: 0,
          }}>Retry</button>
        </div>
      )}

      {/* Current plan card */}
      <DCard>
        <div style={{ padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <Award size={18} color={C.gold} />
            <span style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.08em" }}>CURRENT PLAN</span>
            <span style={{ marginLeft: "auto" }}>
              <Pill bg={pill.bg} color={pill.color} icon={pill.icon}>{pill.label}</Pill>
            </span>
          </div>

          <div style={{ fontSize: 22, fontWeight: 800, color: C.deep, letterSpacing: "-0.01em" }}>
            {hasPlan ? `${planLabel} plan` : "No active plan"}
          </div>

          {/* Plan detail rows */}
          {hasPlan ? (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {status === "trialing" && trialEnds && (
                <BillingRow icon={Sparkles} label="Trial ends" value={trialEnds} />
              )}
              {renewal && (
                <BillingRow
                  icon={Calendar}
                  label={isCanceling ? "Access until" : "Renews on"}
                  value={renewal}
                />
              )}
              {isCanceling && (
                <div style={{
                  marginTop: 4, padding: "9px 11px", borderRadius: 10,
                  background: C.warnL, color: C.warn, fontSize: 12, fontWeight: 600,
                  display: "flex", alignItems: "flex-start", gap: 7, lineHeight: 1.4,
                }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    Your subscription is set to cancel{cancelAt ? ` on ${cancelAt}` : " at the end of the current period"}.
                    You keep access until then.{ssNoPurchase() ? "" : " Resubscribe any time from Manage billing."}
                  </span>
                </div>
              )}
            </div>
          ) : (
            /* [tier-collapse-2026-09-20] Was "priority job access and premium features",
               which named nothing concrete and referenced the retired Premium tier. */
            <div style={{ fontSize: 12, color: C.steel, marginTop: 6, lineHeight: 1.5 }}>
              {ssNoPurchase()
                ? "You're on the free plan."
                : <>You're on the free tier. Pro ({SS_SERVER_PLANS[0].price}) unlocks every job with no $25 cap,
              a 2-minute head start on new jobs, free instant payouts (no 1.5% fee),
              the mileage tracker for deductions, and advanced earnings &amp; tax tools.</>}
            </div>
          )}
        </div>
      </DCard>

      {/* Subscribe (no active plan) OR Manage (has a plan). [ios-no-purchase-2026-10-05] hidden on iOS. */}
      {ssNoPurchase() ? null : hasPlan ? (
        <button
          onClick={manage}
          disabled={busy}
          style={{
            width: "100%", padding: 14, marginBottom: 16, cursor: busy ? "default" : "pointer",
            background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 14,
            display: "flex", alignItems: "center", gap: 12, textAlign: "left",
            opacity: busy ? 0.6 : 1,
          }}>
          <CreditCard size={18} color={C.deep} strokeWidth={1.8} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>
              {busy ? "Opening…" : "Manage billing"}
            </div>
            <div style={{ fontSize: 10, color: C.steel, marginTop: 1 }}>
              Update card · view invoices · cancel
            </div>
          </div>
          <ExternalLink size={14} color={C.steel} />
        </button>
      ) : (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", margin: "0 4px 8px" }}>
            CHOOSE A PLAN
          </div>
          {SS_SERVER_PLANS.map((p) => {
            const onDark = p.id === "pro";
            return (
            <button
              key={p.id}
              onClick={() => subscribe(p.id)}
              disabled={busy}
              style={{
                width: "100%", padding: 16, marginBottom: 12, cursor: busy ? "default" : "pointer",
                background: onDark
                  ? `linear-gradient(135deg, ${C.purple} 0%, #5a23c4 100%)`
                  : C.white,
                border: onDark ? "none" : `1px solid ${C.steelXL}`,
                borderRadius: 14, textAlign: "left", color: onDark ? C.white : C.deep,
                opacity: busy ? 0.6 : 1, position: "relative", overflow: "hidden",
                boxShadow: onDark ? "0 6px 18px rgba(124,58,237,0.28)" : "none",
              }}>
              {p.popular && (
                <div style={{
                  position: "absolute", top: 12, right: 12,
                  background: C.goldL, color: C.deep, fontSize: 9, fontWeight: 800,
                  letterSpacing: "0.06em", padding: "3px 8px", borderRadius: 8,
                }}>MOST POPULAR</div>
              )}
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{p.name}</div>
                <div style={{
                  fontSize: 13, fontWeight: 800,
                  color: onDark ? C.goldL : C.deep,
                }}>{p.price}</div>
              </div>
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                {p.features.map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <Check size={13} color={onDark ? C.goldL : C.purple} strokeWidth={3} style={{ flexShrink: 0 }} />
                    <span style={{
                      fontSize: 11.5, lineHeight: 1.35,
                      color: onDark ? "rgba(255,255,255,0.9)" : C.deep,
                    }}>{f}</span>
                  </div>
                ))}
              </div>
              <div style={{
                fontSize: 11, fontWeight: 800, marginTop: 12, letterSpacing: "0.02em",
                display: "flex", alignItems: "center", gap: 5,
                color: onDark ? C.white : C.purple,
              }}>
                {busy ? "Opening checkout…" : "Start 30-day free trial"}
                {!busy && <ArrowRight size={13} />}
              </div>
            </button>
            );
          })}
        </>
      )}

      {/* Refresh + manage shortcut for existing-plan edge cases */}
      <button onClick={load} style={{
        width: "100%", padding: "10px 0", marginTop: 2, background: "none", border: "none",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer",
      }}>
        <RotateCcw size={13} color={C.steel} />
        <span style={{ fontSize: 12, fontWeight: 600, color: C.steel }}>Refresh plan status</span>
      </button>

      <div style={{
        marginTop: 10, padding: "0 6px", fontSize: 10, color: C.steel,
        textAlign: "center", lineHeight: 1.5,
      }}>
        Subscriptions are billed by StrikeServe through Stripe. Checkout and billing
        management open securely inside the app.
      </div>
    </>
  );
}

function BillingRow({ icon: Icon, label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Icon size={15} color={C.steel} strokeWidth={1.8} />
      <span style={{ fontSize: 12, color: C.steel }}>{label}</span>
      <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: C.deep }}>{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// [server-referrals-screen-2026-10-08] REFERRALS — the server's own referral
// code + who signed up with it. Backend (cle-backend server.js, plain `auth`,
// any role):
//   GET  /api/referrals/my       → { code: null, referrals: [], totalEarned: 0, pendingPayout: 0 }
//                                  or { code, discountPct, rewardAmount, referrals:[{ referred_email,
//                                  referred_name, status, reward_amount, created_at, paid_at }],
//                                  totalEarned, pendingPayout, totalReferrals }
//                                  (its catch ALSO answers 200 { code:null, referrals:[], totalEarned:0 })
//   POST /api/referrals/generate → { code, discountPct, rewardAmount } (or { code, message } if one exists)
// status: pending → qualified (owner marks it, payout owed) → paid (paid_at set).
// totalEarned = sum of paid rewards; pendingPayout = sum of qualified rewards.
// There is no referral signup URL on the backend (nothing reads ?ref=), so the
// screen shares the CODE, not a link. discountPct is the referred client's
// first-order discount — client pricing, deliberately never shown here.
// ─────────────────────────────────────────────────────────────────────────
function ssReferralMoney(v) {
  const n = Number(v);
  if (!isFinite(n)) return "$0.00";
  return "$" + n.toFixed(2);
}
function ssReferralStatus(r) {
  const s = String((r && r.status) || "pending").toLowerCase();
  if (s === "paid") return { label: "Paid", bg: C.okL, color: C.ok };
  if (s === "qualified") return { label: "Payout pending", bg: C.infoL, color: C.info };
  if (s === "pending") return { label: "Waiting on first job", bg: C.warnL, color: C.warn };
  return { label: s.charAt(0).toUpperCase() + s.slice(1), bg: C.steelXL, color: C.steel };
}
async function ssCopyText(text) {
  try {
    const Clip = ssPlugin("Clipboard");
    if (Clip && Clip.write) { await Clip.write({ string: text }); return true; }
  } catch (_) {}
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch (_) {}
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch (_) { return false; }
}

function ReferralsScreen() {
  const [data, setData] = React.useState(null); // null = loading
  const [loadErr, setLoadErr] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoadErr("");
    try {
      const r = await api.get("/api/referrals/my");
      setData(r && typeof r === "object" ? r : { code: null, referrals: [] });
    } catch (e) {
      setData({ code: null, referrals: [] });
      setLoadErr("We couldn't load your referrals. Check your connection and try again.");
      console.warn("[referrals] load failed:", e && e.message);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  async function generate() {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/api/referrals/generate", {});
      if (!r || !r.code) throw new Error("No code returned");
      ssToast("ok", "Your referral code is ready.");
      await load();
    } catch (e) {
      ssToast("err", "We couldn't create your code. Please try again.");
      console.warn("[referrals] generate failed:", e && e.message);
    } finally { setBusy(false); }
  }

  const code = data && data.code ? String(data.code) : "";
  const shareText = code ? `Use my StrikeServe referral code ${code} when you place your first order.` : "";

  async function shareCode() {
    if (!code) return;
    try {
      const Share = ssPlugin("Share");
      if (Share && Share.share) { await Share.share({ title: "My referral code", text: shareText, dialogTitle: "Share your code" }); return; }
    } catch (e) { if (e && /cancel/i.test(String(e.message || e))) return; }
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share({ title: "My referral code", text: shareText }); return; }
      catch (e) { if (e && e.name === "AbortError") return; }
    }
    const ok = await ssCopyText(shareText);
    ssToast(ok ? "ok" : "err", ok ? "Sharing isn't available here — the message was copied instead." : "Couldn't copy. Your code is " + code + ".");
  }

  const refs = data && Array.isArray(data.referrals) ? data.referrals : [];
  const reward = data && data.rewardAmount != null ? Number(data.rewardAmount) : null;

  return (
    <>
      {loadErr && (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 10, background: C.errL, color: C.err, fontSize: 12, fontWeight: 600 }}>
          {loadErr}
          <button onClick={() => { setData(null); load(); }} style={{ marginLeft: 8, background: "none", border: "none", color: C.err, fontWeight: 800, textDecoration: "underline", cursor: "pointer", fontSize: 12 }}>Try again</button>
        </div>
      )}

      {data == null && (
        <DCard><div style={{ padding: 16, fontSize: 12, color: C.steel }}>Loading your referrals…</div></DCard>
      )}

      {data != null && !code && !loadErr && (
        <DCard>
          <div style={{ padding: 18, textAlign: "center", color: C.steel }}>
            <Gift size={28} color={C.gold} />
            <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, marginTop: 10 }}>You don't have a referral code yet.</div>
            <div style={{ fontSize: 11, marginTop: 4, lineHeight: 1.45 }}>
              Create one, then share it with law firms and other clients. You earn a reward after someone you refer has their first job completed.
            </div>
            <button onClick={generate} disabled={busy} style={{
              marginTop: 14, padding: "11px 18px", borderRadius: 12, border: "none",
              background: C.deep, color: C.white, fontWeight: 800, fontSize: 13,
              cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
            }}>{busy ? "Creating…" : "Get my referral code"}</button>
          </div>
        </DCard>
      )}

      {code && (
        <>
          <DCard>
            <div style={{ padding: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.12em" }}>YOUR REFERRAL CODE</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: C.deep, marginTop: 6, letterSpacing: "0.04em", fontFamily: "ui-monospace, Menlo, monospace", wordBreak: "break-all" }}>{code}</div>
              {reward != null && isFinite(reward) && reward > 0 && (
                <div style={{ fontSize: 11, color: C.steel, marginTop: 6, lineHeight: 1.45 }}>
                  You earn {ssReferralMoney(reward)} for each person who signs up with your code, once their first job is completed.
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button onClick={async () => { const ok = await ssCopyText(code); ssToast(ok ? "ok" : "err", ok ? "Code copied." : "Couldn't copy the code."); }} style={{
                  flex: 1, padding: "10px 12px", borderRadius: 12, border: `1px solid ${C.steelXL}`,
                  background: C.white, color: C.deep, fontWeight: 700, fontSize: 13, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}><Copy size={15} /> Copy</button>
                <button onClick={shareCode} style={{
                  flex: 1, padding: "10px 12px", borderRadius: 12, border: "none",
                  background: C.deep, color: C.white, fontWeight: 700, fontSize: 13, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}><Share2 size={15} /> Share</button>
              </div>
            </div>
          </DCard>

          <div style={{ display: "flex", gap: 8, margin: "12px 0" }}>
            {[
              { label: "Signed up", value: String(data.totalReferrals != null ? data.totalReferrals : refs.length) },
              { label: "Earned (paid)", value: ssReferralMoney(data.totalEarned || 0) },
              { label: "Payout pending", value: ssReferralMoney(data.pendingPayout || 0) },
            ].map(s => (
              <div key={s.label} style={{ flex: 1, background: C.white, borderRadius: 12, padding: "10px 8px", textAlign: "center", border: `1px solid ${C.steelXL}` }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.deep }}>{s.value}</div>
                <div style={{ fontSize: 10, color: C.steel, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {refs.length === 0 ? (
            <DCard>
              <div style={{ padding: 16, fontSize: 12, color: C.steel, textAlign: "center", lineHeight: 1.45 }}>
                No one has signed up with your code yet. Share it and they'll show up here.
              </div>
            </DCard>
          ) : (
            <DCard>
              {refs.map((r, i) => {
                const st = ssReferralStatus(r);
                const who = (r.referred_name && String(r.referred_name).trim()) || r.referred_email || "Someone";
                const when = r.created_at ? new Date(r.created_at) : null;
                const paid = r.paid_at ? new Date(r.paid_at) : null;
                const fmt = (d) => d && !isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
                return (
                  <div key={(r.referred_email || "") + i} style={{ padding: "12px 14px", borderTop: i ? `1px solid ${C.steelXXL}` : "none", display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{who}</div>
                      <div style={{ fontSize: 11, color: C.steel, marginTop: 2 }}>
                        {fmt(when) ? "Signed up " + fmt(when) : "Signed up"}
                        {String(r.status).toLowerCase() === "paid" && fmt(paid) ? " · Paid " + fmt(paid) : ""}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {r.reward_amount != null && <div style={{ fontSize: 13, fontWeight: 800, color: C.deep }}>{ssReferralMoney(r.reward_amount)}</div>}
                      <span style={{ display: "inline-block", marginTop: 3, padding: "2px 8px", borderRadius: 999, background: st.bg, color: st.color, fontSize: 10, fontWeight: 700 }}>{st.label}</span>
                    </div>
                  </div>
                );
              })}
            </DCard>
          )}
        </>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// MILEAGE LOG — list of recorded tracking sessions, CSV export per row
// ─────────────────────────────────────────────────────────────────────────
function MileageLogScreen() {
  const [sessions, setSessions] = React.useState(null); // null = loading
  const [loadErr, setLoadErr] = React.useState("");
  const [selected, setSelected] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/api/mileage/sessions");
        if (!alive) return;
        setSessions(Array.isArray(r) ? r : []);
      } catch (e) {
        if (!alive) return;
        setSessions([]);
        setLoadErr(e && e.message ? e.message : "Could not load mileage log");
        console.warn("[mileage log] load failed:", e && e.message);
      }
    })();
    return () => { alive = false; };
  }, []);

  function fmtDate(ts) {
    if (!ts) return "—";
    const d = new Date(ts);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
         + " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  function fmtDuration(mins) {
    if (mins == null) return "—";
    const m = Math.max(0, Math.round(Number(mins)));
    if (m < 60) return m + "m";
    return Math.floor(m / 60) + "h " + (m % 60) + "m";
  }

  return (
    <>
      <div style={{
        padding: "10px 4px 10px", fontSize: 11, color: C.steel, lineHeight: 1.5,
        fontStyle: "italic",
      }}>
        Mileage logs are for personal tax record-keeping. StrikeServe does not provide tax advice.
      </div>

      {loadErr && (
        <div style={{
          marginBottom: 12, padding: "10px 12px", borderRadius: 10,
          background: C.errL, color: C.err, fontSize: 12, fontWeight: 600,
        }}>
          {loadErr}
        </div>
      )}

      {sessions == null && (
        <DCard><div style={{ padding: 16, fontSize: 12, color: C.steel }}>Loading mileage log…</div></DCard>
      )}

      {sessions && sessions.length === 0 && (
        <DCard>
          <div style={{ padding: 18, textAlign: "center", color: C.steel }}>
            <Route size={28} color={C.steelL} />
            <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, marginTop: 10 }}>
              No mileage sessions yet.
            </div>
            <div style={{ fontSize: 11, marginTop: 4, lineHeight: 1.45 }}>
              Open Route Planner ("Plan my day") and tap Track mileage to record a route.
            </div>
          </div>
        </DCard>
      )}

      {sessions && sessions.length > 0 && (
        <DCard>
          {sessions.map((s, i) => {
            // Derive a center coord from whatever the backend returned. The schema is
            // not strictly fixed, so we probe a few common field names and only render
            // the thumbnail when we actually have a usable lat/lng pair. If
            // /api/maps/static isn't deployed yet, the <img> just fails silently.
            const lat = Number(
              s.center_lat != null ? s.center_lat :
              s.start_lat  != null ? s.start_lat  :
              (s.start && s.start.lat != null) ? s.start.lat :
              (s.first_point && s.first_point.lat != null) ? s.first_point.lat :
              NaN
            );
            const lng = Number(
              s.center_lng != null ? s.center_lng :
              s.start_lng  != null ? s.start_lng  :
              (s.start && s.start.lng != null) ? s.start.lng :
              (s.first_point && s.first_point.lng != null) ? s.first_point.lng :
              NaN
            );
            const hasCenter = Number.isFinite(lat) && Number.isFinite(lng);
            const thumbUrl = hasCenter
              ? `${API_URL}/api/maps/static?lat=${lat}&lng=${lng}&zoom=12&size=80x60`
              : null;
            return (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                style={{
                  width: "100%", padding: "13px 14px", background: "none", border: "none",
                  borderTop: i === 0 ? "none" : `1px solid ${C.steelXXL}`,
                  display: "inline-flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left",
                  flexWrap: "wrap",
                }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 19,
                  background: s.status === "completed" ? C.okL : C.steelXXL,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Route size={18} color={s.status === "completed" ? C.ok : C.steel} />
                </div>
                {thumbUrl && (
                  <img
                    src={thumbUrl}
                    alt=""
                    width={80}
                    height={60}
                    onError={(e) => { try { e.currentTarget.style.display = "none"; } catch (_) {} }}
                    style={{
                      width: 80, height: 60, borderRadius: 8, objectFit: "cover",
                      border: `1px solid ${C.steelXXL}`, flexShrink: 0, background: C.steelXXL,
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {fmtDate(s.started_at)}
                  </div>
                  <div style={{ fontSize: 11, color: C.steel, marginTop: 2 }}>
                    {Number(s.total_miles || 0).toFixed(2)} mi · {fmtDuration(s.duration_minutes)} · {s.total_points || 0} pts
                  </div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.deep }}>View</div>
                <ChevronRight size={16} color={C.steel} />
              </button>
            );
          })}
        </DCard>
      )}

      {selected && <MileageSessionSheet session={selected} onClose={() => setSelected(null)} onDeleted={(id) => setSessions((prev) => (prev || []).filter((x) => x.id !== id))} />}
    </>
  );
}

function MileageSessionSheet({ session, onClose, onDeleted }) {
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState("");

  function fmtDate(ts) {
    if (!ts) return "—";
    const d = new Date(ts);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  }

  async function exportCsv() {
    if (busy) return;
    setBusy(true);
    setMsg("");
    try {
      // WKWebView (Capacitor iOS) silently fails on Blob + <a download>, so we
      // prefer the Capacitor Filesystem plugin (writes to Documents/) when
      // available, and fall back to opening the export URL via openExternal —
      // which uses SFSafariViewController and lets iOS handle the CSV in Safari.
      const fs = ssPlugin("Filesystem"); // [server-parity-web-2026-10-05]
      const isNative = ssIsNative();

      if (isNative && fs) {
        // Fetch + write to native Documents/ via Filesystem plugin.
        const r = await fetch(API_URL + "/api/mileage/sessions/" + encodeURIComponent(session.id) + "/export-csv", {
          method: "POST",
          headers: api.headers(),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.error || r.statusText || "Export failed");
        }
        const csv = await r.text();
        try {
          await fs.writeFile({
            path: "mileage_session_" + session.id + ".csv",
            data: csv,
            directory: "DOCUMENTS",
            encoding: "utf8",
          });
          setMsg("CSV saved to Files → On My iPhone → StrikeServe.");
          return;
        } catch (e) {
          // Fall through to the openExternal path if Filesystem write fails.
        }
      }

      // Web + WKWebView fallback: open the export URL directly so the browser
      // (or SFSafariViewController) can handle the CSV download natively.
      const exportUrl = API_URL + "/api/mileage/sessions/" + encodeURIComponent(session.id) + "/export-csv?download=1";
      await openExternal(exportUrl);
      setMsg("Opening CSV in your browser…");
    } catch (e) {
      setMsg(e && e.message ? e.message : "Export failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet onClose={onClose} small>
      <div style={{ background: C.deep, padding: "16px 16px 14px", color: C.white }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button onClick={onClose} style={iconBtn}>
            <ChevronLeft size={22} color={C.white} />
          </button>
          <div style={{ fontSize: 16, fontWeight: 800 }}>Session #{session.id}</div>
          <div style={{ width: 38 }} />
        </div>
      </div>
      {/* [sheet-scroll-audit-2026-07-25] flex+minHeight+overflow so this scrolls instead of clipping. */}
      <div style={{ padding: "16px 14px 24px", flex: 1, minHeight: 0, overflowY: "auto" }}>
        <DCard>
          <div style={{ padding: "13px 14px", borderBottom: `1px solid ${C.steelXXL}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>STARTED</div>
            <div style={{ fontSize: 13, color: C.deep, marginTop: 2 }}>{fmtDate(session.started_at)}</div>
          </div>
          <div style={{ padding: "13px 14px", borderBottom: `1px solid ${C.steelXXL}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>ENDED</div>
            <div style={{ fontSize: 13, color: C.deep, marginTop: 2 }}>{fmtDate(session.ended_at)}</div>
          </div>
          <div style={{ padding: "13px 14px", borderBottom: `1px solid ${C.steelXXL}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>TOTAL MILES</div>
            <div style={{ fontSize: 16, color: C.deep, fontWeight: 800, marginTop: 2 }}>
              {Number(session.total_miles || 0).toFixed(2)} mi
            </div>
          </div>
          <div style={{ padding: "13px 14px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>STATUS</div>
            <div style={{ fontSize: 13, color: C.deep, marginTop: 2 }}>{session.status || "active"}</div>
          </div>
        </DCard>

        {msg && (
          <div style={{
            marginTop: 12, padding: "10px 12px", borderRadius: 10,
            background: C.infoL, color: C.info, fontSize: 12, fontWeight: 600,
          }}>{msg}</div>
        )}

        <button onClick={exportCsv} disabled={busy} style={{
          marginTop: 14, width: "100%", padding: 13,
          background: busy ? C.steelXXL : `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
          color: busy ? C.steel : C.white,
          border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: busy ? "wait" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          <Download size={16} /> {busy ? "Preparing…" : "Download CSV"}
        </button>
        {/* [server-app-removals-2026-10-08] delete this session from your mileage log */}
        {session.status !== "active" && (
          <button onClick={async () => {
            const reason = ssAskReason("this mileage session"); if (!reason) return;
            setBusy(true);
            try { await api.req("DELETE", "/api/mileage/sessions/" + encodeURIComponent(session.id), { reason }); ssToast("ok", "Session deleted"); if (onDeleted) onDeleted(session.id); onClose(); }
            catch (e) { setMsg((e && e.message) || "Could not delete the session"); }
            setBusy(false);
          }} disabled={busy} style={{
            marginTop: 10, width: "100%", padding: 12, background: C.white, color: C.err,
            border: `1px solid ${C.errL}`, borderRadius: 12, fontSize: 13, fontWeight: 800, cursor: busy ? "wait" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}><Trash2 size={15} color={C.err} /> Delete session</button>
        )}
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SHARED PRIMITIVES used across DrawerScreen sub-screens
// ─────────────────────────────────────────────────────────────────────────
function DSectionLabel({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.1em", margin: "0 4px 6px" }}>
      {String(children).toUpperCase()}
    </div>
  );
}

function DCard({ children, style }) {
  return (
    <div style={{
      background: C.white, borderRadius: 14, border: `1px solid ${C.steelXL}`,
      marginBottom: 16, overflow: "hidden", ...(style || {}),
    }}>
      {children}
    </div>
  );
}

// [address-picker-sweep-2026-07-28] `address` opts a DInput into AddressAutocomplete.
// Off by default, so every other DInput in the app renders the identical plain input
// it always did. The picker takes the row's flex slot (wrapStyle) and the input keeps
// the exact borderless styling, so the card is pixel-identical.
function DInput({ label, value, onChange, type = "text", readOnly, icon: Icon, placeholder, address, onAddressParsed }) {
  const fieldStyle = {
    flex: 1, border: "none", outline: "none", background: "transparent",
    fontSize: 14, fontWeight: 600, color: readOnly ? C.steel : C.deep,
    padding: 0, fontFamily: "inherit",
  };
  return (
    <div style={{ padding: "12px 14px", borderBottom: `1px solid ${C.steelXXL}` }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.06em", marginBottom: 5 }}>
        {label.toUpperCase()}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {address && !readOnly ? (
          /* [address-picker-sweep-2026-07-28] */
          <AddressAutocomplete
            value={value}
            onChange={v => onChange && onChange(v)}
            onAddressParsed={onAddressParsed}
            placeholder={placeholder || ""}
            wrapStyle={{ flex: 1, minWidth: 0 }}
            style={{ ...fieldStyle, flex: "none", width: "100%" }}
          />
        ) : (
          <input
            type={type}
            value={value || ""}
            onChange={e => onChange && onChange(e.target.value)}
            readOnly={!!readOnly}
            placeholder={placeholder || ""}
            style={fieldStyle}
          />
        )}
        {Icon && <Icon size={14} color={C.steel} />}
      </div>
    </div>
  );
}

function DToggleRow({ label, sub, value, onChange, last }) {
  return (
    <div style={{
      padding: "13px 14px", display: "flex", alignItems: "center", justifyContent: "space-between",
      borderBottom: last ? "none" : `1px solid ${C.steelXXL}`, gap: 10,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: C.deep, fontWeight: 600 }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: C.steel, marginTop: 2 }}>{sub}</div>}
      </div>
      <button onClick={() => onChange && onChange(!value)} style={{
        width: 42, height: 24, borderRadius: 12, border: "none",
        background: value ? C.ok : C.steelL,
        position: "relative", cursor: "pointer", transition: "background 0.2s",
        flexShrink: 0,
      }}>
        <div style={{
          position: "absolute", top: 2, left: value ? 20 : 2, width: 20, height: 20,
          borderRadius: "50%", background: C.white, transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }} />
      </button>
    </div>
  );
}

function DGoldButton({ children, onClick, disabled, icon: Icon, title }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title} style={{
      width: "100%", padding: 14, background: disabled ? C.steelL : C.gold,
      color: C.deep, border: "none", borderRadius: 12,
      fontSize: 14, fontWeight: 800, letterSpacing: "0.02em",
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
      opacity: disabled ? 0.7 : 1, transition: "all 0.15s",
    }}>
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

function DInlineAlert({ kind, children }) {
  if (!children) return null;
  const palette = kind === "ok"
    ? { bg: C.okL, fg: C.ok, Icon: CheckCircle2 }
    : { bg: C.errL, fg: C.err, Icon: AlertCircle };
  const Icon = palette.Icon;
  return (
    <div style={{
      background: palette.bg, color: palette.fg, padding: "10px 12px", borderRadius: 10,
      marginBottom: 12, display: "flex", alignItems: "flex-start", gap: 8,
      fontSize: 12, fontWeight: 600, lineHeight: 1.4,
    }}>
      <Icon size={14} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PROFILE — edit name / phone / license number, plus web-parity fields
// (county, address, registration role) and a digital signature capture.
// Fields use the SAME backend names the web admin app (cle-frontend-live
// Account) persists via PATCH /api/auth/profile so data round-trips:
//   name, phone, regNumber, county, address, registrationRole, signature
// ─────────────────────────────────────────────────────────────────────────
/* [workflow-audit-2026-10-28 V-17] Street / city / state / ZIP on the profile. users.city,
   users.state and users.zip exist (server.js migration); PATCH /api/auth/profile accepts
   `address` today and city/state/zip once the backend allow-list takes them. So the app sends
   BOTH: the separate fields, and `address` as the one full line "123 Main St, Antioch, CA 94509"
   that FL-330 item 6 prints and the sign-city fallback parses. An older backend ignores the
   separate keys and still gets the full line. Loading prefers the separate columns and falls
   back to splitting the stored line. */
function ssSplitAddressLine(line) {
  const out = { street: "", city: "", state: "", zip: "" };
  const parts = String(line || "").split(",").map((x) => x.trim()).filter(Boolean);
  if (!parts.length) return out;
  const tail = parts[parts.length - 1].match(/^([A-Za-z]{2})\s*(\d{5}(?:-\d{4})?)?$/);
  if (parts.length >= 3 && tail) {
    out.state = tail[1].toUpperCase(); out.zip = tail[2] || "";
    out.city = parts[parts.length - 2];
    out.street = parts.slice(0, -2).join(", ");
  } else if (parts.length >= 2) {
    const m = parts[parts.length - 1].match(/^(.*?)\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    if (m) { out.city = m[1]; out.state = m[2].toUpperCase(); out.zip = m[3]; out.street = parts.slice(0, -1).join(", "); }
    else out.street = parts.join(", ");
  } else out.street = parts[0];
  return out;
}
function ssJoinAddressLine(a) {
  const street = String(a.street || "").trim(), city = String(a.city || "").trim();
  const stz = [String(a.state || "").trim().toUpperCase(), String(a.zip || "").trim()].filter(Boolean).join(" ");
  return [street, city, stz].filter(Boolean).join(", ");
}

function ProfileScreen() {
  const { user, setUser } = React.useContext(AppCtx);
  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [license, setLicense] = React.useState("");
  const [county, setCounty] = React.useState("");
  const [address, setAddress] = React.useState(""); // street line [workflow-audit-2026-10-28 V-17]
  const [addrCity, setAddrCity] = React.useState("");
  const [addrState, setAddrState] = React.useState("");
  const [addrZip, setAddrZip] = React.useState("");
  const [regRole, setRegRole] = React.useState(""); // [pos-overhaul-2026-10-07:server-app] no silent "owner" default
  const [busy, setBusy] = React.useState(false);
  const [okMsg, setOkMsg] = React.useState("");
  const [errMsg, setErrMsg] = React.useState("");

  // Digital signature (PNG data URL) loaded from the user profile. When a
  // signature exists we show it with a Re-draw control; otherwise the draw pad.
  const [savedSig, setSavedSig] = React.useState("");
  const [drawing, setDrawing] = React.useState(false);
  const [sigBusy, setSigBusy] = React.useState(false);

  // Profile photo capture state. The avatar is tappable; tapping opens the
  // native Camera plugin (system action sheet: Take photo / Choose from
  // library), uploads through the SAME presign → PUT to S3 → /api/uploads/
  // complete flow used by AttemptSheet/FilingCompleteSheet, then PATCHes the
  // resulting URL onto the profile so ssAvatarUrl() picks it up everywhere.
  const [photoBusy, setPhotoBusy] = React.useState(false);

  React.useEffect(() => {
    setName(user?.name || "");
    setPhone(user?.phone || "");
    setLicense(user?.regNumber || user?.reg_number || user?.license_number || "");
    setCounty(user?.county || user?.reg_county || user?.regCounty || "");
    {
      /* [workflow-audit-2026-10-28 V-17] separate columns when the backend returns them, else split the line */
      const sp = ssSplitAddressLine(user?.address || "");
      const hasCols = !!(user?.city || user?.state || user?.zip);
      setAddress(hasCols ? (user?.street || user?.address_street || sp.street || user?.address || "") : (sp.street || user?.address || ""));
      setAddrCity(String(user?.city || sp.city || ""));
      setAddrState(String(user?.state || sp.state || "").toUpperCase().slice(0, 2));
      setAddrZip(String(user?.zip || sp.zip || ""));
    }
    /* [pos-overhaul-2026-10-07:server-app] canonical values are owner | employee |
       independent_contractor; this select used to send "contractor". */
    {
      const r = String(user?.registrationRole || user?.registration_role || "").toLowerCase().replace(/[\s-]+/g, "_");
      setRegRole(r === "contractor" || r === "independentcontractor" ? "independent_contractor" : (["owner", "employee", "independent_contractor"].includes(r) ? r : ""));
    }
    setSavedSig(user?.signature || "");
    setDrawing(!user?.signature);
  }, [user]);

  // Cold-launch protection: AuthGate normally hydrates `user` before this
  // screen mounts (from localStorage). If it hasn't yet, show 6 skeleton
  // lines instead of an empty form — matches the 4-input layout plus the
  // avatar + section label.
  if (!user) {
    return (
      <SkeletonGroup label="Loading profile">
        <div style={{
          background: C.white, borderRadius: 14, padding: 16, marginBottom: 14,
          border: `1px solid ${C.steelXL}`, display: "flex", flexDirection: "column", alignItems: "center",
        }}>
          <SkeletonCircle size={80} style={{ marginBottom: 10 }} />
          <SkeletonLine width="50%" height={14} style={{ marginBottom: 0 }} />
        </div>
        <SkeletonLine width="35%" height={11} />
        <div style={{
          background: C.white, borderRadius: 14, padding: 14, marginBottom: 14,
          border: `1px solid ${C.steelXL}`,
        }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{ marginBottom: i === 3 ? 0 : 12 }}>
              <SkeletonLine width="25%" height={10} />
              <SkeletonLine width="100%" height={36} style={{ marginBottom: 0 }} />
            </div>
          ))}
        </div>
        <SkeletonLine width="100%" height={44} style={{ marginBottom: 0 }} />
      </SkeletonGroup>
    );
  }

  const initials = ((name || user?.name || "S").split(" ").map(s => s[0]).join("").slice(0, 2)).toUpperCase();
  const email = user?.email || "";

  async function save() {
    if (busy) return;
    /* [pos-overhaul-2026-10-07:server-app] POS item 7 prints the server's registration
       role, so it is required whenever a registration number is on file. */
    if (license.trim() && !regRole) {
      setOkMsg("");
      setErrMsg("Choose your registration role — Owner, Employee or Independent contractor.");
      return;
    }
    setBusy(true);
    setOkMsg("");
    setErrMsg("");
    // Same backend field names as the web admin app (cle-frontend-live Account.save).
    const patch = {
      name: name.trim(),
      phone: phone.trim() || null,
      regNumber: license.trim() || null,
      county: county.trim() || null,
      address: ssJoinAddressLine({ street: address, city: addrCity, state: addrState, zip: addrZip }) || null, /* [workflow-audit-2026-10-28 V-17] full line */
      city: addrCity.trim() || null,
      state: addrState.trim().toUpperCase().slice(0, 2) || null,
      zip: addrZip.trim() || null,
      registrationRole: regRole || null,
    };
    try {
      await api.patch("/api/auth/profile", patch);
      // Reflect into context so the rest of the app sees changes without a reload.
      if (user) setUser({ ...user, ...patch, reg_number: patch.regNumber });
      // Reflect locally so the rest of the app sees the new name without a reload
      try {
        const cached = JSON.parse(localStorage.getItem(SS_USER_KEY) || "null");
        if (cached) {
          cached.name = patch.name;
          cached.phone = patch.phone;
          cached.reg_number = patch.regNumber;
          cached.county = patch.county;
          cached.address = patch.address;
          cached.city = patch.city; cached.state = patch.state; cached.zip = patch.zip; /* [workflow-audit-2026-10-28 V-17] */
          cached.registrationRole = patch.registrationRole;
          localStorage.setItem(SS_USER_KEY, JSON.stringify(cached));
        }
      } catch {}
      setOkMsg("Profile saved");
      ssRefreshPosProfile(); // [pos-overhaul-2026-10-07:server-app]
    } catch (e) {
      setErrMsg(e.message || "Could not save profile");
    } finally {
      setBusy(false);
    }
  }

  // Persist a freshly drawn signature to the SAME `signature` profile field the
  // web app writes (cle-frontend-live Account → PATCH /api/auth/profile). The
  // saved PNG is reused to auto-stamp affidavits / POS court forms.
  async function saveSignature(dataUrl) {
    if (sigBusy) return;
    setSigBusy(true);
    setOkMsg(""); setErrMsg("");
    try {
      await api.patch("/api/auth/profile", { signature: dataUrl });
      setSavedSig(dataUrl);
      setDrawing(false);
      if (user) setUser({ ...user, signature: dataUrl });
      // Cache for the POS SignatureSheet so it can offer "apply saved signature".
      try { localStorage.setItem("ss_signature_v1", dataUrl); } catch {}
      try {
        const cached = JSON.parse(localStorage.getItem(SS_USER_KEY) || "null");
        if (cached) { cached.signature = dataUrl; localStorage.setItem(SS_USER_KEY, JSON.stringify(cached)); }
      } catch {}
      setOkMsg("Signature saved");
    } catch (e) {
      setErrMsg(e.message || "Could not save signature");
    } finally {
      setSigBusy(false);
    }
  }

  // Capture / change the profile photo. Mirrors the AttemptSheet upload flow:
  // Camera.getPhoto(base64) → /api/uploads/presign → PUT to S3 →
  // /api/uploads/complete → PATCH the returned URL onto the profile. Uses
  // source "PROMPT" so the user can take a photo OR pick from the library.
  // Writes the URL under several alias keys so whichever field the backend
  // honors (mirrors ssAvatarUrl's read aliases) is set; reflects into context
  // + localStorage so the avatar updates app-wide without a reload.
  async function changePhoto() {
    if (photoBusy) return;
    const Camera = ssPlugin("Camera"); // [server-parity-web-2026-10-05]
    if (!Camera) { setErrMsg("Camera unavailable — open the installed app build"); return; }
    setPhotoBusy(true); setOkMsg(""); setErrMsg("");
    try {
      const p = await Camera.getPhoto({
        quality: 85,
        resultType: "base64",
        source: "PROMPT",
        saveToGallery: false,
        allowEditing: false,
        correctOrientation: true,
        promptLabelHeader: "Profile photo",
        promptLabelPhoto: "Choose from library",
        promptLabelPicture: "Take photo",
      });
      const b64 = (p && (p.base64String || p.base64)) || "";
      if (!b64) { setPhotoBusy(false); return; }
      const fileName = `profile-${Date.now()}.jpg`;
      const approxSize = Math.floor(b64.length * 0.75);
      const presign = await api.post("/api/uploads/presign", {
        fileName, fileType: "image/jpeg", fileSize: approxSize,
        context: "profile-photo",
      });
      if (!presign || !presign.presignedUrl) throw new Error("No upload URL from backend");
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const put = await fetch(presign.presignedUrl, {
        method: "PUT",
        headers: { "Content-Type": "image/jpeg" },
        body: ssS3Body(bytes, "image/jpeg", "photo.jpg"), /* [ios-binary-upload-2026-10-07] */
      });
      if (!put.ok) throw new Error("S3 upload failed: " + put.status);
      await api.post("/api/uploads/complete", {
        key: presign.key, fileName, fileSize: approxSize, fileType: "image/jpeg",
        context: "profile-photo",
      });
      const url = presign.fileUrl || presign.url || null;
      // Persist on the profile. Send the URL under the same aliases ssAvatarUrl
      // reads, plus the S3 key, so the backend can pick whichever it stores.
      const patch = { avatar_url: url, avatarUrl: url, photo_url: url, selfie_url: url, avatarKey: presign.key };
      await api.patch("/api/auth/profile", patch);
      if (user) setUser({ ...user, avatar_url: url, selfie_url: url, photo_url: url });
      try {
        const cached = JSON.parse(localStorage.getItem(SS_USER_KEY) || "null");
        if (cached) {
          cached.avatar_url = url; cached.selfie_url = url; cached.photo_url = url;
          localStorage.setItem(SS_USER_KEY, JSON.stringify(cached));
        }
      } catch {}
      setOkMsg("Profile photo updated");
    } catch (e) {
      const msg = (e && e.message) ? String(e.message) : "";
      // Native camera throws on user cancel — treat that as a silent no-op.
      if (!/cancel/i.test(msg)) setErrMsg(msg || "Could not update photo");
    } finally {
      setPhotoBusy(false);
    }
  }

  return (
    <>
      <DInlineAlert kind="ok">{okMsg}</DInlineAlert>
      <DInlineAlert kind="err">{errMsg}</DInlineAlert>

      <div style={{
        background: C.white, borderRadius: 14, padding: 16, marginBottom: 14,
        border: `1px solid ${C.steelXL}`, textAlign: "center",
      }}>
        <button
          onClick={changePhoto}
          disabled={photoBusy}
          aria-label="Change profile photo"
          style={{
            position: "relative", width: 80, height: 80, margin: "0 auto 10px",
            padding: 0, border: "none", background: "transparent", display: "block",
            cursor: photoBusy ? "wait" : "pointer", borderRadius: "50%",
          }}>
          <Avatar user={user} size={80} fontSize={30} />
          {/* Camera badge overlay — signals the avatar is tappable to set a photo. */}
          <span style={{
            position: "absolute", right: -2, bottom: -2, width: 28, height: 28,
            borderRadius: "50%", background: C.deep, border: `2px solid ${C.white}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Camera size={14} color={C.white} />
          </span>
        </button>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.deep }}>{user?.name || "Server"}</div>
          <ProBadge user={user} />
        </div>
        <button
          onClick={changePhoto}
          disabled={photoBusy}
          style={{
            marginTop: 8, padding: "6px 14px", background: "transparent", color: C.deep,
            border: `1.5px solid ${C.steelXL}`, borderRadius: 10, fontSize: 12, fontWeight: 700,
            cursor: photoBusy ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 6,
          }}>
          <Camera size={13} color={C.deep} />
          {photoBusy ? "Uploading…" : (ssAvatarUrl(user) ? "Change photo" : "Add photo")}
        </button>
      </div>

      <DSectionLabel>Personal details</DSectionLabel>
      <DCard>
        <DInput label="Full name" value={name} onChange={setName} placeholder="Your name" />
        <DInput label="Email" value={email} readOnly icon={Lock} />
        <DInput label="Phone" value={phone} onChange={setPhone} type="tel" placeholder="(555) 555-0100" />
        {/* [address-picker-sweep-2026-07-28] CONVERT — this value auto-fills POS-010 /
            court forms, so a standardized line here propagates to every form the
            server files.
            [workflow-audit-2026-10-28 V-17] Now street + city/state/ZIP siblings: a picked
            suggestion fills all four; save joins them back into the full line. */}
        <DInput label="Street address" value={address} onChange={setAddress} address placeholder="e.g. 123 Main St"
          onAddressParsed={(p) => {
            if (p && p.street) setAddress(p.street);
            if (p && p.city) setAddrCity(p.city);
            if (p && p.state) setAddrState(String(p.state).toUpperCase().slice(0, 2));
            if (p && p.zip) setAddrZip(String(p.zip).replace(/[^\d-]/g, "").slice(0, 10));
          }} />
        <DInput label="City" value={addrCity} onChange={setAddrCity} placeholder="e.g. Sacramento" />
        <div style={{ display: "flex" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <DInput label="State" value={addrState} onChange={(v) => setAddrState(String(v || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2))} placeholder="CA" />
          </div>
          <div style={{ flex: 2, minWidth: 0 }}>
            <DInput label="ZIP" value={addrZip} onChange={(v) => setAddrZip(String(v || "").replace(/[^\d-]/g, "").slice(0, 10))} placeholder="95816" />
          </div>
        </div>
      </DCard>

      {/* Process-server registration info — used to auto-fill POS-010 / court forms.
          Matches the web admin app's process-server fields (regNumber, county, role). */}
      <DSectionLabel>Process server info</DSectionLabel>
      <DCard>
        <DInput label="License / registration #" value={license} onChange={setLicense} placeholder="e.g. CA-2025-12345" />
        <DInput label="Primary county" value={county} onChange={setCounty} placeholder="e.g. Sacramento" />
        <div style={{ padding: "12px 14px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.06em", marginBottom: 5 }}>
            REGISTRATION ROLE{license.trim() ? <span style={{ color: C.err }}> *</span> : null}
          </div>
          <select value={regRole} onChange={e => setRegRole(e.target.value)} aria-required={!!license.trim()} style={{
            width: "100%", border: "none", outline: "none", background: "transparent",
            fontSize: 14, fontWeight: 600, color: regRole ? C.deep : C.steel, padding: 0, fontFamily: "inherit",
          }}>
            <option value="">{license.trim() ? "Select your role (required)" : "Select your role"}</option>
            <option value="owner">Owner</option>
            <option value="employee">Employee</option>
            <option value="independent_contractor">Independent contractor</option>
          </select>
          <div style={{ fontSize: 11, color: C.steel, marginTop: 4, lineHeight: 1.4 }}>
            Printed on your proofs of service (item 7). Required when you are a registered process server.
          </div>
        </div>
      </DCard>

      {/* [zone-coverage-2026-10-29:server-area] Read-only: the server's OWN service area only.
          Radius = min(coverageRadius/serviceZoneMiles, 30) from /api/auth/me + login; the
          30-mile limit is absolute. No other servers, counts or prices; no widen control. */}
      {(() => {
        const hb = ssHomeBase(user);
        const rawR = Number(user?.serviceZoneMiles ?? user?.coverageRadius);
        const miles = Math.min(isFinite(rawR) && rawR > 0 ? rawR : SS_ZONE_MILES, SS_ZONE_MILES);
        const sp = ssSplitAddressLine(hb?.address || "");
        const place = sp.city ? `${sp.city}${sp.state ? ", " + sp.state : ""}` : (hb?.address || "your home base");
        return (
          <>
            <DSectionLabel>Service area</DSectionLabel>
            <DCard style={{ padding: 14 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <MapPin size={16} color={C.steel} style={{ marginTop: 1, flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: C.deep, lineHeight: 1.5 }}>
                  {hb ? `Your service area: within ${miles} miles of ${place}${user?.serviceZone && user.serviceZone.code ? ` (${user.serviceZone.code} ${user.serviceZone.name || ""})`.replace(" )", ")") : ""}` : "Your service area is not set yet. Your office sets your home base when you are approved."}
                </div>
              </div>
            </DCard>
          </>
        );
      })()}

      <DGoldButton onClick={save} disabled={busy} icon={Check}>
        {busy ? "Saving..." : "Save changes"}
      </DGoldButton>

      {/* Digital signature — parity with web Account "Digital Signature" card.
          Saved as a PNG data URL to the `signature` field via PATCH /api/auth/profile,
          then reused to auto-stamp affidavits and POS court forms. */}
      <div style={{ height: 18 }} />
      <DSectionLabel>Digital signature</DSectionLabel>
      <DCard style={{ padding: 14 }}>
        <div style={{ fontSize: 12, color: C.steel, marginBottom: 12, lineHeight: 1.5 }}>
          Used to auto-stamp affidavits and court forms (POS-010, declarations).
        </div>
        {(!drawing && savedSig) ? (
          <div>
            <div style={{ border: `1px solid ${C.steelXL}`, borderRadius: 10, padding: 8, background: C.bgW, textAlign: "center" }}>
              <img src={savedSig} alt="Your signature" style={{ maxWidth: "100%", maxHeight: 140, objectFit: "contain", display: "inline-block" }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.ok, display: "flex", alignItems: "center", gap: 4 }}>
                <Check size={13} /> Signature on file
              </span>
              <button onClick={() => setDrawing(true)} style={{
                marginLeft: "auto", padding: "8px 14px", background: C.white, color: C.deep,
                border: `1.5px solid ${C.deep}`, borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6,
              }}><RotateCcw size={13} /> Re-draw</button>
            </div>
          </div>
        ) : (
          <ProfileSignaturePad
            busy={sigBusy}
            onSave={saveSignature}
            onCancel={savedSig ? () => setDrawing(false) : null}
          />
        )}
      </DCard>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Inline draw-to-sign canvas for the profile screen. Mirrors the touch+mouse
// drawing logic of the POS SignatureSheet above for UI consistency, but saves
// to the user's `signature` profile field rather than signing a single POS.
// ─────────────────────────────────────────────────────────────────────────
function ProfileSignaturePad({ onSave, onCancel, busy, saveLabel }) { // [pos-overhaul-2026-10-07:server-app] saveLabel: reused by the POS sign sheet
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const cssSize = useRef({ w: 0, h: 0 });   // displayed CSS-pixel size (drawing coordinate space)
  const [hasInk, setHasInk] = useState(false);

  // HiDPI init: size the backing store to (CSS size × devicePixelRatio) so the
  // exported PNG is crisp on Retina/iPhone (DPR 2–3×). The CSS size stays fixed
  // (style width 100% / height 200) — only the backing store grows. The 2D
  // context is scaled by DPR so all drawing/clearing uses CSS-pixel coordinates.
  // Runs after layout so getBoundingClientRect returns the real laid-out size;
  // re-runs on resize/orientation change.
  function setupCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    // Fall back to a sensible fixed CSS size if layout hasn't happened yet.
    const cssW = rect.width  || 600;
    const cssH = rect.height || 200;
    cssSize.current = { w: cssW, h: cssH };
    canvas.width  = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);   // reset any prior scale before re-applying
    ctx.scale(dpr, dpr);                  // draw in CSS-pixel coordinates
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.lineWidth = 2.5; ctx.strokeStyle = "#0a1628";
  }
  useEffect(() => {
    setupCanvas();
    const onResize = () => {
      // Resizing wipes the backing store; only re-init when nothing is drawn yet
      // so we don't silently clear an in-progress signature.
      if (!drawing.current && !hasInk) setupCanvas();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Returns the pointer position in CSS pixels (ctx.scale handles DPR — do NOT
  // multiply by dpr here).
  function getPos(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  }
  function start(e) {
    e.preventDefault();
    drawing.current = true;
    const p = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  }
  function move(e) {
    if (!drawing.current) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#0a1628";
    ctx.lineTo(p.x, p.y); ctx.stroke();
    setHasInk(true);
  }
  function end() { drawing.current = false; }
  function clear() {
    const c = canvasRef.current; if (!c) return;
    // Context is DPR-scaled, so clear in CSS-pixel dimensions.
    c.getContext("2d").clearRect(0, 0, cssSize.current.w, cssSize.current.h);
    setHasInk(false);
  }
  function save() {
    if (!hasInk || busy) return;
    onSave(canvasRef.current.toDataURL("image/png"));   // lossless, full HiDPI backing-store resolution
  }

  return (
    <div>
      <div style={{ background: C.bgW, borderRadius: 12, padding: 6, border: `1px solid ${C.steelXL}` }}>
        <canvas
          ref={canvasRef}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          style={{ width: "100%", height: 200, background: C.white, borderRadius: 8, touchAction: "none", display: "block" }}
        />
        <div style={{ textAlign: "center", fontSize: 10, color: C.steel, marginTop: 6 }}>
          Draw your signature with your finger or mouse
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        {onCancel && (
          <button onClick={onCancel} disabled={busy} style={{
            flex: 1, padding: 11, background: C.white, color: C.steel,
            border: `1.5px solid ${C.steelL}`, borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>Cancel</button>
        )}
        <button onClick={clear} disabled={busy} style={{
          flex: 1, padding: 11, background: C.white, color: C.deep,
          border: `1.5px solid ${C.deep}`, borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: "pointer",
        }}>Clear</button>
        <button onClick={save} disabled={busy || !hasInk} style={{
          flex: 2, padding: 11, background: hasInk && !busy ? C.deep : C.steelL,
          color: C.white, border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800,
          cursor: hasInk && !busy ? "pointer" : "not-allowed",
        }}>{busy ? "Saving..." : (saveLabel || "Save signature")}</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SETTINGS — notifications, display, security, sign out
// ─────────────────────────────────────────────────────────────────────────
function SettingsScreen({ onClose }) {
  const { user, logout, dark, setDarkMode } = React.useContext(AppCtx);
  /* [email-optout-hard-gate-2026-08-15] Was `user?.notification_prefs` — a key the
     API has never returned. GET /api/auth/me and PATCH /api/auth/profile both send
     `notifPrefs` (camelCase; server.js RETURNING notif_prefs -> notifPrefs). So
     prefs0 was ALWAYS {}, every toggle here defaulted to on via `!== false`, and a
     server who switched Email off saw it back On at the next app load — while the
     backend had in fact stored email:false. The toggle looked broken and was also
     silently misreporting its own state. Read the field the API actually sends, and
     tolerate the snake_case shape in case an older payload turns up. */
  const prefs0 = user?.notifPrefs || user?.notification_prefs || {};
  const [push, setPush] = React.useState(prefs0.push !== false);
  const [pushError, setPushError] = React.useState("");
  const [sms, setSms] = React.useState(prefs0.sms !== false);
  const [emailPref, setEmailPref] = React.useState(prefs0.email !== false);
  const [units, setUnits] = React.useState(() => {
    try { return localStorage.getItem("ss_units") || "mi"; } catch { return "mi"; }
  });
  const [pwOpen, setPwOpen] = React.useState(false);
  const [delOpen, setDelOpen] = React.useState(false); // [account-delete-2026-09-30]

  /* [biometric-login-2026-10-11] Face ID sign-in toggle. Turning it ON needs the password once:
     only a password sign-in returns the refresh token that Face ID protects. */
  const [bioSt, setBioSt] = React.useState({ available: false, enrolled: false, label: "Face ID" });
  const [bioPwOpen, setBioPwOpen] = React.useState(false);
  const [bioPw, setBioPw] = React.useState("");
  const [bioBusy, setBioBusy] = React.useState(false);
  const [bioErr, setBioErr] = React.useState("");
  React.useEffect(() => {
    let alive = true;
    (async () => {
      const a = await bioAvailable();
      if (!a.available) return;
      const en = await bioEnrolled("server");
      if (alive) setBioSt({ available: true, enrolled: en, label: a.label });
    })();
    return () => { alive = false; };
  }, []);
  async function toggleBio(v) {
    setBioErr("");
    if (!v) { await bioDisable("server"); setBioSt(s => ({ ...s, enrolled: false })); setBioPwOpen(false); return; }
    setBioPwOpen(true);
  }
  async function confirmBio() {
    if (!bioPw || !user?.email) return;
    setBioBusy(true); setBioErr("");
    try {
      /* Raw fetch, not api.post: a wrong password is a 401, and api.req treats any
         401 as an expired session and signs the user out. */
      const res = await fetch(API_URL + "/api/auth/login", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: String(user.email).toLowerCase(), password: bioPw, rememberMe: true }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(res.status === 401 ? "Incorrect password" : (r.error || "Could not verify your password"));
      if (!r || !r.refreshToken) throw new Error("Sign out and sign back in to turn this on.");
      if (r.token) api.setToken(r.token);
      const ok = await bioEnroll("server", user.email, r.refreshToken);
      if (!ok) throw new Error("Could not turn on " + bioSt.label + ". Check that it is set up in iOS Settings.");
      setBioSt(s => ({ ...s, enrolled: true })); setBioPwOpen(false); setBioPw("");
    } catch (e) {
      setBioErr((e && e.message) || "Incorrect password");
    } finally { setBioBusy(false); }
  }

  /* [notif-prefs-full-2026-10-18] savePrefs used to post only {push,sms,email} and the
     backend replaced the whole notif_prefs blob, wiping jobAlerts / msgAlerts /
     deadlineAlerts / soundTone / dailyDigest. The backend now merges, and the app also
     sends the full object: every stored key plus the three toggles, kept in a ref so
     back-to-back toggles build on each other rather than on the stale user payload. */
  const prefsRef = React.useRef(prefs0 && typeof prefs0 === "object" && !Array.isArray(prefs0) ? { ...prefs0 } : {});
  async function savePrefs(next) {
    const full = { ...prefsRef.current, ...next };
    prefsRef.current = full;
    try {
      await api.patch("/api/auth/profile", { notifPrefs: full });
    } catch (e) { console.warn("[settings] preferences", e.message); }
  }
  async function togglePush(v)  {
    setPush(v);
    setPushError("");
    savePrefs({ push: v, sms, email: emailPref });
    // Newly enabled → request OS permission + register the APNs token now.
    // Newly disabled → forget the token server-side so we stop targeting it.
    if (v) {
      try {
        const r = await registerPushNotifications();
        if (r && r.granted === false) {
          // OS denied (or plugin unavailable) — revert the toggle so the UI
          // doesn't lie, and surface a hint pointing the user at iOS Settings.
          setPush(false);
          savePrefs({ push: false, sms, email: emailPref });
          setPushError("Permission denied. Enable notifications in iOS Settings.");
        }
      } catch (_) {
        setPush(false);
        savePrefs({ push: false, sms, email: emailPref });
        setPushError("Permission denied. Enable notifications in iOS Settings.");
      }
    } else {
      try { unregisterPushNotifications(); } catch (_) {}
    }
  }
  function toggleSms(v)   { setSms(v);       savePrefs({ push, sms: v, email: emailPref }); }
  function toggleEmail(v) { setEmailPref(v); savePrefs({ push, sms, email: v }); }

  function toggleDark(v) {
    // Delegate to the app-level setter so palette C + the whole tree restyle.
    setDarkMode(v);
  }
  function setUnitsPref(v) {
    setUnits(v);
    try { localStorage.setItem("ss_units", v); } catch {}
  }

  return (
    <>
      <DSectionLabel>Notifications</DSectionLabel>
      <DCard>
        <DToggleRow label="Push notifications" sub="Job alerts and updates" value={push} onChange={togglePush} />
        {pushError && (
          <div style={{
            padding: "8px 14px 10px",
            fontSize: 11, color: C.err, fontWeight: 600,
            background: C.errL,
          }}>
            {pushError}
          </div>
        )}
        <DToggleRow label="SMS" sub="Texts for rush jobs and time-critical alerts" value={sms} onChange={toggleSms} />
        <DToggleRow label="Email" sub="Daily summaries and statements" value={emailPref} onChange={toggleEmail} last />
      </DCard>

      <DSectionLabel>Display</DSectionLabel>
      <DCard>
        <DToggleRow label="Dark mode" sub="Reduce screen brightness at night" value={dark} onChange={toggleDark} />
        <div style={{ padding: "13px 14px" }}>
          <div style={{ fontSize: 13, color: C.deep, fontWeight: 600, marginBottom: 8 }}>Distance units</div>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { id: "mi", label: "Miles" },
              { id: "km", label: "Kilometers" },
            ].map(u => {
              const active = units === u.id;
              return (
                <button key={u.id} onClick={() => setUnitsPref(u.id)} style={{
                  flex: 1, padding: "9px 0", borderRadius: 10,
                  border: `1px solid ${active ? C.deep : C.steelXL}`,
                  background: active ? C.deep : C.white,
                  color: active ? C.white : C.deep,
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                }}>
                  {active && <Check size={12} />}
                  {u.label}
                </button>
              );
            })}
          </div>
        </div>
      </DCard>

      <DSectionLabel>Security</DSectionLabel>
      <DCard>
        {bioSt.available && (
          <>
            <DToggleRow label={"Sign in with " + bioSt.label} sub={"Use " + bioSt.label + " instead of your password on this iPhone"} value={bioSt.enrolled || bioPwOpen} onChange={toggleBio} />
            {bioPwOpen && !bioSt.enrolled && (
              <div style={{ padding: "4px 14px 12px", borderBottom: `1px solid ${C.steelXXL}` }}>
                <div style={{ fontSize: 11, color: C.steel, marginBottom: 6 }}>Enter your password to turn on {bioSt.label}.</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input type="password" value={bioPw} onChange={e => setBioPw(e.target.value)} autoComplete="current-password"
                    style={{ flex: 1, padding: "9px 10px", borderRadius: 8, border: `1px solid ${C.steelXL}`, fontSize: 16, minWidth: 0 }} />
                  <button onClick={confirmBio} disabled={bioBusy || !bioPw} style={{
                    padding: "9px 14px", borderRadius: 8, border: "none", background: C.deep, color: C.white,
                    fontSize: 12, fontWeight: 700, cursor: bioBusy ? "wait" : "pointer",
                  }}>{bioBusy ? "..." : "Turn on"}</button>
                </div>
                {bioErr && <div style={{ fontSize: 11, color: C.err, fontWeight: 600, marginTop: 6 }}>{bioErr}</div>}
              </div>
            )}
          </>
        )}
        <button onClick={() => setPwOpen(true)} style={{
          width: "100%", padding: "13px 14px", background: "none", border: "none",
          display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textAlign: "left",
        }}>
          <Lock size={16} color={C.deep} />
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.deep }}>Change password</div>
          <ChevronRight size={16} color={C.steel} />
        </button>
      </DCard>

      <DSectionLabel>Account</DSectionLabel>
      <DCard style={{ marginBottom: 0 }}>
        <button onClick={() => { if (onClose) onClose(); logout(); }} style={{
          width: "100%", padding: "13px 14px", background: "none", border: "none",
          borderBottom: `1px solid ${C.steelXXL}`,
          display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textAlign: "left",
        }}>
          <LogOut size={16} color={C.err} />
          <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.err }}>Sign out</div>
          <ChevronRight size={16} color={C.err} />
        </button>
        {/* [account-delete-2026-09-30] [server-parity-web-2026-10-05] */}
        <button onClick={() => setDelOpen(true)} style={{
          width: "100%", padding: "13px 14px", background: "none", border: "none",
          display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textAlign: "left",
        }}>
          <Trash2 size={16} color={C.err} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.err }}>Delete account</div>
            <div style={{ fontSize: 10, color: C.steel, marginTop: 1 }}>Permanently delete your account and data</div>
          </div>
          <ChevronRight size={16} color={C.err} />
        </button>
      </DCard>

      {pwOpen && <ChangePasswordSheet onClose={() => setPwOpen(false)} />}
      {delOpen && <DeleteAccountSheet onClose={() => setDelOpen(false)} />}
    </>
  );
}

// [account-delete-2026-09-30] IN-APP ACCOUNT DELETION (App Store Guideline 5.1.1(v)).
// [server-parity-web-2026-10-05] ported from strikeserve-mobile. Calls
// POST /api/account/delete (cancels any active subscription, anonymizes the
// user's personal data, disables the login), then shows a confirmation and signs out.
// The user must type DELETE to arm the button so it cannot be triggered by accident.
function DeleteAccountPanel({ onCancel }) {
  const { logout } = React.useContext(AppCtx);
  const [confirmText, setConfirmText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState("");
  const [done, setDone] = React.useState(false);
  const armed = confirmText.trim().toUpperCase() === "DELETE";

  async function submit() {
    if (!armed || busy) return;
    setBusy(true);
    setErrMsg("");
    try {
      const r = await api.post("/api/account/delete", { reason: "self-service account deletion", confirm_text: "DELETE" });
      if (r && r.ok === false) throw new Error(r.error || "Account deletion failed");
      setDone(true);
    } catch (e) {
      setErrMsg((e && e.message) || "Account deletion failed. Please try again or contact support@strikeserve.com.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div style={{ padding: "8px 2px" }}>
        <DCard>
          <div style={{ padding: "18px 14px", textAlign: "center" }}>
            <CheckCircle2 size={40} color={C.ok} />
            <div style={{ fontSize: 17, fontWeight: 800, color: C.deep, marginTop: 10 }}>Your account has been deleted</div>
            <div style={{ fontSize: 13, color: C.steel, marginTop: 8, lineHeight: 1.5 }}>
              Your personal information has been removed and you can no longer sign in with this account.
              Any active subscription has been cancelled.
            </div>
          </div>
        </DCard>
        <DGoldButton onClick={() => { try { logout(); } catch (_) {} }} icon={Check}>Done</DGoldButton>
      </div>
    );
  }

  return (
    <div style={{ padding: "8px 2px" }}>
      <DInlineAlert kind="err">{errMsg}</DInlineAlert>
      <DCard>
        <div style={{ padding: "14px 14px 4px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={18} color={C.err} />
            <div style={{ fontSize: 15, fontWeight: 800, color: C.err }}>Permanently delete your account</div>
          </div>
          <div style={{ fontSize: 13, color: C.deep, marginTop: 10, lineHeight: 1.55 }}>This will:</div>
          <ul style={{ fontSize: 13, color: C.deep, lineHeight: 1.55, margin: "6px 0 0", paddingLeft: 18 }}>
            <li>Delete your profile and personal information</li>
            <li>Cancel any active StrikeServe subscription</li>
            <li>Sign you out and prevent this account from signing in again</li>
          </ul>
          <div style={{ fontSize: 12, color: C.steel, marginTop: 10, lineHeight: 1.5 }}>
            Records the law requires us to keep (such as completed proofs of service and payment records) are retained with your personal details removed. This cannot be undone.
          </div>
        </div>
        <DInput label="Type DELETE to confirm" value={confirmText} onChange={setConfirmText} placeholder="DELETE" />
      </DCard>
      <button onClick={submit} disabled={!armed || busy} style={{
        width: "100%", padding: 14, marginBottom: 10,
        background: (!armed || busy) ? C.steelL : C.err, color: C.white,
        border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800,
        cursor: (!armed || busy) ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
      }}>
        <Trash2 size={16} />
        {busy ? "Deleting account..." : "Delete my account"}
      </button>
      {onCancel && (
        <button onClick={onCancel} disabled={busy} style={{
          width: "100%", padding: 12, background: "none", border: `1px solid ${C.steelXL}`,
          borderRadius: 12, fontSize: 14, fontWeight: 700, color: C.deep, cursor: "pointer",
        }}>Cancel</button>
      )}
    </div>
  );
}

function DeleteAccountSheet({ onClose }) {
  return (
    <Sheet onClose={onClose} small>
      <div style={{ background: C.deep, padding: "16px 16px 14px", color: C.white }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button onClick={onClose} style={iconBtn}>
            <ChevronLeft size={22} color={C.white} />
          </button>
          <div style={{ fontSize: 16, fontWeight: 800 }}>Delete account</div>
          <div style={{ width: 38 }} />
        </div>
      </div>
      <div style={{ padding: "16px 14px 24px" }}>
        <DeleteAccountPanel onCancel={onClose} />
      </div>
    </Sheet>
  );
}

function ChangePasswordSheet({ onClose }) {
  const [oldPw, setOldPw] = React.useState("");
  const [newPw, setNewPw] = React.useState("");
  const [confirmPw, setConfirmPw] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [okMsg, setOkMsg] = React.useState("");
  const [errMsg, setErrMsg] = React.useState("");

  async function submit() {
    if (busy) return;
    setOkMsg("");
    setErrMsg("");
    if (!oldPw || !newPw) { setErrMsg("Please fill in both passwords"); return; }
    if (newPw !== confirmPw) { setErrMsg("New passwords do not match"); return; }
    const pwStrong = newPw.length >= 8 && /[A-Z]/.test(newPw) && /[a-z]/.test(newPw) && /\d/.test(newPw)
        && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(newPw);
    if (!pwStrong) { setErrMsg("New password must be 8+ chars and include upper, lower, number, and special character"); return; }
    setBusy(true);
    try {
      await api.post("/api/auth/change-password", {
        currentPassword: oldPw,
        newPassword: newPw,
      });
      setOkMsg("Password updated");
      setOldPw(""); setNewPw(""); setConfirmPw("");
      setTimeout(() => onClose && onClose(), 900);
    } catch (e) {
      setErrMsg(e.message || "Could not change password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet onClose={onClose} small>
      <div style={{ background: C.deep, padding: "16px 16px 14px", color: C.white }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button onClick={onClose} style={iconBtn}>
            <ChevronLeft size={22} color={C.white} />
          </button>
          <div style={{ fontSize: 16, fontWeight: 800 }}>Change password</div>
          <div style={{ width: 38 }} />
        </div>
      </div>
      <div style={{ padding: "16px 14px 24px" }}>
        <DInlineAlert kind="ok">{okMsg}</DInlineAlert>
        <DInlineAlert kind="err">{errMsg}</DInlineAlert>
        <DCard>
          <DInput label="Current password" value={oldPw} onChange={setOldPw} type="password" />
          <DInput label="New password" value={newPw} onChange={setNewPw} type="password" />
          <DInput label="Confirm new password" value={confirmPw} onChange={setConfirmPw} type="password" />
        </DCard>
        <DGoldButton onClick={submit} disabled={busy} icon={Check}>
          {busy ? "Updating..." : "Update password"}
        </DGoldButton>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TRAINING — list of videos with real playback + backend-sync progress
// ─────────────────────────────────────────────────────────────────────────
// Seed catalog used as the offline / pre-migration fallback. Backend
// /api/training/videos is authoritative once available.
// 11-video canonical catalog. Must stay in lockstep with:
//   * cle-backend/migrations/20260602_training_videos.sql
//   * cle-backend/routes/training.js (const SEED_VIDEOS)
// Backend /api/training/videos overrides this once available.
const TRAINING_VIDEOS = [
  { id: 1,  title: "Getting Started: Application & Approval",  duration: "12m", category: "Required", description: "Application, onboarding, and approval walkthrough for new StrikeServe servers.",                video_url: null, thumbnail_url: null, required: true,  sort_order: 5,  done: false },
  { id: 2,  title: "Intro to Process Serving",                  duration: "10m", category: "Required", description: "What process serving is, the role you play, and the lifecycle of a serve.",                     video_url: null, thumbnail_url: null, required: true,  sort_order: 10, done: false },
  { id: 3,  title: "Legal Rules of Process Serving",            duration: "15m", category: "Required", description: "CCP §§ 415–417 fundamentals: who can serve, who can be served, and what counts as valid service.", video_url: null, thumbnail_url: null, required: true,  sort_order: 20, done: false },
  { id: 4,  title: "Methods of Legal Service in California",    duration: "14m", category: "Required", description: "Personal, substituted, mail, and publication service — when each applies and how to execute.",  video_url: null, thumbnail_url: null, required: true,  sort_order: 30, done: false },
  { id: 5,  title: "Proof of Service Essentials",               duration: "11m", category: "Required", description: "POS-010/POS-040 completion, signature rules, and common rejection reasons.",                    video_url: null, thumbnail_url: null, required: true,  sort_order: 40, done: false },
  { id: 6,  title: "Server Rights and Safety",                  duration: "12m", category: "Required", description: "Your legal rights on private property, trespass limits, and de-escalation basics.",             video_url: null, thumbnail_url: null, required: true,  sort_order: 50, done: false },
  { id: 7,  title: "Ethics for Process Servers",                duration: "10m", category: "Required", description: "Professional conduct, honesty under oath, and avoiding sewer-service traps.",                   video_url: null, thumbnail_url: null, required: true,  sort_order: 60, done: false },
  { id: 8,  title: "Safety Rules for Process Servers",          duration: "13m", category: "Safety",   description: "Situational awareness, exit planning, and field-safety SOPs.",                                  video_url: null, thumbnail_url: null, required: false, sort_order: 10, done: false },
  { id: 9,  title: "Serving Difficult Recipients",              duration: "12m", category: "Safety",   description: "Handling hostile, evasive, or barricaded servees while staying safe and lawful.",               video_url: null, thumbnail_url: null, required: false, sort_order: 20, done: false },
  { id: 10, title: "Skip Tracing Techniques",                   duration: "14m", category: "Quality",  description: "Finding evasive servees — open-source lookups, address verification, and pattern of life.",     video_url: null, thumbnail_url: null, required: false, sort_order: 10, done: false },
  { id: 11, title: "Skip Tracing (Advanced)",                   duration: "13m", category: "Quality",  description: "Deeper skip-tracing workflows, paid-source tactics, and case studies.",                         video_url: null, thumbnail_url: null, required: false, sort_order: 20, done: false },
];

const SS_TRAINING_COMPLETED_KEY = "ss_training_completed";
const SS_TRAINING_CATALOG_KEY = "ss_training_catalog";
const SS_TRAINING_RESUME_KEY = "ss_training_resume"; // { [videoId]: positionSeconds }

// loadTrainingCompletion() — read the localStorage completion map. Used as the
// fallback / seed before /api/training/progress responds. Backend wins when it
// returns data — see mergeProgress() below.
function loadTrainingCompletion() {
  try {
    const raw = localStorage.getItem(SS_TRAINING_COMPLETED_KEY);
    if (!raw) return {}; // No pre-completed defaults — server completion is authoritative.
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch { return {}; }
}

function saveTrainingCompletion(map) {
  try { localStorage.setItem(SS_TRAINING_COMPLETED_KEY, JSON.stringify(map || {})); } catch {}
}

function loadTrainingCatalogCache() {
  try {
    const raw = localStorage.getItem(SS_TRAINING_CATALOG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch { return null; }
}

function saveTrainingCatalogCache(arr) {
  try { localStorage.setItem(SS_TRAINING_CATALOG_KEY, JSON.stringify(arr || [])); } catch {}
}

function getResumePosition(videoId) {
  try {
    const raw = localStorage.getItem(SS_TRAINING_RESUME_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    const v = parsed && parsed[String(videoId)];
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch { return 0; }
}

function saveResumePosition(videoId, seconds) {
  try {
    const raw = localStorage.getItem(SS_TRAINING_RESUME_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const next = (parsed && typeof parsed === "object") ? parsed : {};
    const n = Math.max(0, Math.floor(Number(seconds) || 0));
    if (n <= 0) { delete next[String(videoId)]; }
    else { next[String(videoId)] = n; }
    localStorage.setItem(SS_TRAINING_RESUME_KEY, JSON.stringify(next));
  } catch {}
}

// mergeProgress — take a backend payload like
//   { completed_video_ids: [1,2,5], required_videos_completed: 2, required_videos_total: 3, positions: { "3": 142 } }
// and normalize into { completedMap, requiredDone, requiredTotal, positions }.
// Falls back gracefully when fields are missing.
function mergeProgress(payload, fallbackMap) {
  const out = {
    completedMap: { ...(fallbackMap || {}) },
    requiredDone: 0,
    requiredTotal: 0,
    positions: {},
  };
  if (!payload || typeof payload !== "object") return out;
  if (Array.isArray(payload.completed_video_ids)) {
    // Backend is authoritative when present — replace, don't merge.
    out.completedMap = {};
    for (const id of payload.completed_video_ids) out.completedMap[id] = true;
  }
  if (Number.isFinite(payload.required_videos_completed)) out.requiredDone = payload.required_videos_completed;
  if (Number.isFinite(payload.required_videos_total)) out.requiredTotal = payload.required_videos_total;
  if (payload.positions && typeof payload.positions === "object") out.positions = payload.positions;
  return out;
}

function trainingCategoryStyle(cat) {
  const c = String(cat || "").toLowerCase();
  if (c === "required") return { bg: C.errL, fg: C.err };
  if (c === "safety")   return { bg: C.warnL, fg: C.warn };
  return { bg: C.infoL, fg: C.info };
}

// detectVideoKind — figure out how to render a given video_url.
// Returns { kind: "youtube"|"vimeo"|"mp4"|"none", embedUrl?: string }.
function detectVideoKind(url) {
  if (!url || typeof url !== "string") return { kind: "none" };
  const trimmed = url.trim();
  if (!trimmed) return { kind: "none" };

  // YouTube — watch?v=, youtu.be/, or already-embedded (incl. youtube-nocookie.com)
  let m = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube(?:-nocookie)?\.com\/embed\/)([A-Za-z0-9_-]{6,})/);
  if (m) {
    // Use youtube-nocookie.com to avoid WKWebView 3rd-party cookie blocks
    // that surface as YouTube error 153 / "video config error" in Capacitor iOS.
    return { kind: "youtube", embedUrl: `https://www.youtube-nocookie.com/embed/${m[1]}?rel=0&modestbranding=1&playsinline=1&enablejsapi=0` };
  }

  // Vimeo — vimeo.com/123456 or player.vimeo.com/video/123456
  m = trimmed.match(/(?:player\.vimeo\.com\/video\/|vimeo\.com\/)(\d+)/);
  if (m) {
    return { kind: "vimeo", embedUrl: `https://player.vimeo.com/video/${m[1]}` };
  }

  // Default: treat as a direct media URL (mp4 / hls / webm). The <video> element handles it.
  return { kind: "mp4" };
}

function TrainingScreen() {
  // Hydrate catalog + completion from cache immediately so cold launches don't
  // flash an empty screen. Cache miss + no backend = fall back to the seed.
  const [catalog, setCatalog] = React.useState(() => loadTrainingCatalogCache() || TRAINING_VIDEOS);
  const [catalogLoaded, setCatalogLoaded] = React.useState(() => loadTrainingCatalogCache() != null);
  const [completed, setCompleted] = React.useState(loadTrainingCompletion);
  const [positions, setPositions] = React.useState({}); // { [videoId]: seconds } from backend
  const [requiredDone, setRequiredDone] = React.useState(0);
  const [requiredTotal, setRequiredTotal] = React.useState(0);
  const [loadingCold, setLoadingCold] = React.useState(() => loadTrainingCatalogCache() == null);
  const [selected, setSelected] = React.useState(null);

  // Debounced progress refetch — used after a POST to confirm server state.
  const refetchTimer = React.useRef(null);

  // Fetch catalog from backend (silent on 404 — keeps the seed fallback).
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/api/training/videos");
        if (cancelled) return;
        const arr = Array.isArray(r) ? r : (Array.isArray(r?.videos) ? r.videos : null);
        if (arr && arr.length) {
          setCatalog(arr);
          saveTrainingCatalogCache(arr);
        }
        setCatalogLoaded(true);
      } catch (e) {
        // 404 / network error → keep the seed. Don't surface anything.
        setCatalogLoaded(true);
      } finally {
        if (!cancelled) setLoadingCold(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch progress (completion + saved positions + required-gate counters).
  const refreshProgress = React.useCallback(async () => {
    try {
      const r = await api.get("/api/training/progress");
      const merged = mergeProgress(r, loadTrainingCompletion());
      setCompleted(merged.completedMap);
      saveTrainingCompletion(merged.completedMap);
      setPositions(merged.positions || {});
      setRequiredDone(merged.requiredDone || 0);
      setRequiredTotal(merged.requiredTotal || 0);
    } catch (e) {
      // 404 / offline → leave local state alone.
    }
  }, []);

  React.useEffect(() => { refreshProgress(); }, [refreshProgress]);

  // Schedule a debounced re-fetch (used after each successful POST).
  const scheduleRefresh = React.useCallback(() => {
    if (refetchTimer.current) clearTimeout(refetchTimer.current);
    refetchTimer.current = setTimeout(() => { refreshProgress(); }, 1500);
  }, [refreshProgress]);

  React.useEffect(() => () => {
    if (refetchTimer.current) clearTimeout(refetchTimer.current);
  }, []);

  // Local optimistic mark. Persists to localStorage immediately (so it survives
  // a refresh even if the backend POST fails). Backend confirmation happens via
  // the parent POST + refreshProgress() flow.
  const markCompletedLocal = React.useCallback((id, val) => {
    setCompleted(prev => {
      const next = { ...prev, [id]: !!val };
      saveTrainingCompletion(next);
      return next;
    });
  }, []);

  // Compute the gate from the most authoritative numbers we have. If the
  // backend gave us required_videos_total, trust it; otherwise derive from the
  // catalog ourselves.
  const derivedRequiredTotal = catalog.filter(v => v.required || v.category === "Required").length;
  const derivedRequiredDone = catalog.filter(v => (v.required || v.category === "Required") && completed[v.id]).length;
  const gateTotal = requiredTotal || derivedRequiredTotal;
  const gateDone = requiredTotal ? requiredDone : derivedRequiredDone;
  const gateRemaining = Math.max(0, gateTotal - gateDone);

  const total = catalog.length;
  const done = catalog.filter(v => completed[v.id]).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  // Cold-cache skeleton: 4 video-row placeholders mirroring the real
  // catalog row footprint (thumbnail + title + metadata). Replaces the
  // old spinner so the layout doesn't pop when the catalog hydrates.
  if (loadingCold && !catalogLoaded) {
    return (
      <div style={{ padding: "16px 0" }}>
        <SkeletonGroup label="Loading training catalog">
          {/* Header card placeholder (progress / certification banner) */}
          <div style={{
            background: C.white, borderRadius: 16, padding: 16, marginBottom: 16,
            border: `1px solid ${C.steelXL}`,
          }}>
            <SkeletonLine width="40%" height={10} />
            <SkeletonLine width="70%" height={18} />
            <SkeletonRect width="100%" height={8} radius={4} />
          </div>
          {[0,1,2,3].map(i => (
            <div key={i} style={{
              display: "flex", gap: 12, background: C.white, borderRadius: 14,
              padding: 12, marginBottom: 10, border: `1px solid ${C.steelXL}`,
            }}>
              <SkeletonRect width={88} height={56} radius={8} />
              <div style={{ flex: 1 }}>
                <SkeletonLine width="80%" height={13} />
                <SkeletonLine width="50%" height={11} style={{ marginBottom: 0 }} />
              </div>
            </div>
          ))}
        </SkeletonGroup>
      </div>
    );
  }

  return (
    <>
      {/* Progress card */}
      <div style={{
        background: `linear-gradient(135deg, ${C.purple} 0%, #5a23c4 100%)`,
        borderRadius: 16, padding: 16, marginBottom: 16, color: C.white,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.goldL, letterSpacing: "0.1em" }}>CERTIFICATION</div>
            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>{done} of {total} videos completed</div>
            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>{pct}% complete</div>
          </div>
          <Award size={32} color={C.goldL} />
        </div>
        <div style={{ height: 8, background: "rgba(255,255,255,0.15)", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: C.gold, transition: "width 0.3s" }} />
        </div>

        {/* [cert-of-completion-2026-07-03] Certificate CTA — appears once all required
            videos are done. Opens the in-app StrikeServe Certificate of Completion overlay
            (was a dead /api/training/certificate backend link that returned "Not authenticated"). */}
        {gateRemaining === 0 && done > 0 && (
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("ss-open-certificate"))}
            style={{
              width: "100%", marginTop: 12, padding: "12px 14px",
              background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldM} 100%)`,
              color: C.deep, border: "none", borderRadius: 12,
              fontSize: 13, fontWeight: 800, letterSpacing: "0.02em", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              boxShadow: `0 4px 12px ${C.gold}55`,
            }}>
            <Award size={16} /> View your certificate
          </button>
        )}
      </div>

      {/* Required-training gate hint — UX only; backend enforces the real claim gate. */}
      {gateRemaining > 0 && (
        <div style={{
          background: C.deep, color: C.white, borderRadius: 12, padding: "10px 12px",
          marginBottom: 12, display: "flex", alignItems: "flex-start", gap: 8,
          fontSize: 12, fontWeight: 600, lineHeight: 1.4,
        }}>
          <Lock size={14} color={C.goldL} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1 }}>
            Complete {gateRemaining} required {gateRemaining === 1 ? "video" : "videos"} to access higher-paying jobs.
          </div>
        </div>
      )}

      <DSectionLabel>Videos</DSectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {catalog.map(v => {
          const isDone = !!completed[v.id];
          const pill = trainingCategoryStyle(v.category);
          const hasVideo = !!v.video_url;
          return (
            <button key={v.id} onClick={() => setSelected(v)} style={{
              background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 14,
              padding: 12, display: "flex", alignItems: "center", gap: 12,
              cursor: "pointer", textAlign: "left", width: "100%",
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 10,
                background: `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                position: "relative", flexShrink: 0,
              }}>
                <PlayCircle size={20} color={C.white} fill="rgba(255,255,255,0.15)" />
                {isDone && (
                  <div style={{
                    position: "absolute", top: -4, right: -4, width: 18, height: 18, borderRadius: "50%",
                    background: C.ok, display: "flex", alignItems: "center", justifyContent: "center",
                    border: `2px solid ${C.white}`,
                  }}>
                    <Check size={10} color={C.white} strokeWidth={3} />
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{
                    background: pill.bg, color: pill.fg, fontSize: 9, fontWeight: 800,
                    letterSpacing: "0.05em", padding: "2px 6px", borderRadius: 4,
                  }}>{(v.category || "").toUpperCase()}</span>
                  {!hasVideo && (
                    <span style={{
                      background: C.steelXL, color: C.steel, fontSize: 9, fontWeight: 800,
                      letterSpacing: "0.05em", padding: "2px 6px", borderRadius: 4,
                    }}>COMING SOON</span>
                  )}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, lineHeight: 1.3 }}>{v.title}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: C.steel, marginTop: 3 }}>
                  <Clock size={10} /> {v.duration}
                </div>
              </div>
              {isDone
                ? <CheckCircle2 size={18} color={C.ok} />
                : <ChevronRight size={16} color={C.steel} />}
            </button>
          );
        })}
      </div>

      {selected && (
        <TrainingVideoSheet
          video={selected}
          isDone={!!completed[selected.id]}
          resumePosition={Number(positions[selected.id] || positions[String(selected.id)] || getResumePosition(selected.id)) || 0}
          onMarkLocal={(val) => markCompletedLocal(selected.id, val)}
          onProgressSynced={scheduleRefresh}
          onClose={() => setSelected(null)}
          catalog={catalog}
          completed={completed}
          onSelectVideo={(v) => setSelected(v)}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TrainingVideoSheet — real <video> / iframe playback with backend-sync
// progress writes. Auto-completes at 95% watched, throttled position pings
// every 20s, and a manual "Mark complete" override.
// ─────────────────────────────────────────────────────────────────────────
function TrainingVideoSheet({ video, isDone, resumePosition, onMarkLocal, onProgressSynced, onClose, catalog, completed, onSelectVideo }) {
  const videoRef = React.useRef(null);
  const iframeRef = React.useRef(null);
  const lastPingRef = React.useRef(0);     // last position-only POST (epoch ms)
  const completedFiredRef = React.useRef(false); // guards the 95%/ended auto-complete
  const inFlightRef = React.useRef(false); // crude mutex so we don't stack POSTs
  const metadataAppliedRef = React.useRef(false);

  const [localDone, setLocalDone] = React.useState(!!isDone);
  const [watchPct, setWatchPct] = React.useState(0); // 0..1 for unlocking the gold button
  const [errMsg, setErrMsg] = React.useState("");

  // Reset transient state when the selected video changes.
  React.useEffect(() => {
    setLocalDone(!!isDone);
    setWatchPct(0);
    setErrMsg("");
    completedFiredRef.current = !!isDone; // already completed → don't re-fire
    metadataAppliedRef.current = false;
    lastPingRef.current = 0;
  }, [video?.id, isDone]);

  // Pause on close / unmount so audio doesn't bleed through after the sheet dismisses.
  React.useEffect(() => {
    return () => {
      try { videoRef.current?.pause(); } catch {}
      try {
        // For iframe (YouTube/Vimeo), reload the src to halt playback — neither
        // exposes a postMessage API we can rely on without their JS SDK.
        const f = iframeRef.current;
        if (f && f.src) {
          const s = f.src; f.src = ""; f.src = s;
        }
      } catch {}
    };
  }, []);

  const kind = detectVideoKind(video?.video_url);

  // ── Backend sync helper ─────────────────────────────────────────────
  // Silent — never surface backend errors to the user (just keep playing).
  // Throttling is handled by the callers, not here.
  const postProgress = React.useCallback(async (positionSeconds, markCompleted) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      await api.post("/api/training/progress", {
        video_id: video.id,
        position_seconds: Math.max(0, Math.floor(Number(positionSeconds) || 0)),
        completed: !!markCompleted,
      });
      if (onProgressSynced) onProgressSynced();
    } catch (e) {
      // Swallow — backend may not exist yet (pre-migration). The localStorage
      // mirror in TrainingScreen keeps the UI honest in the meantime.
    } finally {
      inFlightRef.current = false;
    }
  }, [video?.id, onProgressSynced]);

  // ── <video> event handlers ──────────────────────────────────────────
  const handleLoadedMetadata = React.useCallback(() => {
    if (metadataAppliedRef.current) return;
    metadataAppliedRef.current = true;
    const v = videoRef.current;
    if (!v) return;
    const start = Number(resumePosition) || 0;
    // Don't restore if user is already at/past the saved point (e.g., near the end).
    if (start > 1 && Number.isFinite(v.duration) && start < (v.duration - 5)) {
      try { v.currentTime = start; } catch {}
    }
  }, [resumePosition]);

  const handleTimeUpdate = React.useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const dur = Number(v.duration);
    const cur = Number(v.currentTime);
    if (!Number.isFinite(dur) || dur <= 0 || !Number.isFinite(cur)) return;

    const pct = cur / dur;
    setWatchPct(pct);

    // Mirror the position to localStorage every tick — cheap, no rerenders.
    saveResumePosition(video.id, cur);

    // Auto-complete at >= 95%.
    if (pct >= 0.95 && !completedFiredRef.current) {
      completedFiredRef.current = true;
      setLocalDone(true);
      onMarkLocal(true);
      saveResumePosition(video.id, 0); // clear resume on completion
      postProgress(cur, true);
      return;
    }

    // Otherwise: position-only ping every ~20s while playing.
    if (pct < 0.95) {
      const now = Date.now();
      if (now - lastPingRef.current >= 20000) {
        lastPingRef.current = now;
        postProgress(cur, false);
      }
    }
  }, [video?.id, onMarkLocal, postProgress]);

  const handleEnded = React.useCallback(() => {
    const v = videoRef.current;
    const cur = v ? Number(v.currentTime) || 0 : 0;
    if (!completedFiredRef.current) {
      completedFiredRef.current = true;
      setLocalDone(true);
      onMarkLocal(true);
      saveResumePosition(video.id, 0);
      postProgress(cur, true);
    }
  }, [video?.id, onMarkLocal, postProgress]);

  const handleError = React.useCallback(() => {
    setErrMsg("Video failed to load. You can still mark this video complete manually.");
  }, []);

  // Manual "Mark complete" tap — overrides the 95% gate.
  // Hard no-op when the module hasn't shipped yet (`!video.video_url`): no
  // localStorage write, no backend POST, no local state change. This prevents
  // QA / users from racking up "completions" against placeholders before the
  // real content lands.
  const handleManualComplete = React.useCallback(() => {
    if (localDone) return;
    if (!video?.video_url) return;
    const v = videoRef.current;
    const cur = v ? Number(v.currentTime) || 0 : 0;
    completedFiredRef.current = true;
    setLocalDone(true);
    onMarkLocal(true);
    saveResumePosition(video.id, 0);
    postProgress(cur, true);
  }, [localDone, video?.id, video?.video_url, onMarkLocal, postProgress]);

  // Manual UN-complete (only really useful in dev / for QA).
  const handleManualUncomplete = React.useCallback(() => {
    if (!localDone) return;
    const v = videoRef.current;
    const cur = v ? Number(v.currentTime) || 0 : 0;
    completedFiredRef.current = false;
    setLocalDone(false);
    onMarkLocal(false);
    postProgress(cur, false);
  }, [localDone, video?.id, onMarkLocal, postProgress]);

  // Button is enabled once 95% watched OR via manual tap path; we always allow
  // a manual tap (spec says "OR user manually taps"), so the gold button stays
  // tappable. We only show "Completed" state when localDone.
  const buttonReady = watchPct >= 0.95 || !video?.video_url; // soft hint; never disabled per spec

  const pill = trainingCategoryStyle(video?.category);

  // ── Player block ────────────────────────────────────────────────────
  let playerBlock;
  if (kind.kind === "none") {
    // No upload yet — placeholder card per spec.
    playerBlock = (
      <div style={{
        background: video?.thumbnail_url
          ? `url(${video.thumbnail_url}) center/cover, linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`
          : `linear-gradient(135deg, ${C.deep} 0%, ${C.royal} 100%)`,
        borderRadius: 14, padding: "40px 16px", marginBottom: 14,
        color: C.white, textAlign: "center",
      }}>
        <PlayCircle size={48} color={C.goldL} style={{ marginBottom: 10, opacity: 0.7 }} />
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>Coming soon</div>
        <div style={{ fontSize: 12, opacity: 0.85, lineHeight: 1.4 }}>
          This video hasn't been uploaded yet. Tap “Mark complete” to record manual confirmation.
        </div>
      </div>
    );
  } else if (kind.kind === "youtube" || kind.kind === "vimeo") {
    // External players — render an iframe. Auto-complete by 95% doesn't work
    // here (no timeupdate without the provider SDK), so the user must use the
    // manual "Mark complete" button. We still write a single position=0
    // completion when they tap it.
    playerBlock = (
      <div style={{
        width: "100%", aspectRatio: "16 / 9", marginBottom: 14, borderRadius: 12,
        overflow: "hidden", background: "#000",
      }}>
        <iframe
          ref={iframeRef}
          src={kind.embedUrl}
          title={video.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ width: "100%", height: "100%", border: "none", display: "block" }}
        />
      </div>
    );
  } else {
    // Direct media URL — HTML5 <video> with controls.
    playerBlock = (
      <video
        ref={videoRef}
        src={video.video_url}
        poster={video.thumbnail_url || undefined}
        controls
        playsInline
        preload="metadata"
        style={{ width: "100%", maxHeight: 360, background: "#000", borderRadius: 12, marginBottom: 14, display: "block" }}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onError={handleError}
      />
    );
  }

  // External player note (under iframe) — explain why auto-complete is manual.
  const externalNote = (kind.kind === "youtube" || kind.kind === "vimeo")
    ? "External player — tap “Mark complete” once you've finished watching."
    : null;

  // ── Next video ──────────────────────────────────────────────────────────
  // Advance to the next still-incomplete video in catalog order, preferring
  // required/uncompleted modules first, then any uncompleted module. Treats
  // the just-watched video as done if it was completed this session.
  const nextVideo = React.useMemo(() => {
    const list = Array.isArray(catalog) ? catalog : [];
    if (!list.length) return null;
    const isComplete = (v) =>
      (completed && !!completed[v.id]) || (v.id === video?.id && localDone);
    const isRequired = (v) => v.required || v.category === "Required";
    const idx = list.findIndex(v => v.id === video?.id);
    // Order to scan: everything after the current item, then everything before
    // it (so the list wraps), so "Next" always finds the nearest pending item.
    const after = idx >= 0 ? list.slice(idx + 1) : list;
    const before = idx >= 0 ? list.slice(0, idx) : [];
    const scan = after.concat(before);
    return scan.find(v => isRequired(v) && !isComplete(v))
        || scan.find(v => !isComplete(v))
        || null;
  }, [catalog, completed, video?.id, localDone]);

  return (
    <Sheet onClose={onClose} small>
      <div style={{ background: C.deep, padding: "16px 16px 14px", color: C.white }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button onClick={onClose} style={iconBtn}>
            <ChevronLeft size={22} color={C.white} />
          </button>
          <div style={{ fontSize: 14, fontWeight: 800 }}>Training video</div>
          <div style={{ width: 38 }}>
            {localDone && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: C.ok, color: C.white, fontSize: 9, fontWeight: 800,
                padding: "3px 6px", borderRadius: 10, letterSpacing: "0.05em",
              }}>
                <CheckCircle2 size={10} /> DONE
              </div>
            )}
          </div>
        </div>
      </div>

      {/* [sheet-scroll-audit-2026-07-25] flex+minHeight so this actually scrolls under the fixed header. */}
      <div style={{ padding: "16px 14px 24px", overflowY: "auto", flex: 1, minHeight: 0 }}>
        {playerBlock}

        {/* Title row + meta */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <span style={{
              background: pill.bg, color: pill.fg, fontSize: 9, fontWeight: 800,
              letterSpacing: "0.05em", padding: "2px 6px", borderRadius: 4,
            }}>{(video?.category || "").toUpperCase()}</span>
            <span style={{ fontSize: 10, color: C.steel, display: "inline-flex", alignItems: "center", gap: 3 }}>
              <Clock size={10} /> {video?.duration}
            </span>
            {localDone && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 3,
                background: C.okL, color: C.ok, fontSize: 9, fontWeight: 800,
                padding: "2px 6px", borderRadius: 4, letterSpacing: "0.05em",
              }}>
                <CheckCircle2 size={10} /> COMPLETED
              </span>
            )}
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.deep, lineHeight: 1.3 }}>{video?.title}</div>
          {video?.description && (
            <div style={{ fontSize: 12, color: C.steel, marginTop: 6, lineHeight: 1.45 }}>{video.description}</div>
          )}
        </div>

        {externalNote && <DInlineAlert kind="ok">{externalNote}</DInlineAlert>}
        {errMsg && <DInlineAlert kind="err">{errMsg}</DInlineAlert>}

        {/* Watch-progress hint for direct video — purely informational. */}
        {kind.kind === "mp4" && !localDone && watchPct > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{
              height: 4, background: C.steelXL, borderRadius: 2, overflow: "hidden", marginBottom: 4,
            }}>
              <div style={{
                width: `${Math.min(100, Math.round(watchPct * 100))}%`, height: "100%",
                background: watchPct >= 0.95 ? C.ok : C.gold, transition: "width 0.3s",
              }} />
            </div>
            <div style={{ fontSize: 10, color: C.steel, fontWeight: 600 }}>
              {watchPct >= 0.95 ? "Ready to mark complete" : `${Math.round(watchPct * 100)}% watched · 95% required for auto-complete`}
            </div>
          </div>
        )}

        {/* Action row — "Back to list" + "Mark complete" */}
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: 14, background: C.white, color: C.deep,
            border: `1px solid ${C.steelXL}`, borderRadius: 12,
            fontSize: 13, fontWeight: 800, letterSpacing: "0.02em", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            <ChevronLeft size={14} /> Back to list
          </button>
          <div style={{ flex: 1 }}>
            {localDone ? (
              <DGoldButton onClick={handleManualUncomplete} icon={XCircle}>
                Completed
              </DGoldButton>
            ) : (
              <DGoldButton
                onClick={handleManualComplete}
                icon={Check}
                disabled={!video?.video_url}
                title={!video?.video_url ? "Available when this module ships" : undefined}
              >
                Mark complete
              </DGoldButton>
            )}
          </div>
        </div>

        {/* Next video — advances to the next incomplete (required-first) module
            in catalog order without returning to the list. */}
        {nextVideo && (
          <button
            onClick={() => onSelectVideo && onSelectVideo(nextVideo)}
            style={{
              width: "100%", marginTop: 10, padding: 14,
              background: C.deep, color: C.white, border: "none", borderRadius: 12,
              fontSize: 13, fontWeight: 800, letterSpacing: "0.02em", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
            Next video <ArrowRight size={15} />
          </button>
        )}

        {/* Soft "watch 95%" hint when manual is the only path forward */}
        {kind.kind === "mp4" && !localDone && watchPct < 0.95 && (
          <div style={{ fontSize: 10, color: C.steel, marginTop: 8, textAlign: "center", lineHeight: 1.4 }}>
            Tip: watching to 95% auto-marks the video complete.
          </div>
        )}
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// COVERAGE — California county selector
// ─────────────────────────────────────────────────────────────────────────
const CA_COUNTIES = [
  "Sacramento", "Placer", "Yolo", "El Dorado", "Sutter", "Yuba",
  "San Joaquin", "Stanislaus", "Solano", "Contra Costa", "Alameda",
  "San Francisco", "San Mateo", "Santa Clara", "Marin", "Sonoma",
  "Napa", "Nevada", "Amador", "Calaveras", "Tuolumne", "Mariposa",
  "Madera", "Fresno", "Kings", "Tulare", "Kern", "Los Angeles",
  "Orange", "San Bernardino", "Riverside", "San Diego", "Imperial",
  "Ventura", "Santa Barbara", "San Luis Obispo", "Monterey",
  "Santa Cruz", "San Benito", "Merced",
];

/* [account-coverage-sub-2026-10-08] Account-menu "Coverage areas" subtitle. This read
   user.coverage_areas, which /api/auth/me never returns, so it always said "Not set".
   /api/auth/me returns serviceCounties (users.service_counties, free text: a Postgres
   array literal "{A,B}", a comma list, or prose) and coverageLat / coverageLng /
   coverageRadius (radius defaults to 30 server-side even when no circle is set, so the
   radius is only shown when a coverage centre exists). parseCounties() is NOT used: it
   substitutes Sacramento/Placer/Yolo for an empty value, which would hide "Not set". */
function ssCoverageSummary(user) {
  if (!user) return "Not set";
  // [server-zone-30-2026-10-18] The service area is now the fixed 30 mi around the home base.
  const hbSum = ssHomeBase(user);
  if (hbSum) return `${SS_ZONE_MILES} mi around ${hbSum.address || "home base"}`;
  return "Service area not set";
}

/* [counties-no-default-2026-10-18] Was: an empty value returned a hardcoded
   ["Sacramento","Placer","Yolo"], and CoverageScreen's debounced save then PATCHed that
   default to /api/auth/profile the next time `user` refreshed, overwriting the server's
   real (empty) coverage with counties they never chose. Empty now means []. Also
   accepts the Postgres array-literal form "{A,B}" that users.service_counties can hold. */
function parseCounties(raw) {
  if (Array.isArray(raw)) return raw.map(s => String(s || "").trim()).filter(Boolean);
  if (typeof raw === "string" && raw.trim()) {
    let t = raw.trim();
    if (t.startsWith("{") && t.endsWith("}")) t = t.slice(1, -1);
    return t.split(",").map(s => s.trim().replace(/^"(.*)"$/, "$1").trim()).filter(Boolean);
  }
  return [];
}

function CoverageScreen() {
  /* [server-zone-30-2026-10-18] Read-only service area. The backend shows a server only the
     available jobs within 30 miles of the HOME BASE an admin set at approval (users.home_lat /
     home_lng / home_address) and refuses claims outside it. The old draggable coverage-centre
     pin, the 5-100 mi radius slider and the one-time GPS snap that wrote the coverage centre
     are gone: this screen never writes coverage centre or radius any more. The map (when a
     home base exists) only displays the 30-mile circle.
     [live-route-2026-09-20] `dark` keeps the map on the explicit sun/moon toggle. */
  const { user, dark } = React.useContext(AppCtx);
  const [counties, setCounties] = React.useState(() => parseCounties(user?.serviceCounties ?? user?.service_counties ?? user?.coverage_counties));
  const [query, setQuery] = React.useState("");
  const [okMsg, setOkMsg] = React.useState("");
  const [errMsg, setErrMsg] = React.useState("");
  const saveTimer = React.useRef(null);
  const firstRun = React.useRef(true);
  // [counties-no-default-2026-10-18] Only a change the user made (add/remove) is saved;
  // re-syncing from a refreshed `user` must never write coverage back.
  const userEdited = React.useRef(false);

  const hb = ssHomeBase(user);
  const hbLat = hb ? hb.lat : null;
  const hbLng = hb ? hb.lng : null;
  const placeLabel = hb ? (hb.address || [user?.city, user?.state].filter(Boolean).join(", ") || "your home base") : "";
  const mapEl = React.useRef(null);
  const mapRef = React.useRef(null);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof mapkit === "undefined") return;
    try { map.colorScheme = dark ? mapkit.Map.ColorSchemes.Dark : mapkit.Map.ColorSchemes.Light; } catch (_) {}
  }, [dark]);

  // Display-only map, rebuilt if the home base changes. No draggable pin, no GPS snap.
  React.useEffect(() => {
    if (hbLat == null || hbLng == null) return;
    if (!mapEl.current || typeof window === "undefined" || !window.__mapkitReady) return;
    let cancelled = false;
    window.__mapkitReady.then(() => {
      if (cancelled || !mapEl.current || mapRef.current) return;
      const coord = new mapkit.Coordinate(hbLat, hbLng);
      const map = new mapkit.Map(mapEl.current, {
        region: new mapkit.CoordinateRegion(coord, new mapkit.CoordinateSpan(1.0, 1.3)),
        showsUserLocation: false,
        showsCompass: mapkit.FeatureVisibility.Hidden,
        showsZoomControl: false,
        showsScale: mapkit.FeatureVisibility.Hidden,
        isRotationEnabled: false,
        colorScheme: dark ? mapkit.Map.ColorSchemes.Dark : mapkit.Map.ColorSchemes.Light,
      });
      mapRef.current = map;
      map.addAnnotation(new mapkit.MarkerAnnotation(coord, {
        color: C.gold, glyphText: "", draggable: false, title: "Home base",
      }));
      const overlay = new mapkit.CircleOverlay(coord, SS_ZONE_MILES * 1609.34);
      overlay.style = new mapkit.Style({
        lineWidth: 2, strokeColor: "#1a3a7a", fillColor: "#1a3a7a", fillOpacity: 0.12, strokeOpacity: 0.9,
      });
      map.addOverlay(overlay);
    }).catch(err => console.warn("[mapkit-coverage] init failed:", err && err.message || err));
    return () => {
      cancelled = true;
      if (mapRef.current) { try { mapRef.current.destroy(); } catch (_) {} mapRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hbLat, hbLng]);

  React.useEffect(() => {
    if (userEdited.current) return; // [counties-no-default-2026-10-18] don't clobber an unsaved edit
    setCounties(parseCounties(user?.serviceCounties ?? user?.service_counties ?? user?.coverage_counties));
  }, [user]);

  // Debounced save (500ms) whenever counties changes
  React.useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (!userEdited.current) return; // [counties-no-default-2026-10-18]
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const prevCounties = counties;
    const nextCounties = counties;
    saveTimer.current = setTimeout(async () => {
      userEdited.current = false; // [counties-no-default-2026-10-18]
      try {
        await api.patch("/api/auth/profile", { serviceCounties: nextCounties });
        setOkMsg("Counties saved");
        setErrMsg("");
        setTimeout(() => setOkMsg(""), 1800);
      } catch (e) {
        setCounties(prevCounties);
        setErrMsg(e.message || "Could not save counties");
      }
    }, 500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [counties]);

  function addCounty(c) {
    if (counties.includes(c)) return;
    userEdited.current = true; // [counties-no-default-2026-10-18]
    setCounties([...counties, c]);
    setQuery("");
  }
  function removeCounty(c) {
    userEdited.current = true; // [counties-no-default-2026-10-18]
    setCounties(counties.filter(x => x !== c));
  }

  const q = query.trim().toLowerCase();
  const suggestions = q
    ? CA_COUNTIES.filter(c => c.toLowerCase().includes(q) && !counties.includes(c)).slice(0, 6)
    : [];

  return (
    <>
      <DInlineAlert kind="ok">{okMsg}</DInlineAlert>
      <DInlineAlert kind="err">{errMsg}</DInlineAlert>

      <DSectionLabel>Service area</DSectionLabel>
      <DCard>
        {hb ? (
          <div ref={mapEl} style={{
            width: "100%", height: 240, background: C.steelXXL,
            borderTopLeftRadius: 14, borderTopRightRadius: 14, overflow: "hidden",
          }} />
        ) : null}
        <div style={{
          padding: "12px 14px", borderTop: hb ? `1px solid ${C.steelXXL}` : "none",
          display: "flex", alignItems: "flex-start", gap: 10,
        }}>
          <MapPin size={16} color={hb ? C.deep : C.err} style={{ flexShrink: 0, marginTop: 1 }} />
          {hb ? (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, lineHeight: 1.4 }}>
                Your service area: {SS_ZONE_MILES} miles around {placeLabel}
              </div>
              <div style={{ fontSize: 11, color: C.steel, marginTop: 3, lineHeight: 1.45 }}>
                You see and can accept available jobs within this area. Your home base is set by the office; contact support to change it.
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, lineHeight: 1.4 }}>
                Your service area hasn't been set yet. Contact support.
              </div>
              <button type="button"
                onClick={() => { try { window.dispatchEvent(new CustomEvent("ss-open-drawer-screen", { detail: "support" })); } catch (_) {} }}
                style={{
                  marginTop: 6, background: "none", border: "none", padding: 0, cursor: "pointer",
                  color: C.royal, fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                }}>
                Contact support
              </button>
            </div>
          )}
        </div>
      </DCard>

      {/* [server-zone-30-2026-10-18] Counties stay on the profile; they no longer widen which jobs are shown. */}
      <DSectionLabel>Counties you serve (profile)</DSectionLabel>
      <DCard>
        <div style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: 8 }}>
          <Search size={14} color={C.steel} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search California counties"
            style={{
              flex: 1, border: "none", outline: "none", background: "transparent",
              fontSize: 14, color: C.deep, padding: 0, fontFamily: "inherit",
            }}
          />
          {query && (
            <button onClick={() => setQuery("")} style={{
              background: "none", border: "none", cursor: "pointer", padding: 0,
            }}>
              <X size={14} color={C.steel} />
            </button>
          )}
        </div>
        {suggestions.length > 0 && (
          <div style={{ borderTop: `1px solid ${C.steelXXL}` }}>
            {suggestions.map(c => (
              <button key={c} onClick={() => addCounty(c)} style={{
                width: "100%", padding: "11px 12px", background: "none", border: "none",
                borderBottom: `1px solid ${C.steelXXL}`,
                display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textAlign: "left",
              }}>
                <Plus size={14} color={C.ok} />
                <div style={{ flex: 1, fontSize: 13, color: C.deep, fontWeight: 600 }}>{c}</div>
                <div style={{ fontSize: 10, color: C.steel }}>Add</div>
              </button>
            ))}
          </div>
        )}
      </DCard>

      {counties.length > 0 && (
        <>
          <DSectionLabel>Counties added</DSectionLabel>
          <DCard>
            {counties.map((c, i) => (
              <div key={c} style={{
                padding: "12px 14px", display: "flex", alignItems: "center", gap: 10,
                borderBottom: i === counties.length - 1 ? "none" : `1px solid ${C.steelXXL}`,
              }}>
                <MapPin size={16} color={C.deep} />
                <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.deep }}>{c} County</div>
                <button onClick={() => removeCounty(c)} style={{
                  background: "none", border: "none", cursor: "pointer", padding: 4,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <X size={16} color={C.err} />
                </button>
              </div>
            ))}
          </DCard>
        </>
      )}

      <div style={{
        background: C.infoL, borderRadius: 12, padding: "10px 12px", marginTop: 4,
        display: "flex", alignItems: "flex-start", gap: 8,
      }}>
        <Info size={14} color={C.info} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 11, color: C.info, lineHeight: 1.5 }}>
          The jobs you see are set by your {SS_ZONE_MILES}-mile service area around your home base. Counties are kept on your profile and do not add jobs outside that area.
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// DOCUMENTS — compliance documents list
// ─────────────────────────────────────────────────────────────────────────
function documentStatusPill(status) {
  if (status === "active")   return { bg: C.okL,   fg: C.ok,   label: "Active",        Icon: CheckCircle2 };
  if (status === "expiring") return { bg: C.warnL, fg: C.warn, label: "Expires soon", Icon: AlertTriangle };
  return { bg: C.errL, fg: C.err, label: "Missing", Icon: AlertCircle };
}

function DocumentsScreen() {
  // Fetch real compliance docs from the backend; fall back to an empty state on 404 / network error.
  const [docs, setDocs] = React.useState(null);
  const [docsErr, setDocsErr] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/api/users/me/documents");
        if (!alive) return;
        const arr = Array.isArray(r) ? r : (Array.isArray(r && r.documents) ? r.documents : []);
        setDocs(arr);
      } catch (_) {
        if (alive) { setDocs([]); setDocsErr(true); }
      }
    })();
    return () => { alive = false; };
  }, []);

  // DocumentViewerSheet was deleted in v1.0 — the upload + viewer flows aren't
  // implemented yet, so the sheet only ever shipped "available in next release"
  // copy. Until the file picker + PDF viewer are wired, documents either render
  // an "Open" button (delegates to Safari) when the backend provides a URL, or
  // surface no action at all.

  if (docs == null) {
    return (
      <>
        <DSectionLabel>Compliance documents</DSectionLabel>
        <DCard><div style={{ padding: 16, fontSize: 12, color: C.steel }}>Loading documents…</div></DCard>
      </>
    );
  }

  if (docs.length === 0) {
    return (
      <>
        <DSectionLabel>Compliance documents</DSectionLabel>
        <DCard>
          <div style={{ padding: 24, textAlign: "center" }}>
            <FolderOpen size={28} color={C.steelL} />
            <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, marginTop: 10 }}>No documents on file yet</div>
            <div style={{ fontSize: 11, color: C.steel, marginTop: 4, lineHeight: 1.45 }}>
              {docsErr
                ? "We couldn't load your documents right now — try again in a moment."
                : "Upload your W-9, bond, insurance, and background check to stay eligible for jobs."}
            </div>
          </div>
        </DCard>
      </>
    );
  }

  return (
    <>
      <DSectionLabel>Compliance documents</DSectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {docs.map(d => {
          const p = documentStatusPill(d.status);
          const PillIcon = p.Icon;
          const missing = d.status === "missing";
          return (
            <div key={d.id} style={{
              background: C.white, padding: 12, borderRadius: 12, border: `1px solid ${C.steelXL}`,
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10, background: C.goldXL,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <FileText size={18} color={C.deep} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.deep, marginBottom: 3 }}>{d.title}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{
                    background: p.bg, color: p.fg, fontSize: 9, fontWeight: 800,
                    letterSpacing: "0.05em", padding: "2px 6px", borderRadius: 4,
                    display: "inline-flex", alignItems: "center", gap: 3,
                  }}>
                    <PillIcon size={9} />
                    {p.label.toUpperCase()}
                  </span>
                  <span style={{ fontSize: 10, color: C.steel }}>{d.expires}</span>
                </div>
              </div>
              {/* Upload + in-app viewer dropped until the file picker + PDF viewer are wired. When the backend
                  provides a URL we render an Open button that delegates to Safari via openExternal. */}
              {d.url ? (
                <button onClick={() => openExternal(String(d.url))} style={{
                  background: "none", border: `1px solid ${C.steelXL}`, borderRadius: 8,
                  padding: "8px 10px", display: "flex", alignItems: "center", gap: 4,
                  fontSize: 11, fontWeight: 700, color: C.deep, cursor: "pointer", flexShrink: 0,
                }}>
                  <Eye size={12} /> Open
                </button>
              ) : null}
              {/* [server-app-removals-2026-10-08] remove one of your uploaded documents (e.g. an outdated or wrong file) */}
              {!missing && d.id != null && /^\d+$/.test(String(d.id)) ? (
                <button aria-label="Delete document" title="Delete this document" onClick={async () => {
                  const reason = ssAskReason("\"" + (d.title || "this document") + "\""); if (!reason) return;
                  try { await api.req("DELETE", "/api/users/me/documents/" + encodeURIComponent(d.id), { reason }); setDocs((prev) => (prev || []).filter((x) => x.id !== d.id)); ssToast("ok", "Document deleted"); }
                  catch (e) { ssToast("err", (e && e.message) || "Could not delete the document"); }
                }} style={{ background: "none", border: `1px solid ${C.steelXL}`, borderRadius: 8, padding: "8px 9px", cursor: "pointer", flexShrink: 0 }}>
                  <Trash2 size={12} color={C.err} />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      <div style={{
        background: C.warnL, borderRadius: 12, padding: "10px 12px", marginTop: 14,
        display: "flex", alignItems: "flex-start", gap: 8,
      }}>
        <ShieldAlert size={14} color={C.warn} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 11, color: C.warn, lineHeight: 1.5, fontWeight: 600 }}>
          Required to stay on shift. Missing or expired documents will pause your eligibility for new jobs.
        </div>
      </div>

    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SUPPORT — contact channels, FAQ accordion, about
// ─────────────────────────────────────────────────────────────────────────
const FAQ_ENTRIES = [
  {
    q: "How does StrikeServe pay servers?",
    a: "Completed serves are paid out weekly via direct deposit to the bank account on file. Each accepted POS triggers a payment line item that you can see in real time on the Earnings tab.",
  },
  {
    q: "What happens if a service attempt fails?",
    a: "Log the failed attempt with notes and any photos, and the job stays open. Most California jobs require diligent search across three attempts at varied times of day before substituted service is permitted.",
  },
  {
    q: "How do I submit a Proof of Service?",
    a: "After a successful serve, open the job and tap Build POS. Fill in the time, manner, and description of the person served, then sign and submit. The signed POS is delivered to the client automatically.",
  },
  {
    /* [server-zone-30-2026-10-18] The service area is fixed at 30 miles around the home base. */
    q: "How do I change my service area?",
    a: "You see and can accept available jobs within 30 miles of the home base the office set from your application address. Open the side menu and choose Coverage areas to see it. To change your home base, contact support.",
  },
  {
    q: "What documents are required to stay active?",
    a: "An active W-9, liability insurance, process server bond, and current background check are required. The My documents screen shows status and upcoming expirations for each.",
  },
  {
    q: "Who handles tax filings?",
    a: "Servers are independent contractors, so you are responsible for your own taxes. StrikeServe issues a 1099-NEC each January for the prior calendar year and posts it in My documents.",
  },
];

function SupportScreen() {
  const [openFaq, setOpenFaq] = React.useState(null);

  return (
    <>
      <DSectionLabel>Contact us</DSectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        {SUPPORT_PHONE ? (
          <button onClick={() => { window.location.href = `tel:+1${SUPPORT_PHONE}`; }} style={{
            background: C.white, padding: 14, borderRadius: 12, border: `1px solid ${C.steelXL}`,
            display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left", width: "100%",
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, background: C.okL,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Phone size={18} color={C.ok} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>Call support</div>
              <div style={{ fontSize: 11, color: C.steel }}>{formatSupportPhone(SUPPORT_PHONE)}</div>
            </div>
            <ChevronRight size={16} color={C.steel} />
          </button>
        ) : null}

        <button onClick={() => { window.location.href = "mailto:support@strikeserve.com"; }} style={{
          background: C.white, padding: 14, borderRadius: 12, border: `1px solid ${C.steelXL}`,
          display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left", width: "100%",
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, background: C.infoL,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <Mail size={18} color={C.info} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>Email support</div>
            <div style={{ fontSize: 11, color: C.steel }}>support@strikeserve.com</div>
          </div>
          <ChevronRight size={16} color={C.steel} />
        </button>
      </div>

      <DSectionLabel>Frequently asked</DSectionLabel>
      <DCard>
        {FAQ_ENTRIES.map((f, i) => {
          const isOpen = openFaq === i;
          return (
            <div key={i} style={{
              borderBottom: i === FAQ_ENTRIES.length - 1 ? "none" : `1px solid ${C.steelXXL}`,
            }}>
              <button onClick={() => setOpenFaq(isOpen ? null : i)} style={{
                width: "100%", padding: "13px 14px", background: "none", border: "none",
                display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textAlign: "left",
              }}>
                <HelpCircle size={14} color={C.deep} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.deep, lineHeight: 1.3 }}>
                  {f.q}
                </div>
                {isOpen
                  ? <ChevronUp size={16} color={C.steel} />
                  : <ChevronDown size={16} color={C.steel} />}
              </button>
              {isOpen && (
                <div style={{
                  padding: "0 14px 14px 36px", fontSize: 12, color: C.steel,
                  lineHeight: 1.55,
                }}>
                  {f.a}
                </div>
              )}
            </div>
          );
        })}
      </DCard>

      <DSectionLabel>About</DSectionLabel>
      <DCard>
        <div style={{ padding: "13px 14px", borderBottom: `1px solid ${C.steelXXL}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.steel, letterSpacing: "0.06em" }}>VERSION</div>
          <div style={{ fontSize: 13, color: C.deep, fontWeight: 600, marginTop: 2 }}>{ssGetBrand().name} Mobile v1.0.0-beta{/* [tenant-brand-2026-07-21] */}</div>
        </div>
        <button onClick={() => { window.location.href = "https://strikeserve.com/terms"; }} style={{
          width: "100%", padding: "13px 14px", background: "none", border: "none",
          borderBottom: `1px solid ${C.steelXXL}`,
          display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textAlign: "left",
        }}>
          <FileText size={14} color={C.deep} />
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.deep }}>Terms of service</div>
          <ExternalLink size={14} color={C.steel} />
        </button>
        <button onClick={() => { window.location.href = "https://strikeserve.com/privacy"; }} style={{
          width: "100%", padding: "13px 14px", background: "none", border: "none",
          display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textAlign: "left",
        }}>
          <Shield size={14} color={C.deep} />
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.deep }}>Privacy policy</div>
          <ExternalLink size={14} color={C.steel} />
        </button>
      </DCard>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// IPAD FIELD VIEW  [field-view-2026-09-07]
// ─────────────────────────────────────────────────────────────────────
// One standing-outside-the-door screen: subject photo (identity check),
// map + directions handoff, subject info, client contact, and the Log
// Attempt CTA. Two columns on a wide viewport (iPad landscape), one
// column on a phone / iPad portrait.
//
// Breakpoint: this uses its OWN width listener (useFieldWide), NOT
// useIsTablet(). useIsTablet() is AND-gated by SS_TABLET_SHELL_ENABLED,
// which defaults OFF — on a real iPad it returns false, which would pin
// the Field View to a single column on the exact device it is for.
//
// Directions: the three map-app buttons are rendered INLINE with the same
// URL construction NavigationSheet uses, rather than opening
// NavigationSheet. The whole point of this screen is "everything in one
// view"; a bottom sheet would add a modal tap in front of the map that is
// already on screen.
// ═══════════════════════════════════════════════════════════════════════

// Wide-layout hook. Independent of the tablet kill switch on purpose (see above).
// 900px: below that, two columns squeeze the map and the photo — the two
// things that must stay big — under ~430px each.
// [field-view-2026-09-07] The ONE way into the Field View. ListJobCard and the
// map claim bar cannot reach setTab (it is AppInner-local and deliberately not
// in AppCtx), so they fire ss-open-field — the sibling of the ss-open-job
// bridge — and AppInner does setSelectedJob(job) + setTab("field").
function openFieldView(job) {                                      // [field-view-2026-09-07]
  if (!job || job.id == null) return;
  try {
    window.dispatchEvent(new CustomEvent("ss-open-field", { detail: String(job.id) }));
  } catch (_) { /* no window (SSR/test) — nothing to navigate */ }
}

function useFieldWide() {                                          // [field-view-2026-09-07]
  const [wide, setWide] = useState(() =>
    typeof window !== "undefined" && window.innerWidth >= 900
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setWide(window.innerWidth >= 900);
    onResize();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  return wide;
}

// Physical description for identity verification.
// [field-brief-2026-09-20] Was `recipient_desc || defendant_physical_notes` —
// first match only, so a job carrying BOTH columns showed strictly less
// identifying detail here than CommandView does (it joins both, see the dossier
// block). On the one screen whose entire purpose is deciding whether the person
// at the door is the right person, dropping half the description is the worst
// possible place to lose data. Joined now, matching CommandView exactly.
// (The old comment claimed _normalizeJob exposes neither column. It exposes
// both — as job.recipientDesc and job.defendantPhysicalNotes. _raw still works
// and stays the source here so a stale normalizer can't blank the field.)
function fieldSubjectDesc(job) {                                   // [field-view-2026-09-07]
  const r = (job && job._raw) || {};
  const s = (v) => String(v == null ? "" : v).trim();
  return [s(r.recipient_desc), s(r.defendant_physical_notes)].filter(Boolean).join(" · ");
}

// Client contact. `client_email` is proven present for server-role JWTs
// (ChatTab reads it). client_phone / client_company are NOT referenced
// anywhere else in this file and may simply not be returned — every field
// is optional and only rendered when non-empty.
function fieldClientContact(job) {                                 // [field-view-2026-09-07]
  const r = (job && job._raw) || {};
  const s = (v) => String(v == null ? "" : v).trim();
  return {
    name: s(job && job.client),
    company: s(r.client_company || r.firm_name),
    email: s(r.client_email || r.created_by),
    phone: s(r.client_phone || r.client_phone_number || r.plaintiff_phone),
  };
}

// jobs.subject_photo is JSONB {name,size,type,key,s3Key,fileUrl,url,s3:true}.
// Key precedence mirrors attemptPhotoKey(): s3Key is canonical. Some drivers
// hand JSONB back as a string, so parse defensively. The bucket is private —
// the key must be presigned; fileUrl/url are never publicly fetchable.
function subjectPhotoKey(job) {                                    // [field-view-2026-09-07]
  let sp = job && (job.subjectPhoto || (job._raw && job._raw.subject_photo));
  if (!sp) return null;
  if (typeof sp === "string") {
    const t = sp.trim();
    if (t.charAt(0) === "{") { try { sp = JSON.parse(t); } catch (_) { return t; } }
    else return t;
  }
  if (!sp || typeof sp !== "object") return null;
  return sp.s3Key || sp.key || sp.s3_key || null;
}

// Card chrome. A FUNCTION, not a const — C is reassigned by applyTheme() on
// the dark-mode toggle, so every color has to be read at render time.
function fieldCard() {                                             // [field-view-2026-09-07]
  return {
    background: C.white, borderRadius: 16, border: `1px solid ${C.steelXL}`,
    padding: 18, marginBottom: 16,
  };
}
function fieldCardLabel() {                                        // [field-view-2026-09-07]
  return { fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", color: C.steel, marginBottom: 10 };
}

/* ═══════════════════════════════════════════════════════════════════════
   FIELD BRIEFING  [field-brief-2026-09-20]
   ─────────────────────────────────────────────────────────────────────
   Chrome for the pre-approach briefing sheet. The old look was floating
   rounded cards on a tinted gutter; this is a RULED SHEET — one continuous
   white column, sections divided by hairlines rather than gaps, labels
   ticked in gold. Functions, never module consts: C is reassigned by
   applyTheme() and a captured color will not repaint on the dark toggle.
   ═══════════════════════════════════════════════════════════════════════ */
function briefBlock(first) {
  return {
    background: C.white,
    padding: 20,
    borderTop: first ? "none" : `1px solid ${C.steelXL}`,
  };
}
function briefLabel() {
  return {
    fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase",
    color: C.steel, borderLeft: `3px solid ${C.gold}`, paddingLeft: 9,
    lineHeight: 1, marginBottom: 8,
  };
}
function briefValue() {
  return { fontSize: 17, fontWeight: 700, color: C.deep, lineHeight: 1.35 };
}
function briefProse() {
  return { fontSize: 15, fontWeight: 600, color: C.ink, lineHeight: 1.5 };
}
function briefField() { return { marginTop: 12 }; }

/* Pull the ONE sentence that tripped a regex, rather than dumping the whole
   instructions blob into a warning row. Split on terminators only — no
   lookbehind, which older iPad Safari does not carry. */
function fieldMatchSentence(text, re) {
  const t = String(text == null ? "" : text).trim();
  if (!t) return "";
  const parts = t.split(/[.;\n]+/).map((s) => s.trim()).filter(Boolean);
  for (let i = 0; i < parts.length; i++) {
    if (re.test(parts[i])) return ssFcTruncate(parts[i], 160);
  }
  return ssFcTruncate(t, 160);
}

/* The backend risk engine (autoDetectRiskFlags) writes jobs.risk_flags and the
   field app has never read it. Only the slugs that change whether or how you
   approach a door are surfaced here — the rest of the catalogue is pricing and
   scheduling signal, not door signal, and padding a safety block with logistics
   is how a safety block stops being read. */
const SS_RISK_HAZARDS = {
  aggressive_history:  { sev: "err",  tag: "HISTORY OF AGGRESSION", detail: "Flagged for prior aggression or threats toward process servers." },
  evasive_recipient:   { sev: "warn", tag: "EVASIVE RECIPIENT",     detail: "This subject has a record of avoiding service." },
  repeat_fail_address: { sev: "warn", tag: "REPEATED FAILURES HERE", detail: "Previous attempts at this address have failed." },
  bad_address:         { sev: "warn", tag: "ADDRESS MAY BE BAD",     detail: "This address did not validate cleanly. Confirm before reporting a failed attempt." },
};

/* Every hazard on one job, worst first. Uses the SAME regexes as
   ssFcFlagLine() so the briefing sheet and the in-motion dash can never
   disagree about whether a job is dangerous.
   Deliberately NOT hazards: gate codes (logistics -> ACCESS) and RUSH
   (schedule -> masthead chip). ssFcFlagLine folds all three into one line
   because it has a 30-character budget; the sheet does not. */
function fieldHazards(job) {
  if (!job) return [];
  const out = [];
  const raw = job._raw || {};
  const instr = String(job.instructions || "");

  const rows = Array.isArray(raw.attempts)
    ? raw.attempts
    : (Array.isArray(job.rawAttempts) ? job.rawAttempts : []);
  const unsafeRow = rows.find((a) => /unsafe|weapon|aggress|threat/i.test(
    String((a && a.notes) || "") + " " + String((a && a.result) || "")
  ));
  if (unsafeRow) {
    out.push({
      sev: "err", tag: "PRIOR UNSAFE ATTEMPT",
      detail: ssFcTruncate(String(unsafeRow.notes || unsafeRow.result || ""), 160)
        || "A previous attempt on this job was logged as unsafe.",
    });
  }

  if (/weapon|firearm|\bgun\b|armed|aggressive|threat|hostile|violent/i.test(instr)) {
    out.push({
      sev: "err", tag: "SAFETY FLAG IN INSTRUCTIONS",
      detail: fieldMatchSentence(instr, /weapon|firearm|\bgun\b|armed|aggressive|threat|hostile|violent/i),
    });
  }
  if (/\bdogs?\b|k-?9|canine|pit ?bull/i.test(instr)) {
    out.push({
      sev: "warn", tag: "DOG ON PROPERTY",
      detail: fieldMatchSentence(instr, /\bdogs?\b|k-?9|canine|pit ?bull/i),
    });
  }

  const flags = Array.isArray(raw.risk_flags) ? raw.risk_flags : [];
  flags.forEach((f) => {
    const hit = SS_RISK_HAZARDS[String(f || "").trim()];
    if (hit) out.push({ sev: hit.sev, tag: hit.tag, detail: hit.detail });
  });

  const alerts = Array.isArray(raw.alerts) ? raw.alerts : [];
  alerts.forEach((a) => {
    const t = String(a == null ? "" : a).trim();
    if (t) out.push({ sev: "err", tag: "ALERT", detail: ssFcTruncate(t, 160) });
  });

  const seen = {};
  const dedup = out.filter((h) => (seen[h.tag] ? false : (seen[h.tag] = true)));
  dedup.sort((a, b) => (a.sev === b.sev ? 0 : (a.sev === "err" ? -1 : 1)));
  return dedup.slice(0, 4);
}

/* [field-brief-2026-09-20] jobs.notes is written by the AI email-intake
   extractor and carries back-office residue: the ServeManager job id, the
   Stripe payment id, the amount collected, the extractor's own confidence
   score. None of that is a process server's business — it is billing and
   pipeline data, and a payment id printed on a screen that gets photographed
   in a driveway is a liability besides. Stripped for DISPLAY only: the full
   text still reaches the hazard and access extractors above, so a safety
   sentence can never be filtered out by accident, and admin surfaces are
   untouched. */
const SS_FIELD_INSTRUCTION_DENY = [
  /servemanager/i,
  /stripe/i,
  /\bpi_[A-Za-z0-9]{6,}/,
  /payment\s*(id|of|received)/i,
  /\bconfidence\s*[:=]/i,
  /\b(invoice|charge)\s*(id|#)/i,
  /\btransaction\s*id/i,
];

function fieldCleanInstructions(text) {
  const raw = String(text == null ? "" : text).replace(/^\s*\[ai\]\s*/i, "").trim();
  if (!raw) return "";
  const lines = [];
  raw.split(/\r?\n+/).forEach((line) => {
    // Sentence split without lookbehind — older iPad Safari does not carry it.
    const sentences = line.match(/[^.!?]+[.!?]*/g) || [line];
    const kept = sentences
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((s) => !SS_FIELD_INSTRUCTION_DENY.some((re) => re.test(s)));
    if (kept.length) lines.push(kept.join(" "));
  });
  return lines.join("\n").trim();
}

/* A gate code is help, not danger — it renders in LOCATION, in gold. */
function fieldAccessNote(job) {
  const instr = String((job && job.instructions) || "");
  const re = /gate ?code|access code|call ?box|keypad|gate #/i;
  if (!re.test(instr)) return "";
  return fieldMatchSentence(instr, re);
}

/* Subject photo — the identity-verification anchor of this screen, so it gets
   three explicitly DIFFERENT visible states. "no photo on file" and "the photo
   exists but could not be loaded" must never look the same to a server who is
   about to decide whether the person at the door is the subject. Load state
   machine, alive ref and zoom overlay all follow AttemptPhotoStrip. */
function FieldSubjectPhoto({ job, fill }) {                        // [field-view-2026-09-07]
  const key = subjectPhotoKey(job);
  const [st, setSt] = useState({ status: key ? "loading" : "none" });
  const [zoom, setZoom] = useState(false);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    if (!key) { setSt({ status: "none" }); return undefined; }
    let cancelled = false;
    setSt({ status: "loading" });
    presignAttemptPhoto(key)
      .then(url => {
        if (cancelled || !aliveRef.current) return;
        setSt({ status: "ok", url });
      })
      .catch(e => {
        if (cancelled || !aliveRef.current) return;
        setSt({ status: "error", err: (e && e.message) || "unavailable" });
      });
    return () => { cancelled = true; };
  }, [key]);

  // [field-brief-2026-09-20] This is the identity-verification anchor — the
  // server is deciding whether the person at the door is the right person — so
  // it is now the dominant object on the sheet instead of a thumbnail in a card.
  // PORTRAIT 3:4, not 4:3: faces are taller than they are wide, and a landscape
  // crop of a head-and-shoulders photo spends its width on background while
  // shrinking the face. Resolves to roughly 440px tall on an iPad.
  // The card wrapper and its own label are gone — it sits inside the SUBJECT
  // block now, directly under the name, which is how a dossier reads.
  // Error border is C.warn, never C.err: on this screen red means exactly one
  // thing, that a person may hurt you, and a failed image load is not that.
  /* [mdt-2026-09-20] `fill` drops the aspect ratio and fills the caller's box —
     the in-car layout gives the photo a fixed pane and needs it to occupy all
     of it, not impose a shape on the grid. */
  const frame = fill ? {
    width: "100%", height: "100%", borderRadius: 4,
    overflow: "hidden", background: C.bg,
    border: `2px solid ${st.status === "error" ? C.warn : C.deep}`,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: st.status === "ok" ? "zoom-in" : "default",
  } : {
    width: "100%", aspectRatio: "3 / 4", minHeight: 320, maxHeight: "52vh",
    borderRadius: 4,
    overflow: "hidden", background: C.bg,
    border: `2px solid ${st.status === "error" ? C.warn : C.deep}`,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: st.status === "ok" ? "zoom-in" : "default",
  };

  return (
    <div style={fill ? { width: "100%", height: "100%", minHeight: 0 } : undefined}>
      <div
        onClick={st.status === "ok" ? () => setZoom(true) : undefined}
        style={frame}
        title={st.status === "error" ? ("Photo unavailable: " + (st.err || "could not be loaded")) : undefined}
      >
        {st.status === "ok" ? (
          <img
            src={st.url}
            alt={"Subject photo — " + jobDisplay(job).name}
            onError={() => {
              if (!aliveRef.current) return;
              setSt({ status: "error", err: "image failed to load" });
            }}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : st.status === "error" ? (
          /* [field-brief-2026-09-20] Amber, not red. Red on this screen is
             reserved entirely for "a person may hurt you" — if a failed image
             load also shows red, the warning colour stops meaning anything.
             The reason line moves off C.steel (3.3:1 on white, gone in glare)
             onto C.ink. */
          <div style={{ textAlign: "center", padding: 16 }}>
            <AlertTriangle size={36} color={C.warn} />
            <div style={{ fontSize: 17, fontWeight: 800, color: C.warn, marginTop: 8 }}>PHOTO UNAVAILABLE</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.ink, marginTop: 4, lineHeight: 1.5 }}>
              {st.err || "could not be loaded"}
            </div>
          </div>
        ) : st.status === "none" ? (
          /* Stays visibly DIFFERENT from the error state — "never photographed"
             and "photo exists but failed" must never look alike to someone
             deciding whether the person at the door is the subject. */
          <div style={{ textAlign: "center", padding: 16 }}>
            <ImageIcon size={36} color={C.steelL} />
            <div style={{ fontSize: 17, fontWeight: 800, color: C.steel, marginTop: 8, letterSpacing: "0.06em" }}>
              NO PHOTO ON FILE
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.ink, marginTop: 6, lineHeight: 1.5 }}>
              Verify by name and physical description, and ask for identification.
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 16 }}>
            <ImageIcon size={30} color={C.steelL} />
            <div style={{ fontSize: 14, fontWeight: 700, color: C.steel, marginTop: 8 }}>Loading photo…</div>
          </div>
        )}
      </div>
      {/* [field-brief-2026-09-20] "Tap the photo to enlarge." removed — a 440px
          photo carrying cursor:zoom-in does not need a caption explaining the
          interface, and copy that describes the UI is filler. */}

      {zoom && st.status === "ok" && (
        <div onClick={() => setZoom(false)} style={{
          position: "fixed", inset: 0, zIndex: 9000,
          background: "rgba(8,12,20,0.94)", display: "flex", flexDirection: "column",
          paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 16px 8px" }}>
            <div style={{ color: C.white, fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em" }}>
              {jobDisplay(job).name}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setZoom(false); }}
              aria-label="Close photo"
              style={{
                width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)",
                border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0,
              }}
            >
              <X size={26} color={C.white} />
            </button>
          </div>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 16px 18px", minHeight: 0 }}>
            <img src={st.url} alt={"Subject photo — " + jobDisplay(job).name}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 12 }} />
          </div>
        </div>
      )}
    </div>
  );
}

/* [live-route-2026-09-20] Frame two coordinates with padding. showItems() can
   only fit annotations and overlays, and the user's blue dot is neither — so
   when there is no route line yet this is what keeps BOTH the operator and the
   destination on screen instead of centring on the pin alone. */
function ssFitCoords(map, a, b) {
  if (!map || !a || !b || typeof mapkit === "undefined") return;
  try {
    const midLat = (Number(a.lat) + Number(b.lat)) / 2;
    const midLng = (Number(a.lng) + Number(b.lng)) / 2;
    const dLat = Math.abs(Number(a.lat) - Number(b.lat));
    const dLng = Math.abs(Number(a.lng) - Number(b.lng));
    // 1.6x so neither marker is jammed against an edge; the floor stops a very
    // short leg from slamming to street level.
    const span = new mapkit.CoordinateSpan(
      Math.max(dLat * 1.6, 0.006),
      Math.max(dLng * 1.6, 0.006)
    );
    map.region = new mapkit.CoordinateRegion(new mapkit.Coordinate(midLat, midLng), span);
  } catch (_) { /* a bad fit must never take the map down */ }
}

/* [mdt-2026-09-20] Was { top: 64, right: 32, bottom: 96, left: 32 } — the 96
   reserved room for the in-canvas readout bar, which now lives in the sheet.
   Leaving it there squeezed the fit and pushed the destination pin out of
   frame on a short, wide map pane. */
const SS_FIELD_FIT_PAD = { top: 40, right: 28, bottom: 40, left: 28 };

/* [live-route-2026-09-20] Live route map. This WAS a "read-only and tight on the
   pin" thumbnail: a 0.012 degree box (~0.8 mi) centred on the destination, with
   showsUserLocation set but the operator almost always outside the box, and no
   route drawn at all. So it showed where the job was and never where you were or
   how to get there.

   It now runs the same machinery CommandView already proved out — a live GPS
   watch publishing into the shared motion bus, ssFcResolveDest for the pin,
   ssGetRoute for real MapKit directions with a cached-leg fallback, and a fit
   that frames the operator and the destination together. */
function FieldMap({ job, height, onRoute }) {                       // [field-view-2026-09-07]
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const annRef = useRef(null);
  const lineRef = useRef(null);
  const aliveRef = useRef(true);
  const [dest, setDest] = useState(null);
  const [geoStatus, setGeoStatus] = useState("loading"); // loading | ok | failed
  const [mapStatus, setMapStatus] = useState("idle");    // idle | ok | unavailable
  const [fix, setFix] = useState(null);
  const [route, setRoute] = useState(null);
  const [routing, setRouting] = useState(false);
  // Auto-fit until the operator pans. Re-framing the map under someone who is
  // deliberately looking at a side street is worse than making them tap once.
  const [followed, setFollowed] = useState(true);

  // [live-route-2026-09-20] ctx.dark, NOT useFcNight(). CommandView themes its
  // whole surface with ssFcPalette(night), so a dark map matches a dark dash.
  // FieldView renders from C, so useFcNight() put a dark map in a light page
  // whenever the device was in system dark mode. Follow the explicit toggle.
  const { dark } = React.useContext(AppCtx);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Live position. Publishes into the shared motion bus (ssPublishMotion) so the
  // lockout and every other subscriber read the same stream — the bus is built
  // for more than one publisher, see the note at ssPublishMotion.
  useEffect(() => {
    const geo = (typeof navigator !== "undefined" && navigator.geolocation) || null;
    if (!geo || typeof geo.watchPosition !== "function") return undefined;
    let id = null;
    const onPos = (p) => {
      const lat = Number(p && p.coords && p.coords.latitude);
      const lng = Number(p && p.coords && p.coords.longitude);
      if (!isFinite(lat) || !isFinite(lng)) return;
      if (!aliveRef.current) return;
      const sample = {
        lat: lat, lng: lng,
        accuracy: Number((p.coords && p.coords.accuracy) || 0),
        speed: (p.coords && p.coords.speed != null) ? Number(p.coords.speed) : null,
        heading: (p.coords && p.coords.heading != null) ? Number(p.coords.heading) : null,
        ts: Number(p.timestamp || Date.now()),
      };
      setFix(sample);
      ssPublishMotion(sample);
    };
    try {
      id = geo.watchPosition(onPos, () => {}, { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 });
    } catch (_) { /* no geolocation — the pin and the handoff buttons still work */ }
    return () => { try { if (id != null) geo.clearWatch(id); } catch (_) {} };
  }, []);

  // Re-apply the scheme on toggle; the init effect bails once mapRef is set.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof mapkit === "undefined") return;
    try { map.colorScheme = dark ? mapkit.Map.ColorSchemes.Dark : mapkit.Map.ColorSchemes.Light; } catch (_) {}
  }, [dark, mapStatus]);

  // [live-route-2026-09-20] A courthouse job is served at the COURT, not at the
  // subject's address. FieldDirections and the address line above this map both
  // already branch on that; this map did not, so a courthouse job was pinned and
  // routed to the wrong place entirely. Route the same destination the operator
  // is being told to drive to.
  const isCourthouse = !!(courthouseServiceType(job) && job && job.courtAddress);
  const destJob = React.useMemo(() => {
    if (!job) return null;
    if (!isCourthouse) return job;
    return {
      ...job,
      lat: null, lng: null,
      address: job.courtAddress,
      city: job.courtCounty ? job.courtCounty + " County" : "",
      zip: "",
    };
  }, [job, isCourthouse]);
  const destLabel = isCourthouse
    ? String(job.courtAddress || "")
    : String((job && job.address) || "");

  const jobKey = job ? String(job.id) : "";

  // Destination. ssFcResolveDest uses the SAME ss_geo_f_* cache key MapView and
  // the old inline block used, so a job already pinned on the Jobs map resolves
  // here with no network call and the two panes cannot disagree about a pin.
  useEffect(() => {
    let cancelled = false;
    setDest(null);
    setRoute(null);
    if (!destJob) { setGeoStatus("failed"); return undefined; }
    setGeoStatus("loading");
    ssFcResolveDest(destJob).then((d) => {
      if (cancelled || !aliveRef.current) return;
      if (d && d.lat != null && isFinite(Number(d.lat))) { setDest(d); setGeoStatus("ok"); }
      else setGeoStatus("failed");
    }).catch((e) => {
      if (cancelled || !aliveRef.current) return;
      console.warn("[field-view geocode]", (e && e.message) || e);
      setGeoStatus("failed");
    });
    return () => { cancelled = true; };
  }, [jobKey, isCourthouse, destJob && destJob.address, destJob && destJob.lat]);

  // Route. A cached leg paints immediately so a dead zone keeps an ETA and a
  // step list on screen rather than going blank.
  const hasFix = !!(fix && fix.lat != null);
  useEffect(() => {
    if (!dest || !hasFix) return undefined;
    let cancelled = false;
    const from = { lat: fix.lat, lng: fix.lng };
    const cached = ssReadCachedRoute(from, dest);
    if (cached) setRoute(cached);
    setRouting(true);
    ssGetRoute(from, dest).then((r) => {
      if (cancelled || !aliveRef.current) return;
      setRouting(false);
      if (r) setRoute(r);
    }).catch(() => { if (!cancelled && aliveRef.current) setRouting(false); });
    return () => { cancelled = true; };
    // Re-route per STOP, not per GPS tick. MapKit allows 25k service calls a day
    // and a 1Hz watch would burn through that before lunch — same rule and same
    // reasoning as CommandView's route effect.
  }, [dest && dest.lat, dest && dest.lng, jobKey, hasFix]);

  useEffect(() => {
    if (!dest || !mapEl.current || mapRef.current) return undefined;
    if (typeof window === "undefined" || !window.__mapkitReady || typeof window.__mapkitReady.then !== "function") {
      setMapStatus("unavailable");
      return undefined;
    }
    let cancelled = false;
    window.__mapkitReady.then(() => {
      if (cancelled || !aliveRef.current || !mapEl.current || mapRef.current) return;
      if (typeof mapkit === "undefined") { setMapStatus("unavailable"); return; }
      const c = new mapkit.Coordinate(Number(dest.lat), Number(dest.lng));
      let map;
      try {
        map = new mapkit.Map(mapEl.current, {
          // [live-route-2026-09-20] was CoordinateSpan(0.012, 0.012) — a ~0.8 mile
          // box around the pin, which is exactly why the operator's own blue dot
          // was never in frame. Opens wider, then the fit effects below frame the
          // actual leg as soon as there is a GPS fix.
          region: new mapkit.CoordinateRegion(c, new mapkit.CoordinateSpan(0.05, 0.05)),
          showsUserLocation: true,
          tracksUserLocation: false,
          showsCompass: mapkit.FeatureVisibility.Adaptive,
          showsZoomControl: false,
          showsScale: mapkit.FeatureVisibility.Hidden,
          colorScheme: dark ? mapkit.Map.ColorSchemes.Dark : mapkit.Map.ColorSchemes.Light,
        });
      } catch (e) {
        // A synchronous throw from mapkit.Map construction is the known iPad
        // unmounted-tree failure. Never let it escape into the parent tree.
        console.warn("[field-view mapkit] construction threw:", (e && e.message) || e);
        if (aliveRef.current) setMapStatus("unavailable");
        return;
      }
      mapRef.current = map;
      const ann = new mapkit.MarkerAnnotation(c, {
        color: C.gold,
        glyphText: "",
        title: jobDisplay(job).name,
        subtitle: destLabel || "Service address",
      });
      map.addAnnotation(ann);
      annRef.current = ann;
      // A pan or a pinch means the operator is deliberately looking somewhere.
      // Stop re-framing under them until they ask for it with Recenter.
      try {
        map.addEventListener("scroll-start", () => { if (aliveRef.current) setFollowed(false); });
        map.addEventListener("zoom-start", () => { if (aliveRef.current) setFollowed(false); });
      } catch (_) { /* older mapkit — auto-fit simply stays on */ }
      setMapStatus("ok");
    }).catch(err => {
      console.warn("[field-view mapkit] init failed:", (err && err.message) || err);
      if (aliveRef.current) setMapStatus("unavailable");
    });
    return () => {
      cancelled = true;
      if (mapRef.current) {
        try { mapRef.current.destroy(); } catch (_) {}
        mapRef.current = null;
        annRef.current = null;
        lineRef.current = null;
      }
    };
  }, [dest && dest.lat, dest && dest.lng]);

  // Route geometry. Redrawn when the leg changes, and framed only while the
  // operator has not taken manual control of the viewport.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof mapkit === "undefined") return undefined;
    if (lineRef.current) { try { map.removeOverlay(lineRef.current); } catch (_) {} lineRef.current = null; }
    const pts = (route && Array.isArray(route.points)) ? route.points : null;
    if (!pts || pts.length < 2) return undefined;
    try {
      const coords = pts
        .filter((p) => p && p.lat != null && p.lng != null)
        .map((p) => new mapkit.Coordinate(Number(p.lat), Number(p.lng)));
      if (coords.length < 2) return undefined;
      const line = new mapkit.PolylineOverlay(coords, {
        style: new mapkit.Style({ strokeColor: C.gold, lineWidth: 6, lineJoin: "round", lineCap: "round" }),
      });
      map.addOverlay(line);
      lineRef.current = line;
      if (followed) {
        try { map.showItems([line], { animate: true, padding: SS_FIELD_FIT_PAD }); } catch (_) {}
      }
    } catch (e) {
      console.warn("[field-view] route overlay failed:", (e && e.message) || e);
    }
    return undefined;
  }, [route, mapStatus, followed]);

  // No route yet (first fix, or directions unavailable) — still frame both ends
  // so the operator can see the relationship between where they are and where
  // they are going. Keyed on hasFix, not on fix, so it fires once per leg rather
  // than on every GPS tick.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !followed || route || !hasFix || !dest) return;
    ssFitCoords(map, { lat: fix.lat, lng: fix.lng }, dest);
  }, [mapStatus, followed, hasFix, route, dest && dest.lat, dest && dest.lng]);

  const recenter = useCallback(() => {
    const map = mapRef.current;
    if (!map || typeof mapkit === "undefined") return;
    setFollowed(true);
    if (lineRef.current) {
      try { map.showItems([lineRef.current], { animate: true, padding: SS_FIELD_FIT_PAD }); } catch (_) {}
    } else if (fix && dest) {
      ssFitCoords(map, { lat: fix.lat, lng: fix.lng }, dest);
    }
  }, [fix, dest]);

  // [live-route-2026-09-20] Height is caller-controlled. It was a fixed 320px box
  // regardless of device, which on an iPad meant a postage stamp with the rest of
  // the viewport wasted. FieldView now hands it the space it actually has.
  // [field-brief-2026-09-20] Hand the leg up to the caller so the ETA can be a
  // legible text field in the sheet instead of an overlay bar eating a third of
  // a 220px map strip. Routed through a ref so an unstable callback identity
  // from the parent cannot turn this into a render loop.
  const onRouteRef = useRef(onRoute);
  useEffect(() => { onRouteRef.current = onRoute; }, [onRoute]);
  useEffect(() => {
    const fn = onRouteRef.current;
    if (typeof fn === "function") fn({ route: route, hasFix: hasFix, routing: routing });
  }, [route, hasFix, routing]);

  const shellStyle = {
    width: "100%",
    height: height || "min(52vh, 460px)",
    // [field-brief-2026-09-20] was a flat minHeight: 220, which silently
    // overrode any smaller caller height and made the "caller-controlled"
    // comment above a lie. The floor now applies only when no height is given.
    minHeight: height ? 0 : 220,
    borderRadius: 4, overflow: "hidden",
    border: `1px solid ${C.steelXL}`, background: C.steelXXL, position: "relative",
  };

  const showMapDiv = geoStatus === "ok" && mapStatus !== "unavailable";
  // When the caller takes the route, it owns the readout too.
  const ownsReadout = typeof onRoute !== "function";
  const nextStep = (route && Array.isArray(route.steps) && route.steps.length)
    ? ssFcTruncate(route.steps[0].text, 84)
    : "";

  const chip = {
    fontSize: 13, fontWeight: 800, letterSpacing: "0.08em",
    color: C.deep, background: C.white, border: `1px solid ${C.steelXL}`,
    borderRadius: 10, padding: "7px 11px", cursor: "pointer", fontFamily: "inherit",
  };

  return (
    <div style={shellStyle}>
      {showMapDiv && <div ref={mapEl} style={{ width: "100%", height: "100%" }} />}

      {showMapDiv && !followed && (
        <button type="button" onClick={recenter} aria-label="Recenter the map on the route"
          style={{ ...chip, position: "absolute", top: 12, right: 12, zIndex: 20 }}>
          RECENTER
        </button>
      )}

      {/* Readout. Anchored at the bottom so it never covers the road ahead of the
          pin, and solid rather than translucent so it stays legible in sunlight.
          [field-brief-2026-09-20] Suppressed when the caller passed onRoute and
          renders the ETA itself. */}
      {showMapDiv && ownsReadout && (
        <div style={{
          position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 15,
          background: C.white, borderTop: `1px solid ${C.steelXL}`,
          padding: "10px 14px",
        }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: C.deep, letterSpacing: "-0.01em" }}>
              {route ? ssFcEta(route.seconds) : (routing ? "…" : "—")}
            </span>
            <span style={{ fontSize: 16, fontWeight: 700, color: C.deep }}>
              {route ? ssFcMiles(route.meters) : "—"}
            </span>
            {route && route.stale ? (
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", color: C.warn }}>
                LAST KNOWN ROUTE
              </span>
            ) : null}
            {!hasFix ? (
              <span style={{ fontSize: 12, fontWeight: 700, color: C.steel }}>
                Waiting for GPS…
              </span>
            ) : null}
          </div>
          {nextStep ? (
            <div style={{ fontSize: 15, fontWeight: 600, color: C.deep, lineHeight: 1.35, marginTop: 4 }}>
              {nextStep}
            </div>
          ) : null}
        </div>
      )}

      {!showMapDiv && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", textAlign: "center", padding: 20,
        }}>
          <MapPin size={30} color={C.steelL} />
          <div style={{ fontSize: 15, fontWeight: 800, color: C.steel, marginTop: 8 }}>
            {geoStatus === "loading" ? "Locating address…"
              : geoStatus === "failed" ? "Address not mapped yet"
              : "Map unavailable"}
          </div>
          <div style={{ fontSize: 13, color: C.steel, marginTop: 6, lineHeight: 1.5, maxWidth: 340 }}>
            {geoStatus === "failed"
              ? "We could not place this address on the map. The directions buttons below still work — they hand the full address straight to your map app."
              : geoStatus === "loading"
                ? "Pinning the service address."
                : "The map component could not start. Use the directions buttons below."}
          </div>
        </div>
      )}
    </div>
  );
}

/* Directions handoff. Identical URL construction to NavigationSheet
   (including its courthouse-address branch) and the same
   window.location.href launch — rendered inline instead of behind a sheet. */
function FieldDirections({ job }) {                                // [field-view-2026-09-07]
  const _destParts = courthouseServiceType(job) && job.courtAddress
    ? [job.courtAddress, job.courtCounty ? `${job.courtCounty} County` : ""]
    : [job.address, job.city, job.zip];
  const dest = encodeURIComponent(_destParts.filter(Boolean).join(", "));
  const urls = {
    apple: `https://maps.apple.com/?daddr=${dest}`,
    google: `https://www.google.com/maps/dir/?api=1&destination=${dest}`,
    waze: `https://waze.com/ul?q=${dest}&navigate=yes`,
  };
  const [launchErr, setLaunchErr] = useState("");
  function launch(id) {
    if (!urls[id]) return;
    try { window.location.href = urls[id]; }
    catch (e) { setLaunchErr((e && e.message) || "Could not open map app"); }
  }
  const apps = [
    { id: "apple", label: "Apple Maps" },
    { id: "google", label: "Google Maps" },
    { id: "waze", label: "Waze" },
  ];
  const disabled = !dest;
  return (
    <div style={{ marginTop: 14 }}>
      {launchErr && (
        <div style={{ color: C.err, fontSize: 13, padding: "10px 12px", background: C.errL, borderRadius: 10, marginBottom: 10 }}>
          {launchErr}
        </div>
      )}
      {disabled && (
        <div style={{ color: C.steel, fontSize: 13, marginBottom: 10 }}>
          No service address on this job — directions are unavailable.
        </div>
      )}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {apps.map(a => (
          <button
            key={a.id}
            onClick={() => launch(a.id)}
            disabled={disabled}
            aria-label={"Open directions in " + a.label}
            style={{
              flex: "1 1 150px", minHeight: 56, padding: "14px 12px",
              background: C.white, border: `1.5px solid ${C.steelXL}`, borderRadius: 14,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
              fontSize: 15, fontWeight: 800, color: C.deep,
            }}
          >
            <Navigation size={19} color={C.deep} />
            <span>{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// [field-view-2026-09-07] KEPT after the tab was removed. The Field View is now
// entered from a job row so selectedJob is normally set, but the "field" tab id
// still exists and selectedJob can go null (Change job, a reload that drops the
// row). Deleting this would leave a null-deref crash path in FieldView, so it
// stays as the defensive fallback — with its own Back-to-Jobs exit.
function FieldJobPicker({ onPick, onBack }) {                      // [field-view-2026-09-07]
  const { jobs, jobsLoading } = React.useContext(AppCtx);
  const list = React.useMemo(() => {
    const all = Array.isArray(jobs) ? jobs.slice() : [];
    const rank = (j) => (j.status === "active" ? 0 : j.status === "available" ? 1 : 2);
    return all.sort((a, b) => rank(a) - rank(b) || ssPriorityRank(b.priority) - ssPriorityRank(a.priority)); // [field-list-priority-2026-10-08]
  }, [jobs]);

  return (
    <div style={{ padding: 20, maxWidth: 760, margin: "0 auto" }}>
      {/* [field-view-2026-09-07] exit — there is no Field bottom tab any more, so
          the picker must offer its own way back to Jobs. */}
      {typeof onBack === "function" && (
        <button onClick={onBack} aria-label="Back to Jobs" style={{
          display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 14,
          minHeight: 40, padding: "8px 12px", borderRadius: 12,
          background: C.white, border: `1px solid ${C.steelXL}`,
          fontSize: 14, fontWeight: 700, color: C.deep, cursor: "pointer",
        }}>
          <ChevronLeft size={18} color={C.deep} strokeWidth={2.4} /> Jobs
        </button>
      )}
      <div style={{ fontSize: 24, fontWeight: 800, color: C.deep, letterSpacing: "-0.02em", marginBottom: 6 }}>
        Field View
      </div>
      <div style={{ fontSize: 15, color: C.steel, lineHeight: 1.55, marginBottom: 20 }}>
        Pick a job to open the field card — subject photo, directions, subject details, client contact, and Log Attempt on one screen.
      </div>
      {jobsLoading && !list.length ? (
        <div style={{ fontSize: 15, color: C.steel }}>Loading your jobs…</div>
      ) : !list.length ? (
        <div style={{ ...fieldCard(), textAlign: "center" }}>
          <Briefcase size={30} color={C.steelL} />
          <div style={{ fontSize: 16, fontWeight: 800, color: C.deep, marginTop: 10 }}>No jobs yet</div>
          <div style={{ fontSize: 14, color: C.steel, marginTop: 6 }}>
            Claim a job from the Jobs tab and it will show up here.
          </div>
        </div>
      ) : (
        list.map(j => (
          <button
            key={j.id}
            onClick={() => onPick(j)}
            aria-label={"Open field view for " + jobDisplay(j).name}
            style={{
              width: "100%", minHeight: 68, padding: "14px 16px", marginBottom: 10,
              background: C.white, border: `1px solid ${C.steelXL}`, borderRadius: 14,
              display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.deep, letterSpacing: "-0.01em" }}>
                {jobDisplay(j).name}
              </div>
              <div style={{ fontSize: 13, color: C.steel, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                #{j.jobNumber}{jobDisplay(j).fullAddress ? " · " + jobDisplay(j).fullAddress : ""}
              </div>
            </div>
            <div style={{
              fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", padding: "6px 10px", borderRadius: 8,
              background: j.status === "active" ? C.okL : C.steelXXL,
              color: j.status === "active" ? C.ok : C.steel, flexShrink: 0,
            }}>
              {String(j.status || "").toUpperCase()}
            </div>
            <ChevronRight size={20} color={C.steel} />
          </button>
        ))
      )}
    </div>
  );
}

/* FIELD VIEW — the tab body. Root mirrors InlineJobDetail: full height,
   position:relative, a flex column with an internal scroller, so <AttemptSheet>
   (Sheet = position:absolute; bottom:0) lands on the viewport bottom rather
   than the bottom of a long scrolled document. */
function FieldView({ selectedJob, setSelectedJob, setTab }) {      // [field-view-2026-09-07]
  const { jobs, isTablet, dark } = React.useContext(AppCtx);
  const [attemptOpen, setAttemptOpen] = useState(false);
  // [field-brief-2026-09-20] The live leg, handed up by FieldMap. Held here so
  // the ETA renders as a legible text field in the sheet instead of an overlay
  // bar eating a third of the map strip. useFieldWide() is deliberately no
  // longer called — see the note on the scroller below.
  const [leg, setLeg] = useState({ route: null, hasFix: false, routing: false });

  // [field-view-2026-09-07] With the Field bottom tab gone this is the exit.
  // Every render path of this component must expose it (header + picker).
  const goBackToJobs = React.useCallback(() => {
    if (typeof setTab === "function") setTab("jobs");
  }, [setTab]);

  // Re-resolve against the live list so a reload() after an attempt shows the
  // fresh row. There is no GET /api/jobs/:id in this app — the list is the
  // only source of truth — so fall back to the passed object.
  const job = React.useMemo(() => {
    if (!selectedJob) return null;
    const m = (jobs || []).find(j => String(j.id) === String(selectedJob.id));
    return m || selectedJob;
  }, [selectedJob, jobs]);
  /* [server-parity-web-2026-10-05] Print all / Briefing sheet. Hook runs before the
     early return below (rules of hooks); its callbacks only read job when pressed. */
  const print = useJobPrint(job);
  const sup = useJobSupport(job); // [support-tickets-2026-10-08] before the early return (rules of hooks)

  if (!job) return <FieldJobPicker onPick={setSelectedJob} onBack={goBackToJobs} />;

  const disp = jobDisplay(job);
  const desc = fieldSubjectDesc(job);
  const client = fieldClientContact(job);
  const courthouseKind = courthouseServiceType(job);

  // [field-brief-2026-09-20] Everything below is the briefing sheet.
  const hazards = fieldHazards(job);
  const worst = hazards.length ? hazards[0].sev : "";
  const access = fieldAccessNote(job);
  // Back-office residue stripped — see fieldCleanInstructions.
  const shownInstructions = fieldCleanInstructions(job.instructions);
  const destAddress = courthouseKind && job.courtAddress ? job.courtAddress : disp.fullAddress;
  const attemptCount = ssAttemptCount(job);
  const dd = ssDeriveWindows(job);
  const recent = (Array.isArray(job.rawAttempts) ? job.rawAttempts : []).slice(-3).reverse();
  // A court filing has no one to identify at the clerk's window, so the photo
  // and the physical description are noise on those jobs.
  const identifies = !courthouseKind;

  // The masthead rule doubles as the always-visible alarm: the header never
  // scrolls, so a hazard scrolled past still paints a red band across the top.
  const ruleColor = worst === "err" ? C.err : (worst === "warn" ? C.warn : C.gold);
  const ruleWidth = worst ? 6 : 3;

  const fmtWhen = (a) => {
    const v = a && (a.date || a.attempted_at || a.timestamp || a.created_at);
    if (!v) return "Time unknown";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "Time unknown";
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const sheet = { maxWidth: 560, margin: "0 auto", width: "100%" };
  const contactRow = {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
    minHeight: 44, textDecoration: "none", padding: "8px 0",
  };

  /* [mdt-2026-09-20] One labelled fact. A function, not a component — a
     component defined in render remounts on every pass. Renders nothing when
     the value is empty, so the grid closes up instead of showing dead labels. */
  const fact = (k, v) => (v ? (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", color: C.steel }}>{k}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.deep, lineHeight: 1.3, wordBreak: "break-word" }}>{v}</div>
    </div>
  ) : null);

  const lastAttempt = recent && recent.length ? recent[0] : null;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", position: "relative", background: C.bg }}>
      {/* [field-brief-2026-09-20] Masthead. Solid navy, not a 135deg gradient —
          the gradient made the gold rule read muddy and was pure decoration.
          The bottom rule is the alarm channel: gold normally, 6px amber or red
          when this job carries a hazard. The header is flexShrink:0 and never
          scrolls, so that band is on screen even when the hazard block is not.
          The subject name is deliberately NOT here — it is the 34px line below,
          and printing it twice would waste the most valuable space on the sheet.
          "Change job" is gone: this screen shows one case. */}
      {/* [mdt-2026-09-20] C.deep is NAVY in light but #dfe6f4 — a near-white —
          in dark, because C_DARK flips deep/royal into foreground accents. Used
          as a background it inverts the masthead to white on a dark page. Dark
          mode gets C.bgW, a real dark surface, and light text to match. */}
      <div style={{
        background: dark ? C.bgW : C.deep,
        borderBottom: `${ruleWidth}px solid ${ruleColor}`,
        padding: "14px 20px 13px", color: dark ? C.ink : C.white, flexShrink: 0,
        paddingTop: "calc(env(safe-area-inset-top) + 14px)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button
            onClick={goBackToJobs}
            aria-label="Back to Jobs"
            style={{
              flexShrink: 0, width: 44, height: 44, borderRadius: 8, padding: 0,
              background: C.royal, border: `1px solid ${C.goldL}`, color: C.white,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}
          >
            <ChevronLeft size={24} color={C.white} strokeWidth={2.4} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* [mdt-2026-09-20] C.white is #1e2430 in the dark palette — a dark
                SURFACE, not white — so `color: C.white` was dark-on-dark and the
                job number all but disappeared. C.goldL is #7a6526 in dark, an
                olive that vanishes too. Both flip. */}
            <div style={{
              fontSize: 11, fontWeight: 800, letterSpacing: "0.16em",
              color: dark ? C.gold : C.goldL,
              fontFamily: "Fraunces, Georgia, serif",
            }}>
              FIELD BRIEFING
            </div>
            <div style={{
              fontSize: 15, fontWeight: 700, color: dark ? C.ink : C.white, marginTop: 3,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              #{job.jobNumber}{job.deadline && job.deadline !== "—" ? ` · DUE ${job.deadline}` : ""}
            </div>
          </div>

          {/* [mdt-2026-09-20] Hazard, in the masthead. The block above the map
              only exists when a job carries one, so on a clean job there is
              nothing to see — correct, but it means the operator has no
              constant place to look. This chip sits with PAY / ATTEMPT /
              WINDOWS, solid-filled rather than tinted, because a danger flag on
              a driving surface has to survive a glance. */}
          {hazards.length > 0 ? (
            <div style={{
              flexShrink: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8,
              background: worst === "err" ? C.err : C.warn,
              color: worst === "err" ? C.errL : C.warnL,
              padding: "7px 12px", borderRadius: 4, maxWidth: 460,
            }}>
              <AlertCircle size={18} color={worst === "err" ? C.errL : C.warnL} style={{ flexShrink: 0 }} />
              <span style={{
                fontSize: 13, fontWeight: 800, letterSpacing: "0.06em",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {hazards.map((h) => h.tag).join(" · ")}
              </span>
            </div>
          ) : null}
          {/* [mdt-2026-09-20] The masthead was mostly empty between the job
              number and the RUSH chip. PAY / ATTEMPT / WINDOWS live here now,
              which also frees two cells out of the facts grid below. */}
          <div style={{ display: "flex", alignItems: "center", gap: 20, flexShrink: 0 }}>
            {job.pay ? (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", color: dark ? C.steel : C.goldL }}>PAY</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: dark ? C.ink : C.white }}>{ssPayLabel(ssJobShownPay(job))/* [job-bundles-v2-2026-10-16] */}</div>
              </div>
            ) : null}
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", color: dark ? C.steel : C.goldL }}>ATTEMPT</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: dark ? C.ink : C.white }}>#{attemptCount + 1}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", color: dark ? C.steel : C.goldL }}>WINDOWS</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: dark ? C.ink : C.white }}>{((dd && dd.covered) || 0)}/3</div>
            </div>
            {ssIsPriorityJob(job) ? (/* [field-list-priority-2026-10-08] */
              <div style={ssPriorityRank(job.priority) >= 2 ? {
                flexShrink: 0, fontSize: 11, fontWeight: 800, letterSpacing: "0.1em",
                background: C.err, color: C.white, border: `1px solid ${C.err}`,
                padding: "5px 9px", borderRadius: 4,
              } : {
                flexShrink: 0, fontSize: 11, fontWeight: 800, letterSpacing: "0.1em",
                background: C.warnL, color: C.warn, border: `1px solid ${C.warn}`,
                padding: "5px 9px", borderRadius: 4,
              }}>{ssPriorityLabel(job.priority).toUpperCase()}</div>
            ) : null}
            {/* [server-parity-web-2026-10-05] Briefing sheet + Print all. 44px targets
                for a gloved hand; the label hides on narrow phones, the icon stays. */}
            {(job.status === "active" || job.status === "completed") ? (
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                {[["brief", "Briefing", FileText, print.briefing, "Briefing sheet"], ["all", "Print all", Printer, print.printAll, "Print all (briefing + documents)"]].map(([which, label, Icon, onClick, title]) => (
                  <button key={which} type="button" onClick={onClick} disabled={!!print.busy} aria-label={title} title={title} style={{
                    minWidth: 44, height: 44, padding: "0 10px", borderRadius: 8,
                    background: C.royal, border: `1px solid ${C.goldL}`, color: C.white,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    cursor: print.busy ? "wait" : "pointer", fontFamily: "inherit",
                    opacity: print.busy && print.busy !== which ? 0.5 : 1,
                    fontSize: 11, fontWeight: 800, letterSpacing: "0.06em",
                  }}>
                    {print.busy === which ? <SpinnerDot /> : <Icon size={18} color={C.white} />}
                    {isTablet ? <span>{label}</span> : null}
                  </button>
                ))}
              </div>
            ) : null}
            {/* [support-tickets-2026-10-08] Escalate / get help — same 44px target as Print. */}
            {sup.canCreate ? (
              <button type="button" onClick={() => sup.openEscalate()} aria-label="Escalate or get help on this job" title="Escalate / get help" style={{
                minWidth: 44, height: 44, padding: "0 10px", borderRadius: 8, flexShrink: 0, position: "relative",
                background: C.royal, border: `1px solid ${C.goldL}`, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em",
              }}>
                <LifeBuoy size={18} color="#fff" />{isTablet ? <span>Help</span> : null}
                {sup.openCount > 0 ? (
                  <span style={{
                    position: "absolute", top: -5, right: -5, minWidth: 16, height: 16, borderRadius: 8, padding: "0 4px",
                    background: sup.needsYou ? C.ok : C.gold, color: "#fff", fontSize: 10, fontWeight: 800, lineHeight: "16px", textAlign: "center",
                  }}>{sup.openCount}</span>
                ) : null}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {isTablet ? (
        /* [mdt-2026-09-20] THE IN-CAR LAYOUT. Everything on one screen, nothing
           scrolls. This is a mobile data terminal, not a document: the operator
           is parked at the curb and needs the face, the map, the description,
           what they are serving and any warning in a single glance, without
           moving a finger.
           Gated on isTablet (SS_TABLET_SHELL_ENABLED && >=768) rather than
           useFieldWide's raw innerWidth>=900 — an iPad reports 1180 even while
           index.html is clamping the app to 480px, which is what produced the
           two squeezed ~215px columns. isTablet is true only when the app
           genuinely has the width. */
        <div style={{
          flex: 1, minHeight: 0, display: "grid",
          /* [mdt-2026-09-20] Was minmax(300px,360px). The map does not need
             1250px to be useful; the facts needed the room more. */
          gridTemplateColumns: "minmax(420px, 44%) 1fr",
          gap: 1, background: C.steelXL, overflow: "hidden",
        }}>

          {/* LEFT — the rap sheet: face over facts. */}
          <div style={{ display: "flex", flexDirection: "column", minHeight: 0, background: C.white }}>
            {/* [mdt-2026-09-20] Photo is BOUNDED, not flexible. It was flex:1
                and starved the facts, which are the reason this column exists.
                Face beside name at the top, everything else below. */}
            <div style={{ flexShrink: 0, display: "flex", gap: 12, padding: 12, alignItems: "flex-start" }}>
              {/* [mdt-2026-09-20] Sized by VIEWPORT HEIGHT, not column width.
                  Landscape is ~360px shorter than portrait, so a width-sized
                  photo ate the vertical room the facts needed and pushed
                  CLIENT / ATTEMPTS / INSTRUCTIONS off the bottom. */}
              <div style={{ height: "min(26vh, 300px)", aspectRatio: "3 / 4", flexShrink: 0, display: "flex" }}>
                <FieldSubjectPhoto job={job} fill />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: C.deep, letterSpacing: "-0.02em", lineHeight: 1.1, wordBreak: "break-word" }}>
                  {disp.name}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, lineHeight: 1.4, marginTop: 6 }}>
                  {desc || "No physical description on file"}
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: destAddress ? C.deep : C.warn, marginTop: 8, lineHeight: 1.3 }}>
                  {destAddress || "NO ADDRESS ON FILE"}
                </div>
              </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "0 14px 14px" }}>
              {/* The record. Two across, empty fields collapse out. */}
              <div style={{
                /* Two across in portrait, three when landscape gives the width —
                   fewer rows is what buys the vertical space back. */
                display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px 14px",
                paddingTop: 10, borderTop: `1px solid ${C.steelXL}`,
              }}>
                {fact("DOB", job.defendantDob)}
                {fact("CASE", job.caseNo)}
                {fact("SERVING", job.type)}
                {fact("DOCUMENTS", job.docs && job.docs !== "—" ? job.docs : null)}
                {fact("COURT", job.courtName || (job.county ? job.county + " County" : null))}
                {fact("CLIENT", client.name || client.company)}
                {fact("SUBJECT PHONE", job.recipientPhone ? "On file — masked call below" : null)}
                {/* [server-parity-web-2026-10-05] CLIENT PHONE removed — servers do not call clients. */}
                {/* ATTEMPT and WINDOWS moved to the masthead — see the note there. */}
                {fact("LAST ATTEMPT", attemptCount === 0 ? "None — first attempt"
                  : (lastAttempt && lastAttempt.result ? lastAttempt.result : "outcome unknown"))}
              </div>

              {/* [server-parity-web-2026-10-05] Masked calls, in the car layout too. */}
              {(job.recipientPhone || job.attorneyPhone) ? (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.steelXL}` }}>
                  {job.recipientPhone ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", color: C.steel }}>SUBJECT</span>
                      <MaskedCallButton job={job} target="subject" hasNumber compact />
                    </div>
                  ) : null}
                  {job.attorneyPhone ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", color: C.steel }}>ATTORNEY</span>
                      <MaskedCallButton job={job} target="attorney" hasNumber compact />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {shownInstructions ? (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.steelXL}` }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", color: C.steel }}>INSTRUCTIONS</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, lineHeight: 1.45, marginTop: 3, whiteSpace: "pre-wrap" }}>
                    {shownInstructions}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* RIGHT — warnings over the live map over the running numbers. */}
          <div style={{ display: "flex", flexDirection: "column", minHeight: 0, background: C.white }}>
            {hazards.length > 0 && (
              <div style={{
                flexShrink: 0, maxHeight: "34%", overflow: "auto",
                background: worst === "err" ? C.errL : C.warnL,
                borderLeft: `6px solid ${worst === "err" ? C.err : C.warn}`,
                borderBottom: `1px solid ${worst === "err" ? C.err : C.warn}`,
                padding: "12px 14px 12px 12px",
              }}>
                {hazards.map((h, i) => (
                  <div key={h.tag + i} style={{ display: "flex", gap: 10, marginTop: i === 0 ? 0 : 10 }}>
                    <AlertCircle size={22} color={h.sev === "err" ? C.err : C.warn}
                      style={{ flexShrink: 0, marginTop: 1 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontSize: 15, fontWeight: 800, letterSpacing: "0.05em",
                        color: h.sev === "err" ? C.err : C.warn,
                      }}>{h.tag}</div>
                      {h.detail ? (
                        <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, lineHeight: 1.4, marginTop: 2 }}>
                          {h.detail}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {access ? (
              <div style={{
                flexShrink: 0, background: C.goldXL, borderLeft: `3px solid ${C.gold}`,
                borderBottom: `1px solid ${C.steelXL}`, padding: "8px 12px",
              }}>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", color: C.steel }}>ACCESS </span>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.deep }}>{access}</span>
              </div>
            ) : null}

            <div style={{ flex: 1, minHeight: 0, display: "flex", padding: 10 }}>
              <FieldMap job={job} height="100%" onRoute={setLeg} />
            </div>

            {/* Running numbers. One row, no wrapping decisions to make at a glance. */}
            <div style={{
              flexShrink: 0, borderTop: `1px solid ${C.steelXL}`, padding: "10px 14px",
              display: "flex", alignItems: "baseline", gap: 20, flexWrap: "wrap",
            }}>
              <div>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", color: C.steel }}>ETA </span>
                <span style={{ fontSize: 20, fontWeight: 800, color: C.deep }}>
                  {leg.route ? ssFcEta(leg.route.seconds) : (leg.hasFix ? (leg.routing ? "…" : "—") : "GPS…")}
                </span>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", color: C.steel }}>DIST </span>
                <span style={{ fontSize: 20, fontWeight: 800, color: C.deep }}>
                  {leg.route ? ssFcMiles(leg.route.meters) : "—"}
                </span>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", color: C.steel }}>ATTEMPT </span>
                <span style={{ fontSize: 20, fontWeight: 800, color: C.deep }}>#{attemptCount + 1}</span>
              </div>
              {leg.route && leg.route.stale ? (
                <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", color: C.warn }}>
                  LAST KNOWN ROUTE
                </span>
              ) : null}
              {leg.route && Array.isArray(leg.route.steps) && leg.route.steps.length ? (
                <span style={{ fontSize: 14, fontWeight: 600, color: C.ink, flex: 1, minWidth: 0 }}>
                  {ssFcTruncate(leg.route.steps[0].text, 60)}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
      /* [field-brief-2026-09-20] ONE scroller, ONE column. The two-column grid
          is gone. Besides being unwanted, it was broken on the target device:
          useFieldWide() flips at innerWidth>=900 and an iPad reports 1180, but
          index.html clamps #root to 480px unless the tablet shell is on (which
          defaults OFF) — so the "wide" branch rendered two ~215px columns and
          squeezed the photo and the map, the exact two things it existed to
          keep big. Sections are divided by hairlines, not gaps: a sheet, not a
          stack of cards. */
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", background: C.white }}>

        {/* 1 — HAZARD. First, because it is the only thing that can cancel the
            trip: knowing the face is worthless if the answer is "do not go
            alone." Full bleed, outside the reading measure. Red is the alarm,
            ink is the message — a paragraph of red is unreadable in glare and
            reads as decoration within seconds. */}
        {hazards.length > 0 && (
          <div style={{
            background: worst === "err" ? C.errL : C.warnL,
            borderLeft: `6px solid ${worst === "err" ? C.err : C.warn}`,
            borderTop: `1px solid ${worst === "err" ? C.err : C.warn}`,
            borderBottom: `1px solid ${worst === "err" ? C.err : C.warn}`,
            padding: "18px 20px 18px 16px",
          }}>
            {hazards.map((h, i) => (
              <div key={h.tag + i} style={{ display: "flex", gap: 12, marginTop: i === 0 ? 0 : 14 }}>
                <AlertCircle size={28} color={h.sev === "err" ? C.err : C.warn}
                  style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 17, fontWeight: 800, letterSpacing: "0.06em",
                    color: h.sev === "err" ? C.err : C.warn,
                  }}>{h.tag}</div>
                  {h.detail ? (
                    <div style={{ ...briefProse(), marginTop: 4 }}>{h.detail}</div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={sheet}>

          {/* 2 — SUBJECT. Who am I about to look at. */}
          <div style={briefBlock(true)}>
            <div style={briefLabel()}>{identifies ? "Subject" : "Filing"}</div>
            <div style={{
              fontSize: 34, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.1,
              color: C.deep, wordBreak: "break-word",
            }}>
              {disp.name && disp.name !== "—" ? disp.name : (
                <span style={{ fontSize: 22, color: C.warn }}>NAME NOT ON RECORD</span>
              )}
            </div>

            {identifies && (
              <>
                <div style={{ marginTop: 28, marginBottom: 28 }}>
                  <FieldSubjectPhoto job={job} />
                </div>

                <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                  {job.defendantDob ? (
                    <div style={{ minWidth: 140, flex: "1 1 140px" }}>
                      <div style={briefLabel()}>Date of birth</div>
                      <div style={briefValue()}>{job.defendantDob}</div>
                    </div>
                  ) : null}
                  {job.caseNo ? (
                    <div style={{ minWidth: 140, flex: "1 1 140px" }}>
                      <div style={briefLabel()}>Case number</div>
                      <div style={briefValue()}>{job.caseNo}</div>
                    </div>
                  ) : null}
                </div>

                <div style={briefField()}>
                  <div style={briefLabel()}>Physical description</div>
                  {/* With no photo the description is doing the photo's job, so
                      it gets the photo's visual weight. */}
                  <div style={desc ? briefProse() : { ...briefProse() }}>
                    {desc || "Not on file"}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* 3 — SERVING. What do I say when the door opens. */}
          <div style={briefBlock()}>
            <div style={briefLabel()}>
              {courthouseKind === "court-filing" ? "Filing at"
                : courthouseKind === "document-retrieval" ? "Retrieving from" : "Serving"}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.deep, lineHeight: 1.25 }}>
              {job.type || "Not specified"}
            </div>
            <div style={briefField()}>
              <div style={briefLabel()}>Documents</div>
              <div style={briefValue()}>
                {job.docs && job.docs !== "—" ? job.docs : "Not specified"}
              </div>
            </div>
            {job.courtName || job.county ? (
              <div style={briefField()}>
                <div style={briefLabel()}>Court</div>
                <div style={briefValue()}>
                  {job.courtName || (job.county ? job.county + " County" : "")}
                </div>
              </div>
            ) : null}
          </div>

          {/* 4 — LOCATION. Where exactly, and how do I get past the gate. */}
          <div style={briefBlock()}>
            <div style={briefLabel()}>Service address</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: destAddress ? C.deep : C.warn, lineHeight: 1.25 }}>
              {destAddress || "NO SERVICE ADDRESS ON FILE"}
            </div>

            {/* A gate code is help, not danger — gold, not amber. */}
            {access ? (
              <div style={{
                ...briefField(), background: C.goldXL, borderLeft: `3px solid ${C.gold}`,
                padding: 12,
              }}>
                <div style={briefLabel()}>Access</div>
                <div style={briefValue()}>{access}</div>
              </div>
            ) : null}

            <div style={briefField()}>
              <div style={briefLabel()}>Arrival</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.deep, lineHeight: 1.25 }}>
                {leg.route
                  ? `${ssFcEta(leg.route.seconds)} · ${ssFcMiles(leg.route.meters)}`
                  : (leg.hasFix ? (leg.routing ? "Routing…" : "No route available") : "Waiting for GPS")}
              </div>
              {leg.route && Array.isArray(leg.route.steps) && leg.route.steps.length ? (
                <div style={{ ...briefProse(), marginTop: 4 }}>
                  {ssFcTruncate(leg.route.steps[0].text, 84)}
                </div>
              ) : null}
              {leg.route && leg.route.stale ? (
                <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.1em", color: C.warn, marginTop: 4 }}>
                  LAST KNOWN ROUTE
                </div>
              ) : null}
            </div>

            <div style={{ marginTop: 12 }}>
              <FieldDirections job={job} />
            </div>
          </div>

          {/* 5 — ROUTE. Demoted below identity on purpose: turn-by-turn ran on
              the drive. This screen's job starts when the vehicle stops. */}
          <div style={briefBlock()}>
            <div style={briefLabel()}>Route</div>
            <FieldMap job={job} height="min(32vh, 320px)" onRoute={setLeg} />
          </div>

          {/* 6 — HISTORY. Have I been here before, and did someone already slam
              this door? The window blocks say whether this trip earns a
              due-diligence window that is not already covered. */}
          <div style={briefBlock()}>
            <div style={briefLabel()}>Prior attempts</div>
            {attemptCount === 0 ? (
              <div style={{
                display: "inline-block", fontSize: 15, fontWeight: 800,
                background: C.okL, color: C.ok, border: `1px solid ${C.ok}`,
                borderRadius: 4, padding: "6px 10px",
              }}>FIRST ATTEMPT</div>
            ) : (
              <>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.deep }}>
                  {attemptCount} attempt{attemptCount === 1 ? "" : "s"}
                </div>
                {recent.map((a, i) => (
                  <div key={i} style={{
                    marginTop: 12, paddingLeft: 11,
                    borderLeft: `3px solid ${/served/i.test(String(a && a.result || "")) ? C.ok : C.steelL}`,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.steel }}>
                      {fmtWhen(a)}{a && a.result ? ` · ${a.result}` : ""}
                    </div>
                    {a && (a.notes || a.outcome) ? (
                      <div style={{ ...briefProse(), marginTop: 2 }}>
                        {ssFcTruncate(String(a.notes || a.outcome), 160)}
                      </div>
                    ) : null}
                  </div>
                ))}
              </>
            )}

            <div style={{ ...briefField(), display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[["day", "DAY"], ["eve", "EVE"], ["weekend", "WKND"]].map(([k, lbl]) => {
                const on = !!(dd && dd.windows && dd.windows[k]);
                return (
                  <div key={k} style={{
                    minWidth: 56, height: 32, borderRadius: 4,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 800, letterSpacing: "0.14em",
                    background: on ? C.deep : C.steelXXL,
                    color: on ? C.white : C.steel,
                    border: on ? "none" : `1px solid ${C.steelXL}`,
                  }}>{lbl}</div>
                );
              })}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.steel, marginTop: 8 }}>
              {(dd && dd.covered) || 0} of 3 due-diligence windows covered
            </div>
          </div>

          {/* 7 — INSTRUCTIONS. The unedited source. Anything load-bearing has
              already been hoisted into HAZARD and ACCESS above. */}
          {shownInstructions ? (
            <div style={briefBlock()}>
              <div style={briefLabel()}>Client instructions</div>
              <div style={{ ...briefProse(), whiteSpace: "pre-wrap" }}>{shownInstructions}</div>
            </div>
          ) : null}

          {/* 8 — CONTACTS. Bottom, because dialling anyone is a failure path,
              not a pre-approach step. */}
          <div style={{ ...briefBlock(), paddingBottom: 40 }}>
            <div style={briefLabel()}>Contacts</div>

            {/* [server-parity-web-2026-10-05] No raw tel: links and no numbers on
                screen. Subject and attorney calls go through the masked bridge
                (POST /api/server/jobs/:id/call); the client's phone is not shown
                to servers at all — the job Chat is the channel to the client. */}
            {client.name || client.company ? (
              <div style={{ ...contactRow, borderBottom: `1px solid ${C.steelXL}` }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", color: C.steel }}>CLIENT</div>
                  <div style={briefValue()}>{client.name || client.company}</div>
                  <div style={{ fontSize: 12, color: C.steel, marginTop: 2 }}>Reach the client through the job Chat</div>
                </div>
              </div>
            ) : null}

            {job.recipientPhone ? (
              <div style={{ ...contactRow, borderBottom: `1px solid ${C.steelXL}` }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", color: C.steel }}>SUBJECT</div>
                  <div style={briefValue()}>Phone on file</div>
                </div>
                <MaskedCallButton job={job} target="subject" hasNumber />
              </div>
            ) : null}

            {job.attorneyPhone ? (
              <div style={contactRow}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", color: C.steel }}>ATTORNEY</div>
                  <div style={briefValue()}>Counsel</div>
                </div>
                <MaskedCallButton job={job} target="attorney" hasNumber />
              </div>
            ) : null}

            {!job.recipientPhone && !job.attorneyPhone ? (
              <div style={briefProse()}>
                No subject or attorney phone on this job. Use the Chat tab in Jobs to reach the client.
              </div>
            ) : null}
          </div>

        </div>
      </div>
      )}

      <BulkServeBanner job={job} style={{ flexShrink: 0, margin: "8px 16px", width: "calc(100% - 32px)" }} />{/* [field-bulk-serve-2026-10-08] */}
      <DoorTagNotice job={job} style={{ flexShrink: 0, margin: "8px 16px", width: "calc(100% - 32px)", boxSizing: "border-box" }} />{/* [door-tags-server-2026-10-22] */}
      {/* [field-brief-2026-09-20] Sticky CTA. Solid gold with a navy keyline —
          the 135deg gradient and the `0 4px 12px ${C.gold}55` glow are gone.
          A coloured glow is the most generic element a generated UI produces,
          and in direct sun it is invisible anyway. On a hazard-free sheet this
          is the ONLY saturated fill on screen, so it cannot be missed. */}
      {/* [mdt-2026-09-20] One slim row instead of a 64px slab with a caption
          above it. The button no longer spans the full width on tablet — the
          context sits to its left, and the ~50px saved goes to the record. */}
      <div style={{
        flexShrink: 0,
        /* [mdt-2026-09-20] Extra right padding on tablet so the button clears
           the SafetyFAB, which floats over this corner and was covering the
           last letter of LOG ATTEMPT. */
        padding: `8px ${isTablet ? 92 : 16}px calc(env(safe-area-inset-bottom) + 8px) 16px`,
        background: C.white, borderTop: `1px solid ${C.steelXL}`,
      }}>
        <div style={{
          ...(isTablet ? { width: "100%" } : sheet),
          display: "flex", alignItems: "center", gap: 14,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.steel, flex: 1, minWidth: 0 }}>
            {lastAttempt && lastAttempt.result
              ? `Last attempt: ${lastAttempt.result}`
              : "No prior attempts on this job"}
          </div>
          <button
            onClick={() => setAttemptOpen(true)}
            aria-label="Log a service attempt"
            style={{
              flex: isTablet ? "0 0 auto" : 1,
              minHeight: 48, padding: "0 24px",
              background: C.gold, color: C.deep,
              border: `2px solid ${C.deep}`, borderRadius: 8,
              fontSize: 16, fontWeight: 800, letterSpacing: "0.07em", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              fontFamily: "inherit",
            }}
          >
            <Plus size={20} color={C.deep} /> LOG ATTEMPT
          </button>
        </div>
      </div>

      {attemptOpen && <AttemptCapture job={job} onClose={() => setAttemptOpen(false)} />}
      {sup.escalate && (
        <EscalateSheet job={job} seedCategory={sup.escalate.seedCategory} seedAttemptRef={sup.escalate.seedAttemptRef}
          onClose={sup.closeEscalate} />
      )}{/* [support-tickets-2026-10-08] */}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SHARED — Sheet, Backdrop
// ═══════════════════════════════════════════════════════════════════════
export function Sheet({ children, onClose, small }) {
  return (
    <>
      <Backdrop onClose={onClose} />
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        height: small ? "auto" : "92%", maxHeight: "92%",
        background: C.bg,
        borderRadius: "24px 24px 0 0",
        zIndex: 200, display: "flex", flexDirection: "column", overflow: "hidden",
        animation: "slideUp 0.28s ease-out",
        boxShadow: "0 -10px 40px rgba(0,0,0,0.3)",
      }}>
        <div style={{
          width: 40, height: 4, background: C.steelL, borderRadius: 2,
          margin: "8px auto 0", flexShrink: 0,
        }} />
        {children}
      </div>
      <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
    </>
  );
}

function Backdrop({ onClose }) {
  return (
    <div onClick={onClose} style={{
      position: "absolute", inset: 0, background: "rgba(10,22,40,0.55)", zIndex: 150,
      animation: "fadeIn 0.2s ease-out",
    }}>
      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </div>
  );
}

const iconBtnLight = {
  width: 32, height: 32, borderRadius: 10, background: C.bg,
  border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0,
};


// ═══════════════════════════════════════════════════════════════════════
// Phase 4 default export — wrap AppInner with AuthGate
// ═══════════════════════════════════════════════════════════════════════
export default function App() {
  return <AuthGate><AppInner /></AuthGate>;
}

/* [field-list-priority-2026-10-08] Priority levels. Backend accepts exactly
   'standard' | 'rush' | 'urgent' | 'same-day' (cle-backend server.js
   getTenantServingPriceDollars / order paths); the AI intake path can emit
   'same_day'. Same-day and urgent are priced alike ($250) and are the
   high-priority tier; rush sits below them. */
function ssNormPriority(p, rushFlag) {
  const v = String(p || "").trim().toLowerCase().replace(/[_\s]+/g, "-");
  if (v === "same-day" || v === "sameday") return "same-day";
  if (v === "urgent") return "urgent";
  if (v === "rush" || rushFlag) return "rush";
  return "standard";
}
function ssPriorityRank(p) {
  const v = ssNormPriority(p);
  return v === "same-day" ? 3 : v === "urgent" ? 2 : v === "rush" ? 1 : 0;
}
/* True for anything above standard — the old `priority === "rush"` checks. */
function ssIsPriorityJob(job) { return !!job && ssPriorityRank(job.priority) >= 1; }
/* Stable sort: same-day, urgent, rush, then everything else in its existing order.
   Only for lists with no user-chosen sort (the field app has none today). */
function ssSortByPriority(list) {
  if (!Array.isArray(list)) return [];
  return list.map((j, i) => [j, i])
    .sort((a, b) => (ssPriorityRank(b[0] && b[0].priority) - ssPriorityRank(a[0] && a[0].priority)) || (a[1] - b[1]))
    .map((x) => x[0]);
}
function ssPriorityLabel(p) {
  const v = ssNormPriority(p);
  return v === "same-day" ? "Same-day" : v === "urgent" ? "Urgent" : v === "rush" ? "Rush" : "";
}
/* Same-day / Urgent render solid red (more prominent than Rush's tinted pill). */
function PriorityPill({ priority }) {
  const v = ssNormPriority(priority);
  if (v === "standard") return null;
  if (v === "rush") return <Pill bg={C.errL} color={C.err} icon={Flame}>Rush</Pill>;
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: C.err, color: C.white, padding: "3px 9px", borderRadius: 12,
      fontSize: 11, fontWeight: 800, letterSpacing: "0.02em",
      boxShadow: "0 1px 4px rgba(192,57,43,0.35)",
    }}>
      {v === "same-day" ? <Zap size={10} /> : <AlertTriangle size={10} />}
      {ssPriorityLabel(v)}
    </div>
  );
}

/* [field-list-attempt-due-2026-10-08] First-attempt due date -> { label, color, bg, tone }.
   tone: "overdue" (red), "today" (amber), "future" / "done" (neutral). Null when the job
   has no attempt_due_date or is completed. A date-only value is read as a LOCAL date so
   "2026-10-08" is not shifted to the 7th west of UTC. */
function ssAttemptDueInfo(job) {
  if (!job || job.status === "completed") return null;
  /* [first-attempt-sla-2026-10-22] An assigned job shows the SERVER's own deadline (Same-Day 8 h,
     Rush 48 h with a 24 h target, everything else 48 h from acceptance), with the time. */
  const _srvDue = job.serverAttemptDueAt || (job._raw && job._raw.server_attempt_due_at) || null;
  if (_srvDue) {
    const sd = new Date(_srvDue);
    if (!isNaN(sd.getTime())) {
      const nowS = new Date();
      const madeS = ssAttemptCount(job) > 0;
      const toneS = madeS ? "done" : sd < nowS ? "overdue" : (sd - nowS) < 12 * 3600 * 1000 ? "today" : "future";
      const dateStrS = sd.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
      const colorS = toneS === "overdue" ? C.err : toneS === "today" ? C.warn : C.steel;
      const bgS = toneS === "overdue" ? C.errL : toneS === "today" ? C.warnL : "transparent";
      return { label: "1st attempt due " + dateStrS, dateStr: dateStrS, color: colorS, bg: bgS, tone: toneS, date: sd };
    }
  }
  const raw = job.attemptDueDate || (job._raw && job._raw.attempt_due_date) || null;
  if (!raw) return null;
  let dt;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(raw).trim());
  if (m) dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  else dt = new Date(raw);
  if (isNaN(dt.getTime())) return null;
  const now = new Date();
  const dayKey = (d) => d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  const sameYear = dt.getFullYear() === now.getFullYear();
  const dateStr = dt.toLocaleDateString(undefined, sameYear
    ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
  const made = ssAttemptCount(job) > 0;
  const tone = made ? "done"
    : dayKey(dt) < dayKey(now) ? "overdue"
    : dayKey(dt) === dayKey(now) ? "today" : "future";
  const color = tone === "overdue" ? C.err : tone === "today" ? C.warn : C.steel;
  const bg = tone === "overdue" ? C.errL : tone === "today" ? C.warnL : "transparent";
  const label = "1st attempt due " + (tone === "today" ? "today" : dateStr);
  return { label, dateStr, color, bg, tone, date: dt };
}

// ═══════════════════════════════════════════════════════════════════════
// BULK SERVE (field)                                  [field-bulk-serve-2026-10-08]
// One person, one door, several orders: a server holding 3 jobs for the same
// subject at the same address serves them in one contact. POST /api/jobs/bulk-serve
// (cle-backend server.js, "B. POST /api/jobs/bulk-serve") applies ONE attempt to up
// to 50 jobs, each through applyServiceAttempt, so every job gets its own attempt
// row and — on a completed serve — its own auto-POS draft.
//
// The capture is NOT re-implemented here. BulkServeSheet renders the normal
// AttemptSheet with a `bulk` prop; AttemptSheet's own gates (GPS, photo minimum,
// identity, mailing, notes, geofence) run unchanged, and only the final POST is
// swapped for bulk.submit(payload). Any rule added to the attempt form is
// therefore inherited here automatically.
// ═══════════════════════════════════════════════════════════════════════
/* Successful-service outcome ids only (AttemptSheet RESULT_MAP: served ->
   served-personal, sub_served -> served-substitute-home). These are the results
   applyServiceAttempt treats as success ('served' | 'sub-served' | 'served-*'),
   i.e. the ones that complete the job and queue the POS draft. */
const SS_BULK_SERVE_OUTCOMES = ["served", "sub_served"];
const SS_BULK_SERVE_MAX = 50; // backend: 'Too many jobs in one bulk-serve (max 50)'
/* EXACTLY the backend's comparison: String(s == null ? '' : s).trim().toLowerCase()
   on the RAW recipient_address / recipient_name columns. Not the normalized
   job.address (which falls back to service_address/address) — a looser client
   match would offer groups the backend then refuses with a 400. */
function ssBulkNorm(s) { return String(s == null ? "" : s).trim().toLowerCase(); }
function ssBulkServeOpen(j, me) {
  const r = (j && j._raw) || {};
  // Assigned to THIS server (backend compares server_id to req.user.email) and
  // still serviceable. Courthouse filing / retrieval jobs are never served.
  return (r.status === "assigned" || r.status === "in-progress")
    && !!me && String(r.server_id || "") === me
    && !isCourthouseJob(j);
}
function ssBulkServeSiblings(job, jobs, user) {
  const me = String((user && user.email) || "");
  if (!job || !ssBulkServeOpen(job, me)) return [];
  const r0 = job._raw || {};
  const addr = ssBulkNorm(r0.recipient_address);
  const name = ssBulkNorm(r0.recipient_name);
  if (!addr || !name) return [];
  const seen = new Set([String(job.id)]);
  const out = [];
  (jobs || []).forEach((j) => {
    if (!j || seen.has(String(j.id))) return;
    if (!ssBulkServeOpen(j, me)) return;
    const r = j._raw || {};
    if (ssBulkNorm(r.recipient_address) !== addr || ssBulkNorm(r.recipient_name) !== name) return;
    seen.add(String(j.id));
    out.push(j);
  });
  return out.slice(0, SS_BULK_SERVE_MAX - 1);
}

/* Banner shown on an open job. Self-contained: reads jobs/user from AppCtx and
   owns the sheet, so each job screen hooks it in with one line. */
function BulkServeBanner({ job, style }) {
  const { jobs, user } = React.useContext(AppCtx);
  const [open, setOpen] = useState(false);
  const siblings = React.useMemo(() => ssBulkServeSiblings(job, jobs, user), [job, jobs, user]);
  if (!siblings.length && !open) return null;
  const n = siblings.length;
  return (
    <>
      {n ? (
        <button
          onClick={() => setOpen(true)}
          aria-label={`Serve ${n + 1} jobs together`}
          style={{
            display: "flex", alignItems: "center", gap: 10, width: "calc(100% - 28px)",
            margin: "10px 14px 0", padding: "11px 12px", textAlign: "left",
            background: C.goldXL, border: `1.5px solid ${C.gold}`, borderRadius: 12,
            color: C.deep, cursor: "pointer", fontFamily: "inherit", boxSizing: "border-box",
            ...(style || {}),
          }}
        >
          <Briefcase size={18} color={C.deep} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.35 }}>
            <strong>{n} more job{n === 1 ? "" : "s"}</strong> for {jobDisplay(job).name} at this address
            <span style={{ display: "block", fontWeight: 800, marginTop: 2 }}>Serve all together</span>
          </span>
          <ChevronRight size={18} color={C.deep} style={{ flexShrink: 0 }} />
        </button>
      ) : null}
      {open ? <BulkServeSheet job={job} siblings={siblings} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function BulkServeSheet({ job, siblings, onClose }) {
  const { reload } = React.useContext(AppCtx);
  // Snapshot: after the serve, reload() drops the completed jobs from the
  // sibling list — the results screen must still be able to name them.
  const [group] = useState(() => [job, ...(siblings || [])].slice(0, SS_BULK_SERVE_MAX));
  const [selected, setSelected] = useState(() => group.map((j) => String(j.id)));
  const [results, setResults] = useState(null);

  // Called by AttemptSheet.submitAttempt with the fully validated payload.
  // Throwing surfaces the message in the sheet's own error line.
  async function submit(payload) {
    const ids = group.map((j) => String(j.id)).filter((id) => selected.includes(id));
    if (!ids.length) throw new Error("Check at least one job to serve.");
    const out = await api.post("/api/jobs/bulk-serve", { jobIds: ids, attempt: payload });
    const rows = Array.isArray(out && out.results) ? out.results : [];
    setResults({ ids, rows });
    try { await reload(); } catch (_) { /* list refresh is best-effort; results are already in hand */ }
  }

  if (results) {
    return <BulkServeResults group={group} sent={results.ids} rows={results.rows} onClose={onClose} />;
  }
  return (
    <AttemptSheet job={job} seedOutcome="served" onClose={onClose}
      bulk={{ jobs: group, selected, setSelected, submit }} />
  );
}

/* Rendered inside AttemptSheet (hook-in point) — the job selector. */
function BulkServeJobPicker({ bulk }) {
  const { jobs, selected, setSelected } = bulk;
  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  return (
    <div style={{ background: C.white, border: `1.5px solid ${C.gold}`, borderRadius: 12, padding: 12, marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: C.deep, letterSpacing: "0.08em" }}>
        BULK SERVE · {selected.length} OF {jobs.length} JOBS
      </div>
      <div style={{ fontSize: 12, color: C.steel, marginTop: 4, marginBottom: 8, lineHeight: 1.4 }}>
        This one attempt (same GPS fix, photos and details) is recorded on every checked job.
        Each job gets its own proof-of-service draft.
      </div>
      {jobs.map((j) => {
        const id = String(j.id);
        const on = selected.includes(id);
        return (
          <label key={id} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "9px 4px",
            borderTop: `1px solid ${C.steelXL}`, cursor: "pointer", minHeight: 44,
          }}>
            <input type="checkbox" checked={on} onChange={() => toggle(id)}
              style={{ width: 22, height: 22, accentColor: C.deep, flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: C.deep }}>
                Job #{j.jobNumber}{j.caseNo ? ` · ${j.caseNo}` : ""}
              </span>
              <span style={{ display: "block", fontSize: 12, color: C.steel, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {[j.client, j.docs && j.docs !== "—" ? j.docs : ""].filter(Boolean).join(" · ") || j.type}
              </span>
            </span>
          </label>
        );
      })}
      {!selected.length ? (
        <div style={{ color: C.err, fontSize: 12, fontWeight: 600, marginTop: 6 }}>Check at least one job to serve.</div>
      ) : null}
    </div>
  );
}

function ssBulkResultLine(row) {
  if (!row) return { tone: "steel", text: "No result returned for this job — check its attempt history." };
  if (!row.ok) return { tone: "err", text: "Not saved — " + (row.error || "the server refused this job") };
  if (row.duplicate) return { tone: "ok", text: "Already recorded — this attempt was already on the job." };
  const w = row.warning || null;
  const completed = row.status == null || row.status === "completed"; // null: older backend without status
  if (!completed) return { tone: "warn", text: "Attempt saved. " + ((w && w.message) || "The job was not marked served.") };
  if (w && w.message) return { tone: "warn", text: "Served — POS draft created. " + w.message };
  return { tone: "ok", text: "Served — POS draft created." };
}

function BulkServeResults({ group, sent, rows, onClose }) {
  const byId = {};
  (rows || []).forEach((r) => { if (r && r.jobId != null) byId[String(r.jobId)] = r; });
  const okN = sent.filter((id) => byId[id] && byId[id].ok).length;
  const tones = { ok: C.ok, warn: C.warn, err: C.err, steel: C.steel };
  return (
    <Sheet onClose={onClose} small>
      <div style={{ padding: "18px 16px 16px", flex: 1, minHeight: 0, overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.steel, letterSpacing: "0.1em" }}>BULK SERVE</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.deep, marginTop: 2 }}>
              {okN} of {sent.length} job{sent.length === 1 ? "" : "s"} saved
            </div>
          </div>
          <button onClick={onClose} style={iconBtnLight} aria-label="Close"><X size={20} color={C.deep} /></button>
        </div>
        {group.map((j) => {
          const id = String(j.id);
          const included = sent.includes(id);
          const line = included ? ssBulkResultLine(byId[id]) : { tone: "steel", text: "Not included in this serve." };
          const Icon = line.tone === "ok" ? CheckCircle2 : (line.tone === "err" ? XCircle : (line.tone === "warn" ? AlertTriangle : Circle));
          return (
            <div key={id} style={{
              display: "flex", gap: 10, alignItems: "flex-start", padding: 12, marginBottom: 8,
              background: C.white, borderRadius: 12, border: `1px solid ${C.steelXL}`,
            }}>
              <Icon size={18} color={tones[line.tone]} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.deep }}>
                  Job #{j.jobNumber}{j.caseNo ? ` · ${j.caseNo}` : ""}
                </div>
                <div style={{ fontSize: 12, color: tones[line.tone], marginTop: 2, lineHeight: 1.4 }}>{line.text}</div>
              </div>
            </div>
          );
        })}
        <button onClick={onClose} style={{
          width: "100%", padding: 14, marginTop: 6, background: C.deep, color: C.white,
          border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800, cursor: "pointer",
        }}>Done</button>
      </div>
    </Sheet>
  );
}

// ── Job normalizer: backend uses snake_case; prototype uses camelCase ──
function _normalizeJob(j) {
  const status =
    j.status === "available" ? "available"
    : j.status === "assigned" || j.status === "in-progress" ? "active"
    : j.status === "completed" ? "completed"
    : j.status;
  function _fmtDate(d) {
    if (!d) return "—";
    try {
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return String(d);
      const today = new Date();
      const sameYear = dt.getFullYear() === today.getFullYear();
      return dt.toLocaleDateString(undefined, sameYear
        ? { month: "short", day: "numeric" }
        : { month: "short", day: "numeric", year: "numeric" });
    } catch { return String(d); }
  }
  return {
    id: String(j.id),
    jobNumber: j.job_number || j.id,
    servee: j.recipient_name || j.defendant || j.servee || "Unnamed",
    type: j.service_type || j.type || "Personal",
    // Raw service_type (snake_case from backend) — used to branch the field UI
    // between serving jobs and in-person courthouse filing / document retrieval.
    serviceType: j.service_type || "",
    /* [field-list-priority-2026-10-08] Was collapsed to rush/standard, which hid the
       backend's 'same-day' and 'urgent' levels (server.js PRIORITY_PRICE_MAP) as
       "standard". ssNormPriority keeps all four. */
    priority: ssNormPriority(j.priority, j.rush),
    status,
    /* [pos-overhaul-2026-10-07:server-app] SERVER PAY ONLY. This read
       `j.server_payout || j.payment`, and jobs.payment is the CLIENT price — so every
       job whose server pay had not been locked yet showed the server what the client
       was charged. Null now means "Pay pending"; render through ssPayLabel(). */
    pay: ssServerPayOf(j),
    /* [distance-fix-2026-09-20] Was `Number(j.distance_miles || j.dist || 0)`.
       `distance_miles` is a PHANTOM — it is not a column on jobs, GET /api/jobs
       is a bare SELECT *, and nothing in the backend computes it for this
       response. (The one place the backend does emit `distance_miles` is
       rankCandidateServers, which measures job -> each candidate SERVER and
       lands on user rows, never here.) So this evaluated to Number(0) on every
       row and rendered "0 mi away" under a real street address, which reads as
       "you are here." Null now, and computed against a real origin in AppInner.
       Two consumers — the map claim bar and the RoutePlanner stop list —
       already carry `!= null` guards written to suppress exactly this; the
       `|| 0` coercion is what had been defeating them. */
    dist: (j.distance_miles != null ? Number(j.distance_miles)
         : (j.dist != null ? Number(j.dist) : null)),
    deadline: _fmtDate(j.deadline || j.deadline_at || j.attempt_due_date),
    /* [field-list-attempt-due-2026-10-08] The first-attempt due date on its own. `deadline`
       above falls back to attempt_due_date only when there is no deadline, so a job with
       both never showed when the first attempt is owed. Raw value (ISO / YYYY-MM-DD);
       render through ssAttemptDueInfo(). */
    attemptDueDate: j.attempt_due_date || null,
    address: j.recipient_address || j.service_address || j.address || "",
    city: j.city || "",
    zip: j.zip || j.postal_code || "",
    county: j.court_county || j.county || "",
    // Courthouse context — for in-person filing / document-retrieval jobs.
    courtName: j.court_name || "",
    courtCounty: j.court_county || "",
    courtAddress: j.court_address || "",
    courtFilingFee: j.court_filing_fee != null ? Number(j.court_filing_fee) : null,
    filingConfirmation: j.filing_confirmation || "",
    client: j.client_name || j.plaintiff || j.firm_name || "",
    caseNo: j.case_number || j.caseNo || "",
    docs: j.docs_label || ((Array.isArray(j.doc_files) && j.doc_files.length) ? `${j.doc_files.length} document(s)` : "—"),
    instructions: j.special_instructions || j.notes || "",
    /* [field-command-2026-09-11] COORDINATE FIX. This read `j.lat` / `j.latitude`,
       neither of which exists on a jobs row — the real columns are
       `recipient_lat` / `recipient_lng`. Every job therefore normalized to
       lat:null/lng:null, which is why MapView geocodes every address one at a
       time through /api/geocode instead of using the coordinates already stored
       on the record. Real columns first; the legacy names stay as a fallback for
       any other producer (route planner fixtures, Watch payloads). */
    lat: j.recipient_lat != null ? Number(j.recipient_lat)
       : (j.lat != null ? Number(j.lat) : (j.latitude != null ? Number(j.latitude) : null)),
    lng: j.recipient_lng != null ? Number(j.recipient_lng)
       : (j.lng != null ? Number(j.lng) : (j.longitude != null ? Number(j.longitude) : null)),
    /* [field-command-2026-09-11] Fields that are already on the job record and were
       simply never mapped by this normalizer, so no field UI could read them.
       Phones: attorney falls back to atty_phone, then to the client's number —
       a firm's main line is the right place to reach an attorney when no direct
       line was supplied. */
    recipientPhone: j.recipient_phone || "",
    clientPhone: j.client_phone || "",
    attorneyPhone: j.attorney_phone || j.atty_phone || j.client_phone || "",
    /* Identifying detail. Gated behind claim in the UI — a face, a DOB and a
       physical description on an unclaimed job would broadcast an identifiable
       person to every browsing server. */
    recipientDesc: j.recipient_desc || "",
    defendantDob: j.defendant_dob || "",
    defendantPhysicalNotes: j.defendant_physical_notes || "",
    /* [fc-crash-fix-2026-09-11] `subjectPhoto` was declared TWICE in this literal
       (esbuild: "Duplicate key subjectPhoto"). The string form that used to sit here
       was dead — the later declaration always won — and it promised a URL that
       jobs.subject_photo never contains. The JSONB object below is the real column
       shape and the one subjectPhotoKey()/FieldSubjectPhoto() are written against,
       so the object declaration is the survivor and this one is gone. */
    attempts: Array.isArray(j.attempts) ? j.attempts.length : 0,
    rawAttempts: Array.isArray(j.attempts) ? j.attempts : [],
    windows: j.windows || { am: false, pm: false, eve: false, weekend: false },
    x: Number(j.x || 50),
    y: Number(j.y || 50),
    posStatus: j.pos_status || null,
    posFormId: j.pos_form_id || null,
    // [field-view-2026-09-07] jobs.subject_photo (JSONB) rides in on the job row;
    // bucket is private so the key must be presigned before it can be rendered.
    subjectPhoto: j.subject_photo || null, // [field-view-2026-09-07]
    ...ssBundleFieldsOf(j), // [job-bundles-2026-10-13] bundle_* survive normalization (snake + camel)
    _raw: ssStripClientPrice(j), // [pos-overhaul-2026-10-07:server-app] never cache/keep the client price
  };
}

/* [job-bundles-2026-10-13] JOB BUNDLES. A bundle is 2+ jobs to the SAME service address
   with ONE total server pay; a server must accept them all together. GET /api/jobs carries
   bundle_id and, when set, bundle_job_count / bundle_total_pay (SERVER pay) /
   bundle_address / bundle_status / bundle_label. Both spellings are kept on the normalized
   job so any reader (and the native jobs cache) finds them. Never reads `payment`. */
function ssBundleFieldsOf(j) {
  if (!j) return {};
  const id = j.bundle_id != null ? j.bundle_id : (j.bundleId != null ? j.bundleId : null);
  if (id == null || id === "") return { bundle_id: null, bundleId: null };
  const cnt = j.bundle_job_count != null ? j.bundle_job_count : j.bundleJobCount;
  const pay = j.bundle_total_pay != null ? j.bundle_total_pay : j.bundleTotalPay;
  const out = {
    bundleId: id,
    bundleJobCount: cnt != null && isFinite(Number(cnt)) ? Number(cnt) : null,
    bundleTotalPay: pay != null && isFinite(Number(pay)) ? Number(pay) : null,
    bundleAddress: j.bundle_address || j.bundleAddress || "",
    bundleStatus: j.bundle_status || j.bundleStatus || null,
    bundleLabel: j.bundle_label || j.bundleLabel || "",
  };
  /* [job-bundles-v2-2026-10-16] v2 fields: first/additional server pay, this job's pay role,
     and whether the bundle has a live offer to THIS server. Server pay only. */
  const num = (v) => (v != null && v !== "" && isFinite(Number(v)) ? Number(v) : null);
  out.bundleFirstPay = num(j.bundle_first_pay != null ? j.bundle_first_pay : j.bundleFirstPay);
  out.bundleAdditionalPay = num(j.bundle_additional_pay != null ? j.bundle_additional_pay : j.bundleAdditionalPay);
  out.bundlePayRole = j.bundle_pay_role || j.bundlePayRole || null;
  out.bundleOfferedToMe = !!(j.bundle_offered_to_me != null ? j.bundle_offered_to_me : j.bundleOfferedToMe);
  return {
    ...out,
    bundle_id: out.bundleId, bundle_job_count: out.bundleJobCount, bundle_total_pay: out.bundleTotalPay,
    bundle_address: out.bundleAddress, bundle_status: out.bundleStatus, bundle_label: out.bundleLabel,
    bundle_first_pay: out.bundleFirstPay, bundle_additional_pay: out.bundleAdditionalPay, // [job-bundles-v2-2026-10-16]
    bundle_pay_role: out.bundlePayRole, bundle_offered_to_me: out.bundleOfferedToMe,
  };
}
/* [job-bundles-2026-10-13] { id, count, totalPay } when the job is in a real (2+) bundle,
   else null. Works on normalized jobs, raw rows and older cached jobs (via _raw). */
function ssJobBundle(job) {
  if (!job) return null;
  let f = ssBundleFieldsOf(job);
  if (f.bundleId == null && job._raw) f = ssBundleFieldsOf(job._raw);
  if (f.bundleId == null) return null;
  return { id: f.bundleId, count: f.bundleJobCount, totalPay: f.bundleTotalPay, address: f.bundleAddress, status: f.bundleStatus,
    label: f.bundleLabel, firstPay: f.bundleFirstPay, additionalPay: f.bundleAdditionalPay, payRole: f.bundlePayRole, offeredToMe: f.bundleOfferedToMe }; // [job-bundles-v2-2026-10-16]
}
function ssIsMultiBundle(job) {
  const b = ssJobBundle(job);
  return !!(b && b.count != null && b.count > 1);
}

/* [pos-overhaul-2026-10-07:server-app] PRICE HIDING. A process server must never see
   the client price. The backend strips these fields for role 'server'; this is the
   client-side belt-and-braces so an older backend (or a cached row) cannot put them
   on screen or into ss_jobs_cache. Server pay comes ONLY from the server-pay fields. */
const SS_CLIENT_PRICE_KEYS = ["price", "client_price", "clientPrice", "payment", "total", "amount", "amount_paid", "amountPaid",
  "payment_amount", "paymentAmount", "invoice_total", "invoiceTotal", "stripe_amount", "stripeAmount", "amount_cents",
  "company_revenue", "companyRevenue", "payout_breakdown", "payoutBreakdown", "order_price", "orderPrice", "total_price", "totalPrice"];
function ssStripClientPrice(o) {
  if (!o || typeof o !== "object") return o;
  const out = { ...o };
  SS_CLIENT_PRICE_KEYS.forEach((k) => { if (k in out) delete out[k]; });
  return out;
}
function ssServerPayOf(j) {
  if (!j) return null;
  const cands = [j.server_pay, j.serverPay, j.server_payout, j.serverPayout, j.server_payment, j.offer_pay, j.offerPay];
  for (const v of cands) {
    if (v != null && v !== "" && isFinite(Number(v)) && Number(v) > 0) return Number(v);
  }
  return null;
}
/* "$95.00" or "Pay pending" — the ONLY way a pay amount is rendered in this app. */
function ssPayLabel(n) {
  return (n != null && n !== "" && isFinite(Number(n)) && Number(n) > 0) ? ssMoney(n) : "Pay pending";
}

/* [fc-crash-fix-2026-09-11] Cache-shape guard for ss_jobs_cache.
   Every normalized job carries `_raw`; no backend row ever does, so that single
   property separates "already normalized" from "raw row that must not reach the UI".
   Re-normalizing an already-normalized job would be destructive (attempts would
   collapse to 0 and _raw would nest), so the check has to run first. */
function _ssRehydrateCachedJob(j) {
  if (!j || typeof j !== "object") return j;
  /* [pos-overhaul-2026-10-07:server-app] A cache written by an earlier build holds
     pay = client price for unpriced jobs and the raw payment fields. Re-derive. */
  /* [field-list-priority-2026-10-08] ...and an earlier build collapsed priority to
     rush/standard and never stored attemptDueDate — re-derive both from _raw too. */
  return j._raw ? { ...j, ...ssBundleFieldsOf(j._raw.bundle_id != null ? j._raw : j), /* [job-bundles-2026-10-13] [job-bundles-v2-2026-10-16] raw row first: it carries every bundle_* column */ pay: ssServerPayOf(j._raw), _raw: ssStripClientPrice(j._raw),
    priority: ssNormPriority(j._raw.priority != null ? j._raw.priority : j.priority, j._raw.rush),
    attemptDueDate: j.attemptDueDate || j._raw.attempt_due_date || null } : _normalizeJob(j);
}

/* [fc-crash-fix-2026-09-11] The attempt COUNT, whatever shape the job is in.
   `attempts` is a number on a normalized job and the array of attempt records on a
   raw row, and handing the array to JSX is exactly the crash this marker fixes.
   Nothing in the UI may read job.attempts directly — call this instead. */
/* [attempts-three-2026-10-06] Every service tier includes up to THREE attempts. This is the single place the
   server app (web + native iOS) defines that number. The backend pricing, the admin app,
   the help center and every service-tier description already say 3; this card was the
   last place still printing "/4 attempts". Display and copy must read this constant,
   never a literal. */
const SS_ATTEMPTS_INCLUDED = 3;

/* [court-closed-not-counted-2026-10-08] A court-closed attempt is on the server (the app
   warned them) and never uses one of the client's 3 attempts. */
function ssCountsTowardAttempts(a) {
  if (!a) return false;
  const r = String((typeof a === "object" ? (a.result || a.outcome) : a) || "").trim();
  if (r === "court-closed" || r === "court_closed") return false;
  if (typeof a === "object" && a.countsTowardLimit === false) return false;
  return true;
}
function ssAttemptCount(job) {
  if (!job) return 0;
  if (Array.isArray(job.attempts)) return job.attempts.filter(ssCountsTowardAttempts).length;
  if (Array.isArray(job.rawAttempts)) return job.rawAttempts.filter(ssCountsTowardAttempts).length;
  const n = Number(job.attempts);
  return isFinite(n) ? n : 0;
}
